/**
 * Thai messages — the source of truth for `MessageKey` (ADR 0175).
 *
 * Thai is the default language, so this file defines the key set and `en.ts` is
 * typed against it. Add a key here first; TypeScript then refuses to compile
 * until English has it too, which is what keeps the two dictionaries in step
 * across eleven translation phases without anyone auditing them by hand.
 *
 * KEY SHAPE — `domain.context.thing`, domains matching the left menu so that
 * "how far has translation got" is answerable by pointing at the sidebar.
 *
 * WHAT STAYS IN ENGLISH (ADR 0175 D11)
 * Acronyms and technologies (GPS, SQL, API, CSV, PDF, OTP, QR), product and
 * screen names (War Room, Copilot, Dashboard), and terms Thai logistics already
 * says in English (Backhaul, Charter, Overflow). The rule is what the people
 * doing this job actually say — the previous system this replaces wrote
 * "วันหมดอายุ GPS" and "จำนวน Check Point", not a translation of either.
 *
 * VOCABULARY comes from `4-Archives/original_projects/deepinnocube-web`, the Vue
 * system these users came from, so the words are ones they already know rather
 * than more correct ones nobody has seen (D10).
 */

export const th = {
  // ── common — used everywhere; translate once, never per screen ──────────
  "common.save": "บันทึก",
  "common.cancel": "ยกเลิก",
  "common.delete": "ลบ",
  "common.edit": "แก้ไข",
  "common.add": "เพิ่ม",
  "common.viewPhoto": "ดูรูป",
  "common.close": "ปิด",
  "common.confirm": "ยืนยัน",
  "common.search": "ค้นหา",
  "common.filter": "ตัวกรอง",
  "common.clear": "ล้าง",
  "common.now": "ตอนนี้",
  "common.selectTime": "เลือกเวลา",
  "common.refresh": "รีเฟรช",
  "common.export": "ส่งออก",
  "common.import": "นำเข้า",
  "common.back": "ย้อนกลับ",
  "common.next": "ถัดไป",
  "common.loading": "กำลังโหลด…",
  "common.saving": "กำลังบันทึก…",
  "common.noData": "ไม่มีข้อมูล",
  "common.all": "ทั้งหมด",
  "common.noOptions": "ยังไม่มีตัวเลือกในหมวดนี้",
  "common.none": "ไม่มี",
  "common.yes": "ใช่",
  "common.no": "ไม่ใช่",
  "common.total": "รวม",
  "common.status": "สถานะ",
  "common.actions": "จัดการ",
  "common.date": "วันที่",
  "common.time": "เวลา",
  "common.name": "ชื่อ",
  "common.note": "หมายเหตุ",
  "common.required": "จำเป็นต้องกรอก",
  "common.sortBy": "เรียงลำดับโดย",
  // Wraps whatever the API said, which is English and out of our reach until the
  // error-code ADR lands (D7). The user reads a Thai sentence, then the detail.
  "common.errorTitle": "เกิดข้อผิดพลาด",
  "common.saveFailed": "บันทึกไม่สำเร็จ",
  "common.loadFailed": "โหลดข้อมูลไม่สำเร็จ",
  "time.justNow": "เมื่อสักครู่",
  "time.minAgo": "{n} นาทีที่แล้ว",
  "time.hrAgo": "{n} ชม.ที่แล้ว",
  "time.dAgo": "{n} วันที่แล้ว",
  "ops.nCompleted": "เสร็จสิ้น {n}",
  "fleet.nActiveOf": "วิ่งอยู่ {n} จาก {total}",
  "common.loadFailedWith": "โหลดข้อมูลไม่สำเร็จ: {message}",
  // hunt fix-load (2026-10-25) — the generic finance load-failure message: a clerk reading a blank
  // page or endless skeleton believed there was no money. Pages with no better-fitting key use this.
  "finance.loadFailed": "โหลดข้อมูลไม่สำเร็จ — ยังไม่ได้แสดงตัวเลข ลองใหม่อีกครั้ง",
  "common.confirmDelete": "ต้องการลบข้อมูลนี้ ?",

  // ── language switcher ──────────────────────────────────────────────────
  "common.language": "ภาษา",
  "common.language.th": "ไทย",
  "common.language.en": "อังกฤษ",

  // ── login — the one screen a user meets before they can choose a language,
  //    which is why the switcher also lives there (ADR 0175) ────────────────
  "login.tagline": "ระบบบริหารงานขนส่ง",
  "login.email": "อีเมล",
  "login.methodOtp": "OTP (ไม่ใช้รหัสผ่าน)",
  "login.methodPassword": "รหัสผ่าน",
  "login.continue": "ดำเนินการต่อ",
  "login.sendOtp": "ส่งรหัส OTP",
  "login.otpSentTo": "ส่งรหัส OTP ไปที่",
  "login.otpRef": "อ้างอิง:",
  "login.otpPlaceholder": "กรอกรหัส 6 หลัก",
  "login.verify": "ยืนยันและเข้าสู่ระบบ",
  "login.backToEmail": "← กลับไปกรอกอีเมล",
  "login.password": "รหัสผ่าน",
  "login.passwordPlaceholder": "รหัสผ่าน",
  "login.signIn": "เข้าสู่ระบบ",
  "login.noAccount": "ยังไม่มีบัญชี?",
  "login.signUp": "สมัครใช้งาน",

  // ── register / invite / select-org — the rest of the pre-login surface ──
  "register.title": "สร้างบัญชี",
  "register.createAccount": "สร้างบัญชี",
  "register.failed": "สมัครใช้งานไม่สำเร็จ",
  "register.verifyFailed": "ยืนยันไม่สำเร็จ",
  "login.otpSendFailed": "ส่งรหัส OTP ไม่สำเร็จ",
  "login.otpVerifyFailed": "ยืนยันรหัส OTP ไม่สำเร็จ",
  "login.failed": "เข้าสู่ระบบไม่สำเร็จ",
  // ADR 0264 — ขั้นตั้งรหัสผ่านใหม่ บนหน้าล็อกอินเดียวกัน (ไม่ใช่หน้าแยกแบบ ADR 0261)
  "login.setNewPasswordHeading": "ตั้งรหัสผ่านใหม่",
  "login.newPassword": "รหัสผ่านใหม่",
  "login.confirmNewPassword": "ยืนยันรหัสผ่านใหม่",
  "login.saveNewPassword": "บันทึกรหัสผ่านใหม่",
  "login.newPasswordHint": "อย่างน้อย 8 ตัวอักษร",
  "login.newPasswordTooShort": "รหัสผ่านใหม่ต้องมีอย่างน้อย 8 ตัวอักษร",
  "login.newPasswordMismatch": "รหัสผ่านทั้งสองช่องไม่ตรงกัน",
  "login.newPasswordSameAsCurrent": "รหัสผ่านใหม่ต้องไม่ซ้ำกับรหัสผ่านเดิม",
  "login.setNewPasswordFailed": "ตั้งรหัสผ่านใหม่ไม่สำเร็จ",
  "login.sessionExpiredRelogin": "หมดเวลาแล้ว กรุณาเข้าสู่ระบบใหม่อีกครั้ง",
  "login.cancel": "ยกเลิก",
  "hr.password.adminResetNotice": "ผู้ใช้จะต้องตั้งรหัสผ่านใหม่ด้วยตนเองเมื่อเข้าสู่ระบบครั้งถัดไป",
  "hr.password.mustChangeBadge": "รอตั้งรหัสผ่านใหม่",
  "register.subtitle": "DeepTrack ระบบบริหารงานขนส่ง",
  "register.displayName": "ชื่อที่แสดง",
  "register.namePlaceholder": "ชื่อของคุณ",
  "register.passwordHint": "อย่างน้อย 8 ตัวอักษร",
  "register.fillAll": "กรุณากรอกข้อมูลให้ครบ",
  "register.passwordMin": "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร",
  "register.passwordWeak": "รหัสผ่านง่ายเกินไป — ผสมตัวพิมพ์ใหญ่/เล็ก ตัวเลข และสัญลักษณ์",
  "nav.notificationsUnread": "การแจ้งเตือน ({count} ยังไม่อ่าน)",
  "register.haveAccount": "มีบัญชีอยู่แล้ว?",
  "register.redirecting": "กำลังพาไปหน้าแรก…",

  "invite.invitedBy": "เชิญโดย",
  "invite.role": "บทบาท",
  "invite.expires": "หมดอายุ",

  "createOrg.descPlaceholder": "คำอธิบายสั้น ๆ ขององค์กรหรือโครงการ",

  "selectOrg.title": "เลือกองค์กร",
  // ADR 0418 frame 3 — wrong domain for this account
  "wrongHost.title": "บัญชีนี้ไม่ได้อยู่ในองค์กร {org}",
  "wrongHost.body": "กรุณาเข้าสู่ระบบที่โดเมนขององค์กรของคุณ หรือติดต่อผู้ดูแลระบบ",
  // ADR 0418 D11 — super admin org form, brand by domain
  "hostBrand.section": "แบรนด์ตามโดเมน",
  "hostBrand.domains": "โดเมน",
  "hostBrand.domainPlaceholder": "เช่น cca.deepinnoline.com",
  "hostBrand.addDomain": "เพิ่มโดเมน",
  "hostBrand.domainInvalid": "ไม่ใช่ชื่อโดเมนที่ใช้ได้",
  "hostBrand.domainsHint": "โดเมนหนึ่งชื่อเป็นขององค์กรเดียว · ต้องตั้ง DNS และเพิ่มโดเมนใน Firebase ด้วย",
  "hostBrand.loginTitle": "ชื่อบนหน้าล็อกอิน",
  "hostBrand.browserTitle": "ชื่อแท็บเบราว์เซอร์",
  "hostBrand.fallbackHint": "เว้นว่าง = ใช้ชื่อองค์กร",
  "hostBrand.logo": "โลโก้",
  "hostBrand.favicon": "ไอคอนแท็บ (favicon)",
  "hostBrand.upload": "อัปโหลด",
  "hostBrand.replace": "เปลี่ยนไฟล์",
  "hostBrand.remove": "เอาออก",
  "hostBrand.pngOnly": "PNG เท่านั้น · โลโก้ 512×512 · ไอคอน 64×64",
  "hostBrand.legacyLogoUrl": "URL โลโก้ (แบบเดิม — ใช้เมื่อไม่มีไฟล์ที่อัปโหลด)",
  "hostBrand.loginOtp": "ปุ่มเข้าสู่ระบบด้วย OTP",
  "hostBrand.poweredBy": "บรรทัด \"Powered by\" และเลขเวอร์ชัน",
  "hostBrand.flagDefault": "ตามค่าของเว็บ",
  "hostBrand.flagShow": "แสดง",
  "hostBrand.flagHide": "ซ่อน",
  "hostBrand.behaviourHint": "มีผลกับหน้าเข้าสู่ระบบขององค์กรนี้ ไม่ต้อง build ใหม่ · \"ตามค่าของเว็บ\" = ใช้ค่าเดิมของเว็บ",
  "selectOrg.searchPlaceholder": "ค้นหาองค์กร",

  // ── analytics — Reports · AI Copilot ───────────────────────────────────
  "analytics.loadFailed": "โหลดรายงานไม่สำเร็จ",
  "analytics.routeOnTime": "ตรงเวลาตามเส้นทาง (สด)",
  "analytics.driverPerf": "ผลงานคนขับ (สด)",
  "analytics.fleetBreakdown": "สัดส่วนสถานะรถ (สด)",
  "analytics.backToReports": "กลับไปหน้ารายงาน",
  "analytics.newTemplate": "แม่แบบใหม่",
  "analytics.reportName": "ชื่อรายงาน",
  "analytics.removeFilter": "ลบตัวกรอง",
  "analytics.thinking": "กำลังคิด…",
  "analytics.sendMessage": "ส่งข้อความ",

  // ── safety — Safety & Cases · vehicle handover ─────────────────────────
  "safety.title": "ความปลอดภัยและเคส",
  "safety.loadFailed": "โหลดข้อมูลเคสไม่สำเร็จ",
  "safety.description": "รายละเอียด",
  "safety.linkedRecord": "รายการที่เชื่อมโยง",
  "safety.workflow": "ขั้นตอนดำเนินการ",
  "safety.sendToHr": "ส่งให้ฝ่ายบุคคล",
  "safety.classifyWarn": "จัดประเภทและออกหนังสือเตือน",
  "safety.markSigned": "บันทึกว่าลงนามแล้ว",
  "safety.recordPayment": "บันทึกการชำระเงิน",
  "safety.addEvidence": "เพิ่มหลักฐาน",
  "safety.addNote": "เพิ่มหมายเหตุ",
  "safety.newCase": "เปิดเคสใหม่",
  "safety.caseTitle": "หัวข้อ",
  "safety.titlePlaceholder": "หัวข้อเคสโดยย่อ",
  "safety.titleRequired": "จำเป็นต้องกรอกหัวข้อ",
  "safety.category": "ประเภท",
  "safety.severity": "ระดับความรุนแรง",
  "safety.describeIncident": "อธิบายเหตุการณ์…",
  "safety.searchPlaceholder": "ค้นหาเลขเคส พนักงาน ความผิด…",
  "safety.offense": "ความผิด",
  "safety.evidence": "หลักฐาน",
  "safety.link": "ลิงก์",
  "safety.noMatch": "ไม่มีเคสที่ตรงกับตัวกรอง",

  "handover.title": "ส่งมอบ / คืนรถ",
  "handover.loadFailed": "โหลดข้อมูลการส่งมอบไม่สำเร็จ",
  "handover.newInspection": "ตรวจสภาพใหม่",
  "handover.vehicle": "รถ",
  "handover.selectVehicle": "เลือกรถ…",
  "handover.plateNumber": "หมายเลขทะเบียน",
  "handover.driver": "คนขับ",
  "handover.selectDriver": "เลือกคนขับ…",
  "handover.type": "ประเภท",
  "handover.handover": "ส่งมอบ",
  "handover.return": "คืนรถ",
  "handover.notes": "หมายเหตุ",
  "handover.notesPlaceholder": "หมายเหตุการตรวจสภาพ…",
  "handover.checklist": "รายการตรวจสภาพ",
  "handover.damageSummary": "สรุปความเสียหาย",
  "handover.signatures": "ลายเซ็นดิจิทัล",
  "handover.searchPlaceholder": "ค้นหารถ คนขับ ผู้ตรวจ…",
  "handover.inspector": "ผู้ตรวจ",

  // ── system — Settings · Roles · Integrations · Master Data ─────────────
  "common.dismiss": "ปิดแจ้งเตือน",
  "common.description": "รายละเอียด",
  "common.remove": "นำออก",
  "common.duplicate": "ทำสำเนา",
  "common.label": "ป้ายกำกับ",
  "common.code": "รหัส",
  "common.color": "สี",
  "common.select": "เลือก…",
  "common.closeForm": "ปิดฟอร์ม",
  "settings.thresholds": "เกณฑ์การปฏิบัติงาน",
  "settings.booking": "ตั้งค่าการจอง",
  "settings.idPattern": "รูปแบบรหัส",
  "settings.entity": "ประเภทข้อมูล",
  "settings.pattern": "รูปแบบ",
  "settings.preview": "ตัวอย่าง",
  "settings.integrations": "การเชื่อมต่อภายนอก",
  "settings.featureFlags": "ฟีเจอร์ที่เปิดใช้",
  "settings.notifChannels": "ช่องทางแจ้งเตือน",
  "settings.event": "เหตุการณ์",
  "settings.push": "Push",
  "settings.sms": "SMS",
  "settings.prefix": "คำนำหน้า",
  "settings.prefixLabel": "ป้ายคำนำหน้า",
  "settings.organization": "องค์กร",
  "settings.slug": "Slug",
  "settings.module": "โมดูล",
  "settings.plan": "แพ็กเกจ",
  "roles.title": "บทบาทและสิทธิ์",
  "roles.loadFailed": "โหลดบทบาทไม่สำเร็จ",
  "roles.name": "ชื่อบทบาท",
  "roles.briefDesc": "คำอธิบายสั้น ๆ",
  "roles.permissions": "สิทธิ์การใช้งาน",
  "roles.editPermissions": "แก้ไขสิทธิ์",
  "roles.newRole": "สร้างบทบาท",
  "integ.title": "ศูนย์เชื่อมต่อ",
  "integ.subtitle": "API Key · Webhook · แม่แบบแจ้งเตือน",
  "integ.generateKey": "สร้าง API Key",
  "integ.keyCreated": "สร้าง API Key แล้ว",
  "integ.copyNow": "คัดลอกคีย์ตอนนี้ ระบบจะไม่แสดงอีก",
  "integ.revoke": "เพิกถอน",
  "integ.addWebhook": "เพิ่ม Webhook",
  "integ.webhookSecret": "รหัสลับ Webhook",
  "integ.rotateSecret": "เปลี่ยน secret",
  "integ.deleteWebhook": "ลบ Webhook",
  "masterData.newCategory": "หมวดใหม่",
  "masterData.protected": "หมวดสงวน แก้ไขไม่ได้",
  "members.cancelInvite": "ยกเลิกคำเชิญ",

  // ── monitoring — War Room · Communications ─────────────────────────────
  "common.retry": "ลองใหม่",
  "common.details": "รายละเอียด",
  "common.clearSearch": "ล้างคำค้น",
  "common.closeSearch": "ปิดการค้นหา",
  "common.searching": "กำลังค้นหา…",
  "warroom.loadingOverview": "กำลังโหลดภาพรวม…",
  "warroom.noPositions": "ยังไม่มีตำแหน่งรถ",
  "warroom.positionsHint": "ตำแหน่งจะขึ้นเมื่อคนขับเริ่มส่ง GPS",
  "warroom.mode": "โหมด",
  "warroom.searchBoth": "ค้นหาทะเบียน · คนขับ · งาน · เส้นทาง",
  "warroom.quickEdit": "แก้ไขงานด่วน",
  "warroom.lateThreshold": "เกณฑ์สาย (นาที)",
  "warroom.assignment": "การมอบหมาย",
  "warroom.searchVehicle": "ค้นหารถจากทะเบียน รหัส ประเภท…",
  "warroom.searchDriver": "ค้นหาคนขับจากชื่อ รหัส ใบขับขี่…",
  "warroom.routeStops": "เส้นทางและจุดจอด",
  "warroom.searchTemplate": "ค้นหาเส้นทาง…",
  "warroom.jobLabel": "ชื่อใบงาน (ไม่บังคับ)…",
  "warroom.bookingSeats": "การจองและที่นั่ง",
  "warroom.boarding": "การขึ้นรถ",
  "warroom.tracking": "การติดตาม",
  "warroom.payRevenue": "ค่าตอบแทนและรายได้",
  "warroom.pb.nJobs": "{n} งาน",
  "warroom.pb.all": "ทั้งหมด",
  "warroom.pb.search": "ค้นหาเลขงาน · คนขับ · ทะเบียน",
  "warroom.pb.searchNone": "ไม่พบ \"{q}\" ใน {day}",
  "warroom.pb.searchHead": "ผลการค้นหา \"{q}\"",
  "warroom.pb.searchMin": "พิมพ์อย่างน้อย 2 ตัวอักษร",
  "warroom.pb.searchMore": "แสดง {n} งานแรก · พิมพ์ให้ชัดขึ้น",
  "warroom.pb.searchError": "ค้นหาไม่สำเร็จ",
  "warroom.pb.lineLegend": "เส้นบนแผนที่",
  "warroom.pb.lineDriven": "วิ่งแล้ว",
  "warroom.pb.lineAhead": "เส้นที่จะวิ่ง",
  "warroom.pb.linePlan": "แผนเส้นทาง",
  "warroom.pb.noneInScope": "ไม่มีงานตรงตัวกรอง",
  "warroom.pb.head": "ย้อนดู",
  "warroom.pb.clearSelectedJob": "ล้างใบงานที่เลือก",
  "warroom.pb.loadingMore": "กำลังโหลด…",
  "warroom.pb.vehicleLabel": "รถ",
  "warroom.pb.noVehiclesDay": "{day} ไม่มีรถวิ่ง",
  "warroom.pb.vehiclesError": "โหลดรายชื่อรถไม่สำเร็จ",
  "warroom.pb.emptyDay": "{day} ไม่มีงาน",
  "warroom.pb.changeDay": "เลือกวันอื่น",
  "warroom.pb.dayOutOfRange": "วันที่ในลิงก์อยู่นอกช่วงที่มีข้อมูล — กลับไปวันนี้",
  "warroom.pb.pickDay": "เลือกวันที่ย้อนดู",
  "warroom.pb.clearFilters": "ล้างตัวกรอง",
  "warroom.pb.vehiclesOfDay": "รถที่วิ่ง {day} · {n} คัน",
  "warroom.pb.listError": "โหลดรายการงานไม่สำเร็จ",
  "warroom.pb.loadingRoutes": "กำลังโหลดเส้นทาง…",
  "warroom.pb.noGpsToday": "วันนี้ไม่มีข้อมูล GPS",
  "warroom.pb.noGpsOn": "{day} ไม่มีข้อมูล GPS",
  "warroom.pb.routesError": "โหลดเส้นทางไม่สำเร็จ",
  "warroom.pb.noGpsJob": "งานนี้ไม่มีประวัติ GPS",
  "warroom.pb.noGpsVehicle": "รถคันนี้ไม่มีประวัติ GPS ในวันนี้",
  "warroom.pb.notFound": "ไม่พบรถหรืองานนี้ในวันที่เลือก",
  "warroom.pb.replay": "เล่นใหม่",
  "warroom.pb.jump": "GPS กระโดด {km} กม. ใน {min} นาที — ไม่นับเป็นเส้นทาง",
  "warroom.pb.points": "{n} จุด",
  "warroom.pb.hideList": "ซ่อนรายการ",
  "warroom.pb.showList": "แสดงรายการ · {n} งาน",
  "warroom.pb.backToList": "← งาน",
  "warroom.pb.speed": "ความเร็ว",
  "warroom.pb.breadcrumb": "ระดับที่ดูอยู่",
  "warroom.pb.timeline": "แถบเวลา",
  "warroom.pb.stop": "จุดจอด {n}",
  "warroom.pb.noArrivals": "งานนี้ไม่มีเวลาถึงจุดจอดที่บันทึกไว้",
  "warroom.pb.dayActivity": "กิจกรรมทั้งวัน",
  "warroom.pb.stUnsettled": "ค้างไม่ปิด",
  "warroom.pb.stNotRun": "ไม่ได้ออกวิ่ง",
  "warroom.pb.noneInFilter": "ไม่มีงานสถานะที่เลือก",
  "warroom.pb.othersExist": "มี {n} งานสถานะอื่น",
  "warroom.pb.showAllStatuses": "แสดงทุกสถานะ",
  "warroom.pb.outsideFilter": "นอกตัวกรอง",
  "warroom.pb.today": "วันนี้",
  "warroom.pb.noJob": "วิ่งแต่ไม่มีใบงาน",
  "warroom.pb.nVehicles": "{n} คัน",
  "warroom.pb.truncated": "ย่อข้อมูลเพื่อความเร็ว",
  "warroom.pb.playAll": "▶ ดูทั้งหมดบนแผนที่",
  "warroom.searchAll": "ค้นหาเส้นทาง งาน รถ คนขับ… เช่น “70-33”, “JT-”",
  "warroom.clearVehicleFilter": "ล้างตัวกรองรถ",
  "warroom.clearFocusedJob": "ล้างงานที่เลือก",
  "warroom.fleetStats": "สถิติกองรถ",
  "warroom.tripsToday": "งานวันนี้",
  "warroom.plateDriverJobRoute": "ทะเบียน · คนขับ · งาน · เส้นทาง",
  "warroom.allContracts": "ทุกสัญญา",
  "warroom.allRoutes": "ทุกเส้นทาง",
  "warroom.noStopData": "ไม่มีข้อมูลจุดจอด",
  "warroom.loadingTicket": "กำลังโหลดใบงาน…",
  "warroom.liveStatus": "สถานะสด",
  "warroom.call": "โทร",
  "warroom.chat": "แชท",
  "warroom.contract": "สัญญา",
  "warroom.route": "เส้นทาง",
  "warroom.schedule": "ตารางเวลา",
  "warroom.fleet": "รถ",
  "warroom.draft": "ร่าง",

  // ── fleet — vehicles, maintenance, insurance, GPS contracts ────────────
  "common.previous": "ก่อนหน้า",
  "common.priority": "ความสำคัญ",
  "common.category": "หมวดหมู่",
  "common.photo": "รูปภาพ",
  "common.province": "จังหวัด",
  "common.notes": "หมายเหตุ",
  "common.itemsPerPage": "จำนวนต่อหน้า",
  "common.optionalNote": "หมายเหตุ (ไม่บังคับ)",
  "common.additionalDetails": "รายละเอียดเพิ่มเติม…",
  "common.keep": "เก็บไว้",
  "fleet.backToFleet": "กลับไปหน้ารถ",
  "fleet.searchVehicle": "ค้นหารถจากทะเบียน รหัส…",
  "fleet.searchAll": "ค้นหารหัส ทะเบียน คนขับ จังหวัด ประเภท…",
  "fleet.licensePlate": "หมายเลขทะเบียน",
  "fleet.plateRequired": "จำเป็นต้องกรอกทะเบียน",
  "fleet.vehicleId": "รหัสรถ",
  "fleet.vehicleIdRequired": "จำเป็นต้องกรอกรหัสรถ",
  "fleet.willBeSavedAs": "จะถูกบันทึกเป็น",
  "fleet.vehicleType": "ชนิดรถ",
  "fleet.seats": "จำนวนที่นั่ง",
  "fleet.ownership": "ประเภทความเป็นเจ้าของ",
  "fleet.primaryDriver": "คนขับประจำ",
  "fleet.searchDriver": "ค้นหาคนขับจากชื่อหรือรหัส…",
  "fleet.searchDepot": "ค้นหาจุดจอด / ที่จอดรถ…",
  "fleet.selectProvince": "เลือกจังหวัด",
  "fleet.dueDate": "กำหนดวันครบ",
  "fleet.shop": "อู่",
  "fleet.workshopName": "ชื่ออู่",
  "fleet.endAfterStart": "วันสิ้นสุดต้องไม่ก่อนวันเริ่ม",
  "fleet.expiryAfterStart": "วันหมดอายุต้องไม่ก่อนวันเริ่ม",
  "fleet.deviceId": "รหัสอุปกรณ์",
  "ops.driverLabel": "คนขับ",
  "fleet.deviceModel": "รุ่นอุปกรณ์",
  "fleet.simNumber": "หมายเลขซิม",
  "fleet.billingCycle": "รอบบิล",
  "fleet.gpsContracts": "สัญญา GPS",
  "fleet.editContract": "แก้ไขสัญญา",
  "fleet.issueExpiryRequired": "จำเป็นต้องกรอกวันออกและวันหมดอายุ",
  "fleet.providerRequired": "จำเป็นต้องกรอกผู้ให้บริการ",
  "fleet.policyRequired": "จำเป็นต้องกรอกเลขกรมธรรม์",
  "fleet.insurerRequired": "จำเป็นต้องกรอกบริษัทประกัน",
  "fleet.coverageType": "ประเภทความคุ้มครอง",
  "fleet.premiumThb": "เบี้ยประกัน (บาท)",
  "fleet.coverageClass": "ชั้นประกัน",
  "fleet.insClass1": "ชั้น 1",
  "fleet.insClass2Plus": "ชั้น 2+",
  "fleet.insClass2": "ชั้น 2",
  "fleet.insClass3Plus": "ชั้น 3+",
  "fleet.insClass3": "ชั้น 3",
  "fleet.sumInsured": "ทุนประกัน",
  "fleet.sumInsuredThb": "ทุนประกัน (บาท)",
  "fleet.deductible": "ค่าเสียหายส่วนแรก",
  "fleet.deductibleThb": "ค่าเสียหายส่วนแรก (บาท)",
  "fleet.repairType": "ประเภทการซ่อม",
  "fleet.repairDealer": "ซ่อมห้าง",
  "fleet.repairGarage": "ซ่อมอู่",
  "fleet.namedDriver": "การระบุผู้ขับขี่",
  "fleet.namedDriverYes": "ระบุผู้ขับขี่",
  "fleet.namedDriverNo": "ไม่ระบุผู้ขับขี่",
  "fleet.coverUnspecified": "ยังไม่ระบุ",
  "fleet.coverDetails": "ความคุ้มครอง",
  "fleet.insurancePolicies": "กรมธรรม์ประกันภัย",
  "fleet.cmiPolicies": "พ.ร.บ. (ประกันภาคบังคับ)",
  "fleet.addCmi": "เพิ่ม พ.ร.บ.",
  "fleet.noCmi": "รถคันนี้ยังไม่มี พ.ร.บ. ในระบบ",
  "fleet.commercialPolicies": "ประกันภัยภาคสมัครใจ",
  "nav.cmi": "พ.ร.บ.",
  "fleet.editPolicy": "แก้ไขกรมธรรม์",
  "fleet.deletePolicy": "ลบกรมธรรม์",
  "fleet.editWorkOrder": "แก้ไขใบสั่งงาน",
  "fleet.searchPmPlaceholder": "ค้นหารถ ประเภทบริการ อู่…",
  "fleet.pmTitle": "บำรุงรักษาตามแผน",
  "fleet.pmLoadFailed": "โหลดรอบบำรุงรักษาไม่สำเร็จ",
  "fleet.pmEdit": "แก้ไขแผนบำรุงรักษา",
  "fleet.pmDelete": "ลบแผนบำรุงรักษา",
  "fleet.pmDeleteQ": "ลบแผนบำรุงรักษานี้?",
  "fleet.deleteWorkOrderQ": "ลบใบงานซ่อมนี้?",
  "fleet.woDeleteFailed": "ลบใบงานซ่อมไม่สำเร็จ",
  "toast.workOrderDeleted": "ลบใบงานซ่อมแล้ว",
  "fleet.pmDeleteFailed": "ลบแผนบำรุงรักษาไม่สำเร็จ",
  "toast.pmDeleted": "ลบแผนบำรุงรักษาแล้ว",
  "fleet.pmNone": "ไม่พบแผนบำรุงรักษา",
  "fleet.addRow": "เพิ่มแถว",
  "fleet.removeRow": "ลบแถว",
  "fleet.addColumn": "เพิ่มคอลัมน์",
  "fleet.removeColumn": "ลบคอลัมน์",
  "fleet.removeGroup": "ลบกลุ่ม",
  "fleet.removeItem": "ลบรายการ",

  // ── people — Employees · Leave · Salary · Payroll · Driver Pay ────────
  "common.prev": "ก่อนหน้า",
  "common.type": "ประเภท",
  "common.reason": "เหตุผล",
  "common.effectivePeriod": "ช่วงที่มีผล",
  "common.openEnded": "ไม่มีกำหนดสิ้นสุด",
  "common.amount": "จำนวนเงิน",
  "common.source": "แหล่งที่มา",
  "common.item": "รายการ",
  "common.restricted": "จำกัดสิทธิ์",
  "common.reject": "ปฏิเสธ",
  "common.verified": "ตรวจสอบ",
  "common.rejected": "ถูกปฏิเสธ",
  "common.pending": "รอดำเนินการ",
  "hr.fullName": "ชื่อ-นามสกุล",
  "hr.nameRequired": "จำเป็นต้องกรอกชื่อ",
  "hr.nameEn": "ชื่อ (อังกฤษ)",
  "hr.nameEnPlaceholder": "ชื่อภาษาอังกฤษ (ไม่บังคับ)",
  "hr.age": "อายุ",
  "hr.gender": "เพศ",
  "hr.phone": "เบอร์โทร",
  "hr.branch": "สาขา",
  "hr.department": "แผนก",
  "hr.position": "ตำแหน่ง",
  "hr.zone": "โซน",
  "hr.minChars8": "อย่างน้อย 8 ตัวอักษร",
  "hr.selectEmployee": "เลือกพนักงาน",
  "hr.removeMember": "นำสมาชิกออก",
  "hr.deductions": "รายการหัก",
  "hr.noSalaryHistory": "ยังไม่มีประวัติเงินเดือน",
  "hr.pipeline": "ขั้นตอนการรับสมัคร",
  "hr.deleteDocument": "ลบเอกสารนี้?",
  "hr.idCards": "บัตรประชาชน",
  "hr.payeeInfo": "ข้อมูลผู้รับเงิน",
  "hr.addBehavior": "เพิ่มบันทึกพฤติกรรม",
  "hr.behaviorHistory": "ประวัติพฤติกรรม",
  "hr.issueDate": "วันที่ออก",
  "hr.notApprovedExcluded": "ยังไม่อนุมัติ — ไม่รวมในยอดสุทธิ",
  "hr.removeCondition": "ลบเงื่อนไข",

  // ── operations — Contracts · Routes · Tickets · Schedule · Booking ────
  "common.prevPage": "หน้าก่อนหน้า",
  "common.nextPage": "หน้าถัดไป",
  "common.saveChanges": "บันทึกการแก้ไข",
  "common.saved": "บันทึกแล้ว",
  "common.customer": "ลูกค้า",
  "common.start": "เริ่ม",
  "common.end": "สิ้นสุด",
  "ops.routes": "เส้นทาง",
  // ADR 0235 — ป้ายกำกับเวลาของเส้นทาง
  "ops.timePresets": "เวลาที่ถูกใช้บ่อย",
  "ops.addTimePreset": "เพิ่มชุดเวลา",
  "ops.noTimePresets": "ยังไม่มีชุดเวลาที่ใช้บ่อย",
  "ops.timePresetNamePlaceholder": "เช่น รอบเช้า",
  "ops.timePresetNameRequired": "ชุดเวลาต้องมีชื่อ",
  "ops.timePresetDuplicate": "ชื่อชุดเวลาซ้ำ: {name}",
  "ops.timePresetTimeInvalid": "เวลาของ {name} ไม่ถูกต้อง",
  "ops.timePresetLateInvalid": "เกณฑ์สายของ {name} ต้องอยู่ระหว่าง 1-240 นาที",
  "ops.lateThresholdMin": "เกณฑ์สาย (นาที)",
  "ops.timeGates": "เกณฑ์เวลา",
  "ops.acceptOpenBefore": "เปิดให้ยืนยันล่วงหน้าก่อนเวลายืนยัน (นาที)",
  "ops.cancelCutoffBefore": "ปิดรับยกเลิกก่อนเริ่มงาน (นาที)",
  "ops.timePresetPickRouteFirst": "เลือกเส้นทางก่อน",
  "ops.timePresetPick": "เวลาที่ถูกใช้บ่อย",
  "ops.timePresetPickPlaceholder": "เลือกชุดเวลา…",
  "ops.timePresetLockedHint": "คอลัมน์นี้มีใบงานแล้ว เปลี่ยนเวลาไม่ได้",
  "common.enable": "เปิดใช้งาน",
  "common.disable": "ปิดใช้งาน",
  "ops.stopsPickDrop": "จุดจอดรับ-ส่ง",
  "ops.viaPoints": "จุดผ่าน",
  "ops.stops": "จุดจอด",
  "ops.checkinMethod": "วิธีเช็คอิน",
  "ops.jobType": "ประเภทงาน",
  "ops.bookingMode": "รูปแบบการจอง",
  "ops.renameBoard": "เปลี่ยนชื่อบอร์ด",
  "ops.deleteTrip": "ลบเที่ยววิ่ง",
  "ops.deposit": "เงินมัดจำ",
  "ops.searchContracts": "ค้นหาสัญญา…",
  "ops.booked": "จองแล้ว",
  "ops.contactPerson": "ชื่อผู้ติดต่อ",
  "ops.jobLabel": "ชื่อใบงาน",
  "ops.beforeCutoff": "ก่อนเวลาปิดรับ (2 ชม. ก่อนออกเดินทาง)",
  "ops.searchEmployee": "ค้นหาพนักงาน",

  "common.firstPage": "หน้าแรก",
  "common.lastPage": "หน้าสุดท้าย",
  "common.sort": "เรียง",
  "common.key": "คีย์",
  "common.value": "ค่า",
  "common.file": "ไฟล์",
  "common.uploaded": "อัปโหลดแล้ว",
  "common.failed": "ไม่สำเร็จ",
  "common.documents": "เอกสาร",
  "common.unsavedChanges": "มีการแก้ไขที่ยังไม่บันทึก",
  "common.generalInfo": "ข้อมูลทั่วไป",
  "common.information": "ข้อมูล",
  "common.optionalDesc": "คำอธิบาย (ไม่บังคับ)…",
  "common.saveAnyway": "บันทึกต่อไป",
  "ops.searchByNameId": "ค้นหาชื่อ รหัส แผนก…",
  "ops.searchPassenger": "ค้นหาผู้โดยสาร",
  "ops.guestName": "ชื่อผู้โดยสารทั่วไป",
  "ops.nameOptional": "ชื่อ (ไม่บังคับ)",
  "ops.idOrPhone": "รหัส / เบอร์โทร",
  "ops.optionalRef": "เลขอ้างอิง (ไม่บังคับ)",
  "ops.documentName": "ชื่อเอกสาร",
  "ops.searchPlace": "ค้นหาสถานที่หรือที่อยู่…",
  "ops.moveUp": "เลื่อนขึ้น",
  "ops.moveDown": "เลื่อนลง",
  "ops.removeStop": "ลบจุดจอด",
  "ops.latitude": "ละติจูด",
  "ops.longitude": "ลองจิจูด",
  // ADR 0222 — บอกเหตุผลเมื่อการวางพิกัดถูกปฏิเสธ (ก่อนหน้านี้เงียบสนิท)
  "ops.pasteSwapped": "พิกัดที่วางดูเหมือนสลับลำดับกัน",
  "ops.pasteSwapAction": "ใช้ {lat}, {lng}",
  "ops.pasteUnreadable": "อ่านค่าพิกัดจากสิ่งที่วางไม่ได้ — ต้องเป็น ละติจูด, ลองจิจูด",
  "ops.speedLimit": "จำกัดความเร็ว (กม./ชม.)",
  "ops.checkinRadius": "รัศมีเช็คอิน (ม.)",
  "ops.serviceDuration": "เวลาให้บริการ (นาที)",
  "ops.revenue": "รายได้",
  "ops.driverCompensation": "ค่าตอบแทนคนขับ",
  "ops.searchChargeType": "ค้นหาประเภทค่าใช้จ่าย…",
  "ops.detailsPlaceholder": "รายละเอียด…",
  "ops.direction": "ทิศทาง",
  "ops.selectDirection": "เลือกทิศทาง…",
  "ops.receipt": "ใบเสร็จ",
  "ops.clearSelectedFile": "ล้างไฟล์ที่เลือก",
  "ops.selectStop": "เลือกจุดจอด…",
  "ops.cancelBooking": "ยกเลิกการจอง",
  "ops.actual": "จริง",
  "ops.noJobsFromBoard": "ยังไม่มีใบงานที่สร้างจากบอร์ดนี้",
  "ops.fitAllRoutes": "แสดงทุกเส้นทาง",
  "ops.period": "ช่วงเวลา",
  "ops.routeTerms": "เงื่อนไขเส้นทาง",
  "ops.pricePerTrip": "ราคา/เที่ยว",
  "ops.effective": "มีผลตั้งแต่",
  "ops.searchRoutes": "ค้นหาเส้นทาง…",
  "ops.jobId": "รหัสใบงาน",
  "ops.noTicketsForDate": "ไม่มีใบงานในวันนี้",
  "ops.searchNameNotes": "ค้นหาชื่อ รหัส ผู้ติดต่อ เบอร์ อีเมล หมายเหตุ…",
  "ops.customerCode": "รหัสลูกค้า",
  "ops.customerNo": "เลขที่ลูกค้า",
  "ops.ticketCustomers": "ลูกค้าทั้งหมด",
  "ops.sharedContracts": "สัญญาร่วม",
  "ops.customerNoColumn": "เลขที่",
  "ops.activeContractsShort": "ใช้งาน {n}",
  "ops.endedContractsShort": "จบแล้ว {n}",
  "ops.noContracts": "ไม่มีสัญญา",
  "ops.contractCount": "จำนวนสัญญา",
  "ops.expiringContractsShort": "ใกล้หมด {n}",
  "ops.expiringContractsCard": "สัญญาใกล้หมดอายุ",
  "ops.pctOfAll": "{pct}% ของทั้งหมด",
  "ops.inclNoContracts": "รวมลูกค้าที่ยังไม่มีสัญญา {n} ราย",
  "ops.expiringWithin30": "มีสัญญาหมดใน ≤ 30 วัน",
  "ops.unitCustomers": "ราย",
  "ops.editContractSoon": "แก้ไขสัญญา — จะมีในรุ่นถัดไป",
  "ops.extendContractSoon": "ต่อสัญญา — จะมีในรุ่นถัดไป",
  "ops.suspendContractSoon": "ระงับสัญญา — จะมีในรุ่นถัดไป",

  "common.members": "สมาชิก",
  "common.contracts": "สัญญา",
  "common.title": "หัวข้อ",
  "hr.jobTitle": "ตำแหน่งงาน",
  "hr.hireDateRequired": "จำเป็นต้องกรอกวันเริ่มงาน",
  "hr.assignedVehicle": "รถที่ผูก",
  "hr.searchVehicleIdPlate": "ค้นหารถจากรหัสหรือทะเบียน…",
  "hr.searchEmployeeAll": "ค้นหาชื่อ อีเมล รหัส เบอร์โทร ตำแหน่ง รถ…",
  "hr.teamLoadFailed": "โหลดทีมคนขับไม่สำเร็จ",
  "hr.teamsLoadFailed": "โหลดรายการทีมคนขับไม่สำเร็จ",
  "hr.searchDriverNameId": "ค้นหาคนขับจากชื่อ รหัส…",
  "hr.driverId": "รหัสคนขับ",
  "hr.noMembersYet": "ยังไม่มีสมาชิกในทีมนี้",
  "hr.removeMemberQ": "นำสมาชิกออก?",
  "hr.driverTeams": "ทีมคนขับ",
  "hr.contractIds": "รหัสสัญญา",
  "hr.teamDetails": "รายละเอียดทีม…",
  "hr.searchTeam": "ค้นหาทีม โซน รายละเอียด…",
  "hr.editTeam": "แก้ไขทีม",
  "hr.deactivateTeam": "ปิดใช้งานทีม",
  "hr.deactivate": "ปิดใช้งาน",
  "hr.deactivateTeamQ": "ปิดใช้งานทีมคนขับ?",
  "hr.importSalaryCsv": "นำเข้าเงินเดือน (CSV)",
  "hr.importEmployeesCsv": "นำเข้าพนักงาน (CSV)",
  "hr.clickReplaceFile": "คลิกเพื่อเปลี่ยนไฟล์",
  "hr.dropCsv": "ลากไฟล์ CSV มาวางที่นี่",
  "hr.currentRules": "กติกาปัจจุบัน",
  "hr.allowances": "เบี้ยเลี้ยง",
  "hr.salaryHistory": "ประวัติเงินเดือน",
  "hr.salaryLoadFailed": "โหลดข้อมูลเงินเดือนไม่สำเร็จ",
  "hr.salaryHrOnly": "กติกาเงินเดือนเปิดให้เฉพาะผู้ดูแลฝ่ายบุคคล",
  "hr.salaryRules": "กติกาเงินเดือน",
  "hr.searchNameEmpId": "ค้นหาชื่อ รหัสพนักงาน…",
  "hr.editRules": "แก้ไขกติกา",
  "hr.noSalaryMatch": "ไม่มีกติกาเงินเดือนที่ตรงกับตัวกรอง",
  "hr.noCandidates": "ไม่มีผู้สมัคร",
  "hr.rejectCandidate": "ปฏิเสธผู้สมัคร",
  "hr.deleteCandidateQ": "ลบผู้สมัคร?",
  "hr.offerBeforeOnboarding": "ต้องตอบรับข้อเสนอก่อนจึงจะเข้าสู่ขั้นตอนรับเข้าทำงานได้",
  "hr.applicationInfo": "ข้อมูลใบสมัคร",
  "hr.interview": "สัมภาษณ์",
  "hr.notYetScheduled": "ยังไม่ได้นัดหมาย",
  "hr.offer": "ข้อเสนอ",
  "hr.markDeclined": "บันทึกว่าปฏิเสธ",
  "hr.noOfferYet": "ยังไม่มีข้อเสนอ",
  "hr.rejectionReason": "เหตุผลที่ปฏิเสธ",
  "hr.editPosting": "แก้ไขประกาศ",
  "hr.addCandidate": "เพิ่มผู้สมัคร",
  "hr.deletePostingQ": "ลบประกาศ?",
  "hr.recruitmentPipeline": "ขั้นตอนรับสมัครงาน",
  "hr.searchPosting": "ค้นหาหัวข้อ สถานที่ แผนก…",

  "common.address": "ที่อยู่",
  "common.view": "ดู",
  "common.replace": "แทนที่",
  "common.titleRequired": "จำเป็นต้องกรอกหัวข้อ",
  "fleet.loadFailed": "โหลดข้อมูลรถไม่สำเร็จ",
  "fleet.row": "แถว",
  "fleet.col": "คอลัมน์",
  "fleet.cellType": "ประเภทช่อง",
  "fleet.clickCell": "คลิกที่ช่องเพื่อเปลี่ยนประเภท",
  "fleet.seatAutoLabel": "ตั้งชื่อที่นั่งอัตโนมัติ (A1, B2 ฯลฯ)",
  "fleet.deviceSerial": "หมายเลขเครื่องหรือ IMEI",
  "fleet.simPhone": "ซิม / เบอร์โทร",
  "fleet.costThb": "ค่าใช้จ่าย (บาท)",
  "fleet.costExclVat": "ค่าใช้จ่ายไม่รวม VAT (บาท)",
  "fleet.gpsLoadFailed": "โหลดข้อมูล GPS ไม่สำเร็จ",
  "fleet.addContract": "เพิ่มสัญญา",
  "fleet.searchGps": "ค้นหารถ ผู้ให้บริการ รุ่น…",
  "fleet.noGpsContracts": "ไม่พบสัญญา GPS",
  "fleet.noGpsForVehicle": "รถคันนี้ไม่มีสัญญา GPS",
  "fleet.deactivateVehicleQ": "ปิดใช้งานรถคันนี้?",
  "fleet.deactivateVehicleHint": "รถจะถูกทำเครื่องหมายว่าไม่ใช้งาน เปิดใช้งานใหม่ได้ภายหลัง",
  "fleet.generalInfo": "ข้อมูลทั่วไป",
  "fleet.depotLocation": "จุดจอด",
  "fleet.coordinates": "พิกัด",
  "fleet.registrationHistory": "ประวัติการจดทะเบียน",
  "fleet.noRegistrationHistory": "ไม่มีประวัติการจดทะเบียน",
  "fleet.addRegistration": "เพิ่มการจดทะเบียน",
  "fleet.currentRegistration": "ทะเบียนปัจจุบัน",
  "fleet.uploadPhoto": "อัปโหลดรูป",
  "fleet.clickChooseImage": "คลิกเพื่อเลือกรูป",
  "fleet.photos": "รูปภาพ",
  "fleet.addPhoto": "เพิ่มรูป",
  "fleet.viewPhoto": "ดูรูป",
  "fleet.replacePhoto": "เปลี่ยนรูป",
  "fleet.deletePhoto": "ลบรูป",
  "fleet.deletePhotoQ": "ลบรูปนี้?",
  "fleet.deviceIdRequired": "จำเป็นต้องกรอกรหัสอุปกรณ์",
  "fleet.installationDate": "วันติดตั้ง",
  "fleet.startEndRequired": "จำเป็นต้องกรอกวันเริ่มและวันสิ้นสุด",
  "fleet.startExpiryRequired": "จำเป็นต้องกรอกวันเริ่มและวันหมดอายุ",
  "fleet.describeWork": "อธิบายงานที่ต้องทำ…",
  "fleet.cancelContract": "ยกเลิกสัญญา",
  "fleet.reactivateContract": "เปิดใช้สัญญาอีกครั้ง",
  "fleet.cancelGpsQ": "ยกเลิกสัญญา GPS?",
  "fleet.reactivateGpsQ": "เปิดใช้สัญญา GPS อีกครั้ง?",

  "common.loadingShort": "กำลังโหลด",
  "common.today": "วันนี้",
  "common.selectDate": "เลือกวันที่",
  "common.openCalendar": "เปิดปฏิทิน",
  "common.userMenu": "เมนูผู้ใช้",
  "common.closePreview": "ปิดตัวอย่าง",
  "common.timeline": "ไทม์ไลน์",
  "common.unassigned": "ยังไม่ระบุ",
  "ops.boardingStop": "จุดขึ้น",
  "ops.selectBoardingStop": "เลือกจุดขึ้น…",
  // ADR 0411 — ผู้โดยสารเป็นคู่ ขึ้น → ลง (คำว่า "ขึ้น/ลง" ใช้กับคน · "รับ/ส่ง" ใช้กับของ — cargo.pair.*)
  "ops.selectDropStop": "เลือกจุดลง…",
  "ops.pair.boardAt": "ขึ้นที่",
  "ops.pair.dropAt": "ลงที่",
  "ops.pair.chooseStop": "เลือกจุด",
  "ops.dropStop": "จุดลง",
  // ADR 0412 — คอลัมน์ "เส้นทาง" ที่รวมต้นทาง-ปลายทางไว้ด้วยกัน
  "ops.route.col": "เส้นทาง",
  "ops.route.noFrom": "ยังไม่มีจุดรับ",
  "ops.route.noTo": "ยังไม่มีจุดส่ง",
  "ops.route.noBoard": "ยังไม่มีจุดขึ้น",
  "ops.route.noDrop": "ยังไม่มีจุดลง",
  "ops.pair.thisStop": "จุดนี้",
  "ops.pair.why.beforePickup": "อยู่ก่อนจุดขึ้น",
  "ops.pair.noDropPair": "ไม่มีจุดลง (รายการเก่า)",
  "ops.pair.lastStopNoBoard": "จุดนี้เป็นปลายทาง ไม่มีใครขึ้นที่นี่",
  "ops.pair.lastStopHow": "ถ้าจะบันทึกคนที่ลงที่จุดนี้ ให้ไปเพิ่มที่จุดก่อนหน้า แล้วเลือก \u201Cลงที่\u201D เป็นจุดนี้",
  "ops.dropoffStop": "จุดลง",
  "ops.optionalDropoff": "จุดลง (ไม่บังคับ)…",
  "ops.passengerName": "ชื่อผู้โดยสาร",
  "ops.passengerNamePlaceholder": "ชื่อผู้โดยสาร…",
  "ops.searchPassengerAll": "ค้นหาผู้โดยสารจากชื่อ รหัส อีเมล…",
  "portal.noPortals": "ไม่มีพอร์ทัลให้ใช้งาน",
  "portal.yourPortals": "พอร์ทัลของคุณ",
  "portal.accessRestricted": "จำกัดสิทธิ์เข้าใช้งาน",
  "portal.ticketsThisWeek": "ใบงานสัปดาห์นี้",
  "portal.recentTickets": "ใบงานล่าสุด",
  "portal.bookingsLoadFailed": "โหลดข้อมูลการจองไม่สำเร็จ",
  "portal.bookings": "การจอง",
  "portal.prevDay": "วันก่อนหน้า",
  "portal.operatingDate": "วันที่เดินรถ",
  "portal.nextDay": "วันถัดไป",
  "portal.allTickets": "ใบงานทั้งหมด",
  "portal.searchBooking": "ค้นหาชื่อผู้โดยสารหรือรหัสการจอง…",
  "portal.noBookings": "ไม่พบการจอง",
  "portal.noBoardingData": "ไม่มีข้อมูลการขึ้นรถ",
  "portal.noShowPassengers": "ผู้โดยสารที่ไม่มา",
  "portal.noNoShows": "ไม่มีบันทึกผู้ไม่มา",
  "portal.totalCancelled": "ยกเลิกทั้งหมด",

  "common.from": "จาก",
  "common.progress": "ความคืบหน้า",
  "ops.stopProgress": "ความคืบหน้าจุดจอด",
  "ops.stopProgress.ours": "ในระบบ",
  "ops.stopProgress.locus": "ในระบบ Locus",
  "ops.stopTime.planned": "แผน {time}",
  // ADR 0500 D3 — grouped map pins
  "map.group.title": "{n} จุดที่ตำแหน่งเดียวกัน",
  "map.group.depotTitle": "คลัง (ออกและกลับ)",
  "map.group.depotLabel": "ออก·กลับ",
  "ops.stopTime.locusDone": "ส่งแล้ว {time} (Locus)",
  "ops.stopTime.nextDay": "(+{n} วัน)",
  "ops.stopPlaceholder": "ชั่วคราว",
  "ops.locusAwaitingOrders": "Locus ยังไม่ส่งจุดส่ง ({n} order) — ระบบจะดึงใหม่ในรอบถัดไป",
  "ops.roadLineFailed": "วาดเส้นตามถนนไม่ได้ — แสดงเส้นตรง",
  "ops.roadLineDrawing": "กำลังวาดเส้นตามถนน…",
  "ops.passengers": "ผู้โดยสาร",
  "ops.driverPay": "ค่าตอบแทนคนขับ",
  "ops.needsAttention": "ต้องตรวจสอบ",
  "ops.newTicket": "สร้างใบงาน",
  "ops.ticketsLoadFailed": "โหลดใบงานไม่สำเร็จ",
  "ops.searchTickets": "ค้นหา: เลขงาน ชื่อใบงาน เส้นทาง คนขับ ทะเบียนรถ สัญญา หมายเหตุ…",
  "ops.notScheduled": "ยังไม่ได้จัดตาราง",
  "ops.noStops": "ไม่มีจุดจอด",
  "ops.createNewTicket": "สร้างใบงานใหม่",
  "ops.selectContract": "เลือกสัญญา…",
  "ops.selectContractFirst": "เลือกสัญญาก่อน…",
  "ops.routeTemplate": "เส้นทาง",
  "ops.assignTicket": "มอบหมายใบงาน",
  "ops.assignDriverVehicle": "มอบหมายคนขับและรถให้",
  "ops.searchDriverNameId": "ค้นหาคนขับจากชื่อ รหัส…",
  "ops.searchVehiclePlateId": "ค้นหารถจากทะเบียน รหัส…",
  "ops.cancelRate": "อัตรายกเลิก",
  "ops.totalBookings": "การจองทั้งหมด",
  "ops.cancelPermissions": "สิทธิ์การยกเลิก",
  "ops.adminOps": "ผู้ดูแล / ฝ่ายปฏิบัติการ",
  "ops.cancelAnyAnytime": "ยกเลิกการจองใดก็ได้ทุกเมื่อ",
  "ops.bulkCancel": "ยกเลิกทั้งเที่ยว",
  "ops.overrideCutoff": "ข้ามเวลาปิดรับ",
  "ops.portalFactory": "พอร์ทัล (โรงงาน)",
  "ops.cancelOwnEmployees": "ยกเลิกได้เฉพาะพนักงานของตัวเอง",
  "ops.mustProvideReason": "ต้องระบุเหตุผล",
  "ops.passenger": "ผู้โดยสาร",
  "ops.cancelOwnBooking": "ยกเลิกได้เฉพาะการจองของตัวเอง",
  "ops.viaMobileApp": "ผ่านแอปมือถือ",
  "ops.cancelledBookings": "การจองที่ยกเลิก",
  "ops.noCancellations": "ไม่มีการยกเลิก",
  "ops.cancelBookingTitle": "ยกเลิกการจอง",
  "ops.reasonRequired": "เหตุผล (จำเป็น)",
  "ops.markNoShow": "บันทึกว่าไม่มา",
  "ops.newBooking": "จองใหม่",
  "ops.jobTicket": "ใบงาน",
  "ops.searchJobTicket": "ค้นหาใบงาน…",
  "ops.editContractUnavailable": "แก้ไขสัญญา (ยังไม่เปิดใช้)",
  "ops.extendContractUnavailable": "ต่อสัญญา (ยังไม่เปิดใช้)",
  "ops.suspendContractUnavailable": "ระงับสัญญา (ยังไม่เปิดใช้)",

  "hr.editPayee": "แก้ไขผู้รับเงิน",
  "hr.paymentMethod": "วิธีรับเงิน",
  "hr.expiryDate": "วันหมดอายุ",
  "hr.setTripRate": "ตั้งเรทต่อเที่ยว",
  "hr.searchContract": "ค้นหาสัญญา…",
  "hr.rateNoteHint": "หมายเหตุที่แสดงตอนเลือกเรทนี้ (ไม่บังคับ)",
  "hr.newLeaveRequest": "ขอลาใหม่",
  "hr.selectType": "เลือกประเภท",
  "hr.uploadDocument": "อัปโหลดเอกสาร",
  "hr.documentType": "ประเภทเอกสาร",
  "hr.editBaseComp": "แก้ไขค่าตอบแทนพื้นฐาน",
  "hr.annualReview": "ทบทวนประจำปี…",
  "hr.deleteChargeQ": "ลบรายการค่าใช้จ่าย?",
  "hr.overrideTicketRate": "แก้เรทใบงานเฉพาะครั้ง",
  "hr.endAfterStart": "วันสิ้นสุดต้องหลังวันเริ่ม",
  "hr.startDateRequired": "จำเป็นต้องกรอกวันเริ่ม",
  "hr.licenseNumber": "เลขที่ใบขับขี่",
  "hr.issuer": "ผู้ออก",
  "hr.deactivateEmployeeQ": "ปิดใช้งานพนักงาน?",
  "hr.deleteRecordQ": "ลบรายการนี้?",
  "hr.deleteLicenseQ": "ลบใบขับขี่?",
  "hr.deleteIdCardQ": "ลบบัตรประชาชน?",
  "hr.cancelLeaveQ": "ยกเลิกคำขอลา?",
  "hr.safetyScore": "คะแนนความปลอดภัย",
  "hr.noDocuments": "ยังไม่มีเอกสารที่อัปโหลด",
  "hr.drivingLicenses": "ใบขับขี่",
  "hr.noLicenses": "ไม่มีใบขับขี่ในระบบ",
  "hr.noIdCards": "ไม่มีบัตรประชาชนในระบบ",
  "hr.editIdCard": "แก้ไขบัตรประชาชน",
  "hr.deleteIdCard": "ลบบัตรประชาชน",
  "hr.voidVersion": "ยกเลิกเวอร์ชันนี้",
  "hr.voidRateQ": "ยกเลิกเรทนี้?",
  "hr.ticketOverrides": "แก้เรทใบงาน (ด้วยมือ)",
  "hr.noTripsWindow": "ไม่มีเที่ยววิ่งในช่วงนี้",
  "hr.periodLocked": "ปิดงวดแล้ว",

  "common.rangeOf": "ช่วง{label}",
  "common.reset": "รีเซ็ต",
  "common.createdBy": "สร้างโดย",
  "common.noteOptional": "หมายเหตุ (ไม่บังคับ)",
  "common.reasonOptional": "เหตุผล (ไม่บังคับ)",
  "common.fromDate": "ตั้งแต่วันที่",
  "common.toDate": "ถึงวันที่",
  "ops.viaPointRouting": "จุดผ่าน — ใช้คำนวณเส้นทางเท่านั้น",
  "ops.passengerType": "ประเภทผู้โดยสาร",
  "ops.pickupStop": "จุดรับ",
  "ops.whyCancelled": "ยกเลิกเพราะอะไร?",
  "ops.ticketNotFound": "ไม่พบใบงาน",
  "ops.financials": "การเงิน",
  "ops.customRate": "เรทกำหนดเอง",
  // ADR 0267 — ที่มาของค่าตอบแทนคนขับ อ่านจาก rate_tier ไม่ใช่เทียบค่า
  "ops.rateSourceRoute": "ค่าตอบแทนมาตรฐานของเส้นทาง",
  "ops.rateSourceRouteInternal": "ค่าตอบแทนมาตรฐานของเส้นทาง · คนขับภายใน",
  "ops.rateSourceRouteExternal": "ค่าตอบแทนมาตรฐานของเส้นทาง · คนขับภายนอก",
  "ops.rateSourceRouteDriver": "เรทรายบุคคลตามเส้นทาง",
  "ops.rateSourceContractDriver": "เรทรายบุคคลตามสัญญา",
  "ops.rateSourceDriverDefault": "เรทตั้งต้นรายบุคคล",
  "ops.rateSourceManual": "ระบุเองที่ใบงาน",
  // ADR 0267 P2 — เรทรายจังหวัดบนใบงาน · ยังไม่มีผลกับเงินที่จ่ายจริง
  "ops.provinceRate": "เรทรายจังหวัด",
  "ops.rateAutoOnAssign": "ค่าตอบแทนจะถูกคิดให้อัตโนมัติตามลำดับขั้น (ถึงขั้นสุดท้ายคือค่าตอบแทนมาตรฐานของเส้นทางตามประเภทคนขับ) เมื่อมอบหมายคนขับแล้ว",
  "ops.provinceRatePlaceholder": "ไม่ได้เลือก",
  "ops.kindIncome": "รายรับ",
  "ops.kindExpense": "รายจ่าย",
  "hr.driverPayPersonalRateHint": "เรทรายบุคคลตั้งที่",
  "ops.routeRate": "เรทตามเส้นทาง",
  "ops.routeBonusLegacy": "โบนัสเส้นทาง (ของเดิม)",
  "ops.operationalCharges": "ค่าใช้จ่ายปฏิบัติการ",
  "ops.addOperationalCharge": "เพิ่มค่าใช้จ่ายปฏิบัติการ",
  "ops.deleteCharge": "ลบค่าใช้จ่าย",
  "ops.editCharge": "แก้ไขค่าใช้จ่าย",
  "ops.receiptKept": "ใบเสร็จเดิมจะถูกเก็บไว้จนกว่าจะแทนที่",
  "ops.jobSettings": "ตั้งค่างาน",
  "ops.timing": "เวลา",
  "ops.assignmentCaps": "การมอบหมาย",
  "ops.detailsCaps": "รายละเอียด",
  "ops.notesCaps": "หมายเหตุ",
  "ops.boardingCaps": "การขึ้นรถ",
  "ops.whenFull": "เมื่อเต็ม",
  "ops.passengerManifest": "รายชื่อผู้โดยสาร",
  "ops.searchNameBookingId": "ค้นหาชื่อหรือรหัสการจอง…",
  "ops.allStops": "ทุกจุดจอด",
  "ops.addPassenger": "เพิ่มผู้โดยสาร",
  "ops.markNoShowShort": "บันทึกว่าไม่มา",
  "ops.editPassenger": "แก้ไขผู้โดยสาร",
  "ops.editSchedule": "แก้ไขตารางเวลา",
  "ops.stopName": "ชื่อจุดจอด",
  "ops.scheduled": "ตามกำหนด",
  "ops.walkIn": "ขึ้นหน้างาน",
  "ops.checkin": "เช็คอิน",
  "fleet.totalFuelCost": "ค่าน้ำมันรวม",

  "common.clearAll": "ล้างทั้งหมด",
  "common.clearAllFilters": "ล้างตัวกรองทั้งหมด",
  "common.rows": "แถว",
  "common.discard": "ทิ้งการแก้ไข",
  "common.changeStatus": "เปลี่ยนสถานะ",
  "common.viewAll": "ดูทั้งหมด",
  "common.done": "เสร็จสิ้น",
  "common.verify": "ยืนยัน",
  "common.selectAll": "เลือกทั้งหมด",
  "common.selectAllVisible": "เลือกทั้งหมดที่เห็น",
  "common.tryAgain": "ลองอีกครั้ง",
  "common.noItems": "ไม่พบรายการ",
  "common.home": "หน้าแรก",
  "common.notifications": "การแจ้งเตือน",
  "common.markAllRead": "อ่านทั้งหมดแล้ว",
  "common.noNotifications": "ไม่มีการแจ้งเตือน",
  "common.dangerZone": "โซนอันตราย",
  "common.role": "บทบาท",
  "common.soon": "เร็ว ๆ นี้",
  "common.pageNotFound": "ไม่พบหน้าที่ต้องการ",
  "common.pageNotFoundHint": "ลิงก์อาจเสียหรือหน้านี้ถูกย้ายไปแล้ว",
  "common.backToDashboard": "กลับไปหน้าแรก",
  "hr.title": "ฝ่ายบุคคล",
  "ops.keepBooking": "เก็บการจองไว้",
  "ops.backToContracts": "กลับไปหน้าสัญญา",
  "ops.backToCustomers": "กลับไปหน้าลูกค้า",
  "ops.backToTickets": "กลับไปหน้าใบงาน",
  "ops.amountKept": "ใบงานที่สร้างไปแล้วจะคงยอดเดิมไว้",
  "ops.createBoard": "สร้างบอร์ด",
  "ops.noDatesMatch": "ไม่มีวันที่ตรงกับตัวกรองวันในสัปดาห์",
  "ops.backToPool": "กลับไปกลุ่มรถ",
  "ops.vehiclesNeeded": "จำนวนรถที่ต้องใช้",
  "ops.noVehiclesSelected": "ยังไม่ได้เลือกรถ",
  "portal.goToMyPortals": "ไปที่พอร์ทัลของฉัน",
  "masterData.codeFormat": "รหัสต้องเป็นตัวอักษรพิมพ์เล็ก ตัวเลข หรือขีดล่างเท่านั้น",
  "settings.noOrgsMatch": "ไม่มีองค์กรที่ตรงกับคำค้น",

  "common.attributes": "คุณสมบัติ",
  "common.result": "ผลลัพธ์",
  "common.startDate": "วันเริ่ม",
  "common.endDate": "วันสิ้นสุด",
  "ops.undoPublish": "ยกเลิกการเผยแพร่",
  "ops.deleteRoute": "ลบเส้นทาง",
  "ops.deleteRoutePair": "ลบคู่เส้นทาง",
  "ops.createPortal": "สร้างพอร์ทัล",
  "ops.createPassenger": "เพิ่มผู้โดยสาร",
  "ops.extend": "ต่ออายุ",
  "ops.suspend": "ระงับ",
  "ops.jobs": "งาน",
  "ops.customersAdminOnly": "หน้าลูกค้าเปิดให้เฉพาะผู้ดูแล",
  "ops.contractTerms": "เงื่อนไขสัญญา",
  "ops.contractStatus": "สถานะสัญญา",
  "ops.financial": "การเงิน",
  "ops.estimateColumn": "ประมาณการ/มัดจำ",
  "ops.remainingTerm": "อายุสัญญาคงเหลือ",
  "ops.remainDaysLeft": "เหลืออีก {n} วัน",
  "ops.remainOverdue": "เกินกำหนด {n} วัน",
  "ops.remainLastDay": "วันสุดท้ายวันนี้",
  "ops.contractDateHistory": "ประวัติการแก้ไขช่วงเวลา",
  "ops.contractNoColumn": "เลขที่",
  "ops.reminders": "บันทึกช่วยจำ",
  "ops.reminderAdd": "เพิ่มบันทึก",
  "ops.reminderPlaceholder": "ระบุสิ่งที่ต้องดำเนินการ…",
  "ops.reminderDue": "ครบกำหนด {date}",
  "ops.reminderDueIn": "เหลืออีก {n} วัน · {date}",
  "ops.reminderDueToday": "ครบกำหนดวันนี้",
  "ops.reminderOverdueDue": "เลยกำหนด {n} วัน · {date}",
  "ops.reminderOverdueN": "เลยกำหนด {n}",
  "ops.reminderOpenN": "ค้าง {n}",
  "ops.reminderColumn": "บันทึก",
  "ops.reminderSoonN": "ใกล้ครบ {n}",
  "ops.reminderDoneBy": "เสร็จโดย {name} · {date}",
  "ops.reminderEmpty": "ยังไม่มีบันทึกช่วยจำ",
  "ops.reminderDeleteConfirm": "ลบบันทึกนี้?",
  "ops.historyNoChanges": "ยังไม่มีการแก้ไขช่วงเวลา",
  "ops.historyWhen": "วันที่แก้ไข",
  "ops.historyWho": "ผู้แก้ไข",
  "ops.manageContractTypesHint": "เพิ่ม/แก้ประเภทได้ที่เมนู ข้อมูลหลัก → หมวด Contract Type",
  "ops.monthlyValue": "มูลค่าค่าสัญญา (บาท)",
  "ops.filterByCustomer": "ลูกค้าทั้งหมด",
  "ops.depositThb": "เงินมัดจำ (บาท)",
  "ops.depositStatus": "สถานะเงินมัดจำ",
  "ops.contractsLoadFailed": "โหลดสัญญาไม่สำเร็จ",
  "ops.routeTemplates": "เส้นทาง",
  "ops.newRouteTemplate": "สร้างเส้นทาง",
  "ops.routeCode": "รหัสเส้นทาง",
  "ops.routeName": "ชื่อเส้นทาง",
  "ops.searchRouteCodeName": "ค้นหารหัสหรือชื่อเส้นทาง…",
  "ops.saveRoute": "บันทึกเส้นทาง",
  "ops.routeOptimizer": "จัดเส้นทาง",
  "ops.addToRoute": "เพิ่มเข้าเส้นทาง",
  "ops.optimizeFor": "จัดให้เหมาะกับ",
  "ops.avoidance": "สิ่งที่หลีกเลี่ยง",
  "ops.returnToDepot": "กลับจุดจอด",
  "ops.returnToDepotHint": "รถกลับไปยังจุดเริ่มต้นหลังจากจุดจอดสุดท้าย",
  "ops.serviceDurationPerStop": "เวลาให้บริการต่อจุด",
  "ops.summaryLanguage": "ภาษาของสรุป",
  "ops.capacityPerVehicle": "ความจุต่อคัน",
  "ops.saveAsTemplate": "บันทึกเป็นแม่แบบเส้นทาง",
  "ops.routeDescPlaceholder": "คำอธิบายสั้น ๆ ของเส้นทางนี้…",
  "ops.addStop": "เพิ่มจุดจอด",
  "ops.stopNamePlaceholder": "ชื่อจุดจอด",

  "common.approve": "อนุมัติ",
  "common.history": "ประวัติ",
  "common.changes": "สิ่งที่เปลี่ยน",
  "common.record": "รายการ",
  "common.actor": "ผู้กระทำ",
  "settings.noDetailRecorded": "ไม่มีรายละเอียดบันทึกไว้",
  "settings.teamMembers": "สมาชิกทีม",
  "settings.gpsInterval": "ความถี่ส่ง GPS",
  // ADR 0219 — map presets (org setting, shared with the driver app)
  "settings.map": "แผนที่",
  "settings.mapPreset": "ระดับรายละเอียดแผนที่",
  "settings.mapPreset.clean": "สะอาด — เห็นเฉพาะข้อมูลเรา",
  "settings.mapPreset.standard": "มาตรฐาน — เห็นชื่อสถานที่",
  "settings.mapPreset.detailed": "ละเอียด — เห็นทุกอย่าง",
  "settings.mapPreset.clean.desc": "ไม่มีชื่อสถานที่ ไอคอน สถานี หรือสนามบิน แผนที่เป็นพื้นเปล่า ให้จุดจอดกับรถเป็นสิ่งเดียวที่เห็น",
  "settings.mapPreset.standard.desc": "เห็นชื่อและไอคอนสถานที่ — ร้านค้า โรงเรียน โรงพยาบาล วัด สวน สถานี สนามบิน แต่ซ่อนเส้นรางกับสายรถเมล์ไว้ เพราะหน้าตาคล้ายเส้นทางวิ่งเกินไป",
  "settings.mapPreset.detailed.desc": "ทุกอย่างของแบบมาตรฐาน บวกเส้นรางกับสายรถเมล์ ชื่อซอย และชื่อย่าน",
  "settings.mapPresetHint": "มีผลกับแผนที่ทุกจอทั้งที่นี่และในแอปคนขับ คนขับจะเห็นค่าใหม่เมื่อเปิดงานถัดไป",
  "settings.gpsIntervalHint": "ระยะห่างระหว่างการส่งพิกัด งานใหม่จะใช้ค่านี้",
  "settings.usernamePrefixes": "คำนำหน้าชื่อผู้ใช้",
  "settings.separator": "ตัวคั่น",
  "settings.labelOptional": "ป้ายกำกับ (ไม่บังคับ)",
  "settings.noPrefixes": "ยังไม่มีคำนำหน้า",
  "settings.recentDeliveries": "การส่งล่าสุด",
  "settings.noDeliveries": "ยังไม่มีการส่ง",
  "settings.noWebhooks": "ยังไม่มี Webhook",
  "settings.addTemplate": "เพิ่มแม่แบบ",
  "settings.auditTitle": "บันทึกการใช้งาน",
  "settings.auditSubtitle": "บันทึกการเปลี่ยนแปลงทั้งระบบพร้อมร่องรอยเต็ม (Asia/Bangkok)",
  "settings.auditSearch": "ค้นหาผู้กระทำ รายการ การกระทำ…",
  "settings.auditLoading": "กำลังโหลดบันทึกการใช้งาน…",
  "settings.auditFilterRecord": "กรองเฉพาะร่องรอยของรายการนี้",
  "settings.auditNoMatch": "ไม่มีบันทึกที่ตรงกับตัวกรอง",
  "settings.auditDetail": "รายละเอียดบันทึก",
  "settings.simulate": "จำลอง",
  "settings.outgoingTransitions": "สถานะถัดไปที่เป็นไปได้",
  "settings.noOutgoing": "ไม่มีสถานะถัดไป (สถานะสุดท้าย)",
  "settings.workflowEngine": "ระบบขั้นตอนงาน",
  "settings.newWorkflow": "สร้างขั้นตอนงาน",
  "settings.membersSubtitle": "จัดการสมาชิกและคำเชิญขององค์กร",
  "settings.inviteMember": "เชิญสมาชิก",
  "settings.emailAddress": "อีเมล",
  "hr.charges": "ค่าใช้จ่าย",

  "common.saveChangesLower": "บันทึกการแก้ไข",
  "common.viewDetails": "ดูรายละเอียด",
  "common.backToList": "กลับไปหน้ารายการ",
  "common.completed": "เสร็จสิ้น",
  "common.inProgress": "กำลังดำเนินการ",
  "warroom.queueUpcoming": "คิว — งานที่จะถึงวันนี้",
  "warroom.noVehicles": "ไม่มีรถ",
  "warroom.noVehiclesMatch": "ไม่มีรถที่ตรงกับตัวกรอง",
  "warroom.vehicles": "รถ",
  // ADR 0498 D4 — the overdue chip and its duration (fmtOverdue) · D5 — a Locus ticket's second progress line
  "warroom.overdueShort": "+{overrun}",
  "warroom.overdueTip": "เลยเวลาที่ควรจบมาแล้ว {overrun}",
  "warroom.overdueTipEnd": "ควรจบ {when}",
  "warroom.dur.min": "{n} นาที",
  "warroom.dur.hr": "{n} ชม.",
  "warroom.dur.day": "{n} วัน",
  "warroom.locusDelivered": "ในระบบ Locus ส่งแล้ว {done}/{total} จุด",
  "warroom.roadFailed": "คำนวณเส้นทางไม่สำเร็จ",
  "warroom.rewind5min": "ถอยหลัง 5 นาที",
  "warroom.rewind30s": "ถอยหลัง 30 วินาที",
  "warroom.forward30s": "เดินหน้า 30 วินาที",
  "warroom.forward5min": "เดินหน้า 5 นาที",
  "warroom.scrubber": "แถบเลื่อนเวลาย้อนหลัง",
  "fleet.seatMapDesigner": "ออกแบบผังที่นั่ง",
  "fleet.selectVehicleToSave": "เลือกรถด้านบนก่อนจึงจะบันทึกได้",
  "fleet.backToFleetLower": "กลับไปหน้ารถ",
  "fleet.plateRequiredShort": "จำเป็นต้องกรอกทะเบียน",
  "fleet.fuelEntries": "รายการเติมน้ำมัน",
  "fleet.noFuelLogs": "รถคันนี้ยังไม่มีรายการเติมน้ำมัน",
  "fleet.noInsurance": "รถคันนี้ไม่มีกรมธรรม์",
  "fleet.deleteInsuranceQ": "ลบกรมธรรม์ประกันภัย?",
  "fleet.taxType": "ประเภทภาษี",
  "fleet.amountThb": "จำนวนเงิน (บาท)",
  "fleet.expiryAfterIssue": "วันหมดอายุต้องไม่ก่อนวันออก",
  "fleet.receiptNumber": "เลขที่ใบเสร็จ",
  "fleet.taxRecords": "ประวัติภาษี",
  "fleet.noTaxRecords": "รถคันนี้ไม่มีประวัติภาษี",

  // ── status — the display half of status maps. The MAP KEY stays the API
  //    value; only these strings are translated (ADR 0175).
  "status.draft": "ร่าง",
  "status.assigned": "มอบหมาย",
  "status.approved": "อนุมัติ",
  "status.accepted": "รับงาน",
  "status.started": "เริ่ม",
  "status.inProgress": "กำลังวิ่ง",
  "status.completed": "เสร็จสิ้น",
  "status.closed": "ปิดแล้ว",
  // ADR 0331 D1 — ใบงานใช้คำของตัวเอง ส่วน status.closed กลางเป็นของอีก 7 จอ
  // (รับสมัครงาน/ความปลอดภัย/เช็กลิสต์/ข้อเสนอแนะ/ผัง workflow/ซ่อมบำรุง) ซึ่ง
  // "closed" ที่นั่นแปลว่าจบสมบูรณ์ ไม่ใช่ถูกปิดการใช้งาน
  "status.ticketClosed": "ไม่ใช้งาน",
  "status.cancelled": "ยกเลิก",
  "status.scheduled": "จัดตาราง",
  "status.active": "ใช้งาน",
  "status.inactive": "ไม่ใช้งาน",
  "status.pending": "รอดำเนินการ",
  "status.expired": "หมดอายุ",
  "status.suspended": "ระงับ",
  "status.confirmed": "ยืนยัน",
  "status.reserved": "จอง",
  "status.boarded": "ขึ้นรถ",
  "status.noShow": "ไม่มา",

  "common.general": "ทั่วไป",
  "common.critical": "วิกฤต",
  "common.created": "สร้างเมื่อ",
  "common.ok": "ปกติ",
  "common.manual": "กรอกเอง",
  "common.yesterday": "เมื่อวาน",
  "common.ready": "พร้อม",
  "common.high": "สูง",
  "common.medium": "กลาง",
  "common.low": "ต่ำ",
  "common.submitted": "ส่ง",
  "common.open": "เปิด",
  "common.archived": "เก็บเข้าคลัง",
  "common.late": "สาย",
  "ops.pickupDropPoint": "จุดรับส่ง",
  "common.guest": "ผู้โดยสารทั่วไป",
  "status.stored": "จอดเก็บ",
  "status.inRepair": "ซ่อมบำรุง",
  "status.walkIn": "ขึ้นหน้างาน",
  "status.noShowCaps": "ไม่มา",
  "ops.noBooking": "ไม่มีการจอง",
  "ops.utilShort": "การใช้รถ",
  "ops.deadhead": "วิ่งเที่ยวเปล่า",
  "ops.chained": "ต่อเที่ยว",
  "ops.backToBoards": "กลับไปหน้าบอร์ด",
  "warroom.todayNDrivers": "วันนี้ · คนขับ {n} คน",
  "warroom.callNow": "โทรเดี๋ยวนี้",
  "ops.problemsGone": "ปัญหาทั้ง {n} ข้อหมดไปแล้ว",
  "ops.nothingWasChanged": "ไม่มีอะไรถูกเปลี่ยน",
  "ops.datesLocked": "{n} วันที่มีใบงานที่ใช้อยู่ จึงเอาออกไม่ได้: {dates}",
  "ops.saveDatesFailed": "บันทึกวันที่ไม่สำเร็จ",
  "ops.removeDateFailed": "เอาวันที่ออกไม่สำเร็จ",
  "ops.dateRemoved": "เอาวันที่ออกแล้ว · ลบเที่ยววิ่ง {n} รายการ",
  "ops.contractLockedPaired": "สัญญาถูกล็อกระหว่างที่เส้นทางจับคู่อยู่ — เปลี่ยน{jobType}เป็น {general} หรือ {charter} ในการ์ด{card} ก่อน",
  "passengers.tempPasswordNote": "ทุกอุปกรณ์ที่ล็อกอินอยู่จะถูกออกจากระบบ และ {name} ต้องตั้งรหัสผ่านใหม่ตอนเข้าใช้ครั้งถัดไป · ส่งรหัสให้เจ้าตัวโดยตรง — แสดงที่นี่ครั้งเดียว ไม่ส่งอีเมลและไม่เก็บไว้",
  "passengers.reactivate": "เปิดใช้งานอีกครั้ง",
  "passengers.noBookingsYet": "ยังไม่มีการจอง",
  "passengers.noBookingsMatch": "ไม่มีการจองที่ตรงกับตัวกรอง",
  "err.passenger.emailExists": "อีเมลนี้มีผู้โดยสารในองค์กรนี้อยู่แล้ว",
  "err.passenger.emailExistsWith": "อีเมลนี้มีผู้โดยสารในองค์กรนี้อยู่แล้ว ({id} · {status})",
  "err.passenger.samePerson": "คนนี้อยู่ในองค์กรนี้อยู่แล้ว (ใช้อีเมลอื่น)",
  "err.passenger.samePersonWith": "คนนี้อยู่ในองค์กรนี้อยู่แล้ว — {id} (ใช้อีเมลอื่น)",
  "err.portal.memberExists": "คนนี้เป็นสมาชิกของพอร์ทัลนี้อยู่แล้ว",
  "passengers.noAccountYet": "ยังไม่มีบัญชี — ใบเชิญยังรอตอบรับอยู่",
  "passengers.setPassword": "ตั้งรหัสผ่าน",
  "passengers.errRemove": "นำผู้โดยสารออกไม่สำเร็จ",
  "passengers.errUpdateStatus": "เปลี่ยนสถานะไม่สำเร็จ",
  "passengers.errResendInvite": "ส่งใบเชิญซ้ำไม่สำเร็จ",
  "passengers.errSetTempPassword": "ตั้งรหัสผ่านชั่วคราวไม่สำเร็จ",
  "passengers.errSaveChanges": "บันทึกการแก้ไขไม่สำเร็จ",
  "passengers.errCreate": "สร้างผู้โดยสารไม่สำเร็จ",
  "ops.markStopForRemoval": "ทำเครื่องหมายนำจุดจอดนี้ออก? จะถูกลบเมื่อคุณกดบันทึก",
  "common.create": "สร้าง",
  "common.outOf": "จาก",
  "portal.countsLine": "สัญญา {contracts} · สมาชิก {members} ({admin} ผู้ดูแล)",
  "portal.deleteConfirmTitle": "ลบพอร์ทัลนี้?",
  "portal.domainNote": " ส่วน {{domain}} จะกลายเป็นที่อยู่จริงของแอปผู้โดยสารเมื่อตั้งค่าเสร็จ",
  "portal.noOrgSlug": "องค์กรนี้ยังไม่ได้ตั้ง slug — จึงยังไม่มีลิงก์เข้าใช้",
  "portal.slugAutoPlaceholder": "สร้างจากชื่ออัตโนมัติ",
  "portal.slugFormatError": "ใช้ได้เฉพาะตัวพิมพ์เล็ก ตัวเลข และขีดกลาง",
  "portal.slugTaken": "“{slug}” ถูกใช้ไปแล้ว",
  "portal.errChangeStatus": "เปลี่ยนสถานะไม่สำเร็จ",
  "portal.errAddMember": "เพิ่มสมาชิกไม่สำเร็จ",
  "portal.errUpdateRole": "เปลี่ยนบทบาทไม่สำเร็จ",
  "portal.errRemoveMember": "นำสมาชิกออกไม่สำเร็จ",
  "portal.errUpdatePortal": "แก้ไขพอร์ทัลไม่สำเร็จ",
  "portal.errDeletePortal": "ลบพอร์ทัลไม่สำเร็จ",
  "portal.errCreatePortal": "สร้างพอร์ทัลไม่สำเร็จ",
  "portal.errSaveChanges": "บันทึกการแก้ไขไม่สำเร็จ",
  "portal.scopeSaved": "บันทึกขอบเขตแล้ว",
  "portal.scopeSearch": "ค้นในขอบเขต — สัญญาหรือเส้นทาง",
  "portal.nContracts": "{n} สัญญา",
  "portal.scopeCustomerTitle": "ลูกค้า / ชื่อสัญญา",
  "portal.scopeNoMatch": "ไม่พบสัญญาหรือเส้นทางที่ตรงกับคำค้นในขอบเขตนี้",
  "portal.scopeEmpty": "Portal นี้ยังไม่มีสัญญา — กด “เพิ่มสัญญา” เพื่อเริ่ม",
  "portal.contractRange": "สัญญา {from}–{to} จาก {total}",
  "portal.draftUnsaved": "ยังไม่บันทึก",
  "portal.draftAdd": "เพิ่ม {n} สัญญา",
  "portal.draftRemove": "เอาออก {n} สัญญา",
  "portal.draftRoutes": "เส้นทางเปลี่ยน {n} รายการ",
  "portal.removeContractFromPortal": "เอาสัญญาออกจากพอร์ทัล",
  "portal.noRoutesOpenWarn": "ยังไม่ได้เปิดเส้นทางใด — ผู้โดยสารไม่เห็นสัญญานี้",
  "portal.selectShown": "เลือกที่แสดงอยู่ ({n})",
  "portal.selectAllN": "เลือกทั้งหมด ({n})",
  "portal.openWord": "เปิดอยู่",
  "portal.shownOfContract": "{n} ที่แสดง · ทั้งสัญญา {total}",
  "portal.searchRoutesInContract": "ค้นในเส้นทางของสัญญานี้",
  "portal.noRoutesMatch": "ไม่มีเส้นทางที่ตรงกับคำค้นในสัญญานี้",
  "portal.nStops": "{n} จุดจอด",
  "portal.routeRange": "เส้นทาง {from}–{to} จาก {total}",
  "portal.addContractsTitle": "เพิ่มสัญญาเข้าพอร์ทัล",
  "portal.addContractsNote": "เลือกได้เฉพาะสัญญา — เส้นทางไปเจาะทีหลังในตารางหลัก · พิมพ์ชื่อเส้นทางก็ค้นได้ จะบอกว่าอยู่สัญญาใบไหน",
  "portal.pickSearch": "รหัส/ชื่อลูกค้า หรือชื่อเส้นทาง",
  "portal.nRoutes": "{n} เส้นทาง",
  "portal.matchedRoutes": "ตรงกับเส้นทาง {routes}",
  "portal.allMatchedAlreadyIn": "ทุกสัญญาที่ตรงกับคำค้นอยู่ในพอร์ทัลนี้แล้ว",
  "portal.noContractsFound": "ไม่พบสัญญาที่ตรงกับคำค้น",
  "portal.alreadyInPortal": "อยู่ในพอร์ทัลนี้แล้ว: {list}",
  "portal.pickedSummary": "เลือก {n} สัญญา · {routes} เส้นทางจะถูกเปิดให้เห็น",
  "portal.nothingPicked": "ยังไม่ได้เลือก",
  "warroom.editableAnyStatus": "แก้ไขได้ทุกสถานะ",
  "warroom.editStops": "แก้ไขจุดจอด",
  "warroom.tripRateCharges": "ค่าเที่ยวและค่าใช้จ่าย",
  "warroom.openInJobDetail": "เปิดในหน้าใบงาน",
  "warroom.saveJobFailed": "บันทึกงานนี้ไม่สำเร็จ",
  "warroom.noRouteBlankJob": "ไม่มีเส้นทาง — งานเปล่า",
  "fuel.proof": "หลักฐาน",
  "warroom.notSet": "ยังไม่กำหนด",
  "warroom.notStarted": "ยังไม่เริ่ม",
  "warroom.expectedWord": "คาดการณ์",
  "warroom.actAt": "จริง {time}",
  "ops.visitedBadge": "ผ่านแล้ว",
  "ops.visitedNoEdit": "ผ่านจุดนี้แล้ว — แก้ไขไม่ได้",
  "ops.visitedNoRemove": "ผ่านจุดนี้แล้ว — ลบไม่ได้",
  "hr.editCandidate": "แก้ไขผู้สมัคร",
  "hr.nJobPostings": "ประกาศรับสมัคร {n} รายการ",
  "hr.noPostingsYet": "ยังไม่มีประกาศรับสมัคร — กด \"ประกาศรับสมัครใหม่\" เพื่อสร้าง",
  "hr.offerAccepted": "ตอบรับแล้ว",
  "hr.offerDeclined": "ปฏิเสธแล้ว",
  "hr.srcWebsite": "เว็บไซต์",
  "hr.srcReferral": "คนแนะนำ",
  "hr.srcAgency": "เอเจนซี",
  "hr.srcLinkedin": "LinkedIn",
  "hr.srcWalkIn": "Walk-in",
  "toast.actionFailed": "ดำเนินการไม่สำเร็จ",
  "toast.advancedStage": "เลื่อนไปขั้นถัดไปแล้ว",
  "toast.rejectFailed": "ปฏิเสธไม่สำเร็จ",
  "toast.deleteFailed": "ลบไม่สำเร็จ",
  "toast.interviewSaved": "บันทึกการสัมภาษณ์แล้ว",
  "toast.offerSaved": "บันทึกข้อเสนอแล้ว",
  "toast.offerAccepted": "ตอบรับข้อเสนอแล้ว",
  "toast.offerDeclined": "ปฏิเสธข้อเสนอแล้ว",
  "ops.noStopsInRoute": "เส้นทางนี้ยังไม่มีจุดจอด",
  "ops.noStopsAddLeft": "ยังไม่มีจุดจอด — เพิ่มได้จากด้านซ้าย",
  "ops.backhaulRadiusHint": "ระยะที่ถือว่า “ใกล้เคียง” เมื่อต่อเที่ยววิ่งถัดไปของรถคันเดิม",
  "ops.driverMinRestHint": "เวลาพักขั้นต่ำระหว่างเที่ยววิ่งข้ามวันของคนขับ (0 = ไม่บังคับ)",
  "toast.statusArrow": "สถานะ → {status}",
  "login.authFailed": "ยืนยันตัวตนไม่สำเร็จ",
  "login.otpInvalidExpired": "รหัสไม่ถูกต้องหรือหมดอายุ",
  "login.otpTooMany": "ลองผิดหลายครั้งเกินไป กรุณาขอรหัสใหม่",
  "login.invalidCredentials": "อีเมลหรือรหัสผ่านไม่ถูกต้อง",
  "login.accountLocked": "บัญชีถูกล็อกชั่วคราว กรุณาลองใหม่ภายหลัง",
  "login.networkError": "เครือข่ายขัดข้อง กรุณาลองใหม่อีกครั้ง",
  "portal.tempPasswordSet": "บัญชีนี้ใช้รหัสผ่านชั่วคราวอยู่ กรุณาตั้งรหัสผ่านถาวรในแอปผู้โดยสารก่อนใช้งานพอร์ทัล",
  "ops.origin": "ต้นทาง",
  "ops.destination": "ปลายทาง",
  "ops.via": "จุดผ่าน",
  // ADR 0255 — ตัวนับท้ายรายการจุดจอด: กฎพหูพจน์อยู่ที่ "การเลือกคีย์" ไม่ใช่ในโค้ด
  //             เพราะ t() ของโปรเจกต์นี้ทำได้แค่แทน {var} ไม่มี plural rules
  "ops.stopCountOne": "{n} จุดจอด",
  "ops.stopsCount": "{n} จุดจอด",
  "ops.viaCount": "{n} จุดผ่าน",
  // ADR 0255 — ป้ายบทบาทในคอลัมน์ 64px แยกคีย์จาก ops.destination ที่ใช้เป็นหัวข้อที่อื่น
  //             (ไทยไม่ต้องย่อ พอดี 63px — ฝั่ง en ต้องย่อ)
  "ops.destinationBadge": "ปลายทาง",
  "hr.factorySide": "ฝั่งโรงงาน",
  "hr.homeSide": "ฝั่งบ้าน",
  "hr.middleStops": "จุดแวะระหว่างทาง",
  "hr.checkedIn": "เช็คอินแล้ว",
  "hr.notCheckedIn": "ไม่ได้เช็คอิน",
  "hr.anyMethod": "วิธีใดก็ได้",
  "hr.gpsCheckin": "เช็คอิน GPS",
  "hr.manualCheckin": "เช็คอินด้วยมือ",
  "hr.requirement": "เงื่อนไข",
  "toast.categoryCreated": "สร้างหมวดแล้ว",
  "toast.categoryUpdated": "อัปเดตหมวดแล้ว",
  "toast.categoryDeleted": "ลบหมวดแล้ว",
  "toast.itemAdded": "เพิ่มรายการแล้ว",
  "toast.itemUpdated": "อัปเดตรายการแล้ว",
  "toast.itemDeleted": "ลบรายการแล้ว",
  "ops.viaLetter": "จุดผ่าน {letter}",
  "ops.viaLetterNamed": "จุดผ่าน {letter} — {name}",
  "ops.routingOnly": "ใช้คำนวณเส้นทางเท่านั้น",
  "ops.viaPointRoutingOnly": "จุดผ่าน — ใช้คำนวณเส้นทางเท่านั้น",
  "common.kmValue": "{n} กม.",
  "common.minValue": "{n} นาที",
  "common.minApprox": "~{n} นาที",
  "ops.hasDepot": "มีจุดจอด",
  "common.nSeconds": "{n} วินาที",
  "toast.exportFailed": "ส่งออกไม่สำเร็จ",
  "toast.inviteCancelled": "ยกเลิกคำเชิญแล้ว",
  "toast.couldNotCancelInvite": "ยกเลิกคำเชิญไม่สำเร็จ",
  "toast.couldNotRemoveMember": "นำสมาชิกออกไม่สำเร็จ",
  "toast.assigned": "มอบหมายแล้ว",
  "toast.tripDeleted": "ลบเที่ยววิ่งแล้ว",
  "toast.jobsPublished": "เผยแพร่ใบงานแล้ว",
  "toast.portalCreated": "สร้างพอร์ทัลแล้ว",
  "toast.contractLinked": "เชื่อมสัญญาแล้ว",
  "toast.contractUnlinked": "ยกเลิกการเชื่อมสัญญาแล้ว",
  "toast.memberAdded": "เพิ่มสมาชิกแล้ว",
  "toast.roleUpdated": "อัปเดตบทบาทแล้ว",
  "toast.memberRemoved": "นำสมาชิกออกแล้ว",
  "toast.portalUpdated": "อัปเดตพอร์ทัลแล้ว",
  "toast.portalDeleted": "ลบพอร์ทัลแล้ว",
  "toast.accessUrlCopied": "คัดลอกลิงก์เข้าใช้แล้ว",
  "toast.couldNotCopy": "คัดลอกไม่สำเร็จ",
  "toast.coordinatesCopied": "คัดลอกพิกัดแล้ว",
  "toast.changesSaved": "บันทึกการแก้ไขแล้ว",
  "toast.passengerAddedTheyCan": "เพิ่มผู้โดยสารแล้ว — เข้าสู่ระบบด้วยรหัสผ่านเริ่มต้นได้เลย",
  "toast.invitationSent": "ส่งคำเชิญแล้ว",
  "toast.passengerRemovedFromOrganization": "นำผู้โดยสารออกจากองค์กรแล้ว",
  "toast.invitationResent": "ส่งคำเชิญซ้ำแล้ว",
  "toast.temporaryPasswordSetThe": "ตั้งรหัสผ่านชั่วคราวแล้ว — ผู้โดยสารต้องเปลี่ยนเมื่อเข้าสู่ระบบครั้งถัดไป",
  "toast.resolveFailed": "แก้ไขไม่สำเร็จ",
  "toast.reopenFailed": "เปิดใหม่ไม่สำเร็จ",
  "toast.autoresolveFailed": "แก้อัตโนมัติไม่สำเร็จ",
  "toast.refreshedOverflowData": "รีเฟรชข้อมูลผู้โดยสารตกค้างแล้ว",
  "toast.autoresolveConfigSaved": "บันทึกการตั้งค่าแก้อัตโนมัติแล้ว",
  "toast.importCompleteListRefreshed": "นำเข้าเสร็จสิ้น — รีเฟรชรายการแล้ว",
  "toast.salaryRulesUpdated": "อัปเดตกติกาเงินเดือนแล้ว",
  "toast.candidateRejected": "ปฏิเสธผู้สมัครแล้ว",
  "toast.candidateRemoved": "นำผู้สมัครออกแล้ว",
  "toast.postingDeleted": "ลบประกาศรับสมัครแล้ว",
  "toast.documentDeleted": "ลบเอกสารแล้ว",
  "toast.licenseDeleted": "ลบใบขับขี่แล้ว",
  "toast.idCardDeleted": "ลบบัตรประชาชนแล้ว",
  "toast.recordDeleted": "ลบรายการแล้ว",
  "toast.leaveRequestCancelled": "ยกเลิกคำขอลาแล้ว",
  "toast.documentUploaded": "อัปโหลดเอกสารแล้ว",
  "toast.licenseAdded": "เพิ่มใบขับขี่แล้ว",
  "toast.licenseUpdated": "อัปเดตใบขับขี่แล้ว",
  "toast.idCardAdded": "เพิ่มบัตรประชาชนแล้ว",
  "toast.idCardUpdated": "อัปเดตบัตรประชาชนแล้ว",
  "toast.leaveRequestSubmitted": "ส่งคำขอลาแล้ว",
  "toast.baseCompensationUpdated": "อัปเดตค่าตอบแทนพื้นฐานแล้ว",
  "toast.payeeUpdated": "อัปเดตผู้รับเงินแล้ว",
  "toast.recordAdded": "เพิ่มรายการแล้ว",
  "toast.newRateSaved": "บันทึกเรทใหม่แล้ว",
  "toast.manualRateSaved": "บันทึกเรทที่ตั้งเองแล้ว",
  "toast.decisionSaved": "บันทึกการตัดสินใจแล้ว",
  "toast.provinceRateDeleted": "ลบเรทจังหวัดแล้ว",
  "toast.provinceRateAdded": "เพิ่มเรทจังหวัดแล้ว",
  "toast.provinceRateUpdated": "อัปเดตเรทจังหวัดแล้ว",
  "toast.bookingCreated": "สร้างการจองแล้ว",
  "toast.bookingCancelled": "ยกเลิกการจองแล้ว",
  "toast.markedAsNoshow": "บันทึกว่าไม่มาแล้ว",
  "toast.failedToSaveDocuments": "บันทึกเอกสารไม่สำเร็จ",
  "toast.savedButSomeFiles": "บันทึกแล้ว แต่บางไฟล์อัปโหลดไม่สำเร็จ",
  "toast.documentsSaved": "บันทึกเอกสารแล้ว",
  "toast.stopsSaved": "บันทึกจุดจอดแล้ว",
  "toast.routeTemplateSaved": "บันทึกเส้นทางแล้ว",
  "toast.routeAssignmentsSaved": "บันทึกการมอบหมายเส้นทางแล้ว",
  "toast.bookingUpdated": "อัปเดตการจองแล้ว",
  "toast.exportIsNotEnabled": "ยังไม่เปิดใช้การส่งออก",
  "toast.financialsSaved": "บันทึกการเงินแล้ว",
  "toast.chargeAdded": "เพิ่มค่าใช้จ่ายแล้ว",
  "toast.chargeDeleted": "ลบค่าใช้จ่ายแล้ว",
  "toast.chargeUpdated": "อัปเดตค่าใช้จ่ายแล้ว",
  "toast.settingsSaved": "บันทึกการตั้งค่าแล้ว",
  "toast.passengerAdded": "เพิ่มผู้โดยสารแล้ว",
  "toast.bookingNotFound": "ไม่พบการจอง",
  "toast.routeStopsSaved": "บันทึกจุดจอดแล้ว",
  "toast.perstopScheduleEditingIsnt": "การแก้เวลาต่อจุดจอดยังไม่ถูกบันทึก",
  "toast.jobSaved": "บันทึกงานแล้ว",
  "toast.ticketCancelledCreateThe": "ยกเลิกใบงานแล้ว — สร้างเที่ยวทดแทนได้จากหน้าใบงาน",
  "toast.couldntOpenTheFile": "เปิดไฟล์ไม่ได้ — ลองใหม่อีกครั้ง",
  "toast.profileImageUpdated": "อัปเดตรูปโปรไฟล์แล้ว",
  "toast.profileImageRemoved": "ลบรูปโปรไฟล์แล้ว",
  "toast.pleaseSelectACharge": "กรุณาเลือกประเภทค่าใช้จ่าย",
  "toast.pleaseEnterAValid": "กรุณากรอกจำนวนเงินให้ถูกต้อง",
  "toast.unsupportedFileJpgPng": "ไฟล์ไม่รองรับ — ใช้ JPG, PNG, WebP, GIF",
  "toast.vehiclePhotoUpdated": "อัปเดตรูปรถแล้ว",
  "toast.vehiclePhotoRemoved": "ลบรูปรถแล้ว",
  "fleet.noVehicle": "ยังไม่มีรถ",
  "ops.removeFromOrgNote": "ผู้โดยสารจะเข้าองค์กรนี้ไม่ได้ บัญชีและประวัติการจองยังอยู่ — เชิญกลับได้ภายหลัง",
  "ops.tempPasswordPending": "ชั่วคราว — ต้องเปลี่ยนเมื่อล็อกอินครั้งถัดไป",
  "ops.accountOwnedByPassenger": "ผู้โดยสารเป็นเจ้าของบัญชี — โปรไฟล์และรหัสผ่านจัดการในแอปผู้โดยสาร",
  "portal.accessLinkNote": "สมาชิกเปิดลิงก์นี้ในแอปผู้โดยสารแล้วเข้าสู่ระบบเพื่อใช้พอร์ทัล",
  "warroom.totalUpper": "ทั้งหมด",
  "warroom.freeUpper": "ว่าง",
  "warroom.jobsUpper": "งาน",
  "warroom.noJobsToday": "วันนี้ยังไม่มีงานวิ่ง",
  "warroom.paletteHint": "พิมพ์อย่างน้อย 2 ตัวอักษรเพื่อค้นหา งาน · เส้นทาง · รถ · คนขับ",
  "warroom.noVehicleAssigned": "⚠ ยังไม่มอบหมายรถ",
  "warroom.routeStopsLine": "เส้นทาง {code} · {n} จุด",
  "warroom.stopsPassed": "ผ่านแล้ว {done}/{total} จุด",
  "warroom.showingOfMatched": "⚠ แสดง {shown} จาก {total} — ค้นหาหรือเลือกตัวกรองเพื่อดูที่เหลือ",
  "hr.driverTeam": "ทีมคนขับ",
  "fleet.inspection": "ตรวจสภาพ",
  "safety.case": "เคส",
  "common.submission": "รายการตรวจ",
  "ops.teamAddedWarn": "ทีม {team}: เพิ่มรถ {added} คัน · คนขับ {missing} คนยังไม่มีรถผูก:",
  "ops.driversNoVehicle": "คนขับ {n} คนยังไม่มีรถผูก:",
  "ops.nDriversShort": "{n} คนขับ",
  "ops.editableWhileDraft": "แก้ไขได้เฉพาะตอนใบงานเป็นร่าง / มอบหมายแล้ว (ก่อนเริ่มเที่ยว)",
  "hr.noChargesForJob": "ไม่มีค่าใช้จ่ายปฏิบัติการสำหรับงานนี้ ·",
  "hr.addFirstCharge": "+ เพิ่มรายการแรก",
  "hr.noOperationalCharges": "ไม่มีค่าใช้จ่ายปฏิบัติการ",
  "common.byActor": "โดย {name}",
  "masterData.nItems": "{n} รายการ",
  "masterData.lockedProtected": "· ล็อก (รายการระบบ)",
  "masterData.dragToReorder": "· ลากแถวเพื่อจัดลำดับ",
  "masterData.cat.expense_category": "หมวดค่าใช้จ่าย",
  "masterData.cat.fuel_type": "ชนิดเชื้อเพลิง",
  "masterData.cat.job_status": "สถานะงาน",
  "masterData.cat.maintenance_type": "ประเภทซ่อมบำรุง",
  "masterData.cat.province": "จังหวัด",
  "masterData.cat.vehicle_type": "ชนิดรถยนต์",
  "masterData.cat.vehicle_id_prefix": "คำนำหน้ารหัสรถ",
  "masterData.cat.vehicle_status": "สถานะรถ",
  "masterData.cat.ownership_type": "ประเภทความเป็นเจ้าของ",
  "masterData.cat.insurance_type": "ประเภทประกันภัย",
  "masterData.cat.insurance_coverage_class": "ชั้นประกันภัย",
  "masterData.cat.insurance_repair_type": "ประเภทการซ่อม",
  "masterData.cat.work_order_category": "หมวดใบสั่งงาน",
  "masterData.cat.work_order_status": "สถานะใบสั่งงาน",
  "masterData.cat.work_order_priority": "ความสำคัญใบสั่งงาน",
  "masterData.cat.inspection_purpose": "วัตถุประสงค์การตรวจ",
  "masterData.cat.inspection_category": "หมวดชุดตรวจ",
  "masterData.cat.employee_type": "ประเภทพนักงาน",
  "masterData.cat.maintenance_report_status": "สถานะรายงานซ่อมบำรุง",
  "masterData.cat.employee_status": "สถานะพนักงาน",
  "masterData.cat.gps_billing_type": "ประเภทค่าบริการ GPS",
  "masterData.cat.department": "แผนก",
  "masterData.cat.tax_type": "ประเภทภาษี",
  "masterData.cat.branch": "สาขา",
  "masterData.cat.finance_type": "ประเภทสินเชื่อ",
  "masterData.cat.document_type": "ประเภทเอกสาร",
  "masterData.cat.doc_verification_status": "สถานะตรวจสอบเอกสาร",
  "masterData.cat.salary_pay_type": "รูปแบบจ่ายเงินเดือน",
  "masterData.cat.payment_method": "วิธีชำระเงิน",
  "masterData.cat.behavior_record_type": "ประเภทบันทึกพฤติกรรม",
  "masterData.cat.leave_type": "ประเภทการลา",
  "masterData.cat.leave_request_status": "สถานะคำขอลา",
  "masterData.cat.recruitment_stage": "ขั้นตอนสรรหา",
  "masterData.cat.job_posting_status": "สถานะประกาศรับสมัคร",
  "masterData.cat.recruitment_source": "แหล่งที่มาผู้สมัคร",
  "masterData.cat.behavior_category": "หมวดพฤติกรรม",
  "masterData.cat.employment_type": "ประเภทการจ้าง",
  "masterData.cat.offer_status": "สถานะข้อเสนอ",
  "masterData.cat.charge_direction": "ทิศทางค่าใช้จ่าย",
  "masterData.cat.compensation_type": "ประเภทค่าตอบแทน",
  "masterData.cat.rate_tier": "ระดับเรท",
  "masterData.cat.overflow_action": "การจัดการผู้โดยสารตกค้าง",
  "masterData.cat.stop_checkin_method": "วิธีเช็คอินจุดจอด",
  "masterData.cat.route_status": "สถานะเส้นทาง",
  "masterData.cat.booking_mode": "โหมดการจอง",
  "masterData.cat.booking_status": "สถานะการจอง",
  "masterData.cat.booking_checkin_method": "วิธีเช็คอินการจอง",
  "masterData.cat.passenger_status": "สถานะผู้โดยสาร",
  "masterData.cat.passenger_type": "ประเภทผู้โดยสาร",
  "masterData.cat.pay_ticket_status": "สถานะใบจ่ายเงิน",
  "masterData.cat.contract_status": "สถานะสัญญา",
  "masterData.cat.deposit_status": "สถานะเงินมัดจำ",
  "masterData.cat.contract_service_type": "ประเภทบริการตามสัญญา",
  "masterData.cat.job_type": "ประเภทงาน",
  "masterData.cat.safety_case_category": "หมวดเคสความปลอดภัย",
  "masterData.cat.safety_case_status": "สถานะเคสความปลอดภัย",
  "masterData.cat.severity_level": "ระดับความรุนแรง",
  "masterData.cat.announcement_audience": "กลุ่มเป้าหมายประกาศ",
  "masterData.cat.announcement_priority": "ความสำคัญประกาศ",
  "masterData.cat.announcement_status": "สถานะประกาศ",
  "masterData.cat.alert_type": "ประเภทแจ้งเตือน",
  "masterData.cat.alert_severity": "ความรุนแรงแจ้งเตือน",
  "masterData.cat.alert_status": "สถานะแจ้งเตือน",
  "masterData.cat.feedback_source": "แหล่งที่มาความคิดเห็น",
  "masterData.cat.feedback_category": "หมวดความคิดเห็น",
  "masterData.cat.feedback_status": "สถานะความคิดเห็น",
  "masterData.cat.contract_document_type": "ประเภทเอกสารสัญญา",
  "masterData.defaultColon": "ค่าเริ่มต้น:",
  "fuel.sourceDriver": "คนขับ",
  "fuel.gaugeBefore": "เกจก่อนเติม",
  "fuel.pumpMeter": "มิเตอร์หัวจ่าย",
  "fuel.gaugeAfter": "เกจหลังเติม",
  "fuel.receiptSlip": "ใบเสร็จ / สลิป",
  "fuel.approvedCostBaht": "ค่าใช้จ่ายที่อนุมัติ (฿)",
  "fuel.changeLaterLogged": "เปลี่ยนภายหลังได้ — ทุกการเปลี่ยนแปลงถูกบันทึก",
  "fuel.currentlyStatusLogged": "ตอนนี้: {status} — การเปลี่ยนแปลงถูกบันทึก",
  "fuel.perLiter": "฿ / ลิตร",
  "settings.openMembers": "เปิดหน้าสมาชิก →",
  "fleet.platePlaceholder": "เช่น กก 1234",
  "ops.jobTypeGeneral": "วิ่งทั่วไป",
  "ops.jobTypeOutbound": "ขาเข้าโรงงาน",
  "ops.jobTypeReturn": "ขาออกโรงงาน",
  "ops.jobTypeCharter": "รับเหมา",
  "ops.jobTypeDistance": "วิ่งตามระยะ",
  "fleet.kmplSpecIs": "ค่าสเปกที่บันทึกไว้ {n} กม./ลิตร",
  "fleet.fuelEffShort": "ประสิทธิภาพน้ำมัน",
  "portal.footerBrand": "DeepTrack TMS · พอร์ทัลลูกค้า",
  "roles.nDefined": "บทบาท {n} รายการ",
  "settings.nWorkflows": "เวิร์กโฟลว์ {n} รายการ",
  "common.allCount": "ทั้งหมด ({n})",
  "common.activeCount": "ใช้งาน ({n})",
  "common.inactiveCount": "ไม่ใช้งาน ({n})",
  "status.submittedCount": "ส่ง ({n})",
  "status.closedCount": "ปิด ({n})",
  "common.template": "แม่แบบ",
  "fleet.nVehiclesTotal": "รถทั้งหมด {n} คัน",
  "fleet.fleetSize": "ขนาดกองรถ",
  "fleet.nPoliciesAcrossFleet": "กรมธรรม์ทั้งกองรถ {n} ฉบับ",
  "fleet.nWorkOrdersTotal": "ใบสั่งงานทั้งหมด {n} ใบ",
  "settings.removePrefix": "ลบ {prefix}",
  "settings.phase2Formulas": "Phase 2 — ฟีเจอร์ที่วางแผนไว้: เครื่องมือสูตรเงินเดือนแบบตั้งค่าได้พร้อมประวัติเวอร์ชัน",
  "settings.phase2Workflow": "Phase 2 — ฟีเจอร์ที่วางแผนไว้: ตัวสร้างเวิร์กโฟลว์แบบภาพพร้อม state machine",
  "settings.nFormulaSets": "ชุดสูตร {n} ชุด",
  "audit.actionAssignBulk": "มอบหมายเป็นชุด",
  "hr.driverTeamsTotal": "ทีมคนขับทั้งหมด {n} ทีม",
  "hr.leaveRequestsCount": "คำขอลา {n} รายการ",
  "ops.bookingTrend30": "แนวโน้มการจอง 30 วัน",
  "ops.nJobTickets": "{n} ใบงาน",
  "ops.utilizationColon": "· การใช้ที่นั่ง:",
  "fleet.nScheduledServices": "{n} รายการซ่อมบำรุงตามกำหนด",
  "fleet.overdueCount": "เกินกำหนด ({n})",
  "fleet.dueSoonCount": "ใกล้ถึงกำหนด ({n})",
  "fleet.okCount": "ปกติ ({n})",
  "fleet.pmOpenCount": "ยังไม่เสร็จ ({n})",
  "fleet.pmClosedCount": "ปิดงานแล้ว ({n})",
  "fleet.gridRowsCols": "{rows} แถว x {cols} คอลัมน์",
  "fuel.standard": "มาตรฐาน",
  "import.previewDryRun": "ดูตัวอย่าง (ยังไม่บันทึก)",
  "import.unsupportedFile": "ไฟล์ชนิดนี้ยังไม่รองรับ — บันทึกเป็น CSV ก่อน (.csv หรือ .txt)",
  "import.encodingUnreadable": "อ่านไฟล์นี้เป็นข้อความไม่ได้ ลองบันทึกใหม่จาก Excel เป็น \u201cCSV UTF-8\u201d แล้วอัปโหลดอีกครั้ง",
  "import.encodingConverted": "อ่านไฟล์เป็น {encoding} แล้วแปลงเป็นยูนิโค้ดให้ — ตรวจข้อความภาษาไทยในตัวอย่างด้านล่างก่อนนำเข้า",
  "import.readFailed": "อ่านไฟล์ไม่สำเร็จ",
  "import.uploadFirst": "อัปโหลดไฟล์ CSV ก่อน",
  "hr.importAdminOnly": "เฉพาะผู้ดูแลองค์กรเท่านั้นที่นำเข้าพนักงานได้",
  "fleet.importAdminOnly": "เฉพาะผู้ดูแลองค์กรเท่านั้นที่นำเข้ารถได้",
  "import.previewFirst": "กดดูตัวอย่างก่อน",
  "import.noValidRows": "ไม่มีแถวที่นำเข้าได้",
  "import.dryRunFailed": "ดูตัวอย่างไม่สำเร็จ",
  "import.importFailed": "นำเข้าไม่สำเร็จ",
  "import.importedRows": "นำเข้าแล้ว {n} แถว",
  "import.importedSkipped": "นำเข้าแล้ว {n} · ข้าม {skipped} แถว — ดูด้านล่าง",
  "import.finishedPartial": "นำเข้าเสร็จ — สำเร็จ {n} · ข้าม {skipped} แถว",
  "import.finishedClean": "นำเข้าเสร็จ — {n} แถว",
  "import.skippedRowsTitle": "แถวที่ถูกข้าม:",
  "import.errorRowsTitle": "ข้อผิดพลาด (แถวเหล่านี้จะถูกข้าม):",
  "hr.expiringExpiredCount": "ใกล้หมด / หมดอายุ ({n})",
  "settings.usernamePrefixHelp": "ชื่อผู้ใช้ = คำนำหน้า + ตัวคั่น + ชื่อคน — เช่น คำนำหน้า ps คั่นด้วย - ได้ ps-somchai; ตัวคั่น none ได้ pssomchai คำนำหน้าต้องไม่ซ้ำกันทั้งระบบ — ถ้าถูกใช้แล้วระบบจะให้เลือกใหม่",
  "settings.withSeparator": "คั่นด้วย",
  "settings.entityJobTicket": "ใบงาน",
  "settings.entityRequest": "คำขอ",
  "settings.evTripLate": "เที่ยวสาย",
  "settings.evSos": "แจ้ง SOS",
  "settings.evGpsLost": "สัญญาณ GPS หาย",
  "settings.evFuelAnomaly": "น้ำมันผิดปกติ",
  "settings.evOverbooking": "พบการจองเกิน",
  "settings.evSchedulePublished": "เผยแพร่ตารางแล้ว",
  "settings.evMaintenanceDue": "ถึงกำหนดซ่อมบำรุง",
  "settings.featNlInput": "ป้อนคำสั่งภาษาธรรมชาติสำหรับวางแผนเส้นทาง",
  "settings.lateThreshold": "เกณฑ์สาย",
  "settings.lateThresholdDesc": "ช่วงผ่อนผันหลังเวลาจบงานที่วางไว้ ก่อน War Room จะนับว่าสาย งานใหม่จะใช้ค่านี้",
  "settings.acceptOpenBefore": "เปิดให้ยืนยันล่วงหน้าก่อนเวลายืนยัน (นาที)",
  "settings.acceptOpenBeforeDesc": "คนขับกดรับงานได้ล่วงหน้ากี่นาทีก่อน \"เวลายืนยัน\" ของใบงาน (แบบเช็คอินเครื่องบิน — เคาน์เตอร์เปิดก่อนเวลาปิดเคาน์เตอร์ ไม่ใช่ก่อนเวลาบิน) ใบที่ไม่ได้กรอกเวลายืนยันจะกดได้ตลอด ใบงานใหม่ใช้ค่านี้ และแก้รายใบได้",
  "settings.cancelCutoffBefore": "ปิดรับยกเลิกก่อนเริ่มงาน (นาที)",
  "settings.cancelCutoffBeforeDesc": "ปุ่มยกเลิกฝั่งคนขับจะหายไปเมื่อเหลือเวลาน้อยกว่านี้ก่อนเริ่มงาน ใบงานใหม่จะใช้ค่านี้ และแก้รายใบได้",
  "settings.fuelStandard": "มาตรฐานน้ำมัน (กม./ลิตร)",
  "settings.fuelStandardDesc": "ต่ำกว่านี้จะแจ้งเตือนน้ำมันผิดปกติ",
  "settings.fuelStationRadius": "รัศมีสถานีบริการน้ำมัน (เมตร)",
  "settings.fuelStationRadiusDesc": "ระยะที่ถือว่าใบเติมนั้นเติมที่สถานีนั้น ใช้ร่วมกันทุกสถานี",
  "settings.idleTimeout": "เวลาจอดนิ่ง",
  "settings.idleTimeoutDesc": "จำนวนนาทีก่อนนับว่ารถจอดนิ่ง",
  "settings.speedLimit": "ความเร็วสูงสุด",
  "settings.speedLimitDesc": "กม./ชม. — ค่าตั้งต้นของจุดจอดที่สร้างใหม่",
  // ADR 0224
  "settings.checkinRadius": "รัศมีเช็คอิน",
  "settings.checkinRadiusDesc": "เมตร — ค่าตั้งต้นของจุดจอดที่สร้างใหม่ และใช้กับจุดจอดเก่าที่ไม่ได้ตั้งค่าไว้",
  "settings.pinSamePlace": "ระยะที่นับว่าเป็นที่เดียวกัน",
  "settings.pinSamePlaceDesc": "เมตร — หมุดที่ห่างกันไม่เกินนี้ใช้หมุดเดิมเลย ถ้าห่างกว่านี้แต่อยู่ในรัศมีเช็คอิน จะถามก่อนว่าจะใช้หมุดร่วมไหม (0 = ต้องตรงกันพอดี)",
  "settings.pinSamePlaceOver": "มากกว่ารัศมีเช็คอิน — หมุดในรัศมีจะใช้หมุดเดิมเองทั้งหมด ไม่ถาม",
  "settings.sosSla": "SLA ตอบสนอง SOS",
  "settings.sosSlaDesc": "จำนวนนาทีที่ต้องรับทราบการแจ้ง SOS",
  "settings.cutoffTime": "เวลาปิดรับจอง (ชั่วโมง)",
  "settings.cutoffTimeDesc": "จำนวนชั่วโมงก่อนออกรถที่ปิดรับจอง",
  "settings.advanceDays": "จองล่วงหน้าได้ (วัน)",
  "settings.advanceDaysDesc": "ผู้โดยสารจองล่วงหน้าได้กี่วัน",
  "settings.seatHold": "ถือที่นั่ง (นาที)",
  "settings.seatHoldDesc": "ระยะเวลาถือที่นั่งสำหรับการจองที่ยังไม่ชำระ",
  "settings.maxOverbooking": "จองเกินได้สูงสุด %",
  "settings.maxOverbookingDesc": "เปอร์เซ็นต์จองเกินที่ยอมได้ก่อนแจ้งเตือน",
  "settings.allowOverbooking": "อนุญาตจองเกิน",
  "settings.allowOverbookingDesc": "เปิดให้จองเกินความจุรถ",
  "settings.walkInBooking": "จอง walk-in",
  "settings.walkInBookingDesc": "ให้แอดมินเพิ่มผู้โดยสาร walk-in ได้",
  "settings.qrRequired": "ต้องใช้ QR",
  "settings.qrRequiredDesc": "ต้องสแกน QR เพื่อยืนยันการขึ้นรถ",
  "hr.employeesTotal": "พนักงานทั้งหมด {n} คน",
  "hr.hireDateFuture": "วันที่เริ่มงานอยู่ในอนาคต — โปรดตรวจสอบ",
  "roles.member": "สมาชิก",
  "roles.driver": "คนขับ",
  "common.password": "รหัสผ่าน",
  "hr.leaveBlankKeep": "(เว้นว่างถ้าไม่เปลี่ยน)",
  "settings.gpsProvider": "ผู้ให้บริการ GPS",
  "settings.gpsProviderDesc": "ผู้ให้บริการ GPS เริ่มต้นสำหรับรถใหม่",
  "settings.googleMapsKey": "Google Maps API Key",
  "settings.googleMapsKeyDesc": "ใช้สำหรับวางแผนเส้นทางและติดตามสด",
  "settings.geminiKey": "Gemini API Key",
  "settings.geminiKeyDesc": "ใช้สำหรับคำแนะนำเส้นทางด้วย AI และการอ่านคำสั่งภาษาธรรมชาติ",
  "settings.featRouteOpt": "จัดเส้นทางอัตโนมัติ",
  "settings.featRouteOptDesc": "วางแผนเส้นทางด้วย AI (OR-Tools)",
  "settings.featOverbooking": "จัดการผู้โดยสารตกค้าง",
  "settings.featOverbookingDesc": "ตรวจจับผู้โดยสารเกินอัตโนมัติพร้อมคำแนะนำจาก AI",
  "settings.featLiveTracking": "ติดตามสด",
  "settings.featLiveTrackingDesc": "ติดตามตำแหน่งรถแบบเรียลไทม์บนแผนที่",
  "settings.featCommsHub": "ศูนย์สื่อสาร",
  "settings.featCommsHubDesc": "แชทในแอป ประกาศ และระบบคำขอ",
  "settings.featPortal": "การเข้าถึงพอร์ทัล",
  "settings.featPortalDesc": "ให้ลูกค้าดูข้อมูลสัญญาของตนเอง",
  "settings.featDriverAppDesc": "แอปมือถือสำหรับคนขับ (WebView)",
  "settings.featPassengerAppDesc": "แอปมือถือสำหรับผู้โดยสารจอง (WebView)",
  "common.noneOption": "— ไม่ระบุ —",
  "hr.noVehicleOption": "— ไม่มีรถ —",
  "hr.changeSalaryNote": "เปลี่ยนเงินเดือนได้ที่แท็บการจัดการเงิน (เก็บประวัติตามวันที่มีผล)",
  "hr.oneVehicleNote": "คนขับหนึ่งคนผูกรถได้หนึ่งคัน — ถ้ารถคันนี้มีคนอื่นถืออยู่ ระบบจะย้ายมาที่นี่",
  "hr.contractRateNote": "มีผลเหนือเรทเริ่มต้นของคนขับ · อ่านอย่างเดียวที่นี่ — จัดการที่",
  "hr.noContractRatesYet": "ยังไม่มีเรทสัญญา — งานจะใช้โบนัสเส้นทาง / เรทเริ่มต้นคนขับ / เรทมาตรฐานเส้นทาง",
  "ops.vehiclesCount": "รถ ({n})",
  "ops.startTimeBangkok": "เวลาเริ่ม · Asia/Bangkok",
  "common.secUnit": "วินาที",
  "ops.minPerStop": "≈ {n} นาที/จุด",
  "ops.stopsPerVehicle": "≈ {per} จุด/คัน ({stops} จุด ÷ {veh} คัน)",
  "ops.autoOnlyTwoPlus": "โหมดอัตโนมัติใช้เมื่อเลือกรถตั้งแต่ 2 คัน",
  "ops.runsHistoryHint": "ประวัติรอบจัดเส้นทางทั้งหมด — คลิกแม่แบบเพื่อดูรายละเอียด",
  "ops.nRuns": "{n} รอบ",
  "common.thai": "ไทย",
  "common.english": "อังกฤษ",
  "common.auto": "อัตโนมัติ",
  "ops.selectedUpper": "เลือกแล้ว",
  "ops.availableUpper": "ว่าง",
  "common.lat": "ละติจูด",
  "common.lng": "ลองจิจูด",
  "hr.noRouteRateSetInHr": "ไม่มีเรทเส้นทาง — ตั้งเรทคนขับที่ บุคลากร › การจัดการเงิน",
  "hr.moneyManagementPath": "บุคลากร › พนักงาน › การจัดการเงิน",
  "hr.noRouteRatesYet": "ยังไม่มีเรทรายบุคคลตามเส้นทาง — ระบบไปดูขั้นถัดไป",
  "hr.noRouteRateSetManually": "ไม่มีเรทเส้นทาง — ตั้งเอง",
  "hr.noDriverAssigned": "ยังไม่มอบหมายคนขับ",
  "common.endOfList": "โหลดแล้ว {n} · หมดรายการ",
  "common.searchPlaceholder": "ค้นหา…",
  "common.typeToSearch": "พิมพ์เพื่อค้นหา…",
  "common.noResultsFor": "ไม่พบผลลัพธ์สำหรับ \"{q}\"",
  "common.topMatches": "{n} รายการแรก • พิมพ์เพื่อค้นหาทั้งหมด",
  "common.nOfTotalTypeToSearch": "{n} จาก {total} • พิมพ์เพื่อค้นหา",
  "common.loadedScrollMore": "โหลดแล้ว {n} • เลื่อนเพื่อดูเพิ่ม • พิมพ์เพื่อกรอง",
  "common.noDocumentsAttach": "ยังไม่มีเอกสาร — กด \"เพิ่มไฟล์\" เพื่อแนบ (เอกสารและรูป ≤{mb}MB)",
  "common.collapseSidebar": "หุบเมนู",
  "common.expandSidebar": "ขยายเมนู",
  "common.collapse": "หุบ",
  "common.expand": "ขยาย",
  "portal.demoFigures": "ตัวเลขตัวอย่างสำหรับ",
  "portal.liveSoon": "ข้อมูลจริงเร็ว ๆ นี้",
  "ops.totalJobTickets": "ใบงานทั้งหมด",
  "status.late": "สาย",
  "ops.limited": "จำกัด",
  "ops.unlimited": "ไม่จำกัด",
  "ops.loadBookingsFailed": "โหลดการจองไม่สำเร็จ",
  "ops.tripNotFound": "ไม่พบเที่ยวนี้",
  "ops.readOnlySuffix": " (อ่านอย่างเดียว)",
  "ops.boardFreely": "ขึ้นรถได้เลย ไม่ต้องจอง",
  "ops.mustBookCapped": "ต้องจอง จำกัดที่นั่ง",
  "ops.mustBookExtra": "ต้องจอง เพิ่มที่นั่งได้",
  "ops.bookingLimitedSeats": "จอง · จำกัดที่นั่ง",
  "ops.bookingUnlimitedSeats": "จอง · ไม่จำกัดที่นั่ง",
  "common.restore": "กู้คืน",
  "copilot.welcome": "สวัสดีครับ! ผม DeepTrack Copilot พร้อมช่วยเรื่อง TMS ครับ ลองถามได้เลย เช่น:\n\n• สรุปสถานะวันนี้\n• คนขับว่างมีใครบ้าง\n• เอกสารใกล้หมดอายุ\n• วิธีจัดการ overflow",
  "copilot.error": "ขออภัย ไม่สามารถประมวลผลได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง",
  "copilot.placeholder": "ถาม Copilot อะไรก็ได้... (เช่น สรุปสถานะวันนี้, คนขับว่างมีใคร)",
  "copilot.cmdSummaryToday": "สรุปสถานะวันนี้ให้หน่อย",
  "copilot.cmdFreeDrivers": "คนขับว่างมีใครบ้าง",
  "copilot.cmdExpiringDocs": "เอกสารที่ใกล้หมดอายุมีอะไรบ้าง",
  "copilot.cmdFleetStatus": "สถานะรถทั้งหมดเป็นอย่างไร",
  "copilot.cmdFuelToday": "สรุปน้ำมันวันนี้",
  "copilot.cmdScheduleTomorrow": "ตารางงานพรุ่งนี้มีอะไรบ้าง",
  "feedback.commentPlaceholder": "บอกเราว่าประสบการณ์เป็นอย่างไร...",
  "feedback.emptyTitle": "ยังไม่มี feedback",
  "feedback.emptyBody": "Feedback ที่ส่งจาก passenger app จะปรากฏที่นี่",
  "reports.templateSaved": "บันทึก report template แล้ว",
  "reports.editSaved": "บันทึกการแก้ไขแล้ว",
  "reports.deleteTemplateQ": "ลบ template \"{name}\"?",
  "common.deleted": "ลบแล้ว",
  "reports.customIntro": "สร้าง report แบบกำหนดเอง — เลือก source, filter, group-by, แล้วรัน",
  "reports.noTemplates": "ยังไม่มี template",
  "reports.pressPlusToCreate": "กด + เพื่อสร้าง",
  "reports.pickTemplateTitle": "เลือก template จากซ้าย หรือสร้างใหม่",
  "reports.pickTemplateBody": "Custom reports ให้คุณประกอบ query เอง — เลือก source, กรอง, group-by, ดูผล",
  "reports.descPlaceholder": "อธิบาย report นี้สั้น ๆ",
  "reports.noRows": "ไม่มีข้อมูลตรงตามเงื่อนไข",
  "integrations.noApiKeyTitle": "ยังไม่มี API key",
  "integrations.noApiKeyBody": "สร้าง key เพื่อให้ระบบภายนอก (Zapier, n8n, integration อื่น ๆ) เข้าถึง TMS แบบอ่านข้อมูลหรือเรียก webhook ได้",
  "reports.r1": "สรุปเที่ยววิ่ง",
  "reports.r2": "ประสิทธิภาพเส้นทาง",
  "reports.r3": "ประสิทธิภาพคนขับ",
  "reports.r4": "สถานะรถ",
  "reports.r5": "บันทึกซ่อมบำรุง",
  "reports.r6": "ประกันหมดอายุ",
  "reports.r7": "สรุปการจอง",
  "reports.r8": "รายงานผู้โดยสารตกค้าง",
  "reports.r9": "การใช้น้ำมัน",
  "reports.r10": "รายได้และต้นทุน",
  "reports.r11": "การเรียกเก็บตามสัญญา",
  "audit.timestamp": "เวลา",
  "audit.summary": "สรุป",
  "audit.entityId": "รหัสรายการ",
  "audit.moreRowsExport": "+{n} แถว — ส่งออก .xlsx เพื่อดูทั้งหมด",
  "hr.safetyChip": "ความปลอดภัย {score}/100",
  "safety.stepDeptReview": "แผนกตรวจสอบ",
  "safety.stepSentToHr": "ส่งให้ HR",
  "safety.stepHrReview": "HR ตรวจสอบและจำแนก",
  "safety.stepWarningIssued": "ออกหนังสือเตือน",
  "safety.stepESign": "ลงนามรับทราบ",
  "safety.stepClosed": "ปิดเคส",
  "common.system": "ระบบ",
  "masterData.deleteCategoryQ": "ลบหมวดหมู่?",
  "masterData.deleteItemQ": "ลบรายการ?",
  "masterData.deleteCategoryNote": "\"{name}\" และรายการทั้งหมดในหมวดจะถูกปิดใช้งาน",
  "masterData.editCategory": "แก้ไขหมวดหมู่",
  "masterData.protectedCatEdit": "หมวดหมู่ระบบ — แก้ไขไม่ได้",
  "masterData.protectedCatDelete": "หมวดหมู่ระบบ — ลบไม่ได้",
  "masterData.addNewItem": "เพิ่มรายการใหม่",
  "masterData.editItem": "แก้ไข \"{name}\"",
  "masterData.usedIn": "ใช้ใน:",
  "masterData.protectedItemEdit": "รายการระบบ — แก้ไขไม่ได้",
  "masterData.protectedItemDelete": "รายการระบบ — ลบไม่ได้",
  "masterData.protectedItemLocked": "รายการระบบ — ล็อก",
  "masterData.defaultItem": "รายการเริ่มต้น",
  "masterData.setAsDefault": "ตั้งเป็นค่าเริ่มต้น",
  "masterData.groupFleet": "กองรถ",
  "masterData.groupHr": "บุคลากร",
  "masterData.groupRecruitment": "สรรหา",
  "masterData.groupJobs": "งานและจัดรถ",
  "masterData.groupRoutes": "เส้นทางและการจอง",
  "masterData.groupFinance": "การเงินและสัญญา",
  "masterData.groupSafety": "ความปลอดภัย",
  "masterData.groupComms": "สื่อสารและความคิดเห็น",
  "masterData.groupShared": "ใช้ร่วมกัน",
  "audit.actionCreate": "สร้าง",
  "audit.actionUpdate": "แก้ไข",
  "audit.actionDelete": "ลบ",
  "audit.actionStatusChange": "เปลี่ยนสถานะ",
  "audit.actionVoid": "ยกเลิก",
  "audit.fin.bill_move": "ย้ายรายการเข้าบิล",
  "audit.fin.bill_return": "คืนรายการจากบิล",
  "audit.fin.pay_batch_create": "สร้างรอบจ่าย",
  "audit.fin.pay_batch_add": "หยิบบิลเข้ารอบ",
  "audit.fin.pay_batch_remove": "เอาบิลออกจากรอบ",
  "audit.fin.pay_batch_freeze": "แช่แข็งรอบ",
  "audit.fin.pay_batch_unfreeze": "ตีกลับเป็นร่าง",
  "audit.fin.pay_batch_approve": "อนุมัติรอบ",
  "audit.fin.pay_batch_export": "ดาวน์โหลดไฟล์จ่าย",
  "audit.fin.pay_batch_export_accounting": "ดาวน์โหลดไฟล์บัญชี",
  "audit.fin.pay_batch_paid": "ยืนยันโอนแล้ว",
  "audit.fin.pay_batch_delete": "ลบรอบร่าง",
  "audit.fin.payout_settings_update": "แก้ข้อมูลบริษัทผู้จ่าย",
  "audit.fin.payee_update": "แก้บัญชีรับเงิน",
  "audit.fin.finance_item_create": "เพิ่มรายการเงิน",
  "audit.fin.finance_item_update": "แก้รายการเงิน",
  "audit.fin.finance_item_delete": "ลบรายการเงิน",
  "audit.fin.finance_config_set": "ตั้งค่าการเงิน",
  "audit.fin.finance_config_cancel": "ยกเลิกค่าที่รอมีผล",
  "audit.fin.finance_txn_create": "บันทึกรายการเงิน",
  "audit.fin.finance_txn_edit": "แก้รายการเงิน",
  "audit.fin.finance_txn_relink": "ย้ายเอกสารที่ผูก",
  "audit.fin.finance_txn_reverse": "กลับรายการเงิน",
  "audit.fin.finance_txn_void": "ยกเลิกรายการเงิน",
  "audit.fin.finance_txn_status": "เปลี่ยนสถานะรายการเงิน",
  "audit.fin.person_finance_update": "แก้ข้อมูลการเงินของคน",
  "hr.unlimited": "ไม่จำกัด",
  "hr.ofDays": "จาก {n} วัน",
  "feedback.submit": "ส่งความคิดเห็น",
  "feedback.rateStars": "ให้ {n} ดาว",
  "feedback.catSafety": "ความปลอดภัย",
  "analytics.periodLast30": "ช่วงเวลา: 30 วันล่าสุด",
  "common.rolesColon": "บทบาท:",
  "safety.showingCases": "แสดง {n} จาก {total} เคส",
  "handover.showingRecords": "แสดง {n} จาก {total} รายการ",
  "hr.leaveAnnual": "ลาพักร้อน",
  "hr.leaveSick": "ลาป่วย",
  "hr.leavePersonal": "ลากิจ",
  "hr.leaveMaternity": "ลาคลอด",
  "hr.leavePaternity": "ลาเลี้ยงดูบุตร",
  "hr.leaveUnpaid": "ลาไม่รับค่าจ้าง",
  "hr.leaveOther": "อื่นๆ",
  // ── การลา: หัวคอลัมน์ของไฟล์ export (ADR 0265 — คีย์ต้องมีครบทั้ง th/en) ──
  "hr.employeeCode": "รหัสพนักงาน",
  "hr.leaveType": "ประเภทการลา",
  "hr.leaveFirstDayHalf": "ครึ่งวันวันแรก",
  "hr.leaveLastDayHalf": "ครึ่งวันวันสุดท้าย",
  "hr.leaveHalfMorning": "ครึ่งเช้า",
  "hr.leaveHalfAfternoon": "ครึ่งบ่าย",
  "hr.leaveFullDay": "เต็มวัน",
  "hr.leaveSubmittedAt": "ยื่นเมื่อ",
  "hr.leaveDecidedAt": "ตัดสินเมื่อ",
  "hr.leaveDecisionNotes": "เหตุผลการตัดสิน",
  "hr.leaveAttachmentCount": "จำนวนไฟล์แนบ",
  "hr.catGeneral": "ทั่วไป",
  "hr.presetContractors": "คนขับภายนอก",
  "common.issueDate": "วันที่ออก",
  "hr.idNumber": "เลขบัตร",
  "hr.issuedBy": "ออกโดย",
  "common.image": "รูป",
  "hr.usedDays": "ใช้ไป: {n} วัน",
  "hr.sensitiveMasked": "ตัวเลขสำคัญถูกซ่อนไว้ กดแก้ไขเพื่อดู/เปลี่ยนค่าเต็ม",
  "common.opening": "กำลังเปิด…",
  "import.vehicleRequired": "ต้องมีทั้ง plate และ display_id (ADR 0138 — ระบบไม่สร้างรหัสรถให้แล้ว)",
  "hr.scoreWorking": "การทำงาน",
  "hr.scoreSafety": "ความปลอดภัย",
  "hr.scoreAdmin": "ธุรการ",
  "hr.hired": "เริ่มงาน",
  "hr.idCard": "บัตรประชาชน",
  "hr.docOther": "อื่น ๆ",
  "hr.tripRate": "เรทต่อเที่ยว",
  "common.ticket": "ใบงาน",
  "hr.change": "เปลี่ยนแปลง",
  "hr.changedAt": "เปลี่ยนเมื่อ",
  "hr.houseRegistration": "ทะเบียนบ้าน",
  "hr.driverLicense": "ใบขับขี่",
  "hr.medicalCertificate": "ใบรับรองแพทย์",
  "hr.newAccount": "บัญชีใหม่",
  "hr.newAccountBody": "จะสร้างบัญชี OAuth ใหม่ — ผู้ใช้ต้องตั้งรหัสผ่านเมื่อล็อกอินครั้งแรก",
  "hr.oauthUserFound": "พบผู้ใช้ OAuth",
  "hr.oauthFoundMultiOrg": "พบผู้ใช้ OAuth — ผู้ใช้นี้อยู่ในหลายองค์กรแล้ว",
  "hr.oauthFoundFill": "พบผู้ใช้ OAuth — กรอกข้อมูลแล้วกดเพิ่มพนักงาน",
  "hr.foundName": "พบ: {name}",
  "hr.alreadyInSystem": "มีในระบบแล้ว",
  "hr.checkFailed": "ตรวจสอบไม่สำเร็จ",
  "hr.unableCheckEmail": "ตรวจสอบอีเมลไม่ได้ — ลองใหม่อีกครั้ง",
  "hr.checkingAvailability": "กำลังตรวจสอบ…",
  "hr.usernameTaken": "ชื่อผู้ใช้นี้ถูกใช้แล้ว — เลือกชื่ออื่น",
  "hr.selectValidPrefix": "เลือกคำนำหน้าที่ถูกต้อง",
  "hr.restoreEmployee": "กู้คืนพนักงาน",
  "hr.createEmployee": "สร้างพนักงาน",
  "hr.editEmployee": "แก้ไขพนักงาน",
  "hr.nextAvailable": "รหัสถัดไปที่ว่าง:",
  "hr.setNewPassword": "ตั้งรหัสผ่านใหม่",
  "hr.setPasswordForEmployee": "ตั้งรหัสผ่านให้พนักงานคนนี้",
  "common.allGender": "ทุกเพศ",
  "common.male": "ชาย",
  "common.female": "หญิง",
  "hr.documentVerified": "ตรวจสอบเอกสารแล้ว",
  "hr.documentRejected": "ปฏิเสธเอกสารแล้ว",
  "hr.leaveApproved": "อนุมัติการลาแล้ว",
  "hr.leaveRejected": "ปฏิเสธการลาแล้ว",
  "hr.employeeReactivated": "เปิดใช้พนักงานอีกครั้งแล้ว",
  "hr.employeeDeactivated": "ปิดใช้พนักงานแล้ว",
  "hr.allowancesUpdated": "อัปเดตเบี้ยเลี้ยงแล้ว",
  "hr.deductionsUpdated": "อัปเดตรายการหักแล้ว",
  "hr.verifyDocumentQ": "ยืนยันตรวจสอบเอกสาร?",
  "hr.rejectDocumentQ": "ปฏิเสธเอกสาร?",
  "hr.approveLeaveQ": "อนุมัติการลา?",
  "hr.rejectLeaveQ": "ปฏิเสธการลา?",
  "hr.rateVoided": "ยกเลิกเรทแล้ว",
  "hr.voidedRepriced": "ยกเลิกแล้ว — คิดราคาใหม่ {n} ใบงาน",
  "hr.tripRateDriverDefault": "เรทตั้งต้นรายบุคคล",
  "hr.tripRateRoute": "เรทรายบุคคลตามเส้นทาง",
  "hr.tripRateContract": "เรทรายบุคคลตามสัญญา",
  "hr.driverDefaultRateHint": "เรทต่อเที่ยวของคนขับคนนี้สำหรับเที่ยวที่ไม่มีเรทเฉพาะ — บันทึกแบบเพิ่มอย่างเดียว ไม่ลบประวัติ",
  "hr.newRate": "เรทใหม่",
  "hr.newRouteRate": "เรทเส้นทางใหม่",
  "hr.newContractRate": "เรทสัญญาใหม่",
  "hr.notPayableYet": "ยังไม่ถึงกำหนดจ่าย — ยอดสุทธินับเฉพาะเที่ยวที่จ่ายได้",
  "hr.tripRatePlusCharges": "เรทต่อเที่ยว ± ค่าใช้จ่ายปฏิบัติการ",
  "hr.setManualRate": "ตั้งเรทเองสำหรับใบงานนี้",
  "common.enabled": "เปิดใช้",
  "hr.leaveBalanceYear": "วันลาคงเหลือ ({year})",
  "hr.bankTransfer": "โอนเงินผ่านธนาคาร",
  "hr.addPayee": "เพิ่มผู้รับเงิน",
  "hr.belowFloorThreshold": "ต่ำกว่าเกณฑ์ขั้นต่ำ ({n}/100) — ต้องตรวจสอบ",
  "hr.editDriverTeam": "แก้ไขทีมคนขับ",
  "hr.newDriverTeam": "ทีมคนขับใหม่",
  "hr.createTeam": "สร้างทีม",
  "hr.showingTeams": "แสดง {n} จาก {total} ทีม",
  "common.exportXlsx": "ส่งออก .xlsx",
  "hr.driverGroup": "ทีมคนขับ",
  "hr.leaveLegend": "ทึบ = อนุมัติแล้ว · จาง = รออนุมัติ",
  "hr.documents": "เอกสาร",
  "hr.document": "เอกสาร",
  "hr.presetActiveDrivers": "คนขับที่ใช้งาน",
  "hr.presetTrialPeriod": "ช่วงทดลองงาน",
  "hr.presetLicenseExpiring": "ใบขับขี่ใกล้หมดอายุ",
  "common.maxSize": "สูงสุด {mb} MB",
  "fleet.owner": "เจ้าของ",
  "fleet.odometer": "เลขไมล์",
  "fleet.dueCompleted": "กำหนด / เสร็จ",
  "fleet.woMaintenance": "ซ่อมบำรุง",
  "fleet.woRepair": "ซ่อม",
  "fleet.woInspection": "ตรวจสภาพ",
  "fleet.woFuel": "น้ำมัน",
  "common.importRows": "นำเข้า {rows}",
  "common.nRows": "{n} แถว",
  "import.hint": "อัปโหลด CSV — ดูตัวอย่างและแก้ข้อผิดพลาดก่อนบันทึก",
  "import.downloadSample": "ดาวน์โหลด CSV ตัวอย่าง",
  "import.clickToSelect": "คลิกเพื่อเลือก CSV (หรือลากไฟล์มาวาง)",
  "fleet.uploadFailed": "อัปโหลดไม่สำเร็จ",
  "common.uploading": "กำลังอัปโหลด…",
  "fleet.removePhotoConfirm": "ลบรูป {label}? ย้อนกลับไม่ได้",
  "fleet.saveGpsContract": "บันทึกสัญญา GPS",
  "fleet.saveTaxRecord": "บันทึกรายการภาษี",
  "fleet.saveFinanceRecord": "บันทึกรายการสินเชื่อ",
  "fleet.roadTax": "ภาษีรถประจำปี",
  "common.play": "เล่น",
  "common.pause": "หยุด",
  "warroom.playbackDate": "วันที่:",
  "warroom.playbackVehicle": "รถ:",
  "warroom.noGpsToday": "ไม่มีข้อมูล GPS ของวันนี้",
  "warroom.fromVehiclesWithJobs": "จากรถที่มีงาน",
  "warroom.points": "จุด",
  "fleet.presetActiveVansBkk": "รถตู้ใช้งาน (กทม.)",
  "fleet.presetNoGps": "ไม่มี GPS",
  "fleet.editVehicle": "แก้ไขรถ",
  "fleet.createVehicle": "สร้างรถ",
  "fleet.addPartAfterPrefix": "เพิ่มส่วนหลัง {prefix}",
  "common.expectedHeader": "หัวคอลัมน์ที่ต้องมี:",
  "fleet.rented": "เช่าระยะสั้น",
  "fleet.editGpsContract": "แก้ไขสัญญา GPS",
  "fleet.addGpsContract": "เพิ่มสัญญา GPS",
  "fleet.saveContract": "บันทึกสัญญา",
  "common.allProviders": "ทุกผู้ให้บริการ",
  "fleet.expiredDaysAgo": "หมดอายุแล้ว {n} วัน",
  "fleet.overdueDaysAgo": "เลยกำหนด {n} วัน",
  "fleet.daysRemaining": "เหลือ {n} วัน",
  "fleet.editInsurancePolicy": "แก้ไขกรมธรรม์",
  "fleet.addInsurancePolicy": "เพิ่มกรมธรรม์",
  "fleet.addCmiPolicy": "เพิ่ม พ.ร.บ.",
  "fleet.editCmiPolicy": "แก้ไข พ.ร.บ.",
  "fleet.savePolicy": "บันทึกกรมธรรม์",
  "fleet.createWorkOrder": "สร้างใบสั่งซ่อม",
  "fleet.editPmSchedule": "แก้ไขรอบบำรุงรักษา",
  "fleet.saveSchedule": "บันทึกตาราง",
  "fleet.apiSeats": "ที่นั่งจาก API: {n}",
  "common.savedBang": "บันทึกแล้ว!",
  "fleet.createTemplate": "สร้างชุดตรวจ",
  "common.enterValue": "กรอกค่า",
  "fleet.submitInspection": "ส่งผลตรวจ",
  "common.submitting": "กำลังส่ง…",
  "common.closing": "กำลังปิด…",
  "fleet.noTemplatesYet": "ยังไม่มีชุดตรวจ สร้างชุดแรกเพื่อเริ่มต้น",
  "fleet.noTemplatesMatch": "ไม่มีชุดตรวจที่ตรงกับตัวกรอง",
  "fleet.noSubmissionsYet": "ยังไม่มีรายการตรวจที่ส่งเข้ามา",
  "fleet.noSubmissionsMatch": "ไม่มีรายการตรวจที่ตรงกับตัวกรอง",
  "fleet.searchTemplates": "ค้นหาชื่อชุดตรวจ รายละเอียด กลุ่ม…",
  "fleet.searchSubmissions": "ค้นหาชุดตรวจ รถ ผู้ตรวจ…",
  "fleet.submittedAt": "ส่งเมื่อ",
  "fleet.closedAt": "ปิดเมื่อ",
  "hr.provinceRatesSubtitle": "{n} อัตรา · ค่าตอบแทนต่อเที่ยวตามจังหวัดปลายทาง",
  "hr.noProvinceRatesMatch": "ไม่มีอัตราจังหวัดที่ตรงกับคำค้นหา",
  "hr.noProvinceRatesYet": "ยังไม่มีอัตราจังหวัด เพิ่มอันแรกเพื่อกำหนดค่าตอบแทนต่อเที่ยวของจังหวัดปลายทาง",
  "fleet.createActiveTemplateFirst": "สร้างชุดตรวจที่เปิดใช้ก่อน",
  "fleet.showingTemplates": "แสดง {n} จาก {total} ชุดตรวจ",
  "fleet.showingSubmissions": "แสดง {n} จาก {total} รายการตรวจ",
  "comms.mockBanner": "API ยังอยู่ระหว่างพัฒนา — แสดงข้อมูลตัวอย่าง",
  "comms.selectChat": "เลือกห้องแชท",
  "status.unassigned": "ยังไม่มอบหมาย",
  "common.noChanges": "ไม่มีการแก้ไข",
  "common.comingSoon": "เร็ว ๆ นี้",
  "common.on": "เปิด",
  "common.off": "ปิด",
  "common.unpin": "เลิกปักหมุด",
  "common.pinToTop": "ปักหมุดไว้บนสุด",
  "warroom.latePlus": "สาย +{min}",
  "warroom.couldNotLoadOverview": "โหลดภาพรวมไม่สำเร็จ: {error}",
  "warroom.cancelTicketConfirm": "ยกเลิกใบงาน {id}?",
  "warroom.confirmCancel": "ยืนยันยกเลิก",
  "warroom.openDetails": "เปิดรายละเอียด",
  "warroom.viewRoute": "ดูเส้นทาง",
  "warroom.zoomToVehicle": "ซูมไปที่รถ",
  "warroom.viewProfile": "ดูโปรไฟล์",
  "warroom.jobs": "งาน",
  "warroom.routes": "เส้นทาง",
  "warroom.drivers": "คนขับ",
  "warroom.pausePlayback": "หยุดเล่นย้อนหลัง",
  "warroom.playPlayback": "เล่นย้อนหลัง",
  "warroom.stateOnTime": "ตรงเวลา",
  "warroom.stateLate": "สาย",
  "warroom.stateCritical": "วิกฤต",
  "warroom.stateNoGps": "ไม่มี GPS",
  "warroom.stateFree": "ว่าง",
  "warroom.live": "สด",
  "ops.confirmBy": "ยืนยันภายใน",
  "ops.lateThreshold": "เกณฑ์สาย",
  "ops.seats": "ที่นั่ง",
  "comms.featureComingSoon": "{feature} — เร็ว ๆ นี้",
  "comms.readBy": "อ่านแล้วโดย: {names}",
  "comms.newAnnouncement": "ประกาศใหม่",
  "comms.enterTitle": "กรุณากรอกหัวข้อประกาศ",
  "comms.draftSaved": "บันทึกแบบร่างแล้ว",
  "comms.errorRetry": "เกิดข้อผิดพลาด ลองใหม่อีกครั้ง",
  "comms.published": "เผยแพร่ประกาศแล้ว",
  "common.joined": "เข้าร่วม",
  "ops.bookedAt": "จองเมื่อ",
  "portal.noContractsMatch": "ไม่มีสัญญาที่ตรงกับตัวกรอง",
  "portal.noContractsLinked": "ยังไม่มีสัญญาที่เชื่อมกับพอร์ทัลนี้",
  "ops.contractId": "รหัสสัญญา",
  "ops.customer": "ลูกค้า",
  "common.linked": "เชื่อม",
  "common.addedBy": "เพิ่มโดย",
  "common.added": "เพิ่มเมื่อ",
  "portal.noMembersMatch": "ไม่มีสมาชิกที่ตรงกับตัวกรอง",
  "portal.noMembersYet": "ยังไม่มีสมาชิกในพอร์ทัลนี้",
  "ops.autoResolve": "แก้อัตโนมัติ",
  "common.activate": "เปิดใช้งาน",
  "ops.noPendingOverflow": "ไม่มีผู้โดยสารตกค้าง",
  "ops.noResolvedItems": "ยังไม่มีรายการที่แก้แล้ว",
  "common.deselectAll": "ยกเลิกเลือกทั้งหมด",
  "ops.resolveDetourTo": "แก้ไข: อ้อมไปรับด้วย {id}",
  "ops.resolveDetourToMove": "แก้ไข: อ้อมไปรับด้วย {id} (ย้าย {n} คน)",
  "ops.routePreview": "ตัวอย่างเส้นทาง: {title}",
  "ops.canDetourCount": "อ้อมรับได้ ({n})",
  "ops.autoResolveAll": "แก้อัตโนมัติทั้งหมด ({n})",
  "ops.projectedSavings": "ประหยัดได้ประมาณ {amount}/เดือน",
  "ops.detourFirst": "อ้อมรับก่อน",
  "ops.newVehicleFirst": "จัดรถใหม่ก่อน",
  "ops.detourFirstDesc": "ลองให้รถเดิมอ้อมรับก่อน ค่อยจัดรถใหม่",
  "ops.newVehicleFirstDesc": "จัดรถใหม่เป็นหลัก ถ้าไม่ได้ค่อยอ้อมรับ",
  "ops.detourModeExistingDesc": "ผู้โดยสารเดินไปขึ้นที่จุดนั้น",
  "ops.detourModeOriginalDesc": "รถอ้อมไปรับที่จุดจอดเดิม",
  "ops.detourModeCustomDesc": "เลือกจุดนัดพบ",
  "ops.detourModeExisting": "ใช้จุดจอดที่มีอยู่",
  "ops.detourModeOriginal": "อ้อมไปจุดจอดเดิม",
  "ops.detourModeCustom": "ปักหมุดเอง",
  "status.available": "ว่าง",
  "ops.passengerAddedLinked": "เพิ่มผู้โดยสารแล้ว — เชื่อมกับบัญชีเดิม",
  "ops.suspendPassengerConfirm": "ระงับผู้โดยสารรายนี้? จะจองหรือเช็คอินไม่ได้",
  "ops.passengerSuspended": "ระงับผู้โดยสารแล้ว",
  "ops.passengerReactivated": "เปิดใช้ผู้โดยสารอีกครั้งแล้ว",
  "ops.setByPassenger": "ผู้โดยสารตั้งเอง",
  "ops.notAvailableMultiOrg": "ไม่พร้อมใช้ — บัญชีนี้อยู่ใน {n} องค์กร",
  "portal.activated": "เปิดใช้พอร์ทัลแล้ว",
  "portal.deactivated": "ปิดพอร์ทัลแล้ว",
  "portal.activateConfirm": "เปิดใช้พอร์ทัลนี้?",
  "portal.deactivateConfirm": "ปิดพอร์ทัลนี้?",
  "portal.activateNote": "พอร์ทัลจะเปิดใช้งาน (เมื่อแอปผู้โดยสารพร้อม สมาชิกจะมองเห็นพอร์ทัลที่เปิดอยู่)",
  "portal.deactivateNote": "พอร์ทัลจะถูกปิด ยังอยู่ในรายการแต่แอปผู้โดยสารจะไม่แสดง",
  "portal.deleteNote": "ยกเลิกการเชื่อมสัญญา {contracts} รายการ และนำสมาชิกออก {members} คน — ตัวสัญญาและผู้โดยสารยังอยู่",
  "portal.deleteConfirmNote": "จะยกเลิกการเชื่อมสัญญา {contracts} รายการ และนำสมาชิกออก {members} คน ย้อนกลับไม่ได้",
  "ops.driverPassedPoint": "คนขับผ่านจุดนี้ไปแล้ว",
  "hr.deduct": "หัก",
  "hr.ticketNet": "ยอดสุทธิใบนี้",
  "hr.pendingNotCounted": "รออนุมัติ {n} รายการ — ยังไม่นับ",
  "hr.setOnFinanceCard": "ตั้งที่การ์ดการเงินรายคน",
  "hr.payBasisSsoElsewhere": "ประเภทการจ้างและประกันสังคมตั้งที่เดียว —",
  "opening.title": "ยกยอดตั้งต้น",
  "opening.lead": "ใช้ครั้งเดียวตอนเริ่มจ่ายผ่านระบบ — ของที่จ่ายด้วยวิธีเดิมไปแล้วต้องไม่ถูกจ่ายซ้ำ และหนี้ที่ยังผ่อนอยู่ต้องเริ่มจากยอดที่ถูก",
  "opening.chip": "ยกยอด",
  "opening.cut.title": "1 · ปิดรายการก่อนวันเริ่มใช้ระบบ",
  "opening.cut.day": "เริ่มจ่ายผ่านระบบตั้งแต่ {day}",
  "opening.cut.none": "ก่อน {day} ไม่มีรายการที่ยังรอจ่าย",
  "opening.cut.found": "ก่อนวันนั้นมี {n} รายการของ {people} คนที่ยังรอจ่าย ({from} – {to})",
  "opening.cut.out": "บริษัทต้องจ่าย",
  "opening.cut.in": "บริษัทต้องหักคืน",
  "opening.cut.kindLine": "{n} รายการ · จ่าย {out} · หัก {in}",
  "opening.cut.after": "กดแล้วทุกรายการนี้จะเป็น \"ดำเนินการแล้ว\" (จ่ายด้วยวิธีเดิมแล้ว) ไม่เข้าบิลอีก — รายการตั้งแต่ {day} เข้าบิลตามปกติ",
  "opening.cut.evidence": "หลักฐาน",
  "opening.cut.evidenceHint": "เช่น ไฟล์สรุปเงินเดือนเดิมของงวดสุดท้าย — แนบไว้กับทุกรายการที่ปิด",
  "opening.cut.note": "บันทึก",
  "opening.cut.notePh": "เช่น จ่ายด้วย Excel ถึงงวด 30 ก.ย. แล้ว",
  "opening.cut.go": "ปิด {n} รายการเป็น \"ดำเนินการแล้ว\"",
  "opening.cut.done": "ยกยอด {code} แล้ว — ปิด {n} รายการ",
  "opening.cut.failed": "ยกยอดไม่สำเร็จ",
  "opening.cut.made": "ที่ทำไปแล้ว",
  "opening.cut.rowLine": "ก่อน {day} · {n} รายการ · {people} คน",
  "opening.cut.undone": "ย้อนแล้ว {n} รายการ ({reason})",
  "opening.confirm.title": "ปิดรายการก่อนวันเริ่มใช้ระบบ?",
  "opening.confirm.body": "{n} รายการของ {people} คนก่อน {day} (จ่าย {out} · หัก {in}) จะเป็น \"ดำเนินการแล้ว\" และไม่เข้าบิลอีก ย้อนได้ทั้งชุดถ้ายังไม่มีรายการไหนเข้ารอบจ่าย",
  "opening.confirm.go": "ปิดเลย",
  "opening.undo.button": "ย้อน",
  "opening.undo.title": "ย้อนยกยอด {code}",
  "opening.undo.what": "{n} รายการก่อน {day} จะกลับไปรอจ่าย — รายการที่เข้ารอบจ่ายแล้วคงไว้",
  "opening.undo.reason": "เหตุผล",
  "opening.undo.confirm": "ย้อนทั้งชุด",
  "opening.undo.done": "ย้อน {code} แล้ว — กลับไปรอจ่าย {n} รายการ · คงไว้ {kept}",
  "opening.undo.failed": "ย้อนไม่สำเร็จ",
  "opening.debts.title": "2 · หนี้ที่ยังผ่อนอยู่",
  "opening.debts.lead": "เงินยืม เงินเบิก เงินค้ำ หรือค่าเสียหายที่ยังผ่อนอยู่วันเริ่มใช้ระบบ — ใส่ยอดเต็ม ยอดที่คืนแล้ว และงวดที่เหลือ เฉพาะงวดที่เหลือจะเข้าบิล",
  "opening.debts.add": "ยกยอดหนี้",
  "opening.debts.none": "ยังไม่มีหนี้ที่ยกยอดมา",
  "opening.debts.line": "คืนแล้ว {back} · เหลือ {left}",
  "opening.debt.title": "ยกยอดหนี้ที่ยังผ่อนอยู่",
  "opening.debt.save": "บันทึก",
  "opening.debt.saved": "ยกยอด {code} แล้ว",
  "opening.debt.failed": "ยกยอดไม่สำเร็จ",
  "opening.debt.who": "ของใคร · เป็นหนี้แบบไหน",
  "opening.debt.kind": "เป็นหนี้แบบไหน",
  "opening.debt.kind.loan": "บริษัทให้ยืม/ให้เบิกไป ยังผ่อนคืนอยู่",
  "opening.debt.kind.plan": "บริษัทหักเป็นงวดอยู่ เช่น เงินค้ำ ค่าเสียหาย",
  "opening.debt.item": "รายการ",
  "opening.debt.q1.loan": "ยืมไปทั้งหมดเท่าไร",
  "opening.debt.q1.plan": "ยอดเต็มที่ต้องหักทั้งหมด",
  "opening.debt.interestHint": "ดอกเบี้ยแบบตัวเลข (ถ้ามี) รวมอยู่ในยอดเต็มแล้ว",
  "opening.debt.total": "ยอดเต็ม",
  "opening.debt.began": "เริ่มเมื่อ {day}",
  "opening.debt.q2.loan": "คืนมาแล้วเท่าไร (ก่อนเริ่มใช้ระบบ)",
  "opening.debt.q2.plan": "หักไปแล้วเท่าไร (ก่อนเริ่มใช้ระบบ)",
  "opening.debt.returned": "ยอดที่คืนแล้ว",
  "opening.debt.left": "เหลือ {left}",
  "opening.debt.q3": "ที่เหลือผ่อนกี่งวด เริ่มงวดถัดไปวันไหน",
  "opening.debt.next": "งวดถัดไป {day}",
  "opening.debt.evidence": "หลักฐาน",
  "opening.debt.evidenceHint": "เช่น สัญญาเงินกู้ หรือหน้าสมุดคุมหนี้เดิม",
  "opening.debt.what": "เป็นเงินอะไร (บังคับ)",
  "opening.debt.notePh": "เช่น เงินกู้ซ่อมบ้าน (สมุดเก่า)",
  "opening.debt.sum.incomplete": "เลือกคน รายการ และใส่ยอดเต็ม",
  "opening.debt.sum.loan": "{who} ยืม{item} ทั้งหมด {total} คืนแล้ว {back} — เหลือ {left} ผ่อน {n} งวด งวดละ {per} ถึง {end}",
  "opening.debt.sum.plan": "{who} {item} ยอดเต็ม {total} หักแล้ว {back} — เหลือ {left} หัก {n} งวด งวดละ {per} ถึง {end}",
  "finance.err.opening_evidence": "แนบหลักฐาน",
  "finance.err.opening_total": "ยอดเต็มต้องมากกว่าศูนย์ (และมากกว่าดอกเบี้ย)",
  "finance.err.opening_returned": "ยอดที่คืนแล้วไม่ถูก",
  "finance.err.opening_nothing_left": "คืนครบแล้ว ไม่มีอะไรต้องยกยอด",
  "finance.err.opening_count": "จำนวนงวดที่เหลือไม่ถูก (1–60 และไม่เกินยอดที่เหลือ)",
  "finance.err.opening_began": "วันเริ่มต้องไม่อยู่ในอนาคต",
  "finance.err.opening_start": "งวดถัดไปต้องไม่ก่อนวันเริ่ม",
  "hold.action": "พักไว้",
  "hold.actionRow": "พักรายการนี้",
  "hold.title": "พักรายการนี้ไว้ก่อน",
  "hold.save": "พักไว้",
  "hold.saved": "พัก {doc} ไว้แล้ว — นัดกลับมาจ่าย {day}",
  "hold.failed": "พักไม่สำเร็จ",
  "hold.why": "ทำไมพัก",
  "hold.whyPh": "เช่น รอใบยืนยันจาก OP",
  "hold.when": "นัดกลับมาจ่าย",
  "hold.time": "เวลานัด",
  // NIGHT-1023 N5 — everything of one person held
  "holdAll.open": "พักทั้งหมดของคนนี้",
  "holdAll.title": "พักทั้งหมดของ {name}",
  "holdAll.what": "ทุกรายการของคนนี้ที่ยังไม่เข้ารอบจ่ายจะเป็น \"พักไว้\" และบิลของคนนี้จะหยิบเข้ารอบจ่ายไม่ได้จนกว่าจะปลดพัก รายการที่เข้ามาใหม่ระหว่างนี้รออยู่ในบิล แต่ก็เข้ารอบไม่ได้เหมือนกัน",
  "holdAll.whyPh": "เช่น ติดต่อไม่ได้ / รอเคลียร์ค่าเสียหาย / บัญชีธนาคารยังไม่ครบ",
  "holdAll.when": "พักถึงวันไหน (ถึงวันนัดระบบปลดให้เอง)",
  "holdAll.while": "ถึงวันนัด ระบบปลดพักให้อัตโนมัติ ทุกรายการกลับเข้าบิลวันของมัน — ปลดก่อนก็ได้จากหน้านี้",
  "holdAll.save": "พักทั้งหมด",
  "holdAll.saved": "พักทั้งหมดของ {name} แล้ว {n} รายการ ถึง {day}",
  "holdAll.banner": "พักทั้งหมดของคนนี้ไว้ ถึง {day}",
  "holdAll.bannerWhy": "{reason} · โดย {by} · พักไว้ {n} รายการ · ระหว่างนี้บิลของคนนี้เข้ารอบจ่ายไม่ได้",
  "releaseAll.open": "ปลดพักทั้งหมด",
  "releaseAll.title": "ปลดพักทั้งหมดของ {name}",
  "releaseAll.what": "รายการที่พักไว้ด้วยคำสั่งนี้ ({n} รายการ) กลับเข้าบิลวันของมัน และบิลของคนนี้หยิบเข้ารอบจ่ายได้อีกครั้ง",
  "releaseAll.save": "ปลดพัก",
  "releaseAll.saved": "ปลดพักทั้งหมดของ {name} แล้ว {n} รายการ",
  "hold.whenPast": "วันเวลานัดต้องอยู่หลังตอนนี้",
  "hold.evidence": "หลักฐาน",
  "hold.while": "ระหว่างพัก รายการนี้ไม่ถูกนับที่ไหนเลย — ไม่เข้าบิล ไม่เข้ารอบ ไม่เป็นฐานของภาษีหรือค่าดำเนินการ ระบบเตือนที่มุมจอเมื่อใกล้และถึงวันนัด",
  "hold.wasHeld": "เคยพัก: \"{reason}\"",
  "release.title": "ปลดพัก",
  "release.save": "ปลดพัก",
  "release.saved": "ปลดพัก {doc} แล้ว",
  "release.failed": "ปลดพักไม่สำเร็จ",
  "release.where": "ให้ไปอยู่บิลไหน",
  "release.to.original": "จ่ายย้อนในรอบเดิม",
  "release.to.originalHint": "กลับไปบิลของวันที่รายการเกิด (ถ้าบิลนั้นจ่ายไปแล้ว ไปบิลถัดไปที่ยังเปิด)",
  "release.to.latest": "รวมกับรอบล่าสุด",
  "release.to.latestHint": "เข้าบิลที่เปิดอยู่วันนี้",
  "release.to.future": "กำหนดรอบในอนาคต",
  "release.to.futureHint": "เข้าบิลที่มีวันที่เลือก",
  "release.on": "บิลที่มีวัน {day}",
  "release.why": "เหตุผล (บังคับ)",
  "release.whyPh": "เช่น ได้ใบยืนยันจาก OP แล้ว",
  "holds.people": "พักทั้งคนไว้ {n} คน",
  "holds.personLine": "ถึง {day} · {reason} · {n} รายการ (ถึงวันนัดระบบปลดให้เอง)",
  "holds.title": "ของที่พักไว้",
  "holds.lead": "รายการที่พักไว้ไม่ถูกนับที่ไหนเลยจนกว่าจะปลดพัก — เรียงตามวันนัด",
  "holds.totals": "พักไว้ {n} รายการ · ค้างจ่าย {out} · ค้างหัก {in}",
  "holds.empty": "ไม่มีรายการที่พักไว้",
  "holds.until": "นัด {day} {time}",
  "holds.due": "ถึงนัดแล้ว {day} {time}",
  "holds.noDay": "ไม่มีวันนัด",
  "holds.by": "พักโดย {name} {day}",
  "holds.release": "ปลดพัก",
  "finHome.act.openHolds": "ดูของที่พักไว้",
  "settle.action": "จบนอกระบบ",
  "settle.actionRow": "บันทึกว่าจบนอกระบบ",
  "settle.title": "ดำเนินการแล้ว (จบนอกระบบ)",
  "settle.save": "บันทึก",
  "settle.saved": "{doc} ดำเนินการแล้ว — ไม่เข้าทำจ่ายอีก",
  "settle.failed": "บันทึกไม่สำเร็จ",
  "settle.how": "จบอย่างไร",
  "settle.how.paid_outside": "จ่าย/รับเงินนอกรอบแล้ว (เงินสด · โอนเอง)",
  "settle.how.done": "จบด้วยวิธีอื่น (หักกลบ · ยกเลิกตามตกลง)",
  "settle.why": "บันทึก",
  "settle.whyPh": "เช่น จ่ายเงินสดที่หน้างาน 12 ต.ค.",
  "settle.after": "รายการนี้จะไม่เข้าบิลและรอบจ่ายอีก แต่ยังเห็นในประวัติของคนนี้และในสมุดรายการ",
  "fixRow.void.action": "ยกเลิกรายการ",
  "fixRow.void.title": "ยกเลิกรายการนี้",
  "fixRow.void.after": "รายการนี้จะไม่นับในบิลและรอบจ่ายอีก แต่ยังเห็นในประวัติ",
  "fixRow.void.afterDebt": "งวดที่ยังไม่จ่ายของหนี้ก้อนนี้จะถูกยกเลิกไปพร้อมกัน",
  "fixRow.reverse.action": "กลับรายการ",
  "fixRow.reverse.title": "กลับรายการที่จ่ายแล้ว",
  "fixRow.reverse.after": "ระบบจะทำรายการยอดเท่ากันฝั่งตรงข้ามลงบิลถัดไปของ {who} ให้เอง",
  "fixRow.thisPerson": "คนนี้",
  "fixRow.why": "เหตุผล (บังคับ)",
  "fixRow.whyPh": "เช่น ลงซ้ำ · ลงผิดคน · ยอดผิด",
  "fixRow.voided": "ยกเลิก {doc} แล้ว",
  "fixRow.reversed": "กลับรายการ {doc} แล้ว — {newDoc} จะอยู่ในบิลถัดไป",
  "fixRow.failed": "ทำไม่สำเร็จ",
  "fixRow.at.ticket": "แก้ที่ใบงาน",
  "fixRow.at.profile": "แก้ที่พนักงาน",
  "fixRow.at.debt": "แก้ที่หน้าหนี้",
  "review.open": "ตรวจแบบผู้อนุมัติ",
  "review.notFound": "ไม่พบรอบจ่ายนี้",
  "review.sub": "จ่าย {day} · {people} คน · {bills} บิล",
  "review.total": "ยอดรอบนี้",
  "review.vsPrev": "รอบก่อน ({name}) {total} · ต่าง {delta} ({pct})",
  "review.noPrev": "ยังไม่มีรอบที่จ่ายแล้วให้เทียบ",
  "review.signals": "สิ่งที่ควรดูก่อนอนุมัติ",
  "review.noSignals": "ไม่มีอะไรผิดปกติ",
  // APPROVER-DASHBOARD — the approver's page as a dashboard
  "board.title": "ตรวจรอบจ่ายก่อนอนุมัติ",
  "board.q": "ยอดรอบนี้มาจากไหน ใครควรดูก่อน และพร้อมอนุมัติหรือยัง",
  "board.meta": "ข้อมูล {name} · จ่าย {day} · {people} คน · {bills} บิล",
  "board.metaPrev": "เทียบกับ {name}",
  "board.memo": "พิมพ์บันทึกอนุมัติ",
  "board.memoPrint": "บันทึกเป็น PDF",
  "board.memoTitle": "บันทึกขออนุมัติรอบจ่าย",
  "board.baht": "บาท",
  "board.people": "{n} คน",
  "board.unitPeople": "คน",
  "board.rows": "{n} รายการ",
  "board.charts.open": "ดูกราฟของรอบนี้",
  "board.charts.fold": "ย่อกราฟ",
  "board.hero.label": "ยอดที่จะโอนรอบนี้",
  "board.hero.from": "จาก{name}",
  "board.hero.rounds": "รอบที่จ่ายแล้ว เทียบรอบนี้",
  "board.hero.thisRound": "รอบนี้",
  "board.story.a": "รอบนี้จ่าย {n} คน",
  "board.story.up": "สูงกว่ารอบก่อน",
  "board.story.down": "ต่ำกว่ารอบก่อน",
  "board.story.because": " ส่วนใหญ่มาจาก",
  "board.story.tickets": " · ค่าเที่ยวจากใบงาน ",
  "board.story.ticketsShare": " คิดเป็น {pct}% ของยอดรวมก่อนหักรายการระบบ (ไม่ใช่ส่วนของยอดที่จะโอน)",
  "board.story.look": "ควรดูก่อน:",
  "board.ready.title": "พร้อมอนุมัติแค่ไหน",
  "board.ready.q": "นับจากคนในรอบ ตรวจบิลแล้วและไม่มีเรื่องค้าง",
  "board.ready.ring": "ตรวจบิลแล้ว {n} จาก {of} คน",
  "board.ready.checked": "ตรวจแล้ว {n}/{of} คน",
  "board.ready.clean": "ตรวจแล้ว วางใจได้",
  "board.ready.withIssue": "ตรวจแล้ว ยังมีเรื่องให้ดู",
  "board.ready.unchecked": "ยังไม่ตรวจ",
  "board.ready.kvChecked": "ตรวจบิลแล้ว",
  "board.ready.kvSample": "สุ่มตรวจ",
  "board.ready.kvIssues": "เรื่องที่ควรดู",
  "board.ready.kvDebt": "หักคืนหนี้รอบนี้",
  "board.wf.title": "จากรอบก่อนมารอบนี้",
  "board.wf.q": "ยอดที่ต่างไปมาจากอะไร — คนที่ไม่อยู่ · คนเดิมได้เปลี่ยน · คนใหม่",
  "board.wf.focal": "{step} ทำให้ยอดเปลี่ยนมากที่สุด",
  "board.wf.prev": "รอบก่อน",
  "board.wf.gone": "ไม่อยู่รอบนี้ {n} คน",
  "board.wf.changed": "คนเดิม {n} คน",
  "board.wf.new": "คนใหม่ {n} คน",
  "board.wf.now": "รอบนี้",
  "board.wf.total": "ยอดรวมรอบ",
  "board.wf.up": "เพิ่ม",
  "board.wf.down": "ลด",
  "board.src.title": "เงินทุกบาทมาจากไหน",
  "board.src.q": "ยิ่งมาจากใบงาน ยิ่งวางใจได้",
  "board.src.focal": "มาจากใบงาน",
  "board.src.evidence": "ดูหลักฐาน",
  "board.src.tickets": "ใบงานที่ปิดแล้ว",
  "board.src.hand": "คนกรอกเอง (หนี้ เบิก รายการมือ)",
  "board.src.profile": "ค่าประจำในโปรไฟล์",
  "board.src.computed": "ระบบคิดเอง (ภาษี ค่าโอน ยกยอด ค่าแรงรายวัน)",
  "board.days.title": "เงินรอบนี้มาจากงานวันไหน",
  "board.days.q": "ยอดตามวันทำงาน · วันที่สูงกว่าเฉลี่ยเกิน 15% เป็นสีส้ม · ชี้แท่งดูยอดวันนั้น",
  "board.days.perDay": "/วัน",
  "board.days.focal": "เฉลี่ย {n} วันที่มีงาน",
  "board.days.thatDay": "ยอดวันนั้น",
  "board.days.avg": "เฉลี่ยต่อวัน",
  "board.days.rows": "รายการ",
  "board.days.foot": "เส้นประ = เฉลี่ยต่อวัน",
  "board.look.q": "กดแถวไหน รายชื่อด้านล่างกรองให้",
  "board.look.focal": "จาก {n} คนในรอบ",
  "board.look.overdueS": "ไม่มีรอบไหนหยิบ — ดูที่หน้ารอบ",
  "board.look.trustedS": "รวม {total} — ตรวจแล้ว · เคยจ่าย · ใกล้ปกติ",
  "board.look.negative": "ยอดติดลบ",
  "board.look.negativeS": "อนุมัติไม่ได้จนกว่าจะแก้",
  "board.look.bank": "บัญชีธนาคารยังไม่ครบ",
  "board.look.bankS": "ไฟล์โอนจ่ายคนกลุ่มนี้ไม่ได้",
  "board.look.shared": "บัญชีเดียวรับเงินหลายคน",
  "board.look.sharedS": "ดูว่าเป็นบัญชีของใครจริง",
  "board.look.payee": "แก้บัญชีหลังล็อกยอด",
  "board.look.payeeS": "ยืนยันกับเจ้าตัวก่อนโอน",
  "board.look.flagged": "บิลมีปัญหาค้าง",
  "board.look.flaggedS": "ยังไม่ได้ปิดเรื่อง",
  "board.look.stale": "บิลเปลี่ยนหลังตรวจ",
  "board.look.staleS": "มีรายการเข้า/แก้หลังตรวจ",
  "board.look.unchecked": "ยังไม่ได้ตรวจบิลก่อนล็อกยอด",
  "board.look.uncheckedS": "เปิดบิลตรวจได้จากรายชื่อ",
  "board.look.high": "ได้มากกว่าปกติชัดเจน",
  "board.look.highS": "ครึ่งเท่าขึ้นไปจากปกติ",
  "board.look.jump": "ยอดต่างจากปกติเกิน 20%",
  "board.look.jumpS": "ดูกราฟเทียบปกติด้านล่าง",
  "board.look.first": "คนใหม่ ไม่เคยจ่ายมาก่อน",
  "board.look.firstS": "ไม่มียอดเดิมให้เทียบ",
  "board.spread.title": "ใครได้เท่าไหร่ เทียบกับปกติ",
  "board.spread.q": "คนเดิม: วงกลมกลวง = ปกติ (เฉลี่ย 3 รอบก่อน) · จุดทึบ = รอบนี้ · คนใหม่ยังไม่มีของเดิมให้เทียบ",
  "board.spread.focal": "ต่างจากปกติเกิน 20%",
  "board.spread.usual": "ปกติ",
  "board.spread.now": "รอบนี้",
  "board.spread.jump": "ต่างจากปกติเกิน 20%",
  "board.spread.noBank": "คนใหม่ที่ยังไม่มีบัญชี",
  "board.spread.new": "คนใหม่ {n} คน",
  "board.memo.figures": "ตัวเลขของรอบ",
  "board.memo.prev": "รอบก่อน ({name})",
  "board.memo.heldOut": "พักออกจากรอบ {n} คน",
  "board.memo.steps": "ขั้นอนุมัติ",
  "board.memo.approver": "ผู้อนุมัติ",
  "board.memo.step": "ขั้น {n} · {name}",
  "board.memo.doneBy": "อนุมัติโดย {name} เมื่อ {at}",
  "board.memo.sample": "สุ่มตรวจ ถูก {ok} ไม่ถูก {bad} จาก {of}",
  "board.memo.waiting": "รออนุมัติ",
  "board.memo.sign": "ลงชื่อ / วันที่",
  "board.memo.printed": "พิมพ์เมื่อ {at}",
  // NIGHT-1023 N4 — the approver's dashboard
  "review.kpi.people": "คนในรอบ",
  "review.kpi.peopleSub": "คนใหม่ {first} · พักออกแล้ว {held}",
  "review.kpi.checked": "ตรวจบิลแล้ว (คน)",
  "review.kpi.checkedSub": "{pct}% ของคนในรอบ",
  "review.kpi.checkedNone": "รอบนี้ล็อกยอดก่อนมีการตรวจบิล",
  "review.kpi.debts": "หักคืนหนี้ในรอบนี้",
  "review.kpi.debtsLent": "ให้ยืมออกพร้อมรอบ {lent} · {n} ก้อน · {people} คน",
  "review.kpi.debtsSub": "{n} ก้อน · {people} คน",
  "review.look.negative": "ยอดติดลบ {n} คน — อนุมัติไม่ได้จนกว่าจะแก้",
  "review.look.bank": "บัญชีธนาคารมีปัญหา {n} คน (ไม่มีบัญชี / ธนาคารที่ไฟล์จ่ายไม่ได้ / เลขบัญชีผิด)",
  "review.look.shared_account": "บัญชีเดียวรับเงินหลายคน {n} คน",
  "review.look.payee_changed": "แก้บัญชีธนาคารหลังล็อกยอด {n} คน",
  "review.look.flagged": "บิลมีปัญหาค้าง {n} คน",
  "review.look.stale": "บิลเปลี่ยนหลังตรวจ {n} คน",
  "review.look.unchecked": "ยังไม่ได้ตรวจบิลก่อนล็อกยอด {n} คน",
  "review.look.high": "ได้มากกว่าปกติชัดเจน {n} คน",
  "review.look.jump": "ยอดต่างจากปกติเกิน 20% {n} คน",
  "review.look.first": "คนใหม่ ไม่เคยจ่ายมาก่อน {n} คน",
  "review.look.show": "ดูรายชื่อ",
  "review.look.showing": "กำลังแสดง",
  "review.lookShort.negative": "ยอดติดลบ",
  "review.lookShort.bank": "บัญชีมีปัญหา",
  "review.lookShort.shared_account": "บัญชีเดียวหลายคน",
  "review.lookShort.payee_changed": "แก้บัญชีหลังล็อก",
  "review.lookShort.flagged": "บิลมีปัญหา",
  "review.lookShort.stale": "บิลเปลี่ยนหลังตรวจ",
  "review.lookShort.unchecked": "ยังไม่ตรวจบิล",
  "review.lookShort.high": "สูงกว่าปกติ",
  "review.lookShort.jump": "ต่างจากปกติ",
  "review.lookShort.first": "คนใหม่",
  "review.trust.title": "วางใจได้",
  "review.trust.body": "{n} คน · {total} ({share}% ของรอบ)",
  "review.trust.none": "ยังไม่มีคนที่ผ่านทุกข้อ",
  "review.trust.rule": "ตรวจบิลแล้ว · เคยจ่ายมาก่อน · ยอดใกล้ปกติ (ต่างไม่เกิน 20%) · ไม่อยู่ในรายการที่ควรดู",
  "review.filter.trusted": "วางใจได้ {n}",
  "review.sort.change": "เรียง: ยอดเปลี่ยนมากสุด",
  "review.sort.total": "เรียง: ยอดสูงสุด",
  "review.sort.name": "เรียง: ชื่อ",
  "review.change": "{sign}{amount} จากปกติ",
  "review.trend": "ยอด {n} รอบก่อนกับรอบนี้",
  "review.comp.title": "ยอดนี้มาจากอะไร",
  "review.comp.items": "ตามประเภทเงิน",
  "review.comp.contracts": "ตามสัญญา",
  "review.comp.noContract": "ไม่ผูกสัญญา (เงินเดือน / รายการอื่น)",
  "review.comp.rows": "{n} รายการ · {people} คน",
  "review.comp.more": "และอีก {n} กลุ่ม",
  "review.heldOut": "พักออกจากรอบนี้แล้ว {n} คน · {total}",
  "review.signal.high": "{n} คนได้มากกว่าปกติชัดเจน",
  "review.signal.payee_changed": "{n} คนถูกแก้บัญชีธนาคารหลังแช่แข็ง",
  "review.signal.negative": "{n} คนยอดติดลบ — ต้องตีกลับแล้วแช่แข็งใหม่ให้ระบบยกยอด",
  "review.signal.overdue": "{n} งวดผ่อนถึงกำหนดแต่ไม่มีรอบไหนหยิบ",
  "review.flag.high": "สูงกว่าปกติ",
  "review.flag.payee_changed": "บัญชีถูกแก้",
  "review.flag.negative": "ติดลบ",
  "review.flag.first": "รอบแรก",
  "review.usual": "ปกติ {total}",
  "review.hold": "พักคนนี้",
  "review.holdTitle": "พัก {name} ออกจากรอบนี้",
  "review.holdWhat": "บิลของคนนี้ ({total}) จะออกจากรอบ และรายการทั้งหมดเป็น \"พักไว้\" จนถึงวันนัด — คนที่เหลืออนุมัติต่อได้",
  "review.holdWhyPh": "เช่น รอยืนยันยอดหนี้กับ HR",
  "review.holdSave": "พักไว้",
  "review.held": "พัก {name} แล้ว — ยอดรอบคิดใหม่",
  "review.holdFailed": "พักไม่สำเร็จ",
  "review.approve": "อนุมัติ {total}",
  "review.sendBack": "ตีกลับ / ดูรายละเอียด",
  "finHome.act.review": "เปิดดูแล้วอนุมัติ",
  "bills.computedLine": "{rate}% ของ {base} · ระบบคิด",
  "financeTxn.kind.computed": "รายการที่ระบบคิด",
  "bills.computedBase": "ของ {base} · ระบบคิด",
  "bills.part.earn": "บวก {amount}",
  "bills.part.given": "ให้ไป (ไม่ใช่รายได้ ไม่คิดภาษี) {amount}",
  "bills.part.pre_tax": "หักก่อนภาษี {amount}",
  "bills.part.post_tax": "หักหลังภาษี (ไม่ลดฐานภาษี) {amount}",
  "bills.monthlyTitle": "เงินเดือน {month}",
  "payBatches.fee": "หักค่าโอน {fee} แล้ว (ครั้งเดียวต่อรอบ)",
  "finHome.todo.unpriced": "ใบงานที่ปิดแล้ว {n} ใบ ({people} คน) ยังไม่มีค่าเที่ยว",
  "finHome.todo.unpricedDetail": "คนที่ได้เงินรายเที่ยวจะไม่ได้อะไรจากใบเหล่านี้จนกว่าจะใส่ค่าเที่ยว",
  "finHome.act.unpriced": "ใส่ค่าเที่ยว",
  "unpriced.title": "ใบงานที่ยังไม่มีค่าเที่ยว",
  "unpriced.lead": "ใบงานที่ปิดแล้วของคนที่ได้เงินรายเที่ยว (62 วันล่าสุด) ที่ค่าเที่ยวเป็นศูนย์ — คนขับจะไม่ได้อะไรจากใบนั้น ใส่ค่าเที่ยวที่นี่ ระบบเขียนลงใบงานให้ และเข้าสมุดรายการในรอบซิงก์ถัดไป",
  "unpriced.empty": "ไม่มีใบงานที่ปิดแล้วแต่ยังไม่มีค่าเที่ยว",
  "unpriced.tickets": "{n} ใบ",
  "unpriced.all": "ใส่เท่ากันทุกใบของคนนี้",
  "unpriced.applyAll": "ใส่ทั้งหมด",
  "unpriced.savePerson": "บันทึก {n} ใบ",
  "unpriced.save": "บันทึก",
  "unpriced.saved": "ใส่ค่าเที่ยวแล้ว {n} ใบ",
  "unpriced.failed": "บันทึกไม่สำเร็จ",
  "unpriced.rate": "ค่าเที่ยว",
  "unpriced.routeRate": "อัตราเส้นทาง {rate}",
  "hr.netToDriver": "สุทธิให้คนขับ",
  "ops.passengerAdded": "เพิ่มผู้โดยสารแล้ว: {name}",
  "ops.createFailed": "สร้างไม่สำเร็จ",
  "ops.ticketAssigned": "มอบหมายใบงานแล้ว",
  "ops.assignFailed": "มอบหมายไม่สำเร็จ",
  "ops.searchRoutesInContract": "ค้นหาเส้นทางในสัญญานี้…",
  "ops.routeNoContract": "ไม่มีสัญญา",
  "ops.searchRoutesInContracts": "ค้นหาเส้นทางใน {n} สัญญาที่เลือก…",
  "ops.contractAdd": "+ เพิ่มสัญญา…",
  "ops.contractSearch": "ค้นหาเลขที่ หัวข้อ หรือชื่อลูกค้า…",
  "ops.contractEnds": "สิ้นสุด",
  "ops.contractRemove": "เอาออก",
  "ops.moreCustomers": "+{n} ลูกค้า",
  "ops.nameAuto": "อัตโนมัติ",
  "ops.nameUseAuto": "ใช้ชื่ออัตโนมัติ",
  "ops.nameFromRoute": "ใบงานมีเส้นทาง ชื่อจึงเป็นชื่อเส้นทาง",
  "ops.createTicket": "สร้างใบงาน",
  "ops.overlapNotAcceptedNote": "คนขับยังไม่รับงานนั้น จึงยังบันทึกได้ — แต่จะวิ่งได้แค่งานเดียว",
  "ops.overlapAcceptedNote": "เวลาทับซ้อนกับงานอื่น บันทึกไม่ได้ — เปลี่ยนคนขับ รถ หรือเวลา",
  "ops.overlapManyNote": "ทับซ้อนกับงานอื่น {n} ใบ บันทึกไม่ได้ — เปลี่ยนคนขับ รถ หรือเวลา",
  "ops.assignNeeds": "ต้องมี:",
  "common.createdAt": "สร้างเมื่อ {when}",
  "ops.income": "รายรับ",
  "ops.expense": "รายจ่าย",
  "ops.saveTemplate": "บันทึกเป็นแม่แบบ",
  "ops.reoptimize": "จัดเส้นทางใหม่",
  "ops.optimizing": "กำลังจัดเส้นทาง…",
  "ops.avoidTollsDesc": "เส้นทางจะไม่ใช้ทางด่วนเก็บค่าผ่านทาง",
  "ops.avoidHighwaysDesc": "เส้นทางจะไม่ใช้มอเตอร์เวย์หรือทางพิเศษ",
  "ops.noRunsWithStatus": "ไม่มีรอบที่สถานะ \"{status}\"",
  "ops.noRunsYet": "ยังไม่มีรอบจัดเส้นทาง — เพิ่มจุดจอดแล้วกดจัดเส้นทางเพื่อเริ่ม",
  "ops.noStopsYet": "ยังไม่มีจุดจอด",
  "ops.searchLocationHint": "ค้นหาสถานที่ด้านบนเพื่อเริ่มต้น",
  "ops.noShowCount": "ไม่มา ({n})",
  "ops.cancellationsCount": "ยกเลิก ({n})",
  "ops.viewRoutes": "ดูเส้นทาง",
  "ops.contractTypeEmployee": "รับ-ส่งพนักงาน",
  "ops.contractTypeStaff": "รับ-ส่งบุคลากร",
  "ops.contractTypeStudent": "รับ-ส่งนักเรียน",
  "ops.contractTypeOther": "อื่นๆ",
  "ops.limitedSeats": "จำกัด · {n} ที่นั่ง",
  "ops.unlimitedSeats": "ไม่จำกัดที่นั่ง",
  "ops.markingNoShow": "กำลังบันทึก…",
  "ops.loadingStops": "กำลังโหลดจุดจอด…",
  "ops.selectPickupStop": "เลือกจุดขึ้นรถ…",
  "ops.noStopsAvailable": "ไม่มีจุดจอด",
  "ops.selectTicketFirst": "เลือกใบงานก่อน",
  "ops.createBooking": "สร้างการจอง",
  "ops.contractNotFound": "ไม่พบสัญญานี้",
  "ops.noContractsYet": "ยังไม่มีสัญญา — กดสร้างสัญญาใหม่เพื่อเริ่มต้น",
  "ops.noContractsMatch": "ไม่มีสัญญาที่ตรงกับตัวกรอง",
  "ops.bookingLimited": "จองได้ · จำกัดที่นั่ง",
  "ops.bookingUnlimited": "จองได้ · ไม่จำกัดที่นั่ง",
  "ops.rejectNewBookings": "ปฏิเสธการจองใหม่",
  "ops.allowAlertTeam": "อนุญาต แต่แจ้งทีม",
  "ops.autoOverflow": "สร้างเที่ยวเสริมอัตโนมัติ",
  "ops.gpsGeofence": "GPS Geofence",
  "ops.qrScan": "สแกน QR",
  "ops.qrScanned": "สแกน QR แล้ว",
  "ops.manualBoard": "บันทึกขึ้นรถเอง",
  "ops.capacitySeats": "ความจุ (ที่นั่ง)",
  "ops.actualBoarded": "ขึ้นรถจริง",
  "ops.boardingRate": "อัตราการขึ้นรถ",
  "ops.boardingReport": "รายงานการขึ้นรถ",
  "ops.viaRoutingOnly": "จุดผ่าน (คำนวณเส้นทางเท่านั้น)",
  "ops.allDates": "ทุกวันที่",
  // ADR 0314 — หน้ารายละเอียดใบงานในพอร์ทัลดูได้อย่างเดียว
  "ops.backToJobTickets": "กลับไปหน้าใบงาน",
  "ops.passengersTotal": "ทั้งหมด {n} คน",
  "ops.passengersCheckedIn": "เช็คอินแล้ว {n} คน",
  "ops.noPassengersBooked": "ยังไม่มีผู้โดยสารจอง",
  "ops.noPassengersMatch": "ไม่มีผู้โดยสารตรงกับตัวกรอง",
  "ops.seatsN": "{n} ที่นั่ง",
  "ops.seatUnassigned": "ไม่ระบุที่นั่ง",
  "ops.seatAssigned": "ระบุที่นั่ง",
  "ops.viewOnlyNotice": "ใบงานนี้ดูได้อย่างเดียว",
  "ops.acceptBy": "ยืนยัน",
  "ops.routeStops": "เส้นทางและจุดจอด",
  "ops.currentStop": "จุดจอดปัจจุบัน",
  "warroom.nowLabel": "ตอนนี้",
  "warroom.jobsLabel": "งาน",
  "warroom.next": "ออกถัดไป",
  "warroom.queued": "คิว",
  "warroom.noJobsQueued": "วันนี้ไม่มีงานในคิว",
  "warroom.upNextToday": "คิวงาน",
  // ADR 0214 — ตัวกรองระดับหน้า: ปุ่มเดียว popover เดียว ชิปข้างนอก
  "warroom.filters": "ตัวกรอง",
  "warroom.clearFilters": "ล้างทั้งหมด",
  // ADR 0215 D2 — "เวลา" กลาง ๆ เพราะเลือกได้แล้วว่าเป็นเวลาไหน; ชิปแสดงป้ายของฟิลด์
  // ADR 0216 — หัวข้อรวมของสองหน้าต่างเวลา + ปุ่มยืนยันในป๊อป
  "warroom.finishedLast24h": "เสร็จสิ้น · ยกเลิก · ปิดงาน · 24 ชม.",
  "warroom.filterTime": "เวลา",
  "warroom.applyFilters": "ใช้ตัวกรอง",
  "warroom.timeFieldConfirm": "เวลายืนยันงาน",
  "warroom.timeFieldStart": "เวลาเริ่มงาน",
  "warroom.filterBoard": "แผนจัดงานจำนวนมาก",
  // ADR 0216 D2 — the inline header control is 104px when unset; the full name
  // does not fit and gets clipped. The popover keeps the full name.
  "warroom.filterBoardShort": "แผนใบงาน",
  "warroom.filterVehicle": "รถ",
  "warroom.boardNone": "ไม่ได้มาจากแผน",
  "warroom.noJobsFiltered": "ไม่มีงานตรงกับตัวกรอง",
  // ADR 0211 — คิวไม่ใช่ของวันนี้อย่างเดียวแล้ว จึงมีตัวเลขสาย และแถวคั่นบอกวัน
  "warroom.lateCount": "สาย",
  // ── ADR 0347 — การ์ดเมื่อกดหมุดรถ ───────────────────────────────────
  "warroom.noDriver": "ไม่ทราบคนขับ",
  "warroom.boundDriver": "คนขับประจำรถ",
  "warroom.atDepot": "จุดจอด",
  "warroom.nextJob": "งานถัดไป",
  "warroom.nextStopAt": "ถึงจุดถัดไป {time}",
  "warroom.openJobDetail": "เปิดรายละเอียดงาน",
  "warroom.fitAll": "ดูทั้งหมด",
  "warroom.fitAllHint": "ซูมออกให้เห็นรถและเส้นทางทั้งหมด",
  // ADR 0343 — งานที่เลยเวลาออกเกินหนึ่งวันทำงานและยังไม่ออก · ไม่ใช่ "สาย"
  "warroom.staleCount": "ค้าง",
  // ADR 0343 — เจ้าของเคาะ 2026-09-15: "+17วัน" ไม่ใช่ "ค้าง 17 วัน"
  // หัวบอร์ดบอกคำแล้ว (`warroom.staleCount` = "ค้าง") แถบจึงบอกแค่ตัวเลข —
  // ไม่ต้องพูดคำเดียวกันสองที่บนจอเดียว และ "+" อ่านออกเองว่าเลยมาแล้วเท่านี้
  // วัดในฟอนต์จริง (Geist): 38.5px จากงบ 57.9px · "+999วัน" 49.8px ก็ยังพอ
  "warroom.dayGroupStale": "+{n}วัน",
  "warroom.dayGroupToday": "วันนี้",
  "warroom.dayGroupJobs": "{n} งาน",
  // ADR 0205 — ป้ายกำกับแท่งสัญญาณเลทบนแถวคิว (4-Archives/plan/tms/0205-...)
  "warroom.confirmLate": "ยังไม่ยืนยัน · เลย {overrun}",
  "warroom.startLate": "ยังไม่ออกรถ · เลย {overrun}",
  "hr.setInterview": "นัดสัมภาษณ์",
  "ops.sameResourceAsCell": "{what} ซ้ำกับอีกช่อง",
  "ops.clashesWith": "ชนกับ",
  "common.resource": "ทรัพยากร",
  "ops.noRoutesInWindow": "ไม่มีเส้นทางในช่วงที่เลือก — แสดงแบบจางทั้งหมด",
  "ops.scrollToLoadMap": "เลื่อนเพื่อโหลดแผนที่…",
  "ops.idleCount": "ว่าง ({n})",
  "ops.conflictsCount": "ชนกัน ({n})",
  "ops.unfilledCount": "ยังไม่เต็ม ({n})",
  "ops.noDatesPickFirst": "ตารางนี้ยังไม่มีวันที่ — เลือกวันที่ก่อน",
  "ops.backhaulRadius": "รัศมีเที่ยวกลับ",
  "ops.driverMinRest": "เวลาพักขั้นต่ำของคนขับ",
  "ops.maxDutyPerDay": "เวลาปฏิบัติงานสูงสุด / คนขับ / วัน",
  "ops.maxDutyHint": "จำกัดช่วงเวลาปฏิบัติงานของคนขับหนึ่งคนในหนึ่งวัน (ค่าตั้งต้น 13)",
  "ops.allOrNothing": "ทั้งหมดหรือไม่เลย — ถ้ามีใบงานใดชนกัน จะไม่สร้างเลยสักใบ",
  "ops.routesDefineColumns": "เส้นทางคือคอลัมน์ — จัดการได้ที่ตาราง",
  "ops.timeLockedToSlot": "ช่วงเวลาผูกกับช่องเส้นทาง — แก้ที่ช่องเพื่อเปลี่ยน",
  "ops.unsavedTripWarning": "มีการแก้ไขเที่ยวนี้ที่ยังไม่บันทึก บันทึกก่อนออกไหม?",
  "ops.deleteTripFor": "ลบเที่ยวของ",
  // ADR 0186 — นำออกจาก cell ที่ publish แล้ว = ยกเลิกใบงานก่อน
  "ops.removeFromSlot": "นำออกจากช่อง",
  "ops.removeFromSlotNamed": "นำ {name} ออกจากช่อง",
  "ops.cancelTicketFailed": "ยกเลิกงานไม่สำเร็จ",
  "ops.jobCancelledAndRemoved": "ยกเลิกงานและนำออกจากผังแล้ว",
  "ops.removeAndCancelJob": "นำออกและยกเลิกงานนี้ใช่หรือไม่?",
  "ops.removeAndCancelJobConfirm": "รถคันนี้มีใบงานของช่องนี้อยู่แล้ว การนำออกจะยกเลิกใบงานนั้นด้วย",
  "ops.cancelWarningAccepted": "คนขับรับงานนี้หรือเริ่มงานแล้ว การยกเลิกจะต้องแจ้งให้คนขับทราบ",
  "ops.cancelWarningDone": "งานนี้เสร็จสิ้นแล้ว การยกเลิกจะไม่ลบข้อมูลที่เกิดขึ้นจริง (GPS, บันทึกขึ้นรถ) แต่จะทำเครื่องหมายงานเป็นยกเลิกเท่านั้น",
  "ops.cancelReasonOptional": "เหตุผล (ไม่บังคับ)",
  "ops.removeAndCancelJobButton": "ยกเลิกงานและนำออก",
  "ops.boardName": "ชื่อบอร์ด",
  "ops.problemsOnly": "เฉพาะที่มีปัญหา",
  "ops.allDaysMinimap": "ทุกวัน",
  "ops.cellsLeftAlone": "{n} ช่องตรงกันอยู่แล้ว ไม่ถูกแตะ",
  "ops.publishFromBoard": "จากบอร์ด {board}",
  "ops.jobsWillBePublished": "ใบงานจะถูกเผยแพร่",
  "ops.nothingToPublish": "ไม่มีอะไรให้เผยแพร่ — ไม่มีใบงานที่จะสร้าง แก้ไข ยกเลิก หรือลบ",
  "ops.nDays": "{n} วัน",
  "ops.nDrivers": "คนขับ {n} คน",
  "ops.nVehicles": "รถ {n} คัน",
  "ops.unassignedSlotsNote": "มีช่องรถ {n} ช่องที่ยังไม่ได้มอบหมายรถ ระบบจะไม่สร้างใบงานให้ช่องเหล่านั้น",
  "ops.nJobsCancelled": "ยกเลิก {n} ใบงาน",
  "ops.nJobsDeleted": "ลบ {n} ใบงาน",
  "ops.createTicketsNow": "สร้างใบงานใหม่ให้คนขับทันที",
  "ops.alreadyUpToDate": "เป็นปัจจุบันอยู่แล้ว",
  "ops.couldNotReadBoard": "อ่านบอร์ดไม่สำเร็จ",
  "ops.publishFailed": "เผยแพร่ไม่สำเร็จ",
  "ops.problemGone": "ปัญหานั้นหมดไปแล้ว",
  "ops.driversCanSeeNow": "— คนขับเห็นได้แล้ว",
  "ops.jobsPublished": "เผยแพร่ {n} ใบงาน",
  "ops.jobsReplaced": "แทนที่ {n} ใบงาน",
  "ops.jobsRemoved": "ถอน {n} ใบงาน",
  "ops.addRouteTitle": "เพิ่มเส้นทาง",
  "ops.editRouteTitle": "แก้ไขเส้นทาง",
  "ops.addRouteBtn": "เพิ่มเส้นทาง",
  "ops.saveRouteBtn": "บันทึกเส้นทาง",
  "ops.route": "เส้นทาง",
  "ops.jobTypeHint": "ค่าตั้งต้นมาจากประเภทงานของเส้นทาง เปลี่ยนได้เฉพาะเส้นทางนี้",
  "ops.acceptDeadlineHint": "กำหนดเวลาให้คนขับยืนยันรับงาน ใช้ตอนเผยแพร่บอร์ดนี้เป็นใบงาน",
  "ops.crossMidnight": "ข้ามเที่ยงคืน — จบวันถัดไป",
  "ops.vehiclesEditHint": "มีผลกับวันที่เพิ่มภายหลัง (ช่องเดิมไม่เปลี่ยน)",
  "ops.vehiclesNewHint": "เติมจำนวนนี้ให้ทุกวันในตารางนี้",
  "ops.slotLabelExample": "เช่น \"กลุ่ม A\", \"รอบ 1\"",
  "ops.slotLabelHint": "ใช้แยกเส้นทางที่ซ้ำกัน ถ้าไม่ใส่ระบบจะใส่เลข #1, #2… ให้เอง",
  "ops.searchRouteTemplateEllipsis": "ค้นหาเส้นทาง…",
  // ADR 0187 Phase 1 — two-column route pairing (Add Route only)
  "ops.pairRoute": "จับคู่เส้นทาง",
  "ops.pairRouteHint": "ปิด = เพิ่มเส้นทางเดียวเหมือนเดิม ค่านี้จำไว้ในเครื่องนี้",
  "ops.routeColumnLabelIn": "เข้า",
  "ops.routeColumnLabelOut": "ออก",
  "ops.routeColumnLabelField": "ชื่อกำกับ",
  "ops.ghostNoPairColumn": "ยังไม่มีคู่เส้นทาง",
  "ops.ghostNoPairCell": "— ไม่มีเที่ยว —",
  "ops.ghostNoPairTitle": "เส้นทางนี้ยังไม่มีคู่ — คลิกเพื่อเพิ่ม",
  "ops.partnerSuggestCompact": "พบคู่เส้นทาง หลัก {partnerCode}",
  "ops.removeColumn": "ลบคอลัมน์นี้",
  "ops.routeLegHeader": "ขาที่ {n}",
  "fleet.ownershipCol": "การถือครอง",
  "common.fileCol": "ไฟล์",
  "safety.caseNo": "เลขที่เคส",
  "fuel.efficiency": "ประสิทธิภาพ",
  "fuel.entries": "รายการ",
  "fuel.std": "เกณฑ์",
  "hr.ticketNo": "เลขที่ใบจ่าย",
  "fuel.totalCost": "ค่าใช้จ่ายรวม",
  "fuel.totalLiters": "ลิตรรวม",
  "ops.createCustomer": "สร้างลูกค้า",
  "common.pageOf": "หน้า {page} / {total}",
  "ops.pageOfRuns": "หน้า {page} / {total} · {n} รอบ",
  "ops.showingCustomers": "แสดง {from}–{to} จาก {total} ราย",
  // ADR 0183 — /jobs/customers i18n gap closure
  "ops.noCustomersMatchSearch": "ไม่พบลูกค้าที่ตรงกับตัวกรอง",
  "ops.noCustomersYet": "ยังไม่มีลูกค้า — กดสร้างลูกค้าใหม่เพื่อเริ่มต้น",
  "common.editItem": "แก้ไข {name}",
  "ops.customerUpdated": "บันทึกลูกค้าแล้ว",
  "ops.customerCreated": "สร้างลูกค้าแล้ว",
  "ops.customerCreatedNo": "สร้างลูกค้า {no} แล้ว",
  "ops.editCustomer": "แก้ไขลูกค้า",
  "ops.editCustomerName": "แก้ไขลูกค้า · {name}",
  "ops.customerNotFound": "ไม่พบลูกค้ารายนี้",
  "ops.customerDeleted": "ลบลูกค้าแล้ว",
  "ops.customerDeleteFailed": "ลบลูกค้าไม่สำเร็จ",
  "common.deleting": "กำลังลบ...",
  "ops.resetRouteMessage": "คืนจุดจอดทั้งหมดกลับเป็นแม่แบบเส้นทางเดิม? การแก้ไขเฉพาะงานนี้จะหายไป",
  "hr.deleteChargeMessage": "ลบ {type}: ฿{amount}?",
  "hr.deleteChargeReceiptNote": " ไฟล์ใบเสร็จจะถูกลบด้วย",
  "ops.cancelJobMessage": "ยืนยันยกเลิก {id}? การกระทำนี้ย้อนกลับไม่ได้ และผู้โดยสารที่จองไว้จะได้รับแจ้ง",
  "ops.jobCancelled": "ยกเลิกงาน {id} แล้ว",
  "ops.changeStatusMessage": "เปลี่ยนสถานะ {id} จาก {from} เป็น {to}?",
  "ops.changeStatusConfirm": "เปลี่ยนเป็น {to}",
  "ops.stopsColShort": "จุด",
  "ops.timeInputHint": "พิมพ์เวลาแบบ 24 ชม. HH:MM เช่น 08:00, 13:00, 23.00 หรือกดไอคอนนาฬิกาในช่องเพื่อเลือกจากรายการ",
  "ops.availFree": "ว่าง",
  "ops.availBusy": "ไม่ว่าง",
  "ops.availOnLeave": "ลา",
  "ops.availOnLeaveHalf": "ลาครึ่งวัน",
  "ops.availOverlap": "ทับซ้อน",
  "ops.availOtherBoard": "บอร์ดอื่น",
  "ops.stopN": "จุดจอด {n}",
  "ops.nStops": "{n} จุดจอด",
  "ops.stopsOverlapWarn": "เขตเช็คอินทับกัน: {stops} ห่างกัน {m} ม. — คนขับต้องขับออกให้พ้นทั้งสองวงก่อนถึงจะเช็คอินจุดที่สองได้ (ลดรัศมีเหลือไม่เกิน {max} ม. ถ้าไม่ต้องการ)",
  "ops.stopsProgress": "{done} / {total} จุดจอด",
  "common.selectEllipsis": "เลือก…",
  "ops.optionalLabel": "ป้ายชื่อ (ไม่บังคับ)",
  "common.nTotal": "ทั้งหมด {n}",
  "ops.nEvents": "{n} เหตุการณ์",
  "ops.appBackground": "เข้าพื้นหลัง:",
  "ops.appKilled": "ปิดแอป:",
  "ops.idColon": "รหัส:",
  "hr.driverPayReadOnly": "อ่านอย่างเดียว — ค่าตอบแทนคนขับจัดการที่",
  "ops.acceptByTime": "ยืนยันก่อน {time}",
  "ops.driverPlusVehicle": "คนขับ + รถ",
  "ops.driverChangedComp": "เปลี่ยนคนขับ — ค่าตอบแทน",
  "hr.routeBase": "อัตราเส้นทาง: ",
  "ops.bookingAndSeats": "การจองและที่นั่ง",
  "ops.saveStopsFailed": "บันทึกจุดจอดไม่สำเร็จ",
  "ops.routeStopsSavedShort": "บันทึกจุดจอดแล้ว",
  "ops.financialsSaved": "บันทึกข้อมูลการเงินแล้ว",
  "ops.availabilityCheckFailed": "ตรวจสอบความว่างไม่สำเร็จ",
  "ops.jobSettingsSaved": "บันทึกการตั้งค่างานแล้ว",
  "ops.saveSettingsFailed": "บันทึกการตั้งค่าไม่สำเร็จ",
  "ops.autosaveIdle": "บันทึกอัตโนมัติ · บันทึกล่าสุด {time}",
  "ops.autosaveNoChanges": "บันทึกอัตโนมัติ",
  "ops.autosaveSaving": "กำลังบันทึก…",
  "ops.autosaveError": "บันทึกไม่สำเร็จ",
  "ops.autosaveReadOnly": "อ่านอย่างเดียว — ไม่มีสิทธิ์แก้ไข",
  "ops.autosaveBlocksStatus": "บันทึกการตั้งค่าไม่สำเร็จ — แก้ก่อนเปลี่ยนสถานะ",
  "ops.autosaveChangedElsewhere": "มีการแก้จากที่อื่น — ค่าของคุณยังอยู่จนกว่าจะบันทึก",
  "ops.statusChangeFailed": "เปลี่ยนสถานะไม่สำเร็จ",
  // ADR 0465 D4 — "Complete anyway" (a cargo job not finished)
  "ops.openCargo.title": "จบงาน {id}?",
  "ops.openCargo.open": "สินค้ายังไม่ได้บันทึก {n} รายการ",
  "ops.openCargo.returns": "ของวางคืนยังไม่มีรูป {n} รายการ",
  "ops.openCargo.confirm": "จบงานแทน",
  "ops.exportingPdf": "กำลังส่งออกรายงานงานเป็น PDF…",
  "hr.compensationOnly": "เฉพาะค่าตอบแทน",
  "hr.replaceReceipt": "เปลี่ยนใบเสร็จ",
  "common.cancelling": "กำลังยกเลิก…",
  "ops.liveUpdateFailed": "อัปเดตสดไม่สำเร็จ — กำลังลองใหม่…",
  "hr.reimburseHint": "เบิกคืน — บวกเข้าค่าตอบแทนคนขับ",
  "hr.deductHint": "หัก — ลบออกจากค่าตอบแทนคนขับ",
  "hr.openReceiptFailed": "เปิดใบเสร็จไม่สำเร็จ",
  "hr.selectChargeType": "กรุณาเลือกประเภทค่าใช้จ่าย",
  "common.enterValidAmount": "กรุณากรอกจำนวนเงินให้ถูกต้อง",
  "hr.saveChargeFailed": "บันทึกค่าใช้จ่ายไม่สำเร็จ",
  "hr.chargeUpdated": "แก้ไขค่าใช้จ่ายแล้ว",
  "hr.chargeApprovalRevoked": "ยกเลิกการอนุมัติค่าใช้จ่ายแล้ว",
  "hr.chargeApproved": "อนุมัติค่าใช้จ่ายแล้ว",
  "hr.updateApprovalFailed": "อัปเดตการอนุมัติไม่สำเร็จ",
  "hr.deleteChargeTitle": "ลบค่าใช้จ่าย",
  "hr.chargeDeleted": "ลบค่าใช้จ่ายแล้ว",
  "hr.deleteChargeFailed": "ลบค่าใช้จ่ายไม่สำเร็จ",
  "hr.noDriverAssignedYet": "ยังไม่ได้มอบหมายคนขับ",
  "hr.attachReceipt": "แนบใบเสร็จ",
  "hr.chargeId": "รหัสค่าใช้จ่าย",
  "fuel.receipt": "ใบเสร็จ",
  "ops.liveTracking": "ติดตามสด",
  "ops.completedRoute": "เส้นทางที่วิ่งจบ",
  "ops.plannedRouteCaps": "เส้นทางที่วางไว้",
  "ops.switchedSelectDriver": "ไปที่แท็บตั้งค่า — เลือกคนขับใหม่",
  "ops.switchedSelectVehicle": "ไปที่แท็บตั้งค่า — เลือกรถใหม่",
  "ops.operatingDate": "วันที่ปฏิบัติงาน",
  "ops.acceptDeadline": "เวลายืนยัน",
  "ops.routeEndTimeLabel": "เวลาสิ้นสุด",
  "ops.columnLocked": "คอลัมน์ถูกล็อก",
  "ops.columnLockedExplain": "คอลัมน์นี้มีใบงานที่เผยแพร่แล้ว แก้ไขเส้นทาง เวลายืนยัน เวลาเริ่ม เวลาสิ้นสุด หรือจำนวนรถตั้งต้นไม่ได้ และลบไม่ได้ทั้งแบบเดี่ยวและแบบจับคู่",
  "ops.dateLockedExplain": "วันที่นี้มีใบงานที่เผยแพร่แล้ว ลบไม่ได้",
  "ops.startTimeLabel": "เวลาเริ่ม",
  "ops.expectedEndLabel": "เวลาสิ้นสุดที่คาด",
  "ops.endUseEstimate": "ใช้เวลาที่คำนวณ ({time})",
  "ops.endAuto": "คำนวณจากหมุด/เส้นทาง",
  "ops.assignNeedEnd": "เวลาสิ้นสุด (ใส่เอง หรือปักหมุดให้ระบบคำนวณ)",
  "ops.pickDriverHint": "เลือกคนขับเพื่อดูรายละเอียด",
  "ops.pickVehicleHint": "เลือกรถเพื่อดูรายละเอียด",
  "ops.noBookingDesc": "ขึ้นรถได้เลย ไม่ต้องจอง",
  "ops.bookingLimitedDesc": "ต้องจอง จำกัดจำนวนที่นั่ง",
  "ops.bookingUnlimitedDesc": "ต้องจอง ที่นั่งเกินได้",
  "ops.saveSettings": "บันทึกการตั้งค่า",
  "ops.selectEmployee": "กรุณาเลือกพนักงาน",
  "ops.selectPassenger": "กรุณาเลือกผู้โดยสาร",
  "ops.addPassengerFailed": "เพิ่มผู้โดยสารไม่สำเร็จ",
  "common.updateFailed": "อัปเดตไม่สำเร็จ",
  "ops.passengerUpdated": "แก้ไขผู้โดยสารแล้ว",
  "ops.markedNoShow": "ทำเครื่องหมายว่าไม่มาขึ้นรถแล้ว",
  "ops.bookingCancelled": "ยกเลิกการจองแล้ว",
  "common.cancelFailed": "ยกเลิกไม่สำเร็จ",
  "ops.allMethods": "ทุกวิธี",
  "ops.telRef": "โทร/อ้างอิง",
  "ops.noPassengersOnTicket": "ยังไม่มีผู้โดยสาร",
  "ops.routeEditCancelled": "ยกเลิกการแก้ไขเส้นทางแล้ว",
  "ops.routeStopsSaved": "บันทึกจุดจอดเรียบร้อย",
  "ops.resetRouteTitle": "คืนค่าเส้นทาง",
  "ops.routeResetDone": "คืนค่าเส้นทางตามแม่แบบแล้ว",
  "ops.timeNotSet": "ยังไม่ได้ตั้งเวลา",
  "ops.overlapsAnotherJob": "ทับซ้อนกับงานอื่น",
  "ops.driverVehicleUnavailable": "คนขับและรถไม่ว่าง",
  "ops.vehicleUnavailable": "รถไม่ว่าง",
  "ops.driverUnavailable": "คนขับไม่ว่าง",
  "common.adding": "กำลังเพิ่ม…",
  "common.showingOf": "แสดง {from}–{to} จาก {total}",
  "common.showingNofM": "แสดง {n} จาก {total} รายการ",
  "fleet.ofNVehicles": "จาก {n} คัน",
  "fuel.noEntriesMatch": "ไม่มีรายการเติมน้ำมันที่ตรงกับตัวกรอง",
  "ops.ticketsScopeToday": "ใบงานวันนี้",
  "ops.ticketsScopeYesterday": "ใบงานเมื่อวาน",
  "ops.ticketsScopeTotal": "ใบงานทั้งหมด",
  "ops.noTemplate": "ไม่มีแม่แบบ",
  "ops.noRouteSet": "ยังไม่ได้ตั้งเส้นทาง",
  "ops.bookingLimitedShort": "จำกัดที่นั่ง",
  "ops.bookingUnlimitedShort": "ไม่จำกัดที่นั่ง",
  "common.showingRange": "แสดง {from}–{to} จาก {total} รายการ",
  "common.entriesCount": "{n} รายการ",
  "fleet.vehiclesTrackedCount": "ติดตามรถ {n} คัน",
  "fuel.allEfficiency": "ทุกระดับประสิทธิภาพ",
  "common.addAttribute": "เพิ่มคุณสมบัติ",
  "hr.addCharge": "เพิ่มค่าใช้จ่าย",
  "fuel.addEntry": "เพิ่มรายการ",
  "masterData.addField": "เพิ่มฟิลด์",
  "common.addFiles": "เพิ่มไฟล์",
  "fleet.addFinanceRecord": "เพิ่มรายการสินเชื่อ",
  "fleet.addGroup": "เพิ่มกลุ่ม",
  "hr.addIdCard": "เพิ่มบัตรประชาชน",
  "common.addItem": "เพิ่มรายการ",
  "hr.addLicense": "เพิ่มใบขับขี่",
  "settings.addMember": "เพิ่มสมาชิก",
  "hr.addOpCharge": "เพิ่มค่าใช้จ่ายปฏิบัติการ",
  "common.addRecord": "เพิ่มรายการ",
  "fleet.addTaxRecord": "เพิ่มรายการภาษี",
  "ops.applyBestSuggestion": "ใช้ข้อเสนอที่ดีที่สุด",
  "ops.cancelJob": "ยกเลิกงาน",
  "ops.changeDriver": "เปลี่ยนคนขับ",
  "ops.changeVehicle": "เปลี่ยนรถ",
  "common.copy": "คัดลอก",
  "warroom.createRoom": "สร้างห้อง",
  "ops.editRouteLower": "แก้ไขเส้นทาง",
  "ops.editRoute": "แก้ไขเส้นทาง",
  "fleet.fail": "ไม่ผ่าน",
  "ops.fillRouteEllipsis": "เติมเส้นทาง…",
  "common.importCsv": "นำเข้า CSV",
  "common.loadingMore": "กำลังโหลดเพิ่ม…",
  "fleet.loadingTemplate": "กำลังโหลดชุดตรวจ…",
  "hr.lockPeriod": "ล็อกงวด",
  "status.locked": "ล็อก",
  "common.markResolved": "ทำเครื่องหมายว่าแก้ไขแล้ว",
  "hr.monthlyCompensation": "ค่าตอบแทนรายเดือน",
  "ops.newBoard": "สร้างบอร์ด",
  "ops.newRoute": "สร้างเส้นทาง",
  "hr.newTeam": "สร้างทีม",
  "settings.notificationsToSend": "การแจ้งเตือนที่จะส่ง",
  "warroom.online": "ออนไลน์",
  "common.openOriginal": "เปิดต้นฉบับ",
  "common.preview": "ดูตัวอย่าง",
  "common.protected": "ป้องกันไว้",
  "warroom.readReceipts": "สถานะการอ่าน",
  "settings.resendInvite": "ส่งคำเชิญอีกครั้ง",
  "safety.signed": "ลงนามแล้ว",
  "common.uploadPhoto": "อัปโหลดรูป",
  "hr.documentUploaded": "อัปโหลดเอกสารเรียบร้อย",
  "ops.anyDirection": "ทุกทิศทาง",
  "ops.outbound": "ขาไป",
  "ops.return": "ขากลับ",
  "hr.applicants": "ผู้สมัคร",
  "common.approvedBy": "อนุมัติโดย",
  "ops.bookingId": "รหัสการจอง",
  "safety.cleanliness": "ความสะอาด",
  "hr.companyDriverLower": "คนขับภายใน",
  "analytics.completionRate": "อัตราจบงาน",
  "ops.contractPeriod": "ระยะเวลาสัญญา",
  "safety.damages": "ความเสียหาย",
  "common.daysLeft": "เหลืออีก (วัน)",
  "fleet.device": "อุปกรณ์",
  "ops.driverVehicle": "คนขับ / รถ",
  "hr.employeeTypeCol": "ประเภทพนักงาน",
  "common.excel": "Excel",
  "settings.formulaSet": "ชุดสูตร",
  "settings.formulas": "สูตร",
  "fuel.id": "รหัสการเติม",
  "common.items": "รายการ",
  "hr.joined": "เข้าร่วมเมื่อ",
  "fleet.lastDone": "ทำล่าสุด",
  "fleet.nextDue": "ครบกำหนดถัดไป",
  "hr.officeStaffLower": "พนักงานสำนักงาน",
  "analytics.onTime": "ตรงเวลา",
  "fleet.premium": "เบี้ยประกัน",
  "fleet.provider": "ผู้ให้บริการ",
  "analytics.punctuality": "ความตรงต่อเวลา",
  "common.rate": "อัตรา",
  "ops.routeContract": "เส้นทาง / สัญญา",
  "fleet.serviceType": "ประเภทบริการ",
  "safety.signatures": "ลายเซ็น",
  "settings.states": "สถานะ",
  "ops.tables": "ตาราง",
  "analytics.totalTrips": "เที่ยวทั้งหมด",
  "settings.transitions": "การเปลี่ยนสถานะ",
  "ops.trip": "เที่ยว",
  "common.version": "เวอร์ชัน",
  "settings.workflowName": "ชื่อเวิร์กโฟลว์",
  "status.resolvedLower": "แก้ไข",
  "common.action": "การกระทำ",
  "hr.activeDrivers": "คนขับที่ใช้งาน",
  "hr.addEmployee": "เพิ่มพนักงาน",
  "fleet.addPmSchedule": "เพิ่มรอบบำรุงรักษา",
  "hr.addRate": "เพิ่มอัตรา",
  "fleet.addVehicle": "เพิ่มรถ",
  "ops.bookingManagement": "จัดการการจอง",
  "fleet.bus": "รถบัส",
  "hr.candidate": "ผู้สมัคร",
  "settings.wfCaseTicket": "ขั้นตอนเคสความปลอดภัย",
  "fleet.company": "บริษัท",
  "hr.wfCompanyDriverPayroll": "เงินเดือนคนขับบริษัท",
  "hr.wfContractorSettlement": "การชำระผู้รับเหมา",
  "hr.contractors": "คนขับภายนอก",
  "common.document": "เอกสาร",
  "roles.driverPortal": "คนขับ (พอร์ทัล)",
  "analytics.driverPerformance": "ประสิทธิภาพคนขับ",
  "analytics.driverPerformanceDesc": "ประเมินคนขับ — ตรงเวลา ประหยัดน้ำมัน อัตราจบงาน",
  "ops.dropoff": "จุดส่ง",
  "settings.wfEmployeeLifecycle": "วงจรพนักงาน",
  "roles.mechanicDesc": "เข้าถึงงานซ่อมบำรุงกองรถ",
  "analytics.fuelScoring": "คะแนนประหยัดน้ำมัน",
  "roles.hrManagerDesc": "ดูแล HR เต็มรูปแบบ บางส่วนของเงินเดือนและความปลอดภัย",
  "roles.superAdminDesc": "เข้าถึงได้ทุกโมดูล",
  "common.groups": "กลุ่ม",
  "roles.hrManager": "ผู้จัดการฝ่ายบุคคล",
  "settings.wfJobTicket": "วงจรใบงาน",
  "fleet.leased": "เช่า",
  "settings.wfLeaveApproval": "อนุมัติคำขอลา",
  "roles.driverPortalDesc": "สิทธิ์จำกัดในพอร์ทัลสำหรับคนขับ",
  "roles.opsManagerDesc": "จัดการกองรถ งาน การจ่ายงาน และการติดตาม",
  "hr.wfMechanicPayroll": "เงินเดือนช่าง",
  "ops.newContract": "สร้างสัญญา",
  "ops.newCustomer": "เพิ่มลูกค้า",
  "hr.newPosting": "ประกาศรับสมัครใหม่",
  "warroom.newRequest": "สร้างคำขอ",
  "fleet.newTemplate": "สร้างชุดตรวจ",
  "fleet.newWorkOrder": "สร้างใบสั่งซ่อม",
  "warroom.noGps": "ไม่มี GPS",
  "hr.noConditionsHint": "ยังไม่มีเงื่อนไข เพิ่มเพื่อกรองตามหลักฐานการเช็คอิน",
  "ops.noDriver": "ไม่มีคนขับ",
  "common.noEntries": "ไม่มีรายการ",
  "warroom.noVehiclesRunning": "ไม่มีรถกำลังวิ่ง",
  "hr.wfOfficeStaffPayroll": "เงินเดือนพนักงานสำนักงาน",
  "fleet.outsource": "จ้างภายนอก",
  "settings.wfPayTicket": "ประมวลผลใบจ่าย",
  "roles.financeDesc": "เงินเดือน การวางบิล สัญญา",
  "ops.pickup": "จุดรับ",
  "fleet.preventive": "บำรุงรักษาเชิงป้องกัน",
  "fleet.repair": "ซ่อม",
  "analytics.routePerformance": "ประสิทธิภาพเส้นทาง",
  "analytics.routePerformanceDesc": "วิเคราะห์เส้นทาง — เวลา ระยะทาง การใช้ที่นั่ง",
  "hr.runCalculation": "เริ่มคำนวณ",
  "settings.wfSlaKpi": "คำนวณ SLA/KPI",
  "roles.safetyOfficer": "เจ้าหน้าที่ความปลอดภัย",
  "roles.safetyOfficerDesc": "เคสความปลอดภัย การตรวจสภาพ การส่งมอบ",
  "roles.superAdmin": "ผู้ดูแลระบบสูงสุด",
  "analytics.tripSummary": "สรุปเที่ยว",
  "analytics.tripSummaryDesc": "สรุปเที่ยว — จำนวนเที่ยว อัตราตรงเวลา ผู้โดยสาร",
  "fleet.vehicleStatus": "สถานะรถ",
  "settings.wfWorkOrderApproval": "อนุมัติใบสั่งซ่อม",
  "common.tue": "อ.",
  "common.wed": "พ.",
  "common.thu": "พฤ.",
  "common.fri": "ศ.",
  "common.sat": "ส.",
  "common.allTime": "ทั้งหมด",
  "fleet.allVehicles": "รถทั้งหมด",
  "fuel.below10Std": "ต่ำกว่าเกณฑ์ 10%",
  "fuel.fleetAverage": "ค่าเฉลี่ยทั้งกอง",
  "safety.needsInvestigation": "ต้องสอบสวน",
  "warroom.noActive": "ไม่มีที่ค้าง",
  "warroom.onJob": "มีงาน",
  "common.todayLower": "วันนี้",
  "fuel.withFuelData": "ที่มีข้อมูลน้ำมัน",
  "common.recorded": "บันทึกแล้ว",
  "common.allPriority": "ทุกระดับความสำคัญ",
  "fleet.allProviders": "ทุกผู้ให้บริการ",
  "safety.allSeverity": "ทุกระดับความรุนแรง",
  "common.allStatus": "ทุกสถานะ",
  "common.allTypes": "ทุกประเภท",
  "ops.cancelledBy": "ยกเลิกโดย",
  "hr.checkinIncomplete": "เช็คอินไม่ครบ",
  "hr.compensation": "ค่าตอบแทน",
  "common.createdVia": "สร้างผ่าน",
  "common.currency": "สกุลเงิน",
  "common.daily": "รายวัน",
  "hr.dailyWindowFrom": "ช่วงเวลารายวันตั้งแต่",
  "common.days": "วัน",
  "hr.decision": "การตัดสิน",
  "hr.employmentType": "ประเภทการจ้าง",
  "hr.emptyRuns": "เที่ยวเปล่า",
  "common.expiry": "วันหมดอายุ",
  "common.fromDateTime": "ตั้งแต่ (วันและเวลา)",
  "ops.gpsTrace": "เส้นทาง GPS",
  "hr.goldStandard": "หลักฐานสมบูรณ์",
  "hr.groupByDriver": "จัดกลุ่มตามคนขับ",
  "hr.hasCharge": "มีค่าใช้จ่าย",
  "hr.hasChargesPending": "มีค่าใช้จ่ายรออนุมัติ",
  "hr.hasNoShow": "มีคนไม่มาขึ้นรถ",
  "hr.hasWalkIn": "มีคนขึ้นรถหน้างาน",
  "hr.includeNoTime": "รวมใบงานที่ไม่มีเวลา",
  "hr.license": "ใบขับขี่",
  "hr.licenseExpiry": "ใบขับขี่หมดอายุ",
  "fuel.liters": "ลิตร",
  "common.locale": "ภาษาและรูปแบบ",
  "common.location": "ตำแหน่ง",
  "status.notStarted": "ยังไม่เริ่ม",
  "hr.otPerHour": "ล่วงเวลา/ชม.",
  "fuel.odometerShort": "เลขไมล์",
  "status.onTime": "ตรงเวลา",
  "hr.paySource": "ที่มาของค่าจ้าง",
  "hr.perTrip": "ต่อเที่ยว",
  "hr.periodState": "สถานะงวด",
  "hr.readyToPay": "พร้อมจ่าย",
  "fuel.receipts": "ใบเสร็จ",
  "warroom.sos": "SOS",
  "hr.salaryRange": "ช่วงเงินเดือน",
  "hr.skippedPickups": "ข้ามจุดรับ",
  "hr.stopCheckin": "เช็คอินจุดจอด",
  "common.toDateTime": "ถึง (วันและเวลา)",
  "ops.tripDirection": "ทิศทางเที่ยว",
  "ops.trips": "เที่ยว",
  "status.unaccepted": "ยังไม่รับงาน",
  "common.updated": "แก้ไขเมื่อ",
  "hr.usernameOrId": "ชื่อผู้ใช้ / รหัส",
  "hr.weakEvidence": "หลักฐานอ่อน",
  "fleet.workOrder": "ใบสั่งซ่อม",
  "hr.zeroRateTrips": "เที่ยวที่อัตราเป็นศูนย์ (ยังไม่ตั้งอัตรา)",
  "hr.acceptedLate": "รับงานแล้ว แต่สาย",
  "nav.contractsLower": "สัญญา",
  "fuel.kmPerLiter": "กม./ลิตร",
  "hr.pastAccept": "เลยเวลารับงาน",
  "hr.toCrossMidnight": "ถึง (ข้ามเที่ยงคืนได้)",
  "common.somethingWentWrong": "เกิดข้อผิดพลาด",
  "fleet.markInactiveHint": "การทำเช่นนี้จะตั้ง {name} เป็นไม่ใช้งาน เปิดใช้ใหม่ได้ภายหลัง",
  "fleet.markActiveHint": "การทำเช่นนี้จะตั้ง {name} กลับมาใช้งานอีกครั้ง",
  "register.otpSentToName": "ส่ง OTP ไปที่ {email}",
  "settings.webhookSigHeader": "X-DeepTrack-Signature",
  "common.menu": "เมนู",
  "settings.searchOrgsShort": "ค้นหาองค์กร",
  "analytics.summaryToday": "สรุปวันนี้",
  "analytics.freeDrivers": "คนขับที่ว่าง",
  "analytics.expiringDocs": "เอกสารใกล้หมดอายุ",
  "analytics.fuelToday": "น้ำมันวันนี้",
  "analytics.scheduleTomorrow": "ตารางพรุ่งนี้",
  "analytics.customReports": "รายงานกำหนดเอง",
  "analytics.source": "แหล่งข้อมูล",
  "analytics.chartType": "ชนิดกราฟ",
  "common.filters": "ตัวกรอง",
  "analytics.groupBy": "จัดกลุ่มตาม",
  "analytics.aggregate": "การรวมค่า",
  "analytics.aggregateField": "ฟิลด์ที่รวมค่า",
  "analytics.srcPayTickets": "ใบจ่าย",
  "analytics.srcSafetyCases": "เคสความปลอดภัย",
  "analytics.aggCount": "นับจำนวน",
  "analytics.aggSum": "ผลรวม",
  "analytics.aggAvg": "ค่าเฉลี่ย",
  "analytics.aggMin": "ต่ำสุด",
  "analytics.aggMax": "สูงสุด",
  "analytics.chartTable": "ตาราง",
  "analytics.chartBar": "แท่ง",
  "analytics.chartLine": "เส้น",
  "analytics.chartPie": "วงกลม",
  "analytics.chartStat": "ตัวเลข",
  "analytics.totalTrips30d": "เที่ยวทั้งหมด (30 วัน)",
  "analytics.avgOnTimeRate": "อัตราตรงเวลาเฉลี่ย",
  "analytics.fleetUtilization": "การใช้งานกองรถ",
  "common.productName": "DeepTrack",
  "createOrg.title": "สร้างองค์กร / โปรเจกต์",
  "createOrg.inviteLater": "เชิญสมาชิกทีมได้หลังตั้งค่าเสร็จ",
  "common.productTagline": "ระบบบริหารงานขนส่ง",
  "invite.notFound": "ไม่พบคำเชิญ",
  "invite.goToLogin": "ไปหน้าเข้าสู่ระบบ",
  "invite.redirecting": "กำลังพาไปหน้าแดชบอร์ด…",
  "invite.createAccount": "สร้างบัญชีเพื่อเข้าร่วม",
  "invite.haveAccount": "มีบัญชีแล้ว? เข้าสู่ระบบ",
  "onboarding.langThai": "ไทย",
  "onboarding.langEnglish": "อังกฤษ",
  "onboarding.vehVan": "รถตู้",
  "onboarding.vehBus40": "รถบัส (40 ที่นั่ง)",
  "onboarding.vehBus50": "รถบัส (50 ที่นั่ง)",
  "onboarding.vehTruck": "รถบรรทุก",
  "roles.fleetManager": "ผู้จัดการกองรถ",
  "roles.opsManager": "ผู้จัดการฝ่ายปฏิบัติการ",
  "roles.viewerReadOnly": "ผู้ดูข้อมูล (อ่านอย่างเดียว)",
  "onboarding.setupWizard": "ตัวช่วยตั้งค่า",
  "onboarding.orgInfo": "ข้อมูลองค์กร",
  "onboarding.orgInfoHint": "ข้อมูลพื้นฐานของบริษัทขนส่งของคุณ",
  "onboarding.logoOptional": "โลโก้ (ไม่บังคับ)",
  "onboarding.uploadLogo": "อัปโหลดโลโก้ (PNG, JPG, SVG)",
  "onboarding.timezone": "เขตเวลา",
  "onboarding.language": "ภาษา",
  "onboarding.depotLocation": "ตำแหน่งจุดจอดรถ",
  "onboarding.depotName": "ชื่อจุดจอดรถ",
  "onboarding.mapPlaceholder": "แผนที่ Google จะแสดงตรงนี้",
  "onboarding.pinDepotHint": "คลิกเพื่อปักหมุดจุดจอดรถ",
  "onboarding.addFirstVehicle": "เพิ่มรถคันแรก",
  "onboarding.capacitySeats": "ความจุ (ที่นั่ง)",
  "onboarding.inviteTeam": "เชิญทีมของคุณ",
  "common.emailAddress": "อีเมล",
  "common.skip": "ข้าม",
  "common.invite": "เชิญ",
  "register.otpSentTo": "ส่ง OTP ไปที่",
  "selectOrg.createNew": "สร้างองค์กร / โปรเจกต์ใหม่",
  "createOrg.subtitle": "สร้างพื้นที่ทำงานสำหรับองค์กรหรือโครงการของคุณเพื่อเริ่มใช้ DeepTrack",
  "createOrg.nameLabel": "ชื่อ *",
  "createOrg.descLabel": "รายละเอียด",
  "common.optional": "(ไม่บังคับ)",
  "createOrg.submit": "สร้างและตั้งค่าต่อ",
  "createOrg.failed": "สร้างองค์กรไม่สำเร็จ",
  "createOrg.slugRequired": "ชื่อต้องมีตัวอักษรภาษาอังกฤษหรือตัวเลขอย่างน้อย 1 ตัว เพื่อใช้สร้างรหัส (slug) ขององค์กร",
  "selectOrg.subtitle": "เลือกองค์กรหรือโครงการเพื่อดำเนินการต่อ",
  "selectOrg.superAdminSubtitle": "ผู้ดูแลระบบเข้าถึงได้ทุกองค์กร{total}",
  "selectOrg.noMatch": "ไม่พบองค์กรที่ตรงกับคำค้น",
  "selectOrg.noneYet": "ยังไม่มีองค์กร",
  "selectOrg.tryDifferent": "ลองใช้ชื่ออื่น",
  "selectOrg.createBelow": "สร้างองค์กรใหม่ด้านล่างเพื่อเริ่มต้น",
  "selectOrg.showMore": "แสดงเพิ่ม ({shown} จาก {total})",
  "onboarding.stepOf": "ขั้นตอนที่ {step} จาก 4",
  "onboarding.vehicleHint": "เพิ่มรถคันแรกของคุณ เพิ่มคันอื่นภายหลังได้ที่เมนูกองรถ",
  "onboarding.inviteHint": "เชิญสมาชิกทีมคนแรก เพิ่มคนอื่นภายหลังได้ที่การตั้งค่า",
  "onboarding.addAnotherMember": "+ เพิ่มสมาชิกอีกคน",
  "onboarding.finishSetup": "เสร็จสิ้นการตั้งค่า",
  "onboarding.saveFailed": "บันทึกการตั้งค่าไม่สำเร็จ",
  "invite.acceptJoin": "ยอมรับและเข้าร่วม",
  "invite.noToken": "ลิงก์คำเชิญไม่ถูกต้อง — ไม่มี token",
  "invite.invalid": "คำเชิญนี้ไม่ถูกต้องหรือหมดอายุแล้ว",
  "invite.loadFailed": "โหลดคำเชิญไม่สำเร็จ กรุณาลองใหม่",
  "invite.acceptFailed": "ยอมรับคำเชิญไม่สำเร็จ",
  "warroom.announcementsLoadFailed": "โหลดประกาศไม่สำเร็จ",
  "warroom.noChatRooms": "ยังไม่มีห้องแชท",
  "warroom.noMessages": "ยังไม่มีข้อความ",
  "warroom.readReceipt": "สถานะการอ่าน",
  "common.remaining": "คงเหลือ",
  "warroom.searchChats": "ค้นหาแชท…",
  "warroom.typeMessage": "พิมพ์ข้อความ…",
  "warroom.sendMessage": "ส่งข้อความ",
  "warroom.announcements": "ประกาศ",
  "warroom.requests": "คำขอ",
  "warroom.upNext": "คิว",
  "common.mon": "จ.",
  "common.sun": "อา.",
  "warroom.rewind5m": "ย้อน 5 นาที",
  "warroom.forward5m": "เดินหน้า 5 นาที",
  "warroom.statDone": "เสร็จ",
  "warroom.statRun": "วิ่ง",
  "warroom.statQueue": "รอ",
  "warroom.statUnassigned": "ไม่มีคน",
  /*
   * ADR 0247 D7 — NOT "ว่าง". The FLEET column's header already carries that
   * word for a different question: it counts vehicles that could take a job now
   * (`free_now`, tier 2+3 = 229 on Chok Chai Aree), while this band counts
   * vehicles with no work today at all (272). Two numbers 43 apart under one
   * label is the screen arguing with itself.
   */
  "warroom.statIdle": "ไม่มีงาน",
  /* ADR 0251 — the SAME word the driver app shows for the same fact
     (`timeline.skipped` in apps/driver). A stop the bus drove past. */
  "warroom.stopSkipped": "พลาด",
  "warroom.statNoGps": "ไม่มี GPS",
  "safety.handoverLoadFailed": "โหลดข้อมูลการส่งมอบไม่สำเร็จ",
  "safety.newInspection": "ตรวจสภาพใหม่",
  "safety.inspector": "ผู้ตรวจ",
  "status.investigating": "กำลังสอบสวน",
  "status.pendingAssessment": "รอประเมิน",
  "status.pendingDeduction": "รอหักเงิน",
  "safety.damagedItems": "รายการที่เสียหาย",
  "safety.missingItems": "รายการที่หาย",
  "safety.totalDamageCost": "มูลค่าความเสียหายรวม",
  "safety.evidencePhotos": "รูปหลักฐาน",
  "safety.pendingInProgress": "รอ / กำลังดำเนินการ",
  "safety.damagedReturns": "คืนแบบเสียหาย",
  "safety.totalRecords": "รายการทั้งหมด",
  "safety.casesLoadFailed": "โหลดเคสความปลอดภัยไม่สำเร็จ",
  "status.hrReview": "HR ตรวจสอบ",
  "status.warningIssued": "ออกใบเตือน",
  "status.pendingESign": "รอลงนามอิเล็กทรอนิกส์",
  "status.pendingPayment": "รอชำระเงิน",
  "safety.accident": "อุบัติเหตุ",
  "safety.violation": "การฝ่าฝืน",
  "safety.complaint": "ข้อร้องเรียน",
  "safety.inspection": "การตรวจสภาพ",
  "safety.trafficViolation": "ฝ่าฝืนกฎจราจร",
  "safety.timeliness": "ความตรงต่อเวลา",
  "safety.customerComplaint": "ข้อร้องเรียนจากลูกค้า",
  "safety.gpsTampering": "แก้ไข GPS",
  "safety.policyNonCompliance": "ไม่ปฏิบัติตามนโยบาย",
  "safety.nonSopCompliance": "ไม่ปฏิบัติตาม SOP",
  "safety.substanceAbuse": "สารเสพติด",
  "safety.offenseType": "ประเภทความผิด",
  "safety.incidentDate": "วันที่เกิดเหตุ",
  "safety.punishment": "บทลงโทษ",
  "safety.openCases": "เคสที่เปิดอยู่",
  "safety.criticalOpen": "วิกฤต (เปิดอยู่)",
  "safety.accidentFreeDays": "จำนวนวันไม่มีอุบัติเหตุ",
  "safety.pendingAction": "รอดำเนินการ",
  "portal.cancelRate": "อัตรายกเลิก",
  "portal.totalBookings": "การจองทั้งหมด",
  "portal.cancelledBookings": "การจองที่ยกเลิก",
  "portal.noCancellations": "ไม่มีการยกเลิก",
  "common.reasonRequired": "เหตุผล (บังคับ)",
  "ops.markAsNoShow": "ทำเครื่องหมายว่าไม่มาขึ้นรถ",
  "ops.editBooking": "แก้ไขการจอง",
  "portal.financialsEditableHint": "แก้ไขข้อมูลการเงินได้เฉพาะตอนที่เที่ยวอยู่ในสถานะ",
  "hr.driverCompensation": "ค่าตอบแทนคนขับ",
  "common.attached": "แนบแล้ว",
  "ops.guest": "ผู้โดยสารทั่วไป",
  "ops.schedule": "กำหนดการ",
  // ADR 0263 — เวลาแผน→จริงในคอลัมน์กำหนดการ + เปลี่ยนสถานะจากตาราง
  "ops.noTicketsMatchFilters": "ไม่มีใบงานตรงกับตัวกรอง",
  "ops.noTicketsFound": "ยังไม่มีใบงาน",
  "ops.actualRunning": "กำลังวิ่ง",
  "ops.lateByMin": "+{m} น.",
  "ops.markedBy": "โดย {name}",
  "ops.statusStaleRefresh": "สถานะงานนี้ถูกเปลี่ยนจากที่อื่นแล้ว — อัปเดตแถวนี้เป็นสถานะจริงให้แล้ว",
  "ops.cancelReasonLabel": "เหตุผลการยกเลิก",
  "ops.noStopsShort": "ไม่มีจุดจอด",
  "ops.newJobTicket": "ใบงานใหม่",
  "ops.searchTicketsAll": "ค้นหา: เลขงาน ชื่อใบงาน เส้นทาง คนขับ ทะเบียนรถ สัญญา…",
  "ops.searchRouteTemplate": "ค้นหาเส้นทาง…",
  "ops.searchVehicleShort": "ค้นหารถ…",
  "ops.searchDriverShort": "ค้นหาคนขับ…",
  "common.demoData": "ข้อมูลตัวอย่าง",
  "ops.activeTickets": "ใบงานที่ใช้งาน",
  "portal.adminOnly": "พอร์ทัลนี้เข้าได้เฉพาะผู้ดูแล",
  "portal.sessionExpired": "เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง",
  "portal.orgRequired": "อีเมลนี้มีบัญชีอยู่มากกว่าหนึ่งองค์กร — กรุณาเข้าผ่านลิงก์พอร์ทัลขององค์กรที่ต้องการ",
  "portal.customerPortal": "พอร์ทัลลูกค้า",
  "settings.planFree": "ฟรี",
  "settings.planStarter": "เริ่มต้น",
  "settings.planPro": "โปร",
  "settings.planEnterprise": "องค์กร",
  // ADR 0394 — ฟีเจอร์ระดับองค์กร
  "settings.orgNameExample": "บริษัทขนส่งตัวอย่าง",
  "common.briefDescription": "คำอธิบายสั้น ๆ (ไม่บังคับ)",
  "settings.orgName": "ชื่อองค์กร",
  "settings.yearEra": "ปีที่แสดง",
  "settings.yearEra.ce": "ค.ศ.",
  "settings.yearEra.be": "พ.ศ.",
  "settings.yearEraHint": "ใช้กับวันที่ทุกจอภาษาไทยขององค์กรนี้ ทั้งเว็บ แอปคนขับ พอร์ทัลลูกค้า ข้อความแจ้งเตือน และข้อความบนบิล — เปลี่ยนแล้วมีผลทันที · ไม่เปลี่ยน: เลขเอกสาร (JOB-2026…, MT-2026…), วันที่ในไฟล์ส่งออก และแบบฟอร์มสรรพากร (ใช้ พ.ศ. เสมอ)",
  "settings.dispatchPhone": "เบอร์กลางสำหรับคนขับ",
  "settings.dispatchPhoneHint": "แสดงในกล่อง \"ต้องการยกเลิกงาน\" ของแอปคนขับ คู่กับเบอร์ผู้สร้างงาน",
  "settings.dispatchPhoneEmpty": "ยังไม่ได้ตั้ง — คนขับจะเห็นเฉพาะเบอร์ผู้สร้างงาน",
  "settings.slugChangeHint": "การเปลี่ยน slug จะอัปเดตใน OAuth ด้วย",
  "settings.tenantId": "รหัสองค์กร",
  "settings.logoUrl": "URL โลโก้",
  "settings.initials": "อักษรย่อ (2 ตัว)",
  "settings.initialsColor": "สีอักษรย่อ",
  "settings.primaryColor": "สีหลัก",
  "settings.noRoleAssignments": "ไม่พบการกำหนดบทบาทใน TMS",
  "common.savedSuccessfully": "บันทึกเรียบร้อยแล้ว",
  "settings.backToOrgs": "กลับไปหน้าองค์กร",
  "settings.branding": "แบรนด์",
  "nav.orgs": "องค์กร",
  "settings.allPlans": "ทุกแพ็กเกจ",
  "settings.noOrgsFound": "ไม่พบองค์กร",
  "settings.searchOrgs": "ค้นหาชื่อ slug หรือรหัส…",
  "common.showDeleted": "แสดงที่ถูกลบ",
  "masterData.displayName": "ชื่อที่แสดง",
  "masterData.noCategories": "ไม่พบหมวดหมู่",
  "masterData.noDropdownYet": "ยังไม่มี dropdown ไหนใช้หมวดนี้",
  "masterData.manageOptions": "จัดการตัวเลือกของช่องนี้ใน Master Data",
  "masterData.searchCategories": "ค้นหาหมวดหมู่…",
  "masterData.customFields": "ฟิลด์กำหนดเอง",
  "common.icon": "ไอคอน",
  "masterData.fillRequiredFields": "กรุณากรอกฟิลด์กำหนดเองที่บังคับให้ครบ",
  "masterData.selectCategory": "เลือกหมวดหมู่",
  "masterData.chooseCategoryHint": "เลือกหมวดหมู่ทางซ้ายเพื่อดูและจัดการรายการ",
  "masterData.effective": "มีผล",
  "common.default": "ค่าตั้งต้น",
  "masterData.noItems": "ยังไม่มีรายการ",
  "common.reorder": "จัดลำดับใหม่",
  "masterData.labelEn": "ป้ายชื่อ (EN)",
  "masterData.labelTh": "ป้ายชื่อ (TH)",
  "masterData.removeField": "ลบฟิลด์",
  "masterData.customItemFields": "ฟิลด์กำหนดเองของรายการ",
  "masterData.loadFailed": "โหลด Master Data ไม่สำเร็จ",
  "masterData.subtitle": "ชุดคำที่ใช้ร่วมกันในฟอร์มทั่วทั้งระบบ",
  "settings.versionHistory": "ประวัติเวอร์ชัน",
  "settings.testCalculate": "ทดสอบคำนวณ",
  "settings.variables": "ตัวแปร",
  "settings.formulaEngine": "ระบบสูตรคำนวณ",
  "settings.newFormulaSet": "ชุดสูตรใหม่",
  "settings.totalFormulaSets": "ชุดสูตรทั้งหมด",
  "settings.totalFormulas": "สูตรทั้งหมด",
  "settings.employeeTypes": "ประเภทพนักงาน",
  "settings.generateKey": "สร้างคีย์",
  "settings.addWebhook": "เพิ่ม Webhook",
  "status.revoked": "เพิกถอน",
  "settings.apiKeys": "คีย์ API",
  "settings.webhooks": "Webhooks",
  "settings.notificationTemplates": "แม่แบบการแจ้งเตือน",
  "roles.dispatcher": "ผู้จัดงานเดินรถ",
  "roles.viewer": "ผู้ดูข้อมูล",
  "settings.closeInviteForm": "ปิดฟอร์มเชิญ",
  "settings.noMembers": "ยังไม่มีสมาชิก",
  "settings.noPendingInvites": "ไม่มีคำเชิญที่รออยู่",
  "settings.pendingInvites": "คำเชิญที่รออยู่",
  "settings.team": "ทีม",
  "settings.idPatterns": "รูปแบบรหัส",
  "nav.integrations": "การเชื่อมต่อ",
  "settings.features": "ฟีเจอร์",
  "settings.editPermissions": "แก้ไขสิทธิ์",
  "settings.newRole": "บทบาทใหม่",
  "status.driverAccepted": "คนขับรับงาน",
  "settings.actAssign": "มอบหมาย",
  "settings.actAccept": "รับงาน",
  "settings.actUnassign": "ยกเลิกมอบหมาย",
  "settings.actStartTrip": "เริ่มเที่ยว",
  "settings.actComplete": "จบงาน",
  "settings.actReopen": "เปิดใหม่",
  "settings.stateId": "รหัสสถานะ",
  "settings.sla": "SLA",
  "settings.autoActions": "การกระทำอัตโนมัติ",
  "settings.activeWorkflows": "เวิร์กโฟลว์ที่ใช้งาน",
  "settings.modulesCovered": "โมดูลที่ครอบคลุม",
  "hr.quickPresets": "ชุดตัวกรองสำเร็จรูป",
  "hr.daysOfWeek": "วันในสัปดาห์",
  "hr.employeeType": "ประเภทพนักงาน",
  "hr.rateSource": "ที่มาของอัตรา",
  "hr.chargeType": "ประเภทค่าใช้จ่าย",
  "common.removeFilter": "ลบตัวกรอง",
  "hr.payDecision": "การตัดสินจ่าย",
  "hr.lateOverMin": "สายเกิน (นาที)",
  "hr.peopleVehicleRoute": "คน · รถ · เส้นทาง",
  "hr.tripTruth": "ข้อเท็จจริงของเที่ยว",
  "hr.rateAndNet": "อัตราและยอดสุทธิ",
  "hr.decisionsPeriod": "การตัดสินและงวด",
  "hr.checkinEvidence": "หลักฐานการเช็คอิน",
  "hr.driverPayAdminOnly": "ข้อมูลการเงินค่าตอบแทนคนขับ เห็นได้เฉพาะผู้ดูแล",
  "hr.noPayLines": "ไม่มีรายการจ่ายที่ตรงกับตัวกรอง",
  "hr.decide": "ตัดสิน",
  "hr.srcRoute": "เรทรายบุคคลตามเส้นทาง",
  "hr.srcContract": "เรทรายบุคคลตามสัญญา",
  "hr.srcRouteBonus": "โบนัสเส้นทาง",
  "hr.srcDefault": "เรทตั้งต้นรายบุคคล",
  "hr.srcStandard": "ค่าตอบแทนมาตรฐานของเส้นทาง",
  "hr.srcManual": "ระบุเองที่ใบงาน",
  "hr.totalPayable": "ยอดที่ต้องจ่ายรวม",
  "hr.tripsPayable": "เที่ยวที่จ่าย",
  "hr.excludedNotRun": "ตัดออก / ไม่ได้วิ่ง",
  "hr.needsDecision": "รอการตัดสิน",
  "hr.deductionsApproved": "รายการหัก (อนุมัติแล้ว)",
  "hr.partial": "บางส่วน…",
  "hr.unknownProvince": "ไม่ทราบจังหวัด",
  "hr.deleteProvinceRate": "ลบอัตราตามจังหวัด",
  "hr.searchProvince": "ค้นหาจังหวัด ป้ายชื่อ…",
  "hr.editRate": "แก้ไขอัตรา",
  "hr.deleteRate": "ลบอัตรา",
  "hr.provinceRatesAdminOnly": "อัตราตามจังหวัด เห็นได้เฉพาะผู้ดูแล",
  "hr.totalRates": "อัตราทั้งหมด",
  "hr.avgCompensation": "ค่าตอบแทนเฉลี่ย",
  "common.highest": "สูงสุด",
  "common.lowest": "ต่ำสุด",
  "hr.username": "ชื่อผู้ใช้",
  "hr.permissionRole": "บทบาทสิทธิ์",
  "hr.roleSystemLevelOnly": "(บทบาทระดับระบบ — แก้จากในองค์กรไม่ได้)",
  "hr.salaryBaseMonth": "เงินเดือน (ฐาน/เดือน)",
  "common.thb": "บาท",
  "status.trial": "ทดลองงาน",
  "status.onLeave": "ลา",
  "status.resigned": "ลาออก",
  "status.terminated": "ให้ออก",
  "hr.licenses": "ใบขับขี่",
  "hr.payee": "ผู้รับเงิน",
  "hr.behavior": "พฤติกรรม",
  "hr.moneyManagement": "การจัดการเงิน",
  "hr.prefix": "คำนำหน้า…",
  "hr.male": "ชาย",
  "hr.female": "หญิง",
  "common.na": "ไม่ระบุ",
  "status.verified": "ตรวจสอบ",
  "hr.perTripPay": "ค่าตอบแทนต่อเที่ยว",
  "hr.compensationHistory": "ประวัติค่าตอบแทน",
  "status.currentCaps": "ปัจจุบัน",
  "hr.noLeaveBalance": "ไม่มีข้อมูลวันลาคงเหลือ",
  "hr.requestHistory": "ประวัติคำขอ",
  "hr.noLeaveRequests": "ยังไม่มีคำขอลา",
  "hr.disciplinaryScorecard": "คะแนนวินัย",
  "common.overall": "ภาพรวม",
  "hr.noBehaviorRecords": "ยังไม่มีบันทึกพฤติกรรม",
  "hr.employeeNotFound": "ไม่พบพนักงาน",
  "hr.manageChargesHint": "จัดการค่าใช้จ่ายของใบงานนี้ (น้ำมัน ทางด่วน รายการหัก…)",
  "hr.idCardAdminOnly": "รายละเอียดบัตรประชาชน เห็นได้เฉพาะผู้ดูแล HR",
  "hr.lifetime": "ตลอดอายุงาน",
  "common.override": "กำหนดเอง",
  "common.manage": "จัดการ",
  "hr.behaviorAdminOnly": "บันทึกพฤติกรรม เห็นได้เฉพาะผู้ดูแล HR",
  "hr.backToEmployees": "กลับไปหน้าพนักงาน",
  "hr.hireDate": "วันเริ่มงาน",
  "hr.trialEnd": "สิ้นสุดทดลองงาน",
  "hr.vehicleAssigned": "รถที่มอบหมาย",
  "hr.srcBonusLegacy": "โบนัส (แบบเก่า)",
  "hr.payCutoffDay": "วันตัดรอบจ่าย",
  "hr.payday": "วันจ่าย",
  "hr.otRate": "อัตราค่าล่วงเวลา",
  "hr.withholdingTax": "ภาษีหัก ณ ที่จ่าย",
  "hr.effectiveFrom": "มีผลตั้งแต่",
  "hr.effectiveTo": "มีผลถึง",
  "hr.lastChangeReason": "เหตุผลการแก้ไขล่าสุด",
  "hr.totalAllowances": "เงินเพิ่มรวม",
  "hr.totalDeductions": "รายการหักรวม",
  "hr.netEffectOnPay": "ผลสุทธิต่อค่าจ้าง",
  "hr.estMonthlyPay": "ค่าจ้างต่อเดือนโดยประมาณ",
  "hr.bank": "ธนาคาร",
  "hr.accountName": "ชื่อบัญชี",
  "hr.accountNumber": "เลขที่บัญชี",
  "hr.taxId": "เลขประจำตัวผู้เสียภาษี",
  "hr.ssoNo": "เลขประกันสังคม",
  "status.warning": "ตักเตือน",
  "status.suspension": "พักงาน",
  "status.commendation": "ชมเชย",
  "hr.documentsAcrossEmployees": "เอกสาร {n} รายการ จากพนักงานทุกคน",
  "hr.documentsLoadFailed": "โหลดเอกสารไม่สำเร็จ",
  "hr.noDocumentsMatch": "ไม่มีเอกสารที่ตรงกับตัวกรอง",
  "hr.searchDocuments": "ค้นหาชื่อ รหัส ประเภทเอกสาร เลขใบขับขี่…",
  "hr.verify": "ตรวจสอบ",
  "hr.noExpiry": "ไม่มีวันหมดอายุ",
  "status.valid": "ใช้ได้",
  "hr.totalDocuments": "เอกสารทั้งหมด",
  "status.unverified": "ยังไม่ตรวจสอบ",
  "hr.expiringSoon90d": "ใกล้หมดอายุ (90 วัน)",
  "hr.allDocuments": "เอกสารทั้งหมด",
  "hr.licensesOnly": "เฉพาะใบขับขี่",
  "hr.backToHr": "กลับไปหน้า HR",
  "hr.importFromCsv": "นำเข้าพนักงานจากไฟล์ CSV",
  "hr.leaveLoadFailed": "โหลดข้อมูลการลาไม่สำเร็จ",
  "common.prevMonth": "เดือนก่อน",
  "common.nextMonth": "เดือนถัดไป",
  "hr.rejectLeave": "ปฏิเสธคำขอลา",
  "hr.searchLeave": "ค้นหาชื่อ รหัส เหตุผล…",
  "hr.leaveSearchAll": "ค้นจากทุกสถานะ (รวมที่อนุมัติแล้ว)",
  "hr.noLeaveMatch": "ไม่มีคำขอลาที่ตรงกับตัวกรอง",
  "status.pendingApproval": "รออนุมัติ",
  "hr.onLeaveToday": "ลาวันนี้",
  "hr.approvedThisMonth": "อนุมัติแล้ว (เดือนนี้)",
  "hr.totalEmployees": "พนักงานทั้งหมด",
  "hr.leaveRequests": "คำขอลา",
  "hr.teamCalendar": "ปฏิทินทีม",
  "hr.drivers": "คนขับ",
  "hr.trialPeriod": "ช่วงทดลองงาน",
  "hr.licenseExpiring": "ใบขับขี่ใกล้หมดอายุ",
  "status.applied": "สมัคร",
  "status.screening": "คัดกรอง",
  "status.onboarding": "เริ่มงาน",
  "status.paused": "หยุดชั่วคราว",
  "status.filled": "ได้คน",
  "hr.partTime": "พาร์ทไทม์",
  "hr.positionType": "ประเภทตำแหน่ง",
  "hr.resume": "เรซูเม่",
  "hr.interviewer": "ผู้สัมภาษณ์",
  "hr.score": "คะแนน",
  "hr.salary": "เงินเดือน",
  "hr.openPostings": "ประกาศที่เปิดรับ",
  "hr.totalPostings": "ประกาศทั้งหมด",
  "hr.totalApplicants": "ผู้สมัครทั้งหมด",
  "hr.payBasis": "ฐานการจ่าย",
  "hr.dailyWage": "ค่าแรงรายวัน",
  "hr.perTripRate": "อัตราต่อเที่ยว",
  "hr.avgBaseSalary": "เงินเดือนพื้นฐานเฉลี่ย",
  "hr.totalMonthlyBase": "ฐานเงินเดือนรวมต่อเดือน",
  "common.onThisPage": "ในหน้านี้",
  "hr.payrollLoadFailed": "โหลดข้อมูลบัญชีเงินเดือนไม่สำเร็จ",
  "hr.income": "รายได้",
  "hr.gross": "ยอดรวม",
  "hr.netPay": "ยอดสุทธิ",
  "hr.tripCountAnomaly": "จำนวนเที่ยวสูงผิดปกติเมื่อเทียบกับค่าเฉลี่ยย้อนหลัง",
  "hr.excelExport": "ส่งออก Excel",
  "hr.bankExport": "ส่งออกไฟล์ธนาคาร",
  "hr.anomaliesOnly": "เฉพาะที่ผิดปกติ",
  "hr.flags": "สัญญาณเตือน",
  "hr.noPayTickets": "ไม่มีใบจ่ายที่ตรงกับตัวกรอง",
  "hr.searchPayTickets": "ค้นหาเลขใบจ่าย พนักงาน…",
  "hr.submitForReview": "ส่งตรวจสอบ",
  "hr.submitForPayment": "ส่งเบิกจ่าย",
  "hr.markAsPaid": "ทำเครื่องหมายว่าจ่ายแล้ว",
  "status.calculated": "คำนวณ",
  "status.paid": "จ่าย",
  "hr.phoneAllowance": "ค่าโทรศัพท์",
  "hr.attendanceBonus": "เบี้ยขยัน",
  "hr.insuranceDeduction": "หักค่าประกัน",
  "hr.gpsDeduction": "หักค่า GPS",
  "hr.transferFee": "ค่าธรรมเนียมโอน",
  "hr.workDaysTripsOt": "วันทำงาน / เที่ยว / ล่วงเวลา",
  "hr.totalNetPay": "ยอดสุทธิรวม",
  "hr.totalGross": "ยอดรวมทั้งหมด",
  "hr.tickets": "ใบจ่าย",
  "hr.anomalies": "รายการผิดปกติ",
  "common.endAfterStart": "วันสิ้นสุดต้องอยู่หลังวันเริ่ม",
  "hr.noProvincesConfigured": "ยังไม่ได้ตั้งค่าจังหวัด เพิ่มใน Master Data ก่อน",
  "hr.noChargesForTicket": "ใบงานนี้ยังไม่มีค่าใช้จ่าย",
  "hr.driverDefaultAnyTrip": "ค่าตั้งต้นของคนขับ (ทุกเที่ยว)",
  "hr.specificContract": "ระบุสัญญา",
  "fleet.tax": "ภาษี",
  "fleet.groupsItems": "กลุ่มและรายการ",
  "fleet.runInspection": "เริ่มตรวจสภาพ",
  "fleet.template": "ชุดตรวจ",
  "fleet.vehicle": "รถ",
  "fleet.templateNoItems": "ชุดตรวจนี้ยังไม่มีรายการ",
  "common.requiredCaps": "บังคับ",
  "fleet.answerRequired": "ต้องตอบ",
  "fleet.noAnswers": "ยังไม่มีคำตอบที่บันทึกไว้",
  "fleet.inspectionChecklist": "รายการตรวจสภาพรถ",
  "fleet.inspectionSubtitle": "สร้างชุดตรวจและดูผลการตรวจที่ส่งเข้ามา",
  "fleet.checklistPurpose": "วัตถุประสงค์ของรายการตรวจนี้…",
  "fleet.groupName": "ชื่อกลุ่ม",
  "fleet.itemLabel": "ชื่อรายการ",
  "fleet.selectTemplate": "เลือกชุดตรวจ…",
  "fleet.closeSignOff": "ปิดและรับรองผลการตรวจ?",
  "fleet.editTemplate": "แก้ไขชุดตรวจ",
  "fleet.deactivateTemplate": "ปิดใช้งานชุดตรวจ",
  "common.deactivate": "ปิดใช้งาน",
  "fleet.deactivateTemplateQ": "ปิดใช้งานชุดตรวจนี้?",
  "fleet.noGroupsYet": "ยังไม่มีกลุ่ม เพิ่มกลุ่มเพื่อเริ่มสร้างรายการตรวจ",
  "fleet.passFail": "ผ่าน / ไม่ผ่าน",
  "common.text": "ข้อความ",
  "common.number": "ตัวเลข",
  "fleet.photoUrl": "รูป (URL)",
  "status.submitted": "ส่ง",
  "fleet.closedBy": "ปิดโดย",
  "fleet.templates": "แม่แบบ",
  "fleet.submissions": "ผลการตรวจ",
  "common.pngJpg": "PNG / JPG",
  "fleet.financeType": "ประเภทสินเชื่อ",
  "fleet.lenderRequired": "ต้องระบุผู้ให้สินเชื่อ",
  "fleet.principal": "เงินต้น (บาท)",
  "fleet.monthlyPayment": "ค่างวดต่อเดือน (บาท)",
  "fleet.termMonths": "ระยะเวลา (เดือน)",
  "fleet.paidInstallments": "งวดที่ชำระแล้ว",
  "fleet.installmentProgress": "ความคืบหน้าการผ่อน",
  "fleet.installmentsExceedTerm": "งวดที่ชำระเกินระยะเวลาที่กำหนด",
  "common.startDateRequired": "ต้องระบุวันเริ่ม",
  "fleet.noFinanceRecords": "รถคันนี้ยังไม่มีข้อมูลสินเชื่อ",
  "fleet.maintenanceHistory": "ประวัติการซ่อมบำรุง",
  "fleet.noWorkOrdersVehicle": "รถคันนี้ยังไม่มีใบสั่งซ่อม",
  "fleet.editTaxRecord": "แก้ไขรายการภาษี",
  "fleet.deleteTaxRecord": "ลบรายการภาษี",
  "fleet.deleteTaxRecordQ": "ลบรายการภาษีนี้?",
  "fleet.editFinanceRecord": "แก้ไขรายการสินเชื่อ",
  "fleet.deleteFinanceRecord": "ลบรายการสินเชื่อ",
  "fleet.deleteFinanceRecordQ": "ลบรายการสินเชื่อนี้?",
  "fleet.firstRegistration": "จดทะเบียนครั้งแรก",
  "fleet.front": "หน้า",
  "fleet.rear": "หลัง",
  "fleet.left": "ซ้าย",
  "fleet.right": "ขวา",
  "fleet.interior": "ภายใน",
  "fleet.registrationBook": "เล่มทะเบียน",
  "common.other": "อื่น ๆ",
  "status.open": "เปิด",
  "fleet.financeLoadFailed": "โหลดข้อมูลสินเชื่อไม่สำเร็จ",
  "fleet.fleetFinance": "การเงินของกองรถ",
  "fleet.fuelCostSummary": "สรุปค่าน้ำมันรายคัน",
  "fleet.noVehiclesFound": "ไม่พบรถ",
  "fleet.topFuelCosts": "ค่าน้ำมันสูงสุด",
  "fleet.noFuelData": "ไม่มีข้อมูลน้ำมัน",
  "fleet.fleetEfficiency": "ประสิทธิภาพกองรถ",
  "fleet.totalVehicles": "รถทั้งหมด",
  "fleet.avgEfficiency": "ประสิทธิภาพเฉลี่ย",
  "fleet.vehiclesTracked": "รถที่ติดตาม",
  "fleet.totalContractCost": "มูลค่าสัญญารวม (฿)",
  "fleet.expiring90d": "ใกล้หมดอายุ ≤90 วัน",
  "fleet.importFromCsv": "นำเข้ารถจากไฟล์ CSV",
  "fleet.vehicleRequired": "ต้องระบุรถ",
  "fleet.policyNumber": "เลขที่กรมธรรม์",
  "fleet.insurer": "บริษัทประกัน",
  "fleet.expiryDate": "วันหมดอายุ",
  "fleet.insuranceLoadFailed": "โหลดข้อมูลประกันไม่สำเร็จ",
  "fleet.addPolicy": "เพิ่มกรมธรรม์",
  "fleet.expiring90dShort": "ใกล้หมดอายุ (90 วัน)",
  "fleet.noPolicies": "ไม่พบกรมธรรม์",
  "fleet.searchInsurance": "ค้นหารถ บริษัท เลขกรมธรรม์…",
  "status.pendingRenewal": "รอต่ออายุ",
  "status.ok": "ปกติ",
  "fleet.activePolicies": "กรมธรรม์ที่ใช้งาน",
  "fleet.totalPremiumYr": "เบี้ยประกันรวม/ปี",
  "fleet.vehiclesInsured": "รถที่ทำประกัน",
  "fleet.workflowTimeline": "ไทม์ไลน์ขั้นตอน",
  "fleet.woLoadFailed": "โหลดใบสั่งซ่อมไม่สำเร็จ",
  "fleet.workOrders": "ใบสั่งซ่อม",
  "fleet.noWorkOrdersMatch": "ไม่มีใบสั่งซ่อมที่ตรงกับตัวกรอง",
  "fleet.issueDetails": "รายละเอียดปัญหา…",
  "fleet.searchWorkOrders": "ค้นหาเลขใบสั่งซ่อม รถ รายละเอียด อู่…",
  "fleet.deleteWorkOrder": "ลบใบสั่งซ่อม",
  "status.critical": "วิกฤต",
  "status.high": "สูง",
  "status.medium": "กลาง",
  "status.low": "ต่ำ",
  "fleet.totalWos": "ใบสั่งซ่อมทั้งหมด",
  "fleet.openWos": "ใบสั่งซ่อมที่เปิดอยู่",
  "common.id": "รหัส",
  "fleet.plate": "ทะเบียน",
  "fleet.province": "จังหวัด",
  "fleet.age": "อายุ",
  "fleet.pmSchedule": "แผนบำรุงรักษา",
  "status.overdue": "เลยกำหนด",
  "status.dueSoon": "ใกล้ครบกำหนด",
  "fleet.totalScheduled": "ที่นัดไว้ทั้งหมด",
  "fleet.drvShort": "DRV",
  "fleet.doorShort": "ประตู",
  "fleet.seat": "ที่นั่ง",
  "fleet.aisle": "ทางเดิน",
  "fleet.door": "ประตู",
  "fleet.empty": "ว่าง",
  "fleet.wheelchair": "รถเข็น",
  "fleet.luggage": "สัมภาระ",
  "ops.upcomingTrips": "เที่ยวที่จะถึง",
  "ops.noUpcomingTrips": "ไม่มีเที่ยวที่จะถึง",
  "ops.recentAlerts": "การแจ้งเตือนล่าสุด",
  "ops.noRecentAlerts": "ไม่มีการแจ้งเตือนล่าสุด",
  "fleet.status": "สถานะรถ",
  "ops.bookingByRoute": "การจองแยกตามเส้นทาง",
  "ops.noBookingData": "ไม่มีข้อมูลการจอง",
  "ops.tripsToday": "เที่ยววันนี้",
  "fleet.vehiclesRunning": "รถที่กำลังวิ่ง",
  "ops.onTimeRate": "อัตราตรงเวลา",
  "ops.passengersBooked": "ผู้โดยสารที่จองแล้ว",
  "ops.seatUtilization": "การใช้ที่นั่ง",
  "status.running": "กำลังวิ่ง",
  "feedback.loadFailed": "โหลดความคิดเห็นไม่สำเร็จ",
  "feedback.rateTrip": "ให้คะแนนการเดินทาง",
  "feedback.rating": "คะแนน",
  "feedback.comment": "ความคิดเห็น",
  "feedback.submitted": "ส่งความคิดเห็นเรียบร้อยแล้ว",
  "feedback.mine": "ความคิดเห็นของฉัน",
  "common.resolve": "ปิดเรื่อง",
  "nav.feedback": "ความคิดเห็น",
  "feedback.submitAnother": "ส่งอีกครั้ง",
  "status.acknowledged": "รับทราบ",
  "status.resolved": "แก้ไข",
  "fuel.loadFailed": "โหลดข้อมูลน้ำมันไม่สำเร็จ",
  "fuel.avgCostPerVehicle": "ค่าใช้จ่ายเฉลี่ย / คัน",
  "fuel.vehiclePerformance": "ประสิทธิภาพรายคัน",
  "fleet.noVehiclesMatch": "ไม่มีรถที่ตรงกับตัวกรอง",
  "common.noRecordsMatch": "ไม่มีรายการที่ตรงกับตัวกรอง",
  "fuel.searchIdPlateDriver": "รหัสน้ำมัน / ทะเบียน / คนขับ",
  "fleet.searchIdPlateType": "ค้นหารหัส ทะเบียน ประเภท…",
  "common.backToPage1": "กลับไปหน้า 1",
  "fuel.pass": "ผ่าน",
  "fuel.watch": "เฝ้าดู",
  "fuel.belowStdShort": "ต่ำกว่าเกณฑ์",
  "fuel.fromNMeasured": "จาก {n} คันที่คำนวณได้",
  "fuel.kmplSample": "{segments} ช่วง · {km} กม. · {liters} ล.",
  "fuel.kmplNoEntries": "ยังไม่มีใบเติม",
  "fuel.kmplTooFew": "ข้อมูลยังน้อย",
  "fuel.kmplNoOdometer": "ยังไม่มีเลขไมล์",
  "fuel.kmplOutOfRange": "ผิดปกติ รอตรวจ",
  "fuel.kmplRowsFlagged": "ลิตรผิดปกติ",
  "fuel.kmplOdometerBackwards": "เลขไมล์ถอยหลัง",
  "fuel.avgKmPerLiter": "กม./ลิตร เฉลี่ย (ทั้งกอง)",
  "fuel.belowStandard": "ต่ำกว่ามาตรฐาน",
  "fuel.efficiencyDashboard": "ภาพรวมประสิทธิภาพ",
  "fuel.vehicleRecords": "บันทึกรายคัน",
  "ops.loadingHistory": "กำลังโหลดประวัติ…",
  "ops.noCompletedTrips": "ยังไม่มีเที่ยวที่เสร็จสิ้น",
  "ops.departure": "เวลาออก",
  "ops.routeHistory": "ประวัติเส้นทาง",
  "legal.terms": "ข้อกำหนด",
  "legal.privacy": "ความเป็นส่วนตัว",
  "legal.pdpa": "PDPA",
  "legal.rightsReserved": "สงวนลิขสิทธิ์",
  "legal.privacyPolicy": "นโยบายความเป็นส่วนตัว",
  "legal.purposeEmails": "เพื่อส่งอีเมลธุรกรรม (ใบแจ้งหนี้ การแจ้งเตือนความปลอดภัย)",
  "legal.purposeReliability": "เพื่อปรับปรุงความเสถียรของบริการและตรวจจับการใช้งานผิดวิธี",
  "legal.purposeLegal": "เพื่อปฏิบัติตามข้อกำหนดทางกฎหมาย",
  "legal.rightAccess": "ขอเข้าถึงข้อมูลที่เราเก็บเกี่ยวกับคุณ",
  "legal.rightCorrect": "ขอแก้ไขข้อมูลที่ไม่ถูกต้อง",
  "legal.rightExport": "ขอส่งออกข้อมูลในรูปแบบที่นำไปใช้ต่อได้",
  "legal.rightObject": "คัดค้านหรือจำกัดการประมวลผล",
  "legal.rightWithdraw": "ถอนความยินยอมเมื่อใดก็ได้",
  "legal.pdpaNotice": "ประกาศ PDPA",
  "legal.termsOfService": "ข้อกำหนดการให้บริการ",
  "legal.prohibitUpload": "อัปโหลดเนื้อหาที่ผิดกฎหมาย เป็นอันตราย หรือละเมิดสิทธิ์",
  "legal.prohibitAccess": "พยายามเข้าถึงข้อมูลขององค์กรอื่น",
  "legal.prohibitProbe": "แทรกแซงหรือทดสอบกลไกความปลอดภัยของบริการ",
  "legal.prohibitSpam": "ใช้บริการเพื่อส่งข้อความที่ผู้รับไม่ต้องการ",
  "ops.startTime": "เวลาเริ่ม",
  "ops.expectedEnd": "เวลาสิ้นสุดที่คาด",
  "common.totalRows": "จำนวนแถวทั้งหมด",
  "common.row": "แถว",
  "common.field": "ฟิลด์",
  "common.message": "ข้อความ",
  "nav.notifications": "การแจ้งเตือน",
  "common.clearSelection": "ล้างที่เลือก",
  "common.dismissNotification": "ปิดการแจ้งเตือน",
  "common.noImage": "ไม่มีรูป",
  "fuel.entryDetails": "รายละเอียดการเติมน้ำมัน",
  "fuel.proofPhotos": "รูปหลักฐาน",
  "fuel.reportedLiters": "จำนวนลิตรที่แจ้ง",
  "fuel.reportedCost": "จำนวนเงินที่แจ้ง",
  "fuel.approxPerLiter": "ประมาณราคาต่อลิตร",
  "fuel.reportedOdometer": "เลขไมล์ที่แจ้ง",
  /* ADR 0275 — รางเหตุการณ์ + แผนที่ในแผงขวาของกล่องรายละเอียดการเติมน้ำมัน */
  "fuel.timeline": "ลำดับเหตุการณ์",
  "fuel.tlTotal": "รวม {d}",
  "fuel.tlCreated": "เริ่มรายการ",
  "fuel.tlSubmitted": "ส่งตรวจ",
  "fuel.tlNoCoords": "ไม่มีพิกัด",
  "fuel.tlNoCoordsAll": "ไม่มีพิกัดจากขั้นใดเลย",
  "fuel.tlOpenMaps": "เปิดใน Google Maps",
  "fuel.tlAccuracy": "ความคลาดเคลื่อน ±{m} ม.",
  "fuel.unitHour": " ชม.",
  "fuel.unitMinute": " นาที",
  "fuel.unitSecond": " วินาที",
  "fuel.sourceWebTest": "ทดสอบจากเว็บ",
  "fuel.sourceWeb": "บันทึกจากเว็บ",
  "fuel.sourceDemoSeed": "ข้อมูลตัวอย่าง",
  "fuel.reviewedBy": "ตรวจโดย",
  "fuel.rejectReason": "เหตุผลที่ปฏิเสธ",
  "fuel.vehicleStatusAtEntry": "สถานะรถตอนเติม",
  "fuel.flagClockSkew": "นาฬิกาเครื่องคลาดเคลื่อน",
  "fuel.flagNoLocation": "ไม่ได้พิกัด",
  "fuel.flagOdometerBackwards": "เลขไมล์ย้อนหลัง",
  "fuel.flagVehicleNotActive": "รถไม่อยู่ในสถานะใช้งาน",
  "fuel.locPermissionDenied": "ไม่ได้พิกัด — ไม่ได้ให้สิทธิ์",
  "fuel.locUnavailable": "ไม่ได้พิกัด — หาสัญญาณไม่ได้",
  "fuel.locTimeout": "ไม่ได้พิกัด — หมดเวลา",
  "fuel.locNotRequested": "ไม่ได้ขอพิกัด",
  "fuel.confirmApproveTitle": "อนุมัติรายการเติมน้ำมันนี้?",
  "fuel.confirmApproveBody": "อนุมัติรายการของ {vehicle}",
  "fuel.confirmSuspiciousTitle": "ทำเครื่องหมายว่าน่าสงสัย?",
  "fuel.confirmSuspiciousBody": "พักรายการของ {vehicle} ไว้เป็นน่าสงสัย จะไม่ถูกนับในยอดใด ๆ จนกว่าจะมีคนอนุมัติ",
  "fuel.confirmRejectTitle": "ปฏิเสธรายการเติมน้ำมันนี้?",
  "fuel.confirmRejectBody": "ปฏิเสธรายการของ {vehicle}\nหมายเหตุ: {note}",
  "fuel.markSuspicious": "ทำเครื่องหมายน่าสงสัย",
  "fuel.reviewFailed": "บันทึกผลตรวจไม่สำเร็จ",
  "fuel.noteFirst": "เขียนหมายเหตุก่อน — การปฏิเสธต้องบอกเหตุผล",
  "fuel.noteHint": "หมายเหตุ — บังคับกรอกเมื่อปฏิเสธ นอกนั้นไม่บังคับ",
  "fuel.adjustedOnReview": "แก้ไขตอนตรวจสอบ",
  "fuel.approvedLiters": "จำนวนลิตรที่อนุมัติ",
  "fuel.odometer": "เลขไมล์ (กม.)",
  "status.rejected": "ปฏิเสธ",
  "status.suspicious": "น่าสงสัย",
  "status.discarded": "ทิ้ง",
  "fuel.logTest": "บันทึกการเติม (ทดสอบ)",
  "fuel.logFromWeb": "บันทึกการเติม",
  "fuel.logFromWebHint": "บันทึกการเติมน้ำมันจากฝั่งสำนักงาน พร้อมแนบหลักฐานได้",
  "fuel.type": "ชนิดเชื้อเพลิง",
  "fuel.payment": "การชำระเงิน",
  "fuel.station": "ปั๊ม",
  "fuel.driverWhoFilled": "คนขับที่เติม",
  "fuel.driverDefaultsToBound": "ไม่เลือก = ใช้คนขับประจำรถคันนี้",
  "fuel.proofGaugeBefore": "มาตรวัดก่อนเติม",
  "fuel.proofGaugeAfter": "มาตรวัดหลังเติม",
  "fuel.proofPumpMeter": "หน้าปัดหัวจ่าย",
  "fuel.proofSlip": "ใบเสร็จ / สลิป",
  "fuel.proofPhotosOptional": "รูปหลักฐาน (ไม่บังคับ)",
  "fleet.searchVehicleIdPlate": "ค้นหารถ (รหัส / ทะเบียน)…",
  "fuel.cash": "เงินสด",
  "fuel.card": "บัตรน้ำมัน",
  "fuel.bankTransfer": "โอนผ่านธนาคาร",
  "nav.portal": "พอร์ทัล",
  "ops.activeContracts": "สัญญาที่ใช้งาน",
  "ops.totalContracts": "สัญญาทั้งหมด",
  "ops.totalCustomers": "ลูกค้าทั้งหมด",
  "ops.deleteCustomer": "ลบลูกค้า",
  "common.confirmDeleteShort": "ยืนยันการลบ",
  "common.lastUpdated": "แก้ไขล่าสุด",
  "ops.ovr": "OVR",
  "ops.bookingDashboard": "ภาพรวมการจอง",
  "ops.totalBookings30d": "การจองทั้งหมด (30 วัน)",
  "ops.loadingTicket": "กำลังโหลดใบงาน…",
  "hr.driverCompReadOnly": "ค่าตอบแทนคนขับ (อ่านอย่างเดียว)",
  "common.quickActions": "ทางลัด",
  "ops.plannedRoute": "เส้นทางที่วางไว้",
  "ops.actualPathLive": "เส้นทางจริง (สด)",
  "ops.actualPathDone": "เส้นทางจริง (จบแล้ว)",
  "ops.arrivedStop": "จุดที่ถึงแล้ว",
  /* ADR 0253 — the fourth state, in the map legend. Same fact as
     `warroom.stopSkipped`; this one names a STOP in a key, that one labels a row. */
  "ops.skippedStop": "จุดที่พลาด",
  "ops.pendingStop": "จุดที่ยังไม่ถึง",
  "ops.promise": "เวลาที่รับปาก",
  "ops.resetToTemplate": "คืนค่าตามแม่แบบ",
  "ops.noActivityYet": "ยังไม่มีกิจกรรมที่บันทึกไว้",
  "ops.refreshTimeline": "โหลดไทม์ไลน์ใหม่",
  "status.invited": "เชิญ",
  "ops.searchNameSlug": "ค้นหาชื่อหรือ slug…",
  "ops.noPortalsYet": "ยังไม่มีพอร์ทัล เริ่มด้วยการสร้างอันแรก",
  "ops.startEndMustDiffer": "เวลาเริ่มต้องไม่ตรงกับเวลาสิ้นสุด",
  "ops.routeLockedHasTrips": "เส้นทางถูกล็อกเมื่อมีเที่ยวแล้ว ลบเที่ยวก่อนจึงจะแก้ได้",
  "ops.defaultVehiclesPerDay": "จำนวนรถตั้งต้น / วัน",
  "ops.noVehicleAssignedYet": "ยังไม่ได้มอบหมายรถ",
  "ops.newScheduleBoard": "สร้างแผนงานใหม่",
  "common.stats": "สถิติ",
  "ops.toggleDepots": "สลับแสดงจุดจอดรถ",
  "ops.editTrip": "แก้ไขเที่ยว",
  "ops.clearAssignments": "ล้างการมอบหมาย",
  "ops.pickDates": "เลือกวันที่",
  "common.jumpToToday": "ไปวันนี้",
  "ops.quickAddThisMonth": "เพิ่มเร็ว (เดือนนี้)",
  "common.monFri": "จ.–ศ.",
  "common.allMondays": "ทุกวันจันทร์",
  "common.tueThu": "อ./พฤ.",
  "ops.clearMonth": "ล้างทั้งเดือน",
  "ops.fillRoute": "เติมเส้นทาง",
  "ops.optimizeSettings": "ตั้งค่าการจัดเส้นทาง",
  "ops.optimizationMode": "โหมดการจัดเส้นทาง",
  "ops.backhaulTuning": "ปรับเที่ยวกลับ",
  "ops.balanced": "สมดุล",
  "ops.backhaul": "เที่ยวกลับ",
  "ops.board": "บอร์ด",
  "ops.rechecking": "กำลังตรวจซ้ำ",
  "ops.publishJobs": "เผยแพร่ใบงาน",
  "ops.checkingBoard": "กำลังตรวจบอร์ด…",
  "ops.readyPublishAgain": "พร้อมเผยแพร่อีกครั้ง",
  "ops.publishSettings": "ตั้งค่าการเผยแพร่",
  "ops.nothingCreated": "ไม่ได้สร้างอะไรเลย",
  "ops.publish": "เผยแพร่",
  "ops.renameTable": "เปลี่ยนชื่อตาราง",
  "ops.removeThisDate": "ลบวันที่นี้",
  "ops.dateRoute": "วันที่ / เส้นทาง",
  // ADR 0202 follow-up — แถบหัวตารางของบอร์ด (TableSection)
  // ไทยไม่มีพหูพจน์ สองคีย์จึงเป็นคำเดียวกัน แต่ต้องมีคู่ให้ครบเพื่อให้ฝั่งอังกฤษเติม s ได้
  "ops.saveAndContinue": "บันทึกแล้วไปต่อ",
  "ops.deleteTripBlockedByJobs": "ช่องนี้สร้างใบงานไปแล้ว {n} ใบ — ให้ถอดรถทีละคันก่อน ระบบจะยกเลิกใบงานให้",
  "ops.deleteTripForOn": "ลบเที่ยวของ {route} วันที่ {date} ใช่ไหม?",
  "ops.unitDate": "วัน",
  "ops.unitDates": "วัน",
  "ops.unitRoute": "เส้นทาง",
  "ops.unitRoutes": "เส้นทาง",
  "ops.unitRun": "เที่ยว",
  "ops.unitRuns": "เที่ยว",
  "ops.sortByTimeTip": "เรียงคอลัมน์ตามเวลา",
  "ops.sortByRouteTip": "จัดกลุ่มคอลัมน์ตามเส้นทาง",
  "ops.sortByPairTip": "จัดคู่ขาไป/ขากลับให้อยู่ติดกัน",
  "ops.clickToEdit": "คลิกเพื่อแก้ไข",
  "ops.runFallback": "เที่ยว",
  "ops.emptyAddRouteFirst": "เพิ่มเส้นทางจากหัวตาราง แล้วเลือกวันที่",
  "ops.emptyNoDatesYet": "ยังไม่มีวันที่ — กด + วันที่ ด้านล่างเพื่อเริ่ม",
  "ops.plusOneDay": "+1 วัน",
  "ops.searchAvailablePlateDriver": "ค้นหารถว่าง — ทะเบียน / คนขับ…",
  "ops.searchPlateOrDriver": "ค้นหาทะเบียนหรือคนขับ…",
  "ops.timeConflictOverlap": "เวลาชนกัน — ถูกมอบหมายให้เที่ยวที่ทับกัน",
  "ops.removeFromPool": "นำออกจากกลุ่ม",
  "ops.vehiclePoolTitle": "กลุ่มรถ",
  "ops.addVehicleToPool": "เพิ่มรถเข้ากลุ่ม",
  "ops.addDriverTeamToPool": "เพิ่มทีมคนขับเข้ากลุ่ม",
  "ops.someDriversNoVehicle": "คนขับบางคนยังไม่มีรถ",
  "status.expiringSoon": "ใกล้หมดอายุ",
  "status.received": "รับ",
  "status.returned": "คืน",
  "common.emailAddr": "อีเมล",
  "ops.jobTimeline": "ไทม์ไลน์งาน",
  "common.contact": "ผู้ติดต่อ",
  "common.noDocuments": "ยังไม่มีเอกสารที่อัปโหลด",
  "status.voided": "ยกเลิกใช้",
  "status.ended": "สิ้นสุด",
  "status.activeCaps": "ใช้งาน",
  "hr.driverRates": "อัตราค่าตอบแทนคนขับ",
  "common.newestFirst": "ใหม่ก่อน",
  "common.oldestFirst": "เก่าก่อน",
  "common.idAsc": "รหัส (น้อย → มาก)",
  "common.idDesc": "รหัส (มาก → น้อย)",
  "common.nameAsc": "ชื่อ (ก → ฮ)",
  "common.nameDesc": "ชื่อ (ฮ → ก)",
  "ops.noPassengersYet": "ยังไม่มีผู้โดยสาร เริ่มด้วยการสร้างคนแรก",
  "hr.initialPassword": "รหัสผ่านเริ่มต้น (ไม่บังคับ)",
  "hr.initialPasswordHint": "อย่างน้อย 8 ตัว เว้นว่างไว้เพื่อส่งอีเมลเชิญแทน",
  "status.ready": "พร้อมใช้",
  "status.decommissioned": "ปลดระวาง",
  "ops.maxSeats": "ที่นั่งสูงสุด",
  "ops.maxSeatsHint": "จำนวนที่นั่งที่อนุญาตทั้งหมด",
  "ops.seatModeHint": "ระบุเลขที่นั่งหรือนับจำนวนเฉย ๆ",
  "ops.capActionHint": "สิ่งที่ทำเมื่อเต็มจำนวน",
  "ops.allowDriverBoarding": "ให้คนขับรับผู้โดยสารขึ้นรถได้",
  "ops.allowQrCheckin": "ให้เช็คอินด้วย QR ได้",
  "ops.gpsInterval": "รอบส่งพิกัด GPS",
  // ADR 0515 D6 — shown under the locked GPS interval of a Locus ticket
  "ops.gpsIntervalLocusLocked": "ใบงาน Locus ใช้รอบส่งพิกัดตามการตั้งค่าโหมดรอขององค์กร ไม่ต้องตั้งที่ใบงาน",
  "ops.deleteRouteWarning": "เส้นทางนี้มี {n} เที่ยว การลบจะลบคอลัมน์และเที่ยวทั้งหมดด้วย",
  "ops.deletePairWarning": "เส้นทางคู่นี้มี {n} เที่ยวรวมกัน การลบจะลบทั้งสองคอลัมน์และเที่ยวทั้งหมดด้วย",
  "ops.removeDateWarning": "วันที่นี้มี {n} เที่ยว การลบจะลบเที่ยวเหล่านั้นด้วย",
  "ops.clearAllWarning": "ล้างรถที่มอบหมายออกจากทุกช่องในบอร์ดนี้? เส้นทาง วันที่ และจำนวนที่ต้องใช้ยังอยู่ครบ ล้างเฉพาะการมอบหมายรถ",
  "ops.clearAll": "ล้างทั้งหมด",
  "ops.removeAndDeleteTrips": "ลบวันที่และเที่ยวทั้งหมด",
  "ops.boardNotFound": "ไม่พบบอร์ด",
  "ops.clearAllOnBoard": "ล้างการมอบหมายรถทั้งหมดในบอร์ดนี้",
  "ops.withdrawLastPublish": "เครื่องมือทดสอบ — ถอนใบงานที่เพิ่งเผยแพร่ล่าสุด",
  "ops.undoLastAiApply": "ย้อนการใช้ AI ครั้งล่าสุด",
  "ops.undoAi": "ย้อน AI",
  "ops.clearAllAssignments": "ล้างการมอบหมายทั้งหมด",
  "ops.removeDate": "ลบวันที่",
  "ops.undoneNotEverything": "ย้อนแล้ว แต่ไม่ทั้งหมด",
  "ops.undoAiPlan": "ย้อนแผน AI",
  "ops.aiOptimizePlan": "ให้ AI จัดแผน",
  "status.published": "เผยแพร่",
  "common.rename": "เปลี่ยนชื่อ",
  "ops.duplicateBoard": "ทำสำเนาบอร์ด",
  "ops.newBoardName": "ชื่อบอร์ดใหม่",
  "ops.datesAndRouteCols": "วันที่และคอลัมน์เส้นทาง",
  "ops.vehiclePool": "กลุ่มรถ",
  "ops.minimapRunning": "วิ่ง {n}",
  "ops.minimapFree": "ว่าง {n}",
  "ops.minimapWarned": "เตือน {n}",
  "ops.minimapPending": "เพิ่ม {added} · เอาออก {removed}",
  "ops.minimapNoChange": "ยังไม่มีการเปลี่ยนแปลง",
  "ops.minimapTooManyOps": "เปลี่ยน {n} ช่อง เกินครั้งละ {max} ช่อง",
  "ops.minimapDragWillAdd": "จะใส่ {n} ช่อง",
  "ops.minimapDragWillRemove": "จะเอาออก {n} ช่อง",
  "ops.minimapClearPending": "ล้างที่เลือก",
  "ops.minimapSelectAll": "เลือกทั้งหมด",
  "ops.minimapSelectAllTip": "ข้ามช่องที่มีคำเตือน — เพิ่มช่องเหล่านั้นเองทีหลังได้",
  "ops.minimapSelectVacant": "เลือกเฉพาะที่ว่าง",
  "ops.viewByTime": "เวลา",
  "ops.viewByRoute": "เส้นทาง",
  "ops.viewByPair": "คู่",
  "ops.boardTabTable": "ตาราง",
  "ops.boardTabStats": "สถิติ",
  "ops.boardTabs": "มุมมองของหน้าบอร์ด",
  "common.checking": "กำลังตรวจสอบ…",
  "ops.noMatchingVehicles": "ไม่พบรถที่ตรงกับที่ค้นหา",
  "ops.noVehiclesAvailable": "ไม่มีรถว่าง",
  "ops.searchTeamName": "ค้นหาชื่อทีม…",
  "ops.poolTeams": "ทีมคนขับ",
  "ops.poolTeamsPicked": "เลือกไว้ {n} ทีม",
  "ops.teamHasNoVehicles": "ทีมนี้ยังไม่มีรถ",
  "ops.poolTeamRemoveTitle": "เอาทีม {name} ออกจากกลุ่มรถ",
  "ops.poolTeamWillDrop": "ถอดรถ {n} คัน",
  "ops.poolTeamAssigned": "{v} คันถูกจ่ายงานไว้แล้ว {c} ช่อง — จะถูกถอดออกจากช่องด้วย",
  "ops.poolTeamPublished": "{n} ช่องในนั้นส่งใบงานให้คนขับไปแล้ว — ใบที่ยังเป็นร่างหรือมอบหมายจะถูกยกเลิกอัตโนมัติ ใบอื่นจะไม่ถูกแตะ",
  "ops.poolRemoveCancelled": "ยกเลิกใบงาน {n} ใบ",
  "ops.poolRemoveLocked": "มี {n} ใบที่ยกเลิกไม่ได้เพราะอยู่ระหว่างดำเนินการ",
  "ops.poolSaved": "บันทึกกลุ่มรถแล้ว",
  "ops.addingTeam": "กำลังเพิ่มทีม…",
  "ops.vehiclePoolRuns": "วิ่ง {n}",
  "ops.statusFree": "ว่าง",
  "ops.statusPartial": "ว่างบางช่วง",
  "ops.statusBusy": "ไม่ว่าง",
  "ops.statusRepair": "ซ่อม",
  "ops.statusConflict": "ชนเวลา",
  "ops.cellSent": "เผยแพร่",
  "ops.cellResend": "แก้ไข",
  "ops.cellSentTitle": "เผยแพร่ใบงานให้คนขับแล้ว",
  "ops.cellResendTitle": "แก้หลังเผยแพร่ไปแล้ว — ต้องกดเผยแพร่อีกครั้ง",
  // ADR 0238 — คำอธิบายสัญลักษณ์บนกริดจัดเส้นทาง (legend)
  "ops.legendGroupCell": "ในช่อง",
  "ops.legendGroupColumn": "หัวคอลัมน์ (เส้นทาง)",
  "ops.legendGroupDate": "หัวแถว (วันที่)",
  "ops.legendCellSent": "ส่งใบงานให้คนขับแล้ว",
  "ops.legendCellSentSub": "มีใบงานจริงตรงกับช่องนี้",
  "ops.legendCellResend": "แก้ไขหลังเผยแพร่แล้ว",
  "ops.legendCellResendSub": "ต้องกดเผยแพร่ซ้ำ ข้อมูลบนมือคนขับยังเป็นของเก่า",
  "ops.legendPlateGreen": "ป้ายทะเบียนสีเขียว",
  "ops.legendPlateGreenSub": "คันนี้มีใบงานที่เผยแพร่แล้ว",
  "ops.legendPlateAmber": "ป้ายทะเบียนสีส้ม",
  "ops.legendPlateAmberSub": "คันนี้ยังไม่ถูกส่ง แม้คันอื่นในช่องเดียวกันส่งแล้ว",
  "ops.legendPlateConflict": "ป้ายทะเบียนขอบแดง",
  "ops.legendPlateConflictSub": "รถคันนี้ถูกจ่ายไปสองที่ที่เวลาชนกัน",
  "ops.legendCountPending": "ตัวเลขสีเทา",
  "ops.legendCountPendingSub": "ยังไม่ได้เลือกรถเลย",
  "ops.legendCountFilled": "ตัวเลขสีเขียว",
  "ops.legendCountFilledSub": "ครบตามจำนวนที่ต้องการ ไม่มีปัญหา",
  "ops.legendCountBad": "ตัวเลขสีแดง",
  "ops.legendCountBadSub": "เกินจำนวนที่ตั้งไว้ หรือมีรถชนเวลากัน",
  "ops.legendConflictBg": "พื้นหลังช่องสีแดงจาง",
  "ops.legendConflictBgSub": "เที่ยวนี้ชนเวลากับอีกเที่ยวของรถคันเดียวกัน",
  "ops.legendEmptyDash": "ขีดกลาง",
  "ops.legendEmptyDashSub": "ไม่ต้องใช้รถในช่องนี้",
  "ops.legendColCheck": "เครื่องหมายถูก สีเขียว",
  "ops.legendColCheckSub": "ทุกวันของคอลัมน์นี้เผยแพร่แล้วและตรงกับข้อมูลปัจจุบัน",
  "ops.legendColAlert": "วงกลมตกใจ สีส้ม",
  "ops.legendColAlertSub": "บางวันในคอลัมน์นี้ยังไม่เผยแพร่ หรือแก้ไขหลังเผยแพร่แล้ว",
  "ops.legendColLock": "แม่กุญแจ",
  "ops.legendColLockSub": "คอลัมน์นี้มีใบงานเผยแพร่แล้ว แก้เส้นทาง/เวลา/จำนวนรถ หรือลบไม่ได้",
  "ops.legendPlusOneDay": "ป้าย +1 วัน",
  "ops.legendPlusOneDaySub": "เที่ยวข้ามเที่ยงคืน เวลาสิ้นสุดจึงมาก่อนเวลาเริ่มในหน้าปัด",
  "ops.legendGhostColumn": "คอลัมน์ลายทแยง",
  "ops.legendGhostColumnSub": "ตั้งใจจับคู่ไว้ แต่ยังไม่ได้เพิ่มเส้นทางอีกฝั่ง",
  "ops.legendDateLock": "แม่กุญแจ",
  "ops.legendDateLockSub": "วันนี้มีใบงานที่เผยแพร่แล้ว ลบวันที่นี้ไม่ได้",
  "ops.tripTimeConflictTitle": "ชนเวลา — ถูกจ่ายให้งานที่เวลาทับกัน",
  "ops.optimizeRunning": "กำลังจัดเส้นทาง…",
  "ops.optimizeRun": "เริ่มจัดเส้นทาง",
  "ops.balancedDesc": "กระจายงานให้รถทุกคันเท่า ๆ กัน (ค่าเริ่มต้น)",
  "ops.backhaulDesc": "ต่องานที่อยู่ใกล้กันเพื่อลดระยะวิ่งเที่ยวเปล่า",
  "ops.hideDepots": "ซ่อนจุดจอดรถ ({n})",
  "ops.showDepots": "แสดงจุดจอดรถ ({n})",
  "ops.timelineBrushHint": "ลากกล่องเพื่อย้าย · ลากขอบเพื่อปรับขนาด · คลิกนอกกล่องเพื่อล้าง",
  "ops.timelineEndHint": "คลิกอีกครั้งเพื่อกำหนดจุดสิ้นสุด",
  "ops.timelineStartHint": "คลิกเพื่อกำหนดจุดเริ่ม แล้วคลิกอีกครั้งเพื่อกำหนดจุดสิ้นสุด",
  "ops.minimapDragOverCap": "เกินครั้งละ {max} ช่อง",
  "ops.minimapNeedsDatesAndRoutes": "บอร์ดนี้ยังไม่มีวันที่หรือเส้นทาง",
  "ops.minimapDateColumn": "วันที่",
  "ops.minimapOpenClashing": "เปิดงานที่ทับเวลาในแท็บใหม่",
  "ops.minimapChangedElsewhere": "มีคนแก้บอร์ดนี้เมื่อ {at} — กดรีเฟรชเพื่อดูของล่าสุด",
  "ops.minimapLoadFailed": "โหลดตารางของคนขับไม่สำเร็จ",
  "ops.minimapSaveFailed": "บันทึกไม่สำเร็จ ไม่มีช่องไหนถูกเปลี่ยน",
  "ops.driverSchedule": "ตารางของคนขับ",
  "ops.confirmSaveTitle": "ยืนยันก่อนบันทึก",
  "ops.previewGroupUnassign": "ผู้ขับจะถอดออกจากเที่ยว",
  "ops.previewGroupCancel": "ผู้ขับจะถอดออกจากเที่ยวและยกเลิกงาน",
  "ops.previewGroupLocked": "ผู้ขับไม่สามารถยกเลิกงานได้",
  "ops.confirmWillCreate": "จะสร้างใบงานใหม่ {n} ใบ",
  "ops.confirmWillReplace": "จะออกใบงานแทนของเดิม {n} ใบ",
  "ops.confirmWillCancel": "จะยกเลิกใบงานเดิม {n} ใบ",
  "ops.confirmUntouchable": "ใบงาน {n} ใบแตะไม่ได้ (คนขับรับงานหรือเริ่มวิ่งแล้ว) จะถูกปล่อยไว้",
  "ops.confirmBlockedConflict": "มีเวลาทับกันในสิ่งที่จะบันทึก แก้ก่อนจึงจะบันทึกได้",
  "ops.confirmBlockedError": "บันทึกไม่ได้ในครั้งเดียว ลองลดจำนวนช่องที่แก้ลง",
  "ops.confirmAndSave": "ยืนยัน",
  "ops.minimapConfirmFailed": "บันทึกไม่สำเร็จ ไม่มีอะไรถูกเปลี่ยน",
  "ops.minimapBoardMoved": "บอร์ดเปลี่ยนระหว่างที่คุณกำลังดู กดบันทึกอีกครั้งเพื่อดูผลใหม่",
  "ops.removeOrphanJobs": "ยกเลิกใบงาน {n} ใบ ที่ช่องหรือวันที่ถูกเอาออกไปแล้ว",
  "ops.backdatedJobsTitle": "มี {n} งานที่เวลาเริ่มผ่านไปแล้ว",
  "ops.backdatedJobsHelp": "เวลาที่กำหนดไว้ผ่านมาแล้วเกิน 30 นาที ยืนยันเพื่อบันทึกงานเหล่านี้เป็น \"เสร็จสิ้น\" ทันที คนขับจะไม่เห็นเป็นงานที่ต้องทำ",
  "ops.backdatedJobsConfirm": "ยืนยันการสร้าง {n} งานนี้เป็นงานเสร็จสิ้นย้อนหลัง",
  "ops.nNewJobs": "{n} งานใหม่",
  "ops.nBackdatedJobs": "{n} งานย้อนหลัง",
  "ops.addToSlot": "เพิ่มเข้าช่องนี้",
  // ADR 0248 — รถที่เพิ่งเพิ่มเข้ากลุ่มแต่ยังไม่ได้กด Save changes มอบหมายเข้า cell ไม่ได้
  "ops.assignNeedsPoolSave": "บันทึกกลุ่มรถก่อน ถึงจะมอบหมายรถคันนี้ได้",
  // ADR 0278 D1 — a pool row is a person; these two states are theirs.
  "ops.memberNoVehicle": "ยังไม่มีรถ",
  "ops.memberHasNoVehicle": "ตอนนี้คนนี้ยังไม่ได้ผูกกับรถคันไหน จึงยังจ่ายลงช่องไม่ได้",
  "ops.memberInactive": "พ้นสภาพ",
  "ops.cellBlockedPoolDirty": "บันทึกกลุ่มรถก่อน ถึงจะเปิดช่องนี้ได้",
  "ops.poolEmpty": "ยังไม่มีรถในกลุ่มรถของบอร์ดนี้",
  "ops.poolEmptyHint": "จ่ายรถให้ช่องไหนไม่ได้จนกว่าจะเพิ่มรถ — กดปุ่มเพิ่มรถด้านบน",
  // ADR 0528
  "ops.poolOutsideDrivers": "มี {n} คนในตารางที่ยังไม่อยู่ในกลุ่มรถ",
  "ops.poolAddOutsideDrivers": "เพิ่มทั้ง {n} คนเข้ากลุ่ม",
  "ops.scheduleNeedsPoolSave": "บันทึกกลุ่มรถก่อน ถึงจะเปิดตารางของคนขับได้",
  "ops.vehiclesPerCell": "จำนวนรถที่ต้องใช้ต่อช่อง",
  "ops.deleteBoard": "ลบบอร์ด",
  "ops.totalBoards": "บอร์ดทั้งหมด",
  "ops.totalTables": "ตารางทั้งหมด",
  "ops.scheduleBoards": "วางแผนงานจำนวนมาก",
  "ops.searchBoards": "ค้นหาบอร์ด…",
  "ops.createFirstBoard": "สร้างบอร์ดแรกของคุณ",
  "ops.editBoard": "แก้ไขบอร์ด",
  "ops.boardDescriptionMore": "รายละเอียดเพิ่มเติม",
  "ops.routesScopedToContract": "แสดงเฉพาะเส้นทางของสัญญาที่ผูกกับบอร์ดนี้ — ถ้าไม่เจอที่ต้องการ ให้ปลดสัญญาออกจากบอร์ดก่อน",
  "ops.contractLockedRefreshed": "มีคนเพิ่มเส้นทางในบอร์ดนี้ระหว่างที่คุณกำลังแก้ เปลี่ยนสัญญาไม่ได้แล้ว",
  "ops.seedVehiclePool": "นำรถที่ผูกคนขับทุกคนเข้ากลุ่ม",
  "ops.seedVehiclePoolEmpty": "ไม่มีรถที่ผูกคนขับให้นำเข้ากลุ่ม — กลุ่มรถของบอร์ดนี้ยังว่าง ต้องเพิ่มรถเอง",
  "ops.boardCreatedAt": "สร้าง",
  "ops.boardCreatedBy": "ผู้สร้าง",
  "ops.boardUpdatedBy": "แก้ไข",
  "ops.people": "ผู้สร้าง",
  "common.tools": "เครื่องมือ",
  "ops.time": "เวลา",
  "ops.boardSaved": "บันทึกบอร์ดแล้ว",
  "ops.boardUpdatedAt": "แก้ไข",
  "ops.boardUpdatedAtHint": "เวลาที่ตั้งค่าบอร์ดถูกแก้ล่าสุด — การจ่ายรถและการเผยแพร่ไม่นับ",
  "common.charsLeft": "เหลือ {n} ตัวอักษร",
  "ops.noBoardsMatchFilters": "ไม่มีบอร์ดที่ตรงกับตัวกรอง",
  "ops.noBoardsYet": "ยังไม่มีบอร์ด",
  "ops.copiesFrom": "คัดลอกจาก {name}:",
  "ops.cellsStartUnassigned": "ช่องทั้งหมดจะยังว่าง — รถและคนขับไม่ถูกคัดลอกไปด้วย",
  "ops.deleteBoardConfirm": "ลบบอร์ด {name} พร้อมตารางและงานทั้งหมดในบอร์ดนี้? ย้อนกลับไม่ได้",
  "ops.boardCreateFailed": "สร้างบอร์ดไม่สำเร็จ",
  "ops.boardRenamed": "เปลี่ยนชื่อแล้ว",
  "ops.boardRenameFailed": "เปลี่ยนชื่อไม่สำเร็จ",
  "ops.boardDuplicated": "ทำสำเนาบอร์ดแล้ว",
  "ops.boardDuplicateFailed": "ทำสำเนาบอร์ดไม่สำเร็จ",
  "ops.boardDeleted": "ลบบอร์ดแล้ว",
  "ops.boardDeleteFailed": "ลบบอร์ดไม่สำเร็จ",
  "common.duplicating": "กำลังทำสำเนา…",
  "ops.routeNameExampleLong": "กรุงเทพฯ → ลาดกระบัง (เช้า)",
  "ops.activeRoutes": "เส้นทางที่ใช้งาน",
  "ops.totalDistance": "ระยะทางรวม",
  "ops.totalRoutes": "เส้นทางทั้งหมด",
  "ops.depot": "จุดจอดรถ",
  "ops.depotMarker": "D",
  "ops.routesApi": "Routes API",
  "ops.fleetVrp": "Fleet VRP",
  "ops.mockOffline": "จำลอง (ออฟไลน์)",
  "status.failed": "ล้มเหลว",
  "status.processing": "กำลังประมวลผล",
  "ops.searchPlateVehicleId": "ค้นหาทะเบียน / รหัสรถ…",
  "ops.avoidTolls": "เลี่ยงทางด่วน",
  "ops.avoidHighways": "เลี่ยงทางหลวง",
  "ops.optimizerRuns": "รอบการจัดเส้นทาง",
  "common.when": "เมื่อ",
  "ops.assignedDemand": "มอบหมาย / ต้องการ",
  "ops.fillRate": "อัตราเติมเต็ม",
  "ops.poolTrimmed": "รถในบอร์ดมากกว่าที่ตัวจัดเส้นทางรับได้ — คิดจาก {considered} คัน ตัดออก {trimmed} คัน",
  "ops.expandMap": "ขยายแผนที่",
  "ops.baselineDistanceAndCost": "ระยะทางตั้งต้นและต้นทุน",
  "ops.baselineDistance": "ระยะทางตั้งต้น",
  "ops.estimatedCost": "ต้นทุนโดยประมาณ",
  "ops.plannedDistance": "ระยะทางจากแผน",
  "ops.plannedDistanceHint": "พิมพ์เพื่อบันทึกเป็นข้อมูล",
  "ops.googleDistance": "ระยะทางจาก Google Map",
  "ops.googleDistanceHint": "คำนวณจากเส้นทางที่วางผ่านแผนที่",
  "ops.ticketDistance": "ระยะทางจากใบงาน",
  "ops.ticketDistanceHint": "เฉลี่ยจากระยะทางตามแผนของแต่ละใบงาน ต่างจากช่องด้านบนเฉพาะใบที่มีการแก้จุดจอด",
  "ops.plannedCost": "ต้นทุนโดยประมาณจากแผน",
  "ops.plannedCostHint": "พิมพ์เพื่อบันทึกเป็นข้อมูล",
  "ops.actualCost": "ต้นทุนจริง",
  "ops.actualCostHint": "เฉลี่ยจากค่าใช้จ่ายที่อนุมัติแล้วของงานที่ทำจบ (ยังไม่รวมค่าน้ำมัน)",
  "ops.costVariance": "ผลต่าง",
  "ops.costVarianceHint": "ต้นทุนจริง − ต้นทุนโดยประมาณ · บวกคือใช้เกินแผน",
  "ops.fromNJobs": "เฉลี่ยจาก {n} งาน",
  "ops.statsLoadFailed": "โหลดสถิติไม่สำเร็จ",
  "ops.showWholeRoute": "ดูทั้งเส้นทาง",
  "ops.showWholeRouteHint": "ซูมออกให้เห็นทุกจุดจอดในเส้นทางนี้",
  "ops.showAllPins": "ดูทุกหมุด", // ADR 0449 D6 — the shop dialog: branches, not a route
  "ops.showAllPinsHint": "ซูมออกให้เห็นทุกหมุดบนแผนที่นี้",
  "ops.pickStopLocation": "เลือกตำแหน่งจุดจอด",
  "ops.pickViaLocation": "เลือกตำแหน่งจุดผ่าน",
  "ops.searchPlaceOrAddress": "ค้นหาสถานที่หรือที่อยู่...",
  "ops.unfilled": "ยังไม่เต็ม",
  "ops.optimize": "จัดเส้นทาง",
  "ops.pendingDatesTitle": "{n} วันยังไม่เผยแพร่",
  "ops.lockedByPrefix": "ล็อกโดย",
  "ops.demandMet": "ตอบสนองความต้องการ",
  "ops.cells": "ช่อง",
  "ops.optimizeNeedsSetup": "เพิ่มเส้นทาง เลือกวันที่ และเพิ่มรถเข้ากลุ่มก่อน",
  "ops.generateAiPlan": "สร้างแผนจัดรถด้วย AI",
  "ops.addRouteColumnFirst": "เพิ่มคอลัมน์เส้นทางก่อน",
  "ops.addDateFirst": "เพิ่มวันที่ก่อน",
  "ops.createTicketsFromBoard": "สร้างใบงานจากบอร์ดนี้",
  "ops.undoStillLive": "ยังใช้งานอยู่ (คนขับรับงานแล้ว):",
  "ops.undoGone": "หายถาวร (ถูกลบโดยการเผยแพร่ครั้งนั้น):",
  "common.applying": "กำลังใช้…",
  "ops.applyPlan": "ใช้แผนนี้",
  "ops.inProgressLower": "กำลังดำเนินการ",
  "ops.blockLockedByStatusTitle": "ช่องที่คุณแก้เป็นของงานที่คนขับรับแล้ว",
  "ops.blockNoCellsTitle": "บอร์ดนี้ไม่มีช่องให้เผยแพร่",
  "ops.blockStaleTitle": "บอร์ดบนจอไม่ใช่เวอร์ชันล่าสุด",
  "ops.blockAllPublishedTitle": "ทุกอย่างเผยแพร่ไปแล้ว",
  "ops.creatingJobs": "กำลังสร้างใบงาน {n} ใบ…",
  "ops.nothingOn": "ไม่มีอะไรบน",
  "ops.needsPublishing": "ที่ต้องเผยแพร่",
  "ops.theBoard": "บอร์ดนี้",
  "ops.outcomeCreated": "สร้าง",
  "ops.outcomeReplaced": "แทนที่",
  "ops.outcomeRemoved": "ลบออก",
  "ops.conflicts": "การชนกัน",
  "ops.vehiclesUsedIdle": "รถที่ใช้ / ว่าง",
  "ops.coverageDateRoute": "ความครอบคลุม · วัน × เส้นทาง",
  "ops.noRoutesOrDates": "ยังไม่มีเส้นทางหรือวันที่",
  "ops.demandVsAssigned": "ต้องการเทียบมอบหมาย · รายวัน",
  "ops.fillRateByRoute": "อัตราเติมเต็ม · รายเส้นทาง",
  "ops.vehicleUtilization": "การใช้งานรถ",
  "ops.noTimeConflicts": "ไม่มีเวลาชนกัน",
  "ops.allTripsStaffed": "ทุกเที่ยวมีคนขับครบ",
  "ops.allDays": "ทุกวัน",
  "ops.noVehiclesAssigned": "ยังไม่ได้มอบหมายรถ",
  "ops.qr": "QR",
  "ops.searchByNameIdEmail": "ค้นหาจากชื่อ รหัส อีเมล…",
  "ops.passengerEmployee": "ผู้โดยสาร (พนักงาน)",
  "common.method": "วิธี",
  "roles.admin": "ผู้ดูแล",
  "ops.loadingPassenger": "กำลังโหลดข้อมูลผู้โดยสาร…",
  "ops.passengerNotFound": "ไม่พบผู้โดยสาร",
  "common.account": "บัญชี",
  "ops.removePassengerQ": "นำผู้โดยสารออก?",
  "hr.invitedAt": "วันที่เชิญ",
  "hr.joinedAt": "วันที่เข้าร่วม",
  "hr.removeFromOrg": "นำออกจากองค์กร",
  "status.upcoming": "ที่จะถึง",
  "ops.searchBookingId": "ค้นหารหัสการจอง…",
  "hr.profileName": "ชื่อในโปรไฟล์",
  "hr.accountEmail": "อีเมลบัญชี",
  "hr.personalPhone": "เบอร์ส่วนตัว",
  "hr.lastLogin": "เข้าใช้ล่าสุด",
  "hr.registered": "วันที่สมัคร",
  "hr.loginAccount": "บัญชีเข้าใช้งาน",
  "hr.setTempPassword": "ตั้งรหัสผ่านชั่วคราว",
  "hr.tempPassword": "รหัสผ่านชั่วคราว",
  "common.removeAttribute": "ลบคุณสมบัติ",
  "portal.member": "สมาชิก",
  "portal.loading": "กำลังโหลดพอร์ทัล…",
  "portal.notFound": "ไม่พบพอร์ทัล",
  "portal.searchIdCustomer": "ค้นหารหัสหรือลูกค้า…",
  "portal.unlinkContract": "ยกเลิกการผูกสัญญา",
  "portal.addContract": "เพิ่มสัญญา",
  "portal.searchContracts": "ค้นหาสัญญาจากรหัสหรือลูกค้า…",
  "portal.searchNameId": "ค้นหาชื่อหรือรหัส…",
  "portal.addMember": "เพิ่มสมาชิก",
  "portal.removeMemberConfirm": "นำ {name} ออกจากพอร์ทัลนี้? เขาจะเข้าพอร์ทัลนี้ไม่ได้อีก แต่ยังเป็นผู้โดยสารขององค์กรอยู่",
  /* ADR 0310 — เพิ่มสมาชิกโดยสร้างผู้โดยสารใหม่ได้ในกล่องเดียว */
  "portal.addHow": "วิธีเพิ่ม",
  "portal.addModeNew": "สร้างใหม่",
  "portal.addModePick": "เลือกที่มีอยู่",
  "portal.initialPassword": "รหัสผ่านเริ่มต้น",
  "portal.createAndAdd": "สร้างและเพิ่ม",
  "portal.dupUseExisting": "ใช้คนนี้",
  "portal.addHalfDone": "สร้าง {id} ไว้แล้ว แต่เพิ่มเข้าพอร์ทัลไม่สำเร็จ — กด “{add}” อีกครั้งได้เลย",
  "portal.searchPassengers": "ค้นหาผู้โดยสารที่ใช้งานอยู่…",
  "portal.deletePortal": "ลบพอร์ทัลนี้",
  "portal.editPortal": "แก้ไขพอร์ทัล",
  "portal.accessUrl": "ลิงก์เข้าใช้",
  "portal.slugLabel": "ชื่อในลิงก์",
  "portal.openLink": "เปิด",
  "portal.openPortalNewTab": "เปิดพอร์ทัลในแท็บใหม่",
  "portal.slugHint": "ไม่ซ้ำกับพอร์ทัลอื่นในองค์กร · ใช้ตัวพิมพ์เล็ก ตัวเลข และขีดกลางเท่านั้น",
  "portal.scope": "สัญญาและเส้นทาง",
  "common.print": "พิมพ์",
  "portal.passengerQr": "QR สำหรับผู้โดยสาร",
  "portal.qrMint": "สร้าง QR",
  "portal.qrStatusScheduled": "ยังไม่เริ่มใช้",
  "portal.qrWindowRangeBoth": "ใช้ได้ {from} 00:00 น. ถึง {until} 23:59 น.",
  "portal.qrWindowRangeFrom": "ใช้ได้ตั้งแต่ {from} 00:00 น. เป็นต้นไป ไม่มีวันหมดอายุ",
  "portal.qrWindowRangeUntil": "ใช้ได้ทันที ถึง {until} 23:59 น.",
  "portal.qrWindowHint": "เว้นว่าง = เริ่มใช้ได้ทันที และไม่มีวันหมดอายุ",
  "portal.qrWindow": "ช่วงเวลาใช้งาน",
  "portal.qrNoExpiry": "ไม่มีวันหมดอายุ",
  "portal.qrWindowFrom": "ตั้งแต่ {from}",
  "portal.qrWindowUntil": "ถึง {until}",
  "portal.qrWindowBoth": "{from} – {until}",
  "portal.qrValidFrom": "เริ่มใช้งาน",
  "portal.qrValidUntil": "หมดอายุ",
  "portal.qrExpiryInPast": "วันหมดอายุต้องไม่เป็นวันที่ผ่านมาแล้ว",
  "portal.qrWindowOrderError": "วันเริ่มใช้งานต้องไม่อยู่หลังวันหมดอายุ",
  "portal.qrRevealFailed": "เปิดดู QR ใบนี้ไม่ได้ — อาจเป็นใบที่สร้างก่อนระบบเก็บภาพไว้",
  "portal.qrDeadWarning": "QR ใบนี้ใช้งานไม่ได้แล้ว เปิดดูเพื่อตรวจสอบได้ แต่อย่าพิมพ์ไปแปะ",
  "portal.qrScheduledNote": "QR ใบนี้ยังไม่ถึงวันเริ่มใช้ — พิมพ์ไปแปะล่วงหน้าได้ แต่สแกนได้ตั้งแต่วันที่กำหนด",
  "portal.qrShortCodeGone": "รหัสสำรองของใบนี้กู้กลับไม่ได้ — ถ้าต้องใช้ ให้สร้างใบใหม่",
  "portal.qrView": "ดู QR",
  "portal.qrCannotReopen": "เปิดดูอีกไม่ได้",
  "portal.qrCannotReopenNote": "ระบบเก็บแต่ลายนิ้วมือของรหัส วาด QR ใบเดิมซ้ำไม่ได้ — ถ้าหาย ให้สร้างใบใหม่แล้วเพิกถอนใบนี้",
  "portal.qrSearchName": "ค้นหาชื่อ QR…",
  "portal.qrNoneMatch": "ไม่พบ QR ที่ตรงกับที่ค้นหรือกรองไว้",
  "portal.qrNoun": "ใบ",
  "portal.qrNone": "ยังไม่มี QR สำหรับพอร์ทัลนี้",
  "portal.qrLabel": "ชื่อ QR",
  "portal.qrScans": "สแกน",
  "portal.qrExpires": "หมดอายุ",
  "portal.qrCreated": "สร้างเมื่อ",
  "portal.qrUnlabelled": "(ไม่ได้ตั้งชื่อ)",
  "portal.qrRevoke": "เพิกถอน",
  "portal.qrRevokedByUnknown": "ไม่ทราบผู้ดำเนินการ",
  "portal.qrRevokeTitle": "เพิกถอน QR",
  "portal.qrRevokeConfirm": "เพิกถอน “{label}” ใช่ไหม? เครื่องที่สแกนไปแล้วจะใช้ต่อไม่ได้ตั้งแต่คำขอถัดไป และย้อนกลับไม่ได้",
  "portal.qrRevoked": "เพิกถอน QR แล้ว",
  "portal.qrRevokeFailed": "เพิกถอน QR ไม่สำเร็จ",
  "portal.qrMintFailed": "สร้าง QR ไม่สำเร็จ",
  "portal.qrStatusLive": "ใช้งานได้",
  "portal.qrStatusRevoked": "ถูกเพิกถอน",
  "portal.qrStatusExpired": "หมดอายุ",
  "portal.qrMintedTitle": "QR ใหม่พร้อมใช้งาน",
  "portal.qrShownOnce": "แสดงครั้งเดียวเท่านั้น — ระบบเก็บแต่ลายนิ้วมือของรหัส อ่านย้อนหลังไม่ได้ ปิดหน้าต่างนี้แล้วต้องสร้างใบใหม่",
  "portal.qrShortCodeNote": "รหัสสำรอง พิมพ์ไว้ใต้ QR ให้คนที่สแกนไม่ได้",
  "portal.qrDownloadSvg": "ดาวน์โหลด SVG",
  "portal.qrDownloadPng": "ดาวน์โหลดภาพ",
  "portal.qrDownloadFailed": "สร้างไฟล์ภาพไม่สำเร็จ",
  "portal.qrDownloaded": "ดาวน์โหลด QR แล้ว",
  "portal.qrPrintBlocked": "เบราว์เซอร์บล็อกหน้าต่างพิมพ์ — อนุญาต pop-up แล้วลองใหม่",
  "portal.qrSaved": "บันทึกไว้แล้ว",
  "ops.loadingOverflow": "กำลังโหลดข้อมูลผู้โดยสารตกค้าง…",
  "ops.overflowCommandCenter": "ศูนย์จัดการผู้โดยสารตกค้าง",
  "ops.totalOverflow": "ตกค้างทั้งหมด",
  "ops.tripsAffected": "เที่ยวที่กระทบ",
  "ops.stopNotFound": "ไม่พบจุดจอด",
  "ops.vehicleNotFound": "ไม่พบรถ",
  "common.zoomIn": "ขยาย",
  "common.zoomOut": "ย่อ",
  "common.rotate": "หมุน",
  "ops.toggleMapLayers": "สลับชั้นแผนที่",
  "common.legend": "คำอธิบายสัญลักษณ์",
  "ops.overflowStop": "จุดจอดที่ตกค้าง",
  "ops.availableVehicle": "รถที่ว่าง",
  "ops.canDetour": "อ้อมรับได้",
  "ops.currentRoute": "เส้นทางปัจจุบัน",
  "ops.proposedRoute": "เส้นทางที่เสนอ",
  "ops.aiSuggestions": "ข้อเสนอจาก AI",
  "ops.selectOverflowTrip": "เลือกเที่ยวที่ตกค้างเพื่อดูข้อเสนอ",
  "common.best": "ดีที่สุด",
  "ops.nearbyVehicles": "รถที่อยู่ใกล้",
  "ops.recurringPatterns": "รูปแบบที่เกิดซ้ำและการวางแผนความจุ",
  "ops.overflowFrequency": "ความถี่การตกค้าง (30 วัน)",
  "ops.capacityRecommendations": "ข้อแนะนำการวางแผนความจุ",
  "ops.aiInsight": "ข้อสังเกตจาก AI",
  "common.confirmAction": "ยืนยันการดำเนินการ",
  "ops.strategy": "กลยุทธ์",
  "ops.passengersAbsorbed": "ผู้โดยสารที่รับได้",
  "ops.additionalTime": "เวลาที่เพิ่ม",
  "ops.etaImpact": "ผลต่อเวลาถึงของผู้โดยสารเดิม",
  "common.cost": "ค่าใช้จ่าย",
  "ops.confirmAndDispatch": "ยืนยันและสั่งงาน",
  "ops.autoResolveConfig": "ตั้งค่าการแก้ไขอัตโนมัติ",
  "ops.enableAutoResolve": "เปิดการแก้ไขอัตโนมัติ",
  "ops.autoResolveHint": "ระบบจะจัดการผู้โดยสารตกค้างให้เอง",
  "ops.maxAutoDispatch": "จำนวนรถที่สั่งอัตโนมัติได้สูงสุด/วัน",
  "ops.maxDetourMin": "ระยะอ้อมสูงสุด (นาที)",
  "ops.strategyPreference": "กลยุทธ์ที่ต้องการ",
  "ops.notifyAdmin": "แจ้งผู้ดูแล",
  "ops.notifyPassengers": "แจ้งผู้โดยสาร",
  "ops.saveConfiguration": "บันทึกการตั้งค่า",
  "ops.statusChangedTo": "เปลี่ยนสถานะเป็น",
  "ops.changeStatusFrom": "เปลี่ยนสถานะจาก",
  "ops.changeStatusTo": "เป็น",
  "ops.loadingRoute": "กำลังโหลดเส้นทาง…",
  "ops.routeNotFound": "ไม่พบเส้นทาง",
  "ops.manageStops": "จัดการจุดจอด",
  "ops.noStopsInTemplate": "แม่แบบเส้นทางนี้ยังไม่มีจุดจอด",
  "ops.latShort": "ละติจูด",
  "ops.lngShort": "ลองจิจูด",
  "ops.copyCoordinates": "คัดลอกพิกัด",
  "ops.limitShort": "จำกัด",
  "ops.speedKmhVal": "{n} กม./ชม.",
  "ops.radiusMVal": "{n} ม.",
  "ops.dwellMinVal": "{n} นาที",
  "ops.radiusShort": "รัศมี",
  "ops.dwellShort": "จอด",
  "ops.dwellMin": "จอด {n} นาที", // ADR 0476 D1
  "ops.copyCoords": "คัดลอกพิกัด", // ADR 0476 D2
  "ops.coordsCopied": "คัดลอกพิกัดแล้ว",
  "common.copyFailed": "คัดลอกไม่สำเร็จ",
  "ops.addViaAfterStop": "เพิ่มจุดผ่านหลังจุดจอดนี้",
  "ops.editStop": "แก้ไขจุดจอด",
  "ops.routeAssignments": "การมอบหมายเส้นทาง",
  "ops.estOperatingCost": "ต้นทุนดำเนินการโดยประมาณ",
  "ops.selectRoutePlaceholder": "เลือกเส้นทาง…",
  "ops.pairedRouteFor": "เส้นทางคู่ ({type})",
  "ops.pairedRouteHint": "ตั้งประเภทตรงข้ามให้อัตโนมัติ · ไม่ใส่ก็ได้ถ้ามีขาเดียว",
  "ops.unpairOnSave": "บันทึกแล้วจะเลิกจับคู่กับ {code} — ประเภทงานของอีกเส้นไม่เปลี่ยน",
  "ops.searchDriver": "ค้นหาคนขับ…",
  "ops.searchVehicle": "ค้นหารถ…",
  "ops.editRouteTemplate": "แก้ไขเส้นทาง",
  "ops.routeCodeExample": "A1-IN",
  "ops.routeNameExample": "กรุงเทพฯ → ลาดกระบัง",
  "common.distance": "ระยะทาง",
  "common.duration": "ระยะเวลา",
  "common.editing": "กำลังแก้ไข",
  "common.new": "ใหม่",
  "common.modified": "แก้ไขแล้ว",
  "common.amounts": "จำนวนเงิน",
  // ADR 0331 D2 — ป้ายในกล่องตัดสินใจจ่ายเงิน (หน้า finance/driver-pay)
  "hr.payAmountThb": "จำนวนเงินที่จ่าย (บาท)",
  "hr.payReason": "เหตุผล",
  "hr.payReasonHint": "แสดงในรายงานเสมอ — ตรวจสอบย้อนหลังได้",
  "hr.payToDriver": "จ่ายให้คนขับ",
  "hr.deductFromDriver": "หักจากคนขับ",
  "hr.baseRatePerTrip": "ค่าตอบแทนพื้นฐาน (ต่อเที่ยว)",
  "hr.employee": "พนักงาน",
  // ADR 0266 — คำศัพท์ภายใน/ภายนอก ให้ตรงกับค่าตอบแทนของเส้นทางและใบงาน
  "hr.companyDriver": "คนขับภายใน",
  "hr.contractor": "คนขับภายนอก",
  "hr.officeStaff": "พนักงานสำนักงาน",
  "hr.mechanic": "ช่าง",
  "hr.baseSalary": "เงินเดือนพื้นฐาน",
  "hr.socialSecurity": "ประกันสังคม",
  "hr.payType": "รูปแบบการจ่าย",
  "hr.hasFile": "มีไฟล์แนบ",
  "fleet.completedAt": "ปิดงานเมื่อ",
  "fleet.costParts": "ค่าอะไหล่",
  "fleet.filterByVehicle": "ทุกคัน",
  "common.km": "กม.",
  "fleet.deletePolicyFailed": "ลบกรมธรรม์ไม่สำเร็จ",
  "toast.policyDeleted": "ลบกรมธรรม์แล้ว",
  "toast.policySaved": "บันทึกกรมธรรม์แล้ว",
  "fleet.uninsuredVehicles": "รถที่ยังไม่มีประกัน",
  "fleet.noCmiVehicles": "รถที่ยังไม่มี พ.ร.บ.",
  "fleet.expiringSoon": "ใกล้หมดอายุ",
  "fleet.renewPolicy": "ต่ออายุ (ออกงวดใหม่)",
  "fleet.deleteContract": "ลบสัญญา GPS",
  "fleet.nContracts": "{n} สัญญา",
  "fleet.allVehiclesInsured": "รถที่ใช้งานอยู่มีความคุ้มครองครบทุกคัน",
  "fleet.allVehiclesHaveCmi": "รถที่ใช้งานอยู่มี พ.ร.บ. ครบทุกคัน",
  "fleet.untrackedVehicles": "รถที่ยังไม่มี GPS",
  "fleet.allVehiclesTracked": "รถที่ใช้งานอยู่มีสัญญา GPS ครบทุกคัน",
  "fleet.gpsContractsTitle": "สัญญา GPS",
  "toast.gpsContractDeleted": "ลบสัญญา GPS แล้ว",
  "fleet.deleteGpsFailed": "ลบสัญญา GPS ไม่สำเร็จ",
  "fleet.filterByShop": "ทุกอู่",
  "fleet.overdueByDays": "เลยมา {n} วัน",
  "fleet.dueToday": "ครบกำหนดวันนี้",
  "fleet.dueInDays": "อีก {n} วัน",
  "fleet.noWorkOrdersYet": "ยังไม่มีใบงานซ่อม — สร้างใบแรกได้เลย",
  "fleet.overdueOnThisPage": "หน้านี้เลยกำหนด {n} ใบ",
  "fleet.noPoliciesYet": "ยังไม่มีกรมธรรม์",
  "fleet.noGpsYet": "ยังไม่มีสัญญา GPS",
  "fleet.uninsuredCount": "ยังไม่มีประกัน ({n})",
  "fleet.noCmiCount": "ยังไม่มี พ.ร.บ. ({n})",
  "fleet.untrackedCount": "ยังไม่มี GPS ({n})",
  "fleet.filterByProvider": "ทุกผู้ให้บริการ",
  "fleet.expiringWindow": "หมดอายุภายใน",
  "fleet.daysN": "{n} วัน",
  "fleet.insCompulsory": "พ.ร.บ.",
  "fleet.insVoluntary": "ภาคสมัครใจ",
  "fleet.insCargo": "ประกันสินค้า",
  "fleet.billingQuarterly": "ราย 3 เดือน",
  "fleet.billingYearly": "รายปี",
  "common.model": "รุ่น",
  "fleet.costLabour": "ค่าแรง",
  "fleet.costTotal": "รวมค่าใช้จ่าย",
  "fleet.insuranceCoverage": "ความคุ้มครอง",
  "fleet.billingType": "รอบเรียกเก็บ",
  "hr.docCategory": "หมวด",
  "hr.docKind": "ชนิดเอกสาร",
  "hr.payTypeMonthly": "รายเดือน",
  "hr.payTypeDaily": "รายวัน",
  "hr.payTypePerTrip": "รายเที่ยว",
  "hr.salaryConfigured": "ตั้งกติกาแล้ว",
  "hr.salaryNotConfigured": "ยังไม่ตั้งกติกา",
  "hr.salaryNotSetShort": "ยังไม่ตั้ง",
  "hr.viewSalaryHistory": "ประวัติเงินเดือน",
  "hr.otMultiplier": "ตัวคูณ OT",
  "common.effectiveFrom": "มีผลตั้งแต่",
  "warroom.run": "วิ่ง",
  "warroom.queue": "คิว",
  "warroom.opMode": "โหมดปฏิบัติการ",
  "warroom.executive": "ผู้บริหาร",
  // ADR 0325 — the day picker.
  "warroom.dayLive": "Live",
  "warroom.dayLiveHint": "ข้อมูลสด รีเฟรชอัตโนมัติทุก 10 วินาที",
  "warroom.dayFrozenHint": "ดูย้อนหลัง — หยุดรีเฟรชอัตโนมัติ และแก้ไขไม่ได้",
  "warroom.dayHistory": "ย้อนหลัง",
  "warroom.dayHistoryLimit": "เก็บย้อนหลังได้ {days} วัน",
  "warroom.dayJobCount": "{n} งาน",
  "warroom.dayNoJobs": "ไม่มีงาน",
  "warroom.dayReadOnly": "ดูย้อนหลัง แก้ไขไม่ได้",
  "warroom.playback": "ย้อนดู",

  "common.to": "ถึง",

  // ── nav — the left menu and top bar, in menu order ─────────────────────
  "nav.group.overview": "ภาพรวม",
  "nav.group.operations": "งานเดินรถ",
  "nav.group.monitoring": "ติดตาม",
  "nav.group.fleet": "ยานพาหนะ",
  "nav.group.people": "บุคลากร",
  "nav.group.finance": "การเงิน",
  "nav.group.analytics": "วิเคราะห์",
  "nav.group.system": "ระบบ",
  "nav.group.apps": "Apps",
  "nav.group.admin": "ผู้ดูแลระบบ",

  "nav.dashboard": "แดชบอร์ด",
  "nav.customers": "ลูกค้า",
  "nav.contracts": "สัญญา",
  "nav.routes": "เส้นทาง",
  "nav.routeOptimizer": "จัดเส้นทาง",
  "nav.tickets": "ใบงาน",
  "nav.dispatch": "วางแผนงานจำนวนมาก",
  "nav.booking": "จองรถ",
  "nav.passengers": "ผู้โดยสาร",
  "nav.portals": "พอร์ทัลลูกค้า",
  "nav.overflow": "ผู้โดยสารตกค้าง",
  "nav.tracking": "War Room",
  "nav.comms": "การสื่อสาร",
  "nav.locusSync": "ดึงงานจาก Locus", // ADR 0490 D1 · D10
  "nav.fleet": "รถ",
  "nav.maintenance": "ซ่อมบำรุง",
  "nav.checklist": "ตรวจสภาพ",
  "nav.insurance": "ประกันภัย",
  "nav.gps": "GPS",
  "nav.fuel": "น้ำมันและพลังงาน",
  "nav.finance": "การเงิน",
  "nav.seatMap": "ผังที่นั่ง",
  "nav.fleetImport": "นำเข้ารถ",
  "nav.pm": "บำรุงรักษาตามแผน",
  "nav.hr": "พนักงาน",
  "nav.driverGroups": "ทีมคนขับ",
  "nav.recruitment": "รับสมัครงาน",
  "nav.leave": "การลา",
  "nav.salary": "กติกาเงินเดือน",
  "nav.provinceRates": "เรทรายจังหวัด",
  /* ADR 0359 — ทะเบียนสถานีบริการน้ำมันขององค์กร */
  "nav.fuelStations": "สถานีบริการน้ำมัน",
  "fuelStation.title": "สถานีบริการน้ำมัน",
  "fuelStation.subtitle": "{n} แห่ง",
  "fuelStation.add": "เพิ่มสถานี",
  "fuelStation.edit": "แก้ไขสถานี",
  "fuelStation.searchPlaceholder": "ค้นหาชื่อสถานี",
  "fuelStation.name": "ชื่อสถานี",
  "fuelStation.note": "รายละเอียด",
  "fuelStation.lat": "ละติจูด",
  "fuelStation.lng": "ลองจิจูด",
  "fuelStation.coordHint": "วางพิกัดจาก Google Maps ได้",
  "fuelStation.pickOnMap": "เลือกจากแผนที่",
  "fuelStation.emptyTitle": "ยังไม่มีสถานีบริการ",
  "fuelStation.emptyCta": "เพิ่มสถานีแรก",
  "fuelStation.overlapWarning": "ทับกับ {names}",
  "fuelStation.saved": "บันทึกแล้ว",
  "fuelStation.deleted": "ลบแล้ว",
  "fuelStation.openInMaps": "เปิดใน Google Maps",
  /* ADR 0360 — สถานีบริการประจำของพนักงาน */
  "hr.fuelStationsTab": "สถานีบริการน้ำมันประจำ",
  "hr.fuelStationsEmptyMeans": "ยังไม่ได้กำหนดสถานี",
  "hr.fuelStationsAdd": "เพิ่มสถานี",
  "hr.fuelStationsAddN": "เพิ่ม {n} แห่ง",
  "hr.fuelStationsAddHint": "เลือกได้หลายแห่ง แล้วกดยืนยันครั้งเดียว",
  "hr.fuelStationsSelected": "{n} แห่ง",
  "hr.fuelStationsAdded": "เพิ่มแล้ว",
  "hr.fuelStationsRemoveTitle": "นำสถานีออกจากรายการประจำ",
  "hr.fuelStationsRemoveBody": "นำ {name} ออกจากรายการประจำของคนนี้ · สถานียังอยู่ในทะเบียน",
  "hr.fuelStationsRemoveConfirm": "นำออก",
  "hr.fuelStationsRemoved": "ลบแล้ว",
  "hr.fuelStationsNoneRegistered": "ยังไม่มีสถานีในระบบ",
  "fuel.flagOffStation": "นอกสถานีประจำ",
  "fuel.stationMatched": "สถานีประจำ",
  "fuel.stationNoHome": "ยังไม่ได้ตั้งสถานีประจำ",
  "fuel.stationNoLocation": "ไม่มีพิกัดให้ตรวจ",
  "fuel.stationNearby": "ใกล้เคียงกับ {name}",
  "fuel.stationNotSet": "ไม่ระบุ",
  "fuelStation.deleteTitle": "ลบสถานีนี้",
  "fuelStation.deleteBody": "ลบ {name} ออกจากรายการ",
  "nav.driverPay": "ค่าตอบแทนคนขับ",
  "nav.documents": "เอกสาร",
  "nav.hrImport": "นำเข้าพนักงาน",
  "nav.payroll": "จ่ายเงินเดือน",
  "nav.safety": "ความปลอดภัยและเคส",
  "nav.reports": "รายงาน",
  "nav.copilot": "ผู้ช่วย AI",
  "nav.masterData": "ข้อมูลหลัก",
  "nav.audit": "บันทึกการใช้งาน",
  "nav.settings": "ตั้งค่า",
  "nav.members": "สมาชิก",
  "nav.organizations": "องค์กร",
  "nav.driverApp": "แอปคนขับ",
  "nav.passengerApp": "แอปผู้โดยสาร",
  "nav.portalView": "มุมมองพอร์ทัล",
  "nav.driverAppDesc": "WebView สำหรับคนขับ",
  "nav.passengerAppDesc": "WebView สำหรับผู้โดยสาร",
  "nav.portalViewDesc": "พอร์ทัลลูกค้า / โรงงาน",

  "nav.collapseSidebar": "หุบเมนู",
  "nav.expandSidebar": "กางเมนู",
  "nav.openMenu": "เปิดเมนู",
  "nav.switchOrg": "สลับองค์กร",
  "nav.createOrg": "สร้างองค์กร",
  "nav.logout": "ออกจากระบบ",
  // ADR 0266 — ค่าตอบแทนแยกตามประเภทคนขับ (ภายใน = พนักงานบริษัท, ภายนอก = รถร่วม)
  "ops.driverRateInternal": "ค่าตอบแทนคนขับภายในต่องาน (บาท)",
  "ops.driverRateExternal": "ค่าตอบแทนคนขับภายนอกต่องาน (บาท)",
  "ops.driverRateExternalSeeded": "ค่านี้ถูกตั้งให้เท่ากับค่าภายในโดยอัตโนมัติ — ยังไม่มีใครตรวจสอบ",
  "ops.payCycleSection": "รอบตัดจ่าย",
  "ops.payCycleCutoff": "วันตัดรอบ",
  "ops.payCyclePayday": "วันจ่าย",
  "ops.payCycleOffset": "จ่ายในเดือน",
  "ops.payCycleNotSet": "ไม่ตั้งรอบ",
  "ops.payCycleMonthEnd": "สิ้นเดือน",
  "ops.payCycleDay": "วันที่ {day}",
  "ops.payCycleOffset0": "เดือนเดียวกับวันตัด",
  "ops.payCycleOffset1": "เดือนถัดไป",
  "ops.payCycleOffset2": "อีก 2 เดือน",
  "ops.payCycleNext": "3 รอบถัดไป",
  "ops.payCycleWindow": "{start} – {end}",
  "ops.payCyclePaid": "จ่าย {payout}",
  "ops.payCycleHint": "ค่าเที่ยวและรายการเงินของคนที่วิ่งสัญญานี้ จะแบ่งเป็นบิลตามรอบนี้",
  "ops.payCycleUnsetHint": "ยังไม่ตั้งรอบ — รายการเงินของสัญญานี้จะไปรอใน “รายการสะสม” ของแต่ละคน จนกว่าจะมีคนหยิบไปจ่าย",
  "ops.payCyclePaydayBeforeCutoff": "ถ้าจ่ายในเดือนเดียวกัน วันจ่ายต้องไม่มาก่อนวันตัดรอบ",
  "ops.payCycleSummary": "ตัดรอบ{cutoff} · จ่าย{payday} {offset}",
  "ops.routeSection": "เส้นทาง",
  "ops.routeCodePrefix": "อักษรนำหน้ารหัสเส้นทาง",
  "ops.routeCodePrefixHint": "ใช้ตั้งต้นรหัสเส้นทางตอนสร้างเส้นทางใหม่ของสัญญานี้ เช่น CCA จะได้ CCA001, CCA002 · ใช้ได้เฉพาะ A-Z, 0-9 และ - ยาวไม่เกิน 10 ตัว",
  "ops.routeCodePrefixInvalid": "ใช้ได้เฉพาะ A-Z, 0-9 และ - ยาวไม่เกิน 10 ตัว",
  "ops.routeCodeFromPrefix": "เติมจาก prefix ของสัญญา",
  "ops.editContract": "แก้ไขสัญญา",
  "ops.createContract": "สร้างสัญญา",
  "ops.contractCreated": "สร้างสัญญาแล้ว",
  "ops.contractUpdated": "บันทึกสัญญาแล้ว",
  "ops.contractSaveFailed": "บันทึกสัญญาไม่สำเร็จ",
  "files.uploadFailed": "อัปโหลดไม่สำเร็จ",
  "files.savedButSomeFailed": "บันทึกแล้ว แต่มีไฟล์บางไฟล์อัปโหลดไม่สำเร็จ",
  "files.unsupportedType": "ไฟล์ชนิดนี้ใช้ไม่ได้: {name}",
  "files.tooLarge": "ไฟล์ใหญ่เกินไป (ไม่เกิน {mb}MB): {name}",
  "common.documentsCount": "เอกสาร ({n})",
  "files.fileName": "ชื่อไฟล์",
  "files.uploadedAt": "อัปโหลดเมื่อ",
  "common.maxChars": "ยาวไม่เกิน {n} ตัวอักษร",
  "common.creating": "กำลังสร้าง…",
  "ops.routeCodeInUse": "รหัส \"{code}\" ถูกใช้ไปแล้ว",
  "ops.routeCreated": "สร้างเส้นทางแล้ว",
  "ops.loadRoutesFailed": "โหลดเส้นทางไม่สำเร็จ: {error}",
  "ops.pairedLegOnDetail": "จับคู่กับขาตรงข้ามได้ที่หน้ารายละเอียด (ไม่บังคับ)",
  "ops.noRoutesMatch": "ไม่มีเส้นทางที่ตรงกับตัวกรอง",
  // ADR 0256 — ตัวกรองช่วงระยะทางในตารางเส้นทาง
  "ops.distanceRange": "ระยะทาง (กม.)",
  "ops.distanceFrom": "ตั้งแต่",
  "ops.distanceTo": "ถึง",
  "ops.distanceMinGtMax": "ค่าเริ่มต้นต้องไม่มากกว่าค่าสิ้นสุด",
  "ops.distanceExcludesUnset": "ไม่รวมเส้นทางที่ยังไม่มีระยะทาง",
  "ops.noRouteTemplates": "ยังไม่มีเส้นทาง กด \"สร้างเส้นทาง\" เพื่อเริ่มต้น",
  "ops.createRouteTemplate": "สร้างเส้นทาง",
  "ops.endFromRoute": "คำนวณจากเส้นทาง — ขับ {drive} น. + จอด {service} น.",
  "ops.endRecalculated": "คำนวณจากเส้นทางได้ {time}",
  "ops.useCalculatedEnd": "ใช้ค่านี้",
  "ops.routeHasNoEstimate": "เส้นทางนี้ยังไม่มีเวลาโดยประมาณ กรอกเวลาจบเอง",
  "ops.useRouteEnd": "ใช้ {time}",
  "ops.overnightTitle": "ข้ามคืน (+1 วัน)",
  "ops.overnightDetail": "— จบ {date} · ≈ {dur}",
  "ops.nextDay": "วันถัดไป",
  "ops.durationHm": "{h} ชม. {m} น.",
  // ADR 0268 — list filters
  "common.tomorrow": "พรุ่งนี้",
  "common.thisMonth": "เดือนนี้",
  "common.clearFiltersCount": "ล้างตัวกรอง ({n})",
  "common.pageN": "หน้า {n}",
  "ops.customerContractActive": "มีสัญญาใช้งาน",
  "ops.customerContractInactive": "ไม่มีสัญญาใช้งาน",
  "ops.customerContractExpiring": "ใกล้หมดอายุ",
  "ops.allJobTypes": "ทุกประเภท",
  "ops.noRoutesYet": "ยังไม่มีเส้นทาง — กดสร้างเส้นทางใหม่เพื่อเริ่มต้น",
  "ops.dateRangeExcludesHint": "ไม่รวมใบงานที่ยังไม่ได้กำหนดวัน",
  "ops.datePickerTitle": "เลือกช่วงวันที่",
  // ADR 0270 — tier-2 filter popover / ADR 0271 — sorting
  "common.filtersCount": "ตัวกรอง ({n})",
  "common.sortByCol": "เรียงตาม {col}",
  "ops.customerFactory": "บริษัท/โรงงาน",
  "ops.allVehicleTypes": "ทุกชนิดรถ",
  "ops.completedVia": "จบโดย",
  "ops.completedViaDriver": "คนขับ",
  "ops.completedViaAdmin": "แอดมิน",
  "ops.cancelledViaLocus": "ยกเลิกใน Locus", // ADR 0494 — cancel_source locus
  "ops.jobTypeOldTicketsHint": "ไม่รวมใบงานเก่าที่ยังไม่มีประเภทงาน",
  "ops.completedViaHint": "นับเฉพาะงานที่จบหลังเปิดใช้",
  // ADR 0272 — CSV export flow
  "common.exportConfirmTitle": "ส่งออก {n} แถว?",
  "common.exportConfirmBody": "ตามตัวกรองปัจจุบัน ทุกหน้าไม่ใช่แค่ที่เห็น · ภาษา: {lang}",
  "common.exportTooManyTitle": "ข้อมูล {n} แถว เกินเพดาน {max} แถว",
  "common.exportTooManyBody": "กรุณากรองให้แคบลงแล้วลองใหม่",
  "common.exportEmpty": "ไม่มีข้อมูลตามตัวกรอง",
  "common.exportCounting": "กำลังนับ…",
  "common.exporting": "กำลังส่งออก {n} แถว…",
  "common.exported": "ส่งออกแล้ว {n} แถว",
  "common.exportFailed": "ส่งออกไม่สำเร็จ ลองใหม่อีกครั้ง",
  "common.exportForbidden": "ไม่มีสิทธิ์ส่งออกข้อมูลนี้",
  "common.exportCsv": "ส่งออก CSV",
  "common.languageThai": "ไทย",
  "common.languageEnglish": "อังกฤษ",
  // ADR 0273 — route / province / date-time filters
  "ops.dateTimeFilter": "วันที่/เวลา",
  "ops.dateTimeTitle": "วันที่และเวลา",
  "ops.dateFieldLabel": "กรองจาก",
  "ops.dateFieldService": "วันที่งาน",
  "ops.dateFieldAccept": "ยืนยัน",
  "ops.dateFieldStart": "เริ่มงาน",
  "ops.dateFieldEnd": "สิ้นสุด",
  "ops.dateFieldPlannedGroup": "ตามแผน",
  "ops.dateFieldActualGroup": "เวลาจริง",
  "ops.hourNeedsTimeField": "วันที่งานไม่มีเวลา",
  "ops.customerProvince": "จังหวัด (ลูกค้า)",
  "ops.customerProvinceHint": "จังหวัดที่ตั้งของลูกค้าตามสัญญา",
  "ops.filterGroupContractCustomer": "สัญญา / ลูกค้า",
  "ops.filterGroupDriverVehicle": "คนขับ / รถ",
  "ops.filterGroupType": "ประเภท",
  "common.keyboardDialogHint": "Esc ปิด · Enter ใช้",
  "common.summary": "สรุป",
  "common.selectRoute": "เลือกเส้นทาง",
  "common.selectCustomer": "เลือกลูกค้า",
  "common.allProvinces": "ทุกจังหวัด",
  "common.hourAbbr": "น.",
  // ADR 0269 — ทำสำเนาเส้นทาง
  "ops.duplicateRoute": "ทำสำเนาเส้นทาง",
  "ops.newRouteName": "ชื่อเส้นทางใหม่",
  "ops.dupCopiesStops": "จุดจอด {n} จุด พร้อมเส้นทางบนแผนที่",
  "ops.dupCopiesStopsVia": "จุดจอด {n} จุด + จุดบังคับเส้นทาง {via} จุด พร้อมเส้นทางบนแผนที่",
  "ops.dupCopiesMoney": "ค่าตอบแทน รายรับ และระยะทางตามแผน",
  "ops.dupCopiesCrew": "คนขับและรถประจำเส้นทาง",
  "ops.dupCopiesPresets": "ชุดเวลา {n} ชุด",
  "ops.dupFromVersion": "ใช้เส้นทางของเวอร์ชัน {n}",
  "ops.dupCopiesHeader": "คัดลอกไปด้วย",
  "ops.dupSkipsHeader": "ไม่คัดลอก",
  "ops.dupSkipDocs": "เอกสารแนบ{docs}",
  "ops.dupSkipPairing": "การจับคู่ขาไป/ขากลับ",
  "ops.dupStartsDraft": "สำเนาเริ่มที่สถานะร่าง",
  "ops.dupSkipsDocsCount": " ({n} ไฟล์)",
  "ops.routeDuplicated": "ทำสำเนาเส้นทางแล้ว",
  "ops.routeDuplicateFailed": "ทำสำเนาเส้นทางไม่สำเร็จ",
  // ADR 0272 — export column headers
  "ops.activeContractsCount": "สัญญาใช้งาน (จำนวน)",
  "ops.expiringContractsCount": "ใกล้หมดอายุ (จำนวน)",
  "ops.endedContractsCount": "จบแล้ว (จำนวน)",
  "ops.contractTitle": "ชื่อสัญญา",
  "ops.contractNo": "เลขสัญญา",
  "ops.remainDaysColumn": "เหลืออีก (วัน)",
  "ops.driverCompensationThb": "ค่าตอบแทนคนขับ (บาท)",
  "ops.compSource": "ที่มาค่าตอบแทน",
  "ops.compFromContract": "ตั้งบนสัญญา",
  "ops.compFromProvince": "จากเรทจังหวัดของลูกค้า",
  "ops.routeCount": "จำนวนเส้นทาง",
  "ops.openReminders": "บันทึกช่วยจำค้าง",
  "ops.durationMin": "ระยะเวลา (นาที)",
  "ops.stopCount": "จำนวนจุดจอด",
  "ops.firstStop": "จุดแรก",
  "ops.lastStop": "จุดสุดท้าย",
  "ops.pairedRoute": "เส้นทางคู่",
  "ops.revenuePerJobThb": "รายได้ต่อรอบ (บาท)",
  "ops.estimatedCostThb": "ต้นทุนประมาณ (บาท)",
  // ADR 0272 D3 — tickets export (34 columns)
  "ops.jobName": "ชื่องาน",
  "ops.driverName": "ชื่อพนักงานขับรถ",
  "ops.plannedStart": "เริ่มงาน (กำหนด)",
  "ops.plannedAccept": "ยืนยัน (กำหนด)",
  "ops.plannedEnd": "สิ้นสุด (ประมาณ)",
  "ops.actualAccept": "ยืนยัน (จริง)",
  "ops.actualStart": "เริ่ม (จริง)",
  "ops.actualEnd": "สิ้นสุด (จริง)",
  "ops.arrivedStopCount": "ถึงแล้ว (จุด)",
  "ops.driverPayTotalThb": "ค่าตอบแทนรวม (บาท)",
  "ops.createdViaAdmin": "สร้างเอง",
  "ops.createdViaPublish": "จากแผน",
  "ops.driverKind": "คนขับภายใน/ภายนอก",
  // ── ADR 0274 — แก้ไขใบงานจำนวนมาก (bulk edit) ──────────────────────────
  "bulk.openButton": "แก้ไขงานจำนวนมาก",
  "bulk.openButtonCount": "แก้ไขงานจำนวนมาก ({n})",
  "bulk.openDisabledTitle": "เลือกใบงานก่อน",
  "bulk.selectAllMatching": "เลือกทั้งหมด {n} ใบที่ตรงตัวกรอง",
  "bulk.filterChangedCleared": "ตัวกรองเปลี่ยน — ล้างการเลือกแล้ว",
  "bulk.rowCheckLabel": "เลือกใบงาน {id}",
  "bulk.titleCount": "แก้ไขงานจำนวนมาก · {n} ใบ",
  "bulk.step1": "กรอกค่า",
  "bulk.step2": "ตรวจสอบ",
  "bulk.step3": "ผลลัพธ์",
  "bulk.groupStatus": "สถานะ",
  "bulk.groupSchedule": "กำหนดการ",
  "bulk.groupAssignment": "การมอบหมาย",
  "bulk.groupPayNotes": "ค่าตอบแทนและหมายเหตุ",
  "bulk.groupJob": "งาน",
  "bulk.statusChangeTo": "เปลี่ยนสถานะเป็น",
  "bulk.statusHint1": "มอบหมายต้องมีคนขับ รถ และเวลาเริ่ม/สิ้นสุดครบ",
  "bulk.statusHint2": "เลือกยกเลิกแล้วฟิลด์อื่นจะปิด",
  "bulk.cancelReasonHint": "เหตุผลเดียวใช้กับทุกใบ",
  "bulk.enableField": "แก้ฟิลด์นี้",
  "bulk.serviceDate": "วันที่งาน",
  "bulk.times": "เวลายืนยัน / เริ่ม / สิ้นสุด",
  "bulk.timesHint": "ช่องที่เว้นว่างไม่แก้",
  "bulk.tripRate": "ค่าตอบแทนต่อเที่ยว (บาท)",
  "bulk.tripRateHint": "กำหนดเอง — เปลี่ยนคนขับภายหลังจะไม่คำนวณใหม่",
  "bulk.notesReplace": "แทนที่",
  "bulk.notesAppend": "ต่อท้าย",
  "bulk.check": "ตรวจสอบ",
  "bulk.checking": "กำลังตรวจสอบ…",
  "bulk.summary": "ผ่าน {ok} · เตือน {warn} · ทำไม่ได้ {error} · ข้าม {skipped}",
  "bulk.summaryNote": "ใบที่ทำไม่ได้/ข้าม จะไม่ถูกแก้",
  "bulk.pillAll": "ทั้งหมด",
  "bulk.pillOk": "ผ่าน",
  "bulk.pillWarn": "เตือน",
  "bulk.pillError": "ทำไม่ได้",
  "bulk.pillSkipped": "ข้าม",
  "bulk.colContinue": "ทำต่อ",
  "bulk.colId": "รหัส",
  "bulk.colResult": "ผล",
  "bulk.colMessage": "คำอธิบาย",
  "bulk.tagDone": "แก้แล้ว",
  "bulk.tagFailed": "ไม่สำเร็จ",
  "bulk.willApply": "จะแก้: {fields}",
  "bulk.back": "‹ กลับไปแก้",
  "bulk.confirm": "ยืนยันแก้ไข {n} ใบ",
  "bulk.confirming": "กำลังแก้ {n} ใบ…",
  "bulk.atomicNote": "ใบที่ผ่านทั้งหมดจะถูกแก้พร้อมกัน — ถ้าใบใดล้ม จะไม่แก้ใบใดเลย",
  "bulk.noneDoable": "ไม่มีใบงานที่ทำได้",
  "bulk.doneCount": "แก้แล้ว {n} ใบ",
  "bulk.rolledBack": "ไม่ได้แก้ใบใดเลย — หยุดที่ {id}: {message} · ย้อนกลับแล้ว",
  "bulk.retryWithout": "ตัดใบนี้ออกแล้วลองใหม่",
  "bulk.tooMany": "เลือกได้สูงสุด {max} ใบต่อครั้ง — แคบตัวกรองก่อน",
  "bulk.forbidden": "สิทธิ์ไม่พอ — ต้องเป็นผู้ดูแลองค์กร",
  "bulk.checkFailed": "ตรวจสอบไม่สำเร็จ",
  "bulk.applyFailed": "แก้ไขไม่สำเร็จ",
  "bulk.err.reasonRequired": "กรุณากรอกเหตุผลการยกเลิก",
  "bulk.err.tooLong": "ยาวเกิน {max} ตัวอักษร",
  "bulk.err.nothingSelected": "ยังไม่ได้เลือกฟิลด์ที่จะแก้",
  "bulk.err.dateRequired": "กรุณาเลือกวันที่งาน",
  "bulk.err.timeFormat": "รูปแบบเวลาต้องเป็น HH:mm",
  "bulk.err.timesEmpty": "ยังไม่ได้ใส่เวลาช่องใด",
  "bulk.err.driverRequired": "กรุณาเลือกคนขับ",
  "bulk.err.vehicleRequired": "กรุณาเลือกรถ",
  "bulk.err.tripRateInvalid": "ค่าตอบแทนต้องเป็นตัวเลข 0 ขึ้นไป",
  "bulk.err.notesRequired": "กรุณากรอกหมายเหตุ",
  "bulk.field.driver_id": "คนขับ",
  "bulk.field.vehicle_id": "รถ",
  "bulk.field.date": "วันที่งาน",
  "bulk.field.settings": "เวลา",
  "bulk.field.accept_deadline": "เวลายืนยัน",
  "bulk.field.start_time": "เวลาเริ่ม",
  "bulk.field.expected_end": "เวลาสิ้นสุด",
  "bulk.field.notes": "หมายเหตุ",
  "bulk.field.compensation": "ค่าตอบแทน",
  "bulk.field.status": "สถานะ",
  // ADR 0274 D4 / rev 2 D — row codes (server `message` is the fallback)
  "bulk.row.EDIT_WINDOW": "แก้คนขับ/รถ/วันที่ได้เฉพาะก่อนคนขับยืนยัน",
  "bulk.row.SETTINGS_WINDOW": "แก้เวลาได้เฉพาะก่อนคนขับยืนยัน",
  "bulk.row.PERIOD_LOCKED": "งวดค่าตอบแทนของเดือนนี้ปิดแล้ว — เปิดงวดก่อน",
  "bulk.row.NOT_AVAILABLE": "คนขับ/รถไม่ว่าง — ชนกับใบงานอื่น",
  "bulk.row.NOT_AVAILABLE_IN_BATCH": "คนขับ/รถชนกับใบในชุดเดียวกัน",
  "bulk.row.ASSIGN_INCOMPLETE": "มอบหมายไม่ได้ — ข้อมูลยังไม่ครบ",
  "bulk.row.CARGO_NOT_FINISHED": "สินค้ายังบันทึกไม่ครบ — จบงานนี้จากหน้างานเอง", // ADR 0465 D5 — left out of a bulk complete
  "bulk.row.TRANSITION": "เปลี่ยนจาก {from} เป็น {to} ไม่ได้",
  "bulk.row.TRANSITION_GENERIC": "เปลี่ยนสถานะแบบนี้ไม่ได้",
  "bulk.row.STALE": "สถานะเปลี่ยนไปแล้วระหว่างตรวจ — โหลดใหม่",
  "bulk.row.INTERNAL": "ระบบขัดข้อง — ยังไม่ได้แก้ใบนี้",
  "bulk.row.NOOP": "ไม่มีอะไรเปลี่ยน (ค่าเดิมอยู่แล้ว / สถานะเป็นอยู่แล้ว)",
  "bulk.row.NOT_FOUND": "ไม่พบใบงาน (ถูกลบหรือไม่ใช่ขององค์กรนี้)",
  "bulk.row.ABORTED": "ยังไม่ถึงคิว — ชุดหยุดก่อนถึงใบนี้",
  "bulk.row.ROLLED_BACK": "แก้แล้วแต่ย้อนกลับ — เพราะใบอื่นในชุดล้ม",
  "bulk.warn.W1_TERMINAL_CANCEL": "ใบงานวิ่งแล้ว/จบแล้ว จะถูกยกเลิก (ค่าใช้จ่ายที่อนุมัติแล้วไม่ถูกลบ)",
  "bulk.warn.W2_PAY_RESEED": "เปลี่ยนคนขับ — ค่าตอบแทนจะคำนวณใหม่ตามอัตราของคนขับใหม่",
  "bulk.warn.W3_END_MISSING": "ตั้งเวลาเริ่มแต่ไม่มีเวลาสิ้นสุด — มอบหมายไม่ได้จนกว่าจะใส่",
  "bulk.warn.W4_FROM_BOARD": "ใบงานมาจากบอร์ด — publish รอบถัดไปจะยกเลิก/ลบใบนี้และสร้างใหม่ตามบอร์ด ให้แก้ที่บอร์ดแทน",
  "bulk.warn.W5_NOTIFY": "จะส่งอีเมลสรุปการยกเลิก 1 ฉบับถึงผู้ดูแล",
  "bulk.warn.W6_PAY_AFTER_DONE": "ใบงานจบ/ยกเลิกแล้ว — ค่าตอบแทนที่คิดไว้แล้วจะเปลี่ยน ตรวจงวดค่าตอบแทนซ้ำ",
  "bulk.warn.W7_PAY_MANUAL": "ค่าตอบแทนจะถูกล็อกเป็นค่าที่กำหนดเอง (เปลี่ยนคนขับภายหลังไม่คำนวณใหม่)",
  "bulk.warn.W8_REVERT_DRAFT": "ถอดคนขับ/รถ → ใบงานกลับเป็นร่าง",
  "bulk.warn.W9_ZERO_WINDOW": "เวลาเริ่มเท่ากับเวลาสิ้นสุด — ช่วงเวลา 0 นาที",
  "bulk.contractOfSelection": "สัญญาของใบงานที่เลือก",
  "bulk.contractMixedHint": "ใบงานที่เลือกต้องอยู่ในสัญญาเดียวกัน — ใบที่สัญญาไม่ตรงจะถูกรายงานในขั้นตรวจสอบ",
  "bulk.err.gateRange": "ต้องเป็นจำนวนเต็ม 0–{max}",
  "bulk.err.jobTypeRequired": "กรุณาเลือกประเภทงาน",
  "bulk.err.routeRequired": "กรุณาเลือกเส้นทาง",
  "bulk.field.job_type": "ประเภทงาน",
  "bulk.field.time_gates": "เกณฑ์เวลา",
  "bulk.field.late_threshold_min": "เกณฑ์สาย",
  "bulk.field.accept_open_before_min": "เปิดให้ยืนยันงานล่วงหน้า",
  "bulk.field.cancel_cutoff_before_min": "ปิดรับยกเลิกก่อนเริ่มงาน",
  "bulk.field.province_rate": "เรทรายจังหวัด",
  "bulk.field.province_rate_id": "เรทรายจังหวัด",
  "bulk.field.route_template_id": "เส้นทาง",
  "bulk.row.PROVINCE_RATE_NOT_FOUND": "ไม่พบเรทรายจังหวัดที่เลือก",
  "bulk.tripRateMode": "วิธีแก้ค่าเที่ยว",
  "bulk.tripRateMode.set": "ตั้งเป็น",
  "bulk.tripRateMode.add": "เพิ่ม",
  "bulk.tripRateMode.sub": "ลด",
  "bulk.row.PAY_BELOW_ZERO": "ลดแล้วค่าเที่ยวติดลบ — ใบนี้ไม่ถูกแก้",
  "bulk.row.CONTRACT_MISMATCH": "เส้นทางนี้ไม่ใช่ของสัญญาเดียวกับใบงาน",
  // ADR 0330 E1 — สามรหัสที่ค้างมาตั้งแต่ 0285 (สองตัวแรก) และ L19 (ตัวสุดท้าย)
  "bulk.row.ROUTE_TEMPLATE_NOT_FOUND": "ไม่พบเส้นทางที่อ้างถึง — อาจถูกลบไปแล้ว",
  "bulk.row.ROUTE_NOT_USABLE": "เส้นทางยังไม่เปิดใช้ — ไปเปิดที่หน้าเส้นทางก่อน",
  "bulk.row.DRIVER_ON_LEAVE": "คนขับลาอนุมัติแล้วในวันนี้",
  "bulk.row.DRIVER_NOT_EMPLOYED": "คนขับออกงานแล้วหรือยังไม่เริ่มงาน ณ เวลาของใบงานนี้",
  "bulk.warn.W10_ROUTE_RESNAPSHOT": "จุดจอดที่แก้เฉพาะใบนี้จะถูกแทนด้วยของเส้นทางใหม่",
  "bulk.err.statusRequired": "กรุณาเลือกสถานะ",
  "bulk.overCap": "เลือกได้สูงสุด {max} ใบต่อครั้ง — ตอนนี้เลือก {n} ใบ แคบตัวกรองหรือลดจำนวนที่เลือกก่อน",
  "bulk.transitionReason.accepted": "คนขับรับงานแล้ว",
  "bulk.transitionReason.started": "งานเริ่มแล้ว",
  "bulk.transitionReason.done": "งานจบแล้ว",
  "bulk.detail.driverAndVehicle": "คนขับและรถ",
  "bulk.detail.clash": "{who} ชนกับ {id}",
  "bulk.detail.clashWith": "ชนกับ {id}",
  "bulk.detail.period": "งวด {period}",
  "bulk.detail.missing": "ขาด {fields}",
  "bulk.selectedShort": "เลือก {n}",
  "bulk.allShort": "ทั้งหมด {n}",
  "bulk.overCapShort": "สูงสุด {max} ใบ",
  "bulk.tagExcluded": "ตัดออกแล้ว",
  "bulk.row.EXCLUDED": "ตัดออกจากชุดนี้แล้ว — ใบที่ทำให้ชุดล้มในรอบก่อน",

  /* ── เอกสารประจำตัว: บัตรประชาชน + ใบขับขี่ ── */
  "hr.expiry.daysLeft": "เหลือ {n} วัน",
  "hr.expiry.today": "หมดอายุวันนี้",
  "hr.expiry.expiredDays": "หมดอายุแล้ว {n} วัน",
  "hr.expiry.expiredToday": "หมดอายุแล้ววันนี้",
  "hr.expiry.none": "ไม่มีข้อมูล",
  "hr.expiry.lifetime": "ตลอดชีพ",
  "hr.expiry.ok": "ปกติ",
  "hr.idCardColumn": "บัตรประชาชน",
  "hr.filterLicenseStatus": "ใบขับขี่",
  "hr.filterIdCardStatus": "บัตรประชาชน",
  "hr.filterAll": "ทั้งหมด",
  "hr.filterExpired": "หมดอายุแล้ว",
  "hr.filterDue30": "เหลือ ≤ 30 วัน",
  "hr.filterDue90": "เหลือ ≤ 90 วัน",
  "hr.filterNormal": "ปกติ",
  "hr.filterNoLicense": "ไม่มีใบขับขี่ในระบบ",
  "hr.filterNoIdCard": "ไม่มีบัตรในระบบ",
  "hr.filterLifetime": "บัตรตลอดชีพ",
  "hr.docsIncomplete": "เอกสารไม่ครบ",
  "hr.docsIncompleteHint": "ยังไม่มีภาพบัตรประชาชน หรือคนขับที่ยังไม่มีภาพใบขับขี่",
  "hr.kpiLicenseExpired": "ใบขับขี่หมดอายุ",
  "hr.kpiDocsIncomplete": "เอกสารไม่ครบ",
  "hr.watermarkFor": "ใช้สำหรับ {org} เท่านั้น",
  "hr.noImageYet": "ยังไม่มีภาพ",
  "hr.attachImage": "แนบภาพ",
  "hr.replaceImage": "เลือกไฟล์ใหม่เพื่อแทนที่",
  "hr.idCardImage": "ภาพบัตรประชาชน",
  "hr.licenseImage": "ภาพใบขับขี่",
  "hr.imageRequired": "ต้องแนบภาพ",
  "hr.imageOnlyHint": "ไฟล์ภาพเท่านั้น (JPG, PNG, WebP, HEIC) · สูงสุด {mb} MB",
  "hr.viewFullSize": "คลิกเพื่อขยาย",
  "hr.closeViewer": "ปิด",
  "hr.showIdNumber": "แสดงเลขบัตร",
  "hr.hideIdNumber": "ซ่อนเลขบัตร",
  "hr.licenseType": "ประเภทใบขับขี่",
  "hr.addLicenseTitle": "เพิ่มใบขับขี่",
  "hr.editLicenseTitle": "แก้ไขใบขับขี่",
  "hr.addIdCardTitle": "เพิ่มบัตรประชาชน",
  "hr.editIdCardTitle": "แก้ไขบัตรประชาชน",
  "hr.idNumberLabel": "เลขบัตรประชาชน (13 หลัก)",
  "hr.idNumberInvalid": "เลขบัตรประชาชนต้องเป็นตัวเลข 13 หลัก",
  "hr.expiryBeforeIssue": "วันหมดอายุต้องไม่ก่อนวันที่ออกบัตร",
  "hr.blankMeansLifetime": "เว้นว่าง = ตลอดชีพ",
  "hr.saving": "กำลังบันทึก…",
  "hr.fileTooLarge": "ไฟล์ใหญ่เกินไป (สูงสุด {mb} MB)",
  "hr.fileTypeUnsupported": "ชนิดไฟล์ไม่รองรับ — รับเฉพาะ {types}",
  "hr.licenseDeleteBody": "ใบขับขี่ \"{name}\" จะถูกลบถาวร",
  "hr.docsMissingBanner": "พนักงานคนนี้ยังมีเอกสารไม่ครบ",
  "hr.docsMissingIdCard": "ยังไม่มีภาพบัตรประชาชน",
  "hr.docsMissingLicense": "ยังไม่มีภาพใบขับขี่",
  "hr.goToTab": "ไปที่แท็บ",
  "hr.idCardRequiredHint": "บังคับทุกตำแหน่ง",
  "hr.licenseRequiredHint": "บังคับเฉพาะพนักงานที่เป็นคนขับ",
  "hr.deleteImage": "ลบภาพ",
  "hr.deleteImageQ": "ลบภาพเอกสารนี้?",
  "hr.deleteImageBody": "ภาพจะถูกลบถาวรและกู้คืนไม่ได้ · ข้อมูลอื่นของเอกสารใบนี้ยังอยู่ครบ",
  "hr.deleteImageIncomplete": "หลังลบแล้วพนักงานคนนี้จะเข้าเงื่อนไข \"เอกสารไม่ครบ\" จนกว่าจะแนบภาพใหม่",
  "toast.imageDeleted": "ลบภาพแล้ว",
  // ADR 0279 — version of a route
  "ops.versionShowing": "เส้นทางที่แสดง",
  "ops.versionN": "เวอร์ชัน {n}",
  "ops.versionOriginal": "เวอร์ชัน {n} (ต้นฉบับ)",
  "ops.versionDraft": "ร่าง",
  "ops.versionActive": "ใช้งาน",
  "ops.versionStopped": "หยุดใช้แล้ว",
  "ops.versionExpired": "หมดช่วงแล้ว",
  "ops.versionInactive": "ไม่ใช้งานแล้ว",
  "ops.versionArchived": "เก็บเข้ากรุ",
  "ops.versionBaseline": "ต้นฉบับ",
  "ops.versionEffectiveNow": "มีผลอยู่",
  "ops.versionNoWindow": "ยังไม่มีช่วงผลบังคับ",
  "ops.versionNoEnd": "ไม่มีวันสิ้นสุด",
  "ops.versionBaselineWindow": "ใช้เมื่อไม่มีเวอร์ชันใดมีผล",
  "ops.versionMenuHint": "ร่างอยู่บนสุด · ที่เหลือเรียงใหม่ → เก่า",
  "ops.versionCreate": "สร้างเวอร์ชันใหม่",
  "ops.versionDiscard": "ทิ้งร่าง",
  "ops.versionActivate": "เปิดใช้งานเวอร์ชันนี้",
  "ops.versionStop": "หยุดใช้เวอร์ชันนี้",
  "ops.versionActivateTitle": "เปิดใช้งานเวอร์ชัน {n}",
  "ops.versionStopTitle": "หยุดใช้เวอร์ชัน {n}",
  "ops.versionStopConfirm": "หยุดใช้เวอร์ชัน {n}",
  "ops.versionFrom": "มีผลตั้งแต่",
  "ops.versionTo": "มีผลถึง",
  "ops.versionStopAt": "มีผลถึง",
  "ops.versionNoEndHint": "เว้นว่าง = ใช้ไปเรื่อย ๆ จนกว่าจะสั่งหยุด หรือมีเวอร์ชันใหม่กว่ามาทับ",
  "ops.versionApplyWhat": "สิ่งที่จะแก้ในงานที่เลือก",
  "ops.versionApplyCeilingHint": "แต่ละใบจะโดนเท่าที่สถานะของมันอนุญาต — งานที่ดำเนินการไปแล้วแก้ได้เฉพาะเงิน",
  "ops.versionFlagPlan": "เส้นทาง / จุดจอด",
  "ops.versionFlagMoney": "เงิน",
  "ops.versionFlagUnavailable": "ไม่มีงานใบไหนในรายการนี้ที่เปลี่ยนส่วนนี้ได้",
  "ops.versionAffectedCount": "งานที่เริ่มในช่วงนี้ · {n} ใบ",
  "ops.versionSelectedCount": "เลือกไว้ {n}",
  "ops.versionSelectAll": "เลือกที่แก้ได้ทั้งหมด",
  "ops.versionNoAffected": "ไม่มีงานที่เริ่มในช่วงนี้",
  "ops.versionRowMoneyOnly": "แก้ได้เฉพาะเงิน",
  "ops.duplicateThisVersion": "สำเนาเวอร์ชันนี้",
  "ops.dupCopyFrom": "คัดลอกเนื้อจาก",
  "ops.dupFromVersionLine": "สำเนาจะได้ **จุดจอดและการมอบหมายของเวอร์ชัน {n}** — ไม่ใช่ของต้นฉบับ",
  "ops.dupFromBaselineLine": "สำเนาจะได้จุดจอดและการมอบหมายของต้นฉบับ",
  "ops.versionPickJobsTitle": "เลือกงานที่จะแก้",
  "ops.versionPickJobsStopTitle": "เลือกงานที่จะดึงกลับ",
  "ops.versionNeedStart": "ต้องเลือกวันที่เริ่มมีผลก่อน",
  "ops.versionEndBeforeStart": "วันสิ้นสุดต้องอยู่หลังวันเริ่ม — ไม่ได้ตั้งเวลาไว้ ระบบถือว่าเป็น 00:00 ของวันนั้น",
  "ops.versionCreateDraftFailed": "สร้างร่างเวอร์ชันไม่สำเร็จ",
  "ops.versionDiscardFailed": "ทิ้งร่างไม่สำเร็จ",
  "ops.versionScanFailed": "ดึงรายการงานไม่สำเร็จ",
  "ops.versionSaveFailed": "บันทึกไม่สำเร็จ",
  "ops.compFromDraft": "ร่างเวอร์ชัน {n} · ค่าตอบแทนที่แก้ตรงนี้ยังไม่มีผลจนกว่าจะเปิดใช้งาน",
  "ops.compFromBaseline": "ค่าตอบแทนของต้นฉบับ (เวอร์ชัน {n}) · ใช้กับวันที่ที่ไม่อยู่ในช่วงของเวอร์ชันไหน",
  "ops.compFromVersion": "ค่าตอบแทนของเวอร์ชัน {n} · เริ่มใช้ {from}",
  "ops.compDraftHint": "ตอนเปิดใช้งานเวอร์ชันนี้ จะให้ระบุวันที่เริ่มใช้ และเลือกได้ว่าจะแก้ใบงานที่ออกไปแล้วด้วยไหม",
  "ops.compLockedHint": "ค่าตอบแทนที่ตั้งไว้แล้วแก้ตรงนี้ไม่ได้ เพราะทุกครั้งที่เปลี่ยนต้องมีวันที่เริ่มใช้และเก็บประวัติไว้ ให้เปลี่ยนผ่านเวอร์ชันใหม่",
  "ops.compEffectiveNow": "ตอนนี้ระบบใช้ค่าของเวอร์ชัน {n}",
  "ops.compChangeViaVersion": "สร้างเวอร์ชันใหม่เพื่อเปลี่ยนค่าตอบแทน",
  "ops.compGoDraft": "ไปแก้ในร่างเวอร์ชัน {n}",
  "err.route.compViaVersion": "ค่าตอบแทนที่ตั้งไว้แล้วเปลี่ยนตรง ๆ ไม่ได้ ให้สร้างเวอร์ชันใหม่แล้วระบุวันที่เริ่มใช้",
  "err.route.compPairRequired": "ต้องกรอกค่าตอบแทนคนขับให้ครบทั้งภายในและภายนอกก่อน",
  /* ═══ ADR 0285 — เส้นทางที่ยังไม่เปิดใช้งาน ═══ */
  "err.route.notUsable": "เส้นทางนี้ยังไม่เปิดใช้งาน — เปิดใช้งานที่หน้าเส้นทางก่อนจึงจะสร้างงานได้",
  /* ═══ ADR 0286 — ยังสร้างเวอร์ชันไม่ได้ ═══ */
  "err.route.incompleteForVersion": "เส้นทางนี้ยังไม่ครบ — ต้องมีจุดจอดและค่าตอบแทนคนขับก่อนจึงจะสร้างเวอร์ชันได้",
  "ops.incompleteTitle": "ยังสร้างเวอร์ชันไม่ได้",
  "ops.incompleteIntroBaseline": "กรอกข้อมูลต้นฉบับให้ครบก่อนสร้างเวอร์ชัน",
  "ops.incompleteIntroSource": "เวอร์ชัน {n} ที่จะคัดลอกยังไม่ครบ",
  "ops.incompleteNeedStops": "จุดจอด",
  "ops.incompleteNeedCompInternal": "ค่าตอบแทนคนขับภายใน",
  "ops.incompleteNeedCompExternal": "ค่าตอบแทนคนขับภายนอก",
  "ops.incompleteGoFixStops": "ไปเพิ่มจุดจอด",
  "ops.incompleteGoFixComp": "ไปกรอกค่าตอบแทน",
  "ops.blockRouteNotUsableTitle": "{n} ช่องอ้างเส้นทางที่ยังไม่เปิดใช้งาน",
  "ops.blockRouteNotUsableNote": "เส้นทางที่เป็นร่างหรือเก็บถาวรแล้ว เอามาสร้างงานไม่ได้ · เปิดใช้งานเส้นทางที่หน้าเส้นทาง หรือเอาช่องนั้นออกจากบอร์ด",
  "err.version.locked": "เวอร์ชันนี้เปิดใช้งานแล้ว แก้ไม่ได้ — สร้างร่างเวอร์ชันถัดไปแทน",
  "err.version.draftExists": "เส้นทางนี้มีร่างเวอร์ชัน {n} อยู่แล้ว ให้แก้ร่างเดิมแทนการสร้างใหม่",
  "err.version.notDraft": "เปิดใช้งานได้เฉพาะร่าง — เวอร์ชันนี้ไม่ใช่ร่างแล้ว ลองโหลดหน้าใหม่",
  "err.version.notActive": "ทำได้เฉพาะเวอร์ชันที่เปิดใช้งานอยู่",
  "err.version.empty": "เวอร์ชันนี้ยังไม่มีจุดจอด — เพิ่มจุดจอดก่อนเปิดใช้งาน",
  "err.version.stillEffective": "เวอร์ชันนี้กำลังมีผลอยู่ — หยุดใช้ก่อนจึงจะเก็บเข้าคลังได้",
  "err.version.identityField": "ฟิลด์นี้เป็นของเส้นทาง ไม่ใช่ของเวอร์ชัน — แก้ที่ต้นฉบับ",
  "err.version.tooManyTickets": "แก้ได้ครั้งละไม่เกิน {n} ใบ — เอาออกบางส่วนหรือแบ่งทำหลายรอบ",
  "ops.versionFlagPlanHint": "เขียนจุดจอด ลำดับ และระยะทางของใบงานใหม่ทั้งชุด และล้างเวลาที่บันทึกว่าถึงจุดจอดแล้ว",
  "ops.versionFlagMoneyHint": "ค่าตอบแทนคนขับ (ภายใน/ภายนอก) รายได้ต่อเที่ยว และต้นทุนประเมิน",
  "ops.versionSearchPlaceholder": "ค้นหาเลขที่ใบงาน",
  "ops.versionFilterFull": "แก้ได้ทั้งใบ",
  "ops.versionFilterMoneyOnly": "แก้ได้เฉพาะเงิน",
  "ops.versionFilterBlocked": "แก้ไม่ได้",
  "ops.versionFilterAria": "กรองตามสิ่งที่แก้ได้",
  "ops.versionColJob": "เลขที่ใบงาน",
  "ops.versionColStart": "เริ่มงาน",
  "ops.versionColStatus": "สถานะ",
  "ops.versionColResult": "จะถูกแก้",
  "ops.versionSelectAllAria": "เลือกงานทั้งหมดที่ตรงตัวกรอง (ทุกหน้า)",
  "ops.versionSelectPageAria": "เลือกทุกใบที่แก้ได้ในหน้านี้",
  "ops.versionSelectAllMatching": "เลือกทั้งหมดที่ตรงตัวกรอง ({n})",
  "ops.versionSelectAllTooMany": "เลือกทั้งหมดที่ตรงตัวกรอง ({n}) — เกินเพดาน {max}",
  /* ADR 0291 — งานใช้เวอร์ชันกับใบงานจำนวนมาก (ทยอยทำทีละก้อน) */

  /* ADR 0292 — แก้ไขงานจำนวนมากทีละก้อน */
  "bulk.runNotice": "ระบบจะทยอยทำเป็นชุดย่อยชุดละ {chunk} ใบ (~{min} นาที) — ชุดที่ทำไปแล้วจะไม่ถูกย้อนกลับถ้าชุดถัดไปติดปัญหา",
  "bulk.runHalted": "หยุดไว้ · ทำแล้ว {done} ใบ · ยังเหลืออีก {n} ใบ (ของที่ทำไปแล้วไม่ถูกย้อน)",
  "bulk.runSkipAndContinue": "ข้ามใบนี้แล้วทำต่อ",
  "bulk.runUnfinished": "งานแก้ไขจำนวนมากค้างอยู่ — ทำแล้ว {done} จาก {total} ใบ",
  "ops.applyRunProgress": "ทำแล้ว {done} จาก {total} ใบ",
  "ops.applyRunRemaining": "เหลืออีกประมาณ {min} นาที",
  "ops.applyRunStop": "หยุด",
  "ops.applyRunBreakdown": "แผน+เงิน {full} ใบ · เงินอย่างเดียว {money} ใบ · ทำไม่ได้ {blocked} ใบ",
  "ops.applyRunKeepOpen": "เปิดหน้านี้ค้างไว้จนกว่าจะครบ — ปิดได้ งานจะหยุดไว้ตรงนี้แล้วกลับมาทำต่อได้",
  "ops.applyRunHalted": "หยุดไว้ · ยังเหลืออีก {n} ใบ",
  "ops.applyRunResume": "ทำต่อ",
  "ops.applyRunCloseJob": "ปิดงานนี้",
  "ops.applyRunEta": "(~{min} นาที)",
  "ops.applyRunUnfinished": "งานใช้เวอร์ชัน {v} ค้างอยู่ — ทำแล้ว {done} จาก {total} ใบ",
  "ops.applyRunCode.ROUTE_VERSION_NOT_ACTIVE": "เวอร์ชันนี้ถูกหยุดใช้ระหว่างทาง",
  "ops.applyRunCode.ROUTE_NOT_USABLE": "เส้นทางนี้ใช้สร้างงานไม่ได้แล้ว",
  "ops.applyRunCode.APPLY_RUN_NOT_RUNNING": "งานนี้ถูกปิดไปแล้ว",
  "ops.applyRunCode.RETRIES_EXHAUSTED": "เครือข่ายหรือเซิร์ฟเวอร์ไม่ตอบหลายครั้งติดกัน",
  "ops.applyRunCode.STOPPED_BY_USER": "คุณกดหยุดไว้",

  "ops.versionOverCap": "เลือกไว้ {n} ใบ เกินเพดาน {max} ใบต่อครั้ง — เอาออกบางส่วนหรือแบ่งทำหลายรอบ",
  "ops.versionTruncated": "งานในช่วงนี้มากเกินกว่าจะแสดงครบ (เกิน {n} ใบ) — แคบช่วงเวลาหรือใช้ตัวกรองเพื่อดูให้ครบ",
  "ops.versionAffectedMoreThan": "งานที่เริ่มในช่วงนี้ · มากกว่า {n} ใบ",
  "ops.versionNoMatch": "ไม่มีงานที่ตรงกับที่ค้นหา",
  "ops.versionResultTitle": "ผลการแก้งาน",
  "ops.versionResultOnlyFailed": "ตารางนี้แสดงเฉพาะใบที่ไม่ผ่าน",
  "ops.versionRolledBack": "ไม่มีใบไหนถูกแก้เลย — ทั้งชุดทำในทรานแซกชันเดียว พลาดหนึ่งใบจึงย้อนคืนทั้งหมด",
  "ops.versionCode.MONEY_ONLY": "แก้ได้เฉพาะเงิน (งานดำเนินการไปแล้ว)",
  "ops.versionCode.OUT_OF_WINDOW": "งานอยู่นอกช่วงผลบังคับของเวอร์ชันนี้",
  "ops.versionCode.UNKNOWN_STATUS": "สถานะของใบงานนี้ระบบยังไม่รู้จัก",
  "ops.versionCode.NOT_FOUND": "ไม่พบใบงาน (อาจถูกลบระหว่างทาง)",
  "ops.versionCode.NOOP": "ไม่มีอะไรต้องเปลี่ยน",
  "ops.versionBookingWarning": "การจอง {n} ที่นั่งจะไม่มีจุดขึ้น/ลง เพราะเวอร์ชันนี้เปลี่ยนจุดจอดทั้งชุด",
  "ops.versionRowBookings": "· จอง {n} ที่",
  "ops.versionRowLocked": "แก้ไม่ได้ — งวดจ่ายเงินปิดแล้ว",
  "ops.versionRowBlocked": "แก้ไม่ได้",
  "ops.versionRowNothing": "ไม่มีอะไรเปลี่ยน",
  "ops.versionActivateOnly": "เปิดใช้งานอย่างเดียว",
  "ops.versionStopOnly": "หยุดอย่างเดียว",
  "ops.versionSubmitApply": "บันทึก + แก้งานที่เลือก ({n})",
  "ops.versionLockedTitle": "เวอร์ชัน {n} เปิดใช้งานอยู่",
  "ops.versionLockedWhy": "เวอร์ชันที่เปิดใช้งานแล้วแก้ไม่ได้ เพราะมีใบงานที่ออกไปแล้วอ้างอิงอยู่",
  "ops.versionLockedAsk": "จะสร้างร่างเวอร์ชัน {n} โดยคัดลอกเส้นทางจากเวอร์ชัน {from} แล้วแก้ในร่างนั้นไหม? ร่างยังไม่มีผลกับงานใดจนกว่าจะกดเปิดใช้งาน",
  "ops.versionLockedCreate": "สร้างร่างเวอร์ชัน {n}",
  "ops.versionDraftBanner": "กำลังแก้ร่างเวอร์ชัน {n}",
  "ops.versionDraftBannerCopiedBaseline": "คัดลอกจากต้นฉบับ · จุดจอดและการมอบหมายคัดลอกมา · เวลาที่ถูกใช้บ่อยและเอกสารใช้ร่วมกับต้นฉบับ",
  "ops.versionDraftBannerCopied": "คัดลอกจากเวอร์ชัน {n} · จุดจอดและการมอบหมายคัดลอกมา · เวลาที่ถูกใช้บ่อยและเอกสารใช้ร่วมกับต้นฉบับ",
  "ops.versionDraftBannerNote": "ยังไม่มีผลกับงานใด จนกว่าจะกดเปิดใช้งาน",
  "ops.versionSharedAcrossVersions": "ใช้ร่วมกันทุกเวอร์ชัน",
  "ops.versionIdentityHint": "ประเภทงาน + เส้นทางคู่ เป็นของเส้นทาง ไม่ใช่ของเวอร์ชัน",
  "toast.versionDraftCreated": "สร้างร่างเวอร์ชัน {n} แล้ว",
  "toast.versionDraftExists": "มีร่างเวอร์ชัน {n} ค้างอยู่ — พาไปที่ร่างนั้นแล้ว",
  "toast.versionDiscarded": "ทิ้งร่างแล้ว",
  "toast.versionActivated": "เปิดใช้งานเวอร์ชัน {n} แล้ว",
  "toast.versionStopped": "หยุดใช้เวอร์ชัน {n} แล้ว",

  /* ── ADR 0289 — แท็บรถประจำผู้ขับ + ตัวช่วยเปลี่ยนรถ ── */
  "vb.tab": "รถประจำ / จุดจอด",
  "depot.tab": "จุดจอด",
  "depot.section": "จุดจอด",
  "vb.section": "รถประจำผู้ขับ",
  "depot.openInMaps": "เปิดใน Google Maps",
  "depot.pickHint": "ค้นหาสถานที่ด้านบน แล้วปรับหมุดบนแผนที่ให้ตรงจุดจอด",
  "depot.clearTitle": "ยกเลิกจุดจอด?",
  "depot.clearBody": "รถประจำจะไม่มีตำแหน่งบนแผนที่จนกว่าจะตั้งจุดจอดใหม่ · ประวัติเดิมยังอยู่",
  "depot.set": "ตั้งจุดจอด",
  "depot.change": "เปลี่ยนจุดจอด",
  "depot.clear": "ยกเลิกจุดจอด",
  "depot.search": "ค้นหาสถานที่",
  "depot.saved": "บันทึกจุดจอดแล้ว",
  "depot.cleared": "ยกเลิกจุดจอดแล้ว",
  "depot.saveFailed": "บันทึกจุดจอดไม่สำเร็จ",
  "depot.never": "ยังไม่เคยตั้งจุดจอด",
  "depot.releasedRow": "ยกเลิกจุดจอด",
  "depot.colDate": "มีผลเมื่อ",
  "depot.colState": "สถานะ",
  "depot.colPlace": "สถานที่",
  "depot.colCoords": "พิกัด",
  "depot.fromDriver": "จุดจอดของ {name}",
  "depot.noDriver": "ไม่มีคนขับประจำ — ไม่มีจุดจอด",
  "vb.manageVehicle": "กำหนดรถประจำ",
  "vb.never": "ยังไม่เคยผูกรถ",
  "vb.current": "ปัจจุบัน",
  "vb.colDate": "วันที่",
  "vb.colState": "สถานะ",
  "vb.colPlate": "ทะเบียน",
  "vb.colFrom": "จากคนขับ",
  "vb.colBy": "โดย",
  "vb.colNote": "หมายเหตุ",
  "vb.released": "ปลดรถ",
  "vb.typeChanged": "ไม่ใช่คนขับแล้ว",
  "vb.step1": "เลือกรถ",
  "vb.step2": "ตรวจใบงาน",
  "vb.step3": "ยืนยัน",
  "vb.sideThis": "ผู้ขับรายนี้",
  "vb.sideDisplaced": "ผู้ขับเดิมของรถคันนี้",
  "vb.pickVehicle": "เลือกรถคันใหม่",
  "vb.pickVehiclePlaceholder": "เลือกรถ…",
  "vb.optionRelease": "ปลดรถ (ไม่ผูกรถ)",
  "vb.notPickedYet": "ยังไม่ได้เลือกรถ",
  "vb.notPickedBody": "เลือกรถทางซ้ายก่อน",
  "vb.freeHead": "รถว่าง",
  "vb.freeBody": "ไม่มีผู้ขับรายอื่นได้รับผลกระทบ",
  "vb.noVehicle": "ไม่มีรถ",
  "vb.free": "ว่าง",
  "vb.freedByChange": "ว่างจากการเปลี่ยนครั้งนี้",
  "vb.inactive": "ไม่ใช้งาน",
  "vb.searchTickets": "ค้นหาใบงาน / เส้นทาง…",
  "vb.colStartAt": "วันเวลาเริ่มงาน",
  "vb.sumMove": "ใบงานจากทะเบียน {from} เป็น {to}",
  "vb.sumClear": "ใบงานจากทะเบียน {from} เป็น ไม่ผูกรถ",
  "vb.sumLeft": "ไม่ได้เลือก",
  "vb.sumKeeps": "ยังถือ {plate}",
  "vb.sumUnit": "{n} ใบ",
  "vb.colJob": "ใบงาน",
  "vb.colStatus": "สถานะ",
  "vb.fromBoard": "บอร์ด",
  "vb.selectedOf": "เลือก {n} จาก {total} ใบ",
  "vb.selectedOutside": "เลือกไว้ {n} ใบที่อยู่นอกคำค้น",
  "vb.overdue": "เลยวันแล้ว — ไม่ติ๊กให้ล่วงหน้า",
  "vb.noTickets": "ไม่มีใบงานค้างกับรถคันนี้",
  "vb.noMatch": "ไม่พบใบงานที่ตรงคำค้น",
  "vb.clearSearch": "ล้างคำค้น",
  "vb.pageOf": "{from}–{to} จาก {total}",
  "vb.overCap": "เลือกได้ไม่เกิน {max} ใบ — ตอนนี้ {n} ใบ",
  "vb.overlapWarn": "คนขับคนนี้มีงานที่ยังไม่จบเวลาทับกันอยู่ {n} ใบ ({ids}) — คนหนึ่งคนขับได้ครั้งละคันเดียว การเปลี่ยนรถไม่ได้ทำให้เกิดและไม่ได้แก้ ต้องไปจัดตารางให้เรียบร้อย",
  "vb.warnTitle": "ข้อควรทราบ",
  "vb.runningWarn": "มีงานกำลังวิ่ง — ทะเบียนในแอปคนขับจะเปลี่ยนทันที",
  "vb.revertWarn": "ใบสถานะ “มอบหมาย” จะกลับเป็นร่าง — คนขับจะไม่เห็นในแอป",
  "vb.next": "ถัดไป",
  "vb.back": "ย้อนกลับ",
  "vb.confirm": "ยืนยัน",
  "vb.reason": "หมายเหตุ (ไม่บังคับ)",
  "vb.jobsLine": "ใบงาน   แก้ {n} ใบ",
  "vb.done": "เปลี่ยนรถแล้ว · แก้ใบงาน {n} ใบ",
  "vb.rolledBack": "ไม่ได้แก้อะไรเลย — ย้อนกลับทั้งหมด",
  "vb.stoppedAt": "หยุดที่ {id}",
  "vb.dropRetry": "ตัดใบนี้ออกแล้วลองใหม่",
  "vb.holderChanged": "ข้อมูลเปลี่ยนไประหว่างตรวจ",
  "vb.recheck": "ตรวจใหม่",
  "vb.manageAtTab": "จัดการที่หน้าข้อมูลผู้ขับ",
  "vb.bindFromDriver": "ผูกจากหน้าข้อมูลผู้ขับ",
  "vb.noDriver": "— ไม่มี —",
  "fleet.expiry.notSet": "ไม่ระบุ",
  "fleet.expiry.overdueDays": "เกิน {n} วัน",
  "fleet.expiry.daysLeft": "{n} วัน",
  "fleet.expiry.window": "ช่วงวันหมดอายุ",
  "fleet.expiry.all": "ทั้งหมด",
  "fleet.expiry.expiring": "ใกล้หมดอายุ",
  "fleet.expiry.expired": "หมดอายุแล้ว",
  "fleet.expiry.withinDays": "ภายใน {n} วัน",
  "fleet.kpi.total": "ทั้งหมด",
  "fleet.kpi.expiring90": "ใกล้หมดอายุ 90 วัน",
  "fleet.kpi.expired": "หมดอายุแล้ว",
  "fleet.kpi.missing": "รถที่ยังไม่มีรายการนี้",
  "fleet.kpi.sumAmount": "ยอดรวมที่กรองอยู่",
  "fleet.lender": "บริษัทไฟแนนซ์",
  "fleet.progress": "ความคืบหน้า",
  "fleet.regPlate": "ทะเบียน",
  "fleet.regProvince": "จังหวัด",
  "fleet.regTo": "ถึง",
  "fleet.regCurrent": "ปัจจุบัน",
  "fleet.noDepotSet": "ยังไม่ได้ตั้งจุดจอด — กดแก้ไขเพื่อเพิ่มตำแหน่ง",
  "fleet.deleteFinanceConfirm": "ลบรายการไฟแนนซ์ของ {lender} ใช่ไหม ลบแล้วกู้คืนไม่ได้",
  "fleet.colPrincipal": "เงินต้น",
  "fleet.colMonthly": "ค่างวด/เดือน",
  "fleet.taxRoadTax": "ภาษีรถยนต์",
  "fleet.taxOther": "ภาษีอื่นๆ",
  "fleet.nTaxRecordsAcrossFleet": "{n} รายการภาษีทั้งกองรถ",
  "fleet.taxLoadFailed": "โหลดข้อมูลภาษีไม่สำเร็จ",
  "fleet.searchTax": "ค้นหาเลขใบเสร็จ, รถ, ทะเบียน…",
  "fleet.addTaxRecord2": "เพิ่มรายการภาษี",
  "fleet.untaxedVehicles": "รถที่ภาษีขาด",
  "fleet.untaxedCount": "ภาษีขาด ({n})",
  "fleet.allVehiclesTaxed": "รถทุกคันมีภาษีที่ยังไม่หมดอายุ",
  "fleet.noTaxRecords2": "ไม่พบรายการภาษีตามที่กรอง",
  "fleet.noTaxRecordsYet": "ยังไม่มีรายการภาษีในระบบ",
  "fleet.taxTotalFiltered": "ยอดภาษีที่กรองอยู่",
  "fleet.taxFeeItems": "รายการค่าใช้จ่าย",
  "fleet.addFeeItem": "เพิ่มรายการย่อย",
  "fleet.feeType": "ประเภทค่าใช้จ่าย",
  "fleet.feeAmount": "จำนวนเงิน (บาท)",
  "fleet.feeItemsTotal": "รวม",
  "fleet.feeItemsHint": "ใส่รายการย่อยแล้วยอดรวมจะคำนวณให้เอง — ไม่ใส่ก็กรอกยอดรวมเองได้",
  "fleet.paidDate": "วันที่ชำระ",
  "fleet.taxStatus": "สถานะชำระ",
  "fleet.documentUrl": "ไฟล์เอกสาร (URL)",
  "fleet.viewDocument": "เปิดเอกสาร",
  "fleet.deleteTaxRecordQ2": "ลบรายการภาษีนี้ใช่ไหม",
  "fleet.editTaxRecord2": "แก้ไขรายการภาษี",
  "fleet.renewTax": "ต่ออายุภาษี",
  "fleet.taxExpiringSoon": "ภาษีใกล้หมดอายุ",
  "nav.tax": "ภาษี",
  "toast.taxSaved": "บันทึกรายการภาษีแล้ว",
  "toast.taxDeleted": "ลบรายการภาษีแล้ว",
  "fleet.taxDeleteFailed": "ลบรายการภาษีไม่สำเร็จ",
  "fleet.taxPageTitle": "ภาษี",
  "fleet.colAmount": "จำนวนเงิน",
  "fleet.colIssue": "วันที่ออก",
  "fleet.deleteTaxConfirm": "ลบรายการภาษีนี้ใช่ไหม {ref} ลบแล้วกู้คืนไม่ได้",
  "fleet.nFeeLines": "{n} รายการย่อย",
  "fleet.deviceType": "ประเภทอุปกรณ์",
  "fleet.installCost": "ค่าติดตั้ง",
  "fleet.warrantyEnd": "ประกันอุปกรณ์ถึง",
  "fleet.certificateNo": "เลขที่ใบรับรอง",
  "fleet.certificateExpiry": "ใบรับรองหมดอายุ",
  "fleet.positionNote": "ตำแหน่งที่ติดตั้ง",
  "fleet.removedAt": "วันที่ถอดออก",
  "fleet.devices": "อุปกรณ์ติดรถ",
  "nav.devices": "อุปกรณ์ติดรถ",
  "fleet.nDevicesAcrossFleet": "{n} อุปกรณ์ทั้งกองรถ",
  "fleet.devicesLoadFailed": "โหลดข้อมูลอุปกรณ์ไม่สำเร็จ",
  "fleet.searchDevice": "ค้นหาผู้ให้บริการ, เลขเครื่อง, ซิม, รถ…",
  "fleet.addDevice": "เพิ่มอุปกรณ์",
  "fleet.editDevice": "แก้ไขอุปกรณ์",
  "fleet.removeDevice": "ถอดอุปกรณ์ออก",
  "fleet.removeDeviceQ": "ถอดอุปกรณ์นี้ออกจากรถใช่ไหม ระบบจะบันทึกวันที่ถอดไว้",
  "fleet.noDevices": "ไม่พบอุปกรณ์ตามที่กรอง",
  "fleet.noDevicesYet": "ยังไม่มีอุปกรณ์ในระบบ",
  "fleet.missingDevice": "รถที่ยังไม่มีอุปกรณ์นี้",
  "fleet.missingDeviceCount": "ยังไม่มี ({n})",
  "fleet.allVehiclesHaveDevice": "รถทุกคันมีอุปกรณ์ชนิดนี้แล้ว",
  "fleet.deviceCostPerMonth": "ค่าอุปกรณ์รวมที่กรองอยู่",
  "fleet.expiryKind": "วันหมดอายุที่ดู",
  "fleet.expiryKindContract": "สัญญาบริการ",
  "fleet.expiryKindWarranty": "ประกันอุปกรณ์",
  "fleet.expiryKindCertificate": "ใบรับรอง",
  "fleet.signal": "สัญญาณล่าสุด",
  "fleet.signalLive": "ส่งข้อมูลอยู่",
  "fleet.signalQuiet": "เงียบ",
  "fleet.signalDead": "ไม่ส่งข้อมูล",
  "fleet.signalNever": "ไม่เคยส่ง",
  "fleet.signalHint": "สถานะสัญญาณมาจากข้อมูลจริงที่รถส่งเข้ามา คนละเรื่องกับสถานะสัญญา",
  "toast.deviceSaved": "บันทึกอุปกรณ์แล้ว",
  "toast.deviceRemoved": "ถอดอุปกรณ์ออกแล้ว",
  "fleet.deviceRemoveFailed": "ถอดอุปกรณ์ไม่สำเร็จ",
  "fleet.deviceTypeGps": "เครื่องติดตาม GPS",

  /* ── GPS devices (ADR 0362) ──
     แยกจาก "อุปกรณ์ติดรถ": กล่อง GPS เป็นทรัพย์สินที่ย้ายรถได้
     คำที่ใช้บนจอเรียก "กล่อง" เพราะผู้ใช้เรียกของจริงแบบนั้น */
  "fleet.gps": "GPS",
  "fleet.gpsDevices": "กล่อง GPS",
  "fleet.gpsTitle": "กล่อง GPS",
  "fleet.gpsSerial": "เลขเครื่อง (S/N)",
  "fleet.gpsStatus": "สถานะกล่อง",
  "fleet.gpsInStock": "อยู่ในคลัง",
  "fleet.gpsInstalled": "ติดตั้งแล้ว",
  "fleet.gpsRepair": "ส่งซ่อม",
  "fleet.gpsRetired": "ปลดระวาง",
  "fleet.gpsAdd": "เพิ่มกล่อง GPS",
  "fleet.gpsEdit": "แก้ไขกล่อง GPS",
  "fleet.gpsTabAll": "กล่องทั้งหมด",
  "fleet.gpsTabStock": "อยู่ในคลัง",
  "fleet.gpsTabMissing": "รถที่ยังไม่มี GPS",
  "fleet.gpsStockCount": "ในคลัง ({n})",
  "fleet.gpsMissingCount": "ยังไม่มี ({n})",
  "fleet.nGpsAcrossFleet": "{n} กล่องทั้งกองรถ",
  "fleet.gpsNone": "ไม่พบกล่อง GPS ตามที่กรอง",
  "fleet.gpsNoneYet": "ยังไม่มีกล่อง GPS ในระบบ",
  "fleet.gpsStockEmpty": "ไม่มีกล่องว่างในคลัง",
  "fleet.gpsAllVehiclesHave": "รถทุกคันมีกล่อง GPS แล้ว",
  "fleet.gpsSearch": "ค้นหาเลขเครื่อง, ผู้ให้บริการ, ซิม…",
  "fleet.gpsCostTotal": "ค่าบริการรวมที่กรองอยู่",
  "fleet.gpsBind": "ผูกกับรถ",
  "fleet.gpsBindTitle": "ผูกกล่อง GPS กับรถ",
  "fleet.gpsUnbind": "ถอดเก็บเข้าคลัง",
  "fleet.gpsMove": "ย้ายไปคันอื่น",
  "fleet.gpsMoveTitle": "ย้ายกล่อง GPS ไปคันอื่น",
  "fleet.gpsMoveTo": "รถปลายทาง",
  "fleet.gpsMoveDate": "วันที่ย้าย",
  "fleet.gpsBindDate": "วันที่ติดตั้ง",
  "fleet.gpsUnbindDate": "วันที่ถอด",
  "fleet.gpsInstallCost": "ค่าติดตั้งครั้งนี้",
  "fleet.gpsReason": "เหตุผล",
  "fleet.gpsReasonHint": "เช่น ย้ายไปรถใหม่ · กล่องเสีย · คืนผู้ให้บริการ",
  "fleet.gpsCurrent": "กล่องที่ติดอยู่ตอนนี้",
  "fleet.gpsNotFitted": "รถคันนี้ยังไม่มีกล่อง GPS",
  "fleet.gpsHistory": "ประวัติการติดตั้ง",
  "fleet.gpsVehicleHistory": "กล่องที่เคยติดรถคันนี้",
  "fleet.gpsNoHistory": "ยังไม่มีประวัติ",
  "fleet.gpsStillFitted": "ยังติดอยู่",
  "fleet.gpsUntilNow": "ปัจจุบัน",
  "fleet.gpsMovedIn": "ย้ายมาจากคันก่อนหน้า",
  "fleet.gpsMovedOut": "ถอดเพราะย้ายไปคันอื่น",
  "fleet.gpsRecordedOn": "บันทึกเมื่อ {date}",
  "fleet.gpsFittedDays": "ติดอยู่ {n} วัน",
  "fleet.gpsMovedBy": "โดย {name}",
  "fleet.gpsWarnHasGps": "รถคันนี้มีกล่อง GPS ติดอยู่แล้ว — ผูกเพิ่มได้ แต่ตรวจดูก่อนว่าไม่ได้เลือกผิดคัน",
  "fleet.gpsUnbindQ": "ถอดกล่องนี้ออกจากรถใช่ไหม ระบบจะบันทึกวันที่ถอดไว้ในประวัติ",
  "fleet.gpsRetire": "ปลดระวาง",
  "fleet.gpsRetireQ": "ปลดระวางกล่องนี้ใช่ไหม ประวัติยังอยู่ แต่จะนำไปติดรถไม่ได้อีก",
  "fleet.gpsRetireBlocked": "ถอดออกจากรถก่อน จึงจะปลดระวางได้",
  "fleet.gpsSaveFailed": "บันทึกกล่อง GPS ไม่สำเร็จ",
  "fleet.gpsActionFailed": "ทำรายการกับกล่อง GPS ไม่สำเร็จ",
  "fleet.gpsNote": "หมายเหตุ",
  "fleet.gpsInStockNoVehicle": "—",
  "fleet.costGps": "ค่า GPS",
  "fleet.colGpsContract": "สัญญา GPS",
  "fleet.gpsErrAlreadyFitted": "กล่องนี้ติดอยู่กับรถคันอื่นแล้ว — ใช้ปุ่ม “ย้ายไปคันอื่น” แทนการผูกซ้ำ",
  "fleet.gpsErrSerialTaken": "เลขเครื่องนี้มีอยู่ในระบบแล้ว ตรวจดูว่าพิมพ์ถูกหรือกล่องตัวนี้ถูกบันทึกไปแล้ว",
  "fleet.gpsErrSameVehicle": "กล่องนี้อยู่บนรถคันนี้อยู่แล้ว",
  "fleet.gpsErrRetireWhileFitted": "ถอดกล่องออกจากรถก่อน จึงจะปลดระวางได้",
  "fleet.gpsErrRetiredCannotFit": "กล่องที่ปลดระวางแล้วนำไปติดรถไม่ได้",
  "fleet.gpsErrUnbindBeforeBind": "วันที่ถอดอยู่ก่อนวันที่ติดตั้ง ตรวจวันที่อีกครั้ง",
  "fleet.gpsErrRaced": "มีคนอื่นเพิ่งผูกกล่องนี้กับรถไปก่อนหน้า ลองรีเฟรชแล้วทำใหม่",
  "fleet.gpsErrStatusInstalled": "สถานะ “ติดตั้งแล้ว” ตั้งเองไม่ได้ — เกิดจากการผูกกล่องกับรถ",
  "fleet.gpsErrVehicleGone": "ไม่พบรถคันที่เลือก อาจถูกลบไปแล้ว ลองรีเฟรชแล้วเลือกใหม่",
  "toast.gpsSaved": "บันทึกกล่อง GPS แล้ว",
  "toast.gpsBound": "ผูกกล่อง GPS กับรถแล้ว",
  "toast.gpsUnbound": "ถอดกล่อง GPS เก็บเข้าคลังแล้ว",
  "toast.gpsMoved": "ย้ายกล่อง GPS ไปคันใหม่แล้ว",
  "toast.gpsRetired": "ปลดระวางกล่อง GPS แล้ว",
  "fleet.deviceTypeOther": "อุปกรณ์อื่น",
  "fleet.none": "ไม่มี",
  "nav.installments": "ค่างวด & ไฟแนนซ์",
  "fleet.installments": "ค่างวด & ไฟแนนซ์",
  "fleet.installment": "งวดที่",
  "fleet.installmentNo": "งวดที่ {n}",
  "fleet.nInstallmentsAcrossFleet": "{n} งวดทั้งกองรถ",
  "fleet.installmentsLoadFailed": "โหลดข้อมูลค่างวดไม่สำเร็จ",
  "fleet.searchInstallment": "ค้นหารถ, ทะเบียน, บริษัทไฟแนนซ์, เลขที่สัญญา…",
  "fleet.financeContract": "สัญญาไฟแนนซ์",
  "fleet.contractNumber": "เลขที่สัญญา",
  "fleet.financer": "บริษัทไฟแนนซ์",
  "fleet.vehiclePrice": "ราคารถ (บาท)",
  "fleet.discount": "ส่วนลด (บาท)",
  "fleet.netPrice": "ราคาสุทธิ",
  "fleet.downPayment": "เงินดาวน์ (บาท)",
  "fleet.financeAmount": "ยอดจัด",
  "fleet.interestRate": "ดอกเบี้ย (%)",
  "fleet.totalInterest": "ดอกเบี้ยรวม",
  "fleet.totalAmount": "ยอดรวมทั้งสัญญา",
  "fleet.installmentsTotal": "จำนวนงวด",
  "fleet.installmentAmount": "ค่างวด/เดือน (บาท)",
  "fleet.firstPaymentDate": "งวดแรกครบกำหนด",
  "fleet.deliveryDate": "วันรับมอบ",
  "fleet.saleDate": "วันขายต่อ",
  "fleet.ownershipDate": "เป็นเจ้าของเมื่อ",
  "fleet.addFinanceContract": "เพิ่มสัญญาไฟแนนซ์",
  "fleet.editFinanceContract": "แก้ไขสัญญาไฟแนนซ์",
  "fleet.deleteFinanceContract": "ลบสัญญาไฟแนนซ์",
  "fleet.deleteFinanceContractQ": "ลบสัญญานี้ใช่ไหม ลบได้เฉพาะสัญญาที่ยังไม่มีงวดที่จ่ายแล้ว",
  "fleet.recordPayment": "บันทึกการจ่าย",
  "fleet.clearPayment": "ยกเลิกการบันทึกจ่าย",
  "fleet.amountPaid": "ยอดที่จ่ายจริง (บาท)",
  "fleet.amountDue": "ยอดที่ต้องจ่าย",
  "fleet.receiptUrl": "ใบเสร็จ (URL)",
  "fleet.paidOfTotal": "จ่ายแล้ว {paid}/{total} งวด",
  "fleet.amountRemaining": "ยอดคงเหลือ",
  "fleet.overdueInstallments": "งวดค้างชำระ",
  "fleet.dueNext30": "ครบกำหนดใน 30 วัน",
  "fleet.monthlyTotal": "ค่างวดรวมต่อเดือน",
  "fleet.vehiclesFinanced": "รถที่ผ่อนอยู่",
  "fleet.paidOffContracts": "ผ่อนหมดแล้ว",
  "fleet.importedData": "ข้อมูลยกมา",
  "fleet.importedHint": "งวดนี้ยกมาจากระบบเดิม ไม่มีวันที่จ่ายจริง",
  "fleet.noFinanceContracts": "ยังไม่มีสัญญาไฟแนนซ์",
  "fleet.noInstallments": "ไม่พบงวดตามที่กรอง",
  "fleet.noInstallmentsYet": "ยังไม่มีข้อมูลค่างวดในระบบ",
  "fleet.filterOverdue": "ค้างชำระ",
  "fleet.filterDueSoon": "ใกล้ครบกำหนด",
  "fleet.installmentTimeline": "ตารางงวด",
  "toast.financeContractSaved": "บันทึกสัญญาไฟแนนซ์แล้ว",
  "toast.paymentRecorded": "บันทึกการจ่ายแล้ว",
  "fleet.paymentFailed": "บันทึกการจ่ายไม่สำเร็จ",
  "fleet.contractDeleteFailed": "ลบสัญญาไม่สำเร็จ",
  "status.partial": "จ่ายบางส่วน",
  "fleet.scheduleResyncHint": "แก้จำนวนงวดหรือค่างวดแล้ว ระบบจะปรับตารางงวดที่ยังไม่จ่ายให้ — งวดที่จ่ายแล้วจะไม่ถูกแตะ",
  "fleet.partialHint": "จ่ายไม่ครบ — ยังค้างอีก ฿{n} และจะยังนับเป็นยอดค้างชำระ",
  "common.paymentMethod": "วิธีชำระเงิน",
  "fleet.showAllInstallments": "ดูทั้ง {n} งวด",
  "fleet.nInstallmentsHidden": "ซ่อนอยู่อีก {n} งวด",
  "common.showLess": "ย่อ",
  "nav.costs": "ต้นทุนรวมต่อคัน",
  "fleet.costs": "ต้นทุนรวมต่อคัน",
  "fleet.costsSubtitle": "รวมค่าน้ำมัน ซ่อมบำรุง ประกัน ภาษี อุปกรณ์ GPS และค่างวด",
  "fleet.costsLoadFailed": "โหลดข้อมูลต้นทุนไม่สำเร็จ",
  "fleet.costFuel": "ค่าน้ำมัน",
  "fleet.costMaintenance": "ค่าซ่อมบำรุง",
  "fleet.costInsurance": "ค่าประกัน",
  "fleet.costTax": "ค่าภาษี",
  "fleet.costDevice": "ค่าอุปกรณ์",
  "fleet.costInstallment": "ค่างวด",
  "fleet.costTotal2": "รวมทั้งหมด",
  "fleet.costPerKm": "ต้นทุน/กม.",
  "fleet.kmInPeriod": "ระยะทางในช่วงนี้",
  "fleet.costPeriod": "ช่วงเวลา",
  "fleet.periodThisYear": "ปีนี้",
  "fleet.periodThisMonth": "เดือนนี้",
  "fleet.periodThisQuarter": "ไตรมาสนี้",
  "fleet.periodLastYear": "ปีที่แล้ว",
  "fleet.periodCustom": "กำหนดเอง",
  "fleet.costRecognitionHint": "ต้นทุนนับตอนที่ครบกำหนดจ่ายหรือเกิดขึ้นจริง ไม่ได้เฉลี่ยตลอดอายุกรมธรรม์หรือสัญญา",
  "fleet.noCostData": "ไม่พบรถตามที่กรอง",
  "fleet.costPerKmHint": "คำนวณจากเลขไมล์ในใบเติมน้ำมันช่วงนี้ — ถ้าไม่มีใบเติมจะไม่มีตัวเลข",
  "fleet.aisleHint": "ทางเดิน = ช่องที่จองไม่ได้",
  "fleet.seatCountMismatch": "ผัง {grid} ที่นั่ง ≠ ข้อมูลรถ {declared}",
  "fleet.seatCountMismatchHint": "จำนวนที่นั่งในผังไม่ตรงกับที่บันทึกไว้ในข้อมูลรถ — ตรวจสอบก่อนเปิดขายที่นั่ง",
  "fleet.noVehiclesHint": "ยังไม่มีรถในระบบ — เพิ่มหรือนำเข้ารถก่อน",
  "fleet.seatMap": "ผังที่นั่ง",
  "fleet.inspections": "ประวัติตรวจสภาพ",
  "fleet.noInspections": "ยังไม่มีประวัติตรวจสภาพของรถคันนี้",
  "fleet.inspectionTemplate": "แบบตรวจ",
  "fleet.inspector": "ผู้ตรวจ",
  "fleet.inspectedAt": "ตรวจเมื่อ",
  "fleet.viewAllInspections": "ดูทั้งหมดที่หน้าตรวจสภาพ",
  "fleet.pmSchedules": "รอบบำรุงรักษา",
  "fleet.noPmForVehicle": "ยังไม่มีแผนบำรุงรักษาของรถคันนี้",
  "fleet.viewAllPm": "ดูทั้งหมดที่หน้าบำรุงรักษา",
  "fleet.costsForVehicle": "ต้นทุนของรถคันนี้",
  "fleet.viewAllCosts": "ดูทั้งกองรถ",
  "fleet.make": "ยี่ห้อ",
  "fleet.model": "รุ่น",
  "fleet.year": "ปี",
  "fleet.vin": "เลขตัวถัง (VIN)",
  "fleet.depotId": "รหัสศูนย์รถ",
  "fleet.kmPerLiterLabel": "กม./ลิตร",
  "fleet.attention": "ต้องดูแลด่วน",
  "fleet.attentionHint": "เอกสารใกล้หมดอายุ/หมดแล้ว ไม่มีประกัน หรือมีงานซ่อมเลยกำหนด",
  "fleet.colInsurance": "ประกัน",
  "fleet.colTax": "ภาษี",
  "fleet.colDevices": "อุปกรณ์",
  "fleet.colWorkOrders": "งานซ่อม",
  "fleet.colInstallments": "ค่างวด",
  "fleet.nOverdue": "ค้าง {n}",
  "fleet.fuelSection": "น้ำมัน & พลังงาน",
  "fleet.vehicleFront": "หน้ารถ",
  "fleet.nFailedItems": "ไม่ผ่าน {n} ข้อ",
  "fleet.nOpenOfTotal": "ค้างอยู่ {open} จาก {total} รายการ",
  "fleet.pmSchedule2": "รอบบำรุงรักษา",
  "fleet.pmIntervalType": "รอบคิดจาก",
  "fleet.pmByTime": "ตามเวลา",
  "fleet.pmByOdometer": "ตามระยะทาง",
  "fleet.pmByBoth": "อย่างใดถึงก่อน",
  "fleet.pmEveryMonths": "ทุก {n} เดือน",
  "fleet.pmEveryKm": "ทุก {n} กม.",
  "fleet.pmTimeInterval": "รอบเวลา (เดือน)",
  "fleet.pmOdoInterval": "รอบระยะทาง (กม.)",
  "fleet.pmLastPerformed": "ทำครั้งล่าสุด",
  "fleet.pmLastOdometer": "เลขไมล์ครั้งล่าสุด",
  "fleet.pmNextDue": "ครบกำหนดถัดไป",
  "fleet.pmNextOdometer": "ครบที่เลขไมล์",
  "fleet.pmOdometerRemaining": "เหลืออีก {n} กม.",
  "fleet.pmOverdueKm": "เกินมาแล้ว {n} กม.",
  "fleet.pmAutoCreate": "เปิดใบสั่งซ่อมอัตโนมัติเมื่อถึงรอบ",
  "fleet.pmAutoOn": "อัตโนมัติ",
  "fleet.pmRecordPerformed": "บันทึกว่าทำแล้ว",
  "fleet.pmRecordPerformedQ": "บันทึกว่าทำรายการนี้แล้วใช่ไหม ระบบจะเลื่อนรอบถัดไปให้เอง",
  "fleet.deletePmScheduleQ": "ลบรอบบำรุงรักษานี้ใช่ไหม ใบสั่งซ่อมที่เปิดไปแล้วจะยังอยู่",
  "fleet.noPmSchedules": "ยังไม่มีรอบบำรุงรักษา",
  "fleet.noPmSchedulesFiltered": "ไม่พบรอบบำรุงรักษาตามที่กรอง",
  "fleet.searchPm": "ค้นหารถ, ทะเบียน, รายการ, อู่…",
  "fleet.pmDueOverdue": "เลยกำหนด",
  "fleet.pmDueSoon": "ใกล้ถึงรอบ",
  "toast.pmSaved": "บันทึกรอบบำรุงรักษาแล้ว",
  "toast.pmPerformed": "บันทึกว่าทำแล้ว — เลื่อนรอบถัดไปให้แล้ว",
  "fleet.pmSaveFailed": "บันทึกรอบบำรุงรักษาไม่สำเร็จ",
  "fleet.costItems": "รายการค่าใช้จ่าย",
  "fleet.addCostItem": "เพิ่มรายการ",
  "fleet.costCategory": "หมวด",
  "fleet.costDescription": "รายละเอียด",
  "fleet.costQuantity": "จำนวน",
  "fleet.costUnitPrice": "ราคาต่อหน่วย",
  "fleet.costLineTotal": "รวม",
  "fleet.costItemsHint": "ใส่รายการแล้วยอดค่าอะไหล่/ค่าแรง/ยอดรวมจะคำนวณให้เอง — ไม่ใส่ก็กรอกยอดรวมเองได้",
  "fleet.woTimeline": "ไทม์ไลน์สถานะ",
  "fleet.interestRateRange": "ดอกเบี้ยต้องอยู่ระหว่าง 0–100%",
  "fleet.includeInactive": "รวมรถที่ปิดใช้งาน",
  "fleet.openFleetWide": "ดูทั้งกองรถ",

  // ── ADR 0366 — ตั๋วซ่อมบำรุง: กระดาน · ผู้เกี่ยวข้อง · ใบเสร็จ ──
  "maint.statusRequested": "รอรับเรื่อง",
  "maint.statusOpen": "เปิดตั๋ว",
  "maint.statusInProgress": "กำลังซ่อม",
  "maint.statusCompleted": "จบตั๋วแล้ว",
  "maint.statusCancelled": "ยกเลิก",
  "maint.statusRejected": "ปัดตก",
  "maint.roleOwner": "เจ้าของเรื่อง",
  "maint.roleAssignee": "ผู้รับผิดชอบ",
  "maint.roleDriver": "คนขับ",
  "maint.roleWatcher": "ผู้ติดตาม",
  "maint.sourceDriverApp": "มาจากแอปคนขับ",
  "maint.sourceWeb": "เปิดจากหลังบ้าน",
  "maint.sourceAuto": "รอบบำรุงรักษาอัตโนมัติ",
  "maint.tabBoard": "กระดาน",
  "maint.tabInfo": "ทั่วไป",
  "maint.tabReceipts": "ใบเสร็จ",
  "maint.openFor": "เปิดมาแล้ว",
  "maint.notStarted": "ยังไม่เริ่ม",
  "maint.days": "{n} วัน",
  "maint.lateBy": "เลยมา {n} วัน",
  "maint.dueDate": "กำหนดเสร็จ",
  "maint.odometer": "เลขไมล์",
  "maint.totalCost": "ค่าใช้จ่ายรวม",
  "maint.startedAt": "เริ่มเมื่อ",
  "maint.closedAt": "ปิดเมื่อ",
  "maint.ticketCode": "รหัสตั๋ว",
  "maint.ticketNotFound": "ไม่พบตั๋วซ่อมบำรุงใบนี้",
  "maint.backToList": "กลับไปหน้ารายการ",
  "maint.cost": "ค่าใช้จ่าย",
  "maint.receiptCount": "รวม {n} ใบเสร็จ",
  "maint.ticketAttachments": "ไฟล์แนบของตั๋ว",
  "maint.legacyPhoto": "รูปเดิม (ก่อนย้ายระบบไฟล์)",
  "maint.attachment": "ไฟล์แนบ",
  "maint.openFile": "เปิดไฟล์",
  "maint.actionAccept": "รับเรื่อง",
  "maint.actionReject": "ปัดตก",
  "maint.actionStart": "เริ่มซ่อม",
  "maint.actionFinish": "จบตั๋วซ่อมบำรุง",
  "maint.actionReopen": "เปิดใหม่",
  "maint.actionCancel": "ยกเลิกตั๋ว",
  "maint.statusChanged": "เปลี่ยนสถานะแล้ว",
  "maint.statusChangeFailed": "เปลี่ยนสถานะไม่สำเร็จ",
  "maint.reason": "เหตุผล",
  "maint.reasonHint": "บอกเหตุผลให้คนที่เกี่ยวข้องอ่านบนกระดาน",
  "maint.finishTitle": "จบตั๋วซ่อมบำรุงใบนี้",
  "maint.finishMessage": "ปิดแล้วจะแก้ใบเสร็จไม่ได้ แต่ยังโพสต์คุยกันต่อได้",
  "maint.finishNoReceipts": "ใบนี้ยังไม่มีใบเสร็จสักใบ — ปิดได้ แต่ค่าใช้จ่ายจะเป็นศูนย์",
  "maint.editTicket": "แก้ไขตั๋ว",
  "maint.systemAuthor": "ระบบ",
  "maint.edited": "(แก้แล้ว)",
  "maint.postDeleted": "ข้อความถูกลบ",
  "maint.reply": "ตอบกลับ",
  "maint.replyingTo": "กำลังตอบกลับโพสต์",
  "maint.filterAll": "ทั้งหมด",
  "maint.filterPeople": "คุยกัน",
  "maint.filterSystem": "ความเคลื่อนไหว",
  "maint.boardEmpty": "ยังไม่มีใครพูดอะไรในเรื่องนี้ — เริ่มก่อนได้เลย",
  "maint.composerPlaceholder": "เขียนอะไรถึงทีมในเรื่องนี้…",
  "maint.attachFile": "แนบไฟล์",
  "maint.post": "โพสต์",
  "maint.fileTooBig": "ไฟล์ใหญ่เกิน 20 MB",
  "maint.uploadFailed": "อัปโหลดไม่สำเร็จ",
  "maint.postFailed": "โพสต์ไม่สำเร็จ",
  "maint.deleteFailed": "ลบไม่สำเร็จ",
  "maint.uploading": "กำลังอัปโหลด…",
  "maint.eventCreated": "เปิดตั๋วซ่อมบำรุง",
  "maint.eventStatus": "เปลี่ยนสถานะ",
  "maint.eventJoined": "เพิ่มผู้เกี่ยวข้อง",
  "maint.eventLeft": "ถอดผู้เกี่ยวข้อง",
  "maint.eventReceiptAdded": "บันทึกใบเสร็จ",
  "maint.eventReceiptUpdated": "แก้ใบเสร็จ",
  "maint.eventReceiptRemoved": "ลบใบเสร็จ",
  "maint.eventVehicleChanged": "ย้ายรถของตั๋ว",
  "maint.participants": "ผู้เกี่ยวข้อง",
  "maint.noParticipants": "ยังไม่มีใครในตั๋วนี้",
  "maint.addParticipant": "เพิ่มคน",
  "maint.removeParticipant": "ถอดออกจากตั๋ว",
  "maint.searchPerson": "ค้นชื่อ รหัส หรือแผนก",
  "maint.participantAdded": "เพิ่มผู้เกี่ยวข้องแล้ว",
  "maint.participantAddFailed": "เพิ่มผู้เกี่ยวข้องไม่สำเร็จ",
  "maint.participantRemoveFailed": "ถอดผู้เกี่ยวข้องไม่สำเร็จ",
  "maint.receipts": "ใบเสร็จ",
  "maint.addReceipt": "เพิ่มใบเสร็จ",
  "maint.editReceipt": "แก้ไขใบเสร็จ",
  "maint.noReceipts": "ยังไม่มีใบเสร็จในตั๋วนี้",
  "maint.noReceiptNo": "ไม่ระบุเลขที่",
  "maint.noLines": "ไม่มีรายการย่อย",
  "maint.totalMismatch": "ยอดหน้าใบไม่เท่าผลรวมรายการ",
  "maint.linesSum": "ผลรวมรายการ",
  "maint.receiptPhoto": "ภาพใบเสร็จ",
  "maint.receiptNo": "เลขที่ใบเสร็จ",
  "maint.receiptNoHint": "ไม่มีก็เว้นไว้ได้",
  "maint.receiptDate": "วันที่ในใบเสร็จ",
  "maint.receiptTotal": "ยอดรวมหน้าใบ",
  "maint.receiptFiles": "ภาพ/ไฟล์ใบเสร็จ",
  "maint.vat": "ภาษีมูลค่าเพิ่ม",
  "maint.receiptSaved": "บันทึกใบเสร็จแล้ว",
  "maint.receiptSaveFailed": "บันทึกใบเสร็จไม่สำเร็จ",
  "maint.receiptRemoved": "ลบใบเสร็จแล้ว",
  "maint.receiptRemoveFailed": "ลบใบเสร็จไม่สำเร็จ",
  "maint.removeReceiptTitle": "ลบใบเสร็จใบนี้",
  "maint.removeReceiptMessage": "ยอดรวมของตั๋วจะถูกคำนวณใหม่",
  "maint.receiptsLocked": "ตั๋วปิดแล้ว — แก้ใบเสร็จไม่ได้ ถ้าต้องแก้ให้เปิดตั๋วใหม่ก่อน",
  "maint.age": "อายุตั๋ว",
  "maint.newActivity": "มีโพสต์ใหม่",
  "common.unknown": "ไม่ทราบ",

  // ── ADR 0366 รอบสอง — ภาพรวมซ่อมบำรุง · อาการ/ตำแหน่ง ──
  "maint.dashboardTitle": "ภาพรวมซ่อมบำรุง",
  "maint.dashboardSub": "ค่าใช้จ่าย อาการที่เจอบ่อย และจำนวนวันที่รถจอด",
  "maint.lastDays": "{n} วันล่าสุด",
  "maint.statOpen": "ใบที่ค้างอยู่",
  "maint.statOverdue": "เลยกำหนด",
  "maint.statAwaiting": "รอรับเรื่อง",
  "maint.statAwaitingNote": "คำร้องจากคนขับที่ยังไม่มีใครรับ",
  "maint.statClosed": "ตั๋วที่จบในช่วงนี้",
  "maint.statSpend": "ค่าใช้จ่ายในช่วงนี้",
  "maint.statDaysOffRoad": "วันจอดเฉลี่ย",
  "maint.statDaysOffRoadNote": "นานสุด {n} วัน",
  "maint.spendByMonth": "ค่าใช้จ่ายรายเดือน",
  "maint.topSymptoms": "อาการที่เจอบ่อย",
  "maint.byCategory": "แยกตามประเภทงาน",
  "maint.topParts": "อะไหล่ที่ใช้เงินมากสุด",
  "maint.topShops": "อู่ที่ใช้เงินมากสุด",
  "maint.topVehicles": "รถที่ค่าซ่อมสูงสุด",
  "maint.noDataInRange": "ไม่มีข้อมูลในช่วงเวลานี้",
  "maint.noSymptomsYet": "ยังไม่มีใครบันทึกอาการในรายการอะไหล่ — กรอกในใบเสร็จแล้วกราฟนี้จะมีข้อมูล",
  "maint.timesUnit": "ครั้ง",
  "maint.ticketsUnit": "ใบ",
  "maint.chartTableHint": "ตัวเลขของทุกแถวแสดงเป็นข้อความข้างแท่งอยู่แล้ว",
  "maint.symptom": "อาการ",
  "maint.faultToggle": "ระบุอาการ / ตำแหน่งที่เสีย",
  "maint.faultOptional": "ไม่บังคับ",
  "maint.faultHint": "ไม่บังคับ — ใช้ในหน้าภาพรวมซ่อมบำรุง ดูว่าอาการไหนเจอบ่อย และรถเสียซ้ำตรงไหน",
  "maint.symptomsLabel": "อาการที่พบ",
  "maint.pickMany": "เลือกได้หลายข้อ",
  "maint.pickOne": "เลือก 1 ตำแหน่ง",
  "maint.otherChip": "อื่น ๆ",
  "maint.otherSymptomPlaceholder": "พิมพ์อาการ แล้วกด Enter",
  "maint.otherPositionPlaceholder": "พิมพ์ตำแหน่ง แล้วกด Enter",
  "maint.position": "ตำแหน่งที่เสียหาย",
  "maint.openDashboard": "ภาพรวม",
  "maint.statusModification": "ดัดแปลง",

  // ── ADR 0366 — แท็บข้อมูลที่แก้ไขได้ในที่ ──
  "maint.titleRequired": "ต้องมีหัวข้อ",
  "maint.readOnlyNotice": "คุณดูได้อย่างเดียว — การแก้ไขต้องเป็นผู้ดูแลหรือผู้ควบคุมงาน",
  "maint.source": "ที่มาของใบ",

  // ── ADR 0366 — จอใบเสร็จที่อ่านออก ──
  "fleet.addFirstCostItem": "เพิ่มรายการแรก",
  "maint.mismatchExplain": "ยอดหน้าใบ {total} ไม่เท่ายอดที่คำนวณจากรายการและ VAT {lines} (ต่างกัน {diff}) — บันทึกได้ ถ้าเป็นส่วนลดหรือค่าเรียกช่าง",
  "maint.receiptSheetSubtitle": "หนึ่งใบเสร็จ = หนึ่งใบที่ร้านออกให้ · หลายใบต่อหนึ่งตั๋วได้",
  "maint.receiptHeadSection": "ข้อมูลหน้าใบ",
  "maint.receiptTotalsSection": "สรุปยอด",
  "maint.shopPlaceholder": "ชื่ออู่หรือร้านที่ออกใบนี้",
  "maint.receiptTotalHint": "เว้นว่างไว้ = ใช้ยอดที่คำนวณจากรายการและ VAT",
  "maint.vatMode": "ภาษีมูลค่าเพิ่ม (VAT)",
  "maint.vatNone": "ไม่มี VAT",
  "maint.vatExclusive": "ราคายังไม่รวม VAT",
  "maint.vatInclusive": "ราคารวม VAT แล้ว",
  "maint.vatNoneHint": "ร้านไม่ได้จด VAT หรือออกบิลเงินสด — ยอดรวมเท่ากับผลรวมรายการ",
  "maint.vatExclusiveHint": "ราคาในรายการเป็นราคาก่อนภาษี — ระบบบวก VAT ต่อท้ายให้",
  "maint.vatInclusiveHint": "ราคาในรายการรวมภาษีไว้แล้ว — ระบบถอด VAT ออกมาให้เห็นแยก",
  "maint.vatRate": "อัตรา VAT",
  "maint.taxId": "เลขผู้เสียภาษีของร้าน",
  "maint.taxIdHint": "13 หลัก ตามหัวใบกำกับภาษี — ใช้ตอนขอคืนภาษีซื้อ",
  "maint.baseBeforeVat": "ราคาก่อน VAT",
  "maint.vatWithRate": "VAT {rate}%",
  "maint.vatIncludedWithRate": "VAT {rate}% (รวมอยู่ในราคาแล้ว)",
  "maint.vatAutoHint": "เว้นว่าง = คำนวณให้ ({amount}) · พิมพ์ทับได้ถ้าบนใบปัดเศษต่างไป",
  "maint.vatUseComputed": "กลับไปใช้ค่าที่คำนวณ",
  "maint.vatChipExclusive": "+VAT {rate}%",
  "maint.vatChipInclusive": "รวม VAT {rate}%",
  "maint.statTotal": "ยอดรวม {n} ใบ",
  "maint.statVatCount": "จาก {n} ใบที่มี VAT",
  "maint.statNoVat": "ไม่มีใบไหนมี VAT",
  "maint.attachReceiptPhoto": "แนบภาพหรือไฟล์ใบเสร็จ",
  "maint.receiptNotePlaceholder": "เช่น ต่อราคาหน้าร้านได้ 200 บาท",
  "maint.ticketInfoSection": "ข้อมูลตั๋ว",
  "maint.changeVehicleHint": "ย้ายตั๋วไปคันอื่นได้ ประวัติ ใบเสร็จ และเลขที่ตั๋วตามไปด้วย",
  "maint.createCostHint": "ค่าใช้จ่ายไม่ต้องกรอกตอนนี้ — บันทึกใบเสร็จได้ที่หน้าตั๋วเมื่อบิลมาถึง",
  "maint.titlePlaceholder": "เช่น เปลี่ยนผ้าเบรกหลัง",
  "fleet.woCreateFailed": "เปิดใบซ่อมไม่สำเร็จ",
  "maint.ticketHistorySection": "ที่มาและเวลา",
  "maint.dropHint": "ลากไฟล์มาวาง หรือวางรูปจากคลิปบอร์ดได้เลย",
  "maint.today": "วันนี้",
  "maint.yesterday": "เมื่อวาน",
  "maint.tabActivity": "ความเคลื่อนไหว",
  "maint.comment": "ความเห็น",
  "maint.nComments": "{n} ความเห็น",
  "maint.commentPlaceholder": "เขียนความเห็น…",
  "maint.sendComment": "ส่งความเห็น",
  "maint.noActivity": "ยังไม่มีความเคลื่อนไหวในตั๋วนี้",

  // ── ADR 0333 — กล่องอนุมัติการลา + ปลดคนขับ ─────────────────────────
  "common.month.1": "ม.ค.",
  "common.month.2": "ก.พ.",
  "common.month.3": "มี.ค.",
  "common.month.4": "เม.ย.",
  "common.month.5": "พ.ค.",
  "common.month.6": "มิ.ย.",
  "common.month.7": "ก.ค.",
  "common.month.8": "ส.ค.",
  "common.month.9": "ก.ย.",
  "common.month.10": "ต.ค.",
  "common.month.11": "พ.ย.",
  "common.month.12": "ธ.ค.",
  "common.route": "เส้นทาง",
  "jobs.ticket": "ใบงาน",
  "hr.approveLeaveTitle": "อนุมัติคำขอลา",
  "hr.leaveRangeLabel": "ช่วงลา",
  "hr.leaveDaysN": "{n} วัน",
  "hr.leaveBalanceShort": "โควตา",
  "hr.leaveUncapped": "ไม่จำกัด",
  "hr.leaveAttachments": "ไฟล์แนบ",
  "hr.leaveJobsInWindow": "ใบงานในช่วงลา",
  "hr.leaveSelectedOf": "เลือก {n} จาก {total} ใบ",
  "hr.leaveBlockedCount": "ปลดไม่ได้ {n}",
  "hr.leaveNoJobsInWindow": "ช่วงนี้ไม่มีใบงานที่วางแผนไว้",
  "hr.leaveNoJobsToAct": "ไม่มีใบงานที่ต้องจัดการ",
  "hr.leaveSelectAllJobs": "เลือกใบงานที่ปลดได้ทั้งหมด",
  "hr.leaveJobStart": "วันเวลาเริ่มงาน",
  "hr.leaveReleaseDay": "ปลดทั้งวัน",
  "hr.leaveKeepDay": "คืนทั้งวัน",
  "hr.leaveMoreJobs": "+{n} งาน",
  "hr.leavePlate": "ทะเบียน",
  "hr.leaveVehiclesReleasedNote": "เลือกไว้ {tickets} ใบ แต่รถที่จะถูกปล่อยมี {vehicles} คัน — บางใบไม่มีรถ หรือใช้รถคันเดียวกัน",
  "hr.leaveLegendLeaveDay": "วันที่ลา",
  "hr.leaveLegendRelease": "ปลดคนขับ",
  "hr.leaveLegendKeep": "คงคนขับ",
  "hr.leaveLegendLocked": "ปลดไม่ได้",
  "hr.leaveLegendOutside": "นอกช่วงลา",
  "hr.leaveLegendMore": "ดูในตาราง",
  "hr.leaveApproveRelease": "อนุมัติ · ปลดคนและรถ {n} ใบ",
  "hr.leaveApproveNoRelease": "อนุมัติ (ไม่ปลดใบงาน)",
  "hr.leaveApproveFailed": "อนุมัติไม่สำเร็จ",
  "hr.leaveBookingsWarn": "ใบงานที่เลือกมีผู้จองรวม {n} คน — ปลดแล้วผู้โดยสารจะไม่เห็นเที่ยวนี้จนกว่าจะจ่ายคนขับใหม่",
  "hr.leaveHalfDayWarn": "มีใบงานที่ตกในวันลาครึ่งวัน — ระบบไม่รู้ว่า \"ครึ่ง\" คือกี่โมง จึงแสดงงานทั้งวันให้ตรวจเอง",
  "hr.leaveHalfDayTag": "ครึ่งวัน — ตรวจเอง",
  "hr.leaveBookingsOnTicket": "มีผู้จอง {n} คน",
  "hr.leaveTooManyJobs": "ช่วงลานี้มีใบงาน {n} ใบ เกินที่ทำในครั้งเดียวได้ — ใช้หน้าแก้ไขใบงานจำนวนมากแทน",
  "hr.leaveOverdraftAck": "อนุมัติเกินสิทธิ์ {n} วัน",
  "hr.leaveOverdraftNote": "จะถูกบันทึกลงเหตุผลการตัดสินและลงบันทึกตรวจสอบ",
  "hr.leaveConflictsLoadFailed": "โหลดใบงานที่ชนไม่สำเร็จ — ยังไม่รู้ว่ามีงานค้างอยู่กี่ใบ",
  "hr.leaveApproveWithoutRelease": "อนุมัติตอนนี้ได้ แต่จะไม่มีการปลดใบงานใด ๆ",
  "hr.leaveBlock.ACCEPTED": "คนขับกดรับงานแล้ว",
  "hr.leaveBlock.RUNNING": "งานเริ่มวิ่งแล้ว — ต้องยกเลิกงานเอง",
  "hr.leaveBlock.FINISHED": "งานจบไปแล้ว",
  "hr.leaveBlock.PERIOD_LOCKED": "งวดจ่ายเงินปิดแล้ว",
  "hr.leaveBlock.UNKNOWN": "ปลดไม่ได้",
  "toast.leaveApproved": "อนุมัติการลาแล้ว",
  "toast.leaveApprovedReleased": "อนุมัติการลาแล้ว · ปลดคนขับออกจากใบงาน {n} ใบ",
  "hr.leaveFilteredBy": "กรอง: {list}",
  "hr.leaveDaysCount": "จำนวนวันลา",
  "hr.leaveBalanceLeft": "เหลือโควตา",
  "hr.leaveSelectAllPending": "เลือกคำขอที่รออนุมัติทั้งหน้า",
  "hr.leaveBalanceTooltip": "เดิม {before} วัน · ใบนี้ {days} วัน · เหลือ {after} วัน",
  "hr.leaveDayUnit": "วัน",
  "time.today": "วันนี้",
  "time.yesterday": "เมื่อวาน",
  "time.daysAgo": "{n} วันก่อน",
  "time.monthsAgo": "{n} เดือนก่อน",
  "time.yearsAgo": "{n} ปีก่อน",
  "hr.leaveCheckingJobs": "กำลังตรวจใบงาน…",
  "hr.leaveClashCount": "ชนงาน {n} ใบ",
  "hr.leaveClashTooltip": "ดูว่าชนกับงานใบไหนบ้าง",
  "hr.leaveClashDrawer": "ช่วงนี้ถูกจ่ายงานไว้แล้ว {n} ใบ — อนุมัติแล้วต้องจัดคนใหม่",
  "hr.leaveOutsideWindow": "นอกช่วงลา — แสดงให้เห็นภาระรอบข้าง กดไม่ได้",
  // ── ADR 0346/0348 — เอกสารในใบลา ─────────────────────────────────────
  "hr.tabRequest": "คำขอ",
  "hr.tabTimeline": "ลำดับเวลา",
  "hr.timelineEmpty": "ยังไม่มีเหตุการณ์",
  "hr.docsColumn": "เอกสารเพิ่มเติม",
  "hr.tabExtraDocs": "เอกสารเพิ่มเติม",
  "hr.docsAtFiling": "เอกสารแนบ",
  "hr.docsNone": "ยังไม่มีเอกสารส่งตามมาหลังยื่นคำขอ",
  "hr.docsFile": "ไฟล์",
  "hr.docsSentAt": "ส่งเมื่อ",
  "hr.docsReviewState": "การตรวจ",
  "hr.docsBatch": "ชุดที่ {n}",
  "hr.docStatusPending": "รอตรวจสอบ",
  "hr.docStatusApproved": "ผ่าน",
  "hr.docStatusRejected": "ไม่ผ่าน",
  "hr.docsApprove": "ผ่าน",
  "hr.docsReject": "ไม่ผ่าน",
  "hr.docsRejectReasonHint": "บอกคนขับว่าต้องแก้อะไร เช่น ถ่ายเบลอ อ่านไม่ออก",
  "hr.docsNoteLabel": "ความเห็น:",
  "toast.docsApproved": "บันทึกว่าผ่านแล้ว",
  "toast.docsRejected": "บันทึกว่าไม่ผ่านแล้ว",
  "hr.docsNew": "รอตรวจ {n}",
  "hr.moreFiles": "อีก {n} ไฟล์",
  "hr.docsApprovedN": "ผ่าน {n}",
  "hr.docsRejectedN": "ไม่ผ่าน {n}",
  "hr.docsBadgeTooltip": "เปิดแท็บเอกสารเพิ่มเติม",
  "hr.docsFilterAll": "ทุกเอกสาร",
  "hr.docsPendingReview": "รอตรวจ",
  "hr.kpiDocsUnreviewed": "เอกสารเพิ่มเติมรอตรวจ",
  "hr.docsReviewFailed": "บันทึกว่าตรวจแล้วไม่สำเร็จ",
  "hr.tlDocsReviewed": "ตรวจเอกสาร",
  "hr.tlDocsApproved": "เอกสารผ่าน",
  "hr.tlDocsRejected": "เอกสารไม่ผ่าน",
  "hr.tlSubmitted": "ยื่นคำขอ",
  "hr.tlApproved": "อนุมัติ",
  "hr.tlRejected": "ไม่อนุมัติ",
  "hr.tlCancelled": "ยกเลิก",
  "hr.tlAttachmentAdded": "แนบไฟล์",
  "hr.tlAttachmentRemoved": "ถอนไฟล์ออก",

  // ── ADR 0357 — สมุดบัญชีกลาง (finance transaction) ──
  // plan: 4-Archives/plan/tms/0357-finance-txn-first-slice-job-ticket-and-manual.md
  // ⚠️ D5 — **ห้ามใช้คำว่า "สร้างด้วยมือ" บนหน้าจอ** เป็นคำที่ทีมใช้กันเองเพื่อแยกจาก derived
  "nav.financeBills": "บิล",
  "bills.title": "บิล",
  "bills.intro": "ยอดของแต่ละคน แบ่งตามรอบตัดของสัญญา ตัวเลขยังขยับตามรายการที่เข้ามา จนกว่าจะหยิบบิลเข้ารอบจ่าย",
  "bills.kpiDue": "ครบรอบ รอจ่าย",
  "bills.kpiAccumulating": "กำลังสะสม",
  "bills.kpiBox": "รายการสะสม (ยังไม่มีรอบตัด)",
  "bills.kpiBills": "{n} บิล",
  "bills.kpiPeople": "{n} คน",
  "bills.status.accumulating": "กำลังสะสม",
  "bills.status.due": "ครบรอบ รอจ่าย",
  "bills.status.in_batch": "อยู่ในรอบจ่าย",
  "bills.status.paid": "จ่ายแล้ว",
  "bills.filterStatus": "สถานะบิล",
  "bills.filterBox": "รายการสะสม",
  "bills.allContracts": "ทุกสัญญา",
  "bills.searchPlaceholder": "ค้นหาชื่อพนักงาน",
  "bills.openTotal": "ยอดรวมที่ยังไม่จ่าย",
  "bills.window": "{start} – {end}",
  "bills.payout": "จ่าย {date}",
  "bills.billTotal": "ยอดบิลนี้",
  "bills.boxTitle": "รายการสะสม",
  "bills.boxSubtitle": "ยังไม่มีรอบตัด · เก่าสุด {date}",
  "bills.boxHint": "รายการที่ไม่ได้สังกัดสัญญา หรือสัญญายังไม่ตั้งรอบตัด จะรอตรงนี้ หยิบไปจ่ายเมื่อไรก็ถือว่าใช้แล้ว",
  "bills.boxTotal": "ยอดสะสม",
  "bills.unitTrip": "{n} เที่ยว",
  "bills.unitItem": "{n} รายการ",
  "bills.showLines": "ดูรายการ ({n})",
  "bills.carriedFrom": "ยกมาจากรอบ {date}",
  "bills.unassigned": "มีรายการเงิน {n} รายการที่ไม่ระบุพนักงาน จึงไม่อยู่ในบิลของใคร — ตรวจได้ที่หน้ารายการเงิน",
  "bills.emptyNone": "ยังไม่มีรายการเงินของพนักงาน",
  "bills.emptyNoneHint": "ค่าเที่ยวจะเข้ามาเองจากใบงานที่วิ่งจบแล้ว",
  "bills.emptyNoMatch": "ไม่มีบิลที่ตรงกับตัวกรอง",
  "bills.colPerson": "พนักงาน",
  "bills.colDue": "ครบรอบ รอจ่าย",
  "bills.colAccumulating": "กำลังสะสม",
  "bills.colInBatch": "อยู่ในรอบจ่าย",
  "bills.colBox": "รายการสะสม",
  "bills.colOpen": "ยอดรวมที่ยังไม่จ่าย",
  // NIGHT-1023 N2 — the bills table: next step and what else a person has
  "bills.colNext": "ต้องทำต่อ",
  "bills.next.held_all": "พักทั้งคนไว้ — เข้ารอบไม่ได้",
  "bills.next.flagged": "มีปัญหา {n} บิล — ไปเคลียร์",
  "bills.next.stale": "ต้องดูใหม่ {n} บิล",
  "bills.next.unchecked": "ตรวจบิล {n} บิล",
  "bills.next.ready": "พร้อมเข้ารอบจ่าย",
  "bills.next.box": "ดึงรายการสะสมเข้ารอบ",
  "bills.next.in_batch": "อยู่ในรอบจ่ายแล้ว",
  "bills.next.none": "—",
  "bills.extra.checked": "ตรวจแล้ว {done}/{n} บิล",
  "bills.extra.held": "พักไว้ {amount} ({n} รายการ)",
  "bills.extra.inBatch": "อยู่ในรอบจ่าย {amount}",
  "bills.extra.debt": "หนี้ค้าง {amount}",
  "bills.billsCount": "{n} บิล",
  "bills.billsCountWithBox": "{n} บิล + รายการสะสม {m} รายการ",
  "bills.itemsSince": "{n} รายการ · ตั้งแต่ {date}",
  "bills.personNotFound": "ไม่พบบิลของพนักงานคนนี้",
  "bills.billNotFound": "ไม่พบบิลนี้",
  "bills.byDay": "รายวัน",
  "bills.dayItems": "{n} รายการ",
  "bills.searchDoc": "ค้นเลขใบงาน หรือเลขรายการเงิน",
  "bills.searchResults": "ผลค้นหา {n} รายการ",
  "bills.loadMore": "โหลดเพิ่ม ({shown}/{total})",
  "bills.kindAll": "ทุกประเภท",
  "bills.allDaysHint": "กดวันเพื่อดูรายการของวันนั้น",
  "bills.hintDue": "เลยวันตัดแล้ว รอเข้ารอบจ่าย",
  "bills.hintAccumulating": "ยังไม่ถึงวันตัด ยอดยังเพิ่มได้",
  "bills.hintBox": "ไม่มีรอบตัด รอคนหยิบไปจ่าย",
  "bills.searchNoMatch": "ไม่พบเลขนี้ในบิลนี้ ลองพิมพ์เลขใบงาน (JOB-…) หรือเลขรายการเงิน (FTX-…)",
  "bills.backToBills": "กลับไปหน้าบิล",
  "bills.payoutInRound": "จ่าย {date} · รอบ {round}",
  "personTl.searchPlaceholder": "ค้นในบิลของคนนี้ — ชื่อสัญญา เลขสัญญา ยอดเงิน ชนิดเงิน",
  "personTl.statusLabel": "สถานะบิล",
  "personTl.status.all": "ทั้งหมด",
  "personTl.status.over": "เลยกำหนด",
  "personTl.status.due": "รอจ่าย",
  "personTl.status.accumulating": "สะสม",
  "personTl.status.in_batch": "ในรอบ",
  "personTl.status.paid": "จ่ายแล้ว",
  "personTl.allContracts": "ทุกสัญญา ({n})",
  "personTl.monthly": "เงินเดือน",
  "personTl.from": "ทำงานตั้งแต่",
  "personTl.to": "ถึง",
  "personTl.found": "พบ {n} จาก {m} บิล",
  "personTl.clear": "ล้างตัวกรอง",
  "personTl.noMatch": "ไม่พบบิลที่ตรงกับตัวกรอง",
  "personTl.stripTitle": "วันจ่ายของคนนี้ — กดเพื่อเลื่อนไปที่บิล",
  "personTl.payOn": "จ่าย {date}",
  "personTl.inDays": "อีก {n} วัน",
  "personTl.payToday": "จ่ายวันนี้",
  "personTl.overDays": "เลยวันจ่ายมา {n} วัน",
  "personTl.dayTotal": "รวม",
  "personTl.nBills": "{n} บิล",
  "personTl.nItems": "{n} รายการ",
  "personTl.unfold": "คลี่ดู",
  "personTl.fold": "พับ",
  "personTl.todayLine": "วันนี้ {date}",
  "personTl.todayShort": "วันนี้ {date}",
  "personTl.nodate": "ยังไม่มีวันจ่าย · รายการสะสม",
  "personTl.paidTitle": "จ่ายแล้ว — ย้อนหลังทีละเดือน",
  "personTl.older": "ดูเก่ากว่านี้ ({n} เดือน)",
  "personTl.checkBill": "ตรวจบิลนี้",
  "personTl.checkDay": "ตรวจวันจ่ายนี้",
  "personTl.checkPerson": "ตรวจบิลของคนนี้",
  "bills.openTicket": "เปิดใบงาน",
  "bills.paidBills": "จ่ายแล้ว {n} บิล",
  "bills.move.title": "ย้ายเข้าบิลสัญญา",
  "bills.move.from": "จากรายการสะสมของ",
  "bills.move.to": "ย้ายเข้าสัญญา",
  "bills.move.cutoffDay": "ตัดรอบทุกวันที่ {d}",
  "bills.move.cutoffEnd": "ตัดรอบสิ้นเดือน",
  "bills.move.landHint": "แต่ละรายการจะเข้าบิลของรอบที่ครอบวันที่ของมันเอง ถ้าบิลรอบนั้นเข้ารอบจ่ายไปแล้ว จะไปอยู่บิลรอบถัดไป",
  "bills.move.note": "หมายเหตุ",
  "bills.move.notePlaceholder": "เช่น ลืมผูกสัญญาตอนสร้างใบงาน",
  "bills.move.confirm": "ย้าย {n} รายการ",
  "bills.move.failed": "ทำรายการไม่สำเร็จ ลองใหม่อีกครั้ง",
  "bills.move.done": "ย้าย {n} รายการเข้า {contract} แล้ว",
  "bills.move.panel": "รายการตรงนี้ยังไม่อยู่ในบิลไหน ติ๊กเลือกวันหรือรายการเพื่อย้ายเข้าบิลสัญญาของ {name} หรือย้ายทั้งหมดในครั้งเดียว",
  "bills.move.noTargets": "ยังย้ายเข้าบิลไม่ได้ เพราะสัญญาของ {name} ยังไม่ได้ตั้งรอบตัด",
  "bills.move.goContracts": "ไปตั้งรอบตัดที่หน้าสัญญา",
  "bills.move.all": "ย้ายทั้งหมด ({n} รายการ)",
  "bills.move.pickHint": "ติ๊กวันหรือรายการเพื่อย้าย · กดวันเพื่อดูรายการ",
  "bills.move.picked": "เลือก {n} รายการ",
  "bills.move.clear": "ล้าง",
  "bills.move.action": "ย้ายเข้าบิลสัญญา",
  "bills.move.pickRow": "เลือก {doc}",
  "bills.move.pickDay": "เลือกทั้งวัน {date}",
  "bills.move.movedBy": "ย้ายมาจากรายการสะสม โดย {name} · {date}",
  "bills.return.action": "คืนไปรายการสะสม",
  "bills.return.ask": "คืนรายการนี้?",
  "bills.return.yes": "คืน",
  "bills.return.done": "คืนกลับไปรายการสะสมแล้ว",
  "nav.financePayBatches": "รอบจ่าย",
  "payBatches.title": "รอบจ่าย",
  "payBatches.intro": "หยิบบิลที่ครบรอบมารวมเป็นรอบเดียว ตรวจรายคน แล้วแช่แข็งก่อนอนุมัติและโอน คนหนึ่งคนโอนครั้งเดียวต่อรอบ ต่อให้มีหลายบิล",
  "payBatches.create": "สร้างรอบจ่าย",
  "payBatches.createHint": "รอบใหม่เริ่มเป็นร่าง หยิบบิลเข้าออกได้จนกว่าจะแช่แข็ง",
  "payBatches.payoutDate": "วันที่จ่าย",
  "payBatches.name": "ชื่อรอบ",
  "payBatches.defaultName": "รอบจ่าย {date}",
  "payBatches.createConfirm": "สร้างรอบ",
  "payBatches.empty": "ยังไม่มีรอบจ่าย",
  "payBatches.emptyHint": "สร้างรอบแรก แล้วหยิบบิลที่ครบรอบเข้ามา",
  "payBatches.payOn": "จ่าย {date}",
  "payBatches.peopleBills": "{people} คน · {bills} บิล",
  // NIGHT-1023 N2 — the rounds table
  "payBatches.col.round": "รอบจ่าย",
  "payBatches.col.payOn": "วันจ่าย",
  "payBatches.col.people": "คน · บิล",
  "payBatches.col.check": "ตรวจบิล",
  "payBatches.col.approval": "อนุมัติ",
  "payBatches.col.total": "ยอดรวม",
  "payBatches.col.next": "ต้องทำต่อ",
  "payBatches.createdBy": "สร้างโดย {name}",
  "payBatches.check.checked": "ตรวจแล้ว {done}/{n} คน",
  "payBatches.check.flagged": "มีปัญหา {n}",
  "payBatches.check.unchecked": "ยังไม่ตรวจ {n}",
  "payBatches.check.negative": "ยอดติดลบ {n} คน",
  "payBatches.approval.step": "ขั้น {done}/{of}",
  "payBatches.approval.done": "อนุมัติครบ",
  "payBatches.file.taken": "ไฟล์ธนาคาร ดาวน์โหลด {date}",
  "payBatches.file.notYet": "ยังไม่ดาวน์โหลดไฟล์ธนาคาร",
  "payBatches.next.pick": "เลือกบิลเข้ารอบ",
  "payBatches.next.freeze": "ล็อกยอด ส่งอนุมัติ",
  "payBatches.next.fix_negative": "ยอดติดลบ {n} คน — แก้ก่อน",
  "payBatches.next.approve": "รออนุมัติ — เปิดตรวจ",
  "payBatches.next.export": "ดาวน์โหลดไฟล์ธนาคาร",
  "payBatches.next.confirm_paid": "ยืนยันว่าโอนแล้ว",
  "payBatches.next.done": "เปิดดูรอบ",
  "payBatches.status.draft": "ร่าง",
  "finSettings.section.approval": "ขั้นอนุมัติรอบจ่าย",
  "finSettings.section.fee": "ค่าดำเนินการ (ค่าคอม)",
  "approval.title": "ขั้นอนุมัติรอบจ่าย",
  "approval.lead": "รอบจ่ายที่แช่แข็งแล้วต้องผ่านกี่ขั้นก่อนออกไฟล์ได้ แต่ละขั้นมีชื่อและคนกด เดินทีละขั้นตามลำดับ ตีกลับเมื่อไรก็เริ่มขั้นแรกใหม่",
  "approval.oneStep": "ยังไม่ได้ตั้งขั้น — รอบจ่ายอนุมัติขั้นเดียว ผู้อนุมัติรอบคนใดก็กดได้ (แบบเดิม)",
  "approval.name": "ชื่อขั้น",
  "approval.namePh": "เช่น หัวหน้าฝ่ายการเงิน",
  "approval.up": "เลื่อนขึ้น",
  "approval.down": "เลื่อนลง",
  "approval.remove": "ลบขั้นนี้",
  "approval.removePerson": "เอา {name} ออก",
  "approval.addPerson": "เพิ่มคนกดขั้นนี้",
  "approval.addStep": "เพิ่มขั้น",
  "approval.save": "บันทึก",
  "approval.saved": "บันทึกขั้นอนุมัติแล้ว — ใช้กับรอบที่แช่แข็งจากนี้ไป",
  "approval.saveFailed": "บันทึกขั้นอนุมัติไม่สำเร็จ",
  "approval.note": "รอบที่รออนุมัติอยู่แล้วใช้ขั้นตามตอนที่แช่แข็ง การแก้ที่นี่มีผลกับรอบที่แช่แข็งครั้งต่อไป",
  "approval.stepOf": "ขั้น {n}/{of}",
  "approval.doneBy": "อนุมัติโดย {name} · {date}",
  "approval.waitingFor": "รอ {names} กด",
  "approval.waitingAnyone": "รอผู้อนุมัติรอบกด (ใครก็ได้)",
  "approval.anyone": "ผู้อนุมัติรอบคนใดก็ได้",
  "approval.stepDone": "อนุมัติขั้น {n}/{of} แล้ว — รอขั้นถัดไป",
  "review.approveStep": "อนุมัติขั้น {n}/{of} · {total}",
  "payBatches.accounting.button": "ไฟล์บัญชี",
  "payBatches.accounting.file": "ไฟล์บัญชี",
  "payBatches.accounting.sheet": "ไฟล์บัญชี",
  "payBatches.accounting.failed": "ออกไฟล์บัญชีไม่สำเร็จ",
  "payBatches.accounting.afterApprove": "ออกไฟล์บัญชีได้เมื่อรอบอนุมัติแล้ว",
  "payBatches.status.frozen": "แช่แข็งแล้ว",
  "payBatches.status.approved": "อนุมัติแล้ว",
  "payBatches.status.paid": "จ่ายแล้ว",
  "payBatches.stepsLabel": "ขั้นตอนของรอบจ่าย",
  "payBatches.step1": "หยิบบิลและตรวจ",
  "payBatches.step2": "แช่แข็ง",
  "payBatches.step3": "อนุมัติ",
  "payBatches.step4": "ออกไฟล์และจ่าย",
  "payBatches.boxAll": "ทั้งหมดที่ค้างอยู่",
  "payBatches.negativeOne": "ยอดติดลบ — ตอนแช่แข็งรอบนี้จ่าย 0 และยกส่วนที่ขาดไปหักในบิลถัดไป",
  "payBatches.removeBill": "เอาบิลนี้ออกจากรอบ",
  "payBatches.pickTitle": "หยิบบิลเข้ารอบ",
  "payBatches.pickSummary": "บิลที่ครบรอบแล้วและยังไม่อยู่ในรอบไหน {n} บิล",
  "payBatches.boxWaiting": "{n} คนมีรายการสะสมค้าง รวม {total} หยิบเข้ารอบนี้ได้",
  "payBatches.nothingToPick": "ตอนนี้ไม่มีบิลที่หยิบได้ บิลของสัญญาจะหยิบได้เมื่อเลยวันตัดแล้ว",
  "payBatches.pickLoadFailed": "โหลดรายการบิลไม่สำเร็จ — ยังไม่รู้ว่ามีบิลให้หยิบหรือไม่ ลองใหม่อีกครั้ง",
  "payBatches.reloadFailed": "บันทึกแล้ว แต่โหลดตัวเลขของรอบใหม่ไม่สำเร็จ — กดรีเฟรชหน้าเพื่อดูยอดล่าสุด",
  "payBatches.pickedN": "เลือก {n} บิล",
  "payBatches.addPicked": "เพิ่มเข้ารอบ",
  "payBatches.added": "หยิบเข้ารอบแล้ว {n} บิล",
  "payBatches.removePicked": "เอาออกจากรอบ",
  "payBatches.removed": "เอาออกจากรอบแล้ว {n} บิล",
  "payBatches.edit.tabs": "สลับระหว่างบิลที่หยิบได้กับบิลในรอบ",
  "payBatches.edit.tabPick": "หยิบได้ {n} บิล",
  "payBatches.edit.tabRound": "ในรอบ {n} คน",
  "payBatches.edit.behind": "รายการอาจยังไม่ล่าสุด",
  "payBatches.edit.retry": "ลองใหม่",
  "payBatches.edit.rereadFailed": "บันทึกแล้ว แต่อ่านรายการใหม่ไม่สำเร็จ — กด “ลองใหม่” ที่หัวรายการ",
  "payBatches.edit.elsewhere": "รวมหน้าอื่น {n} บิล",
  "payBatches.edit.recent": "เพิ่งเอาออกจากรอบ · {n} บิล",
  "payBatches.edit.recentAll": "เลือกทุกบิลที่เพิ่งเอาออกจากรอบ",
  "payBatches.edit.recentHide": "ซ่อน",
  "payBatches.edit.refusedOne": "หยิบ “{what}” ไม่ได้: {why} · อ่านรายการใหม่แล้ว เลือกใหม่อีกครั้ง",
  "payBatches.edit.refusedSome": "{why} · อ่านรายการใหม่แล้ว ลองอีกครั้ง",
  "payBatches.edit.selectAll": "เลือกทั้งหมด",
  "payBatches.edit.selectAllMatch": "เลือกทั้งหมดที่ค้นเจอ",
  "payBatches.edit.countBills": "{n} บิล",
  "payBatches.edit.masterPick": "เลือกทุกบิลที่หยิบได้ (ทุกหน้า)",
  "payBatches.edit.masterRound": "เลือกทุกบิลในรอบ (ทุกหน้า)",
  "payBatches.edit.person": "เลือกทุกบิลของ {name}",
  "payBatches.edit.bill": "เลือกบิล {what}",
  "payBatches.edit.clear": "ยกเลิกที่เลือก",
  "payBatches.edit.refreshing": "กำลังอัปเดตรายการ…",
  "payBatches.notFound": "ไม่พบรอบจ่ายนี้",
  "payBatches.totalLabel": "ยอดโอนรวม",
  "payBatches.frozenNote": "แช่แข็งเมื่อ {when} ตัวเลขนิ่งแล้ว รายการที่เข้ามาทีหลังจะไปอยู่บิลรอบถัดไป",
  "payBatches.negativeWarn": "มี {n} คนที่ยอดรวมติดลบ — ตอนแช่แข็ง รอบนี้จ่ายเขา 0 และยกส่วนที่ขาดไปหักในบิลถัดไปของเขา",
  "payBatches.missingWarn": "บิล {n} ใบไม่มีรายการแล้ว (อาจถูกย้ายออกไป) ต้องเอาออกก่อนแช่แข็ง",
  "payBatches.missingFix": "เอาออก",
  "payBatches.freeze": "แช่แข็งรอบนี้",
  "payBatches.freezeHint": "ตรวจรายคนให้เรียบร้อยก่อน หลังแช่แข็งตัวเลขจะไม่ขยับ",
  "payBatches.freezeEmpty": "หยิบบิลเข้ารอบก่อน",
  "payBatches.delete": "ลบรอบร่าง",
  "payBatches.unfreeze": "ตีกลับเป็นร่าง",
  "payBatches.nextApprove": "ขั้นถัดไปคืออนุมัติ",
  "payBatches.inBatch": "ในรอบนี้ {n} คน",
  "payBatches.oneTransfer": "หนึ่งคน = โอนครั้งเดียว",
  "payBatches.noneYet": "ยังไม่มีบิลในรอบนี้ เลือกบิลจากรายการ “หยิบบิลเข้ารอบ” แล้วกดเพิ่มเข้ารอบ",
  "payBatches.history": "ประวัติ",
  "payBatches.event.create": "สร้างรอบ",
  "payBatches.event.add": "หยิบเพิ่ม {n} บิล",
  "payBatches.event.remove": "เอาออก {n} บิล",
  "payBatches.event.freeze": "แช่แข็ง",
  "payBatches.event.unfreeze": "ตีกลับเป็นร่าง",
  "payBatches.event.rename": "เปลี่ยนชื่อ",
  "payBatches.freezeTitle": "แช่แข็งรอบนี้?",
  "payBatches.freezeBody1": "{people} คน ยอดโอนรวม {total} จะถูกล็อกไว้ตามนี้",
  "payBatches.freezeBody2": "รายการที่เข้ามาหลังจากนี้จะไปอยู่บิลรอบถัดไป ถ้าต้องแก้ ให้ตีกลับเป็นร่าง บิลที่หยิบไว้จะยังอยู่ครบ",
  "payBatches.frozenToast": "แช่แข็งรอบแล้ว",
  "payBatches.unfreezeTitle": "ตีกลับเป็นร่าง?",
  "payBatches.unfreezeBody": "ตัวเลขจะกลับมาคำนวณสดอีกครั้ง บิลที่หยิบไว้ยังอยู่ครบ และรายการที่มาช้าจะกลับเข้าบิลของรอบตัวเอง",
  "payBatches.reason": "เหตุผล",
  "payBatches.reasonPlaceholder": "เช่น ยอดคนขับ 2 คนไม่ตรงกับใบงาน",
  "payBatches.unfrozenToast": "ตีกลับเป็นร่างแล้ว",
  "payBatches.deleteTitle": "ลบรอบร่างนี้?",
  "payBatches.deleteBody": "บิลที่หยิบไว้จะกลับไปรอหยิบเข้ารอบอื่น รายการเงินไม่ถูกแตะ",
  "payBatches.methodLabel": "วิธีจ่ายรอบนี้",
  "payBatches.methodOpt.bank_transfer": "โอนเข้าบัญชี",
  "payBatches.methodOpt.cheque": "เช็ค",
  "payBatches.methodOpt.cash": "เงินสด",
  "hr.promptpay": "พร้อมเพย์",
  "hr.promptpayPh": "เบอร์มือถือ 10 หลัก หรือเลขบัตรประชาชน 13 หลัก",
  "hr.promptpayBad": "พร้อมเพย์ต้องเป็นเบอร์มือถือ 10 หลัก หรือเลขบัตรประชาชน 13 หลัก",
  "hr.promptpayNeeded": "รับเงินทางพร้อมเพย์ ต้องใส่เลขพร้อมเพย์",
  "payBatches.methodOpt.promptpay": "พร้อมเพย์",
  "payBatches.method.promptpay": "พร้อมเพย์",
  "payBatches.noPromptPay": "ยังไม่มีเลขพร้อมเพย์ — ใส่ที่หน้าพนักงาน",
  "payBatches.methodChanged": "รอบนี้ไม่ใช่วิธีปกติของคนนี้",
  "payBatches.methodAll": "วิธีจ่ายของทุกคนในรอบนี้",
  "payBatches.methodPick": "เลือกวิธี…",
  "payBatches.methodSaved": "ตั้งวิธีจ่าย {n} คนเป็น{method}แล้ว",
  "payBatches.event.method": "เปลี่ยนวิธีจ่าย {n} คน",
  "payBatches.event.hold_person": "พักคนออกจากรอบ",
  // night 2026-10-14 — the round's safety catches (W1 W2 W3 W4 W12 W17)
  "payBatches.needFile.one": "ดาวน์โหลดไฟล์ {file} ก่อน แล้วจึงยืนยันว่าโอนแล้ว",
  "payBatches.needFile.both": "ดาวน์โหลดไฟล์ {a} และ {b} ก่อน แล้วจึงยืนยันว่าโอนแล้ว",
  "payBatches.needFile.any": "ดาวน์โหลดไฟล์จ่ายก่อน แล้วจึงยืนยันว่าโอนแล้ว",
  "payBatches.sendBackSentWarn": "ไฟล์ธนาคารของรอบนี้ดาวน์โหลดไปแล้วหลังอนุมัติ ถ้าโอนตามไฟล์นั้นไปแล้ว อย่าตีกลับ ให้กดยืนยันว่าโอนแล้วแทน",
  "payBatches.sendBackSentAck": "ไฟล์ธนาคารออกไปแล้ว — ยืนยันว่ายังไม่ได้โอนเงินตามไฟล์นี้",
  "payBatches.payeeAfterApproval": "บัญชีของ {names} เปลี่ยนหลังอนุมัติ — ตีกลับแล้วอนุมัติใหม่ก่อนออกไฟล์",
  "payBatches.cancelledUnpaid": "ใบงานถูกยกเลิกหลังแช่แข็ง {n} ใบ — ตีกลับรอบ ระบบจะเอาออกให้",
  "payBatches.cancelledPaid": "ยกเลิกหลังแช่แข็ง {n} ใบ — ระบบหักคืนในบิลถัดไปแล้ว",
  "payBatches.failed.action": "โอนไม่สำเร็จ",
  "payBatches.failed.title": "โอนให้ {name} ไม่สำเร็จ",
  "payBatches.failed.body": "ยอด {amount} ของรอบนี้จะย้ายไปบิลถัดไปของ {name} เพื่อจ่ายในรอบหน้า ระบบบันทึกให้เอง",
  "payBatches.failed.reason": "เหตุผล",
  "payBatches.failed.reasonPlaceholder": "เช่น ธนาคารตีกลับ บัญชีปิดแล้ว",
  "payBatches.failed.confirm": "บันทึกว่าโอนไม่สำเร็จ",
  "payBatches.failed.line": "โอนไม่สำเร็จ — ยอดย้ายไปรอบถัดไป",
  "payBatches.failed.toast": "บันทึกแล้ว ยอดย้ายไปบิลถัดไป",
  "payBatches.event.approve_step": "อนุมัติหนึ่งขั้น",
  "payBatches.event.transfer_failed": "โอนไม่สำเร็จ ยอดย้ายไปบิลถัดไป",
  "payBatches.event.approval_reset": "ยอดรอบเปลี่ยน — เริ่มอนุมัติใหม่ที่ขั้น 1",
  "payBatches.event.sentAck": "ผู้ตีกลับยืนยันว่ายังไม่ได้โอนตามไฟล์ที่ออกไป",
  "payBatches.err.carryElsewhere": "ยอดติดลบที่ยกไปอยู่ในรอบ {name} แล้ว — ตีกลับรอบ {name} ก่อน",
  "payBatches.err.carryElsewherePaid": "ยอดติดลบที่ยกไปถูกหักในรอบ {name} ที่จ่ายแล้ว — ตีกลับรอบนี้ไม่ได้",
  "slip.title": "สลิปเงินได้",
  "slip.to": "ผู้รับ",
  "slip.round": "รอบจ่าย",
  "slip.paidOn": "วันจ่าย",
  "slip.notPaid": "รอบนี้ยังไม่ได้จ่าย — ตัวเลขเป็นยอดที่แช่แข็งไว้",
  "slip.paidBy": "จ่ายโดย",
  "slip.trips": "ค่าเที่ยว",
  "slip.noContract": "งานนอกสัญญา",
  "slip.tripLine": "{n} เที่ยว × {amount}",
  "slip.income": "รายได้อื่น",
  "slip.deductions": "รายการหัก",
  "slip.items": "{n} รายการ",
  // plan/distance-pay audit — a ticket's own +/− lines, out of the trip fare
  "slip.ticketLines": "รายการบนใบงาน",
  "slip.ticketLine.distance": "ค่าตอบแทนตามระยะ",
  "slip.ticketLine.extra": "เงินเพิ่มอื่นบนใบงาน",
  "slip.ticketLine.deduct": "หักบนใบงาน",
  "slip.ticketLineKm": "{n} ใบงาน · {km} กม.",
  "ticketParts.fare": "ค่าเที่ยว",
  "ticketParts.distance": "ค่าตามระยะ {km} กม. × {rate}",
  "ticketParts.extra": "เงินเพิ่มบนใบงาน",
  "ticketParts.deduct": "หักบนใบงาน",
  "slip.gross": "รวมรายได้",
  "slip.deducted": "รวมรายการหัก",
  "slip.net": "รับสุทธิ",
  "slip.print": "บันทึกเป็น PDF",
  "slip.notFound": "ไม่พบสลิปนี้",
  "slip.open": "สลิป",
  "pending.title": "รอมีผล",
  "pending.lead": "ทุกค่าที่ตั้งไว้ให้เริ่มวันหลัง เรียงจากวันที่ใกล้สุด — ตั้งค่าบริษัท ประเภทการจ้าง และการเปลี่ยนสถานะพนักงานยกเลิกได้ที่นี่ก่อนถึงวัน ส่วนเงินเดือน อัตราค่าเที่ยว และวันลาออก แก้ที่หน้าต้นทาง",
  "pending.empty": "ไม่มีค่าที่รอมีผล",
  "pending.from": "เริ่ม {day}",
  "pending.kind.pay_basis": "ประเภทการจ้าง",
  "pending.kind.home_site": "สัญญาหลัก",
  "pending.kind.salary": "เงินเดือนฐาน",
  "pending.kind.trip_rate": "อัตราค่าเที่ยว",
  "pending.kind.leaving": "วันลาออก",
  "pending.change": "{from} → {to}",
  "pending.becomes": "เป็น {value}",
  "pending.leavingNote": "ปิดยอดก่อนจ่ายรอบสุดท้าย",
  "pending.kind.employment": "สถานะพนักงาน",
  "pending.kind.relation": "ลูกจ้างหรือผู้รับจ้าง",
  "pending.kind.daily_wage": "ค่าแรงรายวัน",
  "pending.kind.pay_schedule": "รอบจ่ายรายคน",
  "pending.kind.sso_status": "ประกันสังคม",
  "pending.kind.payee": "บัญชีรับเงิน",
  "pending.kind.role": "สิทธิ์ในระบบ",
  "pending.kind.employee_type": "ประเภทพนักงาน",
  "pending.kind.operating_fee": "ค่าดำเนินการ",
  "pending.sso.in": "อยู่ในประกันสังคม",
  "pending.sso.out": "ออกจากประกันสังคม",
  "pending.fee.notSet": "ไม่ได้ตั้ง (ใช้ขั้นถัดไป)",
  "pending.payScheduleNote": "ตั้งรอบจ่ายเฉพาะคนนี้ไว้ ดูรายละเอียดที่หน้าคน",
  "pending.atTime": "เวลา {time}",
  "pending.by": "ตั้งโดย {name}",
  "pending.sched.to": "เปลี่ยนเป็น {value}",
  "pending.sched.from": "จากเดิม {value}",
  "pending.open": "เปิดดู",
  "pending.editThere": "แก้ที่ต้นทาง",
  "pending.cancel": "ยกเลิก",
  "pending.cancelTitle": "ยกเลิกค่าที่รอมีผล",
  "pending.cancelBody": "ยกเลิก{what}ที่จะเป็น {value} ตั้งแต่ {day} — ค่าปัจจุบันจะใช้ต่อไป",
  "pending.cancelled": "ยกเลิกแล้ว",
  "pending.cancelFailed": "ยกเลิกไม่สำเร็จ",
  "finHome.pendingTitle": "รอมีผล",
  "finHome.pendingSub": "{n} ค่าที่ตั้งไว้ให้เริ่มวันหลัง",
  "finHome.pendingAll": "ดูทั้งหมด",
  "breakdown.label": "มองรอบนี้แบบ",
  "breakdown.tab.people": "ตามคน",
  "breakdown.tab.contract": "สัญญา › สาย",
  "breakdown.tab.day": "ตามวัน",
  "breakdown.tab.item": "ชนิดรายการ",
  "breakdown.tab.debt": "หนี้",
  "breakdown.noDebts": "รอบนี้ไม่มีงวดหนี้",
  "breakdown.debt": "หนี้",
  "breakdown.seqs": "งวดที่ {seqs}",
  "breakdown.seqsOf": "งวดที่ {seqs} จาก {of}",
  "breakdown.openDebt": "เปิดหน้าหนี้นี้",
  "breakdown.draftNote": "รอบร่าง: ตัวเลขยังขยับตามบิลที่หยิบ ค่าดำเนินการ ภาษีหัก ณ ที่จ่าย และค่าโอน จะเข้ามาตอนแช่แข็ง",
  "breakdown.counts": "{rows} รายการ · {people} คน",
  "breakdown.noContract": "ไม่อยู่ในสัญญา",
  "breakdown.noRoute": "ไม่ระบุสาย",
  "breakdown.more": "และอีก {n} รายการ",
  "overview.title": "ภาพรวมการเงิน",
  "overview.lead": "เงินที่บริษัทจ่ายออกและรับเข้า รายเดือน 6 เดือนล่าสุด และเดือนที่เลือกแยกตามชนิดรายการ · ประเภทพนักงาน · สัญญา (ไม่นับรายการที่ยกเลิก)",
  "overview.trend": "แนวโน้มรายเดือน",
  "overview.out": "จ่ายออก",
  "overview.in": "รับเข้า",
  "overview.byItem": "ตามชนิดรายการ",
  "overview.byType": "ตามประเภทพนักงาน",
  "overview.byContract": "ตามสัญญา",
  "overview.what": "รายการ",
  "overview.rows": "{n} รายการ",
  "overview.none": "ไม่มีรายการในเดือนนี้",
  "overview.noContract": "ไม่อยู่ในสัญญา",
  "overview.type.none": "ไม่ผูกกับคน (รายรับจากลูกค้า)",
  "overview.type.other": "ประเภทอื่น",
  "finHome.overview": "ภาพรวม",
  "finHome.todo.requests": "คำร้องรอรับเรื่อง {n} เรื่อง",
  "finHome.todo.requestsDetail": "คำร้องจากแอปที่ยังไม่มีใครรับเรื่อง — คนยื่นเห็นสถานะในแอป",
  "finHome.act.requests": "เปิดคำร้อง",
  "finHome.bulkTripPay": "แก้ค่าเที่ยวหลายใบ",
  "finHome.bulkTripPayHint": "เลือกใบงานในหน้าใบงาน แล้วกดแก้ไขหลายรายการ › ค่าเที่ยว ตั้งเป็น / เพิ่ม / ลด — ยอดเขียนลงใบงาน",
  "payBatches.find.placeholder": "ค้นชื่อหรือรหัสคนขับ",
  "payBatches.find.show": "แสดง",
  "payBatches.find.all": "ทั้งหมด {n}",
  "payBatches.find.attention": "ต้องดู {n}",
  "payBatches.find.none": "ไม่พบคนที่ตรงกับที่ค้น",
  "payBatches.find.range": "{from}–{to} จาก {n} คน",
  "payBatches.method.cash": "จ่ายเงินสด",
  "payBatches.method.cheque": "จ่ายเช็ค",
  "payBatches.noAccount": "ยังไม่มีเลขบัญชี เพิ่มที่หน้าข้อมูลพนักงาน",
  "payBatches.exported": "ออกไฟล์จ่ายแล้ว {n} คน",
  "payBatches.paidNote": "ยืนยันว่าโอนแล้วเมื่อ {when} รอบนี้ปิดแล้ว แก้ไขไม่ได้อีก",
  "payBatches.missingBankWarn": "มี {n} คนที่ยังไม่มีเลขบัญชี ออกไฟล์จ่ายไม่ได้จนกว่าจะเพิ่มที่หน้าข้อมูลพนักงาน",
  "payBatches.approve": "อนุมัติ",
  "payBatches.download": "ดาวน์โหลดไฟล์จ่าย",
  "payBatches.downloadAgain": "ดาวน์โหลดไฟล์อีกครั้ง",
  "payBatches.markPaid": "ยืนยันว่าโอนแล้ว",
  "payBatches.approveBlocked": "มีคนยอดติดลบ ตีกลับเป็นร่างแล้วเอาบิลของเขาออกก่อนจึงจะอนุมัติได้",
  "payBatches.approveHint": "ตรวจรายคนเรียบร้อยแล้ว กดอนุมัติเพื่อไปขั้นออกไฟล์จ่าย",
  "payBatches.paidHint": "โอนตามไฟล์เสร็จแล้ว กดยืนยันเพื่อปิดรอบ",
  "payBatches.downloadHint": "ดาวน์โหลดไฟล์ด้านบนไปโอนเงินก่อน ทุกครั้งที่ดาวน์โหลดจะถูกบันทึกในประวัติ",
  "payBatches.files.title": "ไฟล์สำหรับธนาคาร (K-Cash Connect Plus)",
  "payBatches.files.all": "ไฟล์รวมทุกคน",
  "payBatches.files.kbank": "บัญชีกสิกรไทย — KBank Payroll",
  "payBatches.files.smart": "ธนาคารอื่น — SMART Payroll",
  "payBatches.files.manual": "จ่ายเอง",
  "payBatches.files.manualWhat": "เงินสด เช็ค หรือคนที่โอนผ่านไฟล์ธนาคารไม่ได้ (ดูเหตุผลในรายชื่อ) — รายชื่อนี้ไม่ต้องอัปโหลด",
  "payBatches.files.people": "{n} คน",
  "payBatches.files.fee": "ค่าธรรมเนียมประมาณ {fee}",
  "payBatches.files.kbankWhen": "อัปโหลดได้ถึงวันจ่าย ก่อน {time} น.",
  "payBatches.files.smartWhen": "ต้องอัปโหลดและอนุมัติในเว็บธนาคารภายใน {date} ก่อน {time} น.",
  "payBatches.files.smartLate": "เลยกำหนดส่งแล้ว ({date} {time} น.) — เลื่อนวันจ่าย หรือโอนกลุ่มนี้ด้วยวิธีอื่น",
  "payBatches.files.smartWeekend": "วันจ่ายตรงกับวันเสาร์หรืออาทิตย์ แต่ธนาคารโอนต่างธนาคารแบบนี้เฉพาะวันทำการ — เลื่อนวันจ่าย",
  "payBatches.files.smartCheck": "{n} คนต้องตรวจรหัสสาขาหรือเลขบัญชีก่อน — ดูคอลัมน์ «ต้องตรวจ» ในไฟล์",
  "payBatches.files.download": "ดาวน์โหลด",
  "payBatches.files.downloadList": "ดาวน์โหลดรายชื่อ",
  "payBatches.files.paidCopy": "ไฟล์ของรอบที่จ่ายแล้ว ดาวน์โหลดซ้ำเพื่อเก็บหรือตรวจทานเท่านั้น",
  "finance.err.bad_debit_account": "เลขบัญชีกสิกรไทยต้องมี 10 หลัก",
  "payBatches.auditLink": "ดูประวัติทั้งหมดในบันทึกการใช้งาน (ใคร · เมื่อไร · จากที่ไหน)",
  "payBatches.exportEvent": "{part} · เงินเข้า {date}",
  "payBatches.files.done": "ออกไฟล์แล้ว · เงินเข้า {date} · โดย {who}",
  "payBatches.files.smartLateOpen": "ถ้าจ่ายวันที่ของรอบ ไฟล์นี้ส่งไม่ทันแล้ว (กำหนด {date} {time} น.) — เลือกวันใหม่ตอนออกไฟล์",
  "payBatches.payeeChanged": "มีการแก้ข้อมูลบัญชีรับเงินหลังแช่แข็งรอบ {n} ครั้ง — ตรวจก่อนอนุมัติและก่อนโอน",
  "payBatches.payeeChangedRow": "{when} โดย {who}: {what}",
  "payBatches.x.title": "ออกไฟล์จ่าย",
  "payBatches.x.open": "ออกไฟล์จ่าย",
  "payBatches.x.openFix": "ออกไฟล์จ่าย (มีข้อมูลต้องแก้)",
  "payBatches.x.openCopy": "ดาวน์โหลดสำเนาไฟล์",
  "payBatches.x.day": "วันที่เงินเข้าบัญชี",
  "payBatches.x.kbankOn": "เงินเข้า {date} · อัปโหลดได้ถึงวันนั้น ก่อน {time} น.",
  "payBatches.x.manualOn": "นัดจ่ายวันที่ {date}",
  "payBatches.x.past": "วันนี้ผ่านไปแล้ว",
  "payBatches.x.weekend": "ตรงวันเสาร์–อาทิตย์ ธนาคารโอนต่างธนาคารได้เฉพาะวันทำการ",
  "payBatches.x.tooLate": "ส่งไม่ทันแล้ว — ต้องอัปโหลดภายใน {date} ก่อน {time} น.",
  "payBatches.x.smartLater": "ใช้วันที่ {date} เฉพาะไฟล์ต่างธนาคาร",
  "payBatches.x.smartSame": "กลับไปใช้วันเดียวกับไฟล์อื่น",
  "payBatches.x.company": "บริษัทผู้จ่าย",
  "payBatches.x.companyFirst": "ครั้งแรกเท่านั้น — ระบบจะจำไว้ใช้รอบต่อไป",
  "payBatches.x.edit": "แก้",
  "payBatches.x.payer": "ชื่อผู้จ่ายตามที่ธนาคารรู้จัก",
  "payBatches.x.debit": "บัญชีที่หักเงิน",
  "payBatches.x.debitNew": "เลขบัญชีกสิกรไทยของบริษัทที่หักเงิน",
  "payBatches.x.debitAdd": "เพิ่มบัญชีหักเงิน (ไม่บังคับ)",
  "payBatches.x.debitLabel": "ชื่อเรียกบัญชี (ไม่บังคับ)",
  "payBatches.x.services": "บริการที่บริษัทสมัครกับธนาคาร",
  "payBatches.x.svcKbank": "KBank Payroll (โอนเข้าบัญชีกสิกรไทย)",
  "payBatches.x.svcSmart": "SMART Payroll (โอนเข้าธนาคารอื่น)",
  "payBatches.x.servicesOff": "บริษัทไม่ได้สมัคร {which} — คนกลุ่มนั้นอยู่ในรายชื่อจ่ายเอง",
  "payBatches.x.saveCompany": "บันทึกข้อมูลบริษัท",
  "payBatches.x.fixTitle": "ต้องแก้ก่อน {n} คน",
  "payBatches.x.fixNote": "บันทึกลงข้อมูลพนักงานด้วย รอบหน้าไม่ต้องกรอกซ้ำ และเก็บประวัติว่าใครแก้",
  "payBatches.x.pickBank": "เลือกธนาคาร",
  "payBatches.x.accountNo": "เลขที่บัญชี",
  "payBatches.x.fixSave": "บันทึก",
  "payBatches.x.fixSaved": "บันทึกบัญชีของ {name} แล้ว",
  "payBatches.x.template": "ใส่ใน template: ผู้จ่าย {payer} · วันที่ {date} · บัญชีหักเงิน {account}",
  "payBatches.x.lastExport": "ดาวน์โหลดล่าสุด {when} โดย {who} · เงินเข้า {date}",
  "payBatches.part.kbankService": "KBank Payroll",
  "payBatches.part.smartService": "SMART Payroll",
  "payBatches.x.ownDay": "ไฟล์นี้ใช้วันที่ {date}",
  "payBatches.files.afterApprove": "อนุมัติรอบก่อน จึงดาวน์โหลดไฟล์ได้",
  "payBatches.files.howTo": "วิธีใช้: เปิดไฟล์ด้วย Excel → คัดลอก 3 คอลัมน์แรก (เลขบัญชี ชื่อ จำนวนเงิน) → วางใน KBank Excel Template → กด «สร้างไฟล์» → อัปโหลดที่ K-Cash Connect Plus โดยเลือกบริการให้ตรงกับไฟล์",
  "payBatches.files.feeNote": "ค่าธรรมเนียมคิดตามอัตราประกาศของธนาคาร (KBank Payroll {kbank} · SMART Payroll {smart} ต่อรายการ) อัตราจริงขึ้นกับสัญญาของบริษัท",
  "payBatches.files.holidayNote": "กำหนดส่งนับข้ามเฉพาะวันเสาร์–อาทิตย์ ถ้ามีวันหยุดนักขัตฤกษ์คั่นต้องส่งเร็วขึ้น",
  "payBatches.part.kbank": "KBank Payroll",
  "payBatches.part.smart": "ต่างธนาคาร (SMART)",
  "payBatches.reason.no_smart": "ธนาคารนี้รับโอนแบบ SMART ไม่ได้ ต้องจ่ายเอง",
  "payBatches.unknownBank": "ไม่รู้จักชื่อธนาคาร «{name}» แก้ที่หน้าข้อมูลพนักงาน",
  "payBatches.badAccount": "เลขบัญชีกสิกรไทยต้องมี 10 หลัก ({account}) แก้ที่หน้าข้อมูลพนักงาน",
  "payBatches.unknownBankWarn": "มี {n} คนที่ระบบไม่รู้จักชื่อธนาคาร ออกไฟล์ไม่ได้จนกว่าจะแก้ที่หน้าข้อมูลพนักงาน",
  "payBatches.badAccountWarn": "มี {n} คนที่เลขบัญชีกสิกรไทยไม่ครบ 10 หลัก ธนาคารจะไม่รับทั้งไฟล์ แก้ที่หน้าข้อมูลพนักงานก่อน",
  "finance.err.bad_account": "เลขบัญชีกสิกรไทยต้องมี 10 หลัก แก้ที่หน้าข้อมูลพนักงานก่อน",
  "payBatches.approveTitle": "อนุมัติรอบนี้?",
  "payBatches.approveBody": "{people} คน ยอดโอนรวม {total} จะพร้อมออกไฟล์จ่าย",
  "payBatches.approvedToast": "อนุมัติรอบแล้ว",
  "payBatches.paidTitle": "ยืนยันว่าโอนแล้ว?",
  "payBatches.paidBody1": "{people} คน ยอดรวม {total} จะถูกบันทึกว่าจ่ายแล้ว",
  "payBatches.paidBody2": "หลังยืนยัน รอบนี้จะแก้ไขหรือตีกลับไม่ได้อีก",
  "payBatches.paidPlaceholder": "เช่น โอนผ่านระบบธนาคารวันที่ 20",
  "payBatches.paidToast": "บันทึกว่าโอนแล้ว ปิดรอบเรียบร้อย",
  "payBatches.event.approve": "อนุมัติ",
  "payBatches.event.export": "ออกไฟล์จ่าย ({n} คน)",
  "payBatches.event.paid": "ยืนยันว่าโอนแล้ว",
  "money.confirm.approveChargeTitle": "อนุมัติรายการนี้?",
  "money.confirm.approveChargeBody": "{type} ฿{amount} จะนับรวมในยอดเงินของคนขับใบงานนี้",
  "money.confirm.revokeChargeTitle": "ยกเลิกการอนุมัติ?",
  "money.confirm.revokeChargeBody": "{type} ฿{amount} จะไม่นับในยอดเงินของคนขับใบงานนี้อีก",
  "money.confirm.approve": "อนุมัติ",
  "money.confirm.revoke": "ยกเลิกการอนุมัติ",
  "money.confirm.backdatedTitle": "บันทึกแบบมีผลย้อนหลัง?",
  "money.confirm.backdatedSalary": "เงินเดือนใหม่จะมีผลตั้งแต่ {date} ย้อนหลัง {n} วันจากวันนี้ ตรวจวันที่ให้แน่ใจก่อนบันทึก",
  "money.confirm.backdatedItems": "{names} จะมีผลตั้งแต่ {date} ย้อนหลัง {n} วันจากวันนี้ ตรวจวันที่ให้แน่ใจก่อนบันทึก",
  "money.confirm.saveBackdated": "บันทึกย้อนหลัง",
  "nav.financeSettings": "ตั้งค่าการเงิน",
  "finSettings.title": "ตั้งค่าการเงิน",
  "finSettings.lead": "ค่าทุกตัวเปลี่ยนได้เองโดยไม่ต้องแก้โปรแกรม · รายการเงินที่บันทึกไปแล้วไม่เปลี่ยนตาม",
  "finSettings.sections": "หัวข้อ",
  "finSettings.section.items": "รายการเงิน",
  "finSettings.items.lead": "ชื่อที่ใช้ในฟอร์มเพิ่มรายการ ไฟล์บัญชี และสลิป · เรียงตามคอลัมน์ในไฟล์บัญชี",
  "finSettings.items.add": "เพิ่มรายการ",
  "finSettings.items.showHidden": "แสดงที่ซ่อน ({n})",
  "finSettings.group.income": "รายได้",
  "finSettings.group.deduction": "รายหัก",
  "finSettings.group.contractor_after_tax": "รายหักคนขับภายนอก หลังหัก ณ ที่จ่าย 1%",
  "finSettings.tag.computed": "ระบบคิดให้",
  "finSettings.tag.perBill": "ทุกบิล",
  "finSettings.tag.monthlyHomeSite": "เดือนละครั้ง",
  "finSettings.tag.perPayout": "ทุกรอบจ่าย",
  "finSettings.tag.perDay": "คิดรายวัน",
  "finSettings.tag.percentOfIncome": "% ของรายได้",
  "finSettings.tag.installments": "ผ่อนได้",
  "finSettings.tag.stagePerRow": "เลือกก่อน/หลังภาษีทีละรายการ",
  "finSettings.tag.postTax": "หลังภาษี",
  "finSettings.tag.bucket": "อื่น ๆ",
  "finSettings.tag.company": "ของบริษัท",
  "finSettings.tag.onlyFor": "เฉพาะ{types}",
  "finSettings.hidden": "ซ่อนอยู่",
  "finSettings.code": "รหัส {code}",
  "finSettings.action.up": "เลื่อน \"{name}\" ขึ้น",
  "finSettings.action.down": "เลื่อน \"{name}\" ลง",
  "finSettings.action.edit": "แก้ไข",
  "finSettings.action.hide": "ซ่อน",
  "finSettings.action.show": "แสดง",
  "finSettings.sheet.addTitle": "เพิ่มรายการเงิน",
  "finSettings.sheet.editTitle": "แก้รายการเงิน",
  "finSettings.f.name": "ชื่อรายการ",
  "finSettings.f.namePh": "เช่น ค่าชุดยูนิฟอร์ม",
  "finSettings.f.side": "เป็นเงินแบบไหน",
  "finSettings.f.earn": "ได้ (จ่ายให้)",
  "finSettings.f.deduct": "หัก (เก็บคืน)",
  "finSettings.f.group": "อยู่กลุ่มไหน",
  "finSettings.f.stage": "หักก่อนหรือหลังภาษี",
  "finSettings.f.preTax": "ก่อนภาษี",
  "finSettings.f.postTax": "หลังภาษี",
  "finSettings.f.frequency": "เกิดบ่อยแค่ไหน",
  "finSettings.f.each": "ทุกครั้งที่บันทึก",
  "finSettings.f.monthlyHomeSite": "เดือนละครั้ง — ลูกจ้างหักจากบิลเงินเดือน รถร่วมหักจากบิลของสัญญาที่วิ่งมากที่สุดในเดือนนั้น",
  "finSettings.f.basis": "จำนวนเงินมาจาก",
  "finSettings.f.fixed": "ใส่จำนวนเงิน",
  "finSettings.f.perDay": "จำนวนวัน × อัตรา",
  "finSettings.f.installments": "ผ่อนเป็นงวดได้",
  "finSettings.f.appliesTo": "ใช้กับใคร",
  "finSettings.f.everyone": "ทุกคน",
  "finSettings.f.systemNote": "รายการตั้งต้นของระบบ เปลี่ยนได้เฉพาะชื่อ ลำดับ และการซ่อน",
  "finSettings.f.computedNote": "ระบบคิดยอดนี้ให้เองจากรายการอื่น จึงซ่อนไม่ได้",
  "finSettings.f.orderAndShow": "ลำดับและการแสดง",
  "finSettings.f.moveUp": "เลื่อนขึ้น",
  "finSettings.f.moveDown": "เลื่อนลง",
  "finSettings.action.delete": "ลบรายการ",
  "finSettings.confirmDelete.title": "ลบ \"{name}\"?",
  "finSettings.confirmDelete.body": "ลบได้เฉพาะรายการที่ยังไม่มีรายการเงินใช้ ถ้าใช้ไปแล้วระบบจะให้ซ่อนแทน",
  "finSettings.deleted": "ลบ \"{name}\" แล้ว",
  "finSettings.section.legal": "อัตราตามกฎหมาย",
  "finSettings.section.payout": "บริษัทผู้จ่ายและวิธีจ่าย",
  "finCfg.lead.legal": "ใช้คิดประกันสังคม ภาษีหัก ณ ที่จ่าย และงวดผ่อน · ค่าใหม่มีผลตามวันที่ตั้ง รอบที่แช่แข็งหรือจ่ายแล้วไม่เปลี่ยน",
  "finCfg.lead.payout": "ค่าโอนที่หักจากคนขับภายนอก ครั้งละหนึ่งการโอน · ใครได้รับแจ้งเรื่องรอบจ่ายและทางไหน",
  "finCfg.title.payout": "ค่าโอนและการแจ้งเตือน",
  "finCfg.lead.company": "ข้อมูลที่ไฟล์ธนาคารใช้ทุกรอบ — ชุดเดียวกับที่ถามตอนออกไฟล์ครั้งแรก",
  "finCfg.col.name": "ค่า",
  "finCfg.col.now": "ตอนนี้",
  "finCfg.col.from": "มีผลตั้งแต่",
  "finCfg.col.pending": "รอมีผล",
  "finCfg.setNew": "ตั้งค่าใหม่…",
  "finCfg.historyN": "ประวัติ {n}",
  "finCfg.default": "ค่าตั้งต้น",
  "finCfg.since": "ตั้งแต่ {date}",
  "finCfg.pendingFrom": "{value} จาก {date}",
  "finCfg.key.sso_rate": "ประกันสังคม",
  "finCfg.key.sso_base": "ฐานประกันสังคม ต่ำสุด–สูงสุด (บาท)",
  "finCfg.key.sso_base_items": "รายการที่นับเป็นฐานประกันสังคม",
  "finCfg.key.wht_no_sso": "หัก ณ ที่จ่าย คนไม่เข้าประกันสังคม (ใช้ก่อนวันเริ่มบิลตามชนิดเงิน)",
  "finCfg.key.wht_contractor": "หัก ณ ที่จ่าย คนขับภายนอก",
  "finCfg.key.days_per_month": "ฐานวันต่อเดือน (คิดตามสัดส่วน · หักขาดงาน)",
  "finCfg.key.installment_rounding": "ปัดเศษงวดผ่อน",
  "finCfg.key.transfer_fee": "ค่าโอน",
  "finCfg.key.notify_in_app": "แจ้งเตือนในระบบ (รอบรออนุมัติ · ตีกลับ · อนุมัติแล้ว · พักถึงนัด · งวดค้าง · บัญชีถูกแก้)",
  "finCfg.key.notify_email": "ส่งแจ้งเตือนทางอีเมลด้วย",
  "finCfg.v.baht": "{amount} บาท",
  "finCfg.key.min_wage_day": "ค่าแรงขั้นต่ำต่อวัน (ใช้เตือนเท่านั้น)",
  "finCfg.key.pay_by_kind": "บิลตามชนิดเงิน + ภาษีลูกจ้างแบบขั้นบันได (ตั้งให้เริ่มวันที่ 1 ของเดือน)",
  "finCfg.key.pay_schedule": "รอบจ่ายเงินเดือนของบริษัท",
  "finCfg.v.notSet": "ยังไม่ได้ตั้ง",
  "finCfg.v.days": "{n} วัน",
  "finCfg.v.roundLast": "เศษไปงวดสุดท้าย",
  "finCfg.v.roundFirst": "เศษไปงวดแรก",
  "finCfg.v.feePer": "{amount} บาทต่อการโอน",
  "finCfg.v.feeOff": "ไม่เก็บ",
  "finCfg.v.capAmount": "{amount} บาท",
  "finCfg.v.yes": "ใช่",
  "finCfg.v.no": "ไม่",
  "finCfg.sheet.title": "ตั้งค่าใหม่",
  "finCfg.f.which": "ค่าไหน",
  "finCfg.f.value": "ค่าใหม่",
  "finCfg.f.from": "มีผลตั้งแต่",
  "finCfg.f.nowIs": "ตอนนี้: {value}",
  "finCfg.f.today": "วันนี้",
  "finCfg.f.nextMonth": "ต้นเดือนหน้า",
  "finCfg.f.nextYear": "1 ม.ค. ปีหน้า",
  "finCfg.f.min": "ต่ำสุด (บาท)",
  "finCfg.f.max": "สูงสุด (บาท)",
  "finCfg.f.feeOn": "เก็บค่าโอน",
  "finCfg.f.bahtUnit": "บาท",
  "finCfg.f.daysUnit": "วัน",
  "finCfg.sum": "{label}: {before} → {after} ตั้งแต่ {date}",
  "finCfg.sum.pending": "ก่อนวันนั้นยังใช้ค่าเดิม",
  "finCfg.sum.back": "ย้อนหลัง {n} วัน — บิลในช่วงนั้นที่ยังไม่แช่แข็งจะคิดด้วยค่าใหม่",
  "finCfg.sum.untouched": "รอบที่แช่แข็งหรือจ่ายแล้วไม่เปลี่ยน",
  "finCfg.sum.same": "ยังเป็นค่าเดิม — ใส่ค่าใหม่แล้วเลือกวันที่มีผล",
  "finCfg.sum.incomplete": "ใส่ค่าใหม่ให้ครบก่อน",
  "finCfg.saved": "บันทึกแล้ว — {label} มีผลตั้งแต่ {date}",
  "finCfg.cancelled": "ยกเลิกค่าที่รอมีผล {date} แล้ว",
  "finCfg.history.title": "ประวัติ: {label}",
  "finCfg.history.default": "ค่าตั้งต้นของระบบ (ก่อนมีการตั้ง)",
  "finCfg.history.by": "ตั้งโดย {name}",
  "finCfg.history.cancel": "ยกเลิกค่านี้",
  "finCfg.state.current": "ใช้อยู่",
  "finCfg.state.pending": "รอมีผล",
  "finCfg.state.past": "เคยใช้",
  "finCfg.cancelConfirm.title": "ยกเลิกค่าที่รอมีผล?",
  "finCfg.cancelConfirm.body": "{label} = {value} ที่จะมีผล {date} จะไม่ถูกใช้",
  "finCfg.company.saved": "บันทึกข้อมูลบริษัทแล้ว",
  "finCfg.company.noDebit": "ยังไม่มีบัญชีหักเงิน",
  "finCfg.company.default": "ใช้เป็นค่าตั้งต้น",
  "finCfg.company.makeDefault": "ตั้งเป็นค่าตั้งต้น",
  "finCfg.company.add": "เพิ่มบัญชี",
  "finCfg.company.remove": "ลบบัญชี {account}",
  "finCfg.company.removeTitle": "ลบบัญชีหักเงิน?",
  "finCfg.company.removeBody": "บัญชี {account} จะไม่ถูกใช้ออกไฟล์อีก ไฟล์ที่ออกไปแล้วไม่เปลี่ยน",
  "finCfg.company.removeConfirm": "ลบบัญชี",
  "financeTxn.status.hold": "พักไว้",
  "financeTxn.status.voided": "ยกเลิกแล้ว",
  "financeTxn.voidedView": "ดูรายการที่ยกเลิก",
  "financeTxn.voidedNote": "แสดงเฉพาะรายการที่ยกเลิกแล้ว — ไม่นับในยอดรวม · เหตุผลดูที่ \"ประวัติ\" ของแต่ละรายการ",
  "financeTxn.status.paidOutside": "จ่ายนอกรอบแล้ว",
  "financeTxn.status.settled": "ดำเนินการแล้ว",
  "addMoney.title": "เพิ่มรายการเงิน",
  "addMoney.who": "ของใคร",
  "addMoney.way": "เงินไปทางไหน",
  "addMoney.earn": "บริษัทจ่ายให้",
  "addMoney.deduct": "หักจากเขา",
  "addMoney.what": "รายการอะไร",
  "addMoney.whatPh": "ค้นหารายการ",
  "addMoney.tripHint": "ค่าเที่ยวแก้ที่ใบงาน ไม่ได้เพิ่มที่นี่",
  "addMoney.howMuch": "เท่าไร",
  "addMoney.split": "แบ่งผ่อนหลายงวด",
  "addMoney.installments": "จำนวนงวด",
  "addMoney.installmentsUnit": "งวด",
  "addMoney.on": "วันที่ {day}",
  "addMoney.startOn": "เริ่ม {day}",
  "addMoney.planLine": "ระบบคิดให้: งวดละ {per} บาท · งวดสุดท้าย {end}",
  "addMoney.source": "ที่มา (บังคับ)",
  "addMoney.department": "แผนก",
  "addMoney.reporter": "ผู้แจ้ง (บังคับ)",
  "addMoney.missing": "ยังขาด: {list}",
  "addMoney.need.person": "คน",
  "addMoney.need.item": "รายการ",
  "addMoney.need.amount": "จำนวนเงิน",
  "addMoney.need.department": "แผนก",
  "addMoney.need.reporter": "ผู้แจ้ง",
  "addMoney.need.settled": "เหตุผลและหลักฐาน (จบนอกระบบ)",
  "addMoney.settled": "บันทึกเป็นดำเนินการแล้ว (จบนอกระบบ)",
  "addMoney.settledHint": "เงินที่จบไปแล้วนอกระบบ เช่น จ่ายเงินสดไปแล้ว หรือบันทึกย้อน — รายการนี้จะไม่เข้าบิลและรอบจ่าย",
  "addMoney.settledNeed": "ต้องเลือกวิธีจบ เขียนบันทึก และแนบหลักฐานอย่างน้อยหนึ่งไฟล์",
  "addMoney.attachRequired": "แนบหลักฐาน (บังคับ)",
  "addMoney.notePhRequired": "บันทึก เช่น จ่ายเงินสดที่หน้างาน 12 ต.ค. (บังคับ)",
  "addMoney.sum.settledTail": " · บันทึกเป็นดำเนินการแล้ว ไม่เข้าบิลและรอบจ่าย",
  "addMoney.attach": "แนบหลักฐาน (ไม่บังคับ)",
  "addMoney.uploading": "กำลังแนบไฟล์…",
  "addMoney.uploadFailed": "แนบไฟล์ไม่สำเร็จ",
  "addMoney.removeFile": "เอา {name} ออก",
  "addMoney.note": "เหตุผล / หมายเหตุ",
  "addMoney.notePh": "เหตุผล / หมายเหตุ (ไม่บังคับ)",
  "addMoney.specific": "เฉพาะรายการนี้ (ไม่บังคับ)",
  "addMoney.contract": "สัญญา",
  "addMoney.vehicle": "ทะเบียนรถ",
  "addMoney.taxStage": "หักก่อนหรือหลังภาษี 1%",
  "addMoney.sum.incomplete": "ตอบข้อ 1–4 แล้วระบบจะสรุปให้ตรงนี้ก่อนบันทึก",
  "addMoney.sum.earn": "จะจ่ายให้ {who} {amount} บาท เป็น{item} วันที่ {day}",
  "addMoney.sum.deduct": "จะหัก {who} {amount} บาท เป็น{item} วันที่ {day}",
  "addMoney.sum.planEarn": "จะจ่ายให้ {who} งวดละ {per} บาท {n} งวด เดือนละงวด ตั้งแต่ {from} ถึง {to}{last}",
  "addMoney.sum.planDeduct": "จะหัก {who} งวดละ {per} บาท {n} งวด เดือนละงวด ตั้งแต่ {from} ถึง {to}{last}",
  "addMoney.sum.lastDiffers": " (งวดที่รับเศษ {last} บาท)",
  "addMoney.save": "บันทึก",
  "addMoney.saved": "บันทึก {name} แล้ว",
  "addMoney.savedPlan": "บันทึก {name} {n} งวดแล้ว",
  "addMoney.failed": "บันทึกไม่สำเร็จ",
  "addMoney.companyCost": "รายการของบริษัท (ไม่ใช่ของคน)",
  "personFin.title": "ข้อมูลการเงิน",
  "personFin.notSet": "ยังไม่ได้ระบุ",
  "personFin.payBasis": "เงินประจำ",
  "personFin.basis.per_trip": "ไม่มี (ได้ค่าเที่ยวตามใบงาน)",
  "personFin.basis.daily": "ค่าแรงรายวัน",
  "personFin.basis.monthly": "เงินเดือน",
  "personFin.change": "เปลี่ยน…",
  "personFin.salaryValue": "฿{amount} / เดือน",
  "personFin.salarySince": "มีผลตั้งแต่ {date}",
  "personFin.lead": "วิธีจ่ายเงินของคนนี้ — ทุกค่ามีวันที่มีผลและเก็บประวัติ",
  "personFin.homeSite": "สัญญาหลัก",
  "personFin.sso": "ประกันสังคม",
  "personFin.ssoDatedHint": "เข้าหรือออกประกันสังคมนับเป็นเดือน ตั้งแต่วันที่ 1 ของเดือนที่เลือก เดือนก่อนหน้ายังคิดตามเดิม คนที่ออกงาน ระบบตั้งออกให้เองตั้งแต่เดือนถัดจากวันทำงานวันสุดท้าย",
  "personFin.ssoFromMonth": "ตั้งแต่เดือน",
  "personFin.taxYear": "ปีภาษี",
  "personFin.ssoIn": "เข้า ตั้งแต่ {month}",
  "personFin.ssoOut": "ไม่เข้า",
  "personFin.ssoYes": "เข้า",
  "personFin.ssoMonth": "เดือนที่เริ่มเข้า",
  "personFin.central": "กลุ่มงานส่วนกลาง",
  "personFin.centralHint": "คนในกลุ่มนี้ไม่ได้ตัวคูณค่าเที่ยว",
  "personFin.operatingFee": "ค่าดำเนินการ (% ของรายได้ในบิล)",
  "personFin.commission": "คอมมิชชั่นรายคน",
  "personFin.resigned": "ทำงานวันสุดท้าย",
  "personFin.resignedHint": "ตั้งจากสถานะการทำงานที่มุมขวาบนของหน้าพนักงาน",
  "personFin.gaps": "ยังไม่ครบ: {list}",
  "personFin.gapsWhat": "ตั้งค่าเงินของคนนี้ยังไม่ครบ: {list} — ค่าเที่ยวจากใบงานยังจ่ายตามปกติ แต่รายการที่ต้องใช้ค่าเหล่านี้ระบบยังคิดให้ไม่ได้",
  "personFin.gapsSet": "ไปตั้งค่า",
  "personFin.gap.pay_basis": "เงินประจำ",
  "personFin.gap.sso": "ประกันสังคม",
  "personFin.gap.home_site": "สัญญาหลัก",
  "personFin.gap.operating_fee": "ค่าดำเนินการ",
  "personFin.saved": "บันทึกข้อมูลการเงินแล้ว",
  "personFin.payBasisHint": "เงินเดือนได้ทุกเดือนตามหน้าเงินเดือน · ค่าแรงรายวันได้เฉพาะวันที่มีใบงานเริ่มวิ่ง หรือวันที่สำนักงานเพิ่มให้ · ค่าเที่ยวตามใบงานได้ทุกแบบ",
  "personFin.homeSiteHint": "บิลของสัญญาหลักเป็นบิลที่หัก GPS และค่าประกอบการเดือนละครั้ง",
  "workDays.title": "วันทำงาน",
  "workDays.thisMonth": "เดือนนี้",
  "workDays.lastMonth": "เดือนก่อน",
  "workDays.add": "เพิ่มวันทำงาน",
  "workDays.summary": "วันละ {wage} บาท · ทำงาน {n} วัน · รวม {total} บาท",
  "workDays.noWage": "ยังไม่ได้ตั้งค่าแรงต่อวัน — ตั้งที่ข้อมูลการเงินในหน้าพนักงาน",
  "workDays.empty": "ยังไม่มีวันทำงานในเดือนนี้",
  "workDays.fromTickets": "ใบงาน {list}",
  "workDays.added": "สำนักงานเพิ่ม: {reason}",
  "workDays.removed": "ไม่นับ: {reason}",
  "workDays.adjusted": "ปรับจากที่จ่ายไปแล้ว",
  "workDays.notCount": "ไม่นับวันนี้",
  "workDays.undo": "ให้ใบงานตัดสิน",
  "workDays.addTitle": "เพิ่มวันทำงาน",
  "workDays.removeTitle": "ไม่นับวันทำงาน",
  "workDays.day": "วันที่",
  "workDays.reason": "เหตุผล (บังคับ)",
  "workDays.reasonPhAdd": "เช่น รอรถที่ลาน ไม่มีงานวิ่ง",
  "workDays.reasonPhRemove": "เช่น ใบงานปิดแทน แต่ไม่ได้มาทำงานจริง",
  "workDays.addHint": "วันที่เพิ่มได้ค่าแรงของวันนั้น แม้ไม่มีใบงาน",
  "workDays.removeHint": "วันนี้จะไม่ได้ค่าแรง ถ้าอยู่ในรอบที่แช่แข็งแล้ว ระบบหักคืนในบิลถัดไป",
  "workDays.saved": "บันทึกวันทำงานแล้ว",
  "workDays.state.in_round": "อยู่ในรอบ",
  "workDays.state.paid": "จ่ายแล้ว",
  "workDays.hint": "นับจากใบงานที่กดเริ่มวิ่งในวันนั้น วันเดียวหลายใบนับครั้งเดียว ค่าแรงตามอัตราของวันนั้น",
  "financeTxn.kind.daily_wage": "ค่าแรงรายวัน",
  "personFin.taxPriorWithheld": "ภาษีที่นายจ้างเดิมหักไว้",
  "personFin.taxPriorIncome": "รายได้จากนายจ้างเดิมในปีนี้",
  "personFin.taxAllowanceHint": "คู่สมรส บุตร ประกัน กองทุน ฯลฯ ตามแบบ ล.ย.01 ที่พนักงานยื่น",
  "personFin.taxAllowance": "ลดหย่อนเพิ่มต่อปี (นอกจาก 60,000 ส่วนตัว)",
  "personFin.taxPrior": "จากนายจ้างเดิมปีนี้ {income} บาท หักไว้ {withheld} บาท",
  "personFin.taxValue": "ลดหย่อนเพิ่ม {amount} บาท",
  "personFin.tax": "ลดหย่อนภาษีต่อปี",
  "personFin.salaryWhere": "จำนวนเงินเดือนตั้งที่หน้าเงินเดือน",
  "personFin.belowMinWage": "ต่ำกว่าค่าแรงขั้นต่ำที่ตั้งไว้ {min} บาทต่อวัน",
  "payBatches.paydayOnly": "จ่ายวันที่ {date} ({n} บิล)",
  "paySched.keep": "เก็บไว้",
  "paySched.periodOf": "งวดที่ {n} ของ {month}",
  "paySched.waiting": "รอมีผล {date}: {what}",
  "paySched.title": "รอบจ่ายเงินเดือน",
  "paySched.followsCompanyText": "ตามรอบของบริษัท",
  "paySched.sum.one": "1 งวดต่อเดือน",
  "paySched.sum.many": "{n} งวดต่อเดือน",
  "paySched.sum.text": "{count} ตัด{cuts} จ่าย{pays}",
  "paySched.sum.day": "วันที่ {day}",
  "paySched.sum.end": "สิ้นเดือน",
  "paySched.sum.nextMonth": "{when} ของเดือนถัดไป",
  "paySched.sum.laterMonths": "{when} ของอีก {n} เดือน",
  "paySched.sum.and": "{a} และ{b}",
  "paySched.sum.andNum": "{a} และ {b}",
  "paySched.sum.withSplit": "{text} แบ่งเงิน {shares}",
  "paySched.lead": "พนักงานที่ได้เงินเดือนหรือค่าแรงรายวัน: เงินเดือน ค่าเที่ยว เบี้ย และรายการหัก เข้างวดตามวันของรายการ",
  "paySched.period": "งวดที่ {n}",
  "paySched.work": "งานวันที่",
  "paySched.dayPrev": "{day} ของเดือนก่อน",
  "paySched.day": "{day}",
  "paySched.to": "ถึงวันที่",
  "paySched.payOn": "ได้เงินวันที่",
  "paySched.share": "ได้กี่ % ของเงินเดือน",
  "paySched.end": "สิ้นเดือน",
  "paySched.month.0": "เดือนเดียวกัน",
  "paySched.month.1": "เดือนถัดไป",
  "paySched.month.2": "อีก 2 เดือน",
  "paySched.add": "เพิ่มงวด",
  "paySched.remove": "ลบงวดที่ {n}",
  "paySched.covered": "ครบทุกวันของเดือน — งวดถัดไปเริ่มวันถัดจากวันตัดของงวดก่อนเสมอ ไม่มีช่องว่าง",
  "paySched.p.no_periods": "ต้องมีอย่างน้อย 1 งวด",
  "paySched.p.too_many": "ตั้งได้ไม่เกิน {max} งวดต่อเดือน",
  "paySched.p.bad_cut": "งวดที่ {n}: วันตัดต้องเป็นวันที่ 1–28 หรือสิ้นเดือน",
  "paySched.p.cuts_out_of_order": "งวดที่ {n}: วันตัดต้องอยู่หลังวันตัดของงวดที่ {prev}",
  "paySched.p.bad_pay_day": "งวดที่ {n}: วันจ่ายไม่ถูกต้อง",
  "paySched.p.paid_before_cut": "งวดที่ {n}: วันจ่ายอยู่ก่อนวันตัด — เลือกวันจ่ายหลังวันตัด หรือจ่ายเดือนถัดไป",
  "paySched.p.bad_share": "งวดที่ {n}: ใส่ % มากกว่า 0 และไม่เกิน 100",
  "paySched.p.shares_not_100": "% รวมทุกงวดต้องเท่ากับ 100 (ตอนนี้ {total})",
  "paySched.preview": "3 งวดแรกนับจากวันที่เริ่มใช้",
  "paySched.col.period": "งวด",
  "paySched.col.work": "งานวันที่",
  "paySched.col.pay": "ได้เงิน",
  "paySched.col.share": "%",
  "paySched.short": "งวดแรกสั้นกว่าปกติ ({days} วัน) — เงินเดือนงวดนี้คิดตามวัน (เงินเดือน ÷ 30 × จำนวนวัน)",
  "paySched.text": "งวดที่ {n}: งานวันที่ {start} ถึง {cut} · จ่ายวันที่ {pay} {month} · {share}%",
  "paySched.legacy": "แบบเดิม: ทั้งเดือน จ่ายสิ้นเดือน (ค่าเที่ยวเข้าบิลเดือนถัดไป)",
  "paySched.company": "ตามบริษัท",
  "paySched.own": "ตั้งเองสำหรับคนนี้",
  "paySched.followsCompany": "ตามบริษัท",
  "paySched.set": "ตั้งรอบใหม่",
  "paySched.saved": "บันทึกรอบจ่ายเงินเดือนแล้ว เริ่ม {date}",
  "paySched.sum": "ตั้งแต่ {date} รอบจ่ายเงินเดือนเป็นแบบนี้ · บิลที่อยู่ในรอบจ่ายหรือจ่ายแล้วไม่ขยับ",
  "paySched.sum.company": "ตั้งแต่ {date} คนนี้ใช้รอบของบริษัท · บิลที่อยู่ในรอบจ่ายหรือจ่ายแล้วไม่ขยับ",
  "paySched.since": "ตั้งแต่ {date}",
  "paySched.pending": "รอมีผล {date}",
  "paySched.history": "ประวัติ {n} ครั้ง",
  "paySched.history.title": "ประวัติรอบจ่ายเงินเดือน",
  "paySched.cancel": "ยกเลิก",
  "paySched.cancelConfirm": "ยกเลิกรอบจ่ายที่จะเริ่ม {date}?",
  "paySched.cancelled": "ยกเลิกรอบจ่ายที่จะเริ่ม {date} แล้ว",
  "paySched.personHint": "เลือก \"ตั้งเองสำหรับคนนี้\" เมื่อคนนี้รับเงินคนละรอบกับคนอื่นในบริษัท",
  "personFin.dailyWageHint": "จ่ายเฉพาะวันที่ทำงานจริง: วันที่มีใบงานเริ่มวิ่ง หรือวันที่สำนักงานเพิ่มให้ (ดูที่หน้าการเงินของคนนี้)",
  "personFin.dailyWageValue": "วันละ {amount} บาท",
  "personFin.dailyWage": "ค่าแรงต่อวัน",
  "personFin.law.contractor": "หักภาษี 1% (ภ.ง.ด.3) + ค่าดำเนินการ",
  "personFin.law.employee": "หักประกันสังคม + ภาษีเงินได้ขั้นบันได (ภ.ง.ด.1)",
  "personFin.relationHint": "ลูกจ้าง: หักประกันสังคมและภาษีเงินได้ตามขั้นบันได (ภ.ง.ด.1) · ผู้รับจ้าง: หักภาษี 1% (ภ.ง.ด.3) และค่าดำเนินการ",
  "personFin.relationFromType": "ตามประเภทใน HR",
  "personFin.relation.contractor": "ผู้รับจ้าง",
  "personFin.relation.employee": "ลูกจ้าง",
  "personFin.relation": "ความสัมพันธ์",
  "personFin.gap.daily_wage": "ค่าแรงต่อวัน",
  "addMoney.recurringHas": "เดือนนี้ระบบลง{item}ให้แล้วจากข้อมูลพนักงาน ({doc} · {amount} บาท) — กรอกซ้ำไม่ได้",
  "addMoney.recurringWill": "ข้อมูลพนักงานจ่าย{item}ทุกเดือนอยู่แล้ว ระบบจะลงให้เองวันที่ 1 — เปลี่ยนยอดประจำที่หน้าพนักงาน",
  "addMoney.adjustThisMonth": "ปรับยอดเดือนนี้",
  "addMoney.viewRow": "ดูรายการนั้น",
  "addMoney.openProfile": "เปิดหน้าพนักงาน",
  "addMoney.adjustTo": "ยอดเดือนนี้ควรเป็น",
  "addMoney.adjustHint": "ระบบลงไว้ {amount} บาท ({doc}) — จะบันทึกเฉพาะส่วนต่าง",
  "addMoney.adjustHintAgain": "เดือนนี้ปรับไปแล้ว {n} ครั้ง ตอนนี้เป็น {amount} บาท ({doc}) — จะบันทึกเฉพาะส่วนต่างจากยอดนี้",
  "addMoney.recurringHasAdjusted": "เดือนนี้ระบบลง{item}ให้แล้วจากข้อมูลพนักงาน และปรับไปแล้ว {n} ครั้ง ตอนนี้เป็น {amount} บาท ({doc}) — กรอกซ้ำไม่ได้",
  "addMoney.adjustSame": "ยอดเท่าเดิม ไม่ต้องปรับ",
  "addMoney.sum.adjust": "จะปรับ{item}ของ {who} เดือนนี้จาก {from} เป็น {to} บาท (บันทึกส่วนต่าง {delta} บาท)",
  "payComp.itemPh": "ผูกกับรายการเงิน",
  "payComp.linked": "ระบบลงรายการนี้ให้ทุกเดือนตั้งแต่เดือนที่ผูก",
  "payComp.notLinked": "ยังไม่ผูกรายการเงิน — ระบบจะไม่จ่ายรายการนี้",
  "financeTxn.kind.recurring": "รายการประจำเดือน",
  "nav.badge": "{n} เรื่องรออยู่",
  "finSteps.aria": "ขั้นการจ่ายเงิน",
  "finSteps.waits.rows": "ยังไม่เข้ารอบ {n} บรรทัด",
  "finSteps.waits.due": "ถึงกำหนด {n} บิล",
  "finSteps.waits.dueBox": "ถึงกำหนด {n} บิล · สะสม {box} คน",
  "finSteps.waits.rounds": "ยังไม่โอน {n} รอบ",
  "nav.finSection.flow": "ขั้นการจ่าย",
  "nav.finSection.source": "ที่มาของเงิน",
  "nav.finSection.more": "รายงานและตั้งค่า",
  "bills.inRound": "อยู่ใน {name} ({status})",
  "bills.notInRound": "ถึงกำหนดแล้ว ยังไม่เข้ารอบจ่าย",
  "bills.goRounds": "ไปที่รอบจ่าย",
  "bills.openTxn": "เปิดบรรทัดนี้ในรายการเงิน",
  "financeTxn.where.round": "{name} · {status}",
  "financeTxn.where.none": "ยังไม่เข้ารอบ",
  "financeTxn.where.bills": "ดูบิล",
  "finFlow.title": "ทางเดินของเงิน",
  "finFlow.lead": "บรรทัดเงินรวมเป็นบิล บิลที่ถึงกำหนดหยิบเข้ารอบจ่าย แล้วโอน",
  "finFlow.rows": "ยังไม่เข้ารอบ",
  "finFlow.rowsSub": "{n} บรรทัด",
  "finFlow.due": "บิลรอเข้ารอบ",
  "finFlow.dueSub": "{n} บิล",
  "finFlow.dueSubBox": "{n} บิลครบรอบ · รายการสะสม {box} คน",
  "finFlow.rounds": "รอบรอโอน",
  "finFlow.roundsSub": "{n} รอบ",
  "finFlow.paid": "จ่ายแล้วเดือนนี้",
  "finFlow.paidSub": "{n} รอบ",
  "finFlow.rowsUnit": "บรรทัด",
  "nav.financeHome": "หน้าหลักการเงิน",
  "finHome.title": "การเงิน",
  "finHome.today": "วันนี้ {date}",
  "finHome.nextPay": "รอบจ่ายถัดไป {date}",
  "finHome.inDays": "อีก {n} วัน",
  "finHome.dueToday": "วันนี้",
  "finHome.overdue": "เลยมา {n} วัน",
  "finHome.noBatch": "ยังไม่ได้ตั้งรอบจ่าย",
  "finHome.findPerson": "ค้นหาคน",
  "finHome.addMoney": "เพิ่มรายการเงิน",
  "finHome.loadFailed": "โหลดหน้าการเงินไม่สำเร็จ — ตัวเลขด้านล่างยังไม่ได้แสดง ลองใหม่อีกครั้ง",
  "finHome.card.next": "ต้องจ่าย · {name}",
  "finHome.card.nextSub": "{people} คน · {bills} บิล · โอน {date}",
  "finHome.card.ready": "พร้อมจ่าย ยังไม่อยู่ในรอบ",
  "finHome.card.readySub": "{bills} บิลครบรอบ · กล่อง {box} คน",
  "finHome.card.decide": "รอคุณตัดสินใจ",
  "finHome.card.decideUnit": "เรื่อง",
  "finHome.card.decideSub": "ด่วน {u} · ไม่ด่วน {r}",
  "finHome.card.instalments": "ค้างบริษัททั้งหมด",
  "finHome.card.instalmentsSub": "{plans} ก้อน · {people} คน",
  "finHome.card.holds": "พักไว้",
  "finHome.card.holdsSub": "{n} รายการ · ถึงวันนัดสัปดาห์นี้ {w}",
  "finHome.todoTitle": "สิ่งที่ต้องทำ",
  "finHome.todoSub": "เรียงจากด่วนสุด · ทำเสร็จแล้วหายไปเอง",
  "finHome.urgent": "ด่วน",
  "finHome.empty": "ไม่มีเรื่องรอตัดสินใจ — งานการเงินวันนี้เรียบร้อย",
  "finHome.more": "และอีก {n}",
  "finHome.batchesTitle": "รอบจ่ายที่ยังไม่จ่าย",
  "finHome.batchesEmpty": "ไม่มีรอบที่ค้าง",
  "finHome.allBatches": "ดูรอบจ่ายทั้งหมด",
  "finHome.batchOn": "โอน {date}",
  "finHome.todo.batchPaid": "{name} ถึงวันโอนแล้ว — ยืนยันว่าจ่ายแล้ว",
  "finHome.todo.batchPaidDetail": "ออกไฟล์แล้ว · โอน {date} · {people} คน · {total} บาท",
  "finHome.todo.batchFile": "{name} อนุมัติแล้ว แต่ยังไม่ได้ออกไฟล์ธนาคาร",
  "finHome.todo.batchFileDetail": "เงินเข้า {date} · {people} คน · {total} บาท",
  "finHome.todo.batchApprove": "{name} รออนุมัติ",
  "finHome.todo.batchApproveDetail": "แช่แข็งยอดแล้ว · โอน {date} · {people} คน · {total} บาท",
  "finHome.todo.batchDraft": "{name} ยังเป็นร่าง ใกล้วันโอนแล้ว",
  "finHome.todo.batchDraftDetail": "โอน {date} · {bills} บิล · {total} บาท — ตรวจแล้วแช่แข็งยอดเพื่อส่งอนุมัติ",
  "finHome.todo.holdDue": "รายการที่พักไว้ถึงวันนัดแล้ว {n} รายการ",
  "finHome.todo.holdSoon": "รายการที่พักไว้จะถึงวันนัดใน 3 วัน {n} รายการ",
  "finHome.todo.holdLine": "{item} · {who} {amount} บาท · นัด {date} · พักเพราะ \"{reason}\"",
  "finHome.todo.holdLineNoReason": "{item} · {who} {amount} บาท · นัด {date}",
  "finHome.todo.unbatched": "เงินที่ถึงเวลาจ่ายแล้ว {total} บาท ยังไม่อยู่ในรอบจ่าย",
  "finHome.todo.unbatchedDetail": "{bills} บิลครบรอบ · กล่อง {box} คน",
  "finHome.todo.unassigned": "รายการเงิน {n} แถวไม่ได้ระบุคน",
  "finHome.todo.unassignedDetail": "ยังจ่ายไม่ได้จนกว่าจะระบุว่าเป็นของใคร",
  "finHome.todo.gaps": "ข้อมูลการเงินของพนักงาน {n} คนยังไม่ครบ",
  "finHome.todo.gapLine": "{who} — ขาด {list}",
  "finHome.act.confirmPaid": "ยืนยันจ่าย",
  "finHome.act.exportFile": "ออกไฟล์",
  "finHome.act.openBatch": "เปิดรอบ",
  "finHome.act.openRow": "ดูรายการ",
  "finHome.act.toBatch": "จัดเข้ารอบ",
  "finHome.act.openBills": "ดูในหน้าบิล",
  "finHome.act.fillIn": "เติมข้อมูล",
  "finHome.corner.title": "การเงิน · ด่วน {n} เรื่อง",
  "finHome.corner.open": "เปิดหน้าหลักการเงิน",
  "finHome.corner.close": "ปิดการเตือน",
  "finPerson.back": "กลับหน้าหลักการเงิน",
  "finPerson.crumbs": "ตำแหน่งหน้า",
  "finPerson.people": "คน",
  "finPerson.homeSite": "สัญญาหลัก {name}",
  "finPerson.payee": "รับเงิน {bank} {account}",
  "finPerson.noPayee": "ยังไม่มีบัญชีรับเงิน",
  "finPerson.profile": "ตั้งค่าค่าตอบแทน",
  "finPerson.card.open": "จะได้ · จ่ายถัดไป {date}",
  "finPerson.card.openNegative": "ยอดติดลบ — หักมากกว่าที่ได้",
  "finPerson.card.openSubDate": "{n} บิลที่ยังไม่จ่าย · รอบถัดไป {date}",
  "finPerson.card.openNoDate": "ยอดที่ยังไม่จ่าย",
  "finPerson.card.openSub": "{n} บิลที่ยังไม่จ่าย",
  "finPerson.card.plansSub": "{n} ก้อนที่ยังค้าง",
  "finPerson.card.holdsSub": "{n} รายการ · นัดถัดไป {date}",
  "finPerson.card.none": "ไม่มี",
  "finPerson.plans": "ภาระที่ผ่อนอยู่",
  "finPerson.plansEmpty": "ไม่มีรายการผ่อน",
  "finPerson.planTotal": "ยอด {total} บาท",
  "finPerson.planDone": "คืนแล้ว {done}/{count} งวด",
  "finPerson.planPer": "งวดละ {per}",
  "finPerson.planLeft": "เหลือ",
  "finPerson.planFinished": "ครบแล้ว",
  "finPerson.holdTrip": "ค่าเที่ยว {job}",
  "finPerson.holds": "ที่พักไว้",
  "finPerson.holdUntil": "นัด {date}",
  "finPerson.holdNoDate": "ไม่ได้นัดวัน",
  "finPerson.holdWhy": "พักเพราะ \"{reason}\"",
  "finPerson.bills": "บิล",
  "finPerson.billCards": "ดูบิลแบบละเอียด",
  "finPerson.billsEmpty": "ไม่มีบิลที่ยังไม่จ่าย",
  "finPerson.window": "{start}–{end}",
  "finPerson.payOn": "จ่าย {date}",
  "finPerson.inBatch": "อยู่ใน {name}",
  "finPerson.paidIn": "จ่ายแล้วเมื่อ {date} · {name}",
  "finPerson.paidBills": "บิลที่จ่ายแล้ว {n} ใบ",
  "nav.financeDebts": "หนี้และงวดผ่อน",
  "nav.financeRequests": "คำร้อง",
  "requests.title": "คำร้อง",
  "requests.tab.open": "ยังไม่ปิด",
  "requests.tab.submitted": "รอรับเรื่อง",
  "requests.tab.received": "รับเรื่องแล้ว",
  "requests.tab.in_progress": "กำลังดำเนินการ",
  "requests.tab.closed": "ปิดแล้ว",
  "requests.tab.rejected": "ไม่อนุมัติ",
  "requests.find": "ค้นหาเลขคำร้อง หัวข้อ ข้อความ ชื่อ หรือรหัสพนักงาน",
  "requests.empty": "ไม่มีคำร้องในหมวดนี้",
  "requests.emptyHint": "พนักงานยื่นคำร้องได้จากแอปคนขับ เมนูคำร้อง",
  "requests.filedOn": "ยื่นเมื่อ {date}",
  "requests.ownedBy": "ผู้รับเรื่อง {name}",
  "requests.status.submitted": "รอรับเรื่อง",
  "requests.status.received": "รับเรื่องแล้ว",
  "requests.status.in_progress": "กำลังดำเนินการ",
  "requests.status.closed": "ปิดแล้ว",
  "requests.status.rejected": "ไม่อนุมัติ",
  "requests.file": "ไฟล์แนบ {n}",
  "requests.step.received": "รับเรื่อง",
  "requests.step.in_progress": "เริ่มดำเนินการ",
  "requests.step.closed": "ปิดเรื่อง",
  "requests.step.rejected": "ไม่อนุมัติ",
  "requests.done.received": "รับเรื่องแล้ว — แจ้งคนยื่นในแอปแล้ว",
  "requests.done.in_progress": "เริ่มดำเนินการแล้ว",
  "requests.done.closed": "ปิดเรื่องแล้ว — แจ้งคนยื่นในแอปแล้ว",
  "requests.done.rejected": "บันทึกว่าไม่อนุมัติแล้ว — แจ้งคนยื่นพร้อมเหตุผล",
  "requests.stepFailed": "เปลี่ยนสถานะคำร้องไม่สำเร็จ",
  "requests.money.add": "เพิ่มรายการเงิน",
  "requests.money.lend": "ให้ยืม / เบิก",
  "requests.moneyHow": "ถ้าคำร้องนี้เกี่ยวกับเงิน สร้างรายการด้วยปุ่มด้านบน — ใส่ยอดและวิธีจ่ายเอง รายการจะผูกกับคำร้องนี้ให้",
  "requests.reasonNo": "เหตุผลที่ไม่อนุมัติ (คนยื่นจะเห็นข้อความนี้)",
  "requests.closeNote": "บันทึกตอนปิดเรื่อง (ไม่บังคับ คนยื่นจะเห็น)",
  "requests.moneyTitle": "เงินที่ผูกกับคำร้องนี้",
  "requests.moneyNone": "ยังไม่มีรายการเงินจากคำร้องนี้",
  "requests.paid": "จ่ายแล้ว",
  "requests.settled": "จบนอกระบบ",
  "requests.inRound": "อยู่ในรอบจ่าย",
  "requests.reject.goes": "รายการเงินที่ผูกไว้ {n} รายการจะถูกยกเลิกไปพร้อมกัน: {docs}",
  "requests.reject.taken": "ยังไม่อนุมัติไม่ได้ — {docs} อยู่ในรอบจ่ายหรือจ่ายแล้ว ให้กลับรายการที่หน้ารายการเงินก่อน",
  "requests.history": "ขั้นตอนของคำร้อง",
  "requests.event.submitted": "ยื่นคำร้อง",
  "docLink.label": "ผูกกับ (ถ้ามี)",
  "docLink.role.none": "ไม่ผูก",
  "docLink.role.work_order": "งานซ่อม",
  "docLink.role.safety": "เคสความปลอดภัย",
  "docLink.role.request": "คำร้อง",
  "docLink.pick.request": "เลือกคำร้อง",
  "docLink.pick.work_order": "เลือกงานซ่อม",
  "docLink.pick.safety": "เลือกเคสความปลอดภัย",
  "docLink.loading": "กำลังโหลด…",
  "docLink.none": "ยังไม่มีเอกสาร",
  "financeTxn.role.work_order": "งานซ่อม",
  "financeTxn.role.safety": "เคสความปลอดภัย",
  "ticketMoney.title": "เงินของใบงานนี้ในการเงิน",
  "ticketMoney.paidOf": "จ่ายแล้ว {paid} จาก {total}",
  "ticketMoney.empty": "ยังไม่มีรายการเงิน — ค่าเที่ยวเข้าการเงินทันทีที่ปิดงาน",
  "ticketMoney.kind.trip": "ค่าเที่ยว",
  "ticketMoney.kind.adjust": "ปรับค่าเที่ยวหลังจ่าย",
  "ticketMoney.inBill": "อยู่ในบิล รอเข้ารอบจ่าย (กำหนดจ่าย {date})",
  "ticketMoney.inRound": "อยู่ในรอบจ่าย {round} (จ่าย {date})",
  "ticketMoney.paid": "จ่ายแล้วในรอบ {round} เมื่อ {date}",
  "ticketMoney.hold": "พักไว้ ถึง {date}",
  "ticketMoney.settled": "จบนอกระบบแล้ว",
  "ticketMoney.none": "ยังไม่อยู่ในบิลใด",
  "ticketMoney.openBill": "บิล {bill}",
  "ticketMoney.openRound": "เปิดรอบจ่าย",
  "nav.financeReports": "รายงาน",
  // plan/finance-full J1/J2 — เทียบยอดกับไฟล์ของบริษัท
  "finCompare.title": "เทียบยอดกับไฟล์ของบริษัท",
  "finCompare.lead": "วางยอดจากไฟล์ของบริษัทเทียบกับระบบทีละคน ใช้ตอนเริ่มใช้ระบบ: ตรวจหนี้ยกมากับรายการของฝ่ายบัญชี และรันคู่ขนานกับ Excel เงินเดือนเดิม หน้านี้อ่านอย่างเดียว ไม่แก้ข้อมูลในระบบ",
  "finCompare.step1": "1 · เทียบกับอะไร",
  "finCompare.step2": "2 · ไฟล์ของบริษัท",
  "finCompare.kind.debts": "หนี้คงเหลือรายคน",
  "finCompare.kind.debtsLead": "ยอดยกมา — หนี้ที่แต่ละคนยังค้าง ณ วันที่เลือก เทียบกับรายการหนี้ของฝ่ายบัญชี",
  "finCompare.kind.round": "ยอดโอนรายคนของรอบจ่าย",
  "finCompare.kind.roundLead": "รันคู่ขนาน — ยอดที่แต่ละคนได้ในรอบ เทียบกับยอดสุทธิใน Excel เงินเดือนเดิม",
  "finCompare.asOf": "ยอด ณ วันที่",
  "finCompare.asOfDay": "ยอด ณ {day}",
  "finCompare.pickRound": "เลือกรอบจ่าย",
  "finCompare.fileLead": "ไฟล์ .xlsx หรือ .csv ที่มีคอลัมน์พนักงาน (รหัสหรือชื่อ) กับคอลัมน์ยอด ใช้ไฟล์ที่ส่งออกจากระบบเดิมได้เลย คนเดียวมีหลายบรรทัด ระบบรวมให้",
  "finCompare.pickFile": "เลือกไฟล์",
  "finCompare.template": "ดาวน์โหลดแบบฟอร์มเปล่า",
  "finCompare.templateFile": "แบบฟอร์มเทียบยอด",
  "finCompare.read": "{name} · อ่านได้ {n} บรรทัด",
  "finCompare.noColumns": "ไม่พบคอลัมน์พนักงานกับคอลัมน์ยอดในไฟล์นี้ — หัวตารางต้องมีคำว่า พนักงาน / รหัสพนักงาน / ชื่อ และ ยอด / จำนวนเงิน / รับสุทธิ / ยอดคงเหลือ",
  "finCompare.tooMany": "ไฟล์เดียวเทียบได้ไม่เกิน {n} บรรทัด",
  "finCompare.unreadable": "อ่านไฟล์นี้ไม่ได้ ลองบันทึกเป็น .xlsx หรือ .csv อีกครั้ง",
  "finCompare.run": "เทียบยอด",
  "finCompare.failed": "เทียบยอดไม่สำเร็จ",
  "finCompare.file": "ผลเทียบยอด",
  "finCompare.export": "บันทึกผลเป็น Excel",
  "finCompare.allMatch": "ตรงกันทุกคน",
  "finCompare.someOff": "ไม่ตรง {n} คน",
  "finCompare.againstLine": "ไฟล์ {file} · เทียบกับ{against}",
  "finCompare.against.round": "รอบ {name} ({status})",
  "finCompare.against.debts": "หนี้คงเหลือ ณ {day}",
  "finCompare.stat.match": "ตรงกัน (คน)",
  "finCompare.stat.differ": "ยอดไม่ตรง (คน)",
  "finCompare.stat.onlyFile": "มีแค่ในไฟล์ (คน)",
  "finCompare.stat.onlySystem": "มีแค่ในระบบ (คน)",
  "finCompare.totals": "ยอดรวมในไฟล์ {file} · ในระบบ {system} · ต่าง {diff}",
  "finCompare.bad": "{n} บรรทัดในไฟล์จับคู่กับพนักงานไม่ได้",
  "finCompare.badLine": "บรรทัด {row} · {who} · {amount} — {why}",
  "finCompare.badMore": "และอีก {n} บรรทัด (อยู่ในไฟล์ Excel ที่บันทึก)",
  "finCompare.filter": "แสดง",
  "finCompare.show.off": "เฉพาะที่ไม่ตรง",
  "finCompare.show.all": "ทั้งหมด",
  "finCompare.show.match": "ที่ตรงกัน",
  "finCompare.nothingHere": "ไม่มีรายการในมุมนี้",
  "finCompare.diffShort": "ต่าง {diff}",
  "finCompare.both": "ไฟล์ {file} · ระบบ {system}",
  "finCompare.col.person": "พนักงาน",
  "finCompare.col.code": "รหัส",
  "finCompare.col.file": "ในไฟล์",
  "finCompare.col.system": "ในระบบ",
  "finCompare.col.diff": "ต่าง (ไฟล์ − ระบบ)",
  "finCompare.col.status": "ผล",
  "finCompare.col.lines": "บรรทัดในไฟล์",
  "finCompare.col.sheet": "ผลเทียบยอด",
  "finCompare.col.badSheet": "บรรทัดที่จับคู่ไม่ได้",
  "finCompare.col.line": "บรรทัด",
  "finCompare.col.problem": "เหตุผล",
  "finCompare.col.tplPerson": "พนักงาน",
  "finCompare.col.tplAmount": "ยอด (บาท)",
  "finCompare.status.match": "ตรงกัน",
  "finCompare.status.differ": "ยอดไม่ตรง",
  "finCompare.status.only_file": "มีแค่ในไฟล์",
  "finCompare.status.only_system": "มีแค่ในระบบ",
  "finCompare.problem.person_blank": "ไม่มีชื่อหรือรหัสพนักงาน",
  "finCompare.problem.person_unknown": "ไม่พบพนักงานคนนี้ในระบบ",
  "finCompare.problem.person_ambiguous": "ชื่อนี้มีหลายคน ใส่รหัสพนักงานแทน",
  "finCompare.problem.amount_bad": "อ่านยอดไม่ได้",
  "finHome.compare": "เทียบยอดกับไฟล์",
  "opening.compareLink": "ตรวจหนี้ยกมากับรายการของฝ่ายบัญชี",
  "finReports.title": "รายงานการเงิน",
  "finReports.lead": "ยอดที่จ่ายให้พนักงาน ดูได้ทั้งรายรอบ รายคน และงวดผ่อนที่ค้าง ตัวเลขเดียวกับหน้ารอบจ่ายและหน้าบิล ทุกหน้าส่งออกเป็นไฟล์ Excel ได้",
  "finReports.export": "ส่งออก Excel",
  "finReports.year": "ปี",
  "finReports.bills": "{n} บิล",
  "finReports.peopleN": "{n} คน",
  "finReports.tab.rounds": "รอบจ่าย",
  "finReports.tab.people": "รายคน",
  "finReports.tab.tax": "ภาษีหัก ณ ที่จ่าย",
  "finReports.file.tax": "รายงานภาษีหัก ณ ที่จ่าย",
  "finReports.col.tax": "ภาษีหัก ณ ที่จ่าย",
  "finReports.col.form": "แบบ",
  "finReports.col.pnd1": "ภ.ง.ด.1",
  "finReports.col.pnd3": "ภ.ง.ด.3",
  "finReports.col.taxBase": "เงินได้ที่ใช้คิดภาษี",
  "finReports.col.withheld": "ภาษีที่หักไว้",
  "finReports.tax.lead": "ภาษีที่ระบบหักไว้จากรอบที่จ่ายแล้ว ใช้ทำหนังสือรับรอง 50 ทวิ และยื่น ภ.ง.ด.1 / ภ.ง.ด.3 — ไฟล์ Excel แยกให้ทุกเดือน · เงินได้นับเข้าเดือนที่บิลจบ (เดือนที่จ่าย) ตามหลักภาษีที่นับตอนได้รับเงิน",
  "cert.title": "หนังสือรับรองการหักภาษี ณ ที่จ่าย",
  "cert.law": "ตามมาตรา 50 ทวิ แห่งประมวลรัษฎากร",
  "cert.payer": "ผู้มีหน้าที่หักภาษี ณ ที่จ่าย",
  "cert.payee": "ผู้ถูกหักภาษี ณ ที่จ่าย",
  "cert.taxId": "เลขประจำตัวผู้เสียภาษีอากร",
  "cert.idNumber": "เลขประจำตัวประชาชน",
  "cert.address": "ที่อยู่",
  "cert.formOrder": "ลำดับที่ในแบบ",
  "cert.pnd1a": "ภ.ง.ด.1ก",
  "cert.pnd3": "ภ.ง.ด.3",
  "cert.col.type": "ประเภทเงินได้พึงประเมินที่จ่าย",
  "cert.col.year": "ปีภาษีที่จ่าย",
  "cert.col.paid": "จำนวนเงินที่จ่าย",
  "cert.col.tax": "ภาษีที่หักและนำส่งไว้",
  "cert.type.salary": "1. เงินเดือน ค่าจ้าง เบี้ยเลี้ยง โบนัส ฯลฯ ตามมาตรา 40 (1)",
  "cert.type.transport": "5. การจ่ายเงินได้ที่ต้องหักภาษี ณ ที่จ่าย ตามคำสั่งกรมสรรพากรที่ออกตามมาตรา 3 เตรส (ค่าขนส่ง)",
  "cert.total": "รวมเงินที่จ่ายและภาษีที่หักนำส่ง",
  "cert.sso": "เงินสมทบจ่ายเข้ากองทุนประกันสังคม {amount} บาท",
  "cert.payerKind": "ผู้จ่ายเงิน",
  "cert.withheldAtSource": "(1) หัก ณ ที่จ่าย",
  "cert.statement": "ขอรับรองว่าข้อความและตัวเลขดังกล่าวข้างต้นถูกต้องตรงกับความจริงทุกประการ",
  "cert.sign": "ลงชื่อ ผู้จ่ายเงิน",
  "cert.date": "วัน เดือน ปี ที่ออกหนังสือรับรอง",
  "cert.none": "ไม่มีภาษีที่หักไว้ของคนนี้ในปีนี้",
  "cert.back": "กลับไปรายงานภาษี",
  "cert.missing": "ยังขาดข้อมูลบนหนังสือ: {list}",
  "cert.missing.payerTax": "เลขผู้เสียภาษีของบริษัท (ตั้งค่า › ผู้จ่าย)",
  "cert.missing.payerAddress": "ที่อยู่บริษัท",
  "cert.missing.idNumber": "เลขบัตรประชาชนของพนักงาน (HR › บัตรประชาชน)",
  "cert.missing.address": "ที่อยู่พนักงาน",
  "finReports.tax.cert": "50 ทวิ",
  "finReports.tax.pnd1a": "ภ.ง.ด.1ก (Excel)",
  "finReports.file.pnd1a": "ภงด1ก",
  "finReports.col.pnd1a": "ภ.ง.ด.1ก",
  "finReports.col.seq": "ลำดับ",
  "finReports.col.idNumber": "เลขประจำตัวผู้เสียภาษี",
  "finReports.col.address": "ที่อยู่",
  "finReports.col.income401": "เงินได้ 40(1) ทั้งปี",
  "finReports.col.taxYear": "ภาษีที่หักทั้งปี",
  "finReports.col.condition": "เงื่อนไข",
  "finCfg.company.taxId": "เลขประจำตัวผู้เสียภาษีของบริษัท",
  "finCfg.company.taxIdHint": "13 หลัก ใช้บนหนังสือรับรอง 50 ทวิ และ ภ.ง.ด.1ก",
  "finCfg.company.address": "ที่อยู่บริษัท (ตามที่จดทะเบียน)",
  "finReports.tax.withheld": "ภาษีที่หักไว้ทั้งปี {year}",
  "finReports.tax.base": "จากเงินได้ {amount} บาท",
  "finReports.tax.empty": "ปี {year} ยังไม่มีรอบที่จ่ายแล้วที่หักภาษีไว้",
  "finReports.tab.overdue": "งวดค้าง",
  "finReports.file.rounds": "รายงานรอบจ่าย",
  "finReports.file.people": "รายงานรายคน",
  "finReports.file.overdue": "รายงานงวดค้าง",
  "finReports.col.round": "รอบจ่าย",
  "finReports.col.payDay": "วันจ่าย",
  "finReports.col.status": "สถานะ",
  "finReports.col.people": "คน",
  "finReports.col.bills": "บิล",
  "finReports.col.total": "ยอด",
  "finReports.col.approvedBy": "อนุมัติโดย",
  "finReports.col.approvedAt": "อนุมัติเมื่อ",
  "finReports.col.paidAt": "ยืนยันจ่ายเมื่อ",
  "finReports.col.held": "พักคนระหว่างอนุมัติ",
  "finReports.col.sentBack": "ตีกลับ (ครั้ง)",
  "finReports.col.createdBy": "สร้างโดย",
  "finReports.col.notes": "หมายเหตุ",
  "finReports.col.person": "พนักงาน",
  "finReports.col.employeeNo": "รหัสพนักงาน",
  "finReports.col.yearTotal": "รวมทั้งปี",
  "finReports.col.rounds": "จำนวนรอบ",
  "finReports.col.sum": "รวม",
  "finReports.col.overdue": "งวดค้าง",
  "finReports.col.debt": "เลขที่หนี้",
  "finReports.col.item": "รายการ",
  "finReports.col.instalment": "งวดที่",
  "finReports.col.dueDay": "ถึงกำหนด",
  "finReports.col.amount": "ยอด",
  "finReports.col.skipped": "เลื่อนมาแล้ว (ครั้ง)",
  "finReports.rounds.paid": "จ่ายแล้วทั้งปี",
  "finReports.rounds.waiting": "ยังไม่จ่าย",
  "finReports.rounds.waitingSub": "รอบร่าง · รออนุมัติ · อนุมัติแล้ว",
  "finReports.rounds.count": "จำนวนรอบ",
  "finReports.rounds.empty": "ปี {year} ยังไม่มีรอบจ่าย",
  "finReports.rounds.held": "พัก {n} คน",
  "finReports.rounds.sentBack": "ตีกลับ {n} ครั้ง",
  "finReports.rounds.approvedBy": "อนุมัติโดย {name}",
  "finReports.people.total": "จ่ายแล้วทั้งปี {year}",
  "finReports.people.totalSub": "นับเฉพาะรอบที่ยืนยันจ่ายแล้ว ตามวันจ่ายของรอบ",
  "finReports.people.count": "จำนวนคนที่ได้รับเงิน",
  "finReports.people.empty": "ปี {year} ยังไม่มีรอบที่ยืนยันจ่ายแล้ว",
  "finReports.people.find": "ค้นหาชื่อหรือรหัสพนักงาน",
  "finReports.people.noMatch": "ไม่พบพนักงานที่ค้นหา",
  "finReports.overdue.lead": "งวดผ่อนที่ถึงกำหนดแล้ว แต่ยังไม่มีรอบจ่ายไหนหยิบไป หยิบเข้ารอบได้ที่หน้ารอบจ่าย หรือเลื่อนงวดที่หน้าหนี้ ส่วนรายการที่พักไว้ดูที่",
  "finReports.overdue.holds": "หน้าพักไว้",
  "finReports.overdue.amount": "ยอดงวดค้าง",
  "finReports.overdue.people": "จำนวนคน",
  "finReports.overdue.empty": "ไม่มีงวดค้าง ทุกงวดที่ถึงกำหนดอยู่ในรอบจ่ายแล้ว",
  "finReports.overdue.instalment": "งวด {n}",
  "finReports.overdue.due": "ถึงกำหนด {date}",
  "finReports.overdue.skipped": "เลื่อนมาแล้ว {n} ครั้ง",
  "finPerson.earned": "รายได้สะสม",
  "finPerson.earned.month": "เดือนนี้",
  "finPerson.earned.year": "ปีนี้",
  "finPerson.earned.sub": "นับเฉพาะรอบที่ยืนยันจ่ายแล้ว",
  "finPerson.earned.report": "ดูทุกคน",
  "audit.fin.finance_debt_lend": "ให้ยืม / เบิกล่วงหน้า",
  "audit.fin.finance_debt_repay_outside": "รับคืนนอกรอบ",
  "audit.fin.finance_debt_reschedule": "เปลี่ยนจำนวนงวด",
  "audit.fin.finance_debt_close": "ปิดยอดหนี้",
  "audit.fin.finance_debt_reprice": "คิดดอกเบี้ยใหม่",
  "debts.title": "หนี้และงวดผ่อน",
  "debts.lend": "ให้ยืม / เบิกล่วงหน้า",
  "debts.totals": "ค้างทั้งหมด {total} บาท · {n} ก้อน · {people} คน",
  "debts.filter": "สถานะหนี้",
  "debts.filter.open": "ยังค้าง",
  "debts.filter.closed": "ปิดแล้ว",
  "debts.filter.all": "ทั้งหมด",
  "debts.empty": "ไม่มีหนี้ที่ยังค้าง",
  "debts.emptyClosed": "ยังไม่มีหนี้ที่ปิดแล้ว",
  "debts.progress": "คืนแล้ว {done}/{count} งวด",
  "debts.next": "งวดถัดไป {date} · {per}",
  "debts.finished": "ปิดแล้ว",
  "debts.left": "คงเหลือ",
  "debts.kept": "เก็บไว้แล้ว",
  "debt.notFound": "ไม่พบหนี้ก้อนนี้",
  "debt.left": "คงเหลือ",
  "debt.kept": "เก็บไว้แล้ว",
  "debt.of": "จาก {total} · คืนแล้ว {back}",
  "debt.leftCount": "เหลือ {n} งวด",
  "debt.sourceLoan": "เงินที่ให้ยืมออกไป",
  "debt.sourcePlan": "รายการผ่อน",
  "debt.loanLine": "บริษัทให้ยืม · {item} {amount} บาท",
  "debt.planLine": "{item} ยอด {amount} บาท",
  "debt.paidOutside": "ให้ยืมไปแล้ว (นอกรอบจ่าย)",
  "debt.paidInBatch": "ให้ยืมไปแล้ว (โอนในรอบจ่าย)",
  "debt.paysWithBatch": "จะโอนให้พร้อมรอบจ่ายถัดไป",
  "debt.interest": "ดอก {interest}",
  "debt.terms": "ผ่อน {n} งวด งวดละ {per}",
  "debt.rowsLoan": "การหักคืน {n} รายการ",
  "debt.rowsPlan": "งวด {n} งวด",
  "debt.row.seq": "งวด {seq}/{count}",
  "debt.row.outside": "คืนนอกรอบ",
  "debt.row.deferred": "เลื่อนมาจากบิลก่อน",
  "debt.state.paid": "คืนแล้ว",
  "debt.state.outside": "คืนนอกรอบ",
  "debt.state.in_batch": "อยู่ในรอบจ่าย",
  "debt.state.held": "พักไว้",
  "debt.state.due": "รอหัก",
  "debt.state.ahead": "ยังไม่ถึง",
  "debt.repay.open": "+ รับคืนนอกรอบ",
  "debt.re.open": "เปลี่ยนจำนวนงวด",
  "debt.close.open": "ปิดยอดทั้งก้อน",
  "debt.repay.explainLoan": "รับคืนนอกรอบ = พนักงานคืนเงินเอง (เงินสด / โอนเข้าบริษัท) พร้อมหลักฐาน ยอดคงเหลือลดทันที งวดที่เหลือระบบคิดใหม่ให้",
  "debt.repay.explainPlan": "รับคืนนอกรอบ = เลือกงวดที่พนักงานคืนเป็นเงินสดแล้ว พร้อมหลักฐาน งวดนั้นจะไม่ถูกหักในบิล",
  "debt.repay.title": "รับคืนนอกรอบ",
  "debt.repay.save": "บันทึกรับคืน",
  "debt.repay.howMuch": "คืนเท่าไร",
  "debt.repay.left": "คงเหลือ {left} บาท",
  "debt.repay.all": "คืนทั้งหมด {left}",
  "debt.repay.which": "งวดที่พนักงานคืนเป็นเงินสดแล้ว",
  "debt.repay.when": "วันที่รับคืน และหลักฐาน",
  "debt.repay.notePh": "เช่น โอนคืนเข้าบัญชีบริษัท / จ่ายเงินสดที่สาขา",
  "debt.repay.note": "รายละเอียด",
  "debt.repay.sumEmpty": "ใส่ยอดที่ได้คืน",
  "debt.repay.sumClosed": "รับคืน {got} บาท — ปิดหนี้ก้อนนี้ งวดที่เหลือถูกยกเลิก",
  "debt.repay.sumSpread": "รับคืน {got} บาท · คงเหลือ {left} บาท แบ่ง {n} งวด งวดละ {per} บาท",
  "debt.repay.sumLeft": "รับคืน {got} บาท · คงเหลือ {left} บาท",
  "debt.re.title": "เปลี่ยนจำนวนงวด",
  "debt.re.save": "บันทึก",
  "debt.re.only": "แก้เฉพาะงวดที่ยังไม่หัก — งวดที่คืนแล้ว อยู่ในรอบจ่าย หรือพักไว้ จะไม่ถูกแตะ",
  "debt.re.count": "จำนวนงวดที่เหลือ",
  "debt.re.from": "เริ่มงวดแรก",
  "debt.re.reason": "เหตุผล (บังคับ)",
  "debt.re.sum": "ที่เหลือแบ่ง {n} งวด งวดละ {per} บาท ตั้งแต่ {from} ถึง {end}",
  "debt.re.sumNothing": "ไม่มียอดที่แบ่งได้",
  "debt.re.fixed": "(ไม่รวม {fixed} บาทที่อยู่ในรอบจ่ายหรือพักไว้)",
  "debt.close.title": "ปิดยอดทั้งก้อน",
  "debt.close.save": "ปิดยอด",
  "debt.close.on": "หักทั้งก้อนวันที่",
  "debt.close.sum": "หักที่เหลือทั้งหมด {total} บาท ในงวดเดียววันที่ {day}",
  "debt.repaid": "บันทึกรับคืน {code} แล้ว",
  "debt.rescheduled": "เปลี่ยนงวดของ {code} แล้ว",
  "debt.closed": "ปิดยอด {code} แล้ว",
  "debt.failed": "บันทึกไม่สำเร็จ",
  "debt.attachRequired": "แนบหลักฐาน (บังคับ)",
  // NIGHT-1023 N1 — where an instalment is taken
  "lend.home.monthly": "หักจากบิลเงินเดือนของ{who} — งวดแรกในบิลเดือนที่ตัดยอด {end}",
  "lend.home.contract": "หักจากบิลสัญญา {contract} ของ{who} (สัญญาที่วิ่งมากที่สุดในเดือนนั้น) — งวดแรกในบิลที่ตัดยอด {end}",
  "lend.home.box": "{who}ไม่มีบิลเงินเดือน และเดือนนั้นยังไม่ได้วิ่งงาน — งวดจะไปรอใน \"รายการสะสม\" ซึ่งไม่ครบรอบเอง ต้องมีคนเลือกเข้ารอบจ่ายทุกครั้ง",
  "lend.home.checking": "กำลังดูว่างวดจะไปหักที่บิลไหน…",
  "debt.where.batch": "อยู่ในรอบจ่าย {name} · {status}",
  "debt.where.monthly": "บิลเงินเดือน ตัดยอด {end}",
  "debt.where.monthlyLater": "จะหักในบิลเงินเดือน ตัดยอด {end}",
  "debt.where.contract": "บิลสัญญา {contract} ตัดยอด {end}",
  "debt.where.contractLater": "จะหักในบิลสัญญา {contract} ตัดยอด {end}",
  "debt.where.box": "รายการสะสม — ต้องเลือกเข้ารอบจ่ายเอง",
  "debt.where.boxNote": "มีงวดของหนี้นี้ไปรอใน \"รายการสะสม\" ของ{who} รายการสะสมไม่ครบรอบเอง งวดจะไม่ถูกหักจนกว่าจะมีคนเลือกเข้ารอบจ่าย",
  "debt.where.boxOpen": "เปิดบิลของ{who}",
  "debt.where.status.accumulating": "กำลังสะสม",
  "debt.where.status.due": "ครบรอบ รอจ่าย",
  "debt.where.status.in_batch": "อยู่ในรอบจ่าย",
  "debt.where.status.paid": "จ่ายแล้ว",
  "debt.where.batchStatus.draft": "ร่าง",
  "debt.where.batchStatus.frozen": "ล็อกยอด รออนุมัติ",
  "debt.where.batchStatus.approved": "อนุมัติแล้ว",
  "debt.where.batchStatus.paid": "จ่ายแล้ว",
  // ADVANCE — pay advance + postpone
  "advance.open": "เบิกล่วงหน้า",
  "advance.title": "เบิกล่วงหน้า",
  "advance.save": "บันทึกการเบิก",
  "advance.who": "ของใคร",
  "advance.q1": "เบิกเท่าไร",
  "advance.amount": "ยอดเบิก",
  "advance.amountDecimals": "ใส่ทศนิยมได้ไม่เกิน 2 ตำแหน่ง",
  "advance.amountOverUnpaid": "มากกว่ายอดที่ยังไม่จ่ายของ{who} ({total}) ตรวจอีกครั้งก่อนบันทึก",
  "advance.q3": "หักคืนจากบิลไหน",
  "advance.pickPersonFirst": "เลือกคนก่อน แล้วจะเห็นบิลของเขา",
  "advance.monthly": "บิลเงินเดือน ตัดยอด {end}",
  "advance.contract": "บิลสัญญา {contract} ตัดยอด {end}",
  "advance.box": "รายการสะสม (ไม่มีบิลหลัก)",
  "advance.thisOne": "(บิลนี้)",
  "advance.takenOn": "หักคืนครั้งเดียวในบิลนี้ · ลงวันที่ {day}",
  "advance.inRound": "บิลนี้อยู่ในรอบจ่ายแล้ว — เลือกบิลถัดไป",
  "advance.notSameBill": "โอนพร้อมรอบถัดไปแล้วหักในบิลเดียวกันไม่ได้ (เงินจะไม่ถึงมือ)",
  "advance.what": "เบิกไปทำอะไร (บังคับ)",
  "advance.whatPh": "เช่น ค่าเทอมลูก / ค่ารักษาพยาบาล",
  "advance.noItem": "องค์กรนี้ยังไม่มีรายการ \"เงินเบิกล่วงหน้า\" — เพิ่มที่ตั้งค่ารายการเงินก่อน",
  "advance.sum": "{who} เบิก {amount} บาท {out} · หักคืนครั้งเดียวจาก{bill}",
  "advance.sumIncomplete": "ตอบให้ครบ 3 ข้อ แล้วระบบจะสรุปให้ก่อนบันทึก",
  "advance.onceHint": "เงินเบิกหักคืนครั้งเดียวในบิลที่เลือก — จะให้ผ่อนหลายงวด ใช้ \"เงินกู้ยืมบริษัท\"",
  "advance.toLoan": "เปลี่ยนเป็นเงินกู้ยืมบริษัท",
  "advance.need.bill": "บิลที่จะหักคืน",
  "advance.need.note": "เบิกไปทำอะไร",
  "advance.need.evidence": "หลักฐานการจ่าย",
  "advance.saved": "บันทึกการเบิกของ {who} {amount} แล้ว · หักคืนจาก{bill}",
  "advance.chip": "เบิก",
  "debt.postpone.open": "ผ่อนผัน / เลื่อนเริ่มหัก",
  "debt.postpone.title": "ผ่อนผัน — เลื่อนวันเริ่มหัก",
  "debt.postpone.from": "เริ่มหักงวดแรกวันไหน",
  "debt.postpone.quick": "เลื่อนเร็ว",
  "debt.postpone.months": "เลื่อน {n} เดือน",
  "debt.postpone.save": "บันทึกการผ่อนผัน",
  "lend.title": "ให้ยืม / เบิกล่วงหน้า",
  "lend.save": "บันทึก",
  "lend.saved": "บันทึก {code} แล้ว",
  "lend.failed": "บันทึกไม่สำเร็จ",
  "lend.who": "ของใคร · ยืมในรายการไหน",
  "lend.item": "เลือกรายการ (เงินเบิก / เงินกู้)",
  "lend.q1": "เท่าไร",
  "lend.principal": "เงินที่ให้ยืม",
  "lend.interest": "ดอกเบี้ย (จำนวนเงิน ไม่บังคับ)",
  "lend.interestHint": "ดอกเป็นจำนวนเงินคงที่ ไม่ใช่เปอร์เซ็นต์",
  "lend.totalBack": "ต้องคืนทั้งหมด {total} บาท",
  "lend.q2": "โอนเงินให้พนักงานยังไง",
  "lend.out.outside": "โอนให้แล้ว (เงินสด / โอนเอง) — แนบสลิป",
  "lend.out.withPayout": "โอนให้พร้อมรอบจ่ายถัดไป",
  "lend.paidOn": "โอนให้เมื่อ {day}",
  "lend.q3": "คืนยังไง",
  "lend.back.once": "หักคืนครั้งเดียวในบิลถัดไป",
  "lend.back.split": "ผ่อนหลายงวด",
  "lend.instalments": "งวด",
  "lend.count": "จำนวนงวด",
  "lend.startOn": "เริ่มหัก {day}",
  "lend.what": "ยืมไปทำอะไร (บังคับ)",
  "lend.whatPh": "เช่น ซ่อมรถ / ค่าเทอมลูก",
  "lend.sum.incomplete": "ตอบให้ครบ 3 คำถาม แล้วระบบจะสรุปให้ก่อนบันทึก",
  "lend.sum": "จะให้ {who} ยืม {amount} บาท{interest} {out} · {back}",
  "lend.sum.interest": " + ดอก {interest} บาท",
  "lend.sum.outside": "โอนให้แล้วเมื่อ {day} (นอกรอบจ่าย)",
  "lend.sum.withPayout": "โอนให้พร้อมรอบจ่ายถัดไป",
  "lend.sum.once": "หักคืน {total} บาทครั้งเดียวในบิลถัดไป",
  "lend.sum.split": "ผ่อน {n} งวด งวดละ {per} บาท จบ {end}",
  "lend.rate.fixed": "ดอกเป็นจำนวนเงิน (แบบระบบเดิม)",
  "lend.rate.onPay": "ดอกเริ่มนับวันที่รอบจ่ายโอนเงินให้จริง (ระบบตั้งให้ตอนยืนยันว่าโอนแล้ว)",
  "lend.rate.pickDay": "กำหนดวันเริ่มคิดดอกเอง",
  "lend.rate.quoteOnPay": "ดอกรวมประมาณ {interest} บาท · ต้องคืนทั้งหมดประมาณ {total} บาท (คิดใหม่ตามวันที่โอนจริง)",
  "debt.formula.onPay": "เริ่มคิดดอกเมื่อจ่ายเงินจริง",
  "opening.debt.formulaHint": "เงินต้นที่ยังค้าง {left} บาท คิดดอกตั้งแต่วันยกยอด (วันนี้)",
  "opening.debt.formulaTotal": "ยอดที่ให้ยืม (เงินต้น)",
  "lend.rate.label": "ดอกเบี้ย",
  "lend.rate.none": "ไม่มีดอก",
  "lend.rate.flat": "ดอกคงที่ — คิดครั้งเดียวจากเงินต้นเต็มก้อน",
  "lend.rate.reducing": "ลดต้นลดดอก — คิดรายวันจากยอดที่ยังค้าง",
  "lend.rate.pct": "อัตราต่อปี",
  "lend.rate.unit": "% ต่อปี",
  "lend.rate.max": "ไม่เกิน 15% ต่อปีตามกฎหมาย",
  "lend.rate.style": "แบบงวด",
  "lend.rate.equal": "งวดเท่ากันทุกงวด",
  "lend.rate.principal": "ตัดต้นเท่ากัน งวดแรกสูงแล้วลดลง",
  "lend.rate.prepay": "ถ้าคืนก่อนกำหนด",
  "lend.rate.prepayNone": "ไม่ลดดอก",
  "lend.rate.prepayProRata": "ลดดอกของงวดที่ยังไม่ถึง",
  "lend.rate.from": "เริ่มคิดดอก {day}",
  "lend.rate.fromHint": "วันที่เงินถึงมือพนักงาน ดอกนับเป็นรายวันตั้งแต่วันนี้",
  "lend.rate.quote": "ดอกรวม {interest} บาท · ต้องคืนทั้งหมด {total} บาท",
  "lend.rate.quoting": "กำลังคิดดอก…",
  "lend.planFalling": "ระบบคิดให้: งวดแรก {first} บาท ลดลงถึง {last} บาท · งวดสุดท้าย {end}",
  "lend.sum.splitFalling": "ผ่อน {n} งวด งวดแรก {first} บาท ลดลงถึง {last} บาท จบ {end}",
  "debt.formula.flat": "ดอกคงที่ {rate}% ต่อปี",
  "debt.formula.reducing_equal": "ลดต้นลดดอก {rate}% ต่อปี งวดเท่ากัน",
  "debt.formula.reducing_principal": "ลดต้นลดดอก {rate}% ต่อปี ตัดต้นเท่ากัน",
  "debt.formula.from": "เริ่มคิดดอก {day}",
  "debt.formula.prepay": "คืนก่อนกำหนดลดดอกงวดที่ยังไม่ถึง",
  "debt.row.parts": "ต้น {principal} · ดอก {interest}",
  "debt.repay.payoff": "ยอดปิดหนี้ ณ {day}: {payoff} บาท",
  "debt.repay.payoffDiscount": "หักดอกของงวดที่ยังไม่ถึงออกแล้ว {discount} บาท",
  "debt.repay.reprice": "หลังบันทึก ระบบคิดดอกและงวดที่เหลือใหม่ให้ตามวันที่รับคืน",
  "finance.err.interest_from": "วันเริ่มคิดดอกต้องไม่เลยงวดแรก",
  "finance.err.debt_too_much": "ยอดคืนมากกว่าที่ค้างอยู่",
  "finHome.createDebt": "ให้ยืม / เบิก",
  "finPerson.planKept": "เก็บไว้แล้ว",
  "finPerson.lend": "ให้ยืม / เบิก",
  "bills.instalment": "งวดผ่อน",
  "finHome.todo.overdue": "งวดผ่อนถึงกำหนดแต่ยังไม่อยู่ในรอบจ่าย {n} งวด",
  "finHome.todo.overdueDetail": "รวม {total} บาท — บิลที่มีงวดเหล่านี้ติดป้าย \"หักงวดผ่อน\" ในรอบจ่าย หยิบบิลเข้ารอบหรือปล่อยไว้รอบหน้า",
  "finHome.todo.overdueLine": "{who} · {item} {code} งวด {seq}/{count} · {amount} บาท",
  "finHome.act.decide": "ตัดสิน",
  "payBatches.overdue.skippedTimes": "ข้ามมาแล้ว {n} ครั้ง",
  "payBatches.overdue.skip": "ข้ามไปรอบหน้า",
  "payBatches.billInstalments": "หักงวดผ่อน {n} งวด {amount}",
  "payBatches.overdue.showThem": "ดูคนเหล่านี้",
  "payBatches.overdue.checkFailed": "ตรวจงวดผ่อนที่ถึงกำหนดไม่สำเร็จ แช่แข็งได้เมื่อตรวจผ่าน",
  "payBatches.overdue.takeBill": "เอาบิลนี้เข้ารอบ",
  "payBatches.overdue.billEnd": "รอบบิลจบ {date}",
  "payBatches.overdue.billLine": "{title} · ทั้งบิล {total}",
  "payBatches.overdue.inPerson": "บิลอีกใบของคนนี้มีงวดผ่อนถึงกำหนด แต่บิลนั้นยังไม่อยู่ในรอบ",
  "payBatches.overdue.reasonPh": "เหตุผลที่ข้าม (บังคับ)",
  "payBatches.overdue.confirmSkip": "ยืนยันข้าม",
  "payBatches.overdue.skippedToast": "ข้ามไปบิลถัดไปแล้ว",
  "payBatches.freezeBlocked": "แช่แข็งไม่ได้จนกว่าจะตัดสินงวดผ่อนของ {n} คนในรอบ ({names}) ที่การ์ดของแต่ละคน",
  "bills.deferredFrom": "เลื่อนมาจากบิล {date}",
  "bills.deferredFromBox": "เลื่อนมาจากรายการสะสม",
  "payBatches.overdue.checking": "กำลังตรวจงวดผ่อนที่ค้าง…",
  "finPerson.card.advances": "ยืมไปแล้ว ยังไม่หักคืน",
  "finPerson.card.advancesSub": "เบิก {lent} · หักคืน {back}",
  "payBatches.lines.open": "ดูรายการ / ติ๊กออก",
  "payBatches.lines.title": "รายการในรอบนี้",
  "payBatches.lines.keep": "เอา {doc} เข้ารอบนี้",
  "payBatches.lines.cameFrom": "เลื่อนมาจากบิลก่อน · \"{reason}\"",
  "payBatches.lines.reasonPh": "เหตุผลที่ไม่เอาเข้ารอบนี้ (บังคับ)",
  "payBatches.lines.untick": "ไม่เอาเข้ารอบนี้",
  "payBatches.lines.more": "และอีก {n} รายการ — ดูทั้งหมดที่หน้าบิล",
  "payBatches.lines.outTitle": "ไม่เอาเข้ารอบนี้ (ไม่หาย เลื่อนไปบิลถัดไป)",
  "payBatches.lines.putBack": "เอา {doc} กลับเข้ารอบ",
  "payBatches.lines.outLine": "\"{reason}\" · เลื่อนไปบิลถัดไป (ตั้งแต่ {date})",
  "payBatches.lines.total": "รวมที่ติ๊กไว้",
  "payBatches.lines.explain": "เอาออกแล้วไม่หาย — ยังค้างอยู่บนหน้ารายคน และขึ้นในบิลถัดไปเอง · เอาออกต้องใส่เหตุผล · งวดผ่อนที่ถึงกำหนดจะนับครั้งที่ข้าม · ทำได้จนกว่าจะแช่แข็งรอบ",
  "payBatches.lines.unticked": "เลื่อนไปบิลถัดไปแล้ว",
  "payBatches.lines.reticked": "เอากลับเข้ารอบแล้ว",
  "audit.fin.bill_defer": "เลื่อนรายการไปบิลถัดไป",
  "audit.fin.bill_undefer": "เอารายการกลับเข้ารอบ",
  "finImport.title": "นำเข้ารายการเงินจากไฟล์",
  "finImport.open": "นำเข้าจากไฟล์",
  "finImport.listTitle": "ชุดที่นำเข้า",
  "finImport.listHint": "ทุกชุดที่นำเข้าจากไฟล์ ใหม่สุดก่อน — เปิดชุดเพื่อดูทุกรายการที่สร้าง หรือย้อนทั้งชุด",
  "finImport.listEmpty": "ยังไม่เคยนำเข้าจากไฟล์",
  "finImport.x.sheetLines": "รายการ",
  "finImport.x.sheetItems": "รายการเงินที่ใช้ได้",
  "finImport.x.sheetPeople": "พนักงาน",
  "finImport.x.sheetHelp": "วิธีกรอก",
  "finImport.col.employee": "พนักงาน (รหัสหรือชื่อ)",
  "finImport.col.item": "รายการเงิน",
  "finImport.col.amount": "จำนวนเงิน (บาท)",
  "finImport.col.date": "วันที่ (วว/ดด/ปปปป)",
  "finImport.col.count": "ผ่อนกี่งวด (ว่าง = ครั้งเดียว)",
  "finImport.col.start_on": "เริ่มงวดแรก (วว/ดด/ปปปป)",
  "finImport.col.tax_stage": "ก่อน/หลังภาษี",
  "finImport.col.note": "หมายเหตุ",
  "finImport.col.department": "แผนกที่แจ้ง",
  "finImport.col.reported_by": "ผู้แจ้ง",
  "finImport.field.employee": "พนักงาน",
  "finImport.field.item": "รายการเงิน",
  "finImport.field.amount": "จำนวนเงิน",
  "finImport.field.date": "วันที่",
  "finImport.field.count": "จำนวนงวด",
  "finImport.field.start_on": "เริ่มงวดแรก",
  "finImport.field.tax_stage": "ก่อน/หลังภาษี",
  "finImport.x.itemName": "ชื่อรายการ (เลือกชื่อนี้ในช่องรายการเงิน)",
  "finImport.x.itemSide": "ได้/หัก",
  "finImport.x.itemPlan": "ผ่อนได้",
  "finImport.x.itemStage": "ถามก่อน/หลังภาษี",
  "finImport.x.itemFor": "ใช้กับ (ว่าง = ทุกคน)",
  "finImport.x.itemCode": "รหัส",
  "finImport.x.personCode": "รหัส",
  "finImport.x.personName": "ชื่อ",
  "finImport.x.personType": "ประเภท",
  "finImport.x.earn": "ได้",
  "finImport.x.deduct": "หัก",
  "finImport.x.yes": "ใช่",
  "finImport.x.preTax": "ก่อนภาษี",
  "finImport.x.postTax": "หลังภาษี",
  "finImport.x.pickFromList": "เลือกจากรายการในช่องนี้",
  "finImport.x.fileName": "แบบฟอร์มนำเข้ารายการเงิน",
  "finImport.x.help1": "วิธีกรอกแบบฟอร์มนำเข้ารายการเงิน",
  "finImport.x.help2": "1) กรอกในชีต \"รายการ\" หนึ่งบรรทัดต่อหนึ่งรายการ ตั้งแต่บรรทัดที่ 2 — อย่าแก้หัวตาราง",
  "finImport.x.help3": "2) พนักงาน: ใส่รหัส (เช่น DRV-0001) หรือชื่อตามชีต \"พนักงาน\" — ถ้ามีคนชื่อซ้ำกันให้ใส่รหัส",
  "finImport.x.help4": "3) รายการเงิน: เลือกจากรายการในช่อง (มาจากชีต \"รายการเงินที่ใช้ได้\") — รายการบอกเองว่าเป็นเงินได้หรือเงินหัก ไม่ต้องใส่เครื่องหมายลบ",
  "finImport.x.help5": "4) จำนวนเงิน: เป็นบาท เช่น 1,500 หรือ 1500.50",
  "finImport.x.help6": "5) วันที่: วว/ดด/ปปปป เช่น 9/10/2026 (พิมพ์ปี พ.ศ. มาก็อ่านได้)",
  "finImport.x.help7": "6) ผ่อน: ใส่จำนวนงวดและวันเริ่มงวดแรก เฉพาะรายการที่ผ่อนได้ · ก่อน/หลังภาษี: ใส่เฉพาะรายการที่ถาม นอกนั้นเว้นว่าง",
  "finImport.x.help8": "7) อัปโหลดกลับที่หน้า รายการเงิน → นำเข้าจากไฟล์ — ระบบตรวจทุกบรรทัดก่อน ยังไม่บันทึกจนกว่าจะกดนำเข้า และย้อนทั้งชุดได้ภายหลัง",
  "finImport.templateFailed": "สร้างแบบฟอร์มไม่สำเร็จ",
  "finImport.missingCols": "ไฟล์นี้ไม่มีคอลัมน์ {cols} — ใช้แบบฟอร์มจากปุ่มข้างบน",
  "finImport.empty": "ไม่มีข้อมูลในไฟล์ — กรอกตั้งแต่บรรทัดที่ 2 ใต้หัวตาราง",
  "finImport.tooMany": "ไฟล์มี {n} บรรทัด เกิน {max} — แบ่งเป็นหลายไฟล์",
  "finImport.readFailed": "อ่านไฟล์ไม่ได้ — ใช้ไฟล์ .xlsx หรือ .csv",
  "finImport.done": "นำเข้าแล้ว ชุด {code}",
  "finImport.failed": "นำเข้าไม่สำเร็จ",
  "finImport.doneTitle": "นำเข้าแล้ว — ชุด {code}",
  "finImport.doneLine": "{rows} รายการ · {people} คน · หัก {in} · จ่าย {out}",
  "finImport.doneUndo": "ถ้าผิดทั้งชุด เปิดหน้าชุดนี้แล้วกด \"ย้อนทั้งชุด\" — รายการที่ยังไม่เข้ารอบจ่ายจะถูกยกเลิกทั้งหมด",
  "finImport.openJob": "ดูชุด {code}",
  "finImport.commit": "นำเข้า {n} บรรทัด",
  "finImport.commitNone": "นำเข้า",
  "finImport.step1": "ดาวน์โหลดแบบฟอร์ม",
  "finImport.step1Hint": "สร้างจากรายการเงินและรายชื่อพนักงานของบริษัท — ช่องรายการเงินมีให้เลือก",
  "finImport.template": "ดาวน์โหลดแบบฟอร์ม (.xlsx)",
  "finImport.templateBusy": "กำลังสร้าง…",
  "finImport.pastJobs": "ชุดที่นำเข้าแล้ว",
  "finImport.step2": "เลือกไฟล์ที่กรอกแล้ว",
  "finImport.pick": "เลือกไฟล์ .xlsx หรือ .csv",
  "finImport.pickAgain": "เลือกไฟล์ใหม่",
  "finImport.reading": "กำลังอ่านและตรวจ…",
  "finImport.step3": "ตรวจทีละบรรทัด",
  "finImport.showing": "แสดง {n} บรรทัดแรกจาก {total} — เลือก \"มีปัญหา\" หรือ \"ควรดู\" เพื่อดูเฉพาะบรรทัดที่ต้องดู",
  "finImport.summary": "{lines} บรรทัด · บรรทัดที่ผ่าน {people} คน หัก {in} จ่าย {out}",
  "finImport.hasErrors": "{n} บรรทัดมีปัญหา — แก้ในไฟล์แล้วเลือกไฟล์ใหม่ ตอนนี้ยังไม่มีอะไรถูกบันทึก",
  "finImport.allOk": "ผ่านทั้ง {n} บรรทัด — ยังไม่บันทึกจนกว่าจะกดนำเข้า",
  "finImport.filter": "แสดง",
  "finImport.f.all": "ทั้งหมด {n}",
  "finImport.f.bad": "มีปัญหา {n}",
  "finImport.f.warn": "ควรดู {n}",
  "finImport.noneHere": "ไม่มีบรรทัดในกลุ่มนี้",
  "finImport.plan": "ผ่อน {n} งวด เริ่ม {start}",
  "finImport.lineNo": "บรรทัด {n}",
  "finImport.step4": "ทั้งชุดนี้คืออะไร",
  "finImport.reasonPh": "เช่น ค่าปรับจราจรเดือนกันยายนจากใบสั่ง สน.",
  "finImport.reasonHint": "บรรทัดที่ไม่มีหมายเหตุจะใช้ข้อความนี้ · แนบหลักฐานของทั้งชุดได้ (ไม่บังคับ)",
  "finImport.err.employee_missing": "ไม่ได้ใส่",
  "finImport.err.employee_unknown": "ไม่พบพนักงานรหัสหรือชื่อนี้ในบริษัท",
  "finImport.err.employee_ambiguous": "มีพนักงานชื่อนี้มากกว่าหนึ่งคน — ใส่รหัสแทน",
  "finImport.err.item_missing": "ไม่ได้ใส่",
  "finImport.err.item_unknown": "ไม่มีรายการเงินชื่อนี้ — เลือกจากรายการในแบบฟอร์ม",
  "finImport.err.item_ambiguous": "มีรายการชื่อนี้มากกว่าหนึ่ง — ใส่รหัสแทน",
  "finImport.err.item_trip": "ค่าเที่ยวคิดจากใบงาน นำเข้าไม่ได้",
  "finImport.err.item_hidden": "รายการนี้ถูกซ่อนแล้ว",
  "finImport.err.item_computed": "รายการนี้ระบบคิดเอง นำเข้าไม่ได้",
  "finImport.err.item_not_for_person": "รายการนี้ใช้กับพนักงานประเภทนี้ไม่ได้",
  "finImport.err.item_direction": "รายการกับทิศทางเงินไม่ตรงกัน",
  "finImport.err.item_recurring": "โปรไฟล์ของคนนี้จ่ายหรือหักรายการนี้ทุกเดือนอยู่แล้ว — แก้ที่โปรไฟล์ หรือปรับเฉพาะเดือนนั้นในหน้ารายการเงิน",
  "finImport.err.amount_missing": "ไม่ได้ใส่",
  "finImport.err.amount_invalid": "ไม่ใช่จำนวนเงิน — ใส่ตัวเลข เช่น 1,500 หรือ 1500.50",
  "finImport.err.amount_zero": "ต้องมากกว่า 0",
  "finImport.err.count_invalid": "ใส่ตัวเลข 1–60 หรือเว้นว่างถ้าครั้งเดียว",
  "finImport.err.item_no_installments": "รายการนี้ผ่อนไม่ได้",
  "finImport.err.count_too_many": "งวดมากเกินไปสำหรับยอดนี้",
  "finImport.err.date_missing": "ไม่ได้ใส่",
  "finImport.err.date_invalid": "อ่านวันที่ไม่ออก — ใช้ วว/ดด/ปปปป เช่น 9/10/2026",
  "finImport.err.start_invalid": "อ่านวันที่ไม่ออก — ใช้ วว/ดด/ปปปป",
  "finImport.err.tax_stage_invalid": "ใส่ \"ก่อนภาษี\" หรือ \"หลังภาษี\" หรือเว้นว่าง",
  "finImport.err.month_closed": "เดือนนี้ปิดยอดแล้ว",
  "finImport.err.refused": "ระบบไม่รับ: {detail}",
  "finImport.err.other": "ระบบไม่รับ ({code})",
  "finImport.warn.same_in_file": "ซ้ำกับบรรทัด {detail} ในไฟล์นี้ — ตั้งใจหรือเปล่า",
  "finImport.warn.same_in_ledger": "มีรายการเดียวกันในระบบแล้ว ({detail}) — นำเข้าซ้ำหรือเปล่า",
  "finImport.warn.future_day": "วันที่ยังไม่ถึง",
  "finImport.warn.other": "ควรดู ({code})",
  "finImport.status.running": "กำลังทำ",
  "finImport.status.done": "ใช้อยู่",
  "finImport.status.undone": "ย้อนแล้ว",
  "finImport.status.failed": "ไม่สำเร็จ",
  "finImport.rowsPeople": "{rows} รายการ · {people} คน",
  "finImport.undoneLine": "ย้อนแล้ว {date} โดย {who} · ยกเลิก {n} รายการ · คงไว้ {kept}",
  "finImport.takeBack": "หัก {amount}",
  "finImport.payOut": "จ่าย {amount}",
  "finImport.notFound": "ไม่พบชุดนี้",
  "finImport.jobTitle": "ชุดนำเข้า",
  "finImport.evidence": "หลักฐาน {n} ไฟล์",
  "finImport.k.rows": "รายการ",
  "finImport.k.people": "คน",
  "finImport.k.in": "หัก",
  "finImport.k.out": "จ่าย",
  "finImport.failedLine": "นำเข้าไม่สำเร็จ ระบบยกเลิกที่บันทึกไปแล้วทั้งหมด: {why}",
  "finImport.keptWhy": "รายการที่คงไว้อยู่ในรอบจ่ายที่แช่แข็งหรือจ่ายแล้ว หรือเดือนปิดยอดแล้ว — ถ้าต้องแก้ ให้ทำเป็นรายการปรับในรอบถัดไป",
  "finImport.rowsTitle": "รายการที่ชุดนี้สร้าง ({n})",
  "finImport.row.live": "ใช้อยู่",
  "finImport.row.frozen": "อยู่ในรอบจ่ายแล้ว",
  "finImport.row.paid": "จ่ายแล้ว",
  "finImport.row.locked": "ปิดเดือนแล้ว",
  "finImport.row.voided": "ยกเลิกแล้ว",
  "finImport.undo.open": "ย้อนทั้งชุด",
  "finImport.undo.title": "ย้อนชุด {code}",
  "finImport.undo.confirm": "ย้อน {n} รายการ",
  "finImport.undo.what": "จะยกเลิก {n} รายการที่ชุดนี้สร้างและยังไม่เข้ารอบจ่าย",
  "finImport.undo.keeps": "อีก {n} รายการอยู่ในรอบจ่ายแล้ว — ที่ยังไม่จ่ายต้องตีกลับรอบก่อน ที่จ่ายแล้วระบบจะทำรายการกลับลงบิลถัดไปให้",
  "finImport.undo.reason": "ทำไมถึงย้อน (บังคับ)",
  "finImport.undo.doneReversed": "ย้อนชุด {code} แล้ว · ยกเลิก {n} รายการ · อีก {reversed} รายการจ่ายไปแล้ว ระบบหักคืนในบิลถัดไปให้",
  "finImport.undo.inRound": "ย้อนไม่ได้ — บางรายการอยู่ในรอบ {rounds} ที่ยังไม่จ่าย ตีกลับรอบนั้นก่อน แล้วค่อยย้อน",
  "finImport.undo.done": "ย้อนชุด {code} แล้ว · ยกเลิก {n} · คงไว้ {kept}",
  "finImport.undo.failed": "ย้อนไม่สำเร็จ",
  "finImport.undo.nothing": "ทุกรายการของชุดนี้อยู่ในรอบจ่ายแล้ว หรือยกเลิกไปแล้ว — ย้อนไม่ได้",
  "finImport.undo.explain": "ย้อนแล้วรายการจะถูกยกเลิก (ไม่ลบ) พร้อมประวัติครบ · ที่จ่ายไปแล้วระบบจะทำรายการกลับลงบิลถัดไปของแต่ละคน",
  "audit.fin.bulk_job_import": "นำเข้ารายการเงินจากไฟล์",
  "audit.fin.bulk_job_undo": "ย้อนชุดนำเข้า",
  "finImport.undoneLineAll": "ย้อนแล้ว {date} โดย {who} · ยกเลิกครบ {n} รายการ",
  "leave.title": "ปิดยอดทั้งหมด — {name}",
  "leave.subtitle": "วันลาออก {date} (จาก HR)",
  "leave.open": "ปิดยอดทั้งหมด (ลาออก)",
  "leave.banner": "ลาออกวันที่ {date} — ยังไม่ได้ปิดยอด รอบจ่ายที่มีคนนี้จะแช่แข็งไม่ได้จนกว่าจะปิดยอด",
  "leave.intro": "ระบบรวมยอดค้างทุกก้อนให้แล้ว — ตัดสินใจแค่ค่าเสียหาย",
  "leave.line.debt": "{item} {code} · เหลือ {n} งวด",
  "leave.line.deposit": "คืนเงินค้ำประกันที่หักไว้ {code}",
  "leave.line.depositDropped": "ไม่เก็บอีก {n} งวดที่เหลือ",
  "leave.line.bills": "เงินในบิลที่ยังไม่จ่าย ({n} บิล)",
  "leave.nothingOpen": "ไม่มียอดค้าง",
  "leave.damage": "หักค่าเสียหาย (ถ้ามี)",
  "leave.damageHint": "หักจากเงินค้ำ/ยอดสุดท้าย · ผูกเคสความปลอดภัยได้",
  "leave.case": "เคสความปลอดภัยที่เกี่ยวข้อง",
  "leave.noCase": "ไม่ผูกเคส",
  "leave.tooEarly": "ปิดยอดได้ตั้งแต่วันทำงานวันสุดท้าย ({date}) — ตอนนี้ดูยอดล่วงหน้าได้อย่างเดียว",
  "leave.info.lastBill": "บิลสุดท้าย: {title} รอบ {from}–{to} จ่าย {payout}",
  "leave.info.lastBillNoWindow": "บิลสุดท้าย: {title}",
  "leave.info.moneyDay": "ยอดที่ปิดลงวันที่ {date}",
  "leave.total": "ยอดสุดท้าย",
  "leave.negative": "ติดลบ = เขาค้างบริษัท — เลือกด้านล่าง",
  "leave.positive": "บริษัทจ่ายยอดนี้ในรอบจ่ายถัดไป",
  "leave.negativeQ": "ยอดติดลบ ทำอย่างไร",
  "leave.neg.carry": "ยกยอดไว้เป็นหนี้ค้าง",
  "leave.neg.carryHint": "บิลสุดท้ายเป็นศูนย์ ยอดนี้เป็นหนี้ที่รับคืนนอกรอบ (ดูได้ที่หน้าหนี้)",
  "leave.neg.write_off": "ตัดเป็นหนี้สูญ",
  "leave.neg.write_offHint": "บริษัทไม่เรียกเก็บ บันทึกเป็นรายการตัดหนี้พร้อมเหตุผล",
  "leave.negWhy": "เหตุผล (บังคับ)",
  "leave.note": "บันทึกพิเศษ (บังคับ)",
  "leave.notePh": "เช่น ลาออกตามใบลาออกเลขที่ … ส่งคืนรถและอุปกรณ์ครบ",
  "leave.info.inBatch": "อยู่ในรอบจ่ายที่แช่แข็งแล้ว {n} บิล {total} — จ่ายตามรอบเดิม ไม่นับในยอดนี้",
  "leave.info.holds": "มีรายการพักไว้ {n} รายการ {total} — ตัดสินใจแยก ไม่นับในยอดนี้",
  "leave.info.later": "อีก {n} รายการลงวันที่หลังวันนี้ — เข้าบิลของมันเอง",
  "leave.info.recurring": "รายการประจำหลังวันลาออก {n} รายการจะถูกยกเลิก",
  "leave.info.stops": "กดแล้ว หนี้ทุกก้อนเหลืองวดเดียวลงวันที่ยอดปิด และหยุดสร้างงวดใหม่",
  "leave.submit": "ปิดยอดลาออก",
  "leave.done": "ปิดยอดลาออกแล้ว",
  "leave.failed": "ปิดยอดไม่สำเร็จ",
  "leave.noDate": "ยังไม่มีวันลาออก — ใส่วันลาออกที่ข้อมูลการเงินของพนักงานก่อน",
  "finHome.todo.leaving": "{n} คนถึงวันทำงานสุดท้ายแล้ว ยังไม่ปิดยอด",
  "finHome.todo.leavingDetail": "ปิดยอดก่อนรอบจ่ายสุดท้ายของเขา — รอบที่มีคนนี้แช่แข็งไม่ได้จนกว่าจะปิด",
  "finHome.todo.leavingLine": "{who} · วันทำงานสุดท้าย {date}",
  "finHome.act.settle": "ปิดยอด",
  "finHome.todo.leavingSoon": "{n} คนจะออกงาน — วันทำงานวันสุดท้ายใกล้ถึง",
  "finHome.todo.leavingSoonDetail": "คนแรกทำงานวันสุดท้าย {date} เปิดแผ่นปิดยอดดูยอดล่วงหน้าได้",
  "finHome.todo.afterLeaving": "ยังต้องทวงหลังออกงาน {total} บาท จาก {n} คน",
  "finHome.todo.afterLeavingDetail": "หนี้ที่ยกไว้หลังออกงานรับคืนนอกรอบ ดูว่าใครค้างเท่าไรและรับคืนไปแล้วเท่าไรได้ที่หน้าคนออกงาน",
  "finHome.act.leavers": "ดูคนออกงาน",
  "nav.financeLeavers": "คนออกงาน",
  "leavers.title": "คนออกงาน",
  "leavers.lead": "คนที่กำลังจะออก คนที่ออกไปแล้ว และคนที่ยังค้างเงินกับบริษัทหลังออกงาน ปิดยอดและตามรับคืนได้จากหน้านี้",
  "leavers.total.unsettled": "ยังไม่ปิดยอด",
  "leavers.total.owing": "ยังค้างหลังออก",
  "leavers.total.owed": "รวมต้องทวง",
  "leavers.total.people": "{n} คน",
  "leavers.total.baht": "{total} บาท",
  "leavers.col.person": "ชื่อ",
  "leavers.col.status": "สถานะ",
  "leavers.col.lastDay": "วันทำงานสุดท้าย",
  "leavers.col.stage": "การปิดยอด",
  "leavers.col.owed": "ค้างหลังออก",
  "leavers.stage.coming": "จะออก",
  "leavers.stage.unsettled": "ถึงวันแล้ว ยังไม่ปิดยอด",
  "leavers.stage.settled": "ปิดยอดแล้ว",
  "leavers.out.carry": "ยกยอดเป็นหนี้ ตามรับคืนนอกรอบ",
  "leavers.out.write_off": "ตัดหนี้สูญ",
  "leavers.out.pay": "บริษัทจ่ายส่วนที่เหลือ {total} บาท",
  "leavers.out.zero": "ไม่ค้างกัน",
  "leavers.settledOn": "ปิดเมื่อ {day}",
  "leavers.inDays": "อีก {n} วัน",
  "leavers.today": "วันนี้",
  "leavers.owed": "ค้าง {amount} บาท",
  "leavers.collected": "รับคืนแล้ว {amount} บาท",
  "leavers.collectedAll": "รับคืนครบ {amount} บาท",
  "leavers.settle": "ปิดยอด",
  "leavers.openDebt": "ดูหนี้ / รับคืนนอกรอบ",
  "leavers.empty": "ยังไม่มีใครออกงานหรือกำลังจะออก",
  "audit.fin.person_finance_settle": "ปิดยอดลาออก",
  "leave.undo.open": "ย้อนการปิดยอด",
  "leave.undo.title": "ย้อนการปิดยอด — {name}",
  "leave.undo.intro": "ระบบจะคืนทุกอย่างที่การปิดยอดเปลี่ยนไปให้เป็นอย่างเดิม แล้วคุณแก้หรือยกเลิกการออกงานของคนนี้ได้อีก",
  "leave.undo.backHead": "สิ่งที่จะกลับมา",
  "leave.undo.backNone": "ไม่มีงวดที่ต้องคืน",
  "leave.undo.debt": "{item} {code} กลับเป็นแผนเดิม เหลือ {n} งวด",
  "leave.undo.deposit": "เงินค้ำประกัน {code} กลับมาเก็บตามแผนเดิม",
  "leave.undo.recurring": "รายการประจำหลังวันลาออก {n} รายการกลับมา",
  "leave.undo.cancelHead": "สิ่งที่จะถูกยกเลิก",
  "leave.undo.cancelRows": "รายการที่การปิดยอดสร้างไว้ {n} รายการ (เช่น งวดสุดท้าย คืนเงินค้ำ ค่าเสียหาย ตัดหนี้สูญ หรือหนี้ที่ยกไว้)",
  "leave.undo.cancelDamage": "ค่าเสียหาย {amount}",
  "leave.undo.cancelCarry": "หนี้ค้างที่ยกไว้ {amount} ({code})",
  "leave.undo.cancelWriteOff": "ตัดหนี้สูญ {amount}",
  "leave.undo.after": "ย้อนแล้วจะปิดยอดใหม่ได้อีกครั้ง บิลของคนนี้กลับเป็นอย่างที่เป็นก่อนกดปิดยอด",
  "leave.undo.reason": "เหตุผลที่ย้อน (บังคับ)",
  "leave.undo.reasonPh": "เช่น ใส่ยอดค่าเสียหายผิด หรือวันลาออกเปลี่ยน",
  "leave.undo.submit": "ย้อนการปิดยอด",
  "leave.undo.done": "ย้อนการปิดยอดแล้ว",
  "leave.undo.failed": "ย้อนการปิดยอดไม่สำเร็จ",
  "leave.undo.blocked.leaving_not_settled": "คนนี้ยังไม่ได้ปิดยอด จึงไม่มีอะไรให้ย้อน",
  "leave.undo.blocked.leaving_undo_no_record": "การปิดยอดครั้งนี้ทำไว้ก่อนที่ระบบจะเก็บแผนงวดเดิมไว้ จึงย้อนให้เองไม่ได้ ถ้าต้องแก้ ให้แก้งวดผ่อนและรายการของคนนี้ทีละรายการที่หน้าหนี้",
  "leave.undo.blocked.leaving_undo_in_round": "ยอดสุดท้ายหรืองวดผ่อนของคนนี้อยู่ในรอบจ่ายที่แช่แข็ง อนุมัติ หรือจ่ายแล้ว ({docs}) จึงย้อนไม่ได้ในตอนนี้ ให้ส่งรอบนั้นกลับก่อน แล้วมากดย้อนอีกครั้ง ถ้ารอบนั้นจ่ายไปแล้วจะย้อนไม่ได้",
  "leave.undo.blocked.leaving_undo_changed": "รายการ {docs} ถูกแก้หลังปิดยอด ระบบจึงไม่เอาแผนเดิมไปทับ ให้ดูประวัติของรายการเหล่านั้นก่อน",
  "audit.fin.person_finance_settle_undo": "ย้อนการปิดยอดลาออก",
  "leave.settledHead": "ปิดยอดลาออกแล้ว {date} โดย {who}",
  "leave.outcomeCarry": "หลังจ่ายรอบสุดท้าย ยังค้างบริษัท {amount} — หนี้ {code} รับคืนนอกรอบ",
  "leave.outcomeWriteOff": "ตัดหนี้สูญ {amount} ({reason}) — หลังจ่ายรอบสุดท้ายไม่ค้างกัน",
  "leave.outcomePay": "บริษัทจ่ายยอดสุดท้าย {total} ในรอบจ่ายถัดไป — หลังจากนั้นไม่ค้างกัน",
  "leave.outcomeZero": "หลังจ่ายรอบสุดท้าย ไม่ค้างกันทั้งสองฝ่าย",
  "leave.card.lastBill": "หักในบิลสุดท้าย",
  "leave.card.lastBillSub": "{n} ก้อน · หมดเมื่อจ่ายรอบสุดท้าย",
  "leave.card.owedAfter": "ค้างหลังลาออก",
  "leave.card.owedAfterSub": "รับคืนนอกรอบ · {codes}",
  "leave.chip.carried": "ค้างหลังลาออก · รับคืนนอกรอบ",
  "leave.chip.lastBill": "หักในบิลสุดท้าย",
  "finSettings.sum.earn": "จะเพิ่ม \"{name}\" เป็นเงินที่จ่ายให้",
  "finSettings.sum.deduct": "จะเพิ่ม \"{name}\" เป็นเงินที่หักคืน",
  "finSettings.sum.contractorAfterTax": "หักจากคนขับภายนอกหลังหัก ณ ที่จ่าย 1%",
  "finSettings.sum.postTax": "หักหลังภาษี",
  "finSettings.sum.monthlyHomeSite": "เดือนละครั้ง จากบิลเงินเดือน หรือบิลของสัญญาที่วิ่งมากที่สุดในเดือน (รถร่วม)",
  "finSettings.sum.perDay": "คิดจากจำนวนวัน",
  "finSettings.sum.installments": "ผ่อนเป็นงวดได้",
  "finSettings.sum.everyone": "ใช้กับทุกคน",
  "finSettings.sum.onlyFor": "ใช้เฉพาะ{types}",
  "finSettings.added": "เพิ่ม \"{name}\" แล้ว",
  "finSettings.savedLater": "บันทึกแล้ว วิธีคิดใหม่ของ \"{name}\" มีผลตั้งแต่ {day}",
  "finSettings.f.when": "วิธีคิดใหม่มีผลตั้งแต่",
  "finSettings.f.whenToday": "วันนี้",
  "finSettings.f.whenNote": "รายการเงินที่บันทึกไว้แล้วไม่เปลี่ยนตาม ส่วนรายการที่ลงวันตั้งแต่วันที่เลือกจะใช้วิธีคิดใหม่",
  "finSettings.f.whenLater": "ถึงวันที่ {day} ระบบจะเปลี่ยนให้เอง ระหว่างนี้ยังใช้วิธีคิดเดิม ยกเลิกได้จากประวัติด้านล่างหรือหน้ารอมีผล",
  "finSettings.f.history": "ประวัติวิธีคิด",
  "finSettings.tag.waiting": "เปลี่ยนวิธีคิด {day}",
  "pending.kind.finance_item": "วิธีคิดรายการเงิน",
  "finSettings.saved": "บันทึก \"{name}\" แล้ว",
  "finSettings.hiddenToast": "ซ่อน \"{name}\" แล้ว — รายการเงินที่ใช้ชื่อนี้อยู่แล้วไม่หาย",
  "finSettings.shownToast": "แสดง \"{name}\" แล้ว",
  "finSettings.save": "บันทึก",
  "finSettings.failed": "บันทึกไม่สำเร็จ",
  "closed.payroll.heading": "ระบบเงินเดือนเดิมปิดแล้ว",
  "closed.payroll.body": "เงินทุกบาทเดินทางเดียวแล้ว: รายการเงิน → บิล → รอบจ่าย → ไฟล์ธนาคาร เงินเดือนและค่าเที่ยวไปรวมอยู่ในบิลของแต่ละคน แล้วจ่ายผ่านรอบจ่าย",
  "closed.formulas.heading": "หน้าสูตรคำนวณเอาออกแล้ว",
  "closed.formulas.body": "หน้านี้เป็นตัวอย่างที่ไม่ได้ต่อกับระบบจริง ค่าเที่ยวกำหนดบนใบงาน (ตั้งต้นจากเรทในสัญญาหรือเรทรายจังหวัด) ส่วนเงินเดือนตั้งที่แท็บการจัดการเงินของพนักงานแต่ละคน",
  "closed.toBills": "ไปที่บิล",
  "closed.toPayBatches": "ไปที่รอบจ่าย",
  "closed.toSalaryRules": "ไปที่รายชื่อพนักงาน",
  "closed.toProvinceRates": "ไปที่เรทรายจังหวัด",
  "driverPay.notPayment": "หน้านี้ใช้ตัดสินค่าเที่ยวทีละเที่ยว (จ่ายเท่าไร หรือไม่จ่าย) ยังไม่ได้จ่ายเงินใคร — จ่ายจริงทำที่ การเงิน › รอบจ่าย",
  "driverPay.toPayBatches": "ไปที่รอบจ่าย",
  "driverPay.exportReport": "ดาวน์โหลดรายงาน .xlsx",
  "driverPay.lockHint": "ล็อกงวด {period}: ค่าเที่ยวเดือนนี้จะไม่เปลี่ยนตามใบงานอีก (ไม่ใช่การจ่ายเงิน)",
  "driverPay.lockPending": "ยังมี {n} เที่ยวในงวดนี้ที่ต้องตัดสินก่อน",
  "driverPay.lockTitle": "ล็อกงวด {period}?",
  "driverPay.lockBody": "ค่าเที่ยวทั้งงวดจะถูกตรึงไว้ที่ {total} ({payable} เที่ยวที่จ่าย, {excluded} เที่ยวที่ไม่จ่าย) หลังล็อก แก้เรทหรือคำตัดสินย้อนหลังไม่ได้ ส่วนที่ต้องแก้จะเป็นรายการปรับในงวดถัดไป — การล็อกไม่ได้จ่ายเงินใคร จ่ายจริงทำที่รอบจ่าย",
  "driverPay.lockConfirm": "ล็อกงวด",
  "nav.financeTxn": "รายการเงิน",
  "financeTxn.role.ticket": "ใบงาน",
  "financeTxn.role.vehicle": "รถ",
  "financeTxn.role.employee": "พนักงาน",
  "financeTxn.role.contract": "สัญญา",
  "financeTxn.role.route": "เส้นทาง",
  "financeTxn.vendorName": "หรือพิมพ์ชื่อผู้ขาย",
  "financeTxn.syncedAt": "คัดข้อมูลล่าสุด {at}",
  "financeTxn.neverSynced": "ยังไม่เคยคัดข้อมูลเข้าตารางนี้",
  "financeTxn.searchTicket": "ค้นหาใบงาน",
  "financeTxn.searchVehicle": "ค้นหาทะเบียน / รหัสรถ",
  "financeTxn.searchEmployee": "ค้นหาชื่อ / รหัสพนักงาน",
  "financeTxn.searchContract": "ค้นหาสัญญา",
  "financeTxn.searchRoute": "ค้นหารหัส / ชื่อเส้นทาง",
  "financeTxn.title": "รายการเงิน",
  "financeTxn.subtitle": "ทุกรายการเงินที่เกิดขึ้นในระบบ",
  "financeTxn.docNo": "เลขที่",
  "financeTxn.type": "ประเภท",
  "financeTxn.party": "จ่ายให้ / รับจาก",
  "financeTxn.amount": "จำนวนเงิน",
  "financeTxn.occurredOn": "วันที่ทำรายการ",
  "financeTxn.occurredTime": "เวลา",
  "financeTxn.noTime": "ต้นทางไม่ได้บันทึกเวลา",
  "financeTxn.linkedDoc": "เอกสารอ้างอิง",
  "financeTxn.kind.recurring_adjust": "ปรับเงินเดือน/ลาย้อนหลัง",
  "financeTxn.kind.trip_pay_adjust": "ปรับค่าเที่ยวหลังจ่าย",
  "financeTxn.kind.carry": "ยกยอด",
  "financeTxn.kind.trip_pay": "ค่าเที่ยวคนขับ",
  "financeTxn.kind.job_revenue": "รายรับตามเส้นทาง",
  "financeTxn.kind.manual": "รายการบันทึกเอง",
  "financeTxn.origin.manual": "บันทึกเอง",
  "financeTxn.edited": "แก้ไขแล้ว",
  "financeTxn.estimatedTime": "เวลาโดยประมาณ",
  "financeTxn.revenue": "รายรับ",
  "financeTxn.expense": "รายจ่าย",
  "financeTxn.direction": "รายรับ / รายจ่าย",
  "financeTxn.sourceRollup": "ยอดรวมจากตารางสรุป",
  "financeTxn.sourceGroup": "ยอดรวมคำนวณสด",
  "financeTxn.add": "เพิ่มรายการ",
  "financeTxn.addTitle": "บันทึกรายการเงิน",
  "financeTxn.linkFirst": "ผูกกับเอกสารอะไร",
  "financeTxn.reason": "เหตุผล",
  "financeTxn.save": "บันทึก",
  "financeTxn.cancel": "ยกเลิก",
  "financeTxn.noMatch": "ไม่มีรายการที่ตรงกับตัวกรอง",
  "financeTxn.noneYet": "ยังไม่มีรายการเงินในช่วงที่เลือก",
  "financeTxn.empty": "ยังไม่มีรายการเงินในช่วงที่เลือก",
  "financeTxn.lastSynced": "คัดข้อมูลล่าสุด",
  "financeTxn.notRealtime": "ทุกรายการเงินของบริษัท หนึ่งบรรทัดหนึ่งรายการ — ค่าเที่ยวเข้ามาทันทีที่ปิดใบงาน",
  "financeTxn.history": "ประวัติการแก้ไข",
  "financeTxn.derivedValue": "ค่าจากต้นทาง",
  "financeTxn.effectiveValue": "ค่าที่ใช้จริง",
  // แถวที่คนสร้างเองไม่มีชั้นค่าต้นทางให้คืน — ห้ามเขียนว่า "คืนค่าเดิม" (ADR 0357 F9)
  "financeTxn.clearOverride": "ลบบันทึกการแก้",
  "financeTxn.reverse": "สร้างรายการกลับ",
  "financeTxn.void": "ยกเลิกรายการ",
  "financeTxn.lockedNotice": "เดือนนี้ปิดยอดแล้ว — แก้ไม่ได้ ต้องสร้างรายการกลับ",
  // hunt r1-money ข้อ 1/4/5/6 (เคาะ 2026-10-25) — กล่อง "เพิ่มรายการ" ของบริษัท (AddTxnDialog)
  "financeTxn.missingPrefix": "ยังไม่มี: {fields}",
  "financeTxn.missingLink": "เอกสารที่ผูก",
  "financeTxn.missingAmount": "จำนวนเงิน",
  "financeTxn.amountDecimals": "ใส่ทศนิยมได้ไม่เกิน 2 ตำแหน่ง",
  "financeTxn.amountSignHint": "เลือกรายรับ / รายจ่ายด้วยปุ่มด้านบน — ช่องนี้ใส่ได้เฉพาะตัวเลข ไม่ต้องใส่เครื่องหมายลบ",
  "financeTxn.amountLargeWarn": "จำนวนนี้สูงกว่าปกติ (เกิน {max} บาท) — กด \"บันทึก\" อีกครั้งเพื่อยืนยันว่าตั้งใจ",
  "financeTxn.amountLargeConfirmed": "ยืนยันจำนวนนี้แล้ว — กด \"บันทึก\" อีกครั้งเพื่อบันทึกจริง",
  "financeTxn.editAction": "แก้จำนวน",
  "financeTxn.editTitle": "แก้รายการเงิน",
  "financeTxn.editReason": "เหตุผลที่แก้",
  "financeTxn.editReasonRequired": "บอกเหตุผลที่แก้ก่อนบันทึก",
  "financeTxn.editLinkedReadonly": "ย้ายเอกสารที่ผูกไม่ได้ที่นี่ — เอกสารเดิมคงอยู่",
  "financeTxn.editNoChange": "ยังไม่มีอะไรเปลี่ยน",
  "financeTxn.staleWrite": "รายการนี้ถูกเปลี่ยนไปแล้ว — โหลดใหม่ก่อน",

  /* ── ADR 0363 · หน้าแดชบอร์ด ─────────────────────────── */
  "dashboard.tab.today": "วันนี้",
  "dashboard.tab.ontime": "ตรงเวลา & งาน",
  "dashboard.tab.fleet": "รถ & น้ำมัน",
  "dashboard.tab.contract": "สัญญา",
  "dashboard.tab.people": "คน",
  "dashboard.tile.today.timeline": "งานวันนี้",
  "dashboard.tile.today.overdueConfirm": "ใบงานที่เลยเวลารับงาน",
  "dashboard.tile.today.overdueStart": "ใบงานที่เลยเวลาเริ่มงาน",
  "dashboard.tile.jobs.ontimeSplit": "ตรงเวลา · สาย · วัดไม่ได้",
  "dashboard.tile.jobs.lateByRoute": "เส้นทางที่สายบ่อยที่สุด",
  "dashboard.tile.jobs.completedVia": "งานที่แอดมินกดจบให้",
  "dashboard.tile.jobs.cancelled": "ยกเลิกในช่วงนี้",
  "dashboard.tile.fleet.statusSplit": "สถานะรถทั้งฟลีต",
  "dashboard.tile.fuel.kmplFleet": "อัตราสิ้นเปลืองเฉลี่ย",
  "dashboard.tile.fuel.worstVehicles": "รถที่กินน้ำมันผิดปกติ",
  "dashboard.tile.fuel.cost": "ค่าน้ำมันในช่วงนี้",
  "dashboard.tile.contracts.expiring": "สัญญาใกล้หมดอายุ",
  "dashboard.tile.contracts.remindersDue": "บันทึกช่วยจำที่ถึงกำหนด",
  "dashboard.tile.finance.roundsWaiting": "รอบจ่ายรออนุมัติ",
  "dashboard.unit.round": "รอบ",
  "dashboard.unit.roundWaiting": "รอบรออนุมัติ",
  "dashboard.rounds.waiting": "รออนุมัติ",
  "dashboard.rounds.approved": "อนุมัติแล้ว รอยืนยันจ่าย",
  "dashboard.rounds.approvedN": "อนุมัติแล้วรอจ่าย {n} รอบ",
  "dashboard.rounds.none": "ไม่มีรอบจ่ายค้างอนุมัติ",
  "dashboard.rounds.open": "ดูรอบจ่ายทั้งหมด",
  "dashboard.note.roundsWaiting": "ตัวเลขคือรอบที่แช่แข็งแล้วรออนุมัติ — รอบที่อนุมัติแล้วแต่ยังไม่ยืนยันจ่ายแสดงในรายการด้วย",
  "dashboard.tile.people.leavePending": "ใบลาที่รออนุมัติ",
  "dashboard.tile.people.leaveToday": "คนลาวันนี้",
  "dashboard.tile.people.docsExpiring": "เอกสารใกล้หมดอายุ",
  "dashboard.tile.people.headcount": "จำนวนพนักงาน",
  "dashboard.cannotCompute.noTicketsToday": "วันนี้ยังไม่มีใบงาน",
  "dashboard.cannotCompute.noTicketsInPeriod": "ยังไม่มีใบงานในช่วงนี้",
  "dashboard.cannotCompute.noLateTicket": "ช่วงนี้ไม่มีใบสาย",
  "dashboard.cannotCompute.noCompletedTicket": "ช่วงนี้ยังไม่มีใบที่จบ",
  "dashboard.cannotCompute.noApprovedFuelEntries": "ยังไม่มีใบเติมที่อนุมัติในช่วงนี้",
  "dashboard.cannotCompute.noMeasuredVehicle": "ยังไม่มีรถที่วัดอัตราสิ้นเปลืองได้",
  "dashboard.period.7d": "7 วัน",
  "dashboard.period.30d": "30 วัน",
  "dashboard.period.90d": "90 วัน",
  "dashboard.empty": "ยังไม่มีข้อมูลในช่วงนี้",
  "dashboard.unit.ticket": "ใบ",
  "dashboard.unit.vehicle": "คัน",
  "dashboard.unit.person": "คน",
  "dashboard.unit.contract": "ฉบับ",
  "dashboard.unit.reminder": "รายการ",
  "dashboard.unit.reminderOverdue": "รายการเกินกำหนด",
  "dashboard.unit.thb": "บาท",
  "dashboard.unit.km_per_l": "กม./ลิตร",
  "dashboard.split.onTime": "ตรงเวลา",
  "dashboard.split.late": "สาย",
  "dashboard.split.unmeasurable": "วัดไม่ได้",
  "dashboard.split.byDriver": "คนขับกดจบ",
  "dashboard.split.byAdmin": "แอดมินกดจบ",
  "dashboard.split.unknown": "ไม่ระบุ",
  "dashboard.fleet.active": "ใช้งานอยู่",
  "dashboard.fleet.repair": "ซ่อมบำรุง",
  "dashboard.fleet.stored": "จอดเก็บ",
  "dashboard.fleet.decommissioned": "ปลดระวาง",
  "dashboard.total": "รวม",
  "dashboard.daysLeft": "วัน",
  "dashboard.daysLeftN": "เหลือ {n} วัน",
  "dashboard.reminder.overdue": "เกินกำหนดแล้ว",
  "dashboard.reminder.dueSoon": "ถึงกำหนดใน 7 วัน",
  "dashboard.reminder.open": "เปิดอยู่ทั้งหมด",
  "dashboard.today": "วันนี้",
  "dashboard.noRoute": "ใบงานที่ไม่มีเส้นทาง",
  "dashboard.noExpiring": "ไม่มีสัญญาใกล้หมดใน 30 วัน",
  "dashboard.asOfNow": "ณ ตอนนี้",
  "dashboard.band.tickets": "ใบงาน",
  "dashboard.band.fuel": "น้ำมัน",
  "dashboard.band.vehicles": "รถ",
  "dashboard.tile.today.cancelled": "ใบงานที่ยกเลิก",
  "dashboard.tile.fuel.logsToday": "ใบเติม",
  "dashboard.tile.fuel.pending": "ยังไม่ตรวจ",
  "dashboard.tile.fuel.reviewed": "ตรวจแล้ว",
  "dashboard.tile.today.vehiclesInUse": "รถที่ใช้งาน",
  "dashboard.tile.vehicle.docsExpiring": "ประกัน/ภาษีใกล้หมด",
  "dashboard.cannotCompute.notWiredYet": "—",
  "dashboard.shape.draft": "ร่าง",
  "dashboard.shape.assigned": "มอบหมาย",
  "dashboard.shape.driverAccepted": "รับงาน",
  "dashboard.shape.started": "เริ่ม",
  "dashboard.shape.inProgress": "กำลังวิ่ง",
  "dashboard.shape.completed": "เสร็จสิ้น",
  "dashboard.shape.closed": "ไม่ใช้งาน",
  "dashboard.shape.other": "สถานะอื่น",
  "dashboard.shape.now": "ตอนนี้",
  "dashboard.shape.noTime": "ไม่มีเวลาออก",
  "dashboard.shape.axis": "เวลาออกตามกำหนด (น.) · ทั้งวัน 00–23 · ตัวเลขบนแท่ง = จำนวนใบ",
  "dashboard.shape.yUnit": "ใบ",
  "dashboard.shape.totalOf": "รวม {n} ใบ",
  "dashboard.compare.yesterdayNow": "เมื่อวานเวลานี้",
  "dashboard.compare.yesterdayFull": "เมื่อวานทั้งวัน",
  "dashboard.compare.prevPeriod": "ช่วงก่อน",
  "dashboard.compare.sameDayLastMonth": "เดือนก่อน",
  "dashboard.compare.sameDayLastMonthLong": "เดือนก่อน ณ วันเดียวกัน",
  "dashboard.compare.none": "—",
  "dashboard.period.thisMonth": "เดือนนี้",
  "dashboard.period.label": "ช่วง",
  "dashboard.period.todayAt": "วันที่ {date} · ข้อมูล ณ {time} น.",
  "dashboard.period.asOfVs": "ณ {date} · เทียบภาพเดียวกัน ณ {prev}",
  "dashboard.end.oldestPending": "เก่าสุดค้างมา {n} วัน",
  "dashboard.end.approved": "อนุมัติ {n}",
  "dashboard.end.rejected": "ตีกลับ {n}",
  "dashboard.end.refusedByDriver": "คนขับปฏิเสธ {n}",
  "dashboard.end.measured": "วัดได้ {n}/{total}",
  "dashboard.end.fromApproved": "จากใบอนุมัติ {n}",
  "dashboard.end.clear": "เคลียร์ →",
  "dashboard.end.ofDrivers": "เป็นคนขับ {n}",
  "dashboard.end.expiredAlready": "หมดอายุ {n}",
  "dashboard.end.withinDays": "ภายใน {n} วัน",
  "dashboard.end.ofActive": "จากที่ยังใช้งาน {n} ฉบับ",
  "dashboard.end.overdueItems": "{n} รายการเกินกำหนด",
  "dashboard.end.notRanked": "{n} คันไม่เข้าอันดับ — คำนวณไม่ได้",
  "dashboard.end.ofMeasured": "สาย {pct}% ของใบที่วัดได้ {n} ใบ",
  "dashboard.end.fromPct": "จาก {pct}%",
  "dashboard.end.outOf": "จาก {n} ใบ",
  "dashboard.hint.unmeasurable": "“วัดไม่ได้” คือก้อนที่สาม — ไม่ถูกนับเป็นตรงเวลา",
  "dashboard.hint.worstFuel": "เทียบกับค่าสเปกของรุ่นนั้น — ยิ่งแท่งสั้นยิ่งกินหนัก",
  "dashboard.note.ontimeSplit": "“วัดไม่ได้” = ไม่มีเวลาคาดว่าจบ หรือใบยังไม่เริ่มวิ่ง — ไม่นับเป็นตรงเวลา",
  "dashboard.note.lateByRoute": "เข้มเฉพาะ 3 อันดับแรก ที่เหลือเทา · แท่ง = สัดส่วนใบสายของเส้นทางนั้น",
  "dashboard.note.fleetStatus": "ทุกสถานะต้องอยู่ในลิสต์ รวมถึง “ปลดระวาง” ไม่งั้นผลรวมไม่เท่ากับยอดรวม",
  "dashboard.note.worstFuel": "แดง = ต่ำกว่า 70% ของค่าสเปก · ที่เหลือเทา",
  "dashboard.note.expiring": "ใกล้หมดภายใน 30 วัน · เข้มเฉพาะที่เหลือน้อยกว่า 14 วัน",
  "dashboard.note.reminders": "ติดสีเฉพาะ “เกินกำหนด” — อีกสองแถวเป็นบริบท ไม่ใช่สัญญาณเตือน",

  /* ── ADR 0377 / 0378 — War Room executive tab (one block, keep together) ── */
  "warroom.exec.range": "ช่วง",
  "warroom.exec.preset.today": "วันนี้",
  "warroom.exec.preset.yesterday": "เมื่อวาน",
  "warroom.exec.preset.7d": "7 วัน",
  "warroom.exec.preset.30d": "30 วัน",
  "warroom.exec.preset.month": "เดือนนี้",
  "warroom.exec.custom": "กำหนดเอง",
  "warroom.exec.from": "ตั้งแต่",
  "warroom.exec.to": "ถึง",
  "warroom.exec.apply": "ใช้ช่วงนี้",
  "warroom.exec.err.too_long": "ยาวสุด {max} วัน — ยาวกว่านี้ดูที่แดชบอร์ด",
  "warroom.exec.err.order": "วันเริ่มต้องไม่หลังวันจบ",
  "warroom.exec.err.future": "เลือกวันในอนาคตไม่ได้",
  "warroom.exec.err.format": "วันที่ไม่ถูกต้อง",
  "warroom.exec.asOf": "ข้อมูล ณ {time}",
  "warroom.exec.refresh": "รีเฟรช",
  "warroom.exec.noJobs": "ไม่มีใบงานในช่วงนี้",
  "warroom.exec.progress.title": "งาน",
  "warroom.exec.progress.doneOf": "จาก {total} ใบ จบแล้ว",
  "warroom.exec.progress.why": "ใบงานในช่วงที่เลือก ไม่นับที่ยกเลิก ({c} ใบ)",
  "warroom.exec.seg.onTime": "ตรงเวลา",
  "warroom.exec.seg.late": "สาย",
  "warroom.exec.seg.used": "ได้งาน",
  "warroom.exec.seg.unused": "ไม่ได้งานเลย",
  "warroom.exec.ontime.title": "ตรงเวลา",
  "warroom.exec.ontime.cannot": "ยังวัดไม่ได้ — ยังไม่มีใบที่ออกวิ่ง",
  "warroom.exec.ontime.late": "สาย {n} ใบ",
  "warroom.exec.cmp.up": "▲ ดีขึ้น",
  "warroom.exec.cmp.down": "▼ แย่ลง",
  "warroom.exec.cmp.from": "จาก {p}% ใน {w}",
  "warroom.exec.cmp.same": "เท่ากับ {p}% ใน {w}",
  "warroom.exec.cmp.na": "เทียบไม่ได้ — {w} ไม่มีใบที่วัดได้",
  "warroom.exec.cmp.winSingle": "{n} วันก่อน",
  "warroom.exec.cmp.winRange": "{n} วันก่อนหน้า",
  "warroom.exec.vehicles.title": "รถที่ใช้",
  "warroom.exec.vehicles.of": "จาก {fleet} คัน",
  "warroom.exec.vehicles.pct": "ใช้ {pct}% ของรถทั้งหมด",
  "warroom.exec.contracts.title": "สัญญาที่ต้องดู",
  "warroom.exec.contracts.count": "{n} สัญญา",
  "warroom.exec.contracts.colName": "สัญญา",
  "warroom.exec.contracts.colBehind": "เลยเวลา ยังไม่จบ",
  "warroom.exec.contracts.none": "ไม่มีสัญญา",
  "warroom.exec.contracts.other": "อื่น ๆ",
  "warroom.exec.contracts.pick": "ดูทั้งหน้าเฉพาะสัญญานี้",
  "warroom.exec.contracts.more": "อีก {n} สัญญาที่ไม่เป็นไปตามแผน · ดูทั้งหมด ▾",
  "warroom.exec.contracts.fold": "ย่อเหลือ 5 อันดับแรก ▴",
  "warroom.exec.contracts.onPlan": "{n} สัญญาเป็นไปตามแผน",
  "warroom.exec.contracts.allOk": "ทุกสัญญาเป็นไปตามแผน ✓",
  "warroom.exec.must.title": "ต้องจัดการตอนนี้",
  "warroom.exec.must.sos": "SOS จากคนขับ",
  "warroom.exec.must.overdue_start": "ถึงเวลาออกแล้วยังไม่ออก",
  "warroom.exec.must.overdue_confirm": "คนขับยังไม่กดรับงาน",
  "warroom.exec.must.stale_from_yesterday": "ใบงานวันก่อนยังไม่ปิด",
  "warroom.exec.must.docs_expired_on_job": "มีงานวันนี้แต่เอกสารหมด",
  "warroom.exec.must.tomorrow_unready": "พรุ่งนี้ ยังไม่มีคนขับ/รถ",
  "warroom.exec.must.sosClear": "SOS ไม่มี · เรื่องอื่นปกติ",
  "warroom.exec.must.clear": "ไม่มีเรื่องต้องจัดการ ✓",
  "warroom.exec.must.notLive": "กล่องนี้เป็นสถานะ ณ ตอนนี้ — ช่วงที่เลือกไม่รวมวันนี้ ระบบไม่เก็บภาพย้อนหลัง",
  "warroom.exec.must.goToday": "เปลี่ยนเป็นวันนี้ →",
  "warroom.exec.see": "ดู →",
  "warroom.exec.trend.last7": "ตรงเวลา 7 วันย้อนหลัง",
  "warroom.exec.trend.daily": "ตรงเวลารายวัน",
  "warroom.exec.trend.noWork": "ไม่มีงาน",
  "warroom.exec.trend.tip": "{day} · คิดจาก {n} ใบ",
  "warroom.exec.contracts.colDoneLate": "จบช้า",
  "warroom.exec.ontime.lateSrc": "= จบช้า {d} · ยังไม่จบแต่เลยเวลา {o}",
  "warroom.exec.progress.whyStale": " · ในนั้นเป็นของวันก่อนที่ไม่มีใครปิด {s}",
  "warroom.exec.progress.whyOver": "เลยเวลาจบแล้ว {n} ใบ = ยังไม่เริ่ม {ns} · วิ่งอยู่ {r}",
  "warroom.exec.progress.overStale": "(ค้างจากวันก่อน {n})",
  "warroom.exec.progress.over": "ในนี้เลยเวลาจบแล้ว {n} ใบ",
  "warroom.exec.why.open": "กำลังดำเนินการ — ยังไม่จบ (ยังไม่เริ่ม หรือวิ่งอยู่)",
  "warroom.exec.why.done_late": "จบช้ากว่าแผน — จบแล้ว แต่เกินเวลานั้น",
  "warroom.exec.why.on_time": "จบตรงเวลา — จบไม่เกินเวลาคาดว่าจบ + เวลาผ่อนผัน",
  "warroom.exec.seg.open": "กำลังดำเนินการ",
  "warroom.exec.seg.done_late": "จบช้ากว่าแผน",
  "warroom.exec.seg.on_time": "จบตรงเวลา",
  "warroom.exec.why.onTime": "ตรงเวลา — จบไม่เกินเวลาคาดว่าจบ + เวลาผ่อนผัน",
  "warroom.exec.why.late": "สาย — จบเกินเวลานั้น",
  "warroom.exec.why.used": "ได้งาน — มีใบงานในช่วงนี้อย่างน้อย 1 ใบ",
  "warroom.exec.why.unused": "ไม่ได้งานเลยในช่วงนี้",
  "warroom.exec.ontime.whyN": "คิดจาก {n} ใบที่ออกวิ่งแล้ว (ใบที่ยังไม่ออกไม่นับ)",
  "warroom.exec.ontime.whyCmp": "เทียบกับ {w} ({dates})",
  "warroom.exec.ontime.whyAdmin": "{a} ใบถูกแอดมินปิดแทนคนขับ — เวลาจบของใบเหล่านั้นอาจไม่ตรง",
  "warroom.exec.vehicles.whyFleet": "จากรถที่ใช้งานได้ {fleet} คัน (ไม่นับที่ปลดระวาง)",
  "warroom.exec.contracts.whyWhich": "เฉพาะสัญญาที่มีใบเลยเวลาจบแล้วยังไม่จบ หรือจบช้า เรียงจากรวมสองอย่างมากไปน้อย",
  "warroom.exec.contracts.whyClick": "กดชื่อสัญญาเพื่อดูทั้งหน้าเฉพาะสัญญานั้น",
  "warroom.exec.trend.volume": "ใบงานรายวัน",
  "warroom.exec.trend.todayOut": "วันนี้ ออกแล้ว {n}",
  "warroom.exec.trend.todayRest": "ยังไม่ออก {n}",
  // ADR 0399 — per-org modules
  "admin.modules.tab": "โมดูล",
  "admin.modules.name.cargo": "ขนส่งสินค้า",
  "admin.modules.name.dashboard": "แดชบอร์ด",
  "admin.modules.desc.dashboard": "แท็บวิเคราะห์ของแดชบอร์ด (ทุกแท็บยกเว้น \"วันนี้\") · ปิดแล้วทุกคนในองค์กรเปิดแท็บเหล่านี้ไม่ได้ (รวม API) · แท็บ \"วันนี้\" เปิดให้ทุกองค์กรเสมอ · ค่าเริ่มต้นเปิด",
  "admin.modules.offChip": "ปิด{name}",
  "admin.modules.offChip.dashboard": "ปิดแท็บวิเคราะห์",
  "admin.modules.name.passengers": "ผู้โดยสาร",
  "admin.modules.desc.cargo": "รายการสินค้า · แท็บขนส่งสินค้าในใบงาน · สินค้าที่จุดจอด",
  "admin.modules.column": "โมดูล",
  "admin.modules.neverSwitched": "ยังไม่เคยเปิด",
  "admin.modules.neverSwitchedOn": "ยังไม่เคยปิด — เปิดอยู่ตามค่าเริ่มต้น", // ADR 0471 — a default-ON module
  "admin.modules.lastEnabled": "เปิดเมื่อ {at} โดย {by}",
  "admin.modules.lastDisabled": "ปิดเมื่อ {at} โดย {by}",
  "admin.modules.confirmEnableTitle": "เปิดโมดูล{name}?",
  "admin.modules.confirmDisableTitle": "ปิดโมดูล{name}?",
  "admin.modules.enableEffect": "เมนูและแท็บของโมดูลนี้จะโผล่ให้ทุกคนในองค์กร",
  "admin.modules.disableEffect": "เมนูและแท็บของโมดูลนี้จะหายจากทุกคนในองค์กร และเพิ่ม/แก้ไม่ได้",
  "admin.modules.noDataLoss": "ไม่มีข้อมูลถูกลบ — เปิดกลับได้เหมือนเดิม",
  "admin.modules.propagation": "อาจใช้เวลาไม่เกิน 1 นาทีจึงมีผลทุกเครื่อง",
  "admin.modules.reason": "เหตุผล (บันทึกใน audit)",
  "admin.modules.enable": "เปิดโมดูล",
  "admin.modules.disable": "ปิดโมดูล",
  // ADR 0482 — GPS (DTC) module card + connection section
  "admin.modules.name.dtc": "GPS (DTC)",
  "admin.modules.desc.dtc": "ดึงข้อมูลรถทุกคันจาก DTC ตามรอบ เก็บไว้ในระบบครบทุกช่อง · ยังไม่แสดงที่ไหน · แผนที่ยังใช้มือถือคนขับเหมือนเดิม",
  "admin.modules.enableEffect.dtc": "เริ่มดึงและเก็บข้อมูลจาก DTC ภายใน 1 นาที · ยังไม่มีเมนูหรือแท็บใหม่",
  "admin.modules.disableEffect.dtc": "หยุดดึงข้อมูลภายใน 1 นาที · ที่อยู่ DTC, token และรอบดึงยังเก็บไว้ เปิดกลับแล้วดึงต่อทันที",
  // ADR 0505 — the finance module card and its switch-off dialog
  "admin.modules.name.finance": "การเงิน",
  "admin.modules.desc.finance": "หน้าการเงินทั้งหมด (รายการเงิน บิล รอบจ่าย หนี้ คำร้อง รายงาน) · รายได้และคำร้องในแอปคนขับ · ปิดแล้วทุกคนในองค์กรเปิดไม่ได้ (รวม API) · เงินเดือนและบัญชีธนาคารในหน้าพนักงานยังแก้ได้",
  "admin.modules.enableEffect.finance": "เมนูการเงินจะโผล่ให้แอดมินในองค์กร และคนขับเห็น \"รายได้ของฉัน\" กับ \"คำร้อง\" ในแอป",
  "admin.modules.disableEffect.finance": "เมนูการเงินทั้งกลุ่ม เรทรายจังหวัด ตัวเลขบนเมนู กล่องเตือนมุมจอ ระฆังการเงิน การ์ดข้อมูลการเงินในหน้าพนักงาน การ์ดเงินของใบงาน และรายได้กับคำร้องในแอปคนขับจะหายไป · เงินเดือน บัญชีธนาคาร และสถานะพนักงานยังเห็น · ระบบยังบันทึกค่าเที่ยวจากใบงานต่อเบื้องหลัง",
  "admin.modules.financeImpact.title": "เงินที่ยังค้างในองค์กรนี้",
  "admin.modules.financeImpact.rows": "รายการเงินที่ยังไม่เข้าบิล",
  "admin.modules.financeImpact.bills_due": "บิลถึงกำหนดจ่าย (คน)",
  "admin.modules.financeImpact.rounds_open": "รอบจ่ายที่ยังไม่จ่าย",
  "admin.modules.financeImpact.requests_open": "คำร้องรออนุมัติ",
  "admin.modules.financeImpact.note": "ทั้งหมดนี้รออยู่ที่เดิม ไม่มีอะไรถูกยกเลิก เปิดกลับแล้วทำต่อได้ทันที",
  "admin.modules.financeImpact.failed": "โหลดยอดค้างไม่ได้ ยังปิดได้ แต่ควรเช็คหน้าการเงินก่อน",
  "admin.dtc.title": "การเชื่อมต่อ",
  "admin.dtc.status.notConnected": "ยังไม่เชื่อมต่อ",
  "admin.dtc.status.connected": "เชื่อมต่อแล้ว",
  "admin.dtc.status.errorSince": "error ตั้งแต่ {at}",
  "admin.dtc.status.paused": "หยุดดึง (โมดูลปิด)",
  "admin.dtc.address": "ที่อยู่ DTC",
  "admin.dtc.interval": "ดึงทุก",
  "admin.dtc.minutes": "{n} นาที",
  "admin.dtc.lastOk": "ดึงสำเร็จล่าสุด",
  "admin.dtc.lastOkValue": "{at} · {n} คัน",
  "admin.dtc.lastError": "error ล่าสุด",
  "admin.dtc.none": "ไม่มี",
  "admin.dtc.boxes": "กล่องใน DTC",
  "admin.dtc.boxesValue": "{total} · admin กรอกแล้ว {entered} · ยังไม่กรอก {left}",
  "admin.dtc.boxesUnknown": "ยังไม่รู้ — กดทดสอบการเชื่อมต่อ",
  "admin.dtc.showNotEntered": "ดู gps_id ที่ยังไม่กรอก {n} กล่อง",
  "admin.dtc.col.plate": "ทะเบียนใน DTC",
  "admin.dtc.col.lastSeen": "สัญญาณล่าสุด",
  "admin.dtc.downloadCsv": "ดาวน์โหลด CSV",
  "admin.dtc.test": "ทดสอบการเชื่อมต่อ",
  "admin.dtc.testOk": "ทดสอบผ่าน · DTC มี {n} กล่อง · {s} วินาที",
  "admin.dtc.pausedHint": "ค่าตั้งยังอยู่ครบ · ข้อมูลที่เก็บแล้วไม่ถูกลบ · เปิดโมดูลอีกครั้งแล้วดึงต่อรอบถัดไปทันที",
  "admin.dtc.keysMissing": "เซิร์ฟเวอร์นี้ยังไม่ได้ตั้งกุญแจ GPS_DTC_ENC_KEY / GPS_DTC_PII_HMAC_KEY — ระบบยังดึงข้อมูลไม่ได้",
  "admin.dtc.saved": "บันทึกแล้ว",
  "admin.dtc.setByOrg": "ที่อยู่และ API key ตั้งโดย org_admin ขององค์กรที่ ตั้งค่า › GPS (DTC) · super admin ดูและแก้ไม่ได้",
  "admin.dtc.setBy": "ตั้งโดย",
  "admin.dtc.saveInterval": "บันทึกรอบดึง",
  "admin.dtc.testSaved": "ทดสอบด้วย key ที่บันทึกไว้",
  "admin.dtc.errorHintSa": "ระบบยังลองดึงตามรอบ · org_admin ขององค์กรแก้ได้ที่ ตั้งค่า › GPS (DTC)",
  // ADR 0485 — settings › GPS (DTC), the org_admin sets the address + key
  "settings.dtc.nav": "GPS (DTC)",
  "settings.dtc.title": "GPS (DTC)",
  "settings.dtc.intro": "ใส่ที่อยู่และ API key ที่ได้จาก DTC แล้วกด \"ทดสอบการเชื่อมต่อ\" · บันทึกได้เมื่อทดสอบผ่านเท่านั้น",
  "settings.dtc.readOnly": "เฉพาะ org_admin ขององค์กรนี้แก้ได้ · คุณดูสถานะได้อย่างเดียว",
  "settings.dtc.keysMissing": "เซิร์ฟเวอร์ยังไม่พร้อมเชื่อมต่อ DTC · ติดต่อผู้ดูแลระบบ",
  "settings.dtc.key": "API key",
  "settings.dtc.keyPlaceholder": "วาง API key (api_token_key) ที่ได้จาก DTC",
  "settings.dtc.keyHint": "ช่องนี้ซ่อนตัวอักษร",
  "settings.dtc.savedBy": "บันทึก {at} โดย {by}",
  "settings.dtc.intervalBySa": "(ผู้ดูแลระบบเป็นผู้ตั้ง)",
  "settings.dtc.notEnteredHint": "กรอก gps_id เป็นเลขเครื่อง (S/N) ที่ ยานพาหนะ › GPS แล้วผูกกับรถ",
  "settings.dtc.errorHint": "ระบบยังลองดึงตามรอบ · ถ้า DTC เปลี่ยน key ให้กด \"เปลี่ยนการเชื่อมต่อ\"",
  "settings.dtc.change": "เปลี่ยนการเชื่อมต่อ",
  "settings.dtc.changing": "กำลังเปลี่ยนการเชื่อมต่อ — key เดิม (•••• {last4}) ยังใช้ดึงต่อจนกว่าจะบันทึกค่าใหม่สำเร็จ",
  "settings.dtc.retest": "ทดสอบอีกครั้ง",
  "settings.dtc.disconnect": "ยกเลิกการเชื่อมต่อ",
  "settings.dtc.disconnectTitle": "ยกเลิกการเชื่อมต่อ DTC?",
  "settings.dtc.disconnectBody": "ระบบจะลบ API key และหยุดดึงข้อมูล · ข้อมูลที่เก็บแล้วไม่ถูกลบ · เชื่อมต่อใหม่ได้ด้วยการใส่ที่อยู่และ key แล้วทดสอบอีกครั้ง",
  "settings.dtc.disconnected": "ยกเลิกการเชื่อมต่อแล้ว",
  "settings.dtc.testing": "กำลังทดสอบ…",
  "settings.dtc.needBoth": "กรอกให้ครบทั้งสองช่องก่อน แล้วกดทดสอบ · ปุ่มบันทึกจะกดได้เมื่อทดสอบผ่าน",
  "settings.dtc.testOk": "ทดสอบผ่าน · DTC มี {n} กล่อง · {s} วินาที — กดบันทึกได้ถึง {until}",
  "settings.dtc.testFail": "ทดสอบไม่ผ่าน · {code} · {message}",
  "settings.dtc.changed": "ค่าเปลี่ยนหลังทดสอบ — ต้องทดสอบใหม่ก่อนบันทึก",
  "settings.dtc.pending": "มีผลทดสอบผ่านเมื่อ {at} ที่ยังไม่ได้บันทึก (ใช้ได้ถึง {until}) · ใส่ค่าเดิมแล้วทดสอบอีกครั้งเพื่อบันทึก",
  "settings.dtc.err.DTC_TEST_REQUIRED": "ต้องทดสอบค่าชุดนี้ให้ผ่านก่อนบันทึก (ผลทดสอบหมดอายุหรือค่าไม่ตรง)",
  "settings.dtc.err.ORG_ADMIN_ONLY": "เฉพาะ org_admin ขององค์กรนี้แก้การเชื่อมต่อ DTC ได้",
  "settings.dtc.err.DTC_TEST_COOLDOWN": "กดทดสอบถี่เกินไป — รอสักครู่แล้วลองใหม่",
  "settings.dtc.err.MODULE_DISABLED": "โมดูล GPS (DTC) ขององค์กรนี้ปิดอยู่",
  "settings.dtc.err.unknown": "ทำรายการไม่สำเร็จ",
  "errors.moduleDisabled": "องค์กรนี้ยังไม่ได้เปิดใช้งานโมดูลนี้",
  // ADR 0400 — cargo products
  "nav.group.cargo": "ขนส่งสินค้า",
  "nav.cargoProducts": "สินค้า",
  "masterData.groupCargo": "ขนส่งสินค้า",
  "masterData.cat.cargo_unit": "หน่วยนับสินค้า",
  "masterData.cat.cargo_fail_reason": "เหตุผลส่งไม่สำเร็จ",
  "cargo.product.add": "เพิ่มสินค้า",
  "cargo.product.edit": "แก้ไขสินค้า",
  "cargo.product.code": "รหัสสินค้า",
  "cargo.product.name": "ชื่อสินค้า",
  "cargo.product.unit": "หน่วยนับ",
  "cargo.product.locationName": "สถานที่จัดเก็บ",
  "cargo.product.qty": "จำนวนคงคลัง",
  "cargo.product.pin": "ปักหมุด",
  "cargo.product.note": "หมายเหตุ",
  "cargo.product.search": "รหัส / ชื่อสินค้า",
  "cargo.product.allLocations": "สถานที่จัดเก็บ: ทั้งหมด",
  "cargo.product.outOfStock": "คงคลังหมด",
  "cargo.product.trash": "ถูกลบ",
  "cargo.product.updated": "แก้ล่าสุด",
  "cargo.product.deletedBy": "ลบโดย · เหตุผล",
  "cargo.product.empty": "ยังไม่มีสินค้า",
  "cargo.product.emptyTrash": "ไม่มีสินค้าที่ถูกลบ",
  "cargo.product.deleted": "ลบแล้ว",
  "cargo.product.restored": "กู้คืนแล้ว",
  "cargo.product.restore": "กู้คืน",
  "cargo.product.deleteTitle": "ลบ {code} · {name}?",
  "cargo.product.deleteHint": "ซ่อนจากรายการ · ผู้ดูแลองค์กรกู้คืนได้",
  "cargo.product.manageUnits": "จัดการหน่วยนับ",
  "cargo.product.fillRequired": "กรอกช่องที่มี * ให้ครบ",
  "cargo.product.errRequired": "จำเป็นต้องกรอก",
  "cargo.product.errCodeTooLong": "รหัสยาวเกิน 40 ตัว",
  "cargo.product.errPinRequired": "ปักหมุดที่เก็บ",
  "cargo.product.errQty": "ต้องเป็นตัวเลขตั้งแต่ 0 ขึ้นไป",
  // ADR 0473 — stock ledger (plan: 4-Archives/plan/tms/0473-cargo-stock-movements-from-jobs.md)
  "cargo.product.errQtyDecimals": "ทศนิยมได้ไม่เกิน 2 ตำแหน่ง",
  "cargo.product.errLocationNoPin": "ตำแหน่งนี้ยังไม่ปักหมุด — ปักหมุดก่อนจึงบันทึกได้",
  "cargo.product.pinLocation": "ปักหมุดในจัดการคลัง",
  "cargo.product.startQty": "ยอดเริ่มต้น",
  "cargo.location.noPin": "ยังไม่ปักหมุด",
  "cargo.stock.section": "คงคลัง",
  "cargo.stock.createHint": "บันทึกแล้วแก้ 2 ช่องนี้ไม่ได้ — ยอดใช้ \"ปรับยอด\" · หน่วยนับล็อก",
  "cargo.stock.adjust": "ปรับยอด",
  "cargo.stock.history": "ดูประวัติ",
  "cargo.stock.last": "ล่าสุด",
  "cargo.stock.none": "ยังไม่มีประวัติ",
  "cargo.stock.noPlace": "ยังไม่มีที่เก็บ — ใบงานยังไม่ตัด/เพิ่มยอดสินค้านี้",
  "cargo.stock.type.opening": "ยอดยกมา",
  "cargo.stock.type.receive": "รับเข้า",
  "cargo.stock.type.issue": "ตัดออก",
  "cargo.stock.type.count": "ปรับตามนับจริง",
  "cargo.stock.type.reversal": "ยกเลิกรายการ",
  "cargo.stock.type.job_pickup": "ขึ้นรถจากคลัง",
  "cargo.stock.type.job_deliver_in": "ส่งเข้าคลังจากใบงาน",
  "cargo.stock.type.job_putback_in": "วางคืนเข้าคลัง",
  "cargo.stock.kind.all": "ทั้งหมด",
  "cargo.stock.kind.job": "จากใบงาน",
  "cargo.stock.kind.adjust": "ปรับยอด",
  "cargo.stock.kind.fix": "ยกเลิก/แก้ผล",
  "cargo.stock.via.driver": "คนขับ",
  "cargo.stock.via.admin": "แอดมิน",
  "cargo.stock.via.system": "ระบบ",
  "cargo.stock.adjustTitle": "ปรับยอด · {code} {name}",
  "cargo.stock.qtyReceive": "จำนวนที่รับเข้า ({unit})",
  "cargo.stock.qtyIssue": "จำนวนที่ตัดออก ({unit})",
  "cargo.stock.qtyCount": "นับได้จริง ({unit})",
  "cargo.stock.reason": "เหตุผล",
  "cargo.stock.summary": "คงคลัง {from} → {to} {unit}",
  "cargo.stock.err.over": "ตัดได้ไม่เกิน {max} {unit}",
  "cargo.stock.err.zero": "ต้องมากกว่า 0",
  "cargo.stock.err.invalid": "ต้องเป็นตัวเลขตั้งแต่ 0 ขึ้นไป",
  "cargo.stock.err.noChange": "ยอดตรงกับที่ระบบมีแล้ว",
  "cargo.stock.err.issueBlocked": "คงคลังเป็น {qty} — ใช้รับเข้า หรือนับจริง",
  "cargo.stock.err.reasonRequired": "ต้องใส่เหตุผล",
  "cargo.stock.changedTitle": "ยอดเปลี่ยนระหว่างที่คุณกรอก",
  "cargo.stock.changedOpen": "ตอนเปิดกล่อง",
  "cargo.stock.changedNow": "ตอนนี้",
  "cargo.stock.changedWhy": "สาเหตุ",
  "cargo.stock.changedHint": "ตรวจตัวเลขอีกครั้ง แล้วกดบันทึก",
  "cargo.stock.saveFailed": "บันทึกไม่สำเร็จ ลองอีกครั้ง",
  "cargo.stock.saved": "ปรับยอดแล้ว · {from} → {to} {unit}",
  "cargo.stock.historyTitle": "ประวัติคงคลัง · {code} {name}",
  "cargo.stock.saveNeeds": "กรอกจำนวนและเหตุผลก่อนบันทึก",
  "cargo.stock.reasonNeeds": "ใส่เหตุผลก่อนยืนยัน",
  "cargo.stock.search": "ค้นหา เลขใบงาน · รหัสรายการ · เหตุผล · ชื่อคน",
  "cargo.stock.col.at": "เวลา",
  "cargo.stock.col.item": "รายการ",
  "cargo.stock.col.ref": "อ้างอิง",
  "cargo.stock.col.qty": "เปลี่ยน",
  "cargo.stock.col.balance": "คงเหลือ",
  "cargo.stock.col.who": "โดย · เหตุผล",
  "cargo.stock.reversed": "ยกเลิกแล้ว",
  "cargo.stock.correction": "แก้ผล",
  "cargo.stock.reverse": "ยกเลิกรายการ",
  "cargo.stock.reverseTitle": "ยกเลิกรายการ \"{type} {qty}\"?",
  "cargo.stock.reverseConfirm": "ยืนยันยกเลิก",
  "cargo.stock.err.reverseNegative": "ยกเลิกไม่ได้ — ยอดจะติดลบ ใช้นับจริงแทน",
  "cargo.stock.err.alreadyReversed": "มีคนยกเลิกรายการนี้ไปก่อนแล้ว",
  "cargo.stock.openingNote": "ยอดก่อนเริ่มเก็บประวัติ ไม่มีรายละเอียดย้อนหลัง",
  "cargo.stock.noMatch": "ไม่พบรายการที่ตรงกับคำค้นหรือตัวกรอง",
  "cargo.stock.useMovement": "ยอดคงคลังเปลี่ยนผ่าน \"ปรับยอด\" แล้ว — โหลดหน้าใหม่",
  "ops.cargo": "ขนส่งสินค้า",
  "cargo.action.label": "ประเภทการขนส่ง",
  "cargo.action.pickup": "รับขึ้น",
  "cargo.action.deliver": "ส่งของ",
  "cargo.action.return": "เก็บคืน",
  "cargo.status.pickup.pending": "รอรับ",
  "cargo.status.pickup.done": "รับขึ้นแล้ว",
  "cargo.status.pickup.partial": "รับได้บางส่วน",
  "cargo.status.pickup.failed": "รับไม่ได้",
  "cargo.status.pickup.cancelled": "ยกเลิก",
  "cargo.status.deliver.pending": "รอส่ง",
  "cargo.status.deliver.done": "ส่งแล้ว",
  "cargo.status.deliver.partial": "ส่งบางส่วน",
  "cargo.status.deliver.failed": "ส่งไม่สำเร็จ",
  "cargo.status.deliver.cancelled": "ยกเลิก",
  "cargo.status.return.pending": "รอเก็บคืน",
  "cargo.status.return.done": "เก็บคืนแล้ว",
  "cargo.status.return.partial": "คืนบางส่วน",
  "cargo.status.return.failed": "เก็บคืนไม่ได้",
  "cargo.status.return.cancelled": "ยกเลิก",
  "cargo.status.generic.pending": "รอดำเนินการ",
  "cargo.item.title": "รายการสินค้า",
  "cargo.item.add": "เพิ่มสินค้า",
  "cargo.item.empty": "ยังไม่มีสินค้าในใบงานนี้",
  "cargo.item.emptyTrash": "ไม่มีรายการที่ถูกลบ",
  "cargo.item.id": "รหัสรายการ",
  "cargo.item.product": "สินค้า",
  "cargo.item.stop": "จุดจอด",
  "cargo.item.qty": "จำนวน",
  "cargo.item.updated": "อัปเดตล่าสุด",
  "cargo.item.search": "รหัสรายการ / สินค้า",
  "cargo.item.allStops": "จุดจอด: ทั้งหมด",
  "cargo.item.allActions": "ประเภท: ทั้งหมด",
  "cargo.item.allStatuses": "สถานะ: ทั้งหมด",
  "cargo.item.trash": "ถูกลบ",
  "cargo.item.setStatus": "ตั้งสถานะ…",
  "cargo.item.edit": "แก้ไข…",
  "cargo.item.editTitle": "แก้ไข {id}",
  "cargo.item.editPendingOnly": "แก้ได้เฉพาะรายการที่ยังรอดำเนินการ",
  "cargo.item.delete": "ลบ…",
  "cargo.item.deleteTitle": "ลบ {id} · {name}?",
  "cargo.item.deleteSelected": "ลบ {n} รายการ?",
  "cargo.item.deleteHint": "ซ่อนจากรายการ · ผู้ดูแลองค์กรกู้คืนได้",
  // ADR 0459 D2/D4 — remove a photo (reason required · soft delete)
  "cargo.photo.remove": "ลบรูป",
  "cargo.photo.removeTitle": "ลบรูปนี้ของ {id}?",
  "cargo.photo.removeHint": "รูปจะถูกซ่อนจากทุกหน้าจอ การลบพร้อมเหตุผลยังอยู่ในไทม์ไลน์ของงาน",
  "cargo.photo.removed": "ลบรูปแล้ว",
  "cargo.item.reason": "เหตุผล",
  "cargo.item.qtyDone": "จำนวนที่ทำได้",
  "cargo.item.failReason": "เหตุผลที่ไม่สำเร็จ",
  "cargo.item.cancelReason": "เหตุผลที่ยกเลิก",
  "cargo.item.otherReason": "ระบุสาเหตุ",
  "cargo.item.progress": "เสร็จ {done}/{total}",
  "cargo.item.problems": "มีปัญหา {n}",
  "cargo.item.selected": "เลือก {n} รายการ",
  "cargo.item.ticketCancelled": "ใบงานถูกยกเลิก มีสินค้ารอดำเนินการ {n} รายการ",
  "cargo.item.cancelAll": "ยกเลิกทั้งหมด",
  "cargo.item.ticketClosed": "ใบงานปิดแล้ว แก้ไขรายการสินค้าไม่ได้",
  "cargo.item.dupTitle": "ซ้ำกับอีกแถว (สินค้า ประเภท จุดเดียวกัน)",
  "cargo.item.revConflict": "มีคนแก้รายการนี้ก่อนคุณ — โหลดข้อมูลใหม่แล้ว",
  "cargo.item.stopGone": "จุดจอดเดิมถูกลบแล้ว กู้คืนไม่ได้",
  "cargo.item.saved": "บันทึกแล้ว",
  "cargo.item.added": "เพิ่ม {n} รายการแล้ว",
  "cargo.item.deleted": "ลบแล้ว",
  "cargo.item.restored": "กู้คืนแล้ว",
  "cargo.item.bulkSkipped": "ข้าม {n} รายการที่เปลี่ยนไม่ได้",
  "cargo.item.deletedBy": "ลบโดย · เหตุผล",
  "cargo.item.selectAll": "เลือกทั้งหมด",
  "cargo.action.returnDeliver": "ส่งคืน",
  "cargo.item.pair": "อีกปลาย",
  "cargo.item.editPairHint": "จำนวนและหมายเหตุ เปลี่ยนทั้งขารับและขาส่ง",
  "cargo.item.deletePairHint": "รายการที่เป็นคู่ จะถูกลบทั้งขารับและขาส่ง",
  "cargo.dialog.title": "เพิ่มสินค้าในใบงาน",
  "cargo.dialog.stopsCount": "{n} จุด",
  "cargo.dialog.catalog": "เลือกสินค้า",
  "cargo.dialog.addedN": "เพิ่มแล้ว {n}",
  // ADR 0433 — add-cargo dialog: catalog columns · stock switch · states S3–S7
  "cargo.dialog.colProduct": "สินค้า",
  "cargo.dialog.colStock": "คงคลัง",
  "cargo.stockFilter.label": "คงคลัง",
  "cargo.stockFilter.all": "ทั้งหมด",
  "cargo.stockFilter.in": "มีคงคลัง",
  "cargo.stockFilter.out": "หมด",
  "cargo.dialog.catalogEmpty": "ยังไม่มีสินค้าในระบบ",
  "cargo.dialog.catalogEmptyHint": "สร้างสินค้าตัวแรกเพื่อใส่ในใบงาน",
  "cargo.dialog.filterEmpty.in": "ไม่มีสินค้าที่มีคงคลัง",
  "cargo.dialog.filterEmpty.out": "ไม่มีสินค้าที่หมด",
  "cargo.dialog.filterEmpty.inAt": "ไม่มีสินค้าที่มีคงคลังใน {warehouse}",
  "cargo.dialog.filterEmpty.outAt": "ไม่มีสินค้าที่หมดใน {warehouse}",
  "cargo.dialog.filterEmpty.at": "ไม่มีสินค้าใน {warehouse}",
  "cargo.dialog.showAll": "ดูทั้งหมด",
  "cargo.dialog.noMatchQuery": "ไม่พบสินค้า \"{q}\"",
  "cargo.dialog.createNamed": "+ สร้างสินค้า \"{q}\"",
  "cargo.dialog.firstN": "แสดง {n} รายการแรก — พิมพ์ชื่อหรือรหัสเพื่อค้นต่อ",
  "cargo.dialog.addProduct": "เพิ่ม {name}",
  "cargo.dialog.newProduct": "+ สินค้าใหม่",
  "cargo.dialog.newProductTitle": "สินค้าใหม่ — เข้ารายการสินค้าด้วย",
  "cargo.dialog.createAndPick": "สร้างและเลือก",
  "cargo.dialog.backToCatalog": "กลับไปรายการสินค้า",
  "cargo.dialog.useExisting": "ใช้ตัวที่มีอยู่",
  "cargo.dialog.codeTaken": "รหัสนี้มีในรายการสินค้าแล้ว",
  "cargo.dialog.rowsTitle": "รายการที่จะเพิ่ม",
  "cargo.dialog.sortBy": "เรียงตาม",
  "cargo.dialog.sortStop": "ลำดับจุดจอด",
  "cargo.dialog.noRows": "กด \"+ เพิ่ม\" ที่สินค้าทางซ้าย",
  "cargo.dialog.chooseStop": "เลือกจุด",
  "cargo.dialog.duplicate": "ทำซ้ำ",
  "cargo.dialog.remove": "เอาออก",
  "cargo.dialog.merge": "รวมจำนวน",
  "cargo.dialog.addNote": "ใส่หมายเหตุ",
  "cargo.dialog.dupSummary": "{n} รายการซ้ำ (สินค้า จุดรับ จุดส่งเดียวกัน) — บันทึกได้ ถ้าตั้งใจแยก (คนละล็อต/PO) ใส่หมายเหตุ",
  "cargo.dialog.stockShort": "{n} รายการรับเกินคงคลัง: {list}", // ADR 0477 D6 (แทน cargo.dialog.stock0 — ใบงานตัดยอดตั้งแต่ ADR 0473)
  "cargo.dialog.stockShortRow": "{code} รับ {wanted} · คงคลัง {onHand}",
  "cargo.stockNote.otherStop": "รับที่จุดนี้ไม่ตัดคงคลัง — สินค้านี้เก็บที่ {storage}", // ADR 0477 D4
  "cargo.stockNote.useStop": "รับที่ {n}. {name}",
  "cargo.stockNote.notOnRoute": "รับที่จุดนี้ไม่ตัดคงคลัง — สินค้านี้เก็บที่ {storage} ซึ่งยังไม่อยู่ในเส้นทาง",
  "cargo.stockNote.addStopHint": "เพิ่มหมุดโดยเลือก \"คลังสินค้า\" ในฟอร์มเพิ่มหมุด",
  "cargo.dialog.submit": "เพิ่ม {n} รายการ",
  "cargo.dialog.finishNewFirst": "ทำสินค้าใหม่ให้เสร็จก่อน",
  "cargo.dialog.fixRows": "กรอกให้ครบทุกแถว (จุดจอด · จำนวน · สถานะ)",
  "cargo.dialog.setAll": "ตั้งสถานะทั้งหมด",
  "cargo.dialog.summary": "{n} รายการ",
  "cargo.dialog.lockedStop": "เพิ่มที่จุด {name}",
  "timeline.created": "สร้างใบงาน",
  "timeline.released": "ปล่อยคนขับ — อนุมัติลาแล้ว",
  "timeline.updated": "แก้ไขใบงาน",
  "timeline.stopsUpdated": "แก้ไขจุดจอด",
  "timeline.chargeAdded": "เพิ่มค่าใช้จ่าย",
  "timeline.chargeRemoved": "ลบค่าใช้จ่าย",
  "timeline.chargeUpdated": "แก้ไขค่าใช้จ่าย",
  "timeline.arrived": "ถึง {place}",
  "timeline.departed": "ออกจาก {place}",
  "timeline.appOpened": "คนขับเปิดแอป",
  "timeline.appResumed": "คนขับกลับมาที่แอป",
  "timeline.changed": "เปลี่ยน {fields}",
  "timeline.boarded": "ขึ้นรถ {n} คน",
  "timeline.loading": "กำลังโหลดไทม์ไลน์…",
  "ops.checkedInOnPage": "ขึ้นรถแล้ว {n} คน (หน้านี้)",
  "timeline.licenseExp": "หมดอายุ",
  "timeline.burst": "ติดกัน {n} ครั้ง",
  "timeline.earlierApp": "+ เหตุการณ์แอปก่อนหน้านี้อีก {n} รายการ",
  "timeline.resumeCount": "กลับมาที่แอป {n} ครั้ง",
  "timeline.openCount": "เปิดแอปใหม่ {n} ครั้ง",
  "timeline.field.label": "ชื่อใบงาน",
  "timeline.field.startTime": "เวลาเริ่ม",
  "timeline.field.expectedEnd": "เวลาสิ้นสุดที่คาด",
  "timeline.field.acceptDeadline": "เวลายืนยัน",
  "timeline.field.revenue": "รายได้",
  "timeline.cargo.item.created": "เพิ่มสินค้า",
  "timeline.cargo.item.updated": "แก้ไขสินค้า",
  "timeline.cargo.item.status": "เปลี่ยนสถานะสินค้า",
  // ADR 0459 — a photo removed by hand (the office · the driver ✕)
  "timeline.cargo.item.photo_removed": "ลบรูปสินค้า",
  "timeline.cargo.item.deleted": "ลบสินค้า",
  "timeline.cargo.item.restored": "กู้คืนสินค้า",
  "ops.stopEdit.addStop": "เพิ่มจุดจอด",
  "ops.stopEdit.stay": "อยู่ต่อ",
  "ops.stopEdit.fitAll": "ดูทั้งเส้นทาง",
  "ops.stopEdit.fullscreen": "เต็มจอ",
  "ops.stopEdit.exitFullscreen": "ออกจากเต็มจอ",
  // ADR 0406 — stops editor: rows open in place · one save · ask before leaving unsaved edits · "ที่จุดนี้" column
  "ops.stopEdit.close": "ปิด",
  "ops.stopEdit.revert": "คืนค่าจุดนี้",
  "ops.stopEdit.edited": "แก้แล้ว",
  "ops.stopEdit.cancelAll": "ยกเลิกทั้งหมด",
  "ops.stopEdit.pendingCount": "แก้ไข {n} รายการ ยังไม่บันทึก",
  "ops.stopEdit.switchTitleStop": "{name} ยังไม่บันทึก",
  "ops.stopEdit.switchAskTarget": "บันทึกก่อนไปที่ {target} ไหม",
  "ops.stopEdit.switchAskAdd": "บันทึกก่อนเพิ่มจุดใหม่ไหม",
  "ops.stopEdit.leaveBody": "แก้ไข {n} รายการในเส้นทางนี้ยังไม่ได้บันทึก",
  "ops.stopEdit.stayHere": "อยู่หน้านี้ต่อ",
  "ops.stopEdit.dontSave": "ไม่บันทึก",
  "ops.stopEdit.coordsPending": "พิกัดของ {name} ยังไม่ครบ",
  "ops.stopEdit.stopN": "จุดที่ {n}",
  "ops.stopEdit.viaX": "จุดผ่าน {letter}",
  "ops.stopEdit.item.edited": "{name} — {fields}",
  "ops.stopEdit.item.moved": "ย้ายลำดับ {name}",
  "ops.stopEdit.item.added": "เพิ่ม {name}",
  "ops.stopEdit.item.addedVia": "เพิ่มจุดผ่าน",
  "ops.stopEdit.item.removed": "ลบ {name}",
  "ops.stopEdit.field.name": "ชื่อ",
  "ops.stopEdit.field.coords": "พิกัด",
  "ops.stopEdit.field.other": "ค่าอื่น",
  "ops.stopSide.title": "ที่จุดนี้",
  "ops.stopSide.via": "จุดผ่านใช้กำหนดเส้นทางเท่านั้น ไม่มีสินค้าหรือผู้โดยสาร",
  "ops.stopSide.unsaved": "บันทึกเส้นทางก่อน จึงเพิ่มสินค้าหรือผู้โดยสารที่จุดนี้ได้",
  "ops.stopSide.up": "ขึ้นที่จุดนี้",
  "ops.stopSide.down": "ลงที่จุดนี้",
  "ops.stopSide.addPax": "เพิ่มผู้โดยสาร",
  "ops.stopSide.truncated": "แสดง {n} คนแรก — ดูทั้งหมดที่แท็บผู้โดยสาร",
  "ops.stopSide.paxSavesNow": "ผู้โดยสารบันทึกทันที",
  "ops.stopSide.removeHasPax": "จุดนี้มีผู้โดยสาร {n} คน — ยกเลิกการจองก่อน",
  "ops.stopEdit.more": "เพิ่มเติม",
  "ops.stopEdit.moveUp": "ย้ายขึ้น",
  "ops.stopEdit.moveDown": "ย้ายลง",
  "ops.stopEdit.remove": "ลบจุดนี้",
  "ops.storage.label": "คลังสินค้า", // ADR 0477 D2
  "ops.storage.none": "ไม่ใช่คลังสินค้า",
  "ops.storage.badge": "คลังสินค้า",
  "ops.storage.locked": "เปลี่ยนไม่ได้ — มีสินค้าบันทึกผลที่หมุดนี้แล้ว",
  "ops.stopSide.addCargo": "เพิ่มสินค้า",
  "ops.stopSide.tabCargo": "สินค้า",
  "ops.stopSide.tabPax": "ผู้โดยสาร",
  "ops.stopPending.new": "ใหม่",
  "ops.stopPending.remove": "เอาออก",
  "ops.stopPending.undo": "คืน",
  "ops.stopPending.willCancel": "จะยกเลิก",
  "ops.stopPending.addToList": "เพิ่มลงรายการ",
  "ops.stopPending.cargoAdd": "เพิ่ม {name} ×{qty} ({from} → {to})",
  "ops.stopPending.cargoStatus": "{name} → {status}",
  "ops.stopPending.paxAdd": "เพิ่ม {name} ที่ {stop}",
  "ops.stopPending.paxNoShow": "{name} → ไม่มา",
  "ops.stopPending.paxCancel": "ยกเลิก {name}",
  "ops.stopPending.groupCargoLabel": "สินค้า",
  "ops.stopPending.groupPaxLabel": "ผู้โดยสาร",
  "ops.stopPending.groupStops": "จุดจอด {n}",
  "ops.stopPending.groupCargo": "สินค้า {n}",
  "ops.stopPending.groupPax": "ผู้โดยสาร {n}",
  "cargo.status.returnDeliver.pending": "รอส่งคืน",
  "cargo.status.returnDeliver.done": "ส่งคืนแล้ว",
  "cargo.status.returnDeliver.partial": "ส่งคืนบางส่วน",
  "cargo.status.returnDeliver.failed": "ส่งคืนไม่สำเร็จ",
  "cargo.status.returnDeliver.cancelled": "ยกเลิก",
  "cargo.dialog.startBar": "สินค้าที่กดเพิ่มจากฝั่งซ้าย จะ",
  "cargo.dialog.sortPickup": "ลำดับจุดรับ",
  "cargo.dialog.summaryReturns": "เก็บคืน {n}",
  "cargo.dialog.incomplete": "ยังเลือกไม่ครบ {n} รายการ — กดเพิ่มแล้วจะพาไปที่ช่องที่ขาด",
  "cargo.dialog.emptyTitle": "ยังไม่มีรายการ",
  "cargo.dialog.emptyBody": "เลือกสินค้าจากรายการด้านซ้าย แล้วกด \"+ เพิ่ม\" · ถ้ายังไม่มีสินค้าในระบบ กด \"+ สินค้าใหม่\"",
  "cargo.pair.qty": "จำนวน",
  "cargo.pair.pickupAt": "รับที่",
  "cargo.pair.deliverAt": "ส่งที่",
  "cargo.pair.sidePickup": "จุดรับ",
  "cargo.pair.sideDeliver": "จุดส่ง",
  "cargo.pair.kind": "ประเภท",
  "cargo.pair.kindNormal": "ส่งของ",
  "cargo.pair.kindReturn": "เก็บคืน",
  "cargo.pair.chooseStop": "เลือกจุด",
  "cargo.pair.choosePickup": "เลือกจุดรับ",
  "cargo.pair.choosePickupReturn": "เลือกจุดที่ไปเก็บ",
  "cargo.pair.chooseDeliver": "เลือกจุดส่ง",
  "cargo.pair.missingPickup": "เลือกจุดรับขึ้น",
  "cargo.pair.missingPickupReturn": "เลือกจุดที่ต้องไปเก็บของคืน",
  "cargo.pair.missingDeliver": "เลือกจุดส่ง — จุดส่งต้องอยู่หลังจุดรับ",
  "cargo.pair.badQty": "ใส่จำนวนมากกว่า 0",
  "cargo.pair.why.before_pickup": "อยู่ก่อนจุดรับ",
  "cargo.pair.why.is_pickup": "เป็นจุดรับแล้ว",
  "cargo.pair.why.after_deliver": "อยู่หลังจุดส่ง",
  "cargo.pair.why.is_deliver": "เป็นจุดส่งแล้ว",
  "cargo.pair.thisStop": "จุดนี้",
  "cargo.pair.lockBar": "{name} เป็น",
  "cargo.pair.lockBarTail": "ของสินค้าที่เพิ่ม",
  "cargo.pair.less": "ลดจำนวน",
  "cargo.pair.more": "เพิ่มจำนวน",
  "cargo.pair.note": "หมายเหตุ",
  "cargo.pair.addNote": "เพิ่มหมายเหตุ",
  "cargo.pair.notePlaceholder": "เช่น เลข PO · ล็อต · ชื่อผู้รับ",
  "cargo.pair.removeNote": "เอาหมายเหตุออก",
  "cargo.pair.remove": "เอาออก",
  "cargo.pair.dupTitle": "สินค้านี้มีอยู่แล้ว",
  "cargo.pair.dupBody": "รับที่ {from} ส่งที่ {to} เหมือนรายการอื่นในนี้",
  "cargo.pair.dupSavedBody": "รับที่ {from} ส่งที่ {to} มีอยู่ในใบงานแล้ว",
  "cargo.pair.dupHint": "ถ้าเป็นของคนละใบสั่งซื้อหรือคนละล็อต ให้ใส่หมายเหตุแยกไว้",
  "cargo.pair.dupMerge": "รวมเป็นรายการเดียว…",
  "cargo.pair.dupKeep": "แยกไว้ (ใส่หมายเหตุ)",
  "cargo.pair.mergeTitle": "รวมเป็นรายการเดียว?",
  "cargo.pair.mergeProduct": "สินค้า",
  "cargo.pair.mergeRoute": "เส้นทาง",
  "cargo.pair.mergeQty": "จำนวน",
  "cargo.pair.mergeNote": "หมายเหตุ",
  "cargo.pair.mergeWarn": "ถ้าแยกไว้เพราะคนละ PO / ล็อต / ผู้รับ ไม่ควรรวม — ใส่หมายเหตุแต่ละรายการแทน",
  "cargo.pair.mergeConfirm": "รวมเป็น {n} {unit}",
  "cargo.pair.removedToast": "เอา {name} ({qty} {unit}) ออกแล้ว",
  "cargo.pair.undo": "เรียกคืน",
  "ops.stopEdit.legendNew": "ใหม่",
  "ops.stopEdit.legendEdited": "แก้ไข",
  "ops.stopEdit.staleTitle": "มีคนบันทึกใบงานนี้ระหว่างที่คุณแก้ —",
  "ops.stopEdit.staleOverwrite": "โหลดของล่าสุดแล้วใส่ร่างทับ",
  "ops.stopEdit.staleDiscard": "ทิ้งร่าง",
  "ops.stopEdit.goTo": "ไปที่ {name}",
  "ops.stopEdit.saveFailedNothing": "บันทึกไม่สำเร็จ — ยังไม่มีอะไรถูกบันทึก",
  "ops.stopEdit.staleBody": "ร่างของคุณยังอยู่ครบ",
  "ops.stopEdit.newStop": "จุดใหม่ (ยังไม่บันทึก)",
  "ops.stopCargo.search": "ค้นหารหัสหรือชื่อสินค้า",
  "ops.stopCargo.clearSearch": "ล้างคำค้น",
  "ops.stopCargo.pickupHere": "รับขึ้นที่จุดนี้",
  "ops.stopCargo.deliverHere": "ส่งที่จุดนี้",
  "ops.stopCargo.nOfM": "{n} จาก {m}",
  "ops.stopCargo.noPickup": "ไม่มีของรับขึ้นที่จุดนี้",
  "ops.stopCargo.noDeliver": "ไม่มีของส่งที่จุดนี้",
  "ops.stopCargo.noMatch": "ไม่พบสินค้าที่ค้นหา",
  "ops.stopCargo.deliverAt": "ส่งที่",
  "ops.stopCargo.pickupFrom": "รับจาก",
  "ops.stopCargo.deliverBeforeHere": "อยู่ก่อนจุดนี้",
  "ops.stopCargo.pickupAfterHere": "อยู่หลังจุดนี้",
  "ops.stopCargo.noDeliverPair": "ไม่มีจุดส่งคู่ (รายการเก่า)",
  "ops.stopCargo.noPickupPair": "ไม่มีจุดรับคู่ (รายการเก่า)",
  "ops.stopCargo.note": "หมายเหตุ",
  "ops.stopCargo.setStatusInCargo": "ตั้งสถานะในแท็บสินค้า", // ADR 0475 D1
  "ops.stopCargo.notePlaceholder": "เช่น เลข PO · ล็อต · ชื่อผู้รับ",
  "ops.stopCargo.remove": "เอาออก",
  "ops.stopCargo.removeBoth": "เอาออกจากทั้ง {here} และ {other}?",
  "ops.stopCargo.removeOne": "เอาออกจากใบงาน?",
  "ops.stopCargo.reason": "เหตุผล",
  "ops.stopCargo.reasonPlaceholder": "เช่น ลูกค้ายกเลิก · สั่งซ้ำ · ของหมด",
  "ops.stopCargo.nItems": "{n} รายการ",
  "ops.stopCargo.sumPickup": "รวมขึ้น",
  "ops.stopCargo.sumDeliver": "รวมส่ง",
  "ops.stopCargo.orderWarning": "ส่งก่อนรับ {n} รายการ — ลำดับจุดเปลี่ยนไป บันทึกได้ แต่ควรแก้ลำดับหรือแก้จุดของสินค้า",
  "ops.stopCargo.pendingHere": "มีสินค้ารอบันทึกที่จุดนี้ {n} รายการ — เอาออกก่อน",
  "ops.stopPending.cargoNote": "{name} → หมายเหตุใหม่",
  "ops.stopPending.cargoRemove": "เอา {name} ออก ({reason})",
  "cargo.dialog.stage": "เพิ่ม {n} รายการลงรายการ",
  "ops.stopSide.cancelAsk": "ยกเลิกการจอง {id} · {name} ไหม",
  "cargo.stop.empty": "ยังไม่มีสินค้าที่จุดนี้",
  "cargo.stop.savesNow": "สินค้าบันทึกทันที · ตำแหน่งจุดบันทึกเมื่อกด บันทึกการแก้ไข",
  "cargo.stop.removeHasCargo": "จุดนี้มีสินค้า {n} รายการ — ย้ายหรือลบสินค้าก่อน",
  "cargo.product.noMatch": "ไม่พบสินค้าตามตัวกรอง",
  "cargo.product.statusActive": "ใช้งาน",
  "nav.cargoWarehouses": "รายการคลัง",
  "cargo.warehouse.manage": "จัดการคลัง",
  "cargo.warehouse.warehouse": "คลัง",
  "cargo.warehouse.location": "ตำแหน่ง",
  "cargo.warehouse.noneHint": "ยังไม่มีคลัง — ไปที่",
  "cargo.warehouse.all": "คลัง: ทั้งหมด",
  "cargo.warehouse.mainPin": "หมุดของตำแหน่งส่วนกลาง",
  "cargo.warehouse.name": "ชื่อคลัง",
  "cargo.warehouse.locations": "ตำแหน่งจัดเก็บ",
  "cargo.warehouse.add": "เพิ่มคลัง",
  "cargo.warehouse.edit": "แก้ไขคลัง",
  "cargo.warehouse.delete": "ลบคลัง",
  "cargo.warehouse.deleted": "ลบคลังแล้ว",
  "cargo.warehouse.inUse": "ย้ายสินค้าออกจากคลังนี้ก่อน",
  "cargo.warehouse.taken": "มีคลังชื่อนี้แล้ว",
  "cargo.warehouse.mainHint": "ระบบจะสร้างตำแหน่ง \"ส่วนกลาง\" ให้ · เพิ่มตำแหน่งอื่นได้ภายหลัง",
  "cargo.warehouse.search": "คลัง / ตำแหน่ง",
  "cargo.warehouse.noMatch": "ไม่พบคลังตามตัวกรอง",
  "cargo.warehouse.empty": "ยังไม่มีคลัง",
  "cargo.warehouse.emptyTrash": "ไม่มีคลังที่ถูกลบ",
  "cargo.warehouse.deleteTitle": "ลบคลัง \"{name}\"?",
  "cargo.warehouse.deleteHint": "ซ่อนจากรายการและตัวเลือก พร้อมตำแหน่งทั้งหมดของคลัง · ผู้ดูแลองค์กรกู้คืนได้",
  "cargo.location.default": "ค่าเริ่มต้น",
  "cargo.location.all": "ตำแหน่ง: ทั้งหมด",
  "cargo.location.inUse": "ยังมีสินค้าอยู่ในตำแหน่งนี้ — ย้ายออกก่อน",
  "cargo.location.taken": "มีตำแหน่งชื่อนี้แล้วในคลังนี้",
  "cargo.location.remove": "เอาตำแหน่งออก",
  "cargo.location.add": "เพิ่มตำแหน่ง",
  "cargo.location.main": "ส่วนกลาง",
  "cargo.location.reserved": "\"Main\" / \"ส่วนกลาง\" สงวนไว้ให้ตำแหน่งค่าเริ่มต้น",
  // ADR 0408 — warehouse detail page
  "cargo.warehouse.summary": "{n} ตำแหน่ง · อัปเดตล่าสุด {date}",
  "cargo.warehouse.locationCount": "จำนวนตำแหน่ง",
  "cargo.warehouse.notFound": "ไม่พบคลัง",
  "cargo.warehouse.deleteZoneNote": "ตำแหน่งทั้งหมดถูกลบไปด้วย · ผู้ดูแลองค์กรกู้คืนได้จากหน้ารายการคลัง",
  "cargo.location.name": "ชื่อตำแหน่ง",
  "cargo.location.edit": "แก้ตำแหน่งจัดเก็บ",
  "cargo.location.pin": "ที่ตั้ง",
  "cargo.location.pinField": "ที่ตั้งของตำแหน่งนี้ (จุดรับสินค้า)",
  "cargo.location.mainPinLocked": "ตำแหน่งจัดเก็บต้องมีหมุด — ย้ายได้ แต่ล้างไม่ได้",
  "cargo.location.noPinYet": "ยังไม่มีหมุด",
  "cargo.location.setPinFirst": "ตำแหน่งนี้ยังไม่มีหมุด — ตั้งหมุดก่อนจึงจะบันทึกได้",
  "cargo.location.removeConfirm": "ลบตำแหน่ง \"{name}\" ออกจากคลังนี้?",
  "cargo.location.removed": "ลบตำแหน่งแล้ว",
  "cargo.location.limit": "คลังหนึ่งมีได้ไม่เกิน {max} ตำแหน่ง",
  "cargo.location.search": "ค้นหาตำแหน่ง",
  "cargo.location.noMatch": "ไม่พบตำแหน่งที่ตรงกับคำค้น",
  "cargo.location.addedAt": "เพิ่มเมื่อ",
  "cargo.evidence.photos": "รูปหลักฐาน",
  "cargo.evidence.byDriver": "คนขับส่งมา",
  "cargo.evidence.noLocation": "ไม่มีพิกัด",
  "cargo.evidence.at": "ถ่ายเมื่อ",
  // ADR 0415 — ของที่ส่งไม่สำเร็จจบได้สองแบบ: คืนที่หมุดในเส้นทาง หรือของหาย
  "cargo.disposition.label": "ของอยู่ที่",
  "cargo.disposition.return_to_stop": "คืนที่หมุด",
  "cargo.disposition.lost": "ของหาย",
  "cargo.disposition.question": "เอาของคืนไปไว้ที่ไหน",
  "cargo.disposition.pickStop": "เลือกหมุด",
  "cargo.disposition.lostHint": "หาย / ถูกขโมย — ไม่มีของให้วางคืน",
  "cargo.evidence.picked": "รับมา {picked} จาก {qty}",
  "cargo.evidence.returnLost": "คืน {back} · หาย {lost}",
  "cargo.disposition.tagHere": "จุดนี้",
  "cargo.disposition.tagOrigin": "จุดรับเดิม",
  "cargo.disposition.bulkHint": "ทุกแถวขาส่งจะใช้ที่วางคืนเดียวกัน",
  "cargo.evidence.failPhotos": "รูปตอนส่ง",
  "cargo.evidence.returnPhotos": "รูปวางคืน",
  "cargo.evidence.noReturnPhoto": "ยังไม่มีรูปวางคืน",
  "settings.customerNoPrefix.title": "เลขที่ลูกค้า",
  "settings.customerNoPrefix.help": "ตัวอักษรนำหน้าเดียวทั้งองค์กร · ระบบรันเลขให้ลูกค้าใหม่ทุกราย · เปลี่ยนแล้วลูกค้าใหม่เริ่ม 0001 ส่วนลูกค้าเก่าคงเลขเดิม",
  "settings.customerNoPrefix.label": "ตัวอักษรนำหน้า",
  "settings.customerNoPrefix.sample": "ตัวอย่าง",
  "settings.customerNoPrefix.invalid": "ตัวอักษร A–Z หรือตัวเลข ขึ้นต้นด้วยตัวอักษร ไม่เกิน 10 ตัว ห้ามมีขีด และห้ามเป็น JOB หรือ CTR",
  "settings.dnPrefix.title": "เลขใบส่งสินค้า",
  "settings.dnPrefix.help": "prefix เดียวทั้งองค์กร · เลขใบส่งคู่กับเลขใบงานเสมอ · ใบที่ออกไปแล้วยังใช้ prefix เดิม",
  "settings.dnPrefix.label": "Prefix ใบส่งสินค้า",
  "settings.dnPrefix.sample": "ตัวอย่าง",
  "settings.dnPrefix.invalid": "ตัวอักษร A–Z หรือตัวเลข ขึ้นต้นด้วยตัวอักษร ไม่เกิน 10 ตัว ห้ามมีขีด และห้ามเป็น JOB",
  "settings.shopPrefix.title": "Prefix รหัสร้านค้า",
  "settings.shopPrefix.help": "เลือกจาก dropdown ตอนสร้างร้านค้า · แต่ละ prefix นับเลขของตัวเอง (BKK-0001, NTB-0001) · บันทึกทันทีที่กด",
  "settings.shopPrefix.labelPh": "เช่น กรุงเทพ",
  "settings.shopPrefix.invalid": "ตัวอักษร A–Z หรือตัวเลข ขึ้นต้นด้วยตัวอักษร ไม่เกิน 10 ตัว ห้ามมีขีด",
  "settings.shopPrefix.empty": "ยังไม่มี prefix — สร้างร้านค้าไม่ได้จนกว่าจะเพิ่มอย่างน้อยหนึ่งตัว",
  "settings.shopPrefix.firstCode": "รหัสแรก",
  "settings.shopPrefix.setDefault": "ตั้งเป็นค่าเริ่มต้น",
  "settings.shopPrefix.deactivate": "ปิดใช้",
  "settings.shopPrefix.activate": "เปิดใช้",
  "settings.shopPrefix.inactive": "ปิดใช้อยู่",
  "settings.shopPrefix.inUse": "มีร้านค้าใช้แล้ว — ปิดใช้ได้ แต่ลบไม่ได้",
  "settings.shopPrefix.readOnly": "เฉพาะผู้ดูแลองค์กรที่แก้ prefix ได้",
  "settings.shopPrefix.showInactive": "ที่ปิดใช้งาน",
  "settings.shopPrefix.labelField": "ป้ายกำกับ",
  "cargo.party.open": "ส่งให้ใคร · เลขอ้างอิงลูกค้า",
  "cargo.party.search": "ค้นร้านค้าหรือคนรับ — รหัสร้านค้า ชื่อร้านค้า ชื่อคน หรือเบอร์",
  "cargo.party.none": "ไม่พบ — กด + ร้านค้าใหม่ หรือพิมพ์คนรับด้านล่าง",
  "cargo.party.pickShop": "ค้นร้านค้าจากช่องด้านบน",
  "cargo.party.newShop": "+ ร้านค้าใหม่",
  "cargo.party.noPeopleYet": "ยังไม่มีคนรับ",
  "cargo.party.shop": "ร้านค้า",
  "cargo.party.clearShop": "เอาร้านค้าออก",
  "cargo.party.newShopPh": "ชื่อร้านค้าใหม่ (ไม่บังคับ)",
  "cargo.party.prefixTitle": "Prefix รหัสร้านค้า",
  "cargo.party.noPrefix": "ยังไม่มี prefix รหัสร้านค้า — เพิ่มที่ Settings › ID Patterns",
  "cargo.party.receiver": "คนรับ",
  "cargo.party.namePh": "ชื่อ",
  "cargo.party.phonePh": "เบอร์โทร",
  "cargo.party.clear": "ล้าง",
  "cargo.party.phoneIs": "เบอร์นี้คือ {name}",
  "cargo.party.phoneIsTail": "บันทึกแล้วจะใช้คนนี้ (ไม่เปลี่ยนชื่อในทะเบียน)",
  "cargo.party.needBoth": "คนรับต้องมีทั้งชื่อและเบอร์",
  "cargo.party.refs": "เลขลูกค้า",
  "cargo.party.refPh": "เช่น 4500123",
  "cargo.party.removeRef": "เอาเลขอ้างอิงนี้ออก",
  "cargo.party.refDup": "เลขซ้ำในรายการนี้",
  "cargo.party.noRefTypes": "ยังไม่มีชนิดเลขอ้างอิงใน Master Data",
  "cargo.party.addRef": "เลขอ้างอิงลูกค้า",
  "cargo.party.pinOff": "หมุดประจำของร้านค้า \"{name}\" ยังไม่อยู่ในเส้นทาง — เพิ่มหมุดนี้ที่ \"เส้นทางและจุดจอด\" ก่อน",
  "masterData.cat.cargo_ref_type": "ชนิดเลขอ้างอิงลูกค้า",
  "masterData.cat.shop_contact_type": "ช่องทางติดต่อร้าน", // ADR 0455 D2
  "cargo.dn.title": "ใบส่งสินค้า",
  "cargo.dn.items": "{n} รายการ",
  "nav.cargoShops": "ร้านค้า",
  "cargo.shop.add": "เพิ่มร้านค้า",
  "cargo.shop.code": "รหัสร้านค้า",
  "cargo.shop.name": "ชื่อร้านค้า",
  "cargo.shop.pin": "ตำแหน่งสาขา", // ADR 0450 D11 — was "หมุดประจำ" (one pin per shop, before branches)
  "cargo.shop.pinPick": "เลือกบนแผนที่",
  "cargo.shop.pinChange": "เปลี่ยนบนแผนที่",
  "cargo.shop.note": "หมายเหตุการรับของ",
  "cargo.shop.notePh": "เช่น ส่งประตูหลัง ก่อน 10 โมง",
  "cargo.shop.codeHint": "ระบบออกรหัสตอนบันทึก และรหัสไม่เปลี่ยนอีก",
  "cargo.shop.people": "คนรับ ({n})",
  "cargo.shop.peopleCol": "คนรับ",
  "cargo.shop.noPeople": "ยังไม่มีคนรับ — จะเพิ่มเองเมื่อบันทึกใบงานที่ส่งให้ร้านค้านี้",
  "cargo.shop.removePerson": "เอา {name} ออก",
  "cargo.shop.revConflict": "มีคนแก้ร้านค้านี้ก่อน — ปิดแล้วเปิดใหม่",
  "cargo.shop.search": "รหัสร้านค้า / ชื่อร้านค้า",
  "cargo.shop.lastUsed": "ใช้ล่าสุด",
  // ADR 0447 D2 · D3 — shops under customers, branches
  "cargo.shop.customer": "ลูกค้า",
  "cargo.shop.pickCustomerShort": "เลือกลูกค้า",
  "cargo.shop.pickCustomer": "เลือกลูกค้าทางซ้ายเพื่อดูร้านค้าของลูกค้ารายนั้น",
  "cargo.shop.noCustomer": "ยังไม่ระบุลูกค้า",
  "cargo.shop.noCustomerHint": "ร้านค้าเดิมก่อนมีการผูกกับลูกค้า",
  "cargo.shop.noCustomerMatch": "ไม่พบลูกค้าที่ค้นหา",
  "cargo.shop.customerSearch": "ค้นหาลูกค้า",
  "cargo.shop.customerSummary": "{shops} ร้านค้า · {branches} สาขา",
  "cargo.shop.emptyCustomer": "ลูกค้ารายนี้ยังไม่มีร้านค้า",
  "cargo.shop.backToCustomers": "ลูกค้าทั้งหมด",
  "cargo.shop.branchCount": "{n} สาขา",
  "cargo.shop.autoCreated": "สร้างพร้อมลูกค้า",
  "cargo.shop.noPinYet": "ยังไม่มีหมุด",
  "cargo.shop.locations": "สาขา ({n})",
  "cargo.shop.locationAdd": "เพิ่มสาขา",
  "cargo.shop.locationDefault": "ค่าเริ่มต้น",
  "cargo.shop.locationMakeDefault": "ตั้งเป็นค่าเริ่มต้น",
  "cargo.shop.locationEdit": "แก้",
  "cargo.shop.locationClose": "ปิด",
  "cargo.shop.locationRemove": "เอาสาขาออก",
  "cargo.shop.locationLabel": "ชื่อสาขา",
  "cargo.shop.locationUnnamed": "(ยังไม่มีชื่อ)",
  "cargo.shop.locationsHint": "สาขาที่เป็นค่าเริ่มต้นคือสาขาที่ระบบเลือกให้ก่อนตอนเพิ่มสินค้า",
  "cargo.shop.noMatch": "ไม่พบร้านค้าที่ตรงกับตัวกรอง",
  "cargo.shop.empty": "ยังไม่มีร้านค้า",
  "cargo.shop.emptyTrash": "ไม่มีร้านค้าที่ลบ",
  // ADR 0449 — the redesigned shops page + the shop dialog with its map
  "cargo.shop.addNeedsCustomer": "เลือกลูกค้าทางซ้ายก่อน",
  "cargo.shop.listTitle": "ร้านค้าของ {name}",
  "cargo.shop.customerMore": "แสดง {n} จาก {total} · เลื่อนเพื่อโหลดต่อ",
  "cargo.shop.customerAll": "แสดงครบ {total} ราย",
  "cargo.shop.deletedLink": "ร้านค้าที่ลบแล้ว ({n})",
  "cargo.shop.deletedTitle": "{name} · ร้านค้าที่ลบแล้ว",
  "cargo.shop.backToActive": "กลับไปร้านค้าที่ใช้งาน",
  "cargo.shop.emptyCustomerNamed": "{name} ยังไม่มีร้านค้า",
  "cargo.shop.addFirst": "เพิ่มร้านค้าแรกของลูกค้านี้",
  "cargo.shop.customerNotFound": "ไม่พบลูกค้านี้",
  "cargo.shop.noCustomers": "ยังไม่มีลูกค้า — เพิ่มที่หน้าลูกค้า",
  "cargo.shop.neverUsed": "ยังไม่เคยใช้",
  "cargo.shop.more": "เพิ่มเติม",
  "cargo.shop.deleteShop": "ลบร้านค้า",
  "cargo.shop.deleteConfirmTitle": "ลบร้านค้า {code}?",
  "cargo.shop.deleteConfirmBody": "รหัส {code} จะไม่ถูกนำกลับมาใช้อีก · กู้คืนได้จากร้านค้าที่ลบแล้ว",
  "cargo.shop.colPlace": "ที่ตั้ง",
  "cargo.shop.colBranch": "สาขา",
  "cargo.shop.recipients": "คนรับของ ({n})",
  "cargo.shop.noPeopleDetail": "ยังไม่มีคนรับของ — จะเพิ่มเองเมื่อใส่ชื่อคนรับตอนเพิ่มสินค้าในใบงาน",
  "cargo.shop.recentJobs": "ใช้กับงานล่าสุด",
  "cargo.shop.noRecentJobs": "ยังไม่เคยใช้กับใบงาน",
  "cargo.shop.mapTitle": "แผนที่สาขา",
  "cargo.shop.mapNoPins": "ยังไม่มีสาขาที่ปักหมุด",
  "cargo.shop.noCustomerBanner": "ร้านค้านี้ยังไม่ผูกกับลูกค้า — ใส่ลงสินค้าในใบงานไม่ได้",
  "cargo.shop.attachCustomer": "ผูกกับลูกค้า…",
  "cargo.shop.deletedAt": "ลบเมื่อ {date}",
  "cargo.shop.deletedNotice": "ร้านค้านี้ถูกลบแล้ว",
  "cargo.shop.pinsProgress": "ทุกสาขาต้องมีชื่อและหมุดก่อนบันทึก ({ready} จาก {total})",
  "cargo.shop.pinsReady": "ทุกสาขามีชื่อและหมุดแล้ว",
  "cargo.shop.pinsShort": "ครบ {ready} จาก {total} สาขา",
  "cargo.shop.pickRowHint": "เลือกแถวสาขาแล้วปักหมุดบนแผนที่ทางขวา · สาขาที่เลือกอยู่ = หมุดสีฟ้าที่ลากได้",
  "cargo.shop.mapTip": "คลิกบนแผนที่เพื่อปักหมุดของ {branch} · ลากหมุดเพื่อเลื่อน",
  "cargo.shop.pinFor": "หมุดของสาขา {n} {label}",
  "cargo.shop.pinnedAt": "ปักแล้ว · {coords}",
  "cargo.shop.noPinLong": "ยังไม่มีหมุด — คลิกบนแผนที่ หรือค้นหาด้านบน",
  "cargo.shop.samePlace": "สาขา {rows} ปักที่จุดเดียวกัน — ตรวจอีกครั้งว่าตั้งใจ",
  "cargo.shop.pasteHint": "วางคู่พิกัด \"13.41, 101.03\" ในช่องใดก็ได้",
  // ADR 0450 — shop details table · split dialogs (info · one branch · two-step new shop)
  "cargo.shop.editInfo": "แก้ไขข้อมูลร้านค้า",
  "cargo.shop.shopChip": "ร้านค้า",
  "cargo.shop.branchAddTitle": "เพิ่มสาขา",
  "cargo.shop.branchEditTitle": "แก้ไขสาขา {n} · {label}",
  "cargo.shop.branchRemove": "ลบสาขานี้",
  "cargo.shop.branchRemoveTitle": "ลบสาขา {label} ของ {code}?",
  "cargo.shop.branchRemoveBody": "สินค้าที่อยู่ในใบงานแล้วยังเก็บชื่อสาขานี้ไว้เหมือนเดิม",
  "cargo.shop.branchMakeDefault": "ใช้เป็นสาขาค่าเริ่มต้น",
  "cargo.shop.branchIsDefaultHint": "ระบบเลือกสาขานี้ให้ก่อนตอนเพิ่มสินค้า · จะเปลี่ยน ให้ไปตั้งที่สาขาอื่น",
  "cargo.shop.branchDefaultNowShort": "ตอนนี้ค่าเริ่มต้นคือ \"{label}\"",
  "cargo.shop.branchOthersShort": "สาขาอื่นของร้านค้านี้เป็นหมุดเลข",
  "cargo.shop.branchMapTip": "คลิกบนแผนที่เพื่อปักหมุด · ลากหมุดเพื่อเลื่อน",
  "cargo.shop.branchPinAction": "ปักหมุด",
  "cargo.shop.newShopOneBranch": "ร้านค้าใหม่เริ่มด้วยสาขาเดียว · เพิ่มสาขาอื่นได้ในรายละเอียดร้านค้าหลังบันทึก",
  "cargo.shop.mainBranchHeading": "สาขาหลัก",
  // ADR 0455 — shop branch contact rows (plan 4-Archives/plan/tms/0455-shop-contact-channels.md)
  "cargo.contact.title": "ช่องทางติดต่อ",
  "cargo.contact.colChannel": "ช่องทาง",
  "cargo.contact.colValue": "เบอร์ / ไอดี",
  "cargo.contact.colLabel": "ผู้ติดต่อ",
  "cargo.contact.ph.label": "เช่น ฝ่ายรับของ",
  "cargo.contact.add": "เพิ่มช่องทาง",
  "cargo.contact.full": "ครบ {n} ช่องทางแล้ว",
  "cargo.contact.noTypes": "ยังไม่มีช่องทางให้เลือก — เพิ่มที่ Master Data",
  "cargo.contact.noTypesLink": "ไปที่ Master Data",
  "cargo.contact.inactive": "(ปิดใช้แล้ว)",
  "cargo.contact.none": "ยังไม่มีช่องทางติดต่อ",
  "cargo.contact.addLink": "เพิ่มช่องทางติดต่อ",
  "cargo.contact.main": "หลัก",
  "cargo.contact.open.phone": "โทร",
  "cargo.contact.open.email": "ส่งอีเมล",
  "cargo.contact.open.link": "เปิดลิงก์",
  "cargo.contact.copy": "คัดลอก",
  "cargo.contact.copied": "คัดลอกแล้ว",
  "cargo.contact.moveUp": "เลื่อนขึ้น",
  "cargo.contact.moveDown": "เลื่อนลง",
  "cargo.contact.remove": "ลบช่องทางนี้",
  "cargo.contact.ph.phone": "เช่น 081-234-5678",
  "cargo.contact.ph.email": "name@shop.com",
  "cargo.contact.ph.url": "เช่น facebook.com/ชื่อร้านค้า",
  "cargo.contact.ph.line": "@ไอดี หรือลิงก์ line.me",
  "cargo.contact.ph.none": "ไอดีหรือเบอร์",
  "cargo.contact.err.typeUnknown": "เลือกช่องทาง",
  "cargo.contact.err.valueRequired": "ใส่เบอร์ ไอดี หรือลิงก์",
  "cargo.contact.err.valueTooLong": "ยาวเกิน 120 ตัวอักษร",
  "cargo.contact.err.phoneInvalid": "เบอร์โทรต้องมีตัวเลข 9–15 หลัก",
  "cargo.contact.err.emailInvalid": "อีเมลไม่ครบ — ต้องเป็นแบบ name@shop.com",
  "cargo.contact.err.labelTooLong": "ผู้ติดต่อยาวเกิน 60 ตัวอักษร",
  "cargo.contact.err.duplicate": "ซ้ำกับแถวก่อนหน้า",
  "cargo.contact.err.tooMany": "สาขาหนึ่งมีได้ไม่เกิน 5 ช่องทาง",
  "cargo.shop.branchRemoveContacts": "ช่องทางติดต่อ {n} รายการของสาขานี้จะถูกลบด้วย",
  "cargo.shop.colAddress": "ที่อยู่",
  "cargo.shop.colCoords": "พิกัด",
  "cargo.shop.colJob": "เลขงาน",
  "cargo.shop.colStatus": "สถานะ",
  "cargo.shop.colDate": "วันที่",
  "cargo.shop.colName": "ชื่อ",
  "cargo.shop.colPhone": "เบอร์",
  "cargo.shop.colNote": "หมายเหตุ",
  "cargo.shop.colLastUsed": "ใช้ล่าสุด",
  "cargo.shop.recentJobsCount": "{n} งานล่าสุด",
  "cargo.shop.personRemoveTitle": "ลบ {name} ออกจากคนรับของ?",
  "cargo.shop.personRemoveBody": "ใบงานที่บันทึกไปแล้วยังเก็บชื่อและเบอร์เดิมไว้",
  "cargo.shop.personRemove": "ลบ",
  "cargo.tabAdd.rows": "รายการที่จะเพิ่ม ({n})",
  "cargo.tabAdd.chooseShopFirst": "เลือกร้านค้าก่อน",
  "cargo.tabAdd.noShopN": "ยังไม่ได้เลือกร้านค้า {n} รายการ",
  "cargo.tabAdd.shopPin": "หมุดของร้านค้า",
  "cargo.tabAdd.shopNoPin": "ร้านค้านี้ยังไม่มีหมุด — เพิ่มหมุดที่หน้าร้านค้าก่อน",
  "cargo.jobMap.title": "แผนที่ใบงาน",
  "cargo.jobMap.open": "แผนที่ใบงาน",
  "cargo.jobMap.all": "ดูทุกหมุดของใบงานบนแผนที่",
  "cargo.jobMap.google": "เปิดใน Google Maps",
  "cargo.jobMap.jobStops": "จุดในใบงาน · ตามลำดับเส้นทาง",
  "cargo.jobMap.newPins": "หมุดใหม่ในรายการนี้ · ลำดับจัดตอนบันทึก",
  "cargo.jobMap.readOnly": "ดูอย่างเดียว — เลือก/ร่วมหมุดทำในรายการ",
  "cargo.tile.sharedWith": "ใช้ร่วมกับรายการ {n}",
  "cargo.tile.jobStop": "จุดที่ {n} ในใบงาน",
  "cargo.tile.existing": "มีของเดิม: {list}",
  "cargo.tile.change": "เปลี่ยน",
  "cargo.tile.onMap": "ดูบนแผนที่",
  "cargo.tile.shopDefault": "ค่าเริ่ม: ตำแหน่งสาขา", // ADR 0451 D2
  "cargo.tile.sendTo": "ส่งให้",
  "cargo.tile.route": "เส้นทาง",
  "cargo.tile.legPickup": "รับ",
  "cargo.tile.legDeliver": "ส่ง",
  "cargo.pair.collectAt": "เก็บที่",
  "cargo.pair.returnTo": "ส่งคืนที่",
  "cargo.shopPick.placeholder": "เลือกร้านค้า — รหัส · ชื่อร้านค้า · ชื่อคน · เบอร์",
  "cargo.shopPick.inJob": "ในใบงานนี้",
  "cargo.party.branch": "สาขา",
  "cargo.party.branchOnly": "{label} (สาขาเดียว)", // ADR 0451 D6
  "cargo.shopPick.noCustomer": "ใบงานนี้ยังไม่มีลูกค้า — เพิ่มสัญญาที่มีลูกค้าในแท็บตั้งค่างานก่อน",
  "cargo.shopPick.ofCustomer": "ร้านค้าของ {name} ({contract})",
  "cargo.shopPick.recent": "ใช้ล่าสุด",
  "cargo.shopPick.never": "ยังไม่เคยใช้",
  "cargo.shopPick.neverShort": "ยังไม่เคยใช้",
  "cargo.shopPick.today": "วันนี้",
  "cargo.shopPick.daysAgo": "{n} วันก่อน",
  "cargo.shopPick.noPin": "ยังไม่มีหมุด",
  "cargo.shopPick.noPinWhy": "ตั้งหมุดของร้านค้าก่อน แล้วจึงเลือกได้",
  "cargo.shopPick.none": "ไม่พบร้านค้า — เพิ่มด้วย + ร้านค้าใหม่",
  "cargo.shopPick.footMore": "โหลดแล้ว {n} • เลื่อนเพื่อดูเพิ่ม • พิมพ์เพื่อกรอง",
  "cargo.shopPick.footAll": "โหลดแล้ว {n} · หมดรายการ",
  "cargo.party.receiverName": "ชื่อคนรับ",
  "cargo.party.phoneLabel": "เบอร์โทร",
  "cargo.close.title": "ยังไม่ได้เพิ่มสินค้า",
  "cargo.close.body": "ปิดแล้ว {n} รายการที่ใส่ไว้จะหาย",
  "cargo.close.line": "รายการ {n} {name} ×{qty}",
  "cargo.close.stay": "อยู่ต่อ",
  "cargo.close.discard": "ทิ้งแล้วปิด",
  "cargo.tabAdd.newPin": "ใหม่",
  "cargo.tabAdd.shared": "ใช้ร่วม {n} รายการ",
  "cargo.tabAdd.groupDraft": "หมุดใหม่ในรายการนี้",
  "cargo.tabAdd.groupJob": "หมุดในใบงาน",
  "cargo.tabAdd.addPin": "+ หมุดใหม่…",
  "cargo.tabAdd.routeLocked": "ใบงานนี้แก้เส้นทางไม่ได้แล้ว",
  "cargo.tabAdd.order": "จุดรับต้องมาก่อนจุดส่ง",
  "cargo.tabAdd.same": "จุดรับกับจุดส่งเป็นที่เดียวกัน",
  "cargo.tabAdd.newPinName": "หมุดใหม่",
  "stopOrder.openRoute": "จัดลำดับทั้งเส้นทาง",
  "stopOrder.saveFirst": "บันทึกการแก้ไขก่อน แล้วค่อยจัดลำดับ",
  "stopOrder.useOrder": "ใช้ลำดับนี้",
  "stopOrder.err.pairOrder": "จัดลำดับไม่ได้ — จุดรับสินค้าใหม่ต้องไปหลังจุดส่งที่คนขับผ่านไปแล้ว ย้อนกลับไปเปลี่ยนจุดรับ/ส่งของรายการนั้น",
  "stopOrder.err.stale": "ใบงานนี้ถูกแก้จากที่อื่นระหว่างนี้ — ย้อนกลับแล้วเปิดหน้าต่างใหม่เพื่อดูของล่าสุด",
  "stopOrder.err.editWindow": "ใบงานนี้แก้เส้นทางไม่ได้แล้ว",
  "stopOrder.err.tooMany": "ใบงานมีจุดเกินกว่าที่คำนวณเส้นถนนได้ในครั้งเดียว",
  "stopOrder.err.noRoad": "Google หาเส้นทางถนนไม่ได้ — หมุดบางจุดอาจอยู่ในทะเลหรือไม่มีถนนเข้าถึง",
  "stopOrder.err.network": "เชื่อมต่อไม่ได้ — ตรวจอินเทอร์เน็ตแล้วลองใหม่",
  "stopOrder.err.unknown": "เกิดข้อผิดพลาด — ลองใหม่อีกครั้ง",
  "stopOrder.saveFailed": "บันทึกไม่สำเร็จ — ยังไม่มีอะไรถูกบันทึก",
  "stopOrder.saving": "กำลังบันทึก…",
  "stopOrder.retrySave": "ลองบันทึกอีกครั้ง",
  "stopOrder.manualTitle": "ระบบจัดลำดับอัตโนมัติไม่ได้ — ช่วยจัดลำดับด้วยมือ",
  "stopOrder.manualGoogle": "Google ไม่ตอบ",
  "stopOrder.manualUnreachable": "เชื่อมต่อระบบจัดลำดับไม่ได้",
  "stopOrder.manualStart": "เรียงเริ่มต้นให้แล้ว: จุดเดิมตามลำดับ แล้วแทรกหมุดใหม่ที่ใกล้สุด (รับก่อนส่ง)",
  "stopOrder.retryAuto": "ลองจัดอัตโนมัติอีกครั้ง",
  "stopOrder.retryAutoReplace": "แทนลำดับที่จัดไว้?",
  "stopOrder.yes": "ใช่ แทนเลย",
  "stopOrder.no": "ไม่",
  "stopOrder.recalc": "คำนวณเส้นทางใหม่",
  "stopOrder.recalcBusy": "กำลังคำนวณ…",
  "stopOrder.recalcFailed": "คำนวณเส้นทางไม่ได้",
  "stopOrder.recalcFailedTail": "ลำดับที่จัดไว้ยังอยู่ · บันทึกได้ตามปกติ",
  "stopOrder.retry": "ลองใหม่",
  "stopOrder.handOrder": "จัดลำดับเอง",
  "stopOrder.legPickup": "รับ",
  "stopOrder.legDeliver": "ส่ง",
  "stopOrder.existingTag": "ของเดิม",
  "stopOrder.map": "แผนที่ลำดับหมุด",
  "stopOrder.start.label": "จุดจอด",
  "stopOrder.start.none": "ไม่ตั้ง — ระบบเลือกจุดแรกให้เอง",
  "stopOrder.start.cleared": "ไม่ตั้ง — จุดจอดที่ใบงานบันทึกไว้จะถูกลบเมื่อกดบันทึก",
  "stopOrder.start.arrived": "จัดต่อจากจุดที่คนขับถึงล่าสุด — ตั้งไม่ได้",
  "stopOrder.start.set": "ตั้งบนแผนที่",
  "stopOrder.start.change": "เปลี่ยน",
  "stopOrder.start.clear": "ไม่ตั้ง",
  "stopOrder.start.restore": "ใช้ค่าเดิม",
  "stopOrder.start.new": "ใหม่",
  "stopOrder.start.saved": "บันทึกไว้ในใบงาน",
  "stopOrder.start.pickTitle": "ตั้งจุดจอด",
  "stopOrder.start.noAddress": "ตำแหน่งที่ปักบนแผนที่",
  "stopOrder.start.closeItem": "จุดจอดที่ตั้งไว้: {address}",
  // ADR 0451 D5 — the job driver's current depot is the default start
  "stopOrder.start.driverOf": "จุดจอดของ {name} ({code}) · ค่าเริ่ม",
  "stopOrder.start.driverOfNoCode": "จุดจอดของ {name} · ค่าเริ่ม",
  "stopOrder.start.useDriver": "ใช้จุดจอดของคนขับ",
  "stopOrder.near.mergeLead": "รวมเข้ากับจุดที่ {n} · ",
  "stopOrder.near.sub": "ห่างกัน {m} ม.",
  "stopOrder.near.passed": "คนขับผ่านจุดนี้ไปแล้ว — รวมไม่ได้",
  "stopOrder.near.mergedN": "รวม {n} หมุด",
  "stopOrder.near.newAway": "ใหม่ · ห่าง {m} ม.",
  "stopOrder.near.existing": "หมุดเดิม",
  "stopOrder.near.blocked": "ยังบันทึกไม่ได้ — จัดลำดับใหม่ เลื่อนเอง หรือกดแยกออก",
  "stopOrder.near.split": "แยกออก",
  "stopOrder.near.problemOrder": "ต้องจัดลำดับใหม่ — {deliver} อยู่ก่อน {pickup} ที่รับของ",
  "stopOrder.near.problemSame": "รวมไม่ได้ — จุดรับและจุดส่งของรายการเดียวกันจะกลายเป็นจุดเดียวกัน",
  "stopOrder.reorder": "จัดลำดับใหม่",
  "stopOrder.reorderBusy": "กำลังจัดลำดับ…",
  "stopOrder.reorderAskTitle": "จัดลำดับใหม่?",
  "stopOrder.reorderAskBody": "ระบบจะจัดลำดับตามระยะทางถนนจริง ลำดับในรายการจะเปลี่ยน",
  "stopOrder.reorderAskMoved": "ลำดับที่คุณเลื่อนเองจะถูกแทน",
  "stopOrder.pending.merge": "รวม/แยกหมุด",
  "stopOrder.pending.order": "ลำดับเปลี่ยน",
  "stopOrder.pending.noRoad": "ยังไม่มีเส้นทาง",
  "stopOrder.pending.start": "จุดจอดเปลี่ยน",
  "cargo.near.hint": "อยู่ใกล้หมุด {name} {m} ม. — เลือกรวมหมุดได้ตอนยืนยันลำดับ",
  "cargo.near.shared": "รวมกับหมุด {name} แล้ว (เลือกตอนยืนยันลำดับ)",
  "stopOrder.title": "ยืนยันลำดับหมุด",
  "stopOrder.loading": "กำลังจัดลำดับ…",
  "stopOrder.google": "จัดลำดับด้วยระยะทางถนนจริง",
  "stopOrder.fallback": "คำนวณอย่างง่าย — Google ไม่ตอบ",
  // ADR 0452 D3 — why suggest-order fell back (`lib/stops/fallback-reason.ts`)
  "stopOrder.fallbackWhy": "คำนวณอย่างง่าย — {why}",
  "stopOrder.reason.timeout": "Google ตอบช้าเกิน 15 วินาที",
  "stopOrder.reason.google_error": "Google ตอบกลับเป็นข้อผิดพลาด",
  "stopOrder.reason.network": "ติดต่อ Google ไม่ได้",
  "stopOrder.reason.not_configured": "ยังไม่ได้ตั้งค่าการเชื่อม Google",
  "stopOrder.reason.skipped": "Google ไม่ได้ใส่บางหมุดในเส้นทาง",
  "stopOrder.reason.cycle": "มีสินค้าที่รับ–ส่งไปกลับระหว่างหมุดเดียวกัน จัดหมุดละแถวไม่ได้",
  "stopOrder.reason.order": "ลำดับที่ได้ทำให้ส่งของก่อนรับ",
  "stopOrder.reason.unknown": "ใช้ลำดับจาก Google ไม่ได้",
  "stopOrder.total": "{km} กม. · {min} นาที",
  "stopOrder.approx": "ประมาณ {km} กม. (เส้นตรง)",
  "stopOrder.new": "ใหม่",
  "stopOrder.passed": "ผ่านแล้ว",
  "stopOrder.whyPair": "ต้องรับก่อนส่ง ({name})",
  "stopOrder.whyArrived": "ผ่านแล้ว",
  "stopOrder.up": "เลื่อนขึ้น",
  "stopOrder.down": "เลื่อนลง",
  "stopOrder.reset": "กลับไปใช้ลำดับที่ระบบเสนอ",
  "stopOrder.back": "ย้อนกลับ",
  "stopOrder.confirm": "ยืนยันและบันทึก",
  "stopOrder.viaCleared": "จุดผ่าน {n} ช่วงจะถูกล้าง เพราะลำดับเปลี่ยน",
  "stopOrder.failed": "จัดลำดับไม่สำเร็จ",
  "stopOrder.notePickup": "รับ {code} {qty}",
  "stopOrder.noteDeliver": "ส่ง {code} {qty}",
  // the screen's own use (a form check / the lock banner) — not an HTTP refusal; the API says the same sentences as err.error.LOCATION_PIN_REQUIRED / TICKET_STATUS_UNKNOWN
  "cargo.err.locationPinRequired": "ตำแหน่งจัดเก็บต้องมีหมุด — ค้นหาสถานที่ คลิกบนแผนที่ หรือใส่ละติจูด/ลองจิจูด",
  "cargo.err.ticketStatusUnknown": "สถานะงานนี้ไม่รองรับการแก้สินค้า",
  "cargo.lock.completed": "งานนี้จบแล้ว — แก้สินค้าไม่ได้ (บันทึกผลสถานะได้)", // ADR 0467
  "cargo.tabAdd.notShopPin": "ไม่ใช่หมุดของร้านค้า {name}",
  "cargo.pinShare.title": "หมุด {name} อยู่ใกล้หมุดในใบงานนี้",
  "cargo.pinShare.use": "ใช้หมุดร่วมกับ {name} (ห่าง {m} ม.)",
  "cargo.pinShare.item": "รายการ {n} {name} ×{qty}",
  "cargo.pinShare.jobStop": "จุดที่ {n} ในใบงาน",
  "cargo.pinShare.unticked": "ไม่ติ๊ก = แยกเป็นหมุดใหม่",
  "cargo.tabAdd.useShopPin": "ใช้หมุดของร้านค้า",
  "cargo.shopChange.title": "ส่งที่จะใช้ที่ไหน",
  "cargo.shopChange.body": "รายการ {n} {name} — เปลี่ยนร้านค้าเป็น {shop} แต่ส่งที่เลือกไว้เองเป็นจุดอื่น",
  "cargo.shopChange.keep": "ใช้จุดที่เลือกไว้: {stop}",
  "cargo.shopChange.useNew": "ใช้หมุดของร้านค้าใหม่: {shop}",
  "cargo.shopChange.cancel": "ยกเลิกการเปลี่ยนร้านค้า",
  "cargo.shopChange.ok": "ตกลง",
  "cargo.shop.created": "เพิ่มร้านค้า {code} แล้ว",
  "ops.searchTicketsCargo": "ค้นหา: เลขงาน ชื่อใบงาน เส้นทาง คนขับ ทะเบียนรถ สัญญา · เลขใบส่งสินค้า (หรือ DN-วันที่) เลขอ้างอิงลูกค้า รหัส/ชื่อร้านค้า…",
  "ops.searchTruncated": "แสดง 200 ใบงานล่าสุดที่ตรงกับร้านค้า/เลขอ้างอิงนี้ — พิมพ์ให้เจาะจงขึ้น หรือกรองช่วงวันที่",
  // ADR 0457
  "cargo.leg.short.pickup": "รับ",
  "cargo.leg.short.deliver": "ส่ง",
  "cargo.leg.short.return": "คืน",
  "ops.stopCargo.attention": "ต้องดูแล {n}",
  "ops.stopCargo.doneOf": "เสร็จ {done}/{total}",
  // ADR 0457
  "ops.cargoCard.title": "ขนส่งสินค้า",
  "ops.cargoCard.allDone": "ครบ",
  // ADR 0457
  "ops.headerContracts": "สัญญา",
  "ops.contractsMore": "+{n} สัญญา",
  "ops.contractsCustomers": "รวม {n} ลูกค้า",
  // ADR 0457
  "ops.stopCargo.noGoods": "ไม่มีของต้องส่ง",
  // ADR 0457
  "cargo.status.problem": "มีปัญหา",
  "cargo.summary.attention": "ต้องดูแล",
  "cargo.group.hide": "ซ่อน",
  "cargo.group.show": "แสดง {n} รายการ",
  "cargo.col.evidence": "หลักฐาน",
  "cargo.ev.key.reason": "สาเหตุ",
  "cargo.ev.key.return": "คืน",
  "cargo.ev.key.photos": "รูป",
  "cargo.ev.key.returnPhoto": "รูปวางคืน",
  "cargo.ev.lostN": "หาย {n}",
  "cargo.item.count": "{n} รายการ",
  // ADR 0457
  "ops.listCargo": "สินค้า {done}/{total}",
  // ADR 0479 — the map stop popup · cargo.row.* = the driver app's words, kept equal by lib/map-stop-view.test.ts
  "cargo.row.pickup.pending": "รอรับ {qty} {unit}",
  "cargo.row.pickup.done": "รับครบ {qty} {unit}",
  "cargo.row.pickup.partial": "รับบางส่วน {done} / {qty} {unit}",
  "cargo.row.pickup.failed": "รับ {qty} {unit}ไม่สำเร็จ",
  "cargo.row.pickup.cancelled": "ยกเลิกรับ {qty} {unit}",
  "cargo.row.deliver.pending": "รอส่ง {qty} {unit}",
  "cargo.row.deliver.done": "ส่งครบ {qty} {unit}",
  "cargo.row.deliver.partial": "ส่งบางส่วน {done} / {qty} {unit}",
  "cargo.row.deliver.failed": "ส่ง {qty} {unit}ไม่สำเร็จ",
  "cargo.row.deliver.cancelled": "ยกเลิกส่ง {qty} {unit}",
  "cargo.row.return.pending": "รอคืน {qty} {unit}",
  "cargo.row.return.done": "คืนครบ {qty} {unit}",
  "cargo.row.return.partial": "คืนบางส่วน {done} / {qty} {unit}",
  "cargo.row.return.failed": "คืน {qty} {unit}ไม่สำเร็จ",
  "cargo.row.return.cancelled": "ยกเลิกคืน {qty} {unit}",
  "cargo.row.deliver.nogoods": "ไม่มีของต้องส่ง",
  "ops.mapStop.state.upcoming": "ยังไม่ถึง",
  "ops.mapStop.locusDone": "ส่งแล้ว {time}",
  "ops.mapStop.locusNoData": "ยังไม่มีข้อมูล",
  "ops.mapStop.state.current": "จุดปัจจุบัน",
  "ops.mapStop.state.arrived": "ถึงแล้ว",
  "ops.mapStop.state.skipped": "ข้ามจุดนี้",
  "ops.mapStop.late": "ช้า {n} นาที",
  "ops.mapStop.sched": "นัด",
  "ops.mapStop.arrivedAt": "ถึง",
  "ops.mapStop.cargoDone": "สินค้าเสร็จ",
  "ops.mapStop.allDone": "ครบ",
  "ops.mapStop.pax": "ผู้โดยสาร",
  "ops.mapStop.booked": "จอง",
  "ops.mapStop.boarded": "ขึ้นจริง",
  "ops.mapStop.walkin": "walk-in",
  "ops.mapStop.checkin": "เช็คอินด้วย {m}",
  "ops.mapStop.checkin.qr": "QR",
  "ops.mapStop.checkin.auto": "GPS",
  "ops.mapStop.checkin.driver": "คนขับ",
  "ops.mapStop.overflow": "เกินที่นั่ง ~{n} คน",
  "ops.mapStop.cargoN": "สินค้า {n} รายการ",
  "ops.mapStop.toCargoTab": "ดูในแท็บขนส่งสินค้า →",
  "ops.mapStop.close": "ปิด",
  // ADR 0457
  "cargo.end.to": "ส่งที่",
  "cargo.end.from": "รับที่",
  "cargo.end.returnTo": "คืนที่",
  "cargo.end.atPin": "ที่หมุด {name}",
  "cargo.ev.key.note": "หมายเหตุ",
  "cargo.ev.key.recipient": "ผู้รับ",
  "cargo.ev.key.ref": "อ้างอิง",
  "cargo.ev.returnAtStop": "{n} ที่จุด {stop}",
  "cargo.ev.lostAll": "ของหายทั้งหมด",
  "cargo.ev.notPutBack": "ยังไม่วางคืน",
  "cargo.branch.noContacts": "ไม่มีช่องทางติดต่อ",
  "cargo.branch.contactsLabel": "ช่องทางติดต่อ",
  "cargo.branch.more": "+{n} ช่องทาง",
  "cargo.branch.shops": "{n} ร้านค้า",
  "cargo.branch.removed": "สาขานี้ถูกลบแล้ว",
  // ── ADR 0394 — insights ──
  "insights.common.overview": "วันนี้",
  "insights.common.switcher": "สลับ Dashboard",
  "insights.common.period": "ช่วงเวลา",
  "insights.common.days30": "30 วัน",
  "insights.common.days90": "90 วัน",
  "insights.common.days365": "12 เดือน",
  "insights.common.periodNote": "ข้อมูล {from} ถึง {to} · เทียบกับ {pf} ถึง {pt}",
  "insights.common.snapshotNote": "สถานะ ณ ตอนนี้ · ปฏิทินมองไปข้างหน้า 12 สัปดาห์",
  "insights.common.vsPrev": "เทียบช่วงก่อน",
  "insights.common.noPrev": "ยังไม่มีค่าเทียบ",
  "insights.common.asOfNow": "ณ ตอนนี้",
  "insights.common.points": "{n} จุด",
  "insights.common.noData": "ยังไม่มีข้อมูลในช่วงนี้",
  "insights.common.calm": "เรียบร้อย ไม่มีรายการค้าง",
  "insights.common.target": "เป้า {v}",
  "insights.common.targetShort": "เป้า / ที่ต้องใช้",
  "insights.common.actual": "จริง",
  "insights.common.low": "น้อย",
  "insights.common.high": "มาก",
  "insights.common.today": "วันนี้",
  "insights.common.within90": "หมดใน 90 วัน",
  "insights.common.expiredBucket": "หมดแล้ว",
  "insights.common.showingOf": "แสดง {n} จาก {total} รายการ — เรียงที่มีปัญหาก่อน",
  "insights.unit.days": "วัน",
  "insights.unit.min": "นาที",
  "insights.unit.km": "กม.",
  "insights.unit.kmpl": "กม./ลิตร",
  "insights.unit.vehicles": "คัน",
  "insights.unit.people": "คน",
  "insights.unit.trips": "เที่ยว",
  "insights.unit.items": "รายการ",
  "insights.status.ok": "ปกติ",
  "insights.status.soon": "หมดใน 30 วัน",
  "insights.status.expired": "หมดแล้ว",
  "insights.status.missing": "ไม่มีข้อมูล",
  "insights.status.na": "ไม่เกี่ยว / ไม่ได้บันทึก",
  "insights.service.title": "บริการตรงเวลา",
  "insights.compliance.title": "เอกสาร & ใกล้หมดอายุ",
  "insights.fleet.title": "การใช้รถ",
  "insights.maintenance.title": "ซ่อมบำรุง & ตรวจสภาพ",
  "insights.cost.title": "ต้นทุน & น้ำมัน",
  "insights.workforce.title": "กำลังคน",
  "insights.finance.title": "การเงิน & ค่าจ้าง",
  "insights.ridership.title": "ผู้โดยสาร & การจอง",
  "insights.drivers.title": "ผลงานคนขับ & ความปลอดภัย",
  "insights.commercial.title": "ลูกค้า & สัญญา",
  "insights.capacity.title": "ความจุ & การจัดคิว",
  "insights.adoption.title": "การสื่อสาร & การใช้แอป",
  "insights.compliance.question": "มีอะไรหมดอายุแล้วหรือใกล้หมด และรถหรือคนไหนวิ่งงานโดยไม่มีความคุ้มครอง",
  "insights.compliance.kpi.expired": "หมดอายุแล้ว",
  "insights.compliance.kpi.due_30": "หมดใน 30 วัน",
  "insights.compliance.kpi.license_expired": "คนขับใบขับขี่หมดอายุ",
  "insights.note.license_missing": "ไม่มีข้อมูลใบขับขี่อีก {n} คน",
  "insights.compliance.card.calendar": "ปฏิทินหมดอายุ 12 สัปดาห์ข้างหน้า",
  "insights.compliance.card.calendar.q": "สัปดาห์ไหนงานต่ออายุกองหนัก · 12 สัปดาห์ข้างหน้า (ที่หมดแล้วอยู่ใน KPI)",
  "insights.compliance.card.gaps": "ช่องโหว่ที่ต้องปิดก่อน",
  "insights.compliance.card.gaps.q": "อะไรผิดกฎหมายหรือเสี่ยงถ้ารถออกวันนี้",
  "insights.compliance.card.matrix": "สถานะเอกสารรายคัน",
  "insights.compliance.card.matrix.q": "คันที่มีเอกสารหมด ขาด หรือใกล้หมด แย่สุดก่อน — กดชื่อเอกสารเพื่อกรอง · กดทะเบียนเพื่อเปิดรถ",
  "insights.common.docsAll": "ทุกคันที่มีเรื่อง",
  "insights.common.docExpiredDays": "หมด {n} วันแล้ว",
  "insights.common.docExpiredToday": "หมดวันนี้",
  "insights.common.docLeftDays": "อีก {n} วัน",
  "insights.common.docMissing": "ไม่มี",
  "insights.common.vstatus_repair": "อยู่ระหว่างซ่อม",
  "insights.common.vstatus_stored": "จอดเก็บ",
  "insights.common.docsNoneInFilter": "ไม่มีคันที่มีเรื่องนี้ในรายการ",
  "insights.common.docsKeyBad": "หมดแล้ว / ไม่มี",
  "insights.common.docsKeySoon": "ใกล้หมดใน 30 วัน",
  "insights.compliance.list.no_cmi": "รถใช้งานไม่มี พ.ร.บ. ที่มีผล",
  "insights.compliance.list.untaxed": "รถใช้งานภาษีหมด/ไม่มี",
  "insights.compliance.list.license_with_jobs": "คนขับใบขับขี่หมด แต่มีงานวันนี้–พรุ่งนี้",
  "insights.compliance.list.no_gps": "รถใช้งานไม่มีกล่อง GPS",
  "insights.compliance.list.docs_unverified": "เอกสารพนักงานรอตรวจ",
  "insights.series.cmi": "พ.ร.บ.",
  "insights.series.insurance": "ประกันภัย",
  "insights.series.tax": "ภาษีรถ",
  "insights.series.registration": "ทะเบียน",
  "insights.series.device": "GPS / อุปกรณ์",
  "insights.series.license": "ใบขับขี่",
  "insights.series.emp_doc": "บัตร / เอกสารพนักงาน",
  "insights.series.contract_doc": "เอกสารสัญญา",
  "insights.col.registration": "ทะเบียน",
  "insights.col.cmi": "พ.ร.บ.",
  "insights.col.insurance": "ประกัน",
  "insights.col.tax": "ภาษี",
  "insights.col.gps": "GPS",
  "insights.col.certificate": "ใบรับรองอุปกรณ์",
  // ── ADR 0394 — insights ──
  "insights.service.question": "เที่ยวรถวิ่งตรงเวลาตามที่สัญญากับลูกค้าไหม ถ้าสาย สายที่ไหน ช่วงไหน เพราะอะไร",
  "insights.service.kpi.ontime": "ตรงเวลา",
  "insights.service.kpi.done": "เที่ยวที่วิ่งจบ",
  "insights.service.kpi.cancel_rate": "ยกเลิก",
  "insights.service.kpi.avg_late": "สายเฉลี่ย (เฉพาะเที่ยวที่จบสาย)",
  "insights.service.card.ontime_trend": "อัตราตรงเวลา",
  "insights.service.card.ontime_trend.q": "ช่วงนี้ดีขึ้นหรือแย่ลง เทียบเป้า · ช่วงยาวรวมเป็นรายสัปดาห์",
  "insights.service.card.outcomes": "ผลของเที่ยว",
  "insights.service.card.outcomes.q": "เที่ยวทั้งหมดจบแบบไหน (รายสัปดาห์ / รายเดือน)",
  "insights.service.card.late_routes": "เส้นทางที่สายบ่อยที่สุด",
  "insights.service.card.late_routes.q": "ควรแก้ตารางเส้นไหนก่อน · นับเฉพาะเส้นที่มี 5 เที่ยวขึ้นไป",
  "insights.service.card.late_heat": "สายตามวันและเวลาออกรถ",
  "insights.service.card.late_heat.q": "% เที่ยวที่สาย แยกตามวันและชั่วโมงที่ออกรถ (00–23) — สายเป็นช่วงเวลาซ้ำ ๆ ไหม",
  "insights.service.card.funnel": "เที่ยวหลุดที่ขั้นไหน",
  "insights.service.card.funnel.q": "ปัญหาอยู่ที่การมอบหมาย การรับงาน หรือการวิ่ง",
  "insights.service.card.cancel_reasons": "สาเหตุการยกเลิก",
  "insights.service.card.cancel_reasons.q": "สีบอกว่าใครเป็นคนยกเลิก",
  "insights.service.stage.created": "สร้างเที่ยว",
  "insights.service.stage.assigned": "มอบหมายคนขับ",
  "insights.service.stage.accepted": "คนขับกดรับ",
  "insights.service.stage.started": "ออกรถ",
  "insights.service.stage.done": "วิ่งจบ",
  "insights.service.reason_unspecified": "ไม่ระบุเหตุผล",
  "insights.series.ontime": "ตรงเวลา",
  "insights.series.on_time": "ตรงเวลา",
  "insights.series.late_small": "สาย ≤15 นาที",
  "insights.series.late_big": "สาย >15 นาที",
  "insights.series.cancelled": "ยกเลิก",
  "insights.series.cancel_admin": "แอดมินยกเลิก",
  "insights.series.cancel_driver": "คนขับยกเลิก",
  "insights.unavailable.no_measurable": "ยังไม่มีเที่ยวที่วัดผลได้ในช่วงนี้",
  // ── ADR 0394 — insights ──
  "insights.maintenance.question": "รถพร้อมวิ่งแค่ไหน งานซ่อมค้างเท่าไร PM ตรงรอบไหม และอะไรเสียบ่อย",
  "insights.maintenance.kpi.open": "งานซ่อมเปิดอยู่",
  "insights.maintenance.kpi.pm_on_time": "PM ไม่เลยรอบ",
  "insights.maintenance.kpi.days_off_road": "วันจอดซ่อมเฉลี่ย",
  "insights.maintenance.kpi.spend": "ค่าซ่อมในช่วงนี้",
  "insights.note.overdue_wo": "เกินกำหนด {n} งาน",
  "insights.maintenance.card.aging": "อายุงานซ่อมที่ยังเปิด",
  "insights.maintenance.card.aging.q": "งานค้างนานแค่ไหน (วันนับจากเปิดงาน)",
  "insights.maintenance.card.failed_items": "ข้อตรวจที่ไม่ผ่านบ่อย",
  "insights.maintenance.card.failed_items.q": "% ไม่ผ่านต่อข้อ จากใบตรวจสภาพในช่วงนี้ (ข้อที่ตอบ 10 ครั้งขึ้นไป)",
  "insights.maintenance.card.repeat_vehicles": "รถซ่อมซ้ำ",
  "insights.maintenance.card.repeat_vehicles.q": "รถที่มีใบซ่อม 2 ครั้งขึ้นไปในช่วงนี้ — ควรพิจารณาปลดระวางไหม",
  "insights.maintenance.card.symptoms": "อาการที่เจอบ่อย",
  "insights.maintenance.card.symptoms.q": "จากรายการในใบเสร็จซ่อม",
  "insights.maintenance.basis.time": "รอบตามเวลา",
  "insights.maintenance.basis.odometer": "รอบตามระยะทาง",
  "insights.maintenance.basis.both": "เวลา + ระยะ",
  "insights.series.pm_overdue": "เลยรอบ",
  "insights.series.pm_due_soon": "ใกล้ถึงรอบ",
  "insights.series.pm_ok": "ปกติ",
  "insights.series.open_wo": "งานซ่อมเปิด",
  "insights.series.wo_repair": "ซ่อม",
  "insights.series.wo_maintenance": "บำรุงรักษา",
  "insights.series.wo_preventive": "PM ตามแผน",
  "insights.series.wo_inspection": "ตรวจสภาพ",
  "insights.series.other": "อื่น ๆ",
  "insights.unavailable.no_pm_plans": "ยังไม่มีแผน PM",
  "insights.unavailable.no_closed_wo": "ยังไม่มีงานซ่อมที่ปิดในช่วงนี้",
  // ── ADR 0394 — insights ──
  "insights.cost.question": "รถคันหนึ่งต้นทุนต่อกิโลเมตรเท่าไร เงินไปกับอะไร คันไหนกินน้ำมันผิดปกติ และข้างหน้าต้องจ่ายเท่าไร",
  "insights.cost.kpi.total": "ต้นทุนรถในช่วงนี้",
  "insights.cost.kpi.per_km": "ค่าวิ่งต่อ กม. (น้ำมัน + ซ่อม)",
  "insights.cost.kpi.kmpl": "อัตราสิ้นเปลืองทั้งกองรถ",
  "insights.note.measured_vehicles": "วัดได้จาก {n} คัน",
  "insights.cost.card.by_month": "ต้นทุนรายเดือนแยกหมวด",
  "insights.cost.card.by_month.q": "เงินไปกับอะไร และเดือนไหนพุ่ง · กติกาเดียวกับหน้าต้นทุนต่อคัน (ลงเดือนที่ถึงกำหนด)",
  "insights.cost.card.per_km": "ค่าวิ่ง/กม. สูงกว่ารถประเภทเดียวกัน",
  "insights.cost.card.per_km.q": "น้ำมัน + ซ่อม ต่อ กม. เทียบค่ากลางของรถประเภทเดียวกัน (ไม่รวมประกัน ภาษี ค่างวด ที่ลงก้อนเดียว) · นับเฉพาะคันที่วิ่งเฉลี่ย ≥ 10 กม./วัน",
  "insights.cost.card.kmpl_vs_spec": "km/L จริง vs สเปกผู้ผลิต",
  "insights.cost.card.kmpl_vs_spec.q": "เส้นแดง = ต่ำกว่าสเปกเกิน 10% · เรียงจากต่างมากสุด",
  "insights.cost.card.fuel_flags": "ใบเติมน้ำมันที่ระบบติดธง",
  "insights.cost.card.fuel_flags.q": "สาเหตุไหนเกิดบ่อย — กดเพื่อไปรีวิว",
  "insights.cost.card.cash_ahead": "เงินที่ต้องจ่ายล่วงหน้า 12 เดือน",
  "insights.cost.card.cash_ahead.q": "ประมาณการ: ค่างวดที่ยังไม่จ่าย + ต่อประกัน/ภาษีด้วยราคาเดิม",
  "insights.cost.flag.off_station": "เติมนอกปั๊มที่กำหนด",
  "insights.cost.flag.no_location": "ไม่มีพิกัด",
  "insights.cost.flag.odometer_backwards": "เลขไมล์ถอยหลัง",
  "insights.cost.flag.clock_skew": "เวลาเครื่องไม่ตรง",
  "insights.cost.flag.vehicle_not_active": "รถไม่ได้ใช้งาน",
  "insights.series.cost_fuel": "น้ำมัน",
  "insights.series.cost_maintenance": "ซ่อม",
  "insights.series.cost_installment": "ค่างวดรถ",
  "insights.series.cost_insurance_tax": "ประกัน + ภาษี",
  "insights.series.cost_devices": "GPS / อุปกรณ์",
  "insights.series.renew_insurance": "ต่อประกัน",
  "insights.series.renew_tax": "ต่อภาษี",
  "insights.series.kmpl_measured": "จริง (จากใบเติม)",
  "insights.series.kmpl_spec": "สเปก",
  "insights.unavailable.no_distance": "ยังไม่รู้ระยะทาง (ต้องมีใบเติมที่กรอกเลขไมล์)",
  // ── ADR 0394 — insights ──
  "insights.fleet.question": "รถแต่ละคันถูกใช้คุ้มไหม คันไหนจอดทิ้ง คันไหนเสีย และกองรถเก่าแค่ไหน",
  "insights.fleet.kpi.utilization": "ออกวิ่งเฉลี่ยต่อวัน",
  "insights.note.on_road": "สถานะใช้งาน {n} คัน",
  "insights.fleet.card.utilization_trend": "% รถที่ออกวิ่งต่อวัน",
  "insights.fleet.card.utilization_trend.q": "มีใบงานอย่างน้อย 1 ใบในวันนั้น เทียบรถสถานะใช้งานทั้งหมด",
  "insights.fleet.card.idle": "รถจอดนิ่ง",
  "insights.fleet.card.idle.q": "วันนับจากเที่ยวล่าสุด (7 วันขึ้นไป) — รถที่ไม่เคยมีใบงานไม่นับ",
  "insights.fleet.all": "ทั้งหมด",
  "insights.series.utilization": "ออกวิ่ง",
  "insights.series.v_active": "ใช้งาน",
  "insights.series.v_repair": "ซ่อม",
  "insights.series.v_stored": "จอดเก็บ",
  "insights.series.vehicles": "รถ",
  "insights.vtype.van": "รถตู้",
  "insights.vtype.minibus": "มินิบัส",
  "insights.vtype.bus": "รถบัส",
  "insights.vtype.truck": "รถบรรทุก",
  "insights.vtype.other": "อื่น ๆ",
  "insights.vtype.pickup": "รถกระบะ",
  "insights.vtype.sedan": "รถเก๋ง",
  "insights.vtype.motorcycle": "รถจักรยานยนต์",
  "insights.x.<2": "< 2 ปี",
  "insights.x.2-4": "2–4 ปี",
  "insights.x.5-7": "5–7 ปี",
  "insights.x.8-10": "8–10 ปี",
  "insights.x.11-13": "11–13 ปี",
  "insights.x.14+": "14 ปีขึ้นไป",
  "insights.x.unknown": "ไม่ระบุปี",
  "insights.x.0-3": "0–3 วัน",
  "insights.x.4-7": "4–7 วัน",
  "insights.x.8-14": "8–14 วัน",
  "insights.x.15-30": "15–30 วัน",
  "insights.x.31+": "31 วันขึ้นไป",
  // ── ADR 0394 — insights ──
  "insights.workforce.question": "ใครลาเมื่อไร ใครจะครบทดลองงาน และการรับคนใหม่ไปถึงไหน",
  "insights.workforce.kpi.active": "พนักงานทำงานอยู่",
  "insights.workforce.kpi.on_leave_today": "ลาวันนี้",
  "insights.workforce.kpi.leave_pending": "ใบลารออนุมัติ",
  "insights.note.drivers": "คนขับ {n} คน",
  "insights.workforce.card.leave_calendar": "ปฏิทินการลา 5 สัปดาห์ข้างหน้า",
  "insights.workforce.card.leave_calendar.q": "จำนวนคนลา (อนุมัติแล้ว) ต่อวัน — วันไหนชนกันเยอะ",
  "insights.workforce.card.hiring": "การรับสมัคร",
  "insights.workforce.card.hiring.q": "ผู้สมัครที่เข้ามาในช่วงนี้ หลุดที่ขั้นไหน",
  "insights.workforce.card.leave_types": "วันลารายเดือนแยกประเภท",
  "insights.workforce.card.leave_types.q": "ลาป่วยเพิ่มขึ้นไหม (นับวันตามวันเริ่มลา · ครึ่งวัน = 0.5)",
  "insights.workforce.stage.applied": "สมัคร",
  "insights.workforce.stage.screening": "คัดกรอง",
  "insights.workforce.stage.interview": "สัมภาษณ์",
  "insights.workforce.stage.offer": "เสนองาน",
  "insights.workforce.stage.onboarding": "เริ่มงาน",
  "insights.series.emp_company_driver": "คนขับภายใน",
  "insights.series.emp_contractor": "คนขับภายนอก",
  "insights.series.emp_office_staff": "สำนักงาน",
  "insights.series.emp_mechanic": "ช่าง",
  "insights.series.emp_part_time": "พาร์ทไทม์",
  "insights.series.leave_annual": "ลาพักร้อน",
  "insights.series.leave_sick": "ลาป่วย",
  "insights.series.leave_personal": "ลากิจ",
  "insights.series.leave_other": "อื่น ๆ",
  // ── ADR 0394 — insights ──
  "insights.finance.question": "รายรับกับค่าเที่ยวเป็นอย่างไร", // ADR 0471 — เหลือเฉพาะรายรับ/ค่าเที่ยว
  "insights.finance.kpi.revenue": "รายรับในช่วงนี้",
  "insights.finance.kpi.expense": "ค่าเที่ยว + รายการบันทึกเอง",
  "insights.finance.card.rev_exp": "รายรับ vs ค่าเที่ยวรายเดือน",
  "insights.finance.card.rev_exp.q": "ฝั่งจ่ายนับแค่ค่าเที่ยวคนขับและรายการที่บันทึกเอง — ยังไม่รวมน้ำมัน ซ่อม ประกัน เงินเดือน",
  "insights.st.open": "ยังไม่ล็อก",
  "insights.st.in_progress": "เดือนนี้ยังไม่จบ",
  "insights.st.no_trips": "ไม่มีเที่ยว",
  "insights.st.locked": "ล็อกแล้ว",
  "insights.st.paid": "จ่ายแล้ว",
  "insights.st.none": "ยังไม่มีใบ",
  "insights.st.draft": "ร่าง",
  "insights.st.calculated": "คำนวณแล้ว",
  "insights.st.verified": "ตรวจแล้ว",
  "insights.series.revenue": "รายรับ",
  "insights.series.expense": "ค่าเที่ยว + รายการเอง",
  "insights.series.pay_base": "เงินเดือนฐาน",
  "insights.series.pay_ot": "OT",
  "insights.series.pay_allowance": "เบี้ยเลี้ยง/ค่าเที่ยว",
  // ── ADR 0394 — insights ──
  "insights.ridership.question": "รถมีคนนั่งคุ้มไหม คนจองแล้วมาจริงไหม และผู้โดยสารพอใจไหม",
  "insights.ridership.kpi.seat_util": "ที่นั่งถูกใช้",
  "insights.ridership.kpi.bookings": "การจอง",
  "insights.ridership.kpi.no_show": "ไม่มาขึ้นรถ (no-show)",
  "insights.ridership.kpi.rating": "คะแนนความพึงพอใจเฉลี่ย",
  "insights.ridership.card.seat_by_route": "เส้นทางที่แน่นสุดและว่างสุด",
  "insights.ridership.card.seat_by_route.q": "เที่ยวที่วิ่งจบแล้ว รวมเที่ยวที่ไม่มีคนจอง · เทียบเป้า 75% — ต่ำกว่าเป้า (แดง) = รถใหญ่เกินงาน",
  "insights.ridership.card.daily": "การจองรายวันแยกผลลัพธ์",
  "insights.ridership.card.daily.q": "จองแล้วมาจริงกี่คน (ตามวันของเที่ยว · ช่วงยาวรวมเป็นรายสัปดาห์)",
  "insights.ridership.card.boarding_heat": "ช่วงเวลาที่คนขึ้นรถ",
  "insights.ridership.card.boarding_heat.q": "จำนวนคนขึ้นรถตามวันและชั่วโมงที่รถออก (00–23)",
  "insights.series.ridden": "ขึ้นรถ",
  "insights.series.no_show_b": "ไม่มา",
  "insights.series.cancelled_b": "ยกเลิกจอง",
  "insights.series.ch_self": "ผู้โดยสารทำเอง (แอป/พอร์ทัล/QR)",
  "insights.series.ch_staff": "เจ้าหน้าที่/คนขับ",
  "insights.series.ch_walkin": "Walk-in",
  "insights.unavailable.no_capacity": "ใบงานในช่วงนี้ไม่ได้ตั้งจำนวนที่นั่ง",
  "insights.unavailable.no_ratings": "ยังไม่มีคะแนนในช่วงนี้",
  // ── ADR 0394 — insights ──
  "insights.drivers.question": "คนขับคนไหนต้องดูแล เกิดเหตุอะไรบ่อย และตอบสนองเหตุฉุกเฉินเร็วแค่ไหน",
  "insights.drivers.kpi.open_cases": "เคสที่ยังเปิด",
  "insights.drivers.kpi.sos_ack": "SOS — นาทีจนมีคนรับ",
  "insights.drivers.card.scorecard": "คนขับที่ควรคุยด้วยก่อน",
  "insights.drivers.card.scorecard.q": "เรียงจากเคส ร้องเรียน และเที่ยวที่สาย (นับตรงเวลาเมื่อวิ่ง 10 เที่ยวขึ้นไป) · ร้องเรียน = ความเห็นของผู้โดยสารที่ผูกกับเที่ยวของคนขับคนนั้น",
  "insights.drivers.card.cases_monthly": "เคสรายเดือนแยกหมวด",
  "insights.drivers.card.cases_monthly.q": "เคสตามหมวด รายเดือน (นับตามวันเกิดเหตุ)",
  "insights.drivers.col.name": "คนขับ",
  "insights.drivers.col.branch": "สาขา",
  "insights.drivers.col.ontime": "ตรงเวลา",
  "insights.drivers.col.trips": "เที่ยว",
  "insights.drivers.col.cases": "เคส",
  "insights.drivers.col.complaints": "ร้องเรียน",
  "insights.ax.cat_accident": "อุบัติเหตุ",
  "insights.ax.cat_violation": "ฝ่าฝืนกฎ",
  "insights.ax.cat_complaint": "ร้องเรียน",
  "insights.ax.cat_inspection": "ตรวจพบ",
  "insights.ax.sev_low": "ต่ำ",
  "insights.ax.sev_medium": "กลาง",
  "insights.ax.sev_high": "สูง",
  "insights.ax.sev_critical": "วิกฤต",
  "insights.series.case_accident": "อุบัติเหตุ",
  "insights.series.case_violation": "ฝ่าฝืนกฎ",
  "insights.series.case_complaint": "ร้องเรียน",
  "insights.series.case_inspection": "ตรวจพบ",
  "insights.series.drivers": "คนขับ",
  "insights.x.<60": "< 60",
  "insights.x.60-69": "60–69",
  "insights.x.70-79": "70–79",
  "insights.x.80-89": "80–89",
  "insights.x.90-100": "90–100",
  "insights.unavailable.no_sos": "ไม่มี SOS ในช่วงนี้",
  "insights.unavailable.sos_unanswered": "มี SOS แต่ยังไม่มีใครรับเลย",
  // ── ADR 0394 — insights ──
  "insights.commercial.question": "รายได้มาจากลูกค้าไหน เส้นทางไหนกำไร/ขาดทุน และสัญญาไหนกำลังจะหมด (ตัวเลขเงินเป็นประมาณการ)",
  "insights.commercial.kpi.monthly_value": "มูลค่าสัญญาต่อเดือน (ประมาณการ)",
  "insights.commercial.kpi.expiring": "สัญญาหมดใน 30 วัน",
  "insights.commercial.kpi.margin": "กำไรขั้นต้น (ประมาณการ)",
  "insights.commercial.card.by_customer": "มูลค่าสัญญาตามลูกค้า",
  "insights.commercial.card.by_customer.q": "บาท/เดือน จากสัญญาที่ใช้งาน — พึ่งลูกค้ารายใหญ่มากแค่ไหน",
  "insights.commercial.card.route_margin": "กำไรรวมรายเส้นทาง (ประมาณการ)",
  "insights.commercial.card.route_margin.q": "รายรับ − ต้นทุน ตามราคาบนใบงานตอนวิ่ง · 8 เส้นที่กระทบเงินมากสุด (บวกหรือลบ) · ชี้เพื่อดูต่อเที่ยว",
  "insights.commercial.card.timeline": "ไทม์ไลน์สัญญา",
  "insights.commercial.card.timeline.q": "สัญญาที่ใกล้หมดอยู่บนสุด — ควรเริ่มเจรจาต่อสัญญาเมื่อไร",
  "insights.commercial.no_customer": "ไม่ระบุลูกค้า",
  "insights.commercial.other_customers": "ลูกค้าอื่น ๆ",
  "insights.unavailable.no_route_econ": "เส้นทางที่วิ่งยังไม่ได้กรอกรายรับ/ต้นทุนต่องาน",
  // ── ADR 0394 — insights ──
  "insights.capacity.question": "อีก 2 สัปดาห์มีรถและคนขับพอกับงานไหม ใครต้องหาคนแทนพรุ่งนี้ และงานกระจายเป็นธรรมไหม",
  "insights.capacity.kpi.unassigned": "เที่ยวที่ยังจัดรถ/คนไม่ครบ (14 วัน)",
  "insights.note.available_vehicles": "จากรถพร้อมวิ่ง {n} คัน",
  "insights.capacity.card.demand_supply": "รถที่ต้องใช้ vs รถพร้อม (14 วันข้างหน้า)",
  "insights.capacity.card.demand_supply.q": "แท่งเกินเส้น = รถไม่พอ · เที่ยวที่ยังไม่จัดรถนับ 1 คันต่อเที่ยว",
  "insights.capacity.card.teams_tomorrow": "คนขับพรุ่งนี้ ตามทีม",
  "insights.capacity.card.teams_tomorrow.q": "คนที่มีงานพรุ่งนี้แต่ลา ต้องหาคนแทนก่อน · ว่างสำรอง = ไม่ได้ลาและยังไม่มีงาน",
  "insights.capacity.col.spare": "ว่างสำรอง",
  "insights.capacity.col.assigned_on_leave": "มีงานแต่ลา",
  "insights.capacity.col.assigned": "มีงาน",
  "insights.capacity.col.name": "ทีม",
  "insights.capacity.card.workload": "ภาระงานคนขับ",
  "insights.capacity.card.workload.q": "เที่ยวต่อสัปดาห์ต่อคนขับในช่วงนี้ — หางยาวขวา = บางคนรับงานหนักเกิน",
  "insights.series.needed": "รถที่ต้องใช้",
  "insights.series.available": "รถพร้อมวิ่ง",
  "insights.series.overflow": "คนล้นรถ",
  "insights.x.0": "0",
  "insights.x.1-9": "1–9",
  "insights.x.10-19": "10–19",
  "insights.x.20-29": "20–29",
  "insights.x.30-39": "30–39",
  "insights.x.40+": "40+",
  "insights.unavailable.no_driver_trips": "ยังไม่มีเที่ยวที่จัดคนขับในช่วงนี้",
  // ── ADR 0394 — insights ──
  "insights.unavailable.no_future_trips": "ยังไม่มีใบงานใน 14 วันข้างหน้า",
  // ── ADR 0394 — insights ──
  "insights.adoption.question": "คนขับใช้แอปปิดงานเองไหม ประกาศถึงคนไหม และอีเมล/คำเชิญค้างไหม",
  "insights.adoption.kpi.latest_read": "คนอ่านประกาศล่าสุดแล้ว",
  "insights.adoption.kpi.email_failed": "อีเมลส่งไม่สำเร็จ",
  "insights.adoption.kpi.invites_pending": "คำเชิญที่ยังไม่ตอบรับ",
  "insights.note.of_drivers": "จากคนขับ {n} คน",
  "insights.adoption.card.driver_logins": "% คนขับที่เข้าแอปต่อวัน",
  "insights.adoption.card.driver_logins.q": "นับคนขับไม่ซ้ำต่อวัน เทียบกับคนขับทั้งหมด",
  "insights.adoption.card.announcements": "อัตราอ่านประกาศ",
  "insights.adoption.card.announcements.q": "6 ประกาศล่าสุด · คิดจากคนที่เห็นประกาศนั้นได้ (คนขับ/ผู้โดยสารที่มีบัญชี) · ผู้โดยสารเริ่มบันทึกการอ่าน 2 ต.ค. 2026",
  "insights.series.drivers_active": "คนขับที่เข้าแอป",
  "insights.series.login_failed": "ล็อกอินล้มเหลว",
  "insights.unavailable.no_announcements": "ยังไม่มีประกาศที่เผยแพร่",
  // ── ADR 0394 — insights ──
  "insights.common.noDataNow": "ยังไม่มีรายการในตอนนี้",
  "insights.common.emptyHintPeriod": "ช่วงที่เลือกไม่มีรายการ — ลองดูช่วงที่ยาวขึ้น",
  "insights.common.emptyHintLongest": "ตลอด 12 เดือนที่ผ่านมายังไม่มีรายการของเรื่องนี้",
  "insights.common.emptyHintSnapshot": "การ์ดนี้จะแสดงเมื่อมีข้อมูลในระบบ",
  "insights.common.widen": "ดู 12 เดือน",
  "insights.common.unavailableHint": "ต้องมีข้อมูลนี้ในระบบก่อนจึงคำนวณได้ — เปลี่ยนช่วงเวลาไม่ช่วย",
  // ── ADR 0394 — insights ──
  "insights.fleet.kpi.idle": "รถจอดนิ่ง 7 วันขึ้นไป",
  "insights.note.never_used": "ไม่มีงานเลยใน 180 วัน {n} คัน",
  "insights.note.sos_unacked": "ยังไม่มีคนรับ {n} ครั้ง",
  "insights.note.unrecorded_bookings": "ไม่ได้บันทึกว่าขึ้นหรือไม่มา {n} ใบ",
  "insights.note.overflow_resolved": "กดแก้แล้ว {n} เที่ยว",
  "insights.common.moreItems": "และอีก {n} รายการ — ดูทั้งหมดในหน้ารายการ",
  // ── ADR 0394 — insights ──
  "insights.common.filters": "ตัวกรอง",
  "insights.common.all": "ทั้งหมด",
  "insights.common.filter_contract_id": "สัญญา",
  "insights.common.filter_vehicle_type": "ประเภทรถ",
  "insights.common.filter_branch": "สาขา",
  "insights.common.moreCount": "และอีก {n} รายการ",
  "insights.common.seeAll": "ดูทั้งหมด",
  "insights.unavailable.filter_unsupported": "ข้อมูลนี้แยกตามตัวกรองที่เลือกไม่ได้ — ล้างตัวกรองเพื่อดู",
  "insights.fleet.card.days_used": "รถแต่ละคันวิ่งกี่ % ของวัน",
  "insights.fleet.card.days_used.q": "ทั้งกองรถในกราฟเดียว — แท่งซ้ายคือรถที่แทบไม่ได้ใช้",
  "insights.x.use_0": "ไม่ได้วิ่ง",
  "insights.x.use_1-25": "1–25%",
  "insights.x.use_26-50": "26–50%",
  "insights.x.use_51-75": "51–75%",
  "insights.x.use_76-100": "76–100%",
  "insights.x.wk_0": "ไม่มีงาน",
  "insights.x.wk_<2": "< 2",
  "insights.x.wk_2-4": "2–4",
  "insights.x.wk_5-9": "5–9",
  "insights.x.wk_10-14": "10–14",
  "insights.x.wk_15+": "15+",
  "insights.x.pay_lt50": "< 50%",
  "insights.x.pay_50-79": "50–79%",
  "insights.x.pay_80-120": "80–120%",
  "insights.x.pay_121-150": "121–150%",
  "insights.x.pay_gt150": "> 150%",
  // ── ADR 0394 — insights ──
  "insights.adoption.kpi.staff_7d": "เจ้าหน้าที่ที่เข้าระบบ 7 วันล่าสุด",
  "insights.adoption.card.staff_logins": "เจ้าหน้าที่ที่เข้าระบบต่อวัน",
  "insights.adoption.card.staff_logins.q": "นับคนไม่ซ้ำต่อวัน · เริ่มบันทึก 2 ต.ค. 2026 ก่อนหน้านั้นจึงเป็นศูนย์ · คนที่อยู่หลายองค์กรยังไม่ถูกนับ",
  "insights.series.staff_active": "เจ้าหน้าที่",
  // ── ADR 0394 — insights ──
  "insights.series.aud_drivers": "ถึงคนขับ",
  "insights.series.aud_passengers": "ถึงผู้โดยสาร",
  "insights.series.aud_all": "ถึงทุกคน",
  // ── ADR 0394 — insights ──
  "insights.common.partialMonth": "จาง/เส้นประ = ช่วงล่าสุดยังไม่จบ (นับถึง {to})",
  // ── ADR 0394 — insights ──
  "insights.focal.margin": "ส่วนต่างหลังหักค่าเที่ยว {x}",
  "insights.focal.busiest_week": "สัปดาห์ที่หมดเยอะสุด · เริ่ม {x}",
  "insights.focal.above_avg": "{x} · สูงกว่าค่าเฉลี่ย {n}%",
  "insights.focal.below_avg": "{x} · ต่ำกว่าค่าเฉลี่ย {n}%",
  "insights.focal.series_share": "สัดส่วน{series} · {x}",
  "insights.focal.barely_used": "คันที่แทบไม่ได้วิ่ง (≤ 25% ของวัน)",
  "insights.focal.stale_wo": "งานซ่อมค้างเกิน 30 วัน",
  "insights.focal.overloaded": "คนขับรับงาน 15+ เที่ยว/สัปดาห์",
  "insights.focal.pay_outliers": "ค่าเที่ยวนอกช่วงปกติ — ควรตรวจก่อนล็อก",
  "insights.focal.short_days": "วันที่รถไม่พอใน 14 วันข้างหน้า",
  "insights.focal.next_months": "ต้องจ่ายใน {n} เดือนข้างหน้า",
  "insights.focal.top": "อันดับ 1 · {label}",
  "insights.focal.top_share": "{label} · ของทั้งหมด",
  "insights.focal.loss_routes": "เส้นทางที่ขาดทุน",
  "insights.focal.below_target": "เส้นทาง/ทีมที่ต่ำกว่าเป้า",
  "insights.focal.above_target": "เกินเป้า",
  "insights.focal.hot": "จุดพีค · {row} {col}",
  "insights.focal.must_fix": "เรื่องผิดกฎหมาย ต้องปิดก่อน",
  "insights.focal.worst_gap": "{label} · ต่ำกว่าสเปกมากสุด",
  "insights.focal.below_target_day": "วันที่ต่ำกว่าเป้า {target}",
  "insights.focal.below_target_week": "สัปดาห์ที่ต่ำกว่าเป้า {target}",
  "insights.focal.below_target_month": "เดือนที่ต่ำกว่าเป้า {target}",
  "insights.focal.lowest_at": "ต่ำสุด · {x}",
  "insights.focal.off_target": "เส้นทางที่ต่ำกว่าเป้าหรือคนล้นรถ",
  "insights.focal.assigned_on_leave": "คนที่มีงานพรุ่งนี้แต่ลา",
  // ── viz audit 7 ต.ค. 2026 — การ์ด/KPI ที่เพิ่มหรือแทนที่ ──
  "insights.fleet.kpi.spare_peak": "รถสำรองช่วงพีค",
  "insights.fleet.kpi.down": "รถจอดเสียตอนนี้",
  "insights.fleet.card.down": "รถจอดเสียตอนนี้",
  "insights.fleet.card.down.q": "คันไหนจอดมากี่วัน เพราะอะไร — นับจากใบซ่อมที่เปิดอยู่ใบเก่าสุด",
  "insights.fleet.col.name": "ทะเบียน",
  "insights.fleet.col.days_down": "จอดมา",
  "insights.fleet.col.reason": "สาเหตุ",
  "insights.note.peak_used": "วันพีคใช้ {n} คัน",
  "insights.note.longest_down_days": "นานสุด {n} วัน",
  "insights.maintenance.card.spend_mix": "ค่าซ่อมตามแผน vs ซ่อมฉุกเฉิน",
  "insights.maintenance.card.spend_mix.q": "ซ่อมฉุกเฉินกินสัดส่วนมากขึ้นไหม · 12 เดือน (ไม่รวมเติมน้ำมัน/ตรวจสภาพ — ยอดรวมทุกหมวดอยู่หน้าต้นทุน)",
  "insights.maintenance.card.pm_overdue": "PM ที่เลยรอบ",
  "insights.maintenance.card.pm_overdue.q": "คันไหน งานอะไร เลยมากี่วัน / กี่ กม. — แย่สุดขึ้นก่อน",
  "insights.maintenance.col.name": "ทะเบียน",
  "insights.maintenance.col.service": "งาน",
  "insights.maintenance.col.days_over": "เลยกี่วัน",
  "insights.maintenance.col.km_over": "เลยกี่ กม.",
  "insights.series.wo_planned": "ตามแผน (PM/ปรับปรุง)",
  "insights.series.wo_unplanned": "ซ่อมฉุกเฉิน",
  "insights.cost.kpi.overdue_installments": "ค่างวดค้างจ่าย ณ วันนี้",
  "insights.note.overdue_payments": "{n} งวด",
  "insights.series.flag_pending": "รอตรวจ",
  "insights.series.flag_reviewed": "ตรวจแล้ว",
  "insights.workforce.kpi.trial_ending": "ครบทดลองงานใน 30 วัน",
  "insights.note.on_trial": "ทดลองงานอยู่ {n} คน",
  "insights.series.pnl_trip_pay": "ค่าเที่ยวคนขับ",
  "insights.series.pnl_payroll": "เงินเดือน",
  "insights.series.pnl_vehicle": "ต้นทุนรถ",
  "insights.focal.pnl_margin": "กำไรหลังหักต้นทุนที่รู้ · {x}",
  "insights.ridership.kpi.overflow": "เที่ยวที่คนล้นรถ",
  "insights.ridership.card.overflow_hours": "คนล้นรถตามชั่วโมง",
  "insights.ridership.card.overflow_hours.q": "ชั่วโมงที่รถออก (00–23) ของเที่ยวที่ใบจองเกินที่นั่ง — ควรเพิ่มรถเสริมช่วงไหน",
  "insights.note.complaints": "ร้องเรียน {n} เรื่อง",
  "insights.focal.peak_overflow": "รอบที่ล้นบ่อยสุด · {x}:00",
  "insights.drivers.kpi.accident_rate": "อุบัติเหตุต่อ 1,000 เที่ยว",
  "insights.drivers.kpi.with_case": "คนขับที่มีเคสในช่วงนี้",
  "insights.drivers.card.serious_open": "เคสร้ายแรงที่ยังเปิด",
  "insights.drivers.card.serious_open.q": "ความรุนแรงสูง/วิกฤต เก่าสุดก่อน — ต้องปิดก่อนเรื่องอื่น",
  "insights.drivers.col.case": "เรื่อง",
  "insights.drivers.col.category": "หมวด",
  "insights.drivers.col.severity": "ความรุนแรง",
  "insights.drivers.col.days_open": "เปิดมา",
  "insights.note.accidents": "อุบัติเหตุ {n} ครั้ง",
  "insights.unit.per_1k": "ครั้ง",
  "insights.unavailable.no_trips": "ยังไม่มีเที่ยวที่วิ่งในช่วงนี้",
  "insights.st.cat_accident": "อุบัติเหตุ",
  "insights.st.cat_violation": "ฝ่าฝืนกฎ",
  "insights.st.cat_complaint": "ร้องเรียน",
  "insights.st.cat_inspection": "ตรวจพบ",
  "insights.st.sev_high": "สูง",
  "insights.st.sev_critical": "วิกฤต",
  "insights.commercial.kpi.revenue_vs_value": "รายได้จริง ÷ มูลค่าสัญญา",
  "insights.note.contracts_with_value": "สัญญาที่กรอกมูลค่า {n} ฉบับ",
  "insights.unavailable.no_contract_value": "สัญญาที่ใช้งานยังไม่มีมูลค่าต่อเดือน",
  "insights.capacity.kpi.spare_tomorrow": "คนขับสำรองพรุ่งนี้",
  "insights.capacity.kpi.schedule_horizon": "มีตารางงานล่วงหน้า",
  "insights.note.assigned_on_leave": "มีงานแต่ลา {n} คน",
  "insights.note.of_days": "จาก {n} วันที่ดู",
  "insights.adoption.kpi.closed_by_driver": "เที่ยวที่คนขับปิดเองในแอป",
  "insights.note.closed_by_admin": "แอดมินปิดแทน {n} เที่ยว",
  "insights.unavailable.no_completed_trips": "ยังไม่มีเที่ยวที่จบในช่วงนี้",
  "insights.compliance.kpi.legal_blocked": "รถที่วิ่งไม่ได้ตามกฎหมายวันนี้",
  "insights.series.doc_vehicle": "เอกสารรถ",
  "insights.series.doc_people": "ใบขับขี่/เอกสารพนักงาน",
  "insights.series.doc_contract": "เอกสารสัญญา",
  "insights.unavailable.no_paid_drivers": "ยังไม่มีคนขับที่ได้ค่าเที่ยวในช่วงนี้ — หาค่ากลางไม่ได้",
  "insights.series.pm_unknown": "ไม่รู้ (ไม่มีเลขไมล์/กำหนด)",
  "insights.note.pm_unknown": "ตัดสินไม่ได้ {n} แผน (ไม่มีเลขไมล์)",
  "insights.status.unknown": "ไม่รู้วันหมด",
  "insights.series.ontime_7d": "เฉลี่ย 7 วัน",
  "insights.series.ontime_daily": "รายวัน",
  // ── insp — ตรวจสภาพ / inspections (ADR 0367) ──────────────────────────
  "insp.pageTitle": "ตรวจสภาพรถ",
  "insp.pageSubtitle": "ใบตรวจ ผลตรวจ และชุดตรวจของทุกคัน",
  "insp.tabResults": "ผลการตรวจ",
  "insp.tabTemplates": "ชุดตรวจ",
  "insp.templates": "ชุดตรวจ",
  "insp.statusDraft": "ร่าง",
  "insp.statusInProgress": "กำลังตรวจ",
  "insp.statusSubmitted": "รอตรวจรับ",
  "insp.statusClosed": "ปิดแล้ว",
  "insp.statusCancelled": "ยกเลิก",
  "insp.roleOwner": "เจ้าของ",
  "insp.roleInspector": "ผู้ตรวจ",
  "insp.roleDriver": "คนขับ",
  "insp.roleReviewer": "ผู้ตรวจรับ",
  "insp.roleWatcher": "ผู้ติดตาม",
  "insp.typePassFail": "ผ่าน/ไม่ผ่าน",
  "insp.typeText": "ข้อความ",
  "insp.typeNumber": "ตัวเลข",
  "insp.typeChoice": "ตัวเลือก",
  "insp.typePhoto": "รูปถ่าย",
  "insp.pass": "ผ่าน",
  "insp.fail": "ไม่ผ่าน",
  "insp.na": "ไม่เกี่ยว",
  "insp.notChecked": "ยังไม่ตรวจ",
  "insp.required": "บังคับ",
  "insp.resultHasFail": "มีข้อไม่ผ่าน",
  "insp.resultFailOpen": "ไม่ผ่านแต่ยังไม่เปิดตั๋ว",
  "insp.resultIncomplete": "ยังตรวจไม่ครบ",
  "insp.resultAllPass": "ผ่านทั้งหมด",
  "insp.resultAny": "ผลตรวจ: ทั้งหมด",
  "insp.resultAria": "ผ่าน {pass} ไม่ผ่าน {fail} จาก {total} ข้อ",
  "insp.blockRequired": "ยังไม่ได้ตอบข้อบังคับ",
  "insp.blockNote": "ไม่ผ่านต้องใส่หมายเหตุ",
  "insp.blockPhoto": "ไม่ผ่านต้องแนบรูป",
  "insp.blockMinPhotos": "รูปยังไม่ครบตามที่กำหนด",
  "insp.statToday": "ตรวจวันนี้",
  "insp.statNotInspected": "ยังไม่ตรวจวันนี้ {n} คัน",
  "insp.statAwaiting": "รอตรวจรับ",
  "insp.statOldest": "ค้างนานสุด {n} วัน",
  "insp.statFail7d": "ใบที่มีข้อไม่ผ่าน (7 วัน)",
  "insp.statFailOpen": "ไม่ผ่านแต่ยังไม่เปิดตั๋วซ่อม",
  "insp.searchPlaceholder": "ค้นหา เลขที่ ทะเบียน ชุดตรวจ ผู้เกี่ยวข้อง หมายเหตุ…",
  "insp.createButton": "สร้างใบตรวจ",
  "insp.filterPerson": "ผู้เกี่ยวข้อง",
  "insp.mine": "ของฉัน",
  "insp.dateRangeActive": "ช่วงวันที่ {from} – {to}",
  "insp.colCode": "เลขที่",
  "insp.colResult": "ผลตรวจ",
  "insp.colFail": "ไม่ผ่าน",
  "insp.colPeople": "ผู้เกี่ยวข้อง",
  "insp.colClosedBy": "ตรวจรับโดย",
  "insp.colFiles": "ไฟล์",
  "insp.columns": "คอลัมน์",
  "insp.nFail": "ไม่ผ่าน {n} ข้อ",
  "insp.ticketsOpened": "เปิดตั๋วซ่อมแล้ว",
  "insp.noTicketYet": "ยังไม่เปิดตั๋ว",
  "insp.openNewTab": "เปิดในแท็บใหม่",
  "insp.copyLink": "คัดลอกลิงก์",
  "insp.linkCopied": "คัดลอกลิงก์แล้ว",
  "insp.linkCopyFailed": "คัดลอกลิงก์ไม่สำเร็จ",
  "insp.createFromThis": "สร้างใบใหม่จากใบนี้",
  "insp.loadFailed": "โหลดรายการใบตรวจไม่สำเร็จ",
  "insp.emptyNone": "ยังไม่มีใบตรวจ",
  "insp.emptyNoneHint": "สร้างใบตรวจแรก แล้วเลือกชุดตรวจที่จะใช้ — ใบหนึ่งแนบได้หลายชุด",
  "insp.emptyFiltered": "ไม่พบใบตรวจที่ตรงกับตัวกรอง",
  "insp.emptyFilteredHint": "ลองลดตัวกรองหรือขยายช่วงวันที่",
  "insp.inspectedAt": "วันเวลาที่ตรวจ",
  "insp.submittedAt": "ส่งผลเมื่อ",
  "insp.closedAt": "ตรวจรับเมื่อ",
  "insp.purpose": "วัตถุประสงค์",
  "insp.sheets": "ชุดตรวจ",
  "insp.createTitle": "สร้างใบตรวจ",
  "insp.createAndOpen": "สร้างแล้วไปกรอกผล",
  "insp.created": "สร้าง {code} แล้ว",
  "insp.createFailed": "สร้างใบตรวจไม่สำเร็จ",
  "insp.lastInspection": "ตรวจล่าสุด {date} · {code}",
  "insp.openDuplicate": "รถคันนี้มี {code} วัตถุประสงค์เดียวกันที่ยังไม่ส่งผลอยู่",
  "insp.openThatOne": "เปิดใบนั้น",
  "insp.suggestedForType": "แนะนำสำหรับประเภทรถนี้",
  "insp.pickVehicleForSuggestions": "เลือกรถเพื่อดูชุดที่แนะนำ",
  "insp.showOtherTemplates": "แสดงชุดตรวจอื่นอีก {n} ชุด",
  "insp.showSuggestedOnly": "แสดงเฉพาะที่แนะนำ",
  "insp.nItems": "{n} ข้อ",
  "insp.minutesShort": "นาที",
  "insp.noActiveTemplates": "ยังไม่มีชุดตรวจที่เปิดใช้งาน",
  "insp.inspector": "ผู้ตรวจ",
  "insp.inspectorDefaultYou": "ค่าตั้งต้น = คุณ",
  "insp.createHint": "คุณจะเป็นเจ้าของใบนี้ · เพิ่มคน แนบรูป และกรอกผลได้ในหน้าถัดไป",
  "insp.notFound": "ไม่พบใบตรวจนี้ หรือคุณไม่ได้อยู่ในใบนี้",
  "insp.backToList": "กลับไปรายการ",
  "insp.checked": "ตรวจแล้ว",
  "insp.itemsTotal": "จำนวนข้อทั้งหมด",
  "insp.files": "ไฟล์แนบ",
  "insp.ticketsShort": "ตั๋วซ่อม",
  "insp.activity": "ความเคลื่อนไหว",
  "insp.print": "พิมพ์",
  "insp.saving": "กำลังบันทึก…",
  "insp.savedAt": "บันทึกแล้ว {time}",
  "insp.saveRetry": "บันทึกไม่สำเร็จ — ลองใหม่",
  "insp.saveFailed": "บันทึกไม่สำเร็จ",
  "insp.actionFailed": "ทำรายการไม่สำเร็จ",
  "insp.submit": "ส่งผลตรวจ",
  "insp.submitBlocked": "ส่งผลตรวจ (เหลือ {n} ข้อ)",
  "insp.jumpToBlock": "ไปที่ข้อแรกที่ยังต้องแก้",
  "insp.return": "ตีกลับ",
  "insp.close": "ตรวจรับ & ปิด",
  "insp.reopen": "เปิดใหม่",
  "insp.cancel": "ยกเลิกใบตรวจ",
  "insp.deleteDraft": "ลบใบร่าง",
  "insp.draftDeleted": "ลบใบร่างแล้ว",
  "insp.dialog_submit": "ส่งผลตรวจ",
  "insp.dialog_return": "ตีกลับให้แก้ไข",
  "insp.dialog_close": "ตรวจรับและปิดใบ",
  "insp.dialog_reopen": "เปิดใบตรวจใหม่",
  "insp.dialog_cancel": "ยกเลิกใบตรวจ",
  "insp.confirm_submit": "ส่งผลตรวจ",
  "insp.confirm_return": "ตีกลับ",
  "insp.confirm_close": "ตรวจรับ & ปิด",
  "insp.confirm_reopen": "เปิดใหม่",
  "insp.confirm_cancel": "ยกเลิกใบ",
  "insp.done_submit": "ส่งผลตรวจแล้ว",
  "insp.done_return": "ตีกลับแล้ว",
  "insp.done_close": "ตรวจรับและปิดแล้ว",
  "insp.done_reopen": "เปิดใบใหม่แล้ว",
  "insp.done_cancel": "ยกเลิกใบแล้ว",
  "insp.submitUncheckedNote": "ยังมี {n} ข้อที่ไม่บังคับและยังไม่ได้ตรวจ — ส่งได้",
  "insp.failOpenWarning": "มี {n} ข้อไม่ผ่านที่ยังไม่ได้เปิดตั๋วซ่อม",
  "insp.submitHint": "หลังส่งผล คำตอบจะล็อก และผู้ตรวจรับจะได้รับแจ้ง",
  "insp.reason": "เหตุผล",
  "insp.reviewNote": "หมายเหตุผู้ตรวจรับ",
  "insp.cancelledBecause": "ยกเลิกเพราะ: {reason}",
  "insp.returnedBecause": "ถูกตีกลับ: {reason}",
  "insp.addSheet": "เพิ่มชุดตรวจ",
  "insp.addNSheets": "เพิ่ม {n} ชุด",
  "insp.alreadyAttached": "อยู่ในใบแล้ว",
  "insp.searchTemplates": "ค้นหาชุดตรวจ…",
  "insp.sheetsAdded": "เพิ่ม {n} ชุดตรวจแล้ว",
  "insp.sheetRemoved": "ถอดชุดตรวจแล้ว",
  "insp.removePerson": "ถอดออกจากใบตรวจ",
  "insp.sheetHandedOff": "ส่งชุดแล้ว",
  "insp.removeSheet": "ถอดชุดตรวจ",
  "insp.removeSheetQ": "ถอด \"{name}\" ออกจากใบ?",
  "insp.removeSheetConfirm": "คำตอบ {n} ข้อและรูปในชุดนี้จะถูกทิ้ง และกู้คืนไม่ได้",
  "insp.assignSheet": "ผู้รับผิดชอบชุดนี้",
  "insp.anyoneMayAnswer": "ผู้ตรวจทุกคนตอบได้",
  "insp.sheetAssignee": "ผู้รับผิดชอบ: {name}",
  "insp.noSheetsYet": "ยังไม่มีชุดตรวจในใบนี้",
  "insp.noSheetsHint": "กด \"เพิ่มชุดตรวจ\" แล้วเลือกได้หลายชุด",
  "insp.noSheetsReadOnly": "ผู้มีสิทธิ์แก้ไขยังไม่ได้เพิ่มชุดตรวจ",
  "insp.filterUnchecked": "ยังไม่ตรวจ",
  "insp.filterFail": "ไม่ผ่าน",
  "insp.passRemaining": "ผ่านข้อที่เหลือทั้งหมด",
  "insp.passGroup": "ผ่านทั้งกลุ่ม",
  "insp.passRemainingConfirm": "จะบันทึก \"ผ่าน\" ให้ {n} ข้อแบบผ่าน/ไม่ผ่านที่ยังไม่ได้ตอบ (ข้อที่ตอบแล้ว ข้อตัวเลข และรูปไม่ถูกแตะ)",
  "insp.passNItems": "ผ่าน {n} ข้อ",
  "insp.newerVersion": "ชุดตรวจนี้มีเวอร์ชันใหม่ (v{from} → v{to}) และในใบนี้ยังไม่มีคำตอบ",
  "insp.upgradeNow": "อัปเดตเป็นเวอร์ชันล่าสุด",
  "insp.upgraded": "อัปเดตชุดตรวจเป็นเวอร์ชันล่าสุดแล้ว",
  "insp.shortcuts": "คีย์ลัด",
  "insp.shortcutsHelp": "J / K ข้อถัดไป/ก่อนหน้า · P ผ่าน · F ไม่ผ่าน · N ไม่เกี่ยว · C หมายเหตุ",
  "insp.noFailures": "ไม่มีข้อไม่ผ่านในชุดนี้",
  "insp.allChecked": "ตรวจครบทุกข้อแล้ว",
  "insp.howToCheck": "วิธีตรวจ",
  "insp.passRange": "ผ่านเมื่ออยู่ระหว่าง {min} – {max} {unit}",
  "insp.flipVerdict": "กดเพื่อกลับผล (เช่น ตัดสินใจให้ผ่านแม้ค่าออกนอกช่วง)",
  "insp.photosOfMin": "รูป {n}/{min}",
  "insp.addPhoto": "เพิ่มรูป",
  "insp.viewPhoto": "ดูรูป",
  "insp.removePhoto": "ลบรูป",
  "insp.maxPhotos": "แนบได้ไม่เกิน {n} รูปต่อข้อ",
  "insp.uploadFailed": "อัปโหลดไม่สำเร็จ",
  "insp.failNotePlaceholder": "อธิบายว่าไม่ผ่านเพราะอะไร…",
  "insp.addNoteOrPhoto": "เพิ่มหมายเหตุหรือรูป",
  "insp.openTicket": "เปิดตั๋วซ่อม",
  "insp.ticketOpened": "เปิดตั๋วซ่อมแล้ว",
  "insp.ticketOpening": "กำลังเปิดตั๋ว…",
  "insp.answeredBy": "ตอบโดย {name}",
  "insp.photoViewer": "ดูรูป",
  "insp.openTicketN": "เปิดตั๋ว ({n} ข้อ)",
  "insp.ticketHint": "เลือกข้อไม่ผ่านที่จะรวมไว้ในตั๋วซ่อมใบเดียวกัน รูปและหมายเหตุจะติดไปด้วย",
  "insp.ticketTitle": "หัวข้อตั๋ว",
  "insp.ticketTitleAuto": "เว้นว่างเพื่อตั้งชื่อจากข้อที่เลือก",
  "insp.ticketCreated": "เปิดตั๋ว {code} แล้ว",
  "insp.ticketFailed": "เปิดตั๋วซ่อมไม่สำเร็จ",
  "insp.generalInfo": "ข้อมูลทั่วไป",
  "insp.editInfo": "แก้ข้อมูลใบตรวจ",
  "insp.location": "สถานที่",
  "insp.createdBy": "สร้างโดย",
  "insp.titleOptional": "หัวข้อ (ไม่บังคับ)",
  "insp.people": "ผู้เกี่ยวข้อง",
  "insp.noReviewerHint": "ยังไม่มีผู้ตรวจรับ — แอดมินตรวจรับแทนได้ หรือเพิ่มคนในบทบาท \"ผู้ตรวจรับ\"",
  "insp.review": "การตรวจรับ",
  "insp.closedByOn": "ตรวจรับโดย {name} เมื่อ {date}",
  "insp.awaitingReviewBy": "ส่งผลโดย {by} เมื่อ {date} · รอ {name} ตรวจรับ",
  "insp.reviewPending": "เมื่อส่งผลแล้ว {name} จะได้รับแจ้งให้ตรวจรับ",
  "insp.anyAdmin": "แอดมิน",
  "insp.vehicleHistory": "ประวัติการตรวจรถคันนี้",
  "insp.noOtherInspections": "ยังไม่มีใบตรวจอื่นของรถคันนี้",
  "insp.seeAll": "ดูทั้งหมด",
  "insp.scopeInspection": "ระดับใบ",
  "insp.scopeItems": "รายข้อ",
  "insp.attachFiles": "แนบไฟล์",
  "insp.dropHint": "ลากไฟล์มาวางในกรอบนี้ได้ · รูป PDF เอกสาร",
  "insp.filesAttached": "แนบ {n} ไฟล์แล้ว",
  "insp.removeFile": "ถอดไฟล์",
  "insp.fileOpenFailed": "เปิดไฟล์ไม่สำเร็จ",
  "insp.inspectionPhoto": "รูประดับใบ",
  "insp.document": "เอกสาร",
  "insp.noFiles": "ยังไม่มีไฟล์แนบ",
  "insp.noActivity": "ยังไม่มีการบันทึกในใบนี้",
  "insp.actCreate": "สร้างใบตรวจ",
  "insp.actUpdate": "แก้ข้อมูลใบ",
  "insp.actDelete": "ลบใบร่าง",
  "insp.actSheetAdd": "เพิ่มชุดตรวจ",
  "insp.actSheetRemove": "ถอดชุดตรวจ",
  "insp.actSheetUpgrade": "อัปเดตเวอร์ชันชุดตรวจ",
  "insp.actSheetUpdate": "แก้ผู้รับผิดชอบ/ลำดับชุด",
  "insp.actAnswer": "เปลี่ยนผลตรวจ",
  "insp.actParticipantAdd": "เพิ่มผู้เกี่ยวข้อง",
  "insp.actParticipantRemove": "ถอดผู้เกี่ยวข้อง",
  "insp.actAttachmentAdd": "แนบไฟล์",
  "insp.actAttachmentRemove": "ถอดไฟล์",
  "insp.actStatus": "เปลี่ยนสถานะ",
  "insp.actWorkOrder": "เปิดตั๋วซ่อม",
  "insp.nAnswersDropped": "ทิ้ง {n} คำตอบ",
  "insp.nFiles": "{n} ไฟล์",
  "insp.printTitle": "ใบตรวจสภาพรถ",
  "insp.item": "รายการ",
  "insp.value": "ค่า",
  "insp.failPhotos": "รูปข้อที่ไม่ผ่าน",
  "insp.signInspector": "ผู้ตรวจ",
  "insp.signReviewer": "ผู้ตรวจรับ",
  "insp.tplActive": "ใช้งาน",
  "insp.tplInactive": "ปิดใช้",
  "insp.tplOff": "ปิดใช้",
  "insp.category": "หมวด",
  "insp.allCategories": "ทุกหมวด",
  "insp.sortBy": "เรียงตาม",
  "insp.sortUsage": "ใช้บ่อย",
  "insp.sortName": "ชื่อ",
  "insp.sortUpdated": "แก้ล่าสุด",
  "insp.sortLastUsed": "ใช้ล่าสุด",
  "insp.nGroupsItems": "{g} กลุ่ม · {n} ข้อ",
  "insp.usedN": "ใช้ {n} ครั้ง",
  "insp.lastUsedOn": "ใช้ล่าสุด {date}",
  "insp.pickTemplate": "เลือกชุดตรวจจากรายการด้านซ้าย",
  "insp.discardChangesQ": "ทิ้งการแก้ไข?",
  "insp.discardChanges": "ชุดตรวจที่กำลังแก้ยังไม่ได้บันทึก ถ้าเปลี่ยนไปชุดอื่นการแก้ไขจะหาย",
  "insp.discard": "ทิ้งการแก้ไข",
  "insp.tplActiveLabel": "เปิดใช้งาน",
  "insp.tplInactiveLabel": "ปิดใช้งาน",
  "insp.tplTurnedOn": "เปิดใช้ชุดตรวจแล้ว",
  "insp.tplTurnedOff": "ปิดใช้ชุดตรวจแล้ว",
  "insp.tplTurnOffQ": "ปิดใช้ \"{name}\"?",
  "insp.tplTurnOffBody": "ใบตรวจใหม่จะเลือกชุดตรวจนี้ไม่ได้ · ใบเดิม {n} ใบไม่เปลี่ยน (เก็บสำเนาไว้แล้ว) · เปิดกลับได้ทุกเมื่อ",
  "insp.tplTurnOff": "ปิดใช้",
  "insp.tplDuplicated": "ทำสำเนาแล้ว (ปิดใช้ไว้ก่อน)",
  "insp.editedBy": "แก้ล่าสุด {date} โดย {name}",
  "insp.fromInspection": "เปิดจากใบตรวจ →",
  "insp.editedOn": "แก้ล่าสุด {date}",
  "insp.previewAsInspector": "ดูแบบผู้ตรวจ",
  "insp.duplicate": "ทำสำเนา",
  "insp.structure": "โครงสร้าง",
  "insp.stats90": "สถิติ 90 วัน",
  "insp.statsBasis": "จาก {n} ใบตรวจ",
  "insp.failRate": "ไม่ผ่าน {pct}% ({fail}/{n})",
  "insp.noData": "ยังไม่มีข้อมูล",
  "insp.photoOnFail": "ต้องมีรูปเมื่อไม่ผ่าน",
  "insp.previewTitle": "ตัวอย่าง: {name}",
  "insp.previewHint": "กดเล่นได้ แต่ไม่มีอะไรถูกบันทึก",
  "insp.itemType": "ชนิดคำตอบ",
  "insp.allowNa": "มีตัวเลือก \"ไม่เกี่ยว\"",
  "insp.unit": "หน่วย",
  "insp.min": "ค่าต่ำสุดที่ผ่าน",
  "insp.max": "ค่าสูงสุดที่ผ่าน",
  "insp.rangeHint": "นอกช่วง = ไม่ผ่านอัตโนมัติ · เว้นว่างได้ทั้งสองฝั่ง",
  "insp.options": "ตัวเลือก",
  "insp.optionLabel": "ชื่อตัวเลือก",
  "insp.countsAsFail": "นับเป็นไม่ผ่าน",
  "insp.addOption": "เพิ่มตัวเลือก",
  "insp.minPhotos": "จำนวนรูปขั้นต่ำ",
  "insp.instruction": "วิธีตรวจ / คำอธิบาย",
  "insp.instructionPlaceholder": "เช่น วัดร่องยางด้านในสุด ต้องไม่น้อยกว่า 1.6 มม.",
  "insp.examplePhotos": "ภาพตัวอย่าง",
  "insp.addExample": "เพิ่มภาพตัวอย่าง",
  "insp.whenFailRequire": "เมื่อไม่ผ่าน ต้องมี:",
  "insp.requireNote": "หมายเหตุ",
  "insp.requirePhoto": "รูป",
  "insp.itemSettings": "ตั้งค่าข้อนี้",
  "insp.moveUp": "เลื่อนขึ้น",
  "insp.moveDown": "เลื่อนลง",
  "insp.tplNamePlaceholder": "เช่น ภายนอกรอบคัน",
  "insp.tplCode": "รหัสสั้น",
  "insp.tplCodePlaceholder": "เช่น TPL-EXT",
  "insp.vehicleTypes": "ประเภทรถที่ใช้",
  "insp.allVehicleTypes": "ทุกประเภท",
  "insp.nTypes": "{n} ประเภท",
  "insp.estimatedMinutes": "เวลาโดยประมาณ (นาที)",
  "insp.errName": "ต้องมีชื่อชุดตรวจ",
  "insp.errGroupName": "ทุกกลุ่มต้องมีชื่อ (หรือลบกลุ่มที่ว่างทิ้ง)",
  "insp.errChoice": "ข้อ \"{label}\" ต้องมีตัวเลือกอย่างน้อย 2 ตัว",
  "insp.errRange": "ข้อ \"{label}\" ค่าต่ำสุดมากกว่าค่าสูงสุด",
  "insp.nChanges": "แก้ไข {n} จุด ยังไม่บันทึก",
  "insp.noChanges": "ยังไม่มีการแก้ไข",
  "insp.saveAsVersionHint": "บันทึกเป็น v{v} · ใบที่กรอกแล้วคงเวอร์ชันเดิม",
  "insp.tplSaved": "บันทึกชุดตรวจแล้ว (v{v})",
  "insp.tplCreated": "สร้างชุดตรวจแล้ว",
  // plan/fuel-cards (2026-10-15) — the company's fuel cards, held by people
  "nav.fuelCards": "บัตรน้ำมัน",
  "hr.fuelCardsTab": "บัตรน้ำมัน",
  "fuelCard.title": "บัตรน้ำมัน",
  "fuelCard.subtitle": "บัตรของบริษัท {n} ใบ · มีคนถือ {held} ใบ",
  "fuelCard.add": "เพิ่มบัตร",
  "fuelCard.search": "ค้นหาค่ายบัตร เลขบัตร หรือชื่อคนถือ",
  "fuelCard.filter.all": "ทั้งหมด",
  "fuelCard.filter.held": "มีคนถือ",
  "fuelCard.filter.free": "ว่าง",
  "fuelCard.filter.over": "เกินวงเงิน",
  "fuelCard.filter.closed": "ปิดใช้งาน",
  "fuelCard.col.card": "บัตร",
  "fuelCard.col.holder": "คนถือ",
  "fuelCard.col.limit": "วงเงิน",
  "fuelCard.col.added": "เติมเพิ่ม",
  "fuelCard.col.used": "ใช้ไป",
  "fuelCard.col.left": "คงเหลือ",
  "fuelCard.col.since": "ถือตั้งแต่",
  "fuelCard.free": "ว่าง ยังไม่มีคนถือ",
  "fuelCard.since": "ถือตั้งแต่ {date}",
  "fuelCard.over": "เกินวงเงิน",
  "fuelCard.closedTag": "ปิดใช้งาน",
  "fuelCard.emptyTitle": "ยังไม่มีบัตรน้ำมัน",
  "fuelCard.emptyBody": "เพิ่มบัตรของบริษัทไว้ที่นี่ก่อน แล้วจึงให้คนถือได้",
  "fuelCard.action.give": "ให้ถือ",
  "fuelCard.action.swap": "สลับคนถือ",
  "fuelCard.action.withdraw": "ถอนบัตร",
  "fuelCard.action.topup": "เติมวงเงิน",
  "fuelCard.action.edit": "แก้ไข",
  "fuelCard.action.close": "ปิดใช้งาน",
  "fuelCard.action.reopen": "เปิดใช้อีกครั้ง",
  "fuelCard.addTitle": "เพิ่มบัตรน้ำมัน",
  "fuelCard.editTitle": "แก้ไขบัตร",
  "fuelCard.f.provider": "ค่ายบัตร",
  "fuelCard.f.providerHint": "เช่น PTT, Shell, บางจาก",
  "fuelCard.f.number": "เลขบัตร",
  "fuelCard.f.note": "หมายเหตุ",
  "fuelCard.f.person": "คนถือบัตร",
  "fuelCard.f.personPick": "ค้นชื่อหรือรหัสพนักงาน",
  "fuelCard.f.limit": "วงเงิน",
  "fuelCard.f.limitHint": "ยอดเติมที่จ่ายด้วยบัตรนี้จะหักจากวงเงินไปเรื่อย ๆ ใช้เกินจะขึ้นสีแดง",
  "fuelCard.f.amount": "จำนวนเงินที่เติม",
  "fuelCard.f.card": "บัตร",
  "fuelCard.giveTitle": "ให้ถือบัตร {card}",
  "fuelCard.swapTitle": "สลับคนถือบัตร {card}",
  "fuelCard.swapFrom": "ตอนนี้ {name} ถืออยู่ คนใหม่จะเริ่มนับยอดใช้จาก 0",
  "fuelCard.topupTitle": "เติมวงเงินบัตร {card}",
  "fuelCard.topupNow": "ตอนนี้คงเหลือ {left} บาท",
  "fuelCard.withdrawQ": "ถอนบัตร {card} คืนจาก {name}?",
  "fuelCard.withdrawBody": "บัตรจะกลับมาว่าง ยอดที่ใช้ไปแล้วยังอยู่ในประวัติ",
  "fuelCard.closeQ": "ปิดใช้งานบัตร {card}?",
  "fuelCard.closeBody": "บัตรจะไม่อยู่ในรายการให้เลือกอีก รายการเติมเก่าที่จ่ายด้วยบัตรนี้ยังเปิดดูได้",
  "fuelCard.saved": "บันทึกแล้ว",
  "fuelCard.lead": "บัตรของบริษัท เพิ่มที่นี่ก่อนแล้วจึงให้คนถือ ใช้เกินวงเงินขึ้นสีแดง",
  "fuelCard.noMatch": "ไม่พบบัตรที่ตรงกับการค้นหาหรือตัวกรอง",
  "fuelCard.f.holderType": "ประเภทคนถือ",
  "fuelCard.f.leftMax": "คงเหลือไม่เกิน",
  "fuelCard.f.leftMaxHint": "เฉพาะบัตรที่มีคนถือ ใส่ 0 = บัตรที่หมดหรือเกินวงเงิน",
  "fuelCard.f.leftMaxChip": "คงเหลือไม่เกิน {amount} บาท",
  "fuelCard.action.history": "ประวัติคนถือ",
  "fuelCard.sort.provider": "ค่ายบัตร",
  "fuelCard.sort.card_number": "เลขบัตร",
  "fuelCard.sort.holder": "คนถือ",
  "fuelCard.sort.since": "วันที่เริ่มถือ",
  "fuelCard.sort.limit": "วงเงิน",
  "fuelCard.sort.added": "เติมเพิ่ม",
  "fuelCard.sort.used": "ใช้ไป",
  "fuelCard.sort.left": "คงเหลือ",
  "fuelCard.history.title": "ประวัติคนถือบัตร {card}",
  "fuelCard.history.none": "บัตรใบนี้ยังไม่เคยมีคนถือ",
  "fuelCard.history.now": "ถืออยู่ตอนนี้",
  "fuelCard.history.range": "{from} – {to}",
  "fuelCard.history.since": "ตั้งแต่ {from}",
  "fuelCard.history.endedSwap": "สลับให้คนอื่น",
  "fuelCard.history.endedWithdraw": "ถอนคืน",
  "fuelCard.history.money": "วงเงิน {limit} · เติมเพิ่ม {added} · ใช้ไป {used} · คงเหลือ {left} บาท",
  "fuelCard.action.limit": "แก้วงเงิน",
  "fuelCard.action.delete": "ลบ",
  "fuelCard.limitTitle": "แก้วงเงินบัตร {card}",
  "fuelCard.limitNow": "ใช้ไปแล้ว {used} บาท · เติมเพิ่มแล้ว {added} บาท ยอดเหล่านี้ไม่เปลี่ยน",
  "fuelCard.f.limitEdit": "วงเงินตั้งต้น",
  "fuelCard.deleteQ": "ลบบัตร {card}?",
  "fuelCard.deleteBody": "บัตรนี้ยังไม่เคยมีคนถือ ลบแล้วจะหายจากรายการ ใช้กับบัตรที่เพิ่มผิด",
  "fuelCard.deleted": "ลบบัตรแล้ว",
  "fuelCard.baht": "{amount} บาท",
  "fuelCard.person.title": "บัตรน้ำมันที่ถืออยู่",
  "fuelCard.person.none": "ยังไม่ได้ถือบัตรน้ำมัน",
  "fuelCard.person.noFree": "ไม่มีบัตรว่าง เพิ่มบัตรในเมนูบัตรน้ำมันก่อน",
  "fuelCard.person.addTitle": "เพิ่มบัตรให้ {name}",
  "fuelCard.person.topups": "เติมวงเงินแล้ว {n} ครั้ง",
  "fuelCard.person.topupLine": "{date} · +{amount} บาท",
  "fuelCard.person.goMenu": "ไปเมนูบัตรน้ำมัน",
  "fuelCard.overChip": "บัตรน้ำมันเกินวงเงิน",
  "fuelCard.legacy": "บัตรระบบเดิม",
  "fuelCard.pickCard": "เลือกบัตร",
  "fuelCard.pickPersonFirst": "เลือกคนเติมก่อน แล้วเลือกบัตรที่เขาถืออยู่",
  "fuelCard.noneHeld": "คนนี้ไม่ได้ถือบัตรน้ำมัน",
  // plan/holiday-pay — the holiday list and the multiplied trip pay
  "nav.financeHolidays": "วันหยุด",
  "holiday.title": "วันหยุด",
  "holiday.lead": "ใบงานที่เริ่มในช่วงเวลาเหล่านี้ ค่าเที่ยวจะถูกคูณ ค่าแรงที่ตั้งไว้บนใบงานไม่เปลี่ยน",
  "holiday.add": "เพิ่มวันหยุด",
  "holiday.addTitle": "เพิ่มวันหยุด",
  "holiday.editTitle": "แก้วันหยุด",
  "holiday.saved": "บันทึกแล้ว ยอดของใบงานที่ตกช่วงนี้ปรับตามแล้ว",
  "holiday.deleted": "ลบแล้ว ใบงานที่เคยตกช่วงนี้กลับเป็นค่าเที่ยวปกติ",
  "holiday.search": "ค้นหาชื่อ หมายเหตุ",
  "holiday.view.all": "ทั้งหมด",
  "holiday.view.upcoming": "ที่จะถึง",
  "holiday.view.past": "ผ่านไปแล้ว",
  "holiday.col.date": "วันที่",
  "holiday.col.name": "ชื่อ",
  "holiday.col.window": "ช่วงเวลา",
  "holiday.col.multiplier": "ตัวคูณ",
  "holiday.col.tickets": "ใบงานที่ตก",
  "holiday.sort.date": "วันที่",
  "holiday.sort.name": "ชื่อ",
  "holiday.sort.start": "เวลาเริ่ม",
  "holiday.sort.multiplier": "ตัวคูณ",
  "holiday.sort.tickets": "ใบงานที่ตก",
  "holiday.wholeDay": "ทั้งวัน",
  "holiday.windowNext": "{start}–{end} วันถัดไป",
  "holiday.ticketsN": "{n} ใบ",
  "holiday.action.edit": "แก้",
  "holiday.action.delete": "ลบ",
  "holiday.noMatch": "ไม่มีวันหยุดที่ตรงกับที่ค้นหา",
  "holiday.emptyTitle": "ยังไม่มีวันหยุด",
  "holiday.emptyBody": "เพิ่มวันหยุดพร้อมช่วงเวลาและตัวคูณ ระบบไม่เดาวันหยุดเอง",
  "holiday.f.date": "วันที่",
  "holiday.f.name": "ชื่อ",
  "holiday.f.nameHint": "เช่น สงกรานต์ กะดึกพิเศษ",
  "holiday.f.window": "ช่วงเวลา",
  "holiday.f.wholeDay": "ทั้งวัน (00:00–24:00)",
  "holiday.f.wholeDayHint": "ใบงานที่เริ่มวันนี้ทั้งวัน",
  "holiday.f.windowHint": "ใบงานที่เริ่มตั้งแต่เวลาเริ่ม จนก่อนเวลาจบ",
  "holiday.f.nextDay": "จบ {end} ของวันถัดไป",
  "holiday.f.start": "เวลาเริ่ม",
  "holiday.f.end": "เวลาจบ",
  "holiday.f.multiplier": "ตัวคูณค่าเที่ยว",
  "holiday.f.multiplierHint": "เช่น 2.5 = ค่าเที่ยว 500 เป็น 1,250 · ไม่คูณเงินเบิก/เงินหักบนใบงาน",
  "holiday.f.multiplierBad": "มากกว่า 1 ไม่เกิน 10 ทศนิยม 2 ตำแหน่ง",
  "holiday.f.note": "หมายเหตุ",
  "holiday.f.range": "ช่วงวันที่",
  "holiday.f.from": "ตั้งแต่วันที่",
  "holiday.f.to": "ถึงวันที่",
  "holiday.f.fromChip": "ตั้งแต่ {d}",
  "holiday.f.toChip": "ถึง {d}",
  "holiday.settledWarn": "มี {n} ใบงานในช่วงนี้ที่เข้ารอบจ่ายหรือจ่ายไปแล้ว แก้แล้วส่วนต่างจะไปอยู่ในบิลถัดไป",
  "holiday.deleteQ": "ลบวันหยุด \"{name}\"?",
  "holiday.deleteBody": "ยังไม่มีใบงานตกช่วงนี้",
  "holiday.deleteBodyTickets": "{n} ใบงานที่ตกช่วงนี้จะกลับเป็นค่าเที่ยวปกติ",
  "holiday.deleteBodySettled": "มี {n} ใบงานที่เข้ารอบจ่ายหรือจ่ายไปแล้ว ส่วนที่คูณไปจะถูกหักคืนในบิลถัดไป",
  "holiday.ticketsLead": "ค่าเที่ยว {mult}",
  "holiday.noTickets": "ยังไม่มีใบงานที่ปิดงานแล้วตกช่วงนี้",
  "holiday.tag": "{base} {mult} · {name}",
  "holiday.tagShort": "{mult} · {name}",
  "holiday.onTicket": "ตกวันหยุด \"{name}\" ค่าเที่ยว {base} {mult} = {total}",
  "holiday.onTicketNoRate": "ตกวันหยุด \"{name}\" ค่าเที่ยวจะถูกคูณ {mult}",
  "audit.fin.finance_holiday_create": "เพิ่มวันหยุด",
  "audit.fin.finance_holiday_update": "แก้วันหยุด",
  "audit.fin.finance_holiday_delete": "ลบวันหยุด",
  "holiday.sumTitle": "ผลของวันหยุด \"{name}\"",
  "holiday.sum.tickets": "ใบงานที่ถูกคูณ",
  "holiday.sum.people": "คน",
  "holiday.sum.base": "ค่าเที่ยวเดิม",
  "holiday.sum.extra": "เพิ่มขึ้นจากวันหยุด",
  "holiday.sum.after": "หลังคูณ {after}",
  "holiday.sum.states": "ยังไม่เข้ารอบ {open} · อยู่ในรอบจ่าย {inRound} · จ่ายแล้ว {paid}",
  "holiday.sum.byPerson": "รายคน (เงินมากสุดก่อน)",
  "holiday.sum.page": "หน้า {page}/{pages} · {total} คน",
  "holiday.sum.all": "ดูทั้ง {n} รายการในรายการเงิน",
  "financeTxn.f.holiday": "วันหยุด",
  "financeTxn.f.noPerson": "ไม่ระบุพนักงาน",
  "nav.group.requests": "คำร้อง",
  // G5 — the request list as a standard list screen
  "requests.leadShort": "เรื่องที่พนักงานยื่นจากแอป เรื่องอะไรก็ได้ รับเรื่อง ดำเนินการ แล้วปิดหรือไม่อนุมัติพร้อมเหตุผล ถ้าเกี่ยวกับเงิน สร้างรายการเงินจากหน้าคำร้อง",
  "requests.col.no": "เลขที่",
  "requests.col.title": "หัวข้อ",
  "requests.col.employee": "ผู้ยื่น",
  "requests.col.status": "สถานะ",
  "requests.col.owner": "ผู้รับเรื่อง",
  "requests.col.filed": "ยื่นเมื่อ",
  "requests.col.updated": "อัปเดตล่าสุด",
  "requests.col.money": "เงินที่ผูก",
  "requests.sort.display_id": "เลขที่",
  "requests.sort.title": "หัวข้อ",
  "requests.sort.employee": "ผู้ยื่น",
  "requests.sort.status": "สถานะ",
  "requests.sort.owner": "ผู้รับเรื่อง",
  "requests.sort.created": "ยื่นเมื่อ",
  "requests.sort.updated": "อัปเดตล่าสุด",
  "requests.sort.money": "เงินที่ผูก",
  "requests.f.employeePick": "เลือกคนยื่น",
  "requests.f.anyone": "ทุกคน",
  "requests.f.from": "ยื่นตั้งแต่วันที่",
  "requests.f.to": "ถึงวันที่",
  "requests.f.filed": "ยื่นเมื่อ",
  "requests.f.fromChip": "ตั้งแต่ {d}",
  "requests.f.toChip": "ถึง {d}",
  "requests.f.moneyYes": "มีเงินผูก",
  "requests.f.moneyNo": "ไม่มีเงินผูก",
  "requests.moneyRows": "{n} รายการเงิน",
  // 2026-10-19 — pick the bill when adding a row
  "billPick.title": "เข้าบิล",
  "billPick.box": "รายการสะสม",
  "billPick.mustPick": "คนนี้มีบิลที่เปิดอยู่ — เลือกว่าจะให้เข้าบิลไหน หรือเลือก \"รายการสะสม\" (ต้องมีคนหยิบเข้ารอบเอง)",
  "addMoney.need.bill": "บิลที่จะให้เข้า",
  "billPick.monthly": "บิลเงินเดือน",
  "billPick.placeFailed": "บันทึกแล้ว แต่ใส่เข้าบิลที่เลือกไม่ได้ — อยู่ในบิลอัตโนมัติ",
  "billPick.autoTag": "อัตโนมัติ",
  "billPick.round": "รอบ {from}–{to}",
  "billPick.pay": "จ่าย {day}",
  "billPick.thisRound": "รอบนี้",
  "billPick.nextRound": "รอบถัดไป",
  "billPick.notYet": "ยังไม่เข้าบิล",
  "billPick.autoLater": "ยังไม่เข้าบิลจนถึงวันที่ของรายการ",
  "billPick.loading": "กำลังโหลดบิลของคนนี้…",
  "billPick.emptyShort": "ยังไม่มีรายการ",
  "billPick.empty": "ยังไม่มีรายการ เริ่มด้วยรายการนี้",
  "billPick.inBill": " · เข้า{bill}",
  // plan/review-tools — checking bills
  "billCheck.open": "ตรวจบิล",
  "billCheck.openCount": "ตรวจบิล ({n})",
  "billCheck.openCountHint": "{n} บิลในรอบที่ครบแล้วยังไม่ได้ตรวจ (คนหนึ่งมีได้หลายบิล — สัญญาละบิล)",
  "billCheck.title": "ตรวจบิล",
  "billCheck.lead": "เลือกสัญญาและรอบ แล้วไล่ตรวจทีละคน กด \"ตรวจแล้ว\" ระบบจะจำบิลตามที่เห็น ถ้าทีหลังมีอะไรเปลี่ยน บิลจะขึ้นว่าต้องดูใหม่ พร้อมบอกว่าเปลี่ยนอะไร",
  "billCheck.back": "กลับไปหน้าบิล",
  "billCheck.pickContract": "เลือกสัญญา",
  "billCheck.scope.monthly": "เงินเดือน (บิลรายเดือนของทุกคน)",
  "billCheck.scope.box": "รายการสะสม (ไม่มีสัญญา)",
  "billCheck.pickFirst": "เลือกสัญญาก่อน แล้วรายชื่อคนในรอบนั้นจะขึ้นมา",
  "billCheck.pickPerson": "เลือกคนจากรายชื่อเพื่อดูบิล",
  "billCheck.window": "รอบ {from}–{to} · จ่าย {pay}",
  "billCheck.progress": "ตรวจแล้ว {done}/{all} คน · {pct}% ของยอด",
  "billCheck.progressMoney": "ยอดก่อนหัก",
  "billCheck.progressMoneyHint": "ยอดรวมของรายการในบิล ก่อนหักรายการที่ระบบคิด เช่น หัก ณ ที่จ่าย — ยอดในรายชื่อคือหลังหักแล้ว",
  "billCheck.filter": "แสดงเฉพาะ",
  "billCheck.filter.all": "ทั้งหมด",
  "billCheck.filter.unchecked": "ยังไม่ตรวจ",
  "billCheck.filter.stale": "ต้องดูใหม่",
  "billCheck.filter.flagged": "มีปัญหา",
  "billCheck.filter.checked": "ตรวจแล้ว",
  "billCheck.filter.unacked": "ผ่านแต่มีจุดค้าง",
  "billCheck.next": "ไปคนถัดไปที่ยังไม่ตรวจ",
  "billCheck.none": "ไม่มีคนในกลุ่มนี้",
  "billCheck.allDone": "ไม่เหลือคนที่ต้องตรวจในรอบนี้ · ตรวจแล้ว {checked} คน · มีปัญหารอปิดเรื่อง {flagged} คน",
  "billCheck.rows": "{n} รายการ",
  "billCheck.checkedBy": "ตรวจโดย {name} {day}",
  "billCheck.state.unchecked": "ยังไม่ตรวจ",
  "billCheck.state.checked": "ตรวจแล้ว",
  "billCheck.state.flagged": "มีปัญหา",
  "billCheck.state.stale": "ต้องดูใหม่",
  "billCheck.state.unacked": "ผ่าน · มีจุดค้าง",
  "billCheck.box": "รายการสะสม",
  "billCheck.staleLead": "{name} ตรวจไว้เมื่อ {day} หลังจากนั้น:",
  "billCheck.why.changed": "มีรายการถูกแก้",
  "billCheck.why.new": "มีรายการเข้ามาใหม่",
  "billCheck.why.left": "มีรายการหลุดออกไป",
  "billCheck.why.remade": "ระบบคิดรายการใหม่",
  "billCheck.sealedBy": "ตรวจแล้วโดย {name} เมื่อ {day}",
  "billCheck.row.changed": "แก้หลังตรวจ (ตอนตรวจ {was})",
  "billCheck.row.new": "เข้ามาหลังตรวจ",
  "billCheck.row.remade": "ระบบคิดใหม่ (ตอนตรวจ {was})",
  "billCheck.flag": "มีปัญหา",
  "billCheck.flagPh": "ปัญหาคืออะไร",
  "billCheck.flagSave": "บันทึก",
  "billCheck.flagClose": "ปิดเรื่อง",
  "billCheck.flagFirst": "ปิดเรื่องที่มีปัญหาก่อน จึงกดตรวจแล้วได้",
  "billCheck.noRows": "บิลนี้ไม่มีรายการ",
  "billCheck.leftTitle": "หลุดออกจากบิลหลังตรวจ {n} รายการ",
  "billCheck.leftTo": "ไปอยู่บิล {bill}",
  "billCheck.leftKind.voided": "ถูกยกเลิก",
  "billCheck.leftKind.hold": "ถูกพักไว้",
  "billCheck.leftKind.settled": "จบนอกระบบ",
  "billCheck.leftKind.unknown": "ไม่อยู่ในบิลไหน",
  "billCheck.movedBy": "ย้ายโดย {name}",
  "billCheck.movedItself": "ระบบย้ายเอง",
  "billCheck.computedTitle": "หักโดยระบบ (ระบบคิดให้ ไม่ต้องติ๊ก)",
  "billCheck.computedWas": "ตอนตรวจ {was}",
  "billCheck.seal": "ตรวจแล้ว",
  "check.chip.remove": "เอาตัวกรองนี้ออก",
  "check.chip.payout": "วันจ่าย {date}",
  "check.chip.nearest": "(ใกล้สุด)",
  "check.chip.round": "รอบ {from}–{to}",
  "check.chip.days": "วันทำงาน {from}–{to}",
  "check.chip.time": "เวลาเริ่ม {from}–{to}",
  "check.add.payout": "วันจ่าย",
  "check.add.person": "คน",
  "check.add.contract": "สัญญา / รอบ",
  "check.add.round": "รอบของสัญญา",
  "check.add.range": "ช่วงวันทำงาน",
  "check.add.menu": "ตัวกรอง",
  "check.pick.personHint": "พิมพ์ชื่อหรือรหัสคน — เลือกได้หลายคน",
  "check.pick.round": "เลือกรอบของสัญญานี้",
  "check.pick.contractSearch": "ค้นชื่อสัญญาหรือเลขสัญญา",
  "check.pick.noContract": "ไม่พบสัญญาที่ยังมีบิลรอจ่าย",
  "check.pick.timeHint": "เวลาเริ่มของใบงาน (ไม่บังคับ) — กะดึกใส่ 22:00 ถึง 06:00 ได้",
  "check.pick.timeFrom": "เริ่มตั้งแต่",
  "check.pick.timeTo": "ถึงก่อน",
  "check.pick.apply": "ใช้ช่วงนี้",
  "check.nPeople": "{n} คน",
  "check.nBills": "{n} บิล",
  "check.tag.flags": "ปัญหา {n}",
  "check.tag.changed": "เปลี่ยน {n} รายการ",
  "check.tag.progress": "ตรวจ {x}/{y}",
  "check.workPeriod": "ช่วงงาน",
  "check.round.none": "ยังไม่เข้ารอบจ่าย",
  "check.round.draft": "อยู่ในรอบร่าง",
  "check.round.frozen": "แช่แข็ง รออนุมัติ",
  "check.round.approved": "อนุมัติแล้ว รอโอน",
  "check.round.paid": "จ่ายแล้ว",
  "check.round.name": "รอบ “{name}”",
  "check.todo.flagged": "มีปัญหา รอปิดเรื่อง",
  "check.todo.stale": "ต้องดูใหม่ มีรายการเปลี่ยนหลังตรวจ",
  "check.todo.signals": "มีจุดน่าสงสัย {n} จุด รอดู",
  "check.todo.rows": "ตรวจอีก {n} รายการ",
  "check.todo.unchecked": "ยังไม่ได้ตรวจ",
  "check.todo.unacked": "ตรวจแล้ว แต่ยังไม่รับทราบ {n} จุด",
  "check.todo.done": "เรียบร้อย",
  "check.sig.unpaid": "เที่ยวไม่มีค่าเที่ยว",
  "check.sig.stuck": "ใบงานค้าง",
  "check.queue": "คิวตรวจ",
  "check.queueSearch": "ค้นในคิว — ชื่อ รหัส สัญญา",
  "check.order": "เรียง: ต้องดูก่อน แล้วยอดมากก่อน",
  "check.pile.problem": "ติดปัญหา — มีบันทึกปัญหาที่ยังเปิด",
  "check.pile.changed": "เปลี่ยนหลังตรวจ",
  "check.pile.odd": "มีสัญญาณให้ดู",
  "check.pile.todo": "ยังไม่ตรวจ — ยอดมากก่อน",
  "check.pile.done": "ตรวจแล้ว",
  "check.doneFolded": "พับไว้",
  "check.shown": "แสดง {n} จาก {m}",
  "check.show": "แสดง",
  "check.loadMore": "โหลดเพิ่ม {n}",
  "check.empty": "ไม่มีบิลที่ตรงกับตัวกรอง",
  "check.workMeta": "{n} บิลในชุดนี้ · รวม",
  "check.position": "คนที่ {at} จาก {of} ที่ยังไม่เสร็จ",
  "check.openPerson": "หน้าบิลของคนนี้",
  "check.billsOf": "บิลของคนนี้ในชุดตรวจ",
  "check.footProgress": "ทั้งคนตรวจแล้ว {x}/{y} รายการ",
  "check.keys": "J/K คนถัดไป/ก่อนหน้า · N คนถัดไป · Enter ตรวจแล้ว",
  "check.nextBill": "บิลถัดไปของคนนี้ →",
  "check.nextPerson": "คนถัดไป →",
  "check.totals": "{people} คน · {bills} บิล · ฿{amount}",
  "check.allDone": "ตรวจครบทุกคนในชุดนี้แล้ว",
  "check.reloadFailed": "บันทึกแล้ว แต่โหลดรายชื่อใหม่ไม่สำเร็จ — กดรีเฟรชหน้าเพื่อดูสถานะล่าสุด",
  "check.totalsBeforeFee": "(ยอดบิล ก่อนหักค่าโอนของรอบ — ยอดโอนดูที่หน้ารอบจ่าย)",
  "check.done": "ตรวจแล้ว {n} คน ({pct}%)",
  "check.roundTools": "เครื่องมือของรอบนี้:",
  "check.byPerson": "ไล่ทีละคน",
  "check.chip.batch": "รอบจ่าย {name}",
  "check.add.batch": "รอบจ่าย",
  "payBatches.checkPeople": "ตรวจรอบนี้ทีละคน",
  "check.viewing": "กำลังดูคนนี้อยู่ด้วย: {names} — ระวังตรวจซ้ำกัน",
  "check.sets": "ชุดตรวจ",
  "check.sets.saved": "บันทึกชุดตรวจแล้ว",
  "check.sets.forMe": "มอบให้ฉัน",
  "check.sets.for": "มอบให้ {name}",
  "check.sets.by": "บันทึกโดย {name}",
  "check.sets.remove": "ลบชุด {name}",
  "check.sets.none": "ยังไม่มีชุดตรวจ",
  "check.sets.saveTitle": "บันทึกตัวกรองตอนนี้เป็นชุด",
  "check.sets.namePh": "ชื่อชุด เช่น สายตะวันออก วันจ่าย 31",
  "check.sets.assignPh": "มอบให้ใคร (ไม่บังคับ)",
  "check.sets.save": "บันทึกชุด",
  "check.sets.needFilter": "เลือกตัวกรองอย่างน้อยหนึ่งอย่างก่อน",
  "check.checkers": "กดตรวจล่าสุดโดย: {list}",
  "check.checker": "{name} {n} บิล ({rows} รายการ)",
  "search.placeholder": "ค้นหาคน ใบงาน ทะเบียนรถ สัญญา ยอดเงิน…",
  "search.title": "ค้นหาอะไรก็ได้",
  "search.esc": "Esc ปิด",
  "search.hint": "พิมพ์อย่างน้อย 2 ตัว — เลขใบงาน ชื่อหรือรหัสคน ทะเบียนรถ ชื่อหรือเลขสัญญา หรือยอดเงิน",
  "search.none": "ไม่พบอะไรที่ตรงกับคำค้น",
  "search.keys": "↑↓ เลือก · Enter เปิด · Tab เปลี่ยนหมวด · Esc ปิด · ค้นจากข้อมูลทั้งองค์กร",
  "search.tab.all": "ทั้งหมด",
  "search.tab.tickets": "ใบงาน",
  "search.tab.vehicles": "รถ",
  "search.tab.people": "คน",
  "search.tab.contracts": "สัญญา",
  "search.tab.amounts": "ยอดเงิน",
  "search.inBill": "บิล {title} {from}–{to}",
  "search.inBox": "รายการสะสม (ยังไม่มีวันจ่าย)",
  "search.noBill": "ยังไม่อยู่ในบิล",
  "search.checkedBy": "ตรวจแล้ว · {name} {day}",
  "search.notChecked": "ยังไม่ตรวจ",
  "search.noPayDay": "ยังไม่มีวันจ่าย",
  "search.drivers": "คนขับช่วงนี้: {names}",
  "search.noDrivers": "ไม่มีคนขับใน 45 วันที่ผ่านมา",
  "search.checkThem": "ตรวจคนขับคันนี้",
  "search.personSub": "เปิดตรวจบิลของคนนี้",
  "billCheck.tickRange": "ตรวจช่วงนี้แล้ว ({n} รายการ)",
  "billCheck.rangeDone": "ช่วงนี้ตรวจครบแล้ว",
  "billCheck.rangeTicked": "ติ๊กรายการในช่วงนี้แล้ว",
  "billCheck.rangeShown": "แสดงเฉพาะรายการในช่วงที่กรอง {n} จาก {m} รายการของบิลนี้ · \"ตรวจช่วงนี้แล้ว\" ติ๊กเฉพาะรายการเหล่านี้",
  "billCheck.rangeOutside": "นอกช่วง {n} รายการ",
  "billCheck.rangeSeeWhole": "ดูทั้งบิล",
  "billCheck.rangeBack": "กลับไปดูเฉพาะช่วง",
  "billCheck.rangeWhole": "กำลังดูทั้งบิล — ปุ่ม \"ตรวจแล้ว\" ปิดทั้งบิล",
  "billCheck.sealAgain": "ตรวจแล้ว (อีกครั้ง)",
  "billCheck.sealed": "บันทึกว่าตรวจแล้ว",
  "billCheck.unseal": "ยกเลิกการตรวจ",
  "billCheck.unsealed": "ยกเลิกการตรวจแล้ว",
  "review.signal.unchecked": "{n} คนยังไม่ได้ตรวจบิลก่อนแช่แข็ง",
  "review.signal.stale": "{n} คนบิลเปลี่ยนหลังตรวจ",
  "review.signal.flagged": "{n} คนมีเรื่องที่ยังเปิดอยู่ (มีปัญหา)",
  "review.check.unchecked": "ยังไม่ตรวจบิล",
  "review.check.checked": "ตรวจบิลแล้ว",
  "review.check.stale": "บิลเปลี่ยนหลังตรวจ",
  "review.check.flagged": "มีปัญหา",
  "billCheck.onlySignalsPeople": "คนที่มีจุดน่าสงสัย",
  "billCheck.signalsN": "{n} จุดน่าสงสัย",
  "billCheck.pointsOpen": "{n} จุดยังไม่มีใครรับทราบ",
  "billCheck.pointsAcked": "{n} จุดน่าสงสัย · รับทราบแล้ว",
  "billCheck.ack.title": "ก่อนกดตรวจแล้ว ดูทีละจุดที่ระบบชี้ ({n} จุด) แล้วติ๊กว่าดูแล้ว ถูกต้อง",
  "billCheck.ack.titleAfter": "{name} กดตรวจแล้วเมื่อ {day} แต่ยังไม่มีใครรับทราบ {n} จุดนี้ — ดูทีละจุดแล้วติ๊ก",
  "billCheck.ack.notePh": "เหตุผลที่ถูกต้อง (ถ้ามี) เช่น หัวหน้าสายยืนยันว่าวิ่งจริง",
  "billCheck.ack.bad": "ติ๊กที่แถวของแต่ละจุด · จุดไหนไม่ถูก ใส่ธงที่แถวนั้นแทน",
  "billCheck.ack.bar": "ต้องดู {n} จุดที่ระบบชี้ · ติ๊กแล้ว {x}/{n}",
  "billCheck.ack.barAfter": "{name} กดตรวจแล้วเมื่อ {day} แต่ยังไม่มีใครรับทราบ {n} จุด · ติ๊กแล้ว {x}/{n}",
  "billCheck.ack.next": "ไปจุดถัดไป",
  "billCheck.ack.rowYes": "ดูแล้ว ถูกต้อง",
  "billCheck.ack.show": "ดูแถว",
  "billCheck.ack.go": "ตรวจแล้ว · รับทราบ {n} จุด",
  "billCheck.ack.goAgain": "รับทราบและยืนยันตรวจแล้ว",
  "billCheck.ack.needAll": "ติ๊กให้ครบทุกจุดก่อน",
  "billCheck.ack.done": "รับทราบแล้ว โดย {who}",
  "billCheck.rate.off": "ไม่ตรงเรทที่ตั้งไว้ — {src} {expected}",
  "billCheck.rate.src.standard": "เรทมาตรฐานของเส้นทาง",
  "billCheck.rate.src.route_driver": "เรทเฉพาะคนนี้ในเส้นทางนี้",
  "billCheck.rate.src.contract_driver": "เรทเฉพาะคนนี้ในสัญญานี้",
  "billCheck.rate.src.driver_default": "เรทประจำตัวคนขับ",
  "billCheck.rate.src.manual": "ตั้งเองบนใบงาน (เทียบกับเรทมาตรฐาน)",
  "billCheck.edit.line": "แก้หลังปิดงาน: เดิม {from} → {to} · {who} · {day}",
  "billCheck.edit.noWho": "ไม่มีบันทึกว่าใครแก้",
  "billCheck.clash.line": "เวลาทับกับ {trips}",
  "billCheck.sig.edited": "ค่าแรงเปลี่ยนหลังปิดงาน",
  "billCheck.sig.overlap": "วิ่งซ้อนเวลากับอีกเที่ยว",
  "billCheck.sig.dup_slot": "เส้นทางและเวลาเดียวกันซ้ำ",
  "billCheck.sig.dup_item": "รายการเดียวกันซ้ำในเดือน",
  "billCheck.sig.on_leave": "มีเที่ยวในวันลา",
  "billCheck.sig.after_leave": "หลังวันลาออก",
  "billCheck.sig.negative": "บิลติดลบ",
  "billCheck.sig.unpaid": "เที่ยวที่ปิดงานแล้วแต่ไม่มีค่าเที่ยว {n} ใบ",
  "billCheck.sig.time_broken": "เวลาบนใบงานผิด (จบก่อนเริ่ม หรือไม่มีเวลาเริ่ม)",
  "billCheck.sig.ran_short": "วิ่งไม่ถึง 5 นาที",
  "billCheck.sig.off_plan": "วิ่งจริงคลาดจากแผนเกิน 3 ชั่วโมง",
  "billCheck.sig.stops_missed": "ถึงจุดจอดไม่ถึงครึ่ง",
  "billCheck.sig.no_pair": "วันนั้นไม่มีเที่ยวคู่ (ขาไป–กลับ)",
  "billCheck.sig.stuck": "ใบงานค้าง \"กำลังวิ่ง\" เกิน 24 ชั่วโมง {n} ใบ",
  "billCheck.sig.vs_last": "ยอดต่างจากเดือนก่อนเกิน 20%",
  "issues.stuck": "ใบงานค้าง \"กำลังวิ่ง\" เกิน 24 ชั่วโมง",
  "billCheck.export.file": "ผลการตรวจบิล-{contract}-{end}",
  "billCheck.export.people": "รายคน",
  "billCheck.export.points": "จุดที่ระบบชี้",
  "billCheck.export.person": "ชื่อ",
  "billCheck.export.state": "สถานะ",
  "billCheck.export.rows": "รายการ",
  "billCheck.export.amount": "ยอดก่อนหัก (บาท)",
  "billCheck.export.checkedBy": "ตรวจโดย",
  "billCheck.export.checkedOn": "วันที่ตรวจ",
  "billCheck.export.signals": "จุดน่าสงสัย",
  "billCheck.export.unacked": "ยังไม่มีใครรับทราบ",
  "billCheck.export.day": "วัน",
  "billCheck.export.time": "เวลา",
  "billCheck.export.route": "เส้นทาง",
  "billCheck.export.money": "ยอด (บาท)",
  "billCheck.export.what": "เรื่อง",
  "billCheck.export.status": "สถานะของจุด",
  "billCheck.export.note": "เหตุผล / บันทึก",
  "billCheck.export.by": "โดย",
  "billCheck.export.part.past": "ตรวจผ่านไปแล้ว แต่ยังไม่มีใครรับทราบ",
  "billCheck.export.part.open": "ยังไม่ตรวจ",
  "billCheck.export.part.flagged": "มีปัญหา รอปิดเรื่อง",
  "billCheck.export.part.acked": "รับทราบแล้ว",
  "roundEdit.open": "แก้ในรอบ",
  "roundEdit.tag": "กำลังแก้",
  "roundEdit.title": "กำลังแก้ {n} บิลในรอบนี้ — ยังอนุมัติไม่ได้",
  "roundEdit.hint": "แก้รายการในบิลให้เสร็จ แล้วกด \"แก้เสร็จ\" บิลจะกลับเข้ารอบนี้และจ่ายพร้อมรอบ",
  "roundEdit.by": "เปิดแก้โดย {who}",
  "roundEdit.go": "ไปแก้บิล",
  "roundEdit.done": "แก้เสร็จ",
  "roundEdit.doneSame": "ใส่บิลกลับเข้ารอบแล้ว ยอดไม่เปลี่ยน",
  "roundEdit.doneChanged": "ใส่บิลกลับเข้ารอบแล้ว ยอดคนนี้ {before} → {after}",
  "roundEdit.doneReset": "ใส่บิลกลับเข้ารอบแล้ว ยอดคนนี้ {before} → {after} · ผู้อนุมัติต้องอนุมัติใหม่ตั้งแต่ขั้น 1",
  "roundEdit.dialogTitle": "แก้บิลนี้ในรอบ",
  "roundEdit.dialogBody1": "บิลนี้จะเปิดให้แก้ได้ บิลอื่นในรอบล็อกเหมือนเดิม",
  "roundEdit.dialogBody2": "แก้เสร็จแล้วกด \"แก้เสร็จ\" บิลจะกลับเข้ารอบนี้ จ่ายรอบนี้ ถ้ายอดเปลี่ยน ผู้อนุมัติต้องอนุมัติใหม่ตั้งแต่ขั้น 1",
  "roundEdit.reason": "แก้เพราะอะไร",
  "roundEdit.reasonPlaceholder": "เช่น ค่าเที่ยวใบหนึ่งผิด",
  "roundEdit.opened": "เปิดบิลให้แก้แล้ว",
  "roundEdit.approveBlocked": "มีบิลกำลังแก้ — กด \"แก้เสร็จ\" ก่อนอนุมัติ",
  "roundEdit.billBanner": "บิลนี้เปิดแก้ในรอบ {name} — แก้เสร็จแล้วกด \"แก้เสร็จ\" บิลจะกลับเข้ารอบเดิม",
  "payBatches.event.bill_open": "เปิดบิลแก้ในรอบ",
  "payBatches.event.bill_done": "แก้เสร็จ ใส่บิลกลับเข้ารอบ",
  "roundEdit.seeRound": "ดูรอบ",
  "billCheck.by": "จัดกลุ่มตาม",
  "billCheck.by.person": "คน",
  "billCheck.by.route": "เส้นทาง",
  "billCheck.by.day": "วัน",
  "billCheck.by.kind": "ประเภทเงิน",
  "billCheck.group.noRoute": "ไม่ผูกใบงาน",
  "billCheck.group.meta": "{n} รายการ · {people} คน · ตรวจแล้ว {done}",
  "billCheck.group.partly": "ตรวจแล้ว {pct}%",
  "billCheck.group.pick": "เลือกกลุ่มจากรายการเพื่อดูรายการเงิน",
  "billCheck.group.layout": "รูปแบบ",
  "billCheck.group.layout.list": "รายการ",
  "billCheck.group.layout.grid": "ตาราง วัน × เวลา",
  "billCheck.group.tick": "ตรวจทั้งกลุ่ม ({n} รายการ)",
  "billCheck.group.tickShown": "ตรวจ {n} รายการที่แสดง",
  "billCheck.group.confirm": "ติ๊กว่าตรวจแล้ว {n} รายการ · {total}",
  "billCheck.group.skipped": "เว้น {n} รายการที่มีปัญหา",
  "billCheck.group.pointed": "เว้น {n} รายการที่มีจุดน่าสงสัย ให้ดูในบิลของคนนั้น",
  "billCheck.group.tickGo": "ยืนยัน ตรวจแล้ว",
  "billCheck.group.ticked": "ตรวจแล้ว {n} รายการ",
  "billCheck.group.undo": "ย้อนการติ๊กล่าสุด",
  "billCheck.group.undone": "ย้อนการติ๊กแล้ว",
  "billCheck.group.cell": "{day} เวลา {time}",
  "billCheck.group.cellLabel": "{day} เวลา {time}: {n} รายการ · {state}",
  "billCheck.group.day": "วัน",
  "billCheck.group.noTimes": "ไม่มีเที่ยวที่ระบุเวลาเริ่มในกลุ่มนี้",
  "billCheck.group.more": "แสดง {shown} จาก {n} รายการ — เลือกช่องในตาราง วัน × เวลา เพื่อดูเฉพาะส่วน",
  "roundCheck.title": "ตรวจบิลแล้ว {done}/{all} คน · {pct}% ของยอด",
  "roundCheck.amountBase": "(เงินที่ตรวจแล้ว / ที่ต้องตรวจ — นับทุกรายการทั้งเงินเข้าและเงินออก ไม่ใช่ยอดโอน)",
  "roundCheck.advice": "แช่แข็งได้เลยโดยไม่ต้องตรวจครบ แต่ผู้อนุมัติจะเห็นตัวเลขนี้ — ถ้ายังมีเวลา กดชื่อเพื่อไปตรวจ",
  "roundCheck.issues": "จุดน่าสงสัยในรอบจ่ายนี้ {n} จุด · {contract} · งานถึง {end}",
  "roundCheck.issuesNone": "ไม่มีจุดน่าสงสัยในรอบจ่ายนี้ · {contract} · งานถึง {end}",
  "roundCheck.issuesScope": "นับเฉพาะบิลในรอบจ่ายนี้ แต่หน้าที่เปิดแสดงทั้งสัญญาและรอบงาน รวมคนที่ไม่ได้อยู่ในรอบจ่ายนี้ จึงอาจมากกว่า ไม่มีจุดน่าสงสัยก็ไม่ได้แปลว่าตรวจครบแล้ว",
  "roundCheck.who": "ใครยังไม่ได้ตรวจ ({n} คน)",
  "roundCheck.more": "และอีก {n} คน — ไล่ต่อที่หน้าตรวจบิล",
  "billCheck.note.title": "โน้ตส่งต่องาน",
  "billCheck.note.placeholder": "เช่น ตรวจถึงคนที่ 38 แล้ว · คนที่ติดธงรอฝ่ายบุคคลตอบ",
  "billCheck.note.save": "บันทึกโน้ต",
  "billCheck.note.saved": "บันทึกโน้ตแล้ว",
  "billCheck.note.removed": "ลบโน้ตแล้ว",
  "billCheck.note.by": "{name} · {day}",
  "billCheck.note.edit": "แก้",
  "billCheck.note.add": "เขียนโน้ตส่งต่องาน",
  "fileCheck.open": "ตรวจไฟล์ก่อนส่ง",
  "fileCheck.title": "ตรวจไฟล์ธนาคารก่อนส่ง",
  "fileCheck.nothingRead": "อ่านรายการโอนในไฟล์นี้ไม่ได้ — ต้องมีคอลัมน์เลขที่บัญชีและจำนวนเงิน",
  "fileCheck.failed": "ตรวจไฟล์ไม่สำเร็จ",
  "fileCheck.ok": "ไฟล์ตรงกับรอบ",
  "fileCheck.notOk": "ไฟล์ไม่ตรงกับรอบ — อย่าเพิ่งส่ง",
  "fileCheck.counts": "ในไฟล์ {fn} รายการ {ft} · ในรอบ {rn} คน {rt}",
  "fileCheck.missing": "อยู่ในรอบแต่ไม่อยู่ในไฟล์ {n} คน (จะไม่ได้เงิน)",
  "fileCheck.diff": "ยอดไม่ตรง {n} บัญชี",
  "fileCheck.diffRow": "รอบ {round} · ไฟล์ {file}",
  "fileCheck.extra": "อยู่ในไฟล์แต่ไม่อยู่ในรอบ {n} บัญชี",
  "fileCheck.repeated": "บัญชีเดียวกันหลายบรรทัด {n} บัญชี",
  "fileCheck.lines": "{n} บรรทัด",
  "fileCheck.privacy": "ไฟล์อ่านบนเครื่องนี้เท่านั้น ส่งไปเทียบแค่เลขบัญชีกับยอดเงิน และบันทึกผลการตรวจไว้ในประวัติ",
  "payBatches.sharedAccount": "มี {n} บัญชีที่หลายคนในรอบนี้โอนเข้าบัญชีเดียวกัน — ตรวจก่อนออกไฟล์",
  "payBatches.allContracts": "ทุกสัญญา",
  "billPanel.trips": "ค่าเที่ยว {n} เที่ยว",
  "billPanel.rateOk": "✓ ตรงเรทที่ตั้งไว้ทุกเที่ยว ({src})",
  "billPanel.rateOkSome": "✓ {n} เที่ยวตรงเรทที่ตั้งไว้",
  "billPanel.rateOff": "⚠ {n} เที่ยวจ่าย {paid} แต่เรทที่ตั้งไว้ {expected}",
  "billPanel.days": "มีเที่ยว {n} วัน",
  "billPanel.leave": "ลา {n} วัน",
  "billPanel.oddDays": "ต้องดู {n} วัน",
  "billPanel.computed": "หักโดยระบบ",
  "billPanel.computedHint": "ระบบคิดให้ ไม่ต้องติ๊ก",
  "billPanel.prev": "รอบก่อน {n} รายการ · {total}",
  "billPanel.noPrev": "รอบแรกของคนนี้ในสัญญานี้ — ยังไม่มีรอบก่อนให้เทียบ",
  "billPanel.usual": "ปกติ: วันละ {n} เที่ยว",
  "billPanel.leg.outbound": "รับเข้า",
  "billPanel.leg.return": "รับออก",
  "billPanel.dayLabel": "{day}: {n} เที่ยว",
  "billPanel.dayMissing": "{day}: วันทำงานที่ไม่มีเที่ยว",
  "billPanel.legend.checked": "ตรวจแล้ว",
  "billPanel.legend.unusual": "ต้องดู (ต่างจากปกติ หรือมีจุดน่าสงสัย)",
  "billPanel.legend.flagged": "มีปัญหา",
  "billPanel.legend.missing": "ขาดงาน",
  "billPanel.legend.leave": "วันลา",
  "billPanel.legend.empty": "หยุด / ไม่มีเที่ยว",
  "billPanel.nothingToLook": "ทุกวันเป็นไปตามปกติ ไม่มีวันที่ต้องดูเป็นพิเศษ",
  "billPanel.dayChecked": "{day} ตรวจแล้ว",
  "billPanel.tickDay": "ติ๊กว่าตรวจวัน {day} แล้ว",
  "billPanel.dayTrips": "{n} เที่ยว",
  "billPanel.dayOther": "รายการอื่น {n}",
  "billPanel.onLeave": "วันลา",
  "billPanel.notTicked": "ติ๊กแล้ว {done}/{n}",
  "billPanel.peek.plan": "แผน",
  "billPanel.peek.ran": "วิ่งจริง",
  "billPanel.peek.stops": "ถึงจุด {done}/{n}",
  "billPanel.peek.noStops": "ไม่มีบันทึกการถึงจุด",
  "billPanel.peek.rate": "ค่าเที่ยวบนใบงาน",
  "billPanel.peek.open": "ดูใบงาน",
  "billPanel.view": "มุมมอง",
  "billPanel.view.days": "รายวัน",
  "billPanel.view.routes": "ตามเส้นทาง",
  "billPanel.view.all": "ทุกแถว",
  "billPanel.onlyLook": "เฉพาะวันที่ต้องดู {n}",
  "billPanel.keys": "J/K วันถัดไป · Space ติ๊กวัน · F ใส่ธง · Enter ตรวจแล้ว · N คนถัดไป",
  "billPanel.keysShort": "Enter ตรวจแล้ว · N คนถัดไป",
  "billPanel.keysLabel": "แป้นลัด",
  "billPanel.more": "รายละเอียด",
  "billPanel.fold": "ย่อ",
  "billPanel.otherRows": "รายการอื่น {n}",
  "billPanel.missingShort": "ขาด {n} วันทำงาน",
  "billPanel.rateOffShort": "⚠ {n} เที่ยวไม่ตรงเรท",
  "billPanel.rateOkShort": "✓ ตรงเรททุกเที่ยว",
  "billPanel.prevShort": "รอบก่อน {total}",
  "billPanel.calendar": "วันในบิล",
  "billPanel.calendarOpen": "ปฏิทิน",
  "billPanel.calendarFold": "ย่อปฏิทิน",
  "billPanel.missing": "ขาด {n} วันทำงาน: {days}",
  "billPanel.noMissing": "ไม่ขาดวันทำงาน",
  "billPanel.offWeekdays": "หยุดประจำ: {days}",
  "compare.pick": "เทียบกับคนอื่นในรอบนี้…",
  "compare.pickShort": "เทียบกับ…",
  "compare.same": "ยอดเท่ากันทุกวัน",
  "compare.less": "{me} ได้น้อยกว่า {peer} {amount}",
  "compare.more": "{me} ได้มากกว่า {peer} {amount}",
  "compare.onlyPeer": "{n} วันที่อีกคนวิ่งแต่คนนี้ไม่ได้วิ่ง ({days}): {amount}",
  "compare.onlyMine": "{n} วันที่คนนี้วิ่งแต่อีกคนไม่ได้วิ่ง ({days}): {amount}",
  "compare.both": "{n} วันที่วิ่งทั้งคู่แต่เงินไม่เท่ากัน ({days}): {amount}",
  "compare.day": "วัน",
  "compare.me": "คนนี้",
  "compare.peer": "อีกคน",
  "compare.diff": "ต่าง",
  "compare.trips": "{n} เที่ยว",
  "bulk.open": "ตรวจแล้วทุกคนที่ไม่มีจุดน่าสงสัย ({n})",
  "bulk.title": "{n} คนนี้ไม่มีจุดที่ระบบชี้ — ดูยอดอีกครั้ง แล้วกดตรวจแล้วทีเดียว",
  "bulk.unusual": "ไม่เท่าคนส่วนใหญ่",
  "bulk.total": "รวม {amount}",
  "bulk.go": "ยืนยัน ตรวจแล้ว {n} คน",
  "bulk.done": "ตรวจแล้ว {n} คน",
  "bulk.skipped": "ตรวจแล้ว {done} คน · เว้น {n} บิลที่เปลี่ยนไปหรือมีจุดใหม่ — เปิดดูทีละคน",
  "billPanel.ahead": "ยังไม่ถึง {n} วัน",
  "billPanel.legend.ahead": "ยังไม่ถึง",
  "glance.title": "ภาพรวมรอบนี้",
  "glance.toCheck": "เทียบกับคนอื่นและกดตรวจแล้ว ที่หน้าตรวจบิล",
  "glance.worked": "มีเที่ยว {n} วัน",
  "glance.leave": "ลา: {days}",
  "glance.noLeave": "ไม่มีใบลาในรอบนี้",
  "glance.noTrip": "ไม่มีใบงาน — วันทำงานที่ขาด",
  "readiness.title": "ตรวจบิลก่อนจ่าย",
  "readiness.lead": "รอบที่ครบแล้วแต่ยังไม่จ่าย — ตรวจไปถึงไหน และเหลืออะไรต้องเคลียร์ก่อนอนุมัติ",
  "readiness.window": "รอบ {from}–{to} · จ่าย {pay} · {n} คน",
  "readiness.windowNoStart": "รอบถึง {to} · จ่าย {pay} · {n} คน",
  "readiness.noWindow": "ไม่มีรอบตัด · {n} คน",
  "readiness.checked": "ตรวจแล้ว {done}/{all}",
  "readiness.ready": "พร้อมอนุมัติ",
  "readiness.open": "ค้าง {n} จุด · {amount}",
  "readiness.flagged": "มีปัญหา {n} คน",
  "readiness.left": "ยังไม่ตรวจ {n} คน",
  "issues.open.btn": "จุดน่าสงสัย",
  "issues.btn.open": "จุดน่าสงสัย {n} จุด",
  "issues.btn.answered": "จุดน่าสงสัย · ตอบครบแล้ว {n} จุด",
  "issues.btn.none": "ไม่มีจุดน่าสงสัย",
  "issues.btn.noneHint": "ระบบไม่เห็นจุดที่แปลกในรอบนี้ แต่ไม่ได้แปลว่าตรวจครบแล้ว — ดูความคืบหน้าการตรวจที่แถบด้านบน",
  "issues.what": "จุดน่าสงสัยคือจุดที่ระบบเห็นว่าแปลก เช่น เที่ยวไม่มีค่าเที่ยว หรือเวลาเพี้ยน ต้องมีคนเปิดดู แล้วกดรับทราบ หรือแจ้งปัญหาถ้าผิดจริง",
  "issues.noneLeft": "ไม่มีจุดน่าสงสัยที่ค้างอยู่ — จุดที่ระบบเห็นถูกตอบครบแล้ว {n} จุด (ดูข้างล่าง)",
  "issues.checkLeft": "แต่ยังไม่ได้ตรวจ {left} จาก {all} คน",
  "issues.checkDone": "และตรวจครบทุกคนแล้ว ({all} คน)",
  "issues.goCheck": "ไปตรวจคนที่เหลือ",
  "issues.open": "จุดน่าสงสัยที่ยังค้าง",
  "issues.flagged": "มีปัญหารอปิดเรื่อง",
  "issues.acked": "รับทราบแล้ว",
  "issues.points": "{n} จุด",
  "issues.pastCheck": "ในนี้ {n} จุดอยู่ในบิลที่กดตรวจแล้ว",
  "issues.none": "ไม่มีจุดน่าสงสัยในรอบนี้",
  "issues.unpaid": "เที่ยวที่ปิดงานแล้วแต่ไม่มีค่าเที่ยว",
  "issues.part.past": "ตรวจผ่านไปแล้ว แต่ยังไม่มีใครรับทราบ ({n})",
  "issues.part.open": "ยังไม่ตรวจ ({n})",
  "issues.part.flagged": "มีปัญหา รอปิดเรื่อง ({n})",
  "issues.part.acked": "รับทราบแล้ว ({n})",
  // ADR 0489 — แท็บการจัดการเงินของพนักงาน + หน้าต่างเรท/เงินเดือน/เบี้ยเลี้ยง (4-Archives/plan/tms/0489-hr-money-tab-thai-text.md)
  "hrMoney.monthlyHint": "เงินเดือน ค่าล่วงเวลา ภาษี และรอบจ่าย พร้อมเบี้ยเลี้ยง/รายการหักประจำเดือน — ทุกการแก้มีช่วงวันเวลาที่มีผลและเก็บประวัติไว้",
  "hrMoney.perMonth": "/เดือน",
  "hrMoney.perHour": "/ชม.",
  "hrMoney.componentsLead": "บวกหรือหักทุกเดือน แต่ละรายการมีช่วงวันที่มีผลของตัวเอง",
  "hrMoney.netLine": "รวมต่อเดือนตอนนี้ {sign}฿{amount}",
  "hrMoney.historyShow": "ดูประวัติเงินเดือน ({n})",
  "hrMoney.historyHide": "ซ่อนประวัติเงินเดือน",
  "hrMoney.tripsLead": "ระบบไล่ดูทีละขั้น เจอขั้นไหนก่อนใช้ขั้นนั้น:",
  "hrMoney.tripsStandardWhere": "ขั้นสุดท้ายตั้งที่หน้ารายละเอียดเส้นทาง (ช่องค่าตอบแทนคนขับภายใน / ภายนอก) ใช้กับทุกคนที่วิ่งเส้นนั้น",
  "hrMoney.componentsTitle": "เบี้ยเลี้ยงและรายการหัก",
  "hrMoney.componentsHint": "รายการประจำเดือนที่บวกหรือหักจากเงินเดือน — แต่ละฝั่งมีปุ่มจัดการของตัวเอง",
  "hrMoney.noAllowances": "ยังไม่มีเบี้ยเลี้ยง",
  "hrMoney.noDeductions": "ยังไม่มีรายการหัก",
  "hrMoney.effectiveAlways": "ไม่กำหนดวันเริ่ม",
  "hrMoney.listEffective": "มีผล {from} → {to}",
  "hrMoney.resolutionOrder": "· ลำดับที่ระบบเลือกใช้ (เจอก่อนใช้ก่อน):",
  "hrMoney.noDefaultRateYet": "ยังไม่มีเรทตั้งต้นรายบุคคล — ระบบใช้ค่าตอบแทนมาตรฐานของเส้นทาง",
  "hrMoney.contractRateHint": "เรทต่อเที่ยวของคนขับคนนี้ในสัญญาหนึ่ง — ใช้ก่อนเรทเริ่มต้นของคนขับ แต่ถ้ามีเรทเส้นทาง เรทเส้นทางจะถูกใช้ก่อน",
  "hrMoney.noContractRatesYet": "ยังไม่มีเรทรายบุคคลตามสัญญา — ระบบไปดูขั้นถัดไป",
  "hrMoney.routeRateHint": "เรทต่อเที่ยวของคนขับคนนี้บนเส้นทางหนึ่ง — เฉพาะเจาะจงที่สุดในเรทประจำ ใช้ก่อนเรทสัญญาและเรทเริ่มต้น",
  "hrMoney.route": "เส้นทาง",
  "hrMoney.contract": "สัญญา",
  "hrMoney.effectiveWindow": "ช่วงที่มีผล",
  "hrMoney.voidRateAria": "ยกเลิกเรท ฿{amount}",
  "hrMoney.voidRateConfirm": "ยกเลิกเรท ฿{amount} ({from} → {to}) ใช่ไหม? แถวนี้ยังอยู่ในประวัติ และใบงานในรอบที่ยังเปิดอยู่จะถูกคิดค่าเที่ยวใหม่",
  "hrMoney.voidRateConfirmLabel": "ยกเลิกเรท",
  "hrMoney.voidRateFailed": "ยกเลิกเรทไม่สำเร็จ",
  "hrMoney.rate.titleDefault": "ตั้งเรทตั้งต้นรายบุคคล",
  "hrMoney.rate.titleRoute": "ตั้งเรทรายบุคคลตามเส้นทาง",
  "hrMoney.rate.titleContract": "ตั้งเรทรายบุคคลตามสัญญา",
  "hrMoney.rate.driverPrefix": "คนขับ: {name} · ",
  "hrMoney.rate.ledgerHint": "เวอร์ชันใหม่จะปิดเวอร์ชันก่อนหน้าให้อัตโนมัติ (เก็บประวัติไว้ทั้งหมด)",
  "hrMoney.rate.appliesTo": "ใช้กับ",
  "hrMoney.rate.perTrip": "เรทต่อเที่ยว (฿)",
  "hrMoney.rate.amountPh": "เช่น 350",
  "hrMoney.rate.backdated": "⚠ ย้อนหลัง — ใบงานที่วิ่งแล้วในช่วงนี้ (เฉพาะรอบที่ยังเปิด) จะถูกคิดค่าเที่ยวใหม่อัตโนมัติ และบันทึกประวัติไว้",
  "hrMoney.rate.reasonPh": "เช่น ตกลงสัญญาใหม่",
  "hrMoney.rate.save": "บันทึกเรทใหม่",
  "hrMoney.window.from": "มีผลตั้งแต่ (วันและเวลา)",
  "hrMoney.window.to": "มีผลถึง (วันและเวลา)",
  "hrMoney.window.blankOpenEnded": "— เว้นว่าง = ไม่มีกำหนดสิ้นสุด",
  "hrMoney.window.backdated": "ย้อนหลัง — มีผลย้อนไปตั้งแต่เวลานี้",
  "hrMoney.window.scheduled": "ตั้งล่วงหน้า — มีผลเมื่อถึงเวลานี้",
  "hrMoney.salary.baseLabel": "เงินเดือนพื้นฐาน (฿/เดือน)",
  "hrMoney.salary.diffFromCurrent": "{diff} ฿ จากเดิม ({current} ฿)",
  "hrMoney.salary.otHourly": "ค่าล่วงเวลาต่อชั่วโมง (฿)",
  "hrMoney.salary.save": "บันทึกค่าตอบแทน",
  "hrMoney.comp.manageAllowances": "จัดการเบี้ยเลี้ยง",
  "hrMoney.comp.manageDeductions": "จัดการรายการหัก",
  "hrMoney.comp.windowHint": "แต่ละรายการมีช่วงที่มีผลของตัวเอง",
  "hrMoney.comp.namePhAllowance": "เช่น ค่าน้ำมัน",
  "hrMoney.comp.namePhDeduction": "เช่น ค่าชุดยูนิฟอร์ม",
  "hrMoney.comp.amountPh": "฿ / เดือน",
  "hrMoney.comp.removeRow": "นำออก {name}",
  "hrMoney.comp.rowN": "แถว {n}",
  "hrMoney.comp.noEnd": "ไม่ใส่วันสิ้นสุด = ไม่มีกำหนดสิ้นสุด",
  "hrMoney.comp.addAllowance": "เพิ่มเบี้ยเลี้ยง",
  "hrMoney.comp.addDeduction": "เพิ่มรายการหัก",
  "hrMoney.comp.totalAllowances": "เบี้ยเลี้ยงรวม ({n} รายการ ทุกช่วงเวลา)",
  "hrMoney.comp.totalDeductions": "รายการหักรวม ({n} รายการ ทุกช่วงเวลา)",
  "hrMoney.comp.reasonPhAllowance": "เช่น เพิ่มค่าน้ำมันตามเส้นทางใหม่ที่ได้รับมอบหมาย",
  "hrMoney.comp.reasonPhDeduction": "เช่น หักค่าชุดยูนิฟอร์มตามนโยบายบริษัท",
  "hrMoney.comp.saveAllowances": "บันทึกเบี้ยเลี้ยง",
  "hrMoney.comp.saveDeductions": "บันทึกรายการหัก",
  // IN-PLACE T5
  "payBatchList.search": "ค้นชื่อรอบ หรือชื่อคนที่รอบนั้นจ่าย",
  "payBatchList.filterStatus": "สถานะรอบ",
  "payBatchList.range": "วันจ่าย",
  "payBatchList.from": "วันจ่ายตั้งแต่",
  "payBatchList.to": "วันจ่ายถึง",
  "payBatchList.fromChip": "ตั้งแต่ {d}",
  "payBatchList.toChip": "ถึง {d}",
  "payBatchList.noMatch": "ไม่พบรอบจ่ายที่ตรงกับที่ค้นหา",
  "billsList.unassigned": "มีรายการเงิน {n} รายการที่ไม่ระบุพนักงาน จึงไม่อยู่ในบิลของใคร —",
  "billsList.unassignedLink": "ตรวจได้ที่หน้ารายการเงิน",
  "billsList.sort.nameAsc": "เรียงตามชื่อ ก → ฮ",
  "billsList.sort.nameDesc": "เรียงตามชื่อ ฮ → ก",
  "billsList.sort.dueDesc": "เรียงตามครบรอบ รอจ่าย มากไปน้อย",
  "billsList.sort.dueAsc": "เรียงตามครบรอบ รอจ่าย น้อยไปมาก",
  "billsList.sort.openDesc": "เรียงตามยอดที่ยังไม่จ่าย มากไปน้อย",
  "billsList.sort.openAsc": "เรียงตามยอดที่ยังไม่จ่าย น้อยไปมาก",
  // IN-PLACE T4
  "financeTxn.searchPh": "FTX-… / JOB-… หรือชื่อคน",
  "financeTxn.perPage": "แถวต่อหน้า",
  "financeTxn.export": "ส่งออก Excel",
  "financeTxn.exportDone": "ส่งออก {n} รายการแล้ว",
  "financeTxn.exportCapped": "ส่งออก {max} รายการแรกจาก {total} — ไฟล์หนึ่งรับได้ไม่เกิน {max} รายการ แคบช่วงวันที่หรือตัวกรองเพื่อเอาส่วนที่เหลือ",
  "financeTxn.exportEmpty": "ไม่มีรายการให้ส่งออก",
  "financeTxn.exportFailed": "ส่งออกไม่สำเร็จ",
  "financeTxn.exportHint": "ทุกหน้าของตัวกรองและการเรียงนี้ ไม่เกิน {max} รายการ",
  "financeTxn.col.status": "สถานะ",
  "financeTxn.status.normal": "ปกติ",
  "financeTxn.historyOpen": "ประวัติ",
  "financeTxn.historyAria": "ประวัติของ {doc}",
  "financeTxn.historyNone": "รายการนี้ยังไม่เคยเปลี่ยนตั้งแต่เข้าระบบ",
  "financeTxn.historyFailed": "โหลดประวัติไม่สำเร็จ",
  "financeTxn.historyNote": "เหตุผล: {note}",
  "financeTxn.revField.status": "สถานะ",
  "financeTxn.revField.voided_at": "ยกเลิกเมื่อ",
  "financeTxn.revField.deferred_from": "เลื่อนไปบิลที่มีวัน",
  "financeTxn.rev.sync": "ระบบคัดข้อมูลใหม่จากต้นทาง",
  "financeTxn.rev.manual_create": "บันทึกรายการ",
  "financeTxn.rev.manual_edit": "แก้รายการ",
  "financeTxn.rev.manual_clear_override": "ลบบันทึกการแก้",
  "financeTxn.rev.manual_relink": "ย้ายเอกสารที่ผูก",
  "financeTxn.rev.manual_void": "ยกเลิกรายการ",
  "financeTxn.rev.manual_reverse": "กลับรายการ",
  "financeTxn.rev.source_disappeared": "เอกสารต้นทางหายไป",
  "financeTxn.rev.period_locked": "เดือนนี้ปิดยอดแล้ว",
  "financeTxn.rev.rebuild": "ระบบสร้างรายการใหม่",
  "financeTxn.rev.bill_move": "ย้ายรายการเข้าบิล",
  "financeTxn.rev.bill_return": "คืนรายการจากบิล",
  "financeTxn.rev.bill_defer": "เลื่อนรายการไปบิลถัดไป",
  "financeTxn.rev.manual_status": "เปลี่ยนสถานะรายการ",
  // IN-PLACE T1
  "billPanel.openBill": "เปิดหน้าบิล",
  "billPanel.readOnly": "บิลนี้จ่ายแล้ว ดูได้อย่างเดียว",
  // IN-PLACE T3
  "roundBill.switch": "บิลของคนนี้ในรอบ {n} ใบ",
  "review.details": "ดูรายละเอียดรอบ",
  // IN-PLACE T6
  "flagThread.replyPh": "ตอบ…",
  "flagThread.send": "ส่ง",
  "flagThread.closePh": "เหตุผลที่ปิดเรื่อง",
  "flagThread.needReason": "ใส่เหตุผลก่อนปิดเรื่อง",
  "flagThread.closedBy": "ปิดโดย {who} · {when}",
  "flagThread.noReason": "ไม่ได้ระบุเหตุผล",
  "issues.part.closed": "ปิดเรื่องแล้ว ({n})",
  "billCheck.export.part.closed": "ปิดเรื่องแล้ว",
  // IN-PLACE T7
  "history.btn": "ประวัติการตรวจ",
  "history.everyone": "ทุกคน",
  "history.kinds": "ชนิดเหตุการณ์",
  "history.kind.all": "ทั้งหมด",
  "history.kind.check": "ตรวจ",
  "history.kind.flag": "มีปัญหา",
  "history.kind.changed": "ต้องดูใหม่",
  "history.ev.check": "ตรวจแล้ว",
  "history.ev.uncheck": "ยกเลิกการตรวจ",
  "history.ev.group_tick": "ตรวจทั้งกลุ่ม",
  "history.ev.group_untick": "ย้อนการตรวจทั้งกลุ่ม",
  "history.ev.flag": "มีปัญหา",
  "history.ev.flag_reply": "ตอบเรื่อง",
  "history.ev.flag_close": "ปิดเรื่อง",
  "history.ev.changed": "ต้องดูใหม่",
  "history.acked": "รับทราบ {n} จุด",
  "history.rows": "{n} รายการ",
  "history.system": "ระบบ",
  "history.none": "รอบนี้ยังไม่มีใครตรวจ",
  "history.noneShown": "ไม่มีรายการตามที่เลือก",
  "history.shown": "แสดง {n} จาก {all} รายการ",
  "history.more": "ดูเก่ากว่านี้",
  // IN-PLACE T8
  "sample.title": "สุ่มตรวจ {x}/{y} (ไม่บังคับ)",
  "sample.lead": "รายการปกติที่ระบบไม่ได้ชี้ สุ่มมาให้ดูว่าถูกไหม — อนุมัติได้โดยไม่ต้องดูครบ",
  "sample.by": "สุ่มโดย {name} · {time}",
  "sample.drawing": "กำลังสุ่มรายการ…",
  "sample.drawFailed": "สุ่มรายการไม่สำเร็จ",
  "sample.none": "ไม่มีรายการปกติให้สุ่ม — ทุกคนอยู่ในสิ่งที่ควรดูก่อนแล้ว",
  "sample.ok": "ถูก",
  "sample.bad": "ไม่ถูก",
  "sample.changed": "เปลี่ยนหลังสุ่ม",
  "sample.flagged": "ใส่ \"มีปัญหา\" แล้ว",
  "sample.openBill": "เปิดบิลของ {name}",
  "sample.badTitle": "ไม่ถูกตรงไหน",
  "sample.badWhat": "ระบบจะใส่ \"มีปัญหา\" ที่รายการนี้พร้อมหมายเหตุ ให้คนตรวจบิลแก้",
  "sample.badPh": "เช่น ยอดไม่ตรงใบงาน",
  "sample.badSave": "บันทึกว่าไม่ถูก",
  "sample.failed": "บันทึกผลสุ่มตรวจไม่สำเร็จ",
  "sample.bar": "สุ่มตรวจ {x}/{y}",
  "sample.open": "เปิดดูแถวที่สุ่ม",
  "sample.fold": "ย่อ",
  "history.kind.sample": "สุ่มตรวจ",
  "history.ev.sample": "สุ่มตรวจ",
  "history.ev.spotOk": "สุ่มตรวจ: ถูก",
  "history.ev.spotBad": "สุ่มตรวจ: ไม่ถูก",
  // ADR 0492 — the Makro Locus card on the employee page · list filter · ADR 0493 vehicle history source (import)
  "hr.locus.filter": "Makro Locus",
  "hr.locus.filter.all": "ทั้งหมด",
  "hr.locus.filter.on": "ดึงงานอยู่",
  "hr.locus.filter.off": "ผูกแต่ไม่ดึง",
  "hr.locus.filter.none": "ไม่ผูก",
  "vb.import": "นำเข้าจากไฟล์คนขับ",
  // ADR 0490 — the /locus-sync page (keys built from lists: lib/locus-sync-page.ts LOCUS_PAGE_LIST_KEYS)
  "locus.title": "ดึงงานจาก Makro Locus",
  "locus.syncNow": "ดึงงานตอนนี้",
  "locus.syncing": "กำลังดึงงาน… {t}",
  "locus.readOnly": "ดูได้อย่างเดียว · การดึงงานสั่งได้โดยผู้ดูแลองค์กร (org_admin) เท่านั้น",
  "locus.why.running": "ปิดหน้านี้ได้ รอบจะวิ่งต่อจนจบ",
  "locus.why.cooldown": "กดได้อีกครั้งเวลา {time}",
  "locus.why.login": "ต้องเข้าสู่ระบบ Locus ใหม่ก่อน",
  "locus.why.blocked": "หยุดเข้าสู่ระบบ Locus ถึง {time}",
  "locus.why.noRiders": "ยังไม่มีพนักงานที่ผูกรหัส Locus",
  "locus.why.notConnected": "องค์กรนี้ยังไม่ได้เชื่อม Locus",
  "locus.badge.running": "กำลังดึงงาน",
  "locus.badge.ok": "เชื่อมต่อแล้ว",
  "locus.badge.error": "รอบล่าสุดผิดพลาด",
  "locus.badge.login": "ต้องเข้าสู่ระบบ Locus ใหม่",
  "locus.badge.blocked": "หยุดเข้าสู่ระบบชั่วคราว",
  "locus.badge.notConnected": "ยังไม่ได้เชื่อม",
  "locus.badge.off": "ปิดการดึงงาน",
  "locus.badge.noContract": "ไม่มีสัญญา",
  "locus.bar.login": "การเข้าสู่ระบบ Locus หมดอายุ — ใบงานจะไม่อัปเดตจนกว่าจะเข้าสู่ระบบใหม่ (ตอนนี้ทีมพัฒนาเป็นคนเข้าสู่ระบบ)",
  "locus.bar.blocked": "หยุดเข้าสู่ระบบ Locus ถึง {time} เพราะเข้าไม่สำเร็จหลายครั้ง",
  "locus.bar.running": "กำลังดึงงาน — เริ่มโดย {name} เมื่อ {time} · ผลจะขึ้นในประวัติเมื่อจบ",
  "locus.bar.noRiders": "ยังไม่มีพนักงานที่ผูกรหัส Locus — งานใน Locus จะไม่ถูกดึง",
  "locus.bar.toHr": "ไปที่หน้าพนักงาน →",
  "locus.toast.done": "ดึงงานเสร็จ · ใบงาน ใหม่ {c} · แก้ {u} · เท่าเดิม {s}",
  "locus.toast.failed": "ดึงงานไม่สำเร็จ — {reason}",
  "locus.toast.runNotFound": "ไม่พบรอบนี้",
  "locus.timeline.title": "รอบดึงงานวันนี้ · {date}",
  "locus.timeline.next": "รอบถัดไป {time}",
  "locus.timeline.now": "ตอนนี้ {time}",
  "locus.timeline.empty": "วันนี้ยังไม่ได้ดึงงาน",
  "locus.timeline.planned": "ตั้งเวลาไว้",
  "locus.timeline.running": "กำลังดึง",
  "locus.timeline.manual": "สี่เหลี่ยมบนหัวแท่ง คือรอบที่กดเอง",
  // ADR 0516 D5 — the minute labels of the timeline grid (the slow mark and half of it)
  "locus.timeline.gridMin": "{n} นาที",
  "locus.timeline.aria": "รอบดึงงานของวันนี้",
  "locus.timeline.openHint": "กดดูรายละเอียด",
  "locus.track.aria": "ติดตามการดึงงาน",
  "locus.track.status": "สถานะ",
  "locus.track.nextRun": "รอบถัดไป {time}",
  "locus.track.manualOnly": "ดึงด้วยมือเท่านั้น",
  "locus.track.runningHead": "เริ่ม {time} · ผ่านไป {t}",
  "locus.track.runningSub": "รอบนี้วิ่งได้นานสุด {n} นาที",
  "locus.track.stale": "ไม่มีความคืบหน้า {n} นาที",
  "locus.track.failedHead": "รอบล่าสุดล้ม {time}",
  "locus.track.streak": "ล้มติดกัน {n} รอบ",
  "locus.track.lastOk": "ดึงสำเร็จล่าสุด",
  "locus.track.never": "ยังไม่เคย",
  "locus.track.login": "เข้าสู่ระบบ Locus",
  "locus.track.loginHours": "ใช้ได้อีก {n} ชม.",
  "locus.track.loginMinutes": "ใช้ได้อีก {n} นาที",
  "locus.track.loginExpiring": "กำลังจะหมด",
  "locus.track.loginGone": "หมดอายุแล้ว",
  "locus.track.loginNone": "ยังไม่เคยเข้า",
  "locus.track.loginBlocked": "หยุดถึง {time}",
  "locus.track.loginsToday": "เข้าวันนี้",
  "locus.track.loginsFails": "ล้ม {n}",
  "locus.track.today": "งานวันนี้ · {date}",
  "locus.track.trips": "เที่ยวใน Locus",
  "locus.track.tickets": "ใบงานในระบบเรา",
  "locus.track.tripsDone": "เสร็จแล้ว",
  "locus.track.ordersDone": "จุดส่งเสร็จ (order)",
  "locus.track.carriedOver": "ใบงานของเมื่อวานที่ยังไม่จบ",
  "locus.track.todayNone": "ยังไม่มีข้อมูลของวันนี้ — ยังไม่ได้ดึงงานสำเร็จ",
  "locus.track.riders": "คนขับ",
  "locus.track.pulled": "ดึงงานอยู่",
  "locus.track.withWork": "มีงานวันนี้",
  "locus.track.noTrips": "ไม่พบงานใน Locus",
  "locus.track.noVehicle": "ยังไม่มีรถประจำ",
  "locus.ago.min": "{n} นาทีที่แล้ว",
  "locus.ago.hour": "{n} ชม. ที่แล้ว",
  "locus.ago.day": "{n} วันที่แล้ว",
  "locus.step.queued": "รอเริ่มงาน…",
  "locus.step.login": "เข้าสู่ระบบ Locus",
  "locus.step.tours": "ค้น trip ของพนักงาน",
  "locus.step.orders": "อ่าน order ของ trip ที่เปลี่ยน",
  "locus.step.write": "เขียนใบงาน + หมุด",
  "locus.stepSub.login": "{riders} คน",
  "locus.stepSub.tours": "{trips} trip · {requests} คำขอ",
  "locus.stepSub.orders": "อ่านร้านแล้ว {orders} จาก {planned} ร้าน",
  "locus.stepSub.ordersNoPlan": "อ่านร้านแล้ว {orders} ร้าน",
  "locus.stepSub.write": "{tickets} trip",
  "locus.col.at": "เวลา",
  "locus.col.by": "สั่งโดย",
  "locus.col.status": "สถานะ",
  "locus.col.ms": "ใช้เวลา (s)",
  "locus.col.trips": "trip ที่ได้",
  "locus.col.orders": "order ที่อ่าน",
  "locus.col.tickets": "ใบงาน",
  "locus.col.ticketsNew": "ใหม่",
  "locus.col.ticketsUpdated": "แก้",
  "locus.col.ticketsSame": "เดิม",
  "locus.seconds": "{n} วิ",
  "locus.filter.status": "สถานะ",
  "locus.empty.noMatch": "ไม่มีรอบที่ตรงกับตัวกรอง",
  "locus.empty.noneYet": "ยังไม่มีรอบดึงงาน",
  "locus.dialog.title": "รายละเอียดรอบ",
  "locus.dialog.errors": "ข้อผิดพลาด",
  "locus.dialog.what": "เกิดอะไร",
  "locus.dialog.request": "คำขอที่ล้ม",
  "locus.dialog.requestVal": "{endpoint} · HTTP {status} · {n} ครั้ง",
  "locus.dialog.requestNet": "{endpoint} · เชื่อมต่อไม่ได้ · {n} ครั้ง",
  "locus.dialog.code": "รหัส error",
  "locus.dialog.raw": "ข้อความจากระบบ",
  "locus.dialog.todo": "ต้องทำอะไร",
  "locus.dialog.result": "ผลลัพธ์",
  "locus.dialog.created": "ใบงานใหม่",
  "locus.dialog.updated": "ใบงานที่แก้",
  "locus.dialog.unchanged": "ใบงานเท่าเดิม",
  "locus.dialog.trips": "trip ที่ได้จาก Locus",
  "locus.dialog.skippedCompleted": "trip จบแล้ว (ไม่ดึง)",
  "locus.dialog.orders": "order ที่อ่าน",
  "locus.dialog.requests": "คำขอไป Locus",
  "locus.dialog.noTrips": "รหัสที่ไม่พบงานใน Locus",
  "locus.dialog.noVehicle": "คนขับที่ยังไม่มีรถประจำ",
  "locus.dialog.nobody": "ไม่มีพนักงานถือรหัสนี้แล้ว",
  "locus.group.ok": "ผ่าน",
  "locus.group.error": "ผิดพลาด",
  "locus.group.skipped": "ข้าม",
  "locus.group.login": "ล็อกอิน Locus", // ADR 0499 D7
  "locus.outcome.ok": "ผ่าน",
  "locus.outcome.skipped_locked": "ข้าม",
  "locus.outcome.not_configured": "ยังไม่ได้เชื่อม Locus",
  "locus.outcome.no_riders": "ข้าม — ไม่มีพนักงานที่ผูกรหัส",
  "locus.outcome.login_needed": "ต้องเข้าสู่ระบบ Locus ใหม่",
  "locus.outcome.login_blocked": "หยุดเข้าสู่ระบบชั่วคราว",
  "locus.outcome.locus_error": "Locus ตอบผิดพลาด",
  "locus.outcome.budget_exceeded": "คำขอเกินเพดาน",
  "locus.outcome.error": "ระบบเราผิดพลาด",
  "locus.trigger.manual": "ระบบ",
  "locus.trigger.loop": "ระบบ",
  "locus.trigger.schedule": "รอบอัตโนมัติ",
  "locus.trigger.web": "กดจากหน้านี้",
  "locus.error.no_riders": "ไม่มีพนักงานที่ผูกรหัส Locus และเปิดดึงงาน",
  "locus.error.skipped_locked": "มีรอบอื่นวิ่งอยู่ในเวลาเดียวกัน",
  "locus.error.not_configured": "องค์กรนี้ยังไม่ได้เชื่อม Locus",
  "locus.error.login_needed": "การเข้าสู่ระบบ Locus หมดอายุ และเซิร์ฟเวอร์ยังเข้าสู่ระบบเองไม่ได้",
  "locus.error.login_blocked": "หยุดเข้าสู่ระบบชั่วคราวหลังเข้าไม่สำเร็จหลายครั้ง",
  "locus.error.http_401": "Locus ไม่รับการเข้าสู่ระบบนี้แล้ว (HTTP 401)",
  "locus.error.http_4xx": "Locus ไม่รับคำขอ (HTTP {http})",
  "locus.error.http_5xx": "Locus ไม่ตอบหรือตอบผิดพลาด (HTTP {http}) ลองซ้ำแล้ว",
  "locus.error.network": "เชื่อมต่อ Locus ไม่ได้ ลองซ้ำแล้ว",
  "locus.error.budget_exceeded": "คำขอไป Locus เกินเพดานต่อรอบ หยุดก่อนเขียน",
  "locus.error.error": "เกิดข้อผิดพลาดในระบบของเรา",
  "locus.error.unknown": "เกิดข้อผิดพลาดที่ระบบไม่รู้จัก",
  "locus.errorTodo.no_riders": "ผูกรหัส Locus ให้พนักงานที่หน้าพนักงาน",
  "locus.errorTodo.skipped_locked": "ไม่ต้องทำอะไร — ผลอยู่ในรอบที่วิ่งอยู่",
  "locus.errorTodo.not_configured": "แจ้งทีมพัฒนาให้เชื่อม Locus",
  "locus.errorTodo.login_needed": "แจ้งทีมพัฒนาให้เข้าสู่ระบบ Locus ใหม่",
  "locus.errorTodo.login_blocked": "รอถึงเวลาที่ลองได้อีก แล้วแจ้งทีมพัฒนาเช็กบัญชี",
  "locus.errorTodo.http_401": "แจ้งทีมพัฒนาให้เข้าสู่ระบบ Locus ใหม่",
  "locus.errorTodo.http_4xx": "แจ้งทีมพัฒนา — คำขอที่เราส่งอาจไม่ตรงกับที่ Locus รับ",
  "locus.errorTodo.http_5xx": "รอรอบถัดไป หรือกด \"ดึงงานตอนนี้\" อีกครั้ง · ถ้าล้มติดกันเกิน 1 ชม. แจ้งทีมพัฒนา",
  "locus.errorTodo.network": "รอรอบถัดไป หรือกด \"ดึงงานตอนนี้\" อีกครั้ง",
  "locus.errorTodo.budget_exceeded": "แจ้งทีมพัฒนา — จำนวน trip วันนี้มากกว่าที่ตั้งไว้",
  "locus.errorTodo.error": "แจ้งทีมพัฒนาพร้อมเวลาของรอบนี้",
  "locus.errorTodo.unknown": "แจ้งทีมพัฒนาพร้อมรหัส error",
  // ADR 0491 — the Locus account + the server login
  "locus.outcome.login_started": "เริ่มเข้าสู่ระบบ Locus",
  "locus.outcome.login_failed": "เข้าสู่ระบบไม่สำเร็จ",
  "locus.error.login_rejected": "Locus ไม่รับ User ID หรือรหัสผ่านของบัญชีที่บันทึกไว้",
  "locus.error.login_shape_changed": "หน้าเข้าสู่ระบบของ Locus เปลี่ยนไป (เช่น ต้องเปลี่ยนรหัสผ่าน หรือเปิดยืนยันตัวตนสองชั้น)",
  "locus.error.login_timeout": "เข้าสู่ระบบ Locus ไม่เสร็จภายในเวลา",
  "locus.errorTodo.login_rejected": "เปิด \"ตั้งค่า Locus\" แล้วใส่รหัสผ่านใหม่",
  "locus.errorTodo.login_shape_changed": "เข้าสู่ระบบที่หน้า Locus เองก่อน แล้วใส่รหัสใหม่ที่ \"ตั้งค่า Locus\"",
  "locus.errorTodo.login_timeout": "กด \"ดึงงานตอนนี้\" อีกครั้ง · ถ้าเป็นซ้ำ แจ้งทีมพัฒนา",
  "locus.badge.loggingIn": "กำลังเข้าสู่ระบบ Locus",
  "locus.bar.loggingIn": "กำลังเข้าสู่ระบบ Locus — เริ่มโดย {name} เมื่อ {time} · เสร็จแล้วจะดึงงานต่อเอง",
  "locus.bar.noAccount": "การเข้าสู่ระบบ Locus หมดอายุ และยังไม่ได้ใส่บัญชี Locus — ใบงานจะไม่อัปเดต",
  "locus.bar.settingsLink": "ตั้งค่า Locus →",
  "locus.bar.askOrgAdmin": "แจ้ง org_admin ขององค์กรให้ใส่บัญชี Locus",
  "locus.why.loginCap": "เข้าสู่ระบบ Locus ครบ 6 ครั้งของวันนี้แล้ว",
  "locus.loggingIn": "กำลังเข้าสู่ระบบ Locus… {t}",
  "locus.toast.loginStarted": "เริ่มเข้าสู่ระบบ Locus · เสร็จแล้วจะดึงงานต่อเอง",
  "locus.track.loginAuto": "หมด · ระบบเข้าใหม่เอง",
  "locus.creds.settings": "ตั้งค่า Locus",
  "locus.creds.userId": "User Id",
  "locus.creds.userIdPh": "User ID ที่ใช้เข้า Locus",
  "locus.creds.password": "รหัสผ่าน",
  "locus.creds.testSave": "ทดสอบและบันทึก",
  "locus.creds.testing": "กำลังทดสอบ…",
  "locus.creds.testingBox": "กำลังทดสอบเข้าสู่ระบบ Locus · อาจใช้ถึง 3 นาที · ปิดแผงนี้ได้",
  "locus.creds.none": "ยังไม่มีบัญชี Locus",
  "locus.creds.noneRead": "ยังไม่มีบัญชี Locus · ผู้ดูแลองค์กร (org_admin) เป็นคนใส่",
  "locus.creds.setBy": "ตั้งโดย",
  "locus.creds.lastTest": "ทดสอบล่าสุด",
  "locus.creds.triesToday": "เข้าสู่ระบบวันนี้",
  "locus.creds.triesLeft": "เหลือ {n} ครั้ง (สูงสุด {max})",
  "locus.creds.change": "เปลี่ยนบัญชี",
  "locus.creds.delete": "ลบบัญชี",
  "locus.creds.deleteTitle": "ลบบัญชี Locus?",
  "locus.creds.deleteMsg": "ระบบจะลบบัญชีและการเข้าสู่ระบบทันที รอบถัดไปจะดึงงานไม่ได้จนกว่าจะใส่บัญชีใหม่",
  "locus.creds.deleted": "ลบบัญชี Locus แล้ว",
  "locus.creds.failed": "ทดสอบไม่ผ่าน — {reason} · บัญชีเดิม (ถ้ามี) ยังใช้อยู่ · ลองได้อีก {n} ครั้งวันนี้",
  "locus.creds.notReady": "ระบบยังไม่พร้อมเก็บบัญชี — แจ้งทีมพัฒนา",
  "locus.creds.lockedBlocked": "หยุดลองเข้าสู่ระบบถึง {time}",
  "locus.creds.lockedCap": "ลองครบ 6 ครั้งของวันนี้แล้ว · ลองใหม่พรุ่งนี้",
  "locus.creds.passed": "ทดสอบผ่าน · บันทึกบัญชีแล้ว",
  "locus.creds.sent": "เริ่มทดสอบบัญชีแล้ว",
  "locus.creds.errUserId": "ใส่ User ID (ไม่เกิน 200 ตัว)",
  "locus.creds.errPassword": "ใส่รหัสผ่าน (ไม่เกิน 200 ตัว)",
  "locus.creds.state.testing": "กำลังทดสอบ",
  "locus.creds.state.ok": "ผ่าน",
  "locus.creds.state.failed": "ไม่ผ่าน",
  "locus.creds.account": "บัญชี Locus",
  "locus.creds.badge.saved": "บันทึกแล้ว",
  "locus.creds.badge.testing": "กำลังทดสอบ",
  "locus.creds.badge.failed": "ทดสอบไม่ผ่าน",
  "locus.creds.badge.locked": "หยุดลองชั่วคราว",
  "locus.creds.badge.none": "ยังไม่มีบัญชี",
  "locus.creds.badge.not_ready": "ระบบยังไม่พร้อม",
  "locus.settings.details": "รายละเอียด",
  "locus.settings.every": "รอบอัตโนมัติ",
  "locus.settings.everyValue": "ทุก {n} นาที",
  "locus.settings.scheduleOff": "ปิดอยู่",
  "locus.settings.next": "รอบถัดไป",
  "locus.settings.changes": "การเปลี่ยนรายชื่อ",
  "locus.settings.changesValue": "มีผลรอบถัดไป",
  "locus.riders.title": "คนขับที่ดึงงานจาก Locus",
  "locus.riders.count": "· ดึงงานอยู่ {on} จาก {of} คน",
  "locus.riders.countLoading": "· ดึงงานอยู่ – จาก – คน",
  "locus.riders.search": "ค้นหาชื่อ · EMP · รหัส ADT",
  "locus.riders.filter": "ตัวกรองคนขับ",
  "locus.riders.group.all": "ทั้งหมด",
  "locus.riders.group.on": "ดึงงานอยู่",
  "locus.riders.group.off": "ไม่ดึง",
  "locus.riders.group.none": "ยังไม่มีรหัส",
  "locus.riders.group.stopped": "หยุดดึง",
  "locus.riders.col.name": "ชื่อ",
  "locus.riders.col.code": "รหัสคนขับ Locus",
  "locus.riders.col.status": "สถานะ",
  "locus.riders.col.setBy": "ตั้งโดย",
  "locus.riders.headCheck": "เลือกทุกคนที่แสดงอยู่",
  "locus.riders.rowCheck": "ดึงงานของ {name}",
  "locus.riders.edit": "แก้หรือลบรหัส",
  "locus.riders.codePh": "รหัสจาก Locus",
  "locus.riders.setByImport": "นำเข้าจากไฟล์คนขับ",
  "locus.riders.unsaved": "ยังไม่บันทึก",
  "locus.riders.otherEmployee": "พนักงานอีกคน",
  "locus.riders.stop.resigned": "หยุดดึง · ลาออก",
  "locus.riders.stop.inactive": "หยุดดึง · ปิดใช้งาน",
  "locus.riders.stop.not_driver": "หยุดดึง · ไม่ใช่คนขับ",
  "locus.riders.err.missing": "ใส่รหัสคนขับจาก Locus ก่อนบันทึก",
  "locus.riders.err.LOCUS_RIDER_BAD_CODE": "รูปแบบรหัสไม่ถูกต้อง · ตัวอย่าง ADT001534",
  "locus.riders.err.LOCUS_RIDER_TAKEN": "รหัสนี้ผูกกับ {emp} อยู่แล้ว",
  "locus.riders.err.LOCUS_RIDER_NOT_DRIVER": "ผูกรหัส Locus ได้เฉพาะพนักงานประเภทคนขับ",
  "locus.riders.err.LOCUS_RIDER_DUPLICATE": "รหัสซ้ำกับ {emp}",
  "locus.riders.err.LOCUS_RIDER_NOT_FOUND": "ไม่พบพนักงานคนนี้แล้ว · ปิดแล้วเปิดแผงใหม่",
  "locus.riders.err.ORG_ADMIN_ONLY": "แก้รายชื่อได้เฉพาะผู้ดูแลองค์กร (org_admin)",
  "locus.riders.readOnly": "แก้รายชื่อได้เฉพาะผู้ดูแลองค์กร (org_admin)",
  "locus.riders.loadError": "โหลดรายชื่อคนขับไม่ได้ · ลองอีกครั้ง",
  "locus.riders.retry": "ลองใหม่",
  "locus.riders.empty": "ยังไม่มีพนักงานประเภทคนขับ",
  "locus.riders.emptyLink": "เพิ่มที่หน้าพนักงาน",
  "locus.riders.noMatch": "ไม่พบคนขับที่ตรงกับคำค้น",
  "locus.riders.summary": "เปลี่ยน {n} คน:",
  "locus.riders.sumAdded": "ดึงเพิ่ม {n}",
  "locus.riders.sumStopped": "หยุด {n}",
  "locus.riders.sumCode": "แก้รหัส {n}",
  "locus.riders.discardChanges": "ยกเลิกการเปลี่ยน",
  "locus.riders.save": "บันทึก {n} คน",
  "locus.riders.saving": "กำลังบันทึก…",
  "locus.riders.savingN": "กำลังบันทึก {n} คน…",
  "locus.riders.saved": "บันทึกแล้ว · มีผลรอบถัดไป {time}",
  "locus.riders.savedNoTime": "บันทึกแล้ว · มีผลรอบถัดไป",
  "locus.riders.saveFailed": "บันทึกไม่ได้ · ต้องแก้ {n} แถว · ไม่มีอะไรถูกบันทึก",
  "locus.riders.tooMany": "บันทึกได้ครั้งละไม่เกิน {max} คน",
  "locus.riders.discardTitle": "ทิ้งการเปลี่ยน {n} คน?",
  "locus.riders.discardMsg": "รายชื่อที่ติ๊กหรือแก้ไว้ยังไม่ได้บันทึก ถ้าปิดตอนนี้จะหายทั้งหมด",
  "locus.riders.discardKeep": "กลับไปแก้ต่อ",
  "locus.riders.discardConfirm": "ทิ้งการเปลี่ยน",
  // refusals of /organizations/locus* (ADR 0490 · 0491)
  "locus.syncErr.LOCUS_SYNC_RUNNING": "มีรอบดึงงานวิ่งอยู่",
  "locus.syncErr.LOCUS_LOGIN_NEEDED": "ต้องเข้าสู่ระบบ Locus ใหม่ก่อน",
  "locus.syncErr.LOCUS_SYNC_COOLDOWN": "รอสักครู่ก่อนกดอีกครั้ง",
  "locus.syncErr.LOCUS_RUNS_BAD_RESULT": "ตัวกรองสถานะไม่ถูกต้อง",
  "locus.syncErr.LOCUS_RUNS_BAD_DATE": "วันที่ไม่ถูกต้อง",
  "locus.syncErr.ORG_ADMIN_ONLY": "เฉพาะผู้ดูแลองค์กร (org_admin) เท่านั้น",
  "locus.syncErr.LOCUS_LOGIN_BLOCKED": "หยุดลองเข้าสู่ระบบ Locus ชั่วคราว",
  "locus.syncErr.LOCUS_LOGIN_DAILY_CAP": "เข้าสู่ระบบ Locus ครบ 6 ครั้งของวันนี้แล้ว",
  "locus.syncErr.LOCUS_LOGIN_RUNNING": "กำลังเข้าสู่ระบบ Locus อยู่",
  "locus.syncErr.LOCUS_LOGIN_RUNNER_FAILED": "เริ่มเข้าสู่ระบบ Locus ไม่ได้ — แจ้งทีมพัฒนา",
  "locus.syncErr.LOCUS_CREDS_BAD_INPUT": "ข้อมูลบัญชีไม่ครบ",
  "locus.syncErr.LOCUS_CREDS_NOT_READY": "ระบบยังไม่พร้อมเก็บบัญชี — แจ้งทีมพัฒนา",
  "locus.syncErr.LOCUS_SYNC_DISPATCH_FAILED": "เริ่มรอบดึงงานไม่ได้ (สั่งตัวตั้งเวลาไม่ได้) — แจ้งทีมพัฒนา รอบอัตโนมัติถัดไปยังวิ่งตามปกติ",
  "locus.syncErr.LOCUS_NO_CONTRACT": "ยังไม่ได้เลือกลูกค้าและสัญญาที่ใช้งาน เลือกได้ที่ \"ตั้งค่า Locus\"",
  "locus.syncErr.LOCUS_NOT_READY": "ต้องทดสอบบัญชี Locus ให้ผ่าน และเลือกลูกค้ากับสัญญาก่อน จึงจะเปิดการดึงงานได้",
  "locus.syncErr.LOCUS_SETUP_INVALID": "สัญญานี้ใช้ไม่ได้ เลือกสัญญาที่ใช้งานของลูกค้าที่เลือก",
  // ADR 0494 — what the pull did to ticket statuses
  "locus.dialog.cancelled": "ยกเลิกตาม Locus",
  "locus.dialog.restored": "กลับมารอดำเนินการ",
  "locus.dialog.cancelSkippedStarted": "Locus ยกเลิกแต่คนขับเริ่มแล้ว",
  "locus.dialog.skippedCancelled": "trip ยกเลิกแล้ว (ไม่ดึง)",
  "locus.dialog.ordersShort": "trip ที่ได้ order ไม่ครบ",
  // ADR 0499 — /locus-sync small fixes
  "locus.track.running": "กำลังวิ่ง",
  "locus.track.notStarted": "ยังไม่เริ่ม",
  "locus.track.ticketsExtra": "มากกว่าเที่ยวใน Locus {n} ใบ — เที่ยวที่ Locus ลบไปแล้ว ระบบยกเลิกเองเมื่อครบ 24 ชม.",
  "locus.newTab": "(เปิดแท็บใหม่)",
  "locus.timeline.sumPulls": "วันนี้ดึง {n} รอบ · ผ่าน {ok} · ผิดพลาด {err}",
  "locus.timeline.sumSkipped": "ข้าม {n} (มีรอบอื่นกำลังดึง)",
  "locus.timeline.sumManual": "กดเอง {n}",
  "locus.timeline.sumLogins": "ล็อกอิน Locus ใหม่ {n} ครั้ง",
  "locus.timeline.tipTickets": "ใหม่ {created} · เปลี่ยน {updated}",
  "locus.timeline.legendGrey": "ข้าม / ล็อกอิน",
  "locus.dialog.titleRun": "รอบ {time}",
  "locus.dialog.took": "ใช้เวลา {n} วินาที",
  "locus.dialog.listCreated": "ใบงานใหม่",
  "locus.dialog.listChanged": "ใบงานที่เปลี่ยน",
  "locus.dialog.listTruncated": "แสดงได้ไม่ครบ — รอบนี้เปลี่ยนเกิน 300 ใบ ดูทั้งหมดได้ที่หน้ารายการใบงาน",
  "locus.dialog.tech": "ตัวเลขการดึง (เทคนิค)",
  "locus.kpi.created": "ใบงานใหม่",
  "locus.kpi.started": "เริ่มวิ่ง",
  "locus.kpi.completed": "เสร็จสิ้น",
  "locus.kpi.cancelled": "ยกเลิก",
  "locus.kpi.other": "ปรับข้อมูล",
  "locus.dialog.updatedSum": "ใบงานที่แก้ทั้งหมด {n} ใบ = เริ่มวิ่ง + เสร็จสิ้น + ยกเลิก + ปรับข้อมูล (ตรงกับช่อง \"แก้\" ในตาราง)",
  "locus.dialog.loginNote": "รอบนี้เป็นการเข้าสู่ระบบ Locus ใหม่ (token หมดอายุ) ยังไม่ได้ดึงงาน — การดึงงานจะตามมาเป็นอีกรอบในประวัติ",
  "locus.change.created": "ใหม่",
  "locus.change.started": "เริ่มวิ่ง",
  "locus.change.completed": "เสร็จสิ้น",
  "locus.change.cancelled": "ยกเลิก",
  "locus.change.restored": "กลับมารอ",
  "locus.change.closed_superseded": "ปิด (เริ่มเที่ยวใหม่)",
  "locus.change.cancelled_missing": "ยกเลิก (หายจาก Locus)",
  "locus.change.updated": "แก้",
  "locus.change.part.stops": "จุดส่งเปลี่ยน",
  "locus.change.part.schedule": "เวลาเปลี่ยน",
  "locus.change.part.driver": "คนขับเปลี่ยน",
  "locus.change.part.vehicle": "รถเปลี่ยน",
  "locus.change.part.progress": "ความคืบหน้าจาก Locus",
  "locus.bar.passwordWarn": "รหัสผ่าน Locus จะหมดอายุ {when} (อีก {n} วัน)",
  "locus.bar.passwordUrgent": "รหัสผ่าน Locus จะหมดอายุ {when} (อีก {h} ชม.)",
  "locus.bar.passwordExpired": "รหัสผ่าน Locus หมดอายุแล้ว ({when}) — ระบบดึงงานไม่ได้จนกว่าจะเปลี่ยนรหัส",
  "locus.bar.passwordChange": "เปิด Locus เพื่อเปลี่ยนรหัส",
  "locus.bar.passwordThenPre": "จากนั้นกด", // ADR 0500 D4
  "locus.bar.passwordThenPost": "แล้วเลือก \"เปลี่ยนบัญชี\" เพื่อกรอกรหัสใหม่",
  "locus.bar.noContract": "หยุดดึงงานจาก Locus เพราะสัญญา {contract} ไม่ได้ใช้งานแล้ว",
  "locus.bar.noContractNone": "หยุดดึงงานจาก Locus เพราะยังไม่ได้เลือกสัญญา",
  "locus.bar.chooseContract": "เลือกสัญญาใหม่",
  "locus.bar.off": "ปิดการดึงงานอัตโนมัติอยู่ ใบงานที่ดึงมาก่อนหน้านี้ยังอยู่ครบ",
  "locus.bar.offNew": "ยังไม่ได้เปิดการดึงงานอัตโนมัติ",
  "locus.bar.turnOn": "เปิดการดึงงาน",
  "locus.bar.tempVehicles": "มีรถชั่วคราว {n} คัน เพราะคนขับยังไม่มีรถประจำ:",
  "locus.bar.and": "และ",
  "locus.bar.andMore": "และอีก {n} คน",
  "locus.bar.tempVehiclesBind": "กดชื่อเพื่อผูกรถให้คนขับ",
  "locus.tempVehicle": "รถชั่วคราว (Locus)",
  "locus.tempVehicleHint": "คนขับยังไม่มีรถประจำ ระบบจึงใช้รถชั่วคราวให้ ผูกรถได้ที่หน้าพนักงาน",
  "locus.pull.title": "การดึงงาน",
  "locus.pull.switch": "ดึงอัตโนมัติ",
  "locus.pull.switchHint": "ทุก {n} นาที",
  "locus.pull.customer": "ลูกค้า",
  "locus.pull.contract": "สัญญา",
  "locus.pull.customerPh": "เลือกลูกค้า",
  "locus.pull.noCustomers": "ยังไม่มีลูกค้าในองค์กร",
  "locus.pull.createCustomer": "สร้างลูกค้า",
  "locus.pull.contractPickCustomer": "เลือกลูกค้าก่อน",
  "locus.pull.contractPh": "เลือกสัญญา",
  "locus.pull.noContracts": "ลูกค้านี้ยังไม่มีสัญญาที่ใช้งาน",
  "locus.pull.createContract": "สร้างสัญญา",
  "locus.pull.changeNote": "เปลี่ยนสัญญาแล้ว มีผลกับใบงานใหม่เท่านั้น",
  "locus.pull.needAccount": "ทดสอบบัญชี Locus ให้ผ่านก่อน",
  "locus.pull.needSetup": "เลือกลูกค้าและสัญญาก่อน",
  "locus.pull.contractGone": "สัญญา {contract} ไม่ได้ใช้งานแล้ว เลือกสัญญาใหม่",
  "locus.pull.saved": "บันทึกสัญญาแล้ว",
  "locus.pull.turnedOn": "เปิดการดึงงานอัตโนมัติแล้ว",
  "locus.pull.turnedOff": "ปิดการดึงงานอัตโนมัติแล้ว",
  "locus.pull.loadFailed": "โหลดรายชื่อลูกค้าและสัญญาไม่สำเร็จ",
  "locus.creds.passwordExpires": "รหัสผ่านหมดอายุ",
  "locus.confirm.title": "ดึงงานจาก Locus ตอนนี้?",
  "locus.confirm.body": "ระบบจะดึงเที่ยวและสถานะล่าสุดจาก Locus มาอัปเดตใบงาน",
  "locus.confirm.withLogin": "ต้องเข้าสู่ระบบ Locus ใหม่ก่อน (วันนี้ใช้ไป {n}/{max} ครั้ง) แล้วระบบจะดึงงานต่อให้เอง",
  "locus.confirm.go": "ดึงงาน",
  "locus.dialog.startedByLocus": "เริ่มวิ่งตาม Locus",
  "locus.dialog.completedByLocus": "เสร็จสิ้นตาม Locus",
  "locus.dialog.closedSuperseded": "ปิดเพราะคนขับเริ่มเที่ยวใหม่",
  "locus.dialog.openDays": "วันที่ยังมีใบเปิดค้าง",
  "locus.dialog.openMissing": "ใบเปิดที่ Locus ไม่ส่งกลับ",
  "locus.dialog.cancelledMissing": "ยกเลิกเพราะเที่ยวหายจาก Locus",
  "locus.dialog.slow": "รอบนี้ช้า — เกิน {min} นาที (ถ้าช้าแบบนี้ทั้งวัน ควรแบ่งรอบเป็นก้อน)",
  "locus.toast.queued": "เริ่มดึงงานแล้ว — ปิดหน้านี้ได้ ผลจะขึ้นในประวัติเมื่อเสร็จ",
  "locus.bar.scheduleSkip": "รอบอัตโนมัติทุก {every} นาทียังไม่ดึงองค์กรนี้: {reason}",
  "locus.skip.not_configured": "ยังไม่ได้เชื่อม Locus",
  "locus.skip.module_off": "ปิดโมดูล Makro Locus อยู่",
  "locus.skip.disabled": "ปิดการเชื่อมต่อ Locus อยู่",
  "locus.skip.no_account": "ยังไม่มีบัญชี Locus ที่ทดสอบผ่าน และ token หมดแล้ว — ใส่บัญชีที่ \"ตั้งค่า Locus\"",
  "locus.skip.no_contract": "ยังไม่ได้เลือกลูกค้าและสัญญาที่ใช้งาน",
  "locus.skip.login_blocked": "เข้าสู่ระบบ Locus ไม่ได้ตอนนี้ (ถูกพักหรือครบจำนวนครั้งของวัน)",
  "locus.skip.login_running": "กำลังเข้าสู่ระบบ Locus อยู่",
  "locus.skip.no_riders": "ยังไม่มีพนักงานที่ผูกรหัส Locus",
  "locus.skip.locked": "มีรอบอื่นกำลังวิ่งอยู่",
  "locus.skip.not_due": "ยังไม่ถึงรอบ",
  "emp.st.pending_start": "รอเริ่มงาน",
  "emp.st.trial": "ทดลองงาน",
  "emp.st.active": "ทำงาน",
  "emp.st.resigned": "ลาออก",
  "emp.st.terminated": "เลิกจ้าง",
  "emp.st.contract_end": "หมดสัญญา",
  "emp.why.trial": "เริ่มนับเป็นช่วงทดลองงาน",
  "emp.why.active": "ผ่านทดลองงานแล้ว หรือกลับมาทำงานตามปกติ",
  "emp.why.resigned": "พนักงานขอออกเอง",
  "emp.why.terminated": "บริษัทให้ออก",
  "emp.why.contract_end": "ครบสัญญาและไม่ต่อ",
  "emp.err.generic": "บันทึกสถานะไม่สำเร็จ ลองอีกครั้ง",
  "emp.sheet.until": "ทดลองงานถึง",
  "emp.sheet.untilPh": "ยังไม่ระบุ",
  "emp.sheet.untilHint": "ไม่ใส่ก็ได้ ถ้าใส่ ฝ่ายบุคคลจะได้รับการเตือนก่อนครบ 1 สัปดาห์",
  "emp.sheet.untilBad": "วันสุดท้ายของทดลองงานต้องอยู่หลังวันที่มีผล",
  "emp.history.until": "ถึง {day}",
  "emp.chip.label": "สถานะการทำงาน",
  "emp.chip.later": "{next} มีผล {moment}",
  "emp.action.change": "เปลี่ยนสถานะ",
  "emp.menu.label": "เมนูอื่นของพนักงาน",
  "emp.menu.off": "ปิดบัญชี (สร้างผิด)",
  "emp.menu.on": "เปิดบัญชีอีกครั้ง",
  "emp.off.note": "บัญชีนี้ถูกปิดไว้ เข้าระบบและรับงานไม่ได้ การปิดบัญชีไม่ใช่การออกจากงาน",
  "emp.off.noteAdmin": "เปิดอีกครั้งได้จากเมนู ⋯ มุมขวาบน",
  "emp.off.confirmTitle": "ปิดบัญชีของคนนี้?",
  "emp.off.confirmBody": "ใช้เมื่อสร้างบัญชีผิดเท่านั้น คนนี้จะหายจากรายชื่อและเข้าระบบไม่ได้ แต่ไม่นับเป็นการออกจากงาน ไม่มีวันทำงานวันสุดท้าย และไม่คิดเงินออกงาน ถ้าเขาลาออกจริง ให้ใช้ปุ่ม \"เปลี่ยนสถานะ\" แทน",
  "emp.off.confirmBtn": "ปิดบัญชี",
  "emp.on.confirmTitle": "เปิดบัญชีอีกครั้ง?",
  "emp.on.confirmBody": "คนนี้จะกลับมาอยู่ในรายชื่อและเข้าระบบได้ สถานะการทำงานไม่เปลี่ยน",
  "emp.on.confirmBtn": "เปิดบัญชี",
  "emp.toast.off": "ปิดบัญชีแล้ว",
  "emp.toast.on": "เปิดบัญชีแล้ว",
  "emp.toast.failed": "ทำไม่สำเร็จ ลองอีกครั้ง",
  "emp.sheet.title": "สถานะการทำงาน",
  "emp.sheet.changeTo": "เปลี่ยนเป็น",
  "emp.sheet.date": "วันที่มีผล",
  "emp.sheet.time": "เวลา",
  "emp.sheet.futureJobs": "ยังมีใบงาน {n} ใบหลังออกงาน — เปลี่ยนคนขับก่อน (ระบบไม่ยกเลิกให้ และจัดคนนี้ลงใบงานหลังออกงานไม่ได้)",
  "emp.sheet.futureJobsMore": "และอีก {n} ใบ",
  "emp.sheet.timeHint": "ไม่ใส่เวลา: วันนี้ = มีผลทันที · วันอื่น = เริ่มวันนั้นตั้งแต่ 00:00",
  "emp.sheet.now": "ตอนนี้ (กดบันทึก)",
  "emp.sheet.reason": "เหตุผล",
  "emp.sheet.reasonPh": "เล่าสั้น ๆ ว่าทำไม",
  "emp.sheet.save": "บันทึกการเปลี่ยน",
  "emp.sheet.saved": "บันทึกสถานะแล้ว",
  "emp.sheet.sentenceLeave": "ทำงานวันสุดท้าย {day} · เข้าระบบและรับงานไม่ได้ตั้งแต่ {moment}",
  "emp.sheet.sentenceStart": "มีผล {moment}",
  "emp.sheet.dailyNote": "เงินที่คิดเป็นรายวัน เช่น เงินเดือนตามสัดส่วนและค่าแรงรายวัน จะเริ่มใช้สถานะใหม่ตั้งแต่วันถัดไป",
  "emp.sheet.backMsg": "เวลาที่เลือกผ่านมาแล้ว สถานะใหม่จะมีผลทันที และอาจกระทบบิลหรือเงินเดือนที่คิดไปแล้ว บันทึกต่อหรือไม่",
  "emp.sheet.waitingTitle": "มีการเปลี่ยนที่รอมีผลอยู่",
  "emp.sheet.waitingHint": "ต้องยกเลิกการเปลี่ยนนี้ก่อน จึงจะตั้งการเปลี่ยนอื่นได้",
  "emp.sheet.cancel": "ยกเลิกการเปลี่ยนนี้",
  "emp.sheet.cancelled": "ยกเลิกแล้ว",
  "emp.sheet.cancelTitle": "ยกเลิกการเปลี่ยนนี้?",
  "emp.sheet.cancelMsg": "{status} ที่ตั้งไว้ มีผล {moment} จะไม่เกิดขึ้น",
  "emp.sheet.cancelKeep": "ไม่ยกเลิก",
  "emp.sheet.noNext": "ตอนนี้ไม่มีสถานะที่เปลี่ยนไปได้",
  "emp.sheet.settled": "ปิดยอดเงินของการออกงานนี้แล้ว จึงยกเลิกหรือแก้การออกงานไม่ได้ ถ้าต้องแก้ ให้ย้อนการปิดยอดที่หน้าการเงินของคนนี้ก่อน",
  "emp.history.startHint": "วันเริ่มงานแก้ที่ฟอร์มพนักงาน (ช่องวันเริ่มงาน)",
  "emp.sheet.settledLink": "ไปหน้าการเงินของคนนี้",
  "emp.sheet.leavingLine": "{status} มีผล {moment} · ทำงานวันสุดท้าย {day}",
  "emp.sheet.lastDay": "ทำงานวันสุดท้าย {day}",
  "emp.sheet.leaveLink": "ดูยอดที่ต้องจ่าย/ต้องทวง และปิดยอด",
  "emp.history.title": "ประวัติสถานะ",
  "emp.history.empty": "ยังไม่มีประวัติ",
  "emp.history.by": "บันทึกโดย {name} · {at}",
  "emp.history.byUnknown": "บันทึกเมื่อ {at}",
  "emp.history.mistake": "ลบ (บันทึกผิด)",
  "emp.history.mistakeTitle": "ลบรายการที่บันทึกผิด",
  "emp.history.mistakeMsg": "ลบ {status} ที่มีผล {moment} ออก เหมือนไม่เคยบันทึก — สถานะ วันทำงานวันสุดท้าย และค่าเที่ยวที่ถูกพักจะกลับเป็นแบบก่อนหน้า บันทึกการใช้งานยังเก็บไว้",
  "emp.history.mistakeGo": "ลบรายการนี้",
  "emp.history.fromOld": "จากข้อมูลเดิมในระบบ (ก่อนมีประวัติสถานะ)",
  "emp.badge.pending": "รอมีผล",
  "emp.badge.current": "ปัจจุบัน",
  "emp.badge.past": "ที่ผ่านมา",
  "emp.loadFailed": "โหลดสถานะไม่สำเร็จ",
  "salary.badge.pending": "รอมีผล",
  "salary.badge.current": "ใช้อยู่",
  "salary.badge.past": "เดิม",
  "salary.history.from": "มีผลตั้งแต่ {day}",
  "salary.cancelled": "ยกเลิกแล้ว",
  "salary.cancel.title": "ยกเลิกการเปลี่ยนเงินเดือนนี้?",
  "salary.cancel.msg": "เงินเดือน ฿{amount} ที่ตั้งไว้ มีผลตั้งแต่ {day} จะไม่เกิดขึ้น เงินเดือนที่ใช้อยู่ตอนนี้ไม่เปลี่ยน",
  "salary.cancel.go": "ยกเลิกการเปลี่ยน",
  "salary.mistake.title": "ลบรายการที่บันทึกผิด",
  "salary.mistake.msg": "ลบเงินเดือน ฿{amount} ที่มีผลตั้งแต่ {day} ออก เหมือนไม่เคยบันทึก เงินเดือนจะกลับเป็น ฿{prev} ใช้เฉพาะรายการที่บันทึกผิดเท่านั้น ถ้าเงินเดือนเปลี่ยนจริง ให้ตั้งเงินเดือนใหม่แทน",
  "salary.effective.label": "มีผลตั้งแต่",
  "salary.effective.note": "เงินเดือนนับเป็นรายวัน ตั้งแต่ 00:00 ของวันที่เลือก ถ้าวันนั้นอยู่กลางรอบจ่าย วันก่อนหน้าจ่ายตามเงินเดือนเดิม ตั้งแต่วันนั้นจ่ายตามเงินเดือนใหม่",
  "salary.effective.future": "ตั้งล่วงหน้า เงินเดือนใหม่จะเริ่มใช้เมื่อถึงวันนี้",
  "salary.diffOnDay": "{diff} ฿ จากเงินเดือนที่ใช้ในวันนั้น ({current} ฿)",
  "salary.noneOnDay": "ยังไม่มีเงินเดือนที่มีผลในวันนั้น",
  "salary.err.generic": "บันทึกเงินเดือนไม่สำเร็จ ลองอีกครั้ง",
  "salary.err.salary_before_history": "มีการเปลี่ยนเงินเดือนที่มีผลไปแล้วหลังวันนี้ เลือกวันที่ใหม่กว่านั้น",
  "salary.err.salary_pending_after": "มีการเปลี่ยนเงินเดือนที่ตั้งไว้ล่วงหน้าหลังวันนี้อยู่แล้ว ยกเลิกอันนั้นในประวัติก่อนจึงจะตั้งอันใหม่ได้",
  "salary.err.effective_in_effect": "รายการนี้มีผลไปแล้ว ยกเลิกไม่ได้ ถ้าบันทึกผิดให้ใช้ \"ลบ (บันทึกผิด)\" ที่รายการล่าสุด",
  "salary.err.effective_not_latest": "ลบได้เฉพาะรายการล่าสุดเท่านั้น",
  "salary.err.salary_only_change": "นี่คือเงินเดือนแรกของคนนี้ ลบไม่ได้ ให้ตั้งเงินเดือนใหม่แทน",
  // plan/employment-lifecycle §5.4 — ค่าดำเนินการ 5 ขั้น (opfee.*)
  "opfee.title": "ค่าดำเนินการ",
  "opfee.alias": "(ค่าคอม)",
  "opfee.notSet": "ไม่ได้ตั้ง",
  "opfee.what": "หักจากค่าเที่ยวของใบงานเท่านั้น ก่อนภาษี — ไม่หักจากเงินเดือน ค่าแรงรายวัน หรือรายการอื่น",
  "opfee.order": "ระบบหาจากเฉพาะที่สุดก่อน: ตัวคน → เส้นทาง → สัญญา → ลูกค้า → ค่าตั้งต้นของบริษัท",
  "opfee.level.employee": "ขั้น 1 จาก 5 · ตัวคน ตกลงพิเศษกับคนนี้ ชนะทุกขั้น",
  "opfee.level.route": "ขั้น 2 จาก 5 · เส้นทาง ใช้กับเที่ยวของเส้นทางนี้",
  "opfee.level.contract": "ขั้น 3 จาก 5 · สัญญา ใช้กับทุกคนที่วิ่งสัญญานี้",
  "opfee.level.customer": "ขั้น 4 จาก 5 · ลูกค้า ใช้กับทุกสัญญาของลูกค้ารายนี้",
  "opfee.level.company": "ขั้น 5 จาก 5 · ค่าตั้งต้นของบริษัท ใช้เมื่อไม่มีขั้นไหนตั้งไว้ (เริ่มที่ 0%)",
  "opfee.since": "ตั้งไว้ มีผลตั้งแต่ {moment}",
  "opfee.notSetHint": "ขั้นนี้ยังไม่ได้ตั้ง ระบบข้ามไปใช้ขั้นถัดไป ต่างจาก 0% ที่ตั้งใจไม่หัก",
  "opfee.ifNot": "ถ้าไม่ตั้งที่นี่ จะใช้ {pct} จาก{source}",
  "opfee.usingNow": "ตอนนี้ใช้ {pct} จาก{source}",
  "opfee.change": "เปลี่ยน",
  "opfee.inline.since": "ตั้งแต่ {moment}",
  "opfee.inline.pending": "จะเป็น {value} ตั้งแต่ {moment}",
  "opfee.changeLink": "เปลี่ยน…",
  "opfee.fieldLabel": "ค่าดำเนินการ (ค่าคอม)",
  "opfee.loadFailed": "โหลดค่าดำเนินการไม่สำเร็จ",
  "opfee.retry": "ลองอีกครั้ง",
  "opfee.pendingTitle": "ตั้งไว้ล่วงหน้า (รอมีผล)",
  "opfee.pendingRow": "{value} · มีผล {moment}",
  "opfee.cancel": "ยกเลิก",
  "opfee.history": "ประวัติ",
  "opfee.historyHide": "ซ่อนประวัติ",
  "opfee.historyEmpty": "ยังไม่เคยตั้งค่าที่ขั้นนี้",
  "opfee.value": "{pct}",
  "opfee.valueNone": "ไม่ตั้งที่ขั้นนี้ (ใช้ขั้นถัดไป)",
  "opfee.from": "มีผล {moment}",
  "opfee.by": "บันทึกโดย {name} · {at}",
  "opfee.byUnknown": "บันทึกเมื่อ {at}",
  "opfee.fromOld": "จากข้อมูลเดิมในระบบ",
  "opfee.badge.pending": "รอมีผล",
  "opfee.badge.current": "ใช้อยู่",
  "opfee.badge.past": "ที่ผ่านมา",
  "opfee.mistake": "ลบ (บันทึกผิด)",
  "opfee.mistakeTitle": "ลบรายการที่บันทึกผิด",
  "opfee.mistakeMsg": "ลบ {value} ที่มีผล {moment} ออก เหมือนไม่เคยบันทึก ค่าจะกลับเป็นแบบก่อนหน้า บิลที่ยังเปิดอยู่คิดใหม่ บิลที่แช่แข็งหรือจ่ายแล้วไม่เปลี่ยน",
  "opfee.mistakeGo": "ลบรายการนี้",
  "opfee.cancelTitle": "ยกเลิกค่าที่ตั้งไว้ล่วงหน้า",
  "opfee.cancelMsg": "ยกเลิก {value} ที่จะมีผล {moment} ค่าที่ใช้อยู่ตอนนี้ไม่เปลี่ยน",
  "opfee.cancelKeep": "เก็บไว้",
  "opfee.sheet.title": "ค่าดำเนินการ",
  "opfee.sheet.choose": "ค่าที่ขั้นนี้",
  "opfee.sheet.setTo": "ตั้งเป็น",
  "opfee.sheet.pct": "เปอร์เซ็นต์",
  "opfee.sheet.unset": "ไม่ตั้งที่ขั้นนี้ (ใช้ขั้นถัดไป)",
  "opfee.sheet.unsetWhy": "ขั้นนี้ไม่หัก ให้ไปใช้ {pct} จาก{source} แทน",
  "opfee.sheet.zeroNote": "0% คือตั้งใจไม่หัก และชนะขั้นที่ต่ำกว่า ต่างจาก \"ไม่ตั้ง\"",
  "opfee.sheet.date": "วันที่มีผล",
  "opfee.sheet.time": "เวลา (ไม่ใส่ = 00:00)",
  "opfee.sheet.timeHint": "ใบงานที่เริ่มเที่ยวตั้งแต่วันและเวลานี้เป็นต้นไปจะใช้ค่าใหม่ เวลาเป็นเวลาไทย",
  "opfee.sheet.reason": "เหตุผล (ไม่บังคับ)",
  "opfee.sheet.reasonPh": "เช่น ตกลงใหม่กับลูกค้า",
  "opfee.sheet.save": "บันทึก",
  "opfee.sheet.now": "ตั้งแต่ตอนนี้",
  "opfee.sheet.sentence": "{what} มีผล {moment}",
  "opfee.sheet.sentenceSet": "ตั้งเป็น {pct}",
  "opfee.sheet.sentenceUnset": "ไม่ตั้งที่ขั้นนี้",
  "opfee.sheet.saved": "บันทึกค่าดำเนินการแล้ว",
  "opfee.sheet.cancelled": "ยกเลิกแล้ว",
  "opfee.sheet.backMsg": "วันและเวลาที่เลือกผ่านมาแล้ว บิลที่ยังเปิดอยู่จะคิดค่าดำเนินการใหม่ตามค่านี้ ส่วนบิลที่แช่แข็งหรือจ่ายแล้วไม่เปลี่ยน บันทึกต่อหรือไม่",
  "opfee.err.generic": "บันทึกค่าดำเนินการไม่สำเร็จ ลองอีกครั้ง",
  "opfee.rates.title": "อัตราที่ใช้จริง",
  "opfee.rates.show": "ดูอัตราที่หักจริงในแต่ละสัญญา (90 วันล่าสุด)",
  "opfee.rates.hide": "ซ่อนอัตราที่หักจริง",
  "opfee.rates.lead": "คนนี้วิ่งสัญญาไหน เส้นทางไหน โดนหักค่าดำเนินการเท่าไร (เที่ยวใน 90 วันล่าสุด)",
  "opfee.rates.contract": "สัญญา",
  "opfee.rates.route": "เส้นทาง",
  "opfee.rates.trips": "เที่ยว (90 วัน)",
  "opfee.rates.rate": "อัตรา",
  "opfee.rates.source": "มาจาก",
  "opfee.rates.empty": "ยังไม่มีเที่ยวใน 90 วันล่าสุด",
  "opfee.rates.own": "ตั้งที่ตัวคนนี้เอง: {pct}",
  "opfee.legacy.title": "ลูกค้า {n} รายมีค่าคอมเดิมค้างอยู่ ยังไม่ถูกหัก",
  "opfee.legacy.lead": "ค่านี้เคยกรอกไว้ที่ลูกค้าแต่ระบบไม่เคยหัก เลือกรายที่ต้องการแล้วกดยืนยัน ระบบจะเริ่มหักเป็นค่าดำเนินการขั้นลูกค้าตั้งแต่วันที่เลือก ไม่มีรายการไหนถูกเปลี่ยนเอง",
  "opfee.legacy.date": "มีผลตั้งแต่",
  "opfee.legacy.confirm": "ยืนยันเป็นค่าดำเนินการ",
  "opfee.legacy.all": "เลือกทั้งหมด",
  "opfee.legacy.done": "ยืนยันแล้ว {n} ราย",
  "opfee.legacy.failed": "ยืนยันไม่สำเร็จ ลองอีกครั้ง",
  "opfee.bill.line": "ค่าดำเนินการ {rate}% ของ {base} · จาก{source}",
  "opfee.row.tag": "ค่าดำเนินการ {pct} (จาก{source}) = {fee}",
  "opfee.companyEffect": "ใช้กับทุกใบงานที่ขั้นตัวคน เส้นทาง สัญญา และลูกค้าไม่ได้ตั้งไว้",
  "opfee.companyNever": "ยังไม่เคยตั้ง เริ่มที่ 0% คือไม่หัก",
  "hr.hireDateSynced": "แก้วันนี้แล้ว วันเริ่มงานในประวัติสถานะเลื่อนตามด้วย",
  "pdv.err.generic": "บันทึกไม่สำเร็จ ลองอีกครั้ง",
  "pdv.when": "มีผลตั้งแต่",
  "pdv.whenNow": "ทันที",
  "pdv.time": "เวลา",
  "pdv.laterNote": "ค่าใหม่จะรออยู่และมีผลตั้งแต่ {moment} ก่อนถึงตอนนั้นยังใช้ค่าปัจจุบัน",
  "pdv.payeeNote": "ไฟล์โอนของรอบจ่ายใช้บัญชีที่มีผลในวันจ่ายของรอบนั้น ไม่ใส่วัน = มีผลทันที",
  "pdv.roleNote": "ไม่ใส่วัน = มีผลทันทีตอนบันทึก ตั้งล่วงหน้าได้ครั้งละหนึ่งรายการ",
  "pdv.typeReleasesVehicleNow": "ประเภทนี้ไม่ใช่คนขับ ระบบจะคืนรถประจำ {plate} ให้ตอนบันทึก ใบงานที่ถือรถคันนี้อยู่ไม่ถูกแตะ",
  "pdv.typeReleasesVehicleLater": "ประเภทนี้ไม่ใช่คนขับ รถประจำ {plate} จะถูกคืนเมื่อถึง {moment} ใบงานที่ถือรถคันนี้อยู่ไม่ถูกแตะ",
  "pdv.typeNote": "ไม่ใส่วัน = มีผลทันทีตอนบันทึก ตั้งล่วงหน้าได้ครั้งละหนึ่งรายการ",
  "pdv.reason": "เหตุผล (ไม่บังคับ)",
  "pdv.reasonPh": "เช่น เปลี่ยนบัญชีตามที่พนักงานแจ้ง",
  "pdv.waitingTitle": "รอมีผล {moment}",
  "pdv.cancelWaiting": "ยกเลิก",
  "pdv.cancelled": "ยกเลิกแล้ว ค่าปัจจุบันไม่เปลี่ยน",
  "payChange.colTicket": "ใบงาน",
  "payChange.colDay": "วันที่",
  "payChange.colFrom": "ค่าเดิม",
  "payChange.colTo": "ค่าใหม่",
  "payChange.more": "และอีก {n} ใบ",
  "payChange.loading": "กำลังดูว่าใบงานไหนค่าเที่ยวเปลี่ยน…",
  "payChange.loadFailed": "ดูรายการใบงานที่ค่าเที่ยวเปลี่ยนไม่ได้",
  "payChange.typeTitle": "เปลี่ยนประเภทแล้ว ค่าเที่ยวจะเปลี่ยน",
  "payChange.typeCancelTitle": "ยกเลิกแล้ว ค่าเที่ยวจะกลับเป็นค่าเดิม",
  "payChange.typeQuiet": "ไม่มีใบงานที่ค่าเที่ยวเปลี่ยน",
  "payChange.typeIntro": "ใบงาน {n} ใบ ตั้งแต่ {day} จะคิดค่าเที่ยวใหม่ตามประเภทของวันนั้น รวม {delta}",
  "payChange.closedNote": "{n} ใบอยู่ในรอบจ่ายที่แช่แข็งหรือจ่ายแล้ว ระบบไม่เปลี่ยน ถ้าต้องปรับ ให้เพิ่มรายการเงินในรอบถัดไป",
  "payChange.closedShould": "ไม่เปลี่ยน (ควรเป็น {to})",
  "payChange.manualNote": "ใบที่กรอกค่าเที่ยวเอง ใบที่ยกเลิก และใบที่ยังไม่มีคนขับ ไม่ถูกแตะ",
  "payChange.typeGo": "บันทึกและคิดค่าเที่ยวใหม่",
  "payChange.typeCancelGo": "ยกเลิกและคิดค่าเที่ยวใหม่",
  "reprice.button": "คิดค่าเที่ยวใหม่",
  "reprice.title": "คิดค่าเที่ยวใหม่ตามค่าปัจจุบัน",
  "reprice.intro": "คิดค่าเที่ยวของใบงานที่เลือกใหม่ ตามค่าที่มีผลของวันใบงานแต่ละใบ: ค่าของเส้นทาง (เวอร์ชันที่มีผล) ค่าเที่ยวรายคน และประเภทพนักงานของวันนั้น",
  "reprice.summary": "เปลี่ยน {n} ใบ รวม {delta}",
  "reprice.nothing": "ไม่มีใบงานที่ค่าเที่ยวเปลี่ยน",
  "reprice.go": "ใช้ค่าใหม่กับ {n} ใบ",
  "reprice.done": "คิดค่าเที่ยวใหม่แล้ว {n} ใบ",
  "reprice.progress": "กำลังบันทึก {done}/{total} ใบ",
  "reprice.failed": "คิดค่าเที่ยวใหม่ไม่สำเร็จ",
  "reprice.tooMany": "เลือกได้ไม่เกิน {max} ใบต่อครั้ง",
  "reprice.outcome.CHANGE": "เปลี่ยน",
  "reprice.outcome.SAME": "ค่าเท่าเดิม",
  "reprice.outcome.MANUAL": "กรอกค่าเอง ไม่แตะ",
  "reprice.outcome.IN_CLOSED_ROUND": "อยู่ในรอบจ่ายที่แช่แข็งหรือจ่ายแล้ว ไม่แตะ",
  "reprice.outcome.CANCELLED": "ยกเลิกแล้ว",
  "reprice.outcome.NO_DRIVER": "ยังไม่มีคนขับ",
  "reprice.outcome.NOT_PRICED": "ยังไม่มีค่าเที่ยว (ดูหน้าใบงานที่ยังไม่มีค่าเที่ยว)",
  "reprice.outcome.UNPRICEABLE": "คิดไม่ได้",
  "reprice.outcome.NOT_FOUND": "ไม่พบใบงาน",
  "ops.versionApplyExisting": "ใช้กับใบงานที่มีอยู่",
  "ops.versionStaleBanner": "มี {n} ใบงานในช่วงของเวอร์ชันนี้ ยังใช้เวอร์ชันเก่าอยู่",
  "ops.versionApplyTitle": "ใช้เวอร์ชัน {n} กับใบงานที่มีอยู่",
  "ops.versionRowClosed": "แก้ไม่ได้ อยู่ในรอบจ่ายที่แช่แข็งหรือจ่ายแล้ว",
  "ops.versionCode.IN_CLOSED_ROUND": "ค่าเที่ยวของใบนี้อยู่ในรอบจ่ายที่แช่แข็งหรือจ่ายแล้ว ระบบไม่แตะ",
  "pdv.cancelTitle": "ยกเลิกการเปลี่ยนที่รออยู่",
  "pdv.cancelMsg": "ยกเลิก \"{text}\" ที่จะมีผล {moment} ค่าปัจจุบันไม่เปลี่ยน",
  "pdv.keep": "ไม่ยกเลิก",
  "pdv.mistake": "บันทึกผิด",
  "pdv.mistakeTitle": "ย้อนรายการล่าสุด",
  "pdv.mistakeMsg": "ย้อน \"{text}\" ที่มีผลตั้งแต่ {moment} กลับไปเป็นค่าก่อนหน้า ใช้เมื่อบันทึกผิดเท่านั้น",
  "pdv.mistakeGo": "ย้อนรายการนี้",
  "pdv.mistakeDone": "ย้อนรายการแล้ว",
  "pdv.showHistory": "ดูประวัติ {n} รายการ",
  "pdv.hideHistory": "ซ่อนประวัติ",
  "pdv.from": "ใช้ตั้งแต่ {moment}",
  "pdv.payeeHistory": "ประวัติบัญชีรับเงิน",
  "pdv.tab": "ประวัติ",
  "pdv.tabLead": "ทุกการเปลี่ยนของคนนี้ เรียงจากใหม่ไปเก่า",
  "pdv.topic.label": "เลือกเรื่อง",
  "pdv.topic.all": "ทั้งหมด",
  "pdv.topic.status": "สถานะ",
  "pdv.topic.salary": "เงินเดือน",
  "pdv.topic.payee": "บัญชีรับเงิน",
  "pdv.topic.finance": "การเงิน",
  "pdv.topic.role": "สิทธิ์/ประเภท",
  "pdv.topic.vehicle": "รถ",
  "pdv.topic.depot": "ศูนย์",
  "pdv.topic.other": "อื่น ๆ",
  "pdv.timelineFailed": "โหลดประวัติไม่สำเร็จ ลองอีกครั้ง",
  "pdv.timelineEmpty": "ยังไม่มีการเปลี่ยนในเรื่องนี้",
  "pdv.byName": "โดย {name}",
  "pdv.byUnknown": "ไม่ทราบว่าใครเป็นผู้ทำ",
  "pdv.effective": "มีผล {moment}",
  "pdv.loadMore": "โหลดเพิ่ม",
  "settings.searchOrgsCount": "ค้นหาจาก {n} องค์กร",
  "payBatches.x.debitOption": "กสิกรไทย {account}",
  "portalScope.status.expired": "หมดอายุแล้ว",
  "portalScope.status.suspended": "ระงับ",
  "portalScope.status.draft": "ร่าง",
  "portalScope.saved": "บันทึกขอบเขตแล้ว",
  "portalScope.searchPlaceholder": "ค้นหาสัญญาหรือเส้นทาง",
  "portalScope.contractsCount": "{n} สัญญา",
  "portalScope.pendingAdds": "+{n} รอบันทึก",
  "portalScope.collapseAll": "หุบทั้งหมด",
  "portalScope.expandAll": "ขยายทั้งหมด",
  "portalScope.addContract": "เพิ่มสัญญา",
  "portalScope.colCode": "รหัส",
  "portalScope.colCustomerName": "ลูกค้า / ชื่อ",
  "portalScope.colRoutes": "เส้นทาง",
  "portalScope.emptySearch": "ไม่พบสัญญาหรือเส้นทางที่ตรงกับคำค้นในขอบเขตนี้",
  "portalScope.empty": "Portal นี้ยังไม่มีสัญญา — กด “เพิ่มสัญญา” เพื่อเริ่ม",
  "portalScope.contractRange": "สัญญา {from}–{to} จาก {total}",
  "portalScope.unsavedPrefix": "ยังไม่บันทึก —",
  "portalScope.draftAdd": "เพิ่ม {n} สัญญา",
  "portalScope.draftRemove": "เอาออก {n} สัญญา",
  "portalScope.draftRoutes": "เส้นทางเปลี่ยน {n} รายการ",
  "portalScope.discard": "ยกเลิกการแก้ไข",
  "portalScope.pendingBadge": "รอบันทึก",
  "portalScope.noRoutesOpen": "⚠ ยังไม่ได้เปิดเส้นทางใด — ผู้โดยสารไม่เห็นสัญญานี้",
  "portalScope.removeContract": "เอาสัญญาออกจาก Portal",
  "portalScope.toggleRoutesPage": "เปิดหรือปิดเส้นทาง {n} รายการในหน้านี้",
  "portalScope.toggleRoutesAll": "เปิดหรือปิดเส้นทางทั้งหมดของสัญญานี้",
  "portalScope.toggleRoutesPageHint": "ทำกับ {n} รายการที่แสดงอยู่ในหน้านี้",
  "portalScope.routeSearchPlaceholder": "ค้นหาเส้นทางในสัญญานี้",
  "portalScope.openOf": "เปิดอยู่ {open} จาก {total}",
  "portalScope.noRoutesMatch": "ไม่มีเส้นทางที่ตรงกับคำค้นในสัญญานี้",
  "portalScope.stops": "{n} จุดจอด",
  "portalScope.routeRange": "เส้นทาง {range} จาก {total}",
  "portalScope.pick.title": "เพิ่มสัญญาเข้า Portal",
  "portalScope.pick.placeholder": "รหัส/ชื่อลูกค้า หรือชื่อเส้นทาง",
  "portalScope.pick.matchedRoutes": "ตรงกับเส้นทาง {routes}",
  "portalScope.pick.routesN": "{n} เส้นทาง",
  "portalScope.pick.allListed": "ทุกสัญญาที่ตรงกับคำค้นอยู่ในรายการแล้ว",
  "portalScope.pick.none": "ไม่พบสัญญาที่ตรงกับคำค้น",
  "portalScope.pick.alreadyIn": "อยู่ในรายการแล้ว:",
  "portalScope.pick.selected": "เลือก {n} สัญญา · {routes} เส้นทางจะถูกเปิดให้เห็น",
  "portalScope.pick.nothing": "ยังไม่ได้เลือก",
  "hrLeave.cal.noOne": "ไม่มีใครลาในเดือน {month}",
  "hrLeave.cal.cellTitle": "{name} · {type} ({status}) {days} · {from}–{to}",
  "hrLeave.cal.hasFiles": " · 📎 มีไฟล์แนบ",
  "hrLeave.cal.legendHalf": "ช่องเต็ม = ลาทั้งวัน · ครึ่งซ้าย = ครึ่งเช้า · ครึ่งขวา = ครึ่งบ่าย",
  "hrLeave.rejected": "ปฏิเสธแล้ว",
  "hrLeave.rejectFailed": "ปฏิเสธไม่สำเร็จ",
  "hrLeave.failed": "ไม่สำเร็จ",
  "hrLeave.bulkApproved": "อนุมัติแล้ว {n} ใบ",
  "hrLeave.bulkMixed": "สำเร็จ {ok} · ไม่สำเร็จ {failed} — {first}",
  "hrLeave.cancelled": "ยกเลิกคำขอแล้ว",
  "hrLeave.cancelFailed": "ยกเลิกไม่สำเร็จ",
  "hrLeave.bulkTitle": "อนุมัติ {n} คำขอ?",
  "hrLeave.bulkTotal": "รวม {days} วัน",
  "hrLeave.bulkPerson": "· {name} · {days} วัน",
  "hrLeave.bulkMore": "· และอีก {n} ใบ",
  "hrLeave.bulkClash": "⚠ {n} ใบชนกับงานที่จ่ายไว้แล้ว ({names})",
  "hrLeave.bulkNote": "แต่ละใบตรวจโควตาและวันทับซ้อนของตัวเองอีกครั้ง ใบที่ไม่ผ่านจะถูกข้ามและรายงานกลับมา",
  "hrLeave.bulkConfirm": "อนุมัติทั้งหมด",
  "hrLeave.rejectReason": "เหตุผลที่ไม่อนุมัติ (optional)",
  "hrLeave.rejectConfirm": "ยืนยันปฏิเสธ",
  "hrLeave.selectedN": "เลือกไว้ {n} ใบ",
  "hrLeave.selectedTotal": " · รวม {days} วัน",
  "hrLeave.clearSelection": "ล้างที่เลือก",
  "hrLeave.approveSelected": "อนุมัติที่เลือก",
  "hrLeave.selectRow": "เลือกคำขอของ {name}",
  "hrLeave.overEntitlement": "เกินสิทธิ์",
  "hrLeave.decidedBy": "โดย {name}",
  "hrLeave.cancelMessage": "{name} · {type} {days} — วันลาจะคืนเข้าโควตาทันที",
  "hrLeave.cancelConfirm": "ยกเลิกคำขอ",
  "hrLeave.rowMenu": "เมนูของคำขอ {name}",
  "hrLeave.openProfile": "เปิดโปรไฟล์พนักงาน",
  "hrLeave.fileTypeNotAllowed": "ชนิดไฟล์นี้แนบไม่ได้",
  "hrLeave.fileTooBig": "ไฟล์ใหญ่เกิน {mb} MB",
  "hrLeave.uploadFailed": "อัปโหลดไม่สำเร็จ",
  "hrLeave.endBeforeStart": "วันสิ้นสุดต้องไม่ก่อนวันเริ่ม",
  "hrLeave.singleDayHalfOnly": "ลาวันเดียวเลือกได้แค่ครึ่งเช้าหรือครึ่งบ่าย",
  "hrLeave.leaveDate": "วันที่ลา",
  "hrLeave.firstDay": "วันแรก",
  "hrLeave.lastDay": "วันสุดท้าย",
  "hrLeave.totalDays": "รวม {days} วัน",
  "hrLeave.sickNeedsDoc": "ลาป่วยตั้งแต่ {n} วัน ต้องแนบเอกสาร",
  "hrLeave.chooseFile": "เลือกไฟล์",
  "hrLeave.removeFile": "เอา {name} ออก",
  "hrLeave.otherNeedsReason": "ประเภท “อื่นๆ” ต้องบอกเหตุผล เพราะชื่อประเภทไม่ได้บอกอะไรเลย",
  "hrLeave.decider": "ผู้ตัดสิน",
  "hrLeave.pendingBadge": "รอ {days} วัน",
  "hrLeave.usedSummary": "ใช้แล้ว {used} · รออนุมัติ {pending} · ใช้ได้ {available}",
  "hrLeave.patternTitle": "รูปแบบการลา ปี {year}",
  "hrLeave.monthTitle": "{month} · {days} วัน",
  "hrLeave.loadingBalance": "กำลังโหลดโควตาวันลา…",
  "hrLeave.decisionApproveMsg": "{type} {from} – {to} จะถูกอนุมัติและหักวันลาออกจากโควตา",
  "hrLeave.decisionRejectMsg": "{type} {from} – {to} จะถูกปฏิเสธ",
  "hrLeave.submitRequest": "ส่งคำขอ",
  "err.title": "เกิดข้อผิดพลาดบนระบบ",
  "err.body": "ระบบขัดข้อง ทีมงานได้รับแจ้งแล้ว กรุณาลองใหม่อีกครั้ง",
  "err.reference": "รหัสอ้างอิง: {ref}",
  "err.toDashboard": "กลับไปหน้าแดชบอร์ด",
  "notFound.title": "ไม่พบหน้าที่คุณต้องการ",
  "notFound.body": "ลิงก์อาจผิด หรือหน้านี้ถูกย้ายไปแล้ว",
  "routeTip.outbound": "ขาไป",
  "routeTip.return": "ขากลับ",
  "routeTip.pairedWith": "คู่กับ",
  "routeTip.stopN": "จุดที่ {n} · {name}",
  "tracking.parkedSuffix": "จุดจอด",
  "optimizer.routeCreated": "สร้างเส้นทาง \"{code}\" สำเร็จ",
  "optimizer.saveFailed": "บันทึกไม่สำเร็จ",
  "roles.emptyTitle": "ยังไม่มีบทบาทในระบบ",
  "roles.emptyBody": "สร้างบทบาทเพื่อกำหนดสิทธิ์ให้สมาชิกในองค์กร เช่น ผู้จัดการกองรถ คนขับ ฝ่ายบัญชี",
  "hr.noEmployeesFound": "ไม่พบข้อมูลพนักงาน",
  "hr.licenseTypeExample": "เช่น ท.1, ท.2, ท.4",
  "hr.licenseIssuerExample": "เช่น กรมการขนส่งทางบก",
  "detour.title": "ย้ายผู้โดยสารไป {vehicle}",
  "detour.step1": "1. จุดรับ",
  "detour.step2": "2. ยืนยัน",
  "detour.moving": "ย้ายผู้โดยสาร",
  "detour.countPeople": "{n} คน",
  "detour.overCapacity": "เลือก {n} คน เกินที่ว่างของ {vehicle} ({seats} ที่)",
  "detour.choosePickup": "เลือกจุดที่รถ {vehicle} จะรับ",
  "detour.stop": "จุดจอด",
  "detour.walk": "ผู้โดยสารเดิน",
  "detour.vehiclePath": "เส้นทางรถ",
  "detour.unchanged": "ไม่เปลี่ยน",
  "detour.pickupPoint": "จุดรับ",
  "detour.walkNone": "0 ม. (ขึ้นที่เดิม)",
  "detour.pathAdded": "เส้นทางรถเพิ่ม",
  "detour.pathAddedValue": "{dist} · ~{min} นาที (ประมาณ)",
  "detour.customHint": "เลือกตำแหน่งบนแผนที่ (เปิดใช้เมื่อเชื่อมระบบเส้นทาง)",
  "detour.origBoarding": "จุดขึ้นเดิม",
  "detour.newPickup": "จุดรับใหม่",
  "detour.vehicleStops": "รถแวะรับ",
  "detour.legendRoute": "━ เส้นทาง {vehicle}",
  "detour.legendOrig": "● จุดขึ้นเดิม",
  "detour.legendPickup": "● จุดรับ",
  "detour.fromTrip": "จากเที่ยว",
  "detour.toTrip": "ไปเที่ยว",
  "detour.method": "วิธีรับ",
  "detour.notify": "แจ้งเตือน",
  "detour.notifyPax": "ผู้โดยสาร {n} คน",
  "detour.notifyPaxMsg": "“รถของคุณเปลี่ยนเป็น {vehicle} จุดขึ้นและเวลาเดิม”",
  "detour.notifyDriver": "คนขับ {vehicle}",
  "detour.notifyDriverMsg": "“เพิ่มจุดรับผู้โดยสาร {n} คน”",
  "detour.previewNote": "ตัวอย่าง · บันทึกจริงเมื่อเชื่อมระบบ",
  "comms.ann.published": "เผยแพร่ประกาศแล้ว",
  "comms.ann.editSaved": "บันทึกการแก้ไขแล้ว",
  "comms.ann.saveFailed": "บันทึกไม่สำเร็จ",
  "comms.ann.audAll": "ทั้งหมด",
  "comms.ann.audDrivers": "คนขับ",
  "comms.ann.audPassengers": "ผู้โดยสาร",
  "comms.ann.prioNormal": "ปกติ",
  "comms.ann.prioImportant": "สำคัญ",
  "comms.ann.prioUrgent": "เร่งด่วน",
  "comms.ann.back": "กลับ",
  "comms.ann.edit": "แก้ไข",
  "comms.ann.editTitle": "แก้ไขประกาศ",
  "comms.ann.read": "อ่านแล้ว",
  "comms.ann.readRate": "อัตราอ่าน",
  "comms.ann.formNew": "สร้างประกาศใหม่",
  "comms.ann.subject": "หัวข้อ",
  "comms.ann.subjectEx": "เช่น แจ้งหยุดวันหยุดพิเศษ...",
  "comms.ann.body": "เนื้อหา",
  "comms.ann.bodyPh": "รายละเอียดประกาศ...",
  "comms.ann.image": "รูปภาพ (URL)",
  "comms.ann.audience": "กลุ่มเป้าหมาย",
  "comms.ann.priority": "ระดับความสำคัญ",
  "comms.ann.schedule": "ตั้งเวลาเผยแพร่",
  "comms.ann.saveAndPublish": "บันทึกและเผยแพร่",
  "comms.ann.publishNow": "เผยแพร่ทันที",
  "comms.ann.save": "บันทึก",
  "comms.ann.saveDraft": "บันทึกแบบร่าง",
  "comms.ann.none": "ไม่มีประกาศ",
  "audit.act.re_resolve": "คำนวณราคาใหม่",
  "audit.act.pay_decision": "ตัดสินการจ่าย",
  "audit.act.lock": "ล็อกรอบ",
  "audit.act.auto_void_on_cancel": "ยกเลิกอัตโนมัติ",
  "audit.act.salary_profile_update": "แก้เงินเดือน",
  "audit.act.login": "เข้าสู่ระบบ",
  "audit.act.login_failed": "เข้าสู่ระบบไม่สำเร็จ",
  "audit.act.logout": "ออกจากระบบ",
  "audit.act.fuel_draft_created": "เริ่มบันทึกการเติมน้ำมัน",
  "audit.act.fuel_step_captured": "ถ่ายรูปการเติมน้ำมันแล้ว",
  "audit.act.fuel_step_failed": "ปฏิเสธรูปการเติมน้ำมัน",
  "audit.act.fuel_submitted": "ส่งรายการเติมน้ำมันแล้ว",
  "audit.act.fuel_submit_failed": "ปฏิเสธรายการเติมน้ำมัน",
  "audit.act.fuel_discarded": "ทิ้งรายการเติมน้ำมัน",
  "audit.act.fuel_log_approved": "อนุมัติการเติมน้ำมัน",
  "audit.act.fuel_log_rejected": "ไม่อนุมัติการเติมน้ำมัน",
  "audit.act.fuel_log_suspicious": "ทำเครื่องหมายน่าสงสัย",
  "audit.sum.signedIn": "{who} เข้าสู่ระบบ",
  "audit.sum.signInFailed": "{who} เข้าสู่ระบบไม่สำเร็จ",
  "audit.sum.signedOut": "ออกจากระบบ",
  "audit.sum.revokeFailed": "ยกเลิกโทเคนไม่สำเร็จ",
  "audit.sum.repriced": "คำนวณราคาใหม่ {n} เที่ยว · ยอดรวม {delta}",
  "audit.sum.locked": "ล็อกรอบ {period} · ต้องจ่าย {payable} ({paid} เที่ยว ตัดออก {excluded} เที่ยว)",
  "audit.sum.opCostVoided": "ยกเลิกค่าใช้จ่ายหน้างาน {amount} (ใบงานถูกยกเลิก)",
  "audit.sum.baseSalary": "เงินเดือนฐาน {from} → {to}",
  "audit.sum.rateVoided": "ยกเลิกเรท {amount} ({from} → {to})",
  "audit.sum.openEnded": "ไม่กำหนดสิ้นสุด",
  "audit.sum.rateSet": "ตั้งเรท {scope} {amount} ตั้งแต่ {from}",
  "audit.sum.rateTo": "ถึง {to}",
  "audit.sum.rateTrimmed": "(ปิดเวอร์ชันก่อนหน้า {n} รายการ)",
  "audit.sum.payRun": "รอบจ่าย {period} — {n} ใบงาน สุทธิ {net}",
  "audit.sum.dirReimburse": "รายการเบิก",
  "audit.sum.dirDeduct": "รายการหัก",
  "audit.sum.dirCharge": "ค่าใช้จ่าย",
  "audit.sum.chargeAdded": "เพิ่ม{dir} {amount}{type}",
  "audit.sum.chargeRemoved": "ลบ{dir} {amount}{type}",
  "audit.sum.chargeApproved": "อนุมัติรายการแล้ว",
  "audit.sum.chargeRevoked": "ยกเลิกการอนุมัติรายการ",
  "audit.sum.via": "(ผ่าน {via})",
  "audit.sum.updated": "แก้ไข {fields}{more}",
  "audit.sum.moreFields": "และอีก {n} รายการ",
  "audit.sum.ticketCreated": "สร้างใบงาน",
  "audit.exported": "ส่งออก {n} รายการเป็น .xlsx แล้ว",
  "audit.loadFailed": "โหลดบันทึกการใช้งานไม่สำเร็จ: {error}",
  "audit.beforeAfter": "ก่อน → หลัง",
  "audit.client": "อุปกรณ์",
  "common.editNamed": "แก้ไข {name}",
  "common.deleteNamed": "ลบ {name}",
  "hr.deleteRateBtn": "ลบเรท",
  "ticketPay.amountBaht": "จำนวนเงิน (฿)",
  "ticketPay.chargeAdded": "เพิ่มแล้ว: {type} ฿{amount}",
  "ticketPay.dirReimburseSub": "เบิก — เพิ่มเข้ายอดจ่ายคนขับ",
  "ticketPay.dirDeductSub": "หัก — หักออกจากยอดจ่ายคนขับ",
  "ticketPay.editChargeMenu": "แก้ไขรายการ…",
  "ticketPay.compCharges": "ค่าเที่ยว ฿{comp}{mult} {sign} รายการ ฿{charges}",
  // plan/distance-pay audit — the lines here pay into (or come off) the driver's net: not "operating expenses"
  "ticketPay.linesTitle": "รายการเงินเพิ่ม/หักของใบงาน",
  "ticketPay.linesEmpty": "ใบงานนี้ยังไม่มีรายการเงินเพิ่มหรือหัก ·",
  "ticketPay.stopOption": "จุดที่ {n}: {name}",
  "ticketPay.stopOptionSub": "ตามแผน {time} • จองแล้ว {booked} คน",
  "rateConfirm.title": "เปลี่ยนคนขับ — จะใช้เรทค่าตอบแทนไหน?",
  "rateConfirm.body": "เรทปัจจุบันถูกตั้งเองเป็น {current} ส่วนเรท {tier} ของคนขับคนใหม่คือ {next}",
  "rateConfirm.keep": "คงไว้ ฿{amount}",
  "rateConfirm.use": "ใช้เรท {tier} ฿{amount}",
  "hr.unit.perMonth": "฿{amount}/เดือน",
  "hr.unit.perDay": "฿{amount}/วัน",
  "hr.unit.perTrip": "฿{amount}/เที่ยว",
  "hr.unit.perHour": "฿{amount}/ชม. ({mult}×)",
  "empImport.uploadFirst": "กรุณาอัปโหลดไฟล์ก่อน",
  "empImport.noValidRows": "ไม่มีแถวที่นำเข้าได้",
  "empImport.done": "นำเข้าพนักงาน {n} คนเรียบร้อยแล้ว",
  "empImport.checking": "กำลังตรวจ...",
  "empImport.preview": "ตรวจสอบก่อนนำเข้า",
  "empImport.importing": "กำลังนำเข้า...",
  "empImport.import": "นำเข้า",
  "empImport.importRows": "นำเข้า ({n} แถว)",
  "empImport.browse": "หรือคลิกเลือกไฟล์ — ส่งออก Excel เป็น .csv ก่อน",
  "empImport.required": "จำเป็น:",
  "empImport.optional": "ไม่บังคับ:",
  "empImport.totalRows": "แถวทั้งหมด:",
  "empImport.valid": "ใช้ได้:",
  "empImport.errors": "มีปัญหา:",
  "empImport.rowError": "แถวที่ {row}{field}: {message}",
  "nav.comingSoonTitle": "{label} — เร็ว ๆ นี้",
  "hr.createPosting": "สร้างประกาศรับสมัคร",
  "hr.confirmReject": "ยืนยันไม่รับ",
  "hr.advanceStage": "ไปขั้นถัดไป",
  "hr.resumeNotUploaded": "ยังไม่อัปโหลด",
  "reports.custom.nameRequired": "กรุณาใส่ชื่อ",
  "reports.custom.runFailed": "รันรายงานไม่สำเร็จ",
  "reports.custom.run": "รันรายงาน",
  "reports.custom.newTemplate": "+ เทมเพลตใหม่",
  "reports.custom.fields": "ฟิลด์ที่แสดง (คั่นด้วยจุลภาค ไม่ใส่ = ทั้งหมด)",
  "reports.custom.addFilter": "+ เพิ่มตัวกรอง",
  "reports.custom.noFilters": "ไม่มีตัวกรอง รายงานจะดึงทุกแถวจาก {source}",
  "reports.custom.opNone": "— ไม่ใช้ —",
  "reports.custom.opEq": "เท่ากับ",
  "reports.custom.opNe": "ไม่เท่ากับ",
  "reports.custom.opIn": "อยู่ใน [a,b,c]",
  "reports.custom.opRegex": "ตรงกับรูปแบบ",
  "reports.d1": "สรุปจำนวนเที่ยว อัตราตรงเวลา และผู้โดยสาร",
  "reports.d2": "วิเคราะห์เส้นทาง เวลา ระยะทาง และอัตราการใช้ที่นั่ง",
  "reports.d3": "ประเมินคนขับ ตรงเวลา ประหยัดน้ำมัน และอัตราจบงาน",
  "reports.d4": "สถานะรถ ใช้งาน ซ่อม จอดเก็บ และระยะทางรวม",
  "reports.d5": "ประวัติซ่อมบำรุง ใบสั่งซ่อม ค่าใช้จ่าย และงานค้าง",
  "reports.d6": "รายงานประกันภัยและทะเบียนที่ใกล้หมดอายุ",
  "reports.d7": "สรุปการจองรายเส้นทางและแนวโน้มการใช้ที่นั่ง",
  "reports.d8": "รายงานผู้โดยสารล้นรถ ความถี่ รูปแบบ และการแก้ไข",
  "reports.d9": "สรุปน้ำมันรายคัน แนวโน้ม กม./ลิตร และจุดผิดปกติ",
  "reports.d10": "รายได้และต้นทุนรายสัญญา รายเส้นทาง และกำไร",
  "reports.d11": "สรุปวางบิลตามสัญญา เที่ยว x เรท = ยอดรวม",
  "hr.employeesCount": "พนักงาน {n} คน",
  "audit.fin.bill_check": "ตรวจบิลแล้ว",
  "audit.fin.bill_uncheck": "ยกเลิกการตรวจบิล",
  "audit.fin.bill_flag": "บันทึกปัญหา",
  "audit.fin.bill_flag_reply": "ตอบเรื่องปัญหา",
  "audit.fin.bill_flag_close": "ปิดเรื่องปัญหา",
  "audit.fin.bill_spot": "สุ่มตรวจรายการ",
  "audit.fin.opening_history": "ยกยอดประวัติ",
  "audit.fin.opening_undo": "ย้อนการยกยอด",
  "audit.fin.pay_batch_bill_open": "เปิดแก้บิลในรอบ",
  "audit.fin.pay_batch_bill_done": "แก้บิลในรอบเสร็จ",
  "audit.fin.pay_batch_file_check": "ตรวจไฟล์ธนาคาร",
  "audit.fin.pay_batch_hold_person": "พักทั้งคนในรอบ",
  "audit.fin.pay_batch_method": "เปลี่ยนวิธีจ่าย",
  "audit.fin.pay_batch_sample": "สุ่มตรวจรอบ",
  "audit.fin.pay_batch_transfer_failed": "โอนไม่สำเร็จ",
  "audit.fin.person_hold": "พักทั้งคน",
  "audit.fin.person_release": "ปลดพักทั้งคน",
  "audit.fin.staff_request_create": "ยื่นคำร้อง",
  "audit.fin.staff_request_move": "เปลี่ยนขั้นคำร้อง",
  "audit.module.finance": "การเงิน",
  "audit.module.hr": "บุคคล",
  "audit.module.jobs": "งาน",
  "audit.module.routes": "เส้นทาง",
  "audit.module.cargo": "ขนส่งสินค้า",
  "audit.module.contracts": "สัญญา",
  "audit.module.dispatch": "จ่ายงาน",
  "audit.module.admin": "ผู้ดูแลระบบ",
  "audit.module.export": "ส่งออกไฟล์",
  "audit.module.auth": "เข้าสู่ระบบ",
  "audit.module.fuel": "น้ำมัน",
  "audit.module.fleet": "ยานพาหนะ",
  "audit.module.settings": "ตั้งค่า",
  // plan/distance-pay — pay by the km a ticket ran: the sheet, the rate field, the ticket's real stats
  "distpay.open": "คิดค่าตอบแทนตามระยะ",
  "distpay.title": "คิดค่าตอบแทนตามระยะ",
  "distpay.lineType": "ค่าตอบแทนตามระยะ",
  "distpay.listOpen": "คิดตามระยะ",
  "distpay.listPaid": "ตามระยะ ฿{amount}",
  "distpay.listPaidHint": "คิดค่าตอบแทนตามระยะแล้ว · กดเพื่อดูหรือแก้",
  "distpay.loadFailed": "โหลดข้อมูลระยะของใบงานนี้ไม่สำเร็จ",
  "distpay.saved": "เพิ่มค่าตอบแทนตามระยะ ฿{amount} เข้าใบงานแล้ว",
  "distpay.removed": "ลบค่าตอบแทนตามระยะแล้ว",
  "distpay.err.generic": "บันทึกไม่สำเร็จ ลองอีกครั้ง",
  "distpay.notRun": "ใบงานนี้ยังไม่ได้วิ่ง คิดค่าตอบแทนตามระยะได้เมื่อเริ่มวิ่งแล้ว",
  "distpay.noDriver": "ใบงานนี้ยังไม่มีคนขับ จึงยังไม่มีคนรับเงิน",
  "distpay.roundClosed": "ค่าเที่ยวของใบงานนี้อยู่ในรอบจ่ายที่แช่แข็งหรือจ่ายแล้ว ยอดที่บันทึกตอนนี้จะไปลงบิลถัดไปเป็นรายการปรับ",
  "distpay.km": "ระยะที่วิ่ง",
  "distpay.src.box": "เลขไมล์กล่อง GPS · {km} กม.",
  "distpay.src.boxBody": "{from} → {to} ({span})",
  "distpay.src.phone": "เส้นทางจากมือถือคนขับ · {km} กม.",
  "distpay.src.phoneBody": "{n} จุด ({span})",
  "distpay.src.phoneGaps": "สัญญาณขาด {n} ช่วง ช่วงที่ขาดนับเป็นเส้นตรง",
  "distpay.src.odometer": "กรอกเลขไมล์เอง",
  "distpay.src.odometerBody": "ใส่เลขไมล์ก่อนวิ่งและหลังวิ่ง",
  "distpay.odoBefore": "ก่อนวิ่ง",
  "distpay.odoAfter": "หลังวิ่ง",
  "distpay.unitKm": "กม.",
  "distpay.unitRate": "บาท/กม.",
  "distpay.noReadings": "ไม่มีข้อมูลจากกล่อง GPS หรือมือถือในช่วงที่ใบงานนี้วิ่ง กรอกเลขไมล์เอง",
  "distpay.odoWrong": "เลขไมล์หลังวิ่งต้องไม่น้อยกว่าก่อนวิ่ง",
  "distpay.planned": "ตามแผน {km} กม.",
  "distpay.rate": "เรทต่อกิโล",
  "distpay.noRate": "{name} ยังไม่มีเรทต่อกิโล",
  "distpay.noRateBoth": "{name} และสัญญา {contract} ยังไม่มีเรทต่อกิโล",
  "distpay.rateFromPerson": "จากเรทของ {name} (มีผล {day}) · แก้ที่นี่ = ใช้กับใบนี้ใบเดียว",
  "distpay.rateFromContract": "จากเรทของสัญญา {name} (มีผล {day}) · แก้ที่นี่ = ใช้กับใบนี้ใบเดียว",
  "distpay.rateTyped": "กรอกเองสำหรับใบนี้ (เรทที่ตั้งไว้ {rate} บาท/กม.)",
  "distpay.keep": "จำเรทนี้ไว้ใช้ครั้งต่อไป เป็นเรทของ",
  "distpay.keepPerson": "{name}",
  "distpay.keepContract": "สัญญา {name}",
  "distpay.keepFrom": "มีผลตั้งแต่ {day} (วันของใบงานนี้) · เปลี่ยนภายหลังได้ที่หน้านั้น",
  "distpay.addsTo": "เพิ่มเข้ารายการเงินของใบงาน",
  "distpay.approvedNote": "บันทึกแล้วอนุมัติทันที และเข้าบิลของคนขับตามปกติ ใบงานหนึ่งมีรายการนี้ได้รายการเดียว",
  "distpay.current": "ตอนนี้ใบงานนี้มีค่าตอบแทนตามระยะ ฿{amount}",
  "distpay.remove": "ลบรายการนี้",
  "distpay.removeTitle": "ลบค่าตอบแทนตามระยะ?",
  "distpay.removeMsg": "ยอด ฿{amount} จะออกจากเงินของใบงานนี้",
  "distpay.save": "บันทึก",
  "distrate.label": "เรทต่อกิโล",
  "distrate.hintPerson": "ใช้ในกรณีคิดค่าตอบแทนตามระยะที่วิ่ง · ใช้ก่อนเรทของสัญญา",
  "distrate.hintContract": "ใช้ในกรณีคิดค่าตอบแทนตามระยะที่วิ่ง · ถ้าคนขับมีเรทของตัวเอง ใช้ของคนขับก่อน",
  "distrate.value": "{rate} บาท/กม.",
  "distrate.notSet": "ไม่ได้ตั้ง",
  "distrate.unsetPerson": "ไม่ตั้งที่คนนี้ (ใช้เรทของสัญญา)",
  "distrate.unsetContract": "ไม่ตั้งที่สัญญานี้",
  "distrate.since": "ตั้งแต่ {day}",
  "distrate.change": "เปลี่ยน…",
  "distrate.setFirst": "ตั้ง…",
  "distrate.sheetTitleFirst": "ตั้งเรทต่อกิโล",
  "distrate.pending": "{value} ตั้งแต่ {day}",
  "distrate.loadFailed": "โหลดเรทต่อกิโลไม่สำเร็จ · ลองอีกครั้ง",
  "distrate.err.generic": "บันทึกไม่สำเร็จ ลองอีกครั้ง",
  "distrate.saved": "บันทึกเรทต่อกิโลแล้ว",
  "distrate.cancelled": "ยกเลิกเรทที่รอมีผลแล้ว",
  "distrate.cancel": "ยกเลิก",
  "distrate.cancelTitle": "ยกเลิกเรทที่รอมีผล?",
  "distrate.cancelMsg": "{value} ที่จะมีผลตั้งแต่ {day} จะถูกยกเลิก",
  "distrate.sheetTitle": "เปลี่ยนเรทต่อกิโล",
  "distrate.save": "บันทึก",
  "distrate.whatPerson": "บาทต่อกิโลที่คนนี้ได้ เมื่อใบงานคิดค่าตอบแทนตามระยะที่วิ่ง ถ้าคนนี้ไม่มีเรท ระบบใช้เรทของสัญญาของใบงาน",
  "distrate.whatContract": "บาทต่อกิโลของใบงานในสัญญานี้ เมื่อคิดค่าตอบแทนตามระยะที่วิ่ง ถ้าคนขับมีเรทของตัวเอง ระบบใช้ของคนขับก่อน",
  "distrate.choose": "ตั้งเป็น",
  "distrate.setTo": "เรท",
  "distrate.rate": "เรทต่อกิโล",
  "distrate.day": "มีผลตั้งแต่วันที่",
  "distrate.dayHint": "ใบงานที่วิ่งตั้งแต่วันนี้ใช้เรทใหม่ ใบงานก่อนหน้าใช้เรทเดิม",
  "distrate.reason": "เหตุผล (ไม่บังคับ)",
  "distrate.history": "ประวัติ",
  "distrate.historyHide": "ซ่อนประวัติ",
  "distrate.historyEmpty": "ยังไม่เคยตั้ง",
  "distrate.from": "มีผลตั้งแต่ {day}",
  "distrate.by": "ตั้งโดย {name} · {at}",
  "ticket.real.running": "กำลังวิ่ง",
  "ticket.real.planKm": "แผน {km} กม.",
  "ticket.real.fromBox": "ระยะจริงจากเลขไมล์กล่อง GPS ช่วงเริ่มวิ่งถึงจบงาน",
  "ticket.real.fromPhone": "ระยะจริงจากเส้นทางมือถือคนขับ (รถคันนี้ไม่มีเลขไมล์จากกล่อง GPS)",
  "ticket.real.skipped": "ข้าม {n} จุด",
  "ticket.real.label": "จริง",
  "ticket.real.timeHint": "เวลาที่คนขับกดเริ่มงานและจบงานจริง",
} as const;

export type MessageKey = keyof typeof th;
